import { useState, useEffect, createContext, useContext, useRef, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { formatVehicleName, formatVehicleNameFromObject } from "@/utils/vehicleName";
import VehicleMakeModelEditor from "@/components/intake/VehicleMakeModelEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Camera, Lock, Check, ChevronRight, ChevronDown, ChevronLeft, ChevronUp, AlertCircle, Car, LogOut, Eye, EyeOff, Send, Clock, FileText, Plus, Trash2, DollarSign, Mail, Link as LinkIcon, History, CheckCircle2, Download, Search, RefreshCw, MessageSquare, Phone, Settings, Upload, X, Pencil, Package, Wrench, Info, Zap, User, Copy, RotateCcw, AlertTriangle, Activity, Bell, ArrowLeft, ArrowUp, Loader2, Shield, ShieldCheck, Tag, Thermometer, Lightbulb, XCircle, Printer, Stethoscope, Snowflake, Key, Save, FileWarning, NotebookPen, Battery, Sparkles, ClipboardList, TrendingDown } from "lucide-react";
import { LockWarningBanner, LockIndicator, ActiveLockBanner } from "@/components/LockWarningBanner";
import { useJobLock, useActiveLocks } from "@/hooks/useJobLock";
import { Toaster, toast } from "@/components/ui/sonner";

// Job Integrity Components
import { JobIntegritySection, JobIntegrityBadge, CollapsedIntegrityStatus } from "@/components/JobIntegritySection";
import QuoteReadinessSummary from "@/components/QuoteReadinessSummary";
import SendGateModal from "@/components/SendGateModal";
import MCCIntegrityBanner from "@/components/pricing/MCCIntegrityBanner";
import SendBackToTesterButton from "@/components/pricing/SendBackToTesterButton";
import QuickApplyAllCCNButton from "@/components/pricing/QuickApplyAllCCNButton";
import { AddRectificationPrompt } from "@/components/pricing/AddRectificationPrompt";
import BatteryTestPanel from "@/components/inspection/BatteryTestPanel";
import LeftPanelSourceBadge, { classifyInspectionItemSource } from "@/components/pricing/LeftPanelSourceBadge";
import MCCViewerModal from "@/components/pricing/MCCViewerModal";

// Alert Dialog for important notifications
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Import extracted components
import { AuthProvider, useAuth, ProtectedRoute, NewToIntakeRedirect, API, BACKEND_URL } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import FeedbackReportButton from "@/components/shared/FeedbackReportButton";
import LoginPage from "@/pages/LoginPage";
import InspectionStatusIndicator from "@/components/shared/InspectionStatusIndicator";
import KeyTagInline from "@/components/shared/KeyTagInline";
import PhotoAnnotator from "@/components/shared/PhotoAnnotator";
import { LazyArrivalPhoto, LazyItemPhotos } from "@/components/shared/LazyPhotoComponents";
import ServiceRecommendationPanel from "@/components/ServiceRecommendationPanel";
import JobsListPage from "@/pages/technician/JobsListPage";
import SectionsListPage from "@/pages/technician/SectionsListPage";
import SectionItemsPage from "@/pages/technician/SectionItemsPage";
import ArrivalPhotosPage from "@/pages/technician/ArrivalPhotosPage";
import SubmitInspectionPage from "@/pages/technician/SubmitInspectionPage";
import StartInspectionPage from "@/pages/technician/StartInspectionPage";
import CreateInspectionPage from "@/pages/technician/CreateInspectionPage";
import InspectionPermanentView from "@/pages/technician/InspectionPermanentView";
import MOTWorkspacePage from "@/pages/technician/MOTWorkspacePage";
import MOTTodaysQueuePage from "@/pages/technician/MOTTodaysQueuePage";
import PartsPackList from "@/pages/foh/PartsPackList";
import PartsPackView from "@/pages/foh/PartsPackView";
import FOHCustomerPreview from "@/pages/foh/FOHCustomerPreview";
import PortalPreviewPage from "@/pages/foh/PortalPreviewPage";
import FOHJobView from "@/pages/foh/FOHJobView";
import FOHSendYestimate from "@/pages/foh/FOHSendYestimate";
import FOHAuditPanel from "@/pages/foh/FOHAuditPanel";
import FOHInspectionArchive from "@/pages/foh/FOHInspectionArchive";
import FOHSendPortal from "@/pages/foh/FOHSendPortal";
import VehicleHistoryPage from "@/pages/foh/VehicleHistoryPage";
import GHOpenGateway from "@/pages/gh/GHOpenGateway";
import RequiredWorkPage from "@/pages/technician/RequiredWorkPage";
import DeclineAnalyticsPage from "@/pages/foh/DeclineAnalyticsPage";
import FrictionDashboard from "@/pages/admin/FrictionDashboard";
import CommonIssuesAdminPage from "@/pages/admin/CommonIssuesAdminPage";
import AdminCommonlyRequiredItems from "@/pages/admin/AdminCommonlyRequiredItems";
import RuleCoverageDashboard from "@/pages/admin/RuleCoverageDashboard";
import PriceBookAdmin from "@/pages/admin/PriceBookAdmin";
import PricingPackageAdmin from "@/pages/admin/PricingPackageAdmin";
import JCEAdmin from "@/pages/admin/JCEAdmin";
import AdminStaffPage from "@/pages/admin/AdminStaffPage";
import ProfilePage from "@/pages/admin/ProfilePage";
import MarketingHomepage from "@/pages/marketing/MarketingHomepage";
import MarketingDemo from "@/pages/demo/MarketingDemo";
import DemoPageStandalone from "@/pages/demo/DemoPageStandalone";
import CustomerBookNowPage from "@/pages/customer/CustomerBookNowPage";
import MobileCustomerPortal from "@/pages/customer/MobileCustomerPortal";
import CustomerMessageCard from "@/components/pricing/CustomerMessageCard";
import RepairIntegrityCard from "@/components/pricing/RepairIntegrityCard";
import AddRecommendationButton from "@/components/recommendations/AddRecommendationButton";
import AlignmentSuggestBanner from "@/components/recommendations/AlignmentSuggestBanner";
import EmissionsRoutingPanel from "@/components/recommendations/EmissionsRoutingPanel";
import TyreReplacementGroupCard from "@/components/recommendations/TyreReplacementGroupCard";
// iter198qq — TesterEvidenceCard import removed; component is no longer
// rendered. Tester evidence + MCC photos now surface in the left-panel
// MOT row's expanded panel instead. Keep this comment so we don't
// reintroduce the import by accident during future refactors.
import TimingServiceModal from "@/components/timing/TimingServiceModal";

import JobCompletenessPanel from "@/components/jce/JobCompletenessPanel";
import JCEV2Panel from "@/components/jce/JCEV2Panel";
import JCEV2InlineHints from "@/components/jce/JCEV2InlineHints";
import JCESendConfirmationModal from "@/components/jce/JCESendConfirmationModal";
import PremiumDiagnosticReport from "@/components/reports/PremiumDiagnosticReport";
import JobGuidePanel from "@/components/JobGuidePanel";
import AuthorisedValueGate, { AuthorisedValueDisplay } from "@/components/pricing/AuthorisedValueGate";
import CustomerIntentModal, { CustomerIntentDisplay } from "@/components/pricing/CustomerIntentModal";
import { DedupeValidationModal } from "@/components/pricing/DedupeValidation";
import JobCentricWorkspace from "@/components/pricing/JobCentricWorkspace";
import { OilServicePanel } from "@/components/pricing/OilServicePanel";
import { PairPolicyModal, PairInfoBanner, MandatoryBundleBanner, PairStatusBadge } from "@/components/pricing/PairPolicyModal";
import { ServiceMappingPanel } from "@/components/pricing/ServiceMappingPanel";
import AddFromPriceBookModal from "@/components/pricing/AddFromPriceBookModal";
import IntakeSessionPage from "@/pages/intake/IntakeSessionPage";
import StartIntakePage from "@/pages/intake/StartIntakePage";
import GHCasePage      from "@/pages/intake/GHCasePage";
import AdminIntegrationHealthPage from "@/pages/admin/AdminIntegrationHealthPage";
import GHPreviewFaultLabPage from "@/pages/admin/GHPreviewFaultLabPage";
import MOTResultsRequiringActionTile from "@/components/intake/MOTResultsRequiringActionTile";
import MOTDayLoadTile from "@/components/intake/MOTDayLoadTile";
import DedupSuggestBanner from "@/components/dedup/DedupSuggestBanner";

// ============== LEGACY TECH ROUTE REDIRECTS ==============
// Redirect old /tech/* routes to new /app/tech/* routes while preserving the path
const TechStartRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/tech/job/${id}/start`} replace />;
};

const TechArrivalRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/tech/job/${id}/arrival`} replace />;
};

const TechSectionsRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/tech/job/${id}/sections`} replace />;
};

const TechSectionRedirect = () => {
  const { id, sectionId } = useParams();
  return <Navigate to={`/app/tech/job/${id}/section/${sectionId}`} replace />;
};

const TechItemRedirect = () => {
  const { id, itemId } = useParams();
  return <Navigate to={`/app/tech/job/${id}/item/${itemId}`} replace />;
};

const TechSubmitRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/tech/job/${id}/submit`} replace />;
};

const TechDiagnosticRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/tech/job/${id}/diagnostic`} replace />;
};

// Helper function to extract error message from API response
// Handles both string errors and Pydantic validation error arrays
const getErrorMessage = (err, defaultMsg = "An error occurred") => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    // Pydantic validation error - extract field name AND message
    // Make it user-friendly by translating technical field names
    const fieldNameMap = {
      'title': 'Job Title',
      'category': 'Labour Category',
      'hours': 'Labour Hours',
      'rate': 'Labour Rate',
      'description': 'Description',
      'unit_price': 'Unit Price',
      'quantity': 'Quantity',
      'amount': 'Amount',
      'charge_type': 'Charge Type',
      'vat_rate': 'VAT Rate',
    };
    
    return detail.map(e => {
      // loc contains the field path, e.g., ["body", "job_groups", 0, "labour_lines", 0, "category"]
      const loc = e.loc || [];
      const fieldParts = loc.filter(l => l !== 'body' && typeof l !== 'number');
      const lastField = fieldParts[fieldParts.length - 1] || 'Unknown field';
      const friendlyName = fieldNameMap[lastField] || lastField.replace(/_/g, ' ');
      
      // Get job group index if available
      const jobIndex = loc.findIndex(l => l === 'job_groups');
      const jobNum = jobIndex !== -1 && typeof loc[jobIndex + 1] === 'number' ? `Job #${loc[jobIndex + 1] + 1}` : null;
      
      const message = e.msg || e.message || 'is required';
      
      if (jobNum) {
        return `${jobNum} - ${friendlyName}: ${message}`;
      }
      return `${friendlyName}: ${message}`;
    }).join('. ');
  } else if (typeof detail === 'string') {
    return detail;
  } else if (detail) {
    return JSON.stringify(detail);
  }
  return err?.message || defaultMsg;
};

// ============== DIAGNOSTIC CAPTURE PAGE ==============

// iter213d-post-diag — Renders the correct "what's next" call-to-
// action after the Diagnostic has been submitted. Consults the
// backend's authoritative outstanding-workflow set instead of the
// commissioning flags on ``job``. See PRD iter213d-routing.
const PostDiagnosticNext = ({ jobId }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading" });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/tech/job/${jobId}/required-work`);
        if (cancelled) return;
        const workflows = r.data?.workflows || [];
        const outstanding = workflows.filter((w) => w.outstanding);
        setState({ phase: "loaded", outstanding, intakeId: r.data?.intake_session_id });
      } catch {
        if (!cancelled) setState({ phase: "loaded", outstanding: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  if (state.phase === "loading") {
    return (
      <div className="mb-4 p-3 text-sm text-slate-500" data-testid="post-diag-next-loading">
        Checking remaining work…
      </div>
    );
  }

  const { outstanding = [] } = state;

  // Nothing else outstanding — clean completion.
  if (outstanding.length === 0) {
    return (
      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg"
           data-testid="post-diag-all-done">
        <div className="flex items-center gap-2 mb-3">
          <Check className="w-5 h-5 text-green-600" />
          <h4 className="font-semibold text-green-800">All technician work complete</h4>
        </div>
        <Button
          onClick={() => navigate("/app/tech/jobs")}
          className="w-full bg-green-600 hover:bg-green-700"
          data-testid="diagnostic-complete-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          Return to Technician Dashboard
        </Button>
      </div>
    );
  }

  // Exactly one workflow outstanding — direct next-step button using
  // the backend-authoritative start_url and canonical label.
  if (outstanding.length === 1) {
    const only = outstanding[0];
    return (
      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg"
           data-testid={`post-diag-next-${only.key}`}>
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <h4 className="font-semibold text-amber-800">{only.label} Still Required</h4>
        </div>
        <p className="text-sm text-amber-700 mb-3">
          The Diagnostic has been submitted. {only.label} is still outstanding on this job.
        </p>
        <Button
          onClick={() => navigate(only.start_url)}
          className="w-full bg-amber-600 hover:bg-amber-700"
          data-testid={`post-diag-start-${only.key}-btn`}
        >
          <ChevronRight className="w-4 h-4 mr-2" />
          {only.start_label}
        </Button>
      </div>
    );
  }

  // Multiple outstanding — send to the chooser.
  return (
    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg"
         data-testid="post-diag-multi-outstanding">
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle className="w-5 h-5 text-amber-600" />
        <h4 className="font-semibold text-amber-800">
          {outstanding.length} tasks still outstanding
        </h4>
      </div>
      <p className="text-sm text-amber-700 mb-3">
        {outstanding.map((w) => w.label).join(" · ")}
      </p>
      <Button
        onClick={() => navigate(`/app/tech/job/${jobId}/required-work`)}
        className="w-full bg-amber-600 hover:bg-amber-700"
        data-testid="post-diag-open-chooser-btn"
      >
        <ChevronRight className="w-4 h-4 mr-2" />
        Choose next task
      </Button>
    </div>
  );
};

const DiagnosticCapturePage = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [polishedReport, setPolishedReport] = useState("");
  const [transmission, setTransmission] = useState("");
  const [timingSystem, setTimingSystem] = useState("");
  const navigate = useNavigate();
  
  // Arrival photos state (shared with VI)
  const [arrivalPhotosStatus, setArrivalPhotosStatus] = useState({ complete: false, photo_count: 0, missing_corners: [] });
  const [showArrivalPhotos, setShowArrivalPhotos] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Photo annotation state
  const [annotatingPhoto, setAnnotatingPhoto] = useState(null);
  const [annotatingPhotoIndex, setAnnotatingPhotoIndex] = useState(null);
  
  // Diagnostic outcome types
  const DIAGNOSTIC_OUTCOME_TYPES = [
    { value: "fault_identified", label: "Fault Identified", requiresDetails: true },
    { value: "no_fault", label: "No fault identified at time of inspection", requiresDetails: false },
    { value: "further_investigation", label: "Further investigation required (non-authorisable)", requiresDetails: true },
    { value: "abandoned", label: "Inspection abandoned", requiresDetails: true }
  ];
  
  // Form state - ELITE DIAGNOSTIC REPORT FIELDS
  const [formData, setFormData] = useState({
    mileage: "",  // MANDATORY - Current mileage
    actions_taken: "",
    outcome_type: "fault_identified",
    conclusion: "",
    abandoned_reason: "",
    evidence_photos: [],
    // Enhanced workflow data
    equipment_used: [],
    tests_performed: [],
    ruled_out: [],
    additional_observations: "",
    // ELITE fields
    fault_recreated: "not_attempted",
    fault_recreation_notes: "",
    fault_status: "stored",
    dtc_codes: [],  // {code, description, status, module}
    live_data_readings: [],  // {parameter, value, unit, status}
    confirmed_failed_parts: [],  // {part_name, part_number, reason}
    required_fixings: [],
    consumables: [],
    suspected_not_confirmed: [],
    diagnostic_time_hours: "",
    estimated_repair_time_hours: "",
    verification_road_test: false,
    verification_live_data: false,
    verification_dtc_rescan: false,
    verification_regen_reset: false,
    verification_adaptation_reset: false,
    verification_other: "",
    // iter211f3 — Tech-side rectification. Tech picks the fix at diag
    // submission time so the yestimate arrives at FOH already carrying
    // a rectification JG (battery replacement, coil set, etc.) with
    // parts + labour + coding time all populated. Optional — a blank
    // rectification_code just means FOH resolves later.
    rectification_code: "",
    rectification_variant_code: "",
    rectification_freetext_title: "",
    rectification_freetext_parts_price: "",
    rectification_freetext_labour_hours: "",
    rectification_freetext_customer_note: ""
  });
  
  // Selected template
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // iter211f3 — Tech rectification catalogue for the "Recommended
  // rectification" step. Loaded lazily when the tech opens the section.
  const [rectCatalogue, setRectCatalogue] = useState([]);
  const [rectMode, setRectMode] = useState("none"); // 'none' | 'catalogue' | 'freetext'
  
  // Workflow configuration from backend
  const [workflowConfig, setWorkflowConfig] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Apply template to form
  const applyTemplate = (templateId) => {
    if (!workflowConfig?.templates?.[templateId]) return;
    const template = workflowConfig.templates[templateId];
    setSelectedTemplate(templateId);
    setFormData(prev => ({
      ...prev,
      equipment_used: template.equipment || [],
      tests_performed: (template.tests || []).map(t => ({ ...t, reading: '' })),
      ruled_out: template.ruled_out || []
    }));
    // Expand the equipment and tests sections
    setExpandedCategories({ equipment: true, tests: true });
  };

  // iter211f3 — Lazy-load the rectification catalogue when the tech
  // opens the "Recommended rectification" section.
  const loadRectCatalogue = async () => {
    if (rectCatalogue.length > 0) return;
    try {
      const r = await axios.get(`${API}/rectification-catalogue`);
      setRectCatalogue(r.data.catalogue || []);
    } catch (err) {
      toast.error("Could not load rectification catalogue");
    }
  };
  const activeRectEntry = rectCatalogue.find((c) => c.code === formData.rectification_code);

  // iter197m — Battery Gateway: every diagnostic VI requires a completed
  // battery health test BEFORE other tests are usable, unless the selected
  // template is flagged pure_mechanical (suspension knock, brake noise etc).
  const batteryGatewayCfg = workflowConfig?.battery_gateway;
  const selectedTemplateObj = selectedTemplate ? workflowConfig?.templates?.[selectedTemplate] : null;
  const requiresBatteryGateway = !!batteryGatewayCfg && !selectedTemplateObj?.pure_mechanical;

  // Auto-insert the battery_test row at the top of tests_performed when the
  // gateway is required and it's not already present. Locked (cannot be
  // unchecked or removed) — handled in the UI render below.
  useEffect(() => {
    if (!requiresBatteryGateway || !batteryGatewayCfg) return;
    setFormData(prev => {
      const hasBattery = (prev.tests_performed || []).some(
        t => t.test_id === batteryGatewayCfg.test_id
      );
      if (hasBattery) return prev;
      return {
        ...prev,
        tests_performed: [
          {
            test_id: batteryGatewayCfg.test_id,
            category_id: batteryGatewayCfg.category_id,
            reading: '',
            interpretation: '',
            evidence_photo: '',
            locked: true,
          },
          ...(prev.tests_performed || []),
        ],
      };
    });
  }, [requiresBatteryGateway, batteryGatewayCfg, selectedTemplate]);

  // Pull the battery row for the dedicated card + readiness check.
  // Gateway is met when the technician has captured voltage, an
  // interpretation, AND the tester output photo. This matches the
  // standard VI battery test fidelity (voltage > free-form "reading").
  const batteryRow = (formData.tests_performed || []).find(
    t => t.test_id === batteryGatewayCfg?.test_id
  );
  const batteryVoltageCaptured = String(
    batteryRow?.battery_voltage_v ?? batteryRow?.reading ?? ""
  ).trim();
  const batteryGatewayMet = !!(
    batteryRow &&
    batteryVoltageCaptured &&
    batteryRow.interpretation &&
    batteryRow.evidence_photo
  );
  const batteryGateBlocks = requiresBatteryGateway && !batteryGatewayMet;

  const updateBatteryRow = (patch) => {
    setFormData(prev => ({
      ...prev,
      tests_performed: (prev.tests_performed || []).map(t =>
        t.test_id === batteryGatewayCfg.test_id ? { ...t, ...patch } : t
      ),
    }));
  };

  const handleBatteryPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => updateBatteryRow({ evidence_photo: reader.result });
    reader.readAsDataURL(file);
  };


  useEffect(() => {
    // Load workflow config
    axios.get(`${API}/diagnostic/workflow-config`)
      .then(res => setWorkflowConfig(res.data))
      .catch(err => console.error("Failed to load workflow config:", err));
    
    axios.get(`${API}/jobs/${id}`)
      .then(res => {
        setJob(res.data);
        // Pre-fill transmission + timing system from job if already set
        setTransmission(res.data.transmission || "");
        setTimingSystem(res.data.timing_system || "");
        // Pre-fill mileage from job if already recorded (from VI or previous diagnostic)
        if (res.data.mileage) {
          setFormData(prev => ({ ...prev, mileage: res.data.mileage.toString() }));
        }
        // If diagnostic already complete, redirect back
        if (res.data.diagnostic_status === 'completed') {
          setSuccess(true);
          setPolishedReport(res.data.diagnostic_polished_report || "");
        }
      })
      .catch(err => console.error("Failed to load job:", err))
      .finally(() => setLoading(false));
    
    // Check arrival photos status (shared between Diag and VI)
    axios.get(`${API}/jobs/${id}/arrival-photos/status`)
      .then(res => {
        setArrivalPhotosStatus(res.data);
        // If arrival photos not complete, show the upload section
        if (!res.data.complete) {
          setShowArrivalPhotos(true);
        }
      })
      .catch(() => {
        // Endpoint might not exist yet for legacy jobs, show upload section
        setShowArrivalPhotos(true);
      });
    
    // Also check if there's an existing inspection with mileage
    axios.get(`${API}/jobs/${id}/inspection`)
      .then(res => {
        if (res.data && res.data.mileage) {
          setFormData(prev => {
            // Only update if not already set (job mileage takes priority)
            if (!prev.mileage) {
              return { ...prev, mileage: res.data.mileage.toString() };
            }
            return prev;
          });
        }
      })
      .catch(() => {}); // Inspection may not exist, that's fine
  }, [id]);

  // Handle arrival photo upload
  const handleArrivalPhotoUpload = async (corner, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.post(`${API}/jobs/${id}/arrival-photos`, {
          corner: corner,
          photo_data: reader.result
        });
        // Refresh status
        const statusRes = await axios.get(`${API}/jobs/${id}/arrival-photos/status`);
        setArrivalPhotosStatus(statusRes.data);
        if (statusRes.data.complete) {
          setShowArrivalPhotos(false);
        }
      } catch (err) {
        setError("Failed to upload arrival photo");
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle evidence photo capture with optional annotation
  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      // Open annotation modal with the captured photo
      setAnnotatingPhoto(reader.result);
      setAnnotatingPhotoIndex(null); // New photo, not editing existing
    };
    reader.readAsDataURL(file);
  };

  // Save annotated photo
  const handleAnnotationSave = (annotatedPhoto) => {
    if (annotatingPhotoIndex !== null) {
      // Replacing existing photo
      setFormData(prev => ({
        ...prev,
        evidence_photos: prev.evidence_photos.map((p, i) => i === annotatingPhotoIndex ? annotatedPhoto : p)
      }));
    } else {
      // Adding new photo
      setFormData(prev => ({
        ...prev,
        evidence_photos: [...prev.evidence_photos, annotatedPhoto]
      }));
    }
    setAnnotatingPhoto(null);
    setAnnotatingPhotoIndex(null);
  };

  // Cancel annotation - still add the photo without annotations
  const handleAnnotationCancel = () => {
    if (annotatingPhoto && annotatingPhotoIndex === null) {
      // New photo - add it without annotations
      setFormData(prev => ({
        ...prev,
        evidence_photos: [...prev.evidence_photos, annotatingPhoto]
      }));
    }
    setAnnotatingPhoto(null);
    setAnnotatingPhotoIndex(null);
  };

  // Edit existing photo annotation
  const handleEditPhotoAnnotation = (index) => {
    setAnnotatingPhoto(formData.evidence_photos[index]);
    setAnnotatingPhotoIndex(index);
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      evidence_photos: prev.evidence_photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // iter211e — Consolidate every validation-fail branch through a
    // single helper so the tech gets an unmissable red toast + a
    // scroll-to-top-of-form on failure. Previously `setError()` only
    // wrote the message into a banner at the top of the form, but the
    // Submit button sits at the bottom of a long form (arrival photos,
    // battery gateway, evidence photos, textareas, etc.) — so
    // validation failures felt like "nothing happening" to the tech.
    const failSubmit = (msg, opts = {}) => {
      setError(msg);
      try {
        toast.error(msg, { duration: 6000 });
      } catch { /* toast lib missing — banner alone is enough */ }
      // Scroll the form container back to the top so the banner is in
      // view. Use requestAnimationFrame so the error state is committed
      // before the scroll fires.
      requestAnimationFrame(() => {
        try {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch { window.scrollTo(0, 0); }
      });
      if (opts.afterFail) opts.afterFail();
    };

    // Validate arrival photos - MANDATORY
    if (!arrivalPhotosStatus.complete) {
      return failSubmit(
        "All 4 arrival photos are required before submitting. Please capture vehicle arrival photos first.",
        { afterFail: () => setShowArrivalPhotos(true) },
      );
    }

    // Validate mileage - MANDATORY
    if (!formData.mileage || parseInt(formData.mileage) <= 0) {
      return failSubmit("Valid mileage is required for diagnostic inspection");
    }

    if (!formData.actions_taken.trim()) {
      return failSubmit("Diagnostic Actions Taken is required");
    }
    
    // Validate based on outcome type
    const outcomeType = formData.outcome_type;
    if (outcomeType === "fault_identified" && !formData.conclusion.trim()) {
      return failSubmit("Diagnostic Conclusion is required when a fault is identified");
    }
    if (outcomeType === "further_investigation" && !formData.conclusion.trim()) {
      return failSubmit("Please describe what further investigation is needed");
    }
    if (outcomeType === "abandoned" && !formData.abandoned_reason.trim()) {
      return failSubmit("Please provide a reason for abandoning the inspection");
    }

    // Validate evidence photos for findings that require them
    const outcomesRequiringPhotos = ["fault_identified", "further_investigation"];
    if (outcomesRequiringPhotos.includes(outcomeType) && formData.evidence_photos.length === 0) {
      return failSubmit("Photo evidence is mandatory when a fault is identified or further investigation is required. Please add at least one evidence photo.");
    }

    // iter197m — Battery gateway guard (skip when outcome is "abandoned",
    // mirrors the backend).
    if (requiresBatteryGateway && outcomeType !== "abandoned" && !batteryGatewayMet) {
      return failSubmit("Battery Health Test must be completed (voltage, interpretation, photo) before submitting.");
    }
    
    // Build the final conclusion based on outcome type
    let finalConclusion = "";
    if (outcomeType === "fault_identified") {
      finalConclusion = formData.conclusion;
    } else if (outcomeType === "no_fault") {
      finalConclusion = "No fault identified at time of inspection. " + (formData.conclusion || "All systems tested within normal parameters.");
    } else if (outcomeType === "further_investigation") {
      finalConclusion = "Further investigation required (non-authorisable): " + formData.conclusion;
    } else if (outcomeType === "abandoned") {
      finalConclusion = "Inspection abandoned – " + formData.abandoned_reason;
    }

    setSubmitting(true);
    try {
      // iter213d — Coerce optional numeric fields to null when blank
      // so the backend's Optional[float] Pydantic validators don't
      // reject the payload with "Input should be a valid number".
      const _numOrNull = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const submitData = {
        ...formData,
        mileage: parseInt(formData.mileage),  // Ensure mileage is sent as integer
        conclusion: finalConclusion,
        transmission: transmission || undefined,
        timing_system: timingSystem || undefined,
        template_id: selectedTemplate || undefined,
        rectification_freetext_parts_price:  _numOrNull(formData.rectification_freetext_parts_price),
        rectification_freetext_labour_hours: _numOrNull(formData.rectification_freetext_labour_hours),
        diagnostic_time_hours:               _numOrNull(formData.diagnostic_time_hours),
        estimated_repair_time_hours:         _numOrNull(formData.estimated_repair_time_hours),
      };
      const res = await axios.post(`${API}/tech/diagnostic/${id}/submit`, submitData);
      setSuccess(true);
      setPolishedReport(res.data.polished_report);
    } catch (err) {
      // iter211e — Route the backend error through the same helper so
      // the tech sees an unmissable toast + scroll, not a silent
      // banner buried at the top of the form.
      failSubmit(getErrorMessage(err, "Failed to submit diagnostic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  // Success state - show polished report
  if (success) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="p-4 max-w-2xl mx-auto">
          <Card className="border-green-300">
            <CardHeader className="bg-green-50">
              <div className="flex items-center gap-3">
                <Check className="w-8 h-8 text-green-600" />
                <div>
                  <CardTitle className="text-green-800">Diagnostic Submitted</CardTitle>
                  <CardDescription className="text-green-600">{job?.reg} - {formatVehicleNameFromObject(job)}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-slate-700">AI-Polished Diagnostic Report:</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/jobs/${id}/diagnostic-report/pdf`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `Diagnostic_Report_${job?.reg || 'report'}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        alert("Failed to download PDF");
                      }
                    }}
                    className="flex items-center gap-2"
                    data-testid="save-diag-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    Save PDF
                  </Button>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border whitespace-pre-wrap text-sm font-mono max-h-96 overflow-y-auto">
                  {polishedReport}
                </div>
              </div>
              
              {/* iter213d-post-diag — Authoritative post-Diagnostic
                  next-step banner. Previously we branched purely on
                  ``job?.requires_vehicle_inspection`` (the *commissioned*
                  flag), which showed "Vehicle Inspection Still Required"
                  even after the S&R inspection had been submitted (live
                  bug on SJ027002). Now the banner consults the shared
                  ``/api/tech/job/{id}/required-work`` endpoint — the
                  same single source of truth the deep-link resolver and
                  the Required Work chooser use — and only surfaces work
                  that is genuinely outstanding. */}
              <PostDiagnosticNext jobId={id} />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate("/app/tech/jobs")}>
                  Back to Jobs
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      {/* Photo Annotation Modal */}
      {annotatingPhoto && (
        <PhotoAnnotator
          photo={annotatingPhoto}
          onSave={handleAnnotationSave}
          onCancel={handleAnnotationCancel}
        />
      )}
      
      <div className="p-4 max-w-2xl mx-auto">
        {/* Arrival Photos Section - Required before diagnostic */}
        {showArrivalPhotos && !arrivalPhotosStatus.complete && (
          <Card className="border-amber-300 mb-4">
            <CardHeader className="bg-amber-50">
              <div className="flex items-center gap-3">
                <Camera className="w-6 h-6 text-amber-600" />
                <div>
                  <CardTitle className="text-amber-800 text-lg">Vehicle Arrival Photos Required</CardTitle>
                  <CardDescription className="text-amber-600">Capture all 4 corners before starting diagnostic</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { corner: "FL", label: "Front Left" },
                  { corner: "FR", label: "Front Right" },
                  { corner: "RL", label: "Rear Left" },
                  { corner: "RR", label: "Rear Right" }
                ].map(({ corner, label }) => {
                  const isUploaded = arrivalPhotosStatus.uploaded_corners?.includes(corner);
                  return (
                    <div key={corner} className={`p-3 rounded-lg border-2 ${isUploaded ? 'border-green-300 bg-green-50' : 'border-dashed border-gray-300'}`}>
                      <p className="text-sm font-medium mb-2 text-center">{label}</p>
                      {isUploaded ? (
                        <div className="flex items-center justify-center text-green-600">
                          <Check className="w-6 h-6" />
                          <span className="ml-1 text-sm">Uploaded</span>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center cursor-pointer py-2">
                          <Camera className="w-8 h-8 text-gray-400 mb-1" />
                          <span className="text-xs text-gray-500">Tap to capture</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => handleArrivalPhotoUpload(corner, e)}
                            disabled={uploadingPhoto}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              {uploadingPhoto && (
                <div className="text-center mt-3 text-amber-600">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                  Uploading...
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3 text-center">
                {arrivalPhotosStatus.photo_count}/4 photos uploaded
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Arrival photos complete indicator */}
        {arrivalPhotosStatus.complete && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-700 text-sm">All 4 arrival photos captured</span>
          </div>
        )}

        <Card className="border-purple-300">
          <CardHeader className="bg-purple-50">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-purple-600" />
              <div>
                <CardTitle className="text-purple-800">Diagnostic Capture</CardTitle>
                <CardDescription className="text-purple-600">{job?.reg} - {formatVehicleNameFromObject(job)}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Customer Reported Concern - Read Only */}
            <div className="mb-6 p-4 bg-purple-100 rounded-lg border border-purple-200">
              <p className="text-xs font-semibold text-purple-600 uppercase mb-1">Customer Reported Concern</p>
              <p className="text-slate-800">{job?.customer_concern}</p>
              <p className="text-xs text-purple-500 mt-2">This concern was submitted by the Service Advisor</p>
            </div>

            {/* Mileage, Transmission, Timing System - MANDATORY for Diagnostic */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <Car className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-800">Vehicle Information</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="diag-mileage" className="text-sm font-medium text-blue-700">Current Mileage *</Label>
                  <Input
                    id="diag-mileage"
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData(prev => ({ ...prev, mileage: e.target.value }))}
                    placeholder="Enter mileage"
                    required
                    min="1"
                    className="mt-1 bg-white"
                    data-testid="diag-mileage-input"
                  />
                  {job?.mileage && formData.mileage === job.mileage.toString() && (
                    <p className="text-xs text-blue-500 mt-1">Pre-filled from previous inspection</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="diag-transmission" className="text-sm font-medium text-blue-700">Transmission</Label>
                  <select
                    id="diag-transmission"
                    value={transmission}
                    onChange={(e) => setTransmission(e.target.value)}
                    className="mt-1 w-full p-2 border rounded-md bg-white h-10 text-sm"
                    data-testid="diag-transmission-input"
                  >
                    <option value="">Select...</option>
                    <option value="Manual">Manual</option>
                    <option value="Automatic">Automatic (Torque Converter)</option>
                    <option value="DSG">DSG / Dual Clutch</option>
                    <option value="CVT">CVT</option>
                    <option value="Automated Manual">Automated Manual</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="diag-timing-system" className="text-sm font-medium text-blue-700">Timing System</Label>
                  <select
                    id="diag-timing-system"
                    value={timingSystem}
                    onChange={(e) => setTimingSystem(e.target.value)}
                    className="mt-1 w-full p-2 border rounded-md bg-white h-10 text-sm"
                    data-testid="diag-timing-system-input"
                  >
                    <option value="">Select...</option>
                    <option value="chain">Timing Chain</option>
                    <option value="belt">Timing Belt (Cam Belt)</option>
                    <option value="wet_belt">Wet Belt (Oil Bath)</option>
                    <option value="unknown">Unknown / Not Visible</option>
                  </select>
                </div>
                {job?.fuel_type && (
                  <div>
                    <Label className="text-sm font-medium text-slate-500">Fuel Type</Label>
                    <p className="mt-1 font-medium text-slate-700 h-10 flex items-center">{job.fuel_type}</p>
                  </div>
                )}
                {job?.year && (
                  <div>
                    <Label className="text-sm font-medium text-slate-500">Year</Label>
                    <p className="mt-1 font-medium text-slate-700 h-10 flex items-center">{job.year}</p>
                  </div>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* ═══════════════ DIAGNOSTIC TEMPLATE SELECTOR ═══════════════ */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-800">Quick Start Templates</h3>
                  <Badge className="bg-purple-100 text-purple-700 text-xs">Speeds up capture</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {workflowConfig?.templates && Object.entries(workflowConfig.templates).map(([key, template]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyTemplate(key)}
                      className={`p-2 rounded border text-left transition-all text-sm ${
                        selectedTemplate === key 
                          ? 'bg-purple-100 border-purple-400 ring-2 ring-purple-300' 
                          : 'bg-white hover:bg-purple-50 border-slate-200'
                      }`}
                    >
                      <span className="font-medium text-slate-800 block">{template.label}</span>
                      <span className="text-xs text-slate-500 line-clamp-1">{template.description}</span>
                    </button>
                  ))}
                </div>
                {selectedTemplate && (
                  <p className="text-xs text-purple-600 mt-2">
                    ✓ Template applied: Pre-selected equipment, tests, and ruled-out items
                  </p>
                )}
              </div>

              {/* ═══════════════ BATTERY GATEWAY (iter197m) ═══════════════ */}
              {requiresBatteryGateway && batteryGatewayCfg && (
                <BatteryTestPanel
                  value={batteryRow || {}}
                  onChange={updateBatteryRow}
                  gateway
                  gatewayMet={batteryGatewayMet}
                  testIdPrefix="battery-gateway"
                />
              )}

              {/* ═══════════════ FAULT RECREATION STATUS ═══════════════ */}
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <RotateCcw className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-800">Fault Recreation Status *</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {workflowConfig?.fault_recreation_options?.map(option => (
                    <label
                      key={option.id}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                        formData.fault_recreated === option.id
                          ? 'bg-amber-100 border-amber-400'
                          : 'bg-white hover:bg-amber-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="fault_recreated"
                        value={option.id}
                        checked={formData.fault_recreated === option.id}
                        onChange={(e) => setFormData(prev => ({ ...prev, fault_recreated: e.target.value }))}
                        className="text-amber-600"
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
                <Textarea
                  value={formData.fault_recreation_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, fault_recreation_notes: e.target.value }))}
                  placeholder="Describe how/when fault was recreated (e.g., 'Fault appeared after 10 mins of driving at operating temp')"
                  className="min-h-[60px] text-sm"
                />
              </div>

              {/* Equipment Used - Collapsible Section */}
              <div
                className={`border rounded-lg overflow-hidden ${batteryGateBlocks ? "pointer-events-none opacity-40" : ""}`}
                data-testid="diag-section-equipment"
              >
                <button
                  type="button"
                  onClick={() => setExpandedCategories(prev => ({ ...prev, equipment: !prev.equipment }))}
                  className="w-full p-3 bg-slate-100 flex items-center justify-between hover:bg-slate-200 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-700">Diagnostic Equipment Used</span>
                    {formData.equipment_used.length > 0 && (
                      <Badge className="bg-purple-500 text-white text-xs">{formData.equipment_used.length} selected</Badge>
                    )}
                  </div>
                  {expandedCategories.equipment ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedCategories.equipment && workflowConfig?.equipment && (
                  <div className="p-4 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {Object.entries(workflowConfig.equipment).map(([key, eq]) => (
                      <label key={key} className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-all ${
                        formData.equipment_used.includes(key) ? 'bg-purple-50 border-purple-400' : 'hover:bg-slate-50'
                      }`}>
                        <Checkbox
                          checked={formData.equipment_used.includes(key)}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              equipment_used: checked 
                                ? [...prev.equipment_used, key]
                                : prev.equipment_used.filter(e => e !== key)
                            }));
                          }}
                        />
                        <div>
                          <span className="text-sm font-medium">{eq.label}</span>
                          <p className="text-xs text-slate-500">{eq.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Tests Performed - Collapsible Categories */}
              <div
                className={`border rounded-lg overflow-hidden ${batteryGateBlocks ? "pointer-events-none opacity-40" : ""}`}
                data-testid="diag-section-tests"
              >
                <button
                  type="button"
                  onClick={() => setExpandedCategories(prev => ({ ...prev, tests: !prev.tests }))}
                  className="w-full p-3 bg-slate-100 flex items-center justify-between hover:bg-slate-200 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Search className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-700">Tests & Examinations Performed</span>
                    {formData.tests_performed.length > 0 && (
                      <Badge className="bg-green-500 text-white text-xs">{formData.tests_performed.length} tests</Badge>
                    )}
                  </div>
                  {expandedCategories.tests ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedCategories.tests && workflowConfig?.test_categories && (
                  <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                    {Object.entries(workflowConfig.test_categories).map(([catId, category]) => {
                      // Count tests selected in this category
                      const testsInCategory = formData.tests_performed.filter(t => t.category_id === catId).length;
                      const hasTests = testsInCategory > 0;
                      
                      return (
                        <div key={catId} className={`border rounded-lg overflow-hidden ${hasTests ? 'border-green-400 ring-1 ring-green-200' : ''}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }))}
                            className={`w-full p-2 flex items-center justify-between text-sm transition-colors ${
                              hasTests 
                                ? 'bg-green-50 hover:bg-green-100' 
                                : 'bg-slate-50 hover:bg-slate-100'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${hasTests ? 'text-green-700' : 'text-slate-700'}`}>
                                {category.label}
                              </span>
                              {hasTests && (
                                <Badge className="bg-green-500 text-white text-xs px-1.5 py-0">
                                  {testsInCategory}
                                </Badge>
                              )}
                            </div>
                            {expandedCategories[catId] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          {expandedCategories[catId] && (
                            <div className="p-2 space-y-2">
                              {category.tests.filter(test => {
                                // iter197m — hide the battery gateway test from
                                // the regular category list (it's now owned by
                                // the dedicated gateway card at the top).
                                if (requiresBatteryGateway && batteryGatewayCfg && test.id === batteryGatewayCfg.test_id) {
                                  return false;
                                }
                                return true;
                              }).map(test => {
                                const isSelected = formData.tests_performed.some(t => t.test_id === test.id);
                                const currentTest = formData.tests_performed.find(t => t.test_id === test.id);
                                return (
                                  <div key={test.id} className={`p-2 rounded border ${isSelected ? 'bg-green-50 border-green-300' : ''}`}>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={(checked) => {
                                          setFormData(prev => ({
                                            ...prev,
                                            tests_performed: checked
                                              ? [...prev.tests_performed, { test_id: test.id, category_id: catId, reading: '' }]
                                              : prev.tests_performed.filter(t => t.test_id !== test.id)
                                          }));
                                        }}
                                      />
                                      <div className="flex-1">
                                        <span className="text-sm font-medium">{test.label}</span>
                                        <p className="text-xs text-slate-500">{test.description}</p>
                                      </div>
                                    </label>
                                    {isSelected && test.has_reading && (
                                      <div className="mt-2 ml-6">
                                        <Input
                                          type="text"
                                          placeholder={test.reading_label || "Enter reading/result"}
                                          value={currentTest?.reading || ''}
                                          onChange={(e) => {
                                            setFormData(prev => ({
                                              ...prev,
                                              tests_performed: prev.tests_performed.map(t => 
                                                t.test_id === test.id ? { ...t, reading: e.target.value } : t
                                              )
                                            }));
                                          }}
                                          className="text-sm h-8"
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Systems Ruled Out */}
              <div
                className={`border rounded-lg overflow-hidden ${batteryGateBlocks ? "pointer-events-none opacity-40" : ""}`}
                data-testid="diag-section-ruled-out"
              >
                <button
                  type="button"
                  onClick={() => setExpandedCategories(prev => ({ ...prev, ruledOut: !prev.ruledOut }))}
                  className="w-full p-3 bg-slate-100 flex items-center justify-between hover:bg-slate-200 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-700">Systems Checked & Ruled Out</span>
                    {formData.ruled_out.length > 0 && (
                      <Badge className="bg-blue-500 text-white text-xs">{formData.ruled_out.length} items</Badge>
                    )}
                  </div>
                  {expandedCategories.ruledOut ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedCategories.ruledOut && workflowConfig?.ruling_out && (
                  <div className="p-4 grid grid-cols-1 gap-2">
                    {workflowConfig.ruling_out.map(item => (
                      <label key={item.id} className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                        formData.ruled_out.includes(item.id) ? 'bg-blue-50 border-blue-400' : 'hover:bg-slate-50'
                      }`}>
                        <Checkbox
                          checked={formData.ruled_out.includes(item.id)}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              ruled_out: checked 
                                ? [...prev.ruled_out, item.id]
                                : prev.ruled_out.filter(r => r !== item.id)
                            }));
                          }}
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Diagnostic Actions Taken */}
              <div>
                <Label htmlFor="actions_taken" className="font-semibold">
                  Additional Diagnostic Actions / Summary *
                </Label>
                <p className="text-sm text-slate-500 mb-2">
                  Describe any additional actions taken or provide a summary of your diagnostic process
                </p>
                <Textarea
                  id="actions_taken"
                  value={formData.actions_taken}
                  onChange={(e) => setFormData(prev => ({ ...prev, actions_taken: e.target.value }))}
                  placeholder="e.g. Connected diagnostic scanner, checked fault codes, inspected engine bay for visible leaks, performed smoke test..."
                  className="min-h-[120px]"
                  data-testid="diag-actions-input"
                />
              </div>

              {/* Additional Observations */}
              <div>
                <Label htmlFor="additional_obs" className="font-semibold">
                  Additional Observations
                </Label>
                <p className="text-sm text-slate-500 mb-2">
                  Note any other relevant observations made during the diagnostic process
                </p>
                <Textarea
                  id="additional_obs"
                  value={formData.additional_observations}
                  onChange={(e) => setFormData(prev => ({ ...prev, additional_observations: e.target.value }))}
                  placeholder="e.g. Customer mentioned issue is worse in cold weather, noticed aftermarket modifications..."
                  className="min-h-[80px]"
                />
              </div>

              {/* ═══════════════ DTC CODES SECTION ═══════════════ */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedCategories(prev => ({ ...prev, dtc: !prev.dtc }))}
                  className="w-full p-3 bg-red-50 flex items-center justify-between hover:bg-red-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-red-800">DTC Fault Codes</span>
                    {formData.dtc_codes.length > 0 && (
                      <Badge className="bg-red-500 text-white text-xs">{formData.dtc_codes.length} codes</Badge>
                    )}
                  </div>
                  {expandedCategories.dtc ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedCategories.dtc && (
                  <div className="p-4 space-y-3">
                    {formData.dtc_codes.map((dtc, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-slate-50 p-2 rounded">
                        <Input
                          placeholder="Code (e.g. P0300)"
                          value={dtc.code}
                          onChange={(e) => {
                            const newCodes = [...formData.dtc_codes];
                            newCodes[idx].code = e.target.value.toUpperCase();
                            setFormData(prev => ({ ...prev, dtc_codes: newCodes }));
                          }}
                          className="col-span-2 text-sm font-mono"
                        />
                        <Input
                          placeholder="Description"
                          value={dtc.description}
                          onChange={(e) => {
                            const newCodes = [...formData.dtc_codes];
                            newCodes[idx].description = e.target.value;
                            setFormData(prev => ({ ...prev, dtc_codes: newCodes }));
                          }}
                          className="col-span-5 text-sm"
                        />
                        <select
                          value={dtc.status || 'stored'}
                          onChange={(e) => {
                            const newCodes = [...formData.dtc_codes];
                            newCodes[idx].status = e.target.value;
                            setFormData(prev => ({ ...prev, dtc_codes: newCodes }));
                          }}
                          className="col-span-2 text-sm border rounded p-2"
                        >
                          <option value="stored">Stored</option>
                          <option value="current">Current</option>
                          <option value="intermittent">Intermittent</option>
                        </select>
                        <Input
                          placeholder="Module"
                          value={dtc.module}
                          onChange={(e) => {
                            const newCodes = [...formData.dtc_codes];
                            newCodes[idx].module = e.target.value;
                            setFormData(prev => ({ ...prev, dtc_codes: newCodes }));
                          }}
                          className="col-span-2 text-sm"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              dtc_codes: prev.dtc_codes.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="col-span-1 text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        dtc_codes: [...prev.dtc_codes, { code: '', description: '', status: 'stored', module: '' }]
                      }))}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add DTC Code
                    </Button>
                  </div>
                )}
              </div>

              {/* ═══════════════ LIVE DATA READINGS ═══════════════ */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedCategories(prev => ({ ...prev, liveData: !prev.liveData }))}
                  className="w-full p-3 bg-cyan-50 flex items-center justify-between hover:bg-cyan-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-cyan-600" />
                    <span className="font-semibold text-cyan-800">Live Data Readings</span>
                    {formData.live_data_readings.length > 0 && (
                      <Badge className="bg-cyan-500 text-white text-xs">{formData.live_data_readings.length} readings</Badge>
                    )}
                  </div>
                  {expandedCategories.liveData ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedCategories.liveData && (
                  <div className="p-4 space-y-3">
                    {formData.live_data_readings.map((reading, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-slate-50 p-2 rounded">
                        <Input
                          placeholder="Parameter (e.g. Boost Pressure)"
                          value={reading.parameter}
                          onChange={(e) => {
                            const newReadings = [...formData.live_data_readings];
                            newReadings[idx].parameter = e.target.value;
                            setFormData(prev => ({ ...prev, live_data_readings: newReadings }));
                          }}
                          className="col-span-4 text-sm"
                        />
                        <Input
                          placeholder="Value"
                          value={reading.value}
                          onChange={(e) => {
                            const newReadings = [...formData.live_data_readings];
                            newReadings[idx].value = e.target.value;
                            setFormData(prev => ({ ...prev, live_data_readings: newReadings }));
                          }}
                          className="col-span-3 text-sm"
                        />
                        <Input
                          placeholder="Unit"
                          value={reading.unit}
                          onChange={(e) => {
                            const newReadings = [...formData.live_data_readings];
                            newReadings[idx].unit = e.target.value;
                            setFormData(prev => ({ ...prev, live_data_readings: newReadings }));
                          }}
                          className="col-span-2 text-sm"
                        />
                        <select
                          value={reading.status || 'normal'}
                          onChange={(e) => {
                            const newReadings = [...formData.live_data_readings];
                            newReadings[idx].status = e.target.value;
                            setFormData(prev => ({ ...prev, live_data_readings: newReadings }));
                          }}
                          className="col-span-2 text-sm border rounded p-2"
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                        </select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              live_data_readings: prev.live_data_readings.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="col-span-1 text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        live_data_readings: [...prev.live_data_readings, { parameter: '', value: '', unit: '', status: 'normal' }]
                      }))}
                      className="text-cyan-600 border-cyan-300 hover:bg-cyan-50"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Reading
                    </Button>
                  </div>
                )}
              </div>

              {/* ═══════════════ RECTIFICATION REQUIREMENTS ═══════════════ */}
              <div className="border-2 border-orange-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedCategories(prev => ({ ...prev, parts: !prev.parts }))}
                  className="w-full p-3 bg-orange-50 flex items-center justify-between hover:bg-orange-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-600" />
                    <span className="font-semibold text-orange-800">Rectification Requirements</span>
                    <Badge className="bg-orange-200 text-orange-800 text-xs">Parts & Labour</Badge>
                  </div>
                  {expandedCategories.parts ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedCategories.parts && (
                  <div className="p-4 space-y-4">
                    {/* Confirmed Failed Parts */}
                    <div>
                      <Label className="text-sm font-semibold text-orange-700 mb-2 block">Confirmed Failed Parts</Label>
                      {formData.confirmed_failed_parts.map((part, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-start mb-2">
                          <Input
                            placeholder="Part name"
                            value={part.part_name}
                            onChange={(e) => {
                              const newParts = [...formData.confirmed_failed_parts];
                              newParts[idx].part_name = e.target.value;
                              setFormData(prev => ({ ...prev, confirmed_failed_parts: newParts }));
                            }}
                            className="col-span-4 text-sm"
                          />
                          <Input
                            placeholder="Part number (optional)"
                            value={part.part_number}
                            onChange={(e) => {
                              const newParts = [...formData.confirmed_failed_parts];
                              newParts[idx].part_number = e.target.value;
                              setFormData(prev => ({ ...prev, confirmed_failed_parts: newParts }));
                            }}
                            className="col-span-3 text-sm"
                          />
                          <Input
                            placeholder="Reason for replacement"
                            value={part.reason}
                            onChange={(e) => {
                              const newParts = [...formData.confirmed_failed_parts];
                              newParts[idx].reason = e.target.value;
                              setFormData(prev => ({ ...prev, confirmed_failed_parts: newParts }));
                            }}
                            className="col-span-4 text-sm"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                confirmed_failed_parts: prev.confirmed_failed_parts.filter((_, i) => i !== idx)
                              }));
                            }}
                            className="col-span-1 text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          confirmed_failed_parts: [...prev.confirmed_failed_parts, { part_name: '', part_number: '', reason: '' }]
                        }))}
                        className="text-orange-600 border-orange-300"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Confirmed Part
                      </Button>
                    </div>
                    
                    {/* Suspected Not Confirmed */}
                    <div>
                      <Label className="text-sm font-semibold text-amber-700 mb-2 block">Suspected (Not Confirmed)</Label>
                      <Textarea
                        value={formData.suspected_not_confirmed.join('\n')}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          suspected_not_confirmed: e.target.value.split('\n').filter(s => s.trim())
                        }))}
                        placeholder="List parts suspected but not confirmed failed (one per line)"
                        className="text-sm min-h-[60px]"
                      />
                    </div>

                    {/* Labour Information */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Diagnostic Time (hours)</Label>
                        <Input
                          type="number"
                          step="0.25"
                          value={formData.diagnostic_time_hours}
                          onChange={(e) => setFormData(prev => ({ ...prev, diagnostic_time_hours: e.target.value }))}
                          placeholder="e.g. 1.5"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Estimated Repair Time (hours)</Label>
                        <Input
                          type="number"
                          step="0.25"
                          value={formData.estimated_repair_time_hours}
                          onChange={(e) => setFormData(prev => ({ ...prev, estimated_repair_time_hours: e.target.value }))}
                          placeholder="e.g. 2.0"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ═══════════════ FAULT STATUS (MANDATORY) ═══════════════ */}
              <div className="bg-red-50 p-4 rounded-lg border-2 border-red-300">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-red-800">Fault Status * (MANDATORY)</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {workflowConfig?.fault_status_options?.map(option => (
                    <label
                      key={option.id}
                      className={`flex items-center gap-2 p-3 rounded border cursor-pointer ${
                        formData.fault_status === option.id
                          ? 'bg-red-100 border-red-400 ring-2 ring-red-300'
                          : 'bg-white hover:bg-red-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="fault_status"
                        value={option.id}
                        checked={formData.fault_status === option.id}
                        onChange={(e) => setFormData(prev => ({ ...prev, fault_status: e.target.value }))}
                        className="text-red-600"
                      />
                      <div>
                        <span className="text-sm font-medium block">{option.label}</span>
                        <span className="text-xs text-slate-500">{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* ═══════════════ POST-REPAIR VERIFICATION ═══════════════ */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-800">Post-Repair Verification Requirements</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                  {[
                    { key: 'verification_road_test', label: 'Road Test Required' },
                    { key: 'verification_live_data', label: 'Live Data Confirmation' },
                    { key: 'verification_dtc_rescan', label: 'DTC Erase & Re-scan' },
                    { key: 'verification_regen_reset', label: 'Regeneration Reset' },
                    { key: 'verification_adaptation_reset', label: 'Adaptation Reset' },
                  ].map(item => (
                    <label
                      key={item.key}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                        formData[item.key] ? 'bg-green-100 border-green-400' : 'bg-white hover:bg-green-50'
                      }`}
                    >
                      <Checkbox
                        checked={formData[item.key]}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, [item.key]: checked }))}
                      />
                      <span className="text-sm">{item.label}</span>
                    </label>
                  ))}
                </div>
                <Input
                  value={formData.verification_other}
                  onChange={(e) => setFormData(prev => ({ ...prev, verification_other: e.target.value }))}
                  placeholder="Other verification requirements..."
                  className="text-sm"
                />
              </div>

              {/* Evidence Photos */}
              <div>
                <Label className="font-semibold">Evidence Photos {['fault_identified', 'further_investigation'].includes(formData.outcome_type) ? '*' : ''}</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Capture photos of fault codes, damaged components, or diagnostic findings. Tap photo to annotate.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {formData.evidence_photos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border group">
                      <img src={photo} alt={`Evidence ${idx + 1}`} className="w-full h-full object-cover" />
                      {/* Edit annotation button */}
                      <button
                        type="button"
                        onClick={() => handleEditPhotoAnnotation(idx)}
                        className="absolute bottom-1 left-1 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit annotation"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {formData.evidence_photos.length < 6 && (
                    <label className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 transition-colors">
                      <Camera className="w-8 h-8 text-slate-400 mb-1" />
                      <span className="text-xs text-slate-500">Add Photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handlePhotoCapture}
                        className="hidden"
                        data-testid="diag-photo-input"
                      />
                    </label>
                  )}
                </div>
                <p className="text-xs text-slate-400">{formData.evidence_photos.length}/6 photos</p>
                {['fault_identified', 'further_investigation'].includes(formData.outcome_type) && formData.evidence_photos.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">⚠️ At least one evidence photo is required for this outcome type</p>
                )}
              </div>

              {/* Diagnostic Outcome Type - MANDATORY */}
              <div className="border-2 border-purple-400 rounded-lg p-4 bg-purple-50 space-y-4">
                <div>
                  <Label className="font-semibold text-purple-800">
                    Diagnostic Outcome * (MANDATORY)
                  </Label>
                  <p className="text-sm text-purple-600 mb-3">
                    Select the outcome of your diagnostic investigation
                  </p>
                  
                  {/* Outcome Type Buttons */}
                  <div className="grid grid-cols-1 gap-2">
                    {DIAGNOSTIC_OUTCOME_TYPES.map(outcome => (
                      <button
                        key={outcome.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          outcome_type: outcome.value,
                          conclusion: outcome.value !== prev.outcome_type ? "" : prev.conclusion,
                          abandoned_reason: ""
                        }))}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.outcome_type === outcome.value 
                            ? 'border-purple-600 bg-purple-100 text-purple-900' 
                            : 'border-gray-200 bg-white hover:border-purple-300'
                        }`}
                        data-testid={`outcome-${outcome.value}`}
                      >
                        <span className="font-medium">{outcome.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Conditional fields based on outcome type */}
                {formData.outcome_type === "fault_identified" && (
                  <div>
                    <Label htmlFor="conclusion" className="font-semibold text-purple-800">
                      Describe the Fault *
                    </Label>
                    <p className="text-sm text-purple-600 mb-2">
                      State the single, factual conclusion from your diagnostic investigation
                    </p>
                    <Textarea
                      id="conclusion"
                      value={formData.conclusion}
                      onChange={(e) => setFormData(prev => ({ ...prev, conclusion: e.target.value }))}
                      placeholder="e.g. Confirmed oil leak from front crankshaft seal. Seal shows signs of wear and hardening. Replacement required."
                      className="min-h-[100px] bg-white"
                      data-testid="diag-conclusion-input"
                    />
                  </div>
                )}
                
                {formData.outcome_type === "no_fault" && (
                  <div>
                    <Label htmlFor="conclusion" className="font-semibold text-purple-800">
                      Additional Notes (Optional)
                    </Label>
                    <p className="text-sm text-purple-600 mb-2">
                      Add any additional context about what was tested
                    </p>
                    <Textarea
                      id="conclusion"
                      value={formData.conclusion}
                      onChange={(e) => setFormData(prev => ({ ...prev, conclusion: e.target.value }))}
                      placeholder="e.g. Performed full diagnostic scan, inspected all related components, road tested vehicle - all systems operating within normal parameters."
                      className="min-h-[80px] bg-white"
                      data-testid="diag-no-fault-notes"
                    />
                  </div>
                )}
                
                {formData.outcome_type === "further_investigation" && (
                  <div>
                    <Label htmlFor="conclusion" className="font-semibold text-purple-800">
                      What Further Investigation is Required? *
                    </Label>
                    <p className="text-sm text-purple-600 mb-2">
                      Describe what additional work is needed to identify the fault
                    </p>
                    <Textarea
                      id="conclusion"
                      value={formData.conclusion}
                      onChange={(e) => setFormData(prev => ({ ...prev, conclusion: e.target.value }))}
                      placeholder="e.g. Intermittent fault not present during inspection. Recommend leaving vehicle for extended test or fitting data logger to capture fault when it occurs."
                      className="min-h-[100px] bg-white"
                      data-testid="diag-further-investigation-input"
                    />
                    <p className="text-xs text-amber-600 mt-2 font-medium">
                      ⚠️ This outcome indicates the diagnostic was inconclusive and further work cannot be authorised at this time
                    </p>
                  </div>
                )}
                
                {formData.outcome_type === "abandoned" && (
                  <div>
                    <Label htmlFor="abandoned_reason" className="font-semibold text-purple-800">
                      Reason for Abandonment *
                    </Label>
                    <p className="text-sm text-purple-600 mb-2">
                      Explain why the inspection was abandoned
                    </p>
                    <Textarea
                      id="abandoned_reason"
                      value={formData.abandoned_reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, abandoned_reason: e.target.value }))}
                      placeholder="e.g. Customer declined further investigation due to cost. Vehicle unsafe to road test. Access to component not possible without extensive strip-down."
                      className="min-h-[80px] bg-white"
                      data-testid="diag-abandoned-reason"
                    />
                  </div>
                )}
                
                <p className="text-xs text-purple-500">
                  This outcome will be processed by AI to generate a polished diagnostic report
                </p>
              </div>

              {/* iter211f3 — Tech-side rectification picker.
                  When the tech already knows the fix (e.g. failed battery
                  load test → battery replacement), they pick from the
                  6-code catalogue here and the yestimate arrives at FOH
                  already carrying the rectification JG. Skip via "None /
                  FOH will resolve" — the pricing-side prompt still
                  catches it later. */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-3" data-testid="diag-rectification-section">
                <div className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Recommended Rectification</h3>
                  <Badge className="bg-blue-100 text-blue-700 text-xs">Optional — pre-fills FOH pricing</Badge>
                </div>
                <p className="text-xs text-blue-700">
                  If you already know the fix, pick it here so FOH gets a pre-priced rectification instead of a blank quote.
                </p>
                <div className="flex gap-1 bg-white p-1 rounded-md border border-blue-200">
                  <button type="button" onClick={() => setRectMode("none")}
                    className={`flex-1 text-xs py-1.5 px-2 rounded font-medium transition-all ${
                      rectMode === "none" ? "bg-blue-500 text-white" : "text-blue-700 hover:bg-blue-100"
                    }`}
                    data-testid="diag-rect-mode-none"
                  >
                    None / FOH will resolve
                  </button>
                  <button type="button" onClick={() => { setRectMode("catalogue"); loadRectCatalogue(); }}
                    className={`flex-1 text-xs py-1.5 px-2 rounded font-medium transition-all ${
                      rectMode === "catalogue" ? "bg-blue-500 text-white" : "text-blue-700 hover:bg-blue-100"
                    }`}
                    data-testid="diag-rect-mode-catalogue"
                  >
                    Pick from catalogue
                  </button>
                  <button type="button" onClick={() => setRectMode("freetext")}
                    className={`flex-1 text-xs py-1.5 px-2 rounded font-medium transition-all ${
                      rectMode === "freetext" ? "bg-blue-500 text-white" : "text-blue-700 hover:bg-blue-100"
                    }`}
                    data-testid="diag-rect-mode-freetext"
                  >
                    Free text
                  </button>
                </div>

                {rectMode === "catalogue" && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-blue-900">Rectification</Label>
                    <select
                      value={formData.rectification_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, rectification_code: e.target.value, rectification_variant_code: "" }))}
                      className="w-full p-2 border border-blue-300 rounded text-sm bg-white"
                      data-testid="diag-rect-code-select"
                    >
                      <option value="">— Pick a rectification —</option>
                      {rectCatalogue.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                    {activeRectEntry && activeRectEntry.variants.length > 0 && (
                      <>
                        <Label className="text-xs font-semibold text-blue-900 mt-2 block">Option</Label>
                        <div className="grid grid-cols-1 gap-1">
                          {activeRectEntry.variants.map((v) => (
                            <button key={v.code} type="button"
                              onClick={() => setFormData(prev => ({ ...prev, rectification_variant_code: v.code }))}
                              className={`text-left text-xs py-1.5 px-2 rounded border transition-all ${
                                formData.rectification_variant_code === v.code
                                  ? "bg-blue-500 text-white border-blue-600"
                                  : "bg-white border-blue-200 hover:border-blue-400"
                              }`}
                              data-testid={`diag-rect-variant-${v.code}`}
                            >
                              {v.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    {activeRectEntry && activeRectEntry.coding_required && (
                      <p className="text-xs text-blue-700 italic">ⓘ Coding / ECU registration required — labour includes coding time.</p>
                    )}
                  </div>
                )}

                {rectMode === "freetext" && (
                  <div className="space-y-2 text-sm">
                    <div>
                      <Label className="text-xs font-semibold text-blue-900">Rectification title</Label>
                      <Input value={formData.rectification_freetext_title}
                        onChange={(e) => setFormData(prev => ({ ...prev, rectification_freetext_title: e.target.value }))}
                        placeholder="e.g. Rear wiring loom repair"
                        className="text-sm"
                        data-testid="diag-rect-freetext-title"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs font-semibold text-blue-900">Parts £ (inc VAT)</Label>
                        <Input type="number" step="0.01" min="0"
                          value={formData.rectification_freetext_parts_price}
                          onChange={(e) => setFormData(prev => ({ ...prev, rectification_freetext_parts_price: e.target.value }))}
                          placeholder="0.00" className="text-sm"
                          data-testid="diag-rect-freetext-parts-price"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-blue-900">Labour hours</Label>
                        <Input type="number" step="0.1" min="0"
                          value={formData.rectification_freetext_labour_hours}
                          onChange={(e) => setFormData(prev => ({ ...prev, rectification_freetext_labour_hours: e.target.value }))}
                          placeholder="0.0" className="text-sm"
                          data-testid="diag-rect-freetext-labour-hours"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-blue-900">Customer-facing note (optional)</Label>
                      <Textarea value={formData.rectification_freetext_customer_note}
                        onChange={(e) => setFormData(prev => ({ ...prev, rectification_freetext_customer_note: e.target.value }))}
                        placeholder="What this repair does, in plain English."
                        className="text-sm min-h-[60px]"
                        data-testid="diag-rect-freetext-note"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-3">
                <Button 
                  type="submit" 
                  className="flex-1 bg-purple-600 hover:bg-purple-700" 
                  disabled={submitting}
                  data-testid="submit-diagnostic-btn"
                >
                  {submitting ? "Processing..." : "Submit Diagnostic"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate(`/app/tech/job/${id}/start`)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== ITEM DETAIL PAGE ==============
const ItemDetailPage = () => {
  const { id, itemId } = useParams();
  const [item, setItem] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [job, setJob] = useState(null);  // Store job data for vehicle info
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [commonIssues, setCommonIssues] = useState({ amber: [], red: [] });
  const navigate = useNavigate();
  const errorRef = useRef(null);  // Ref for scrolling to error

  // Form state
  const [status, setStatus] = useState(null);
  const [notes, setNotes] = useState("");
  const [measurementValue, setMeasurementValue] = useState("");
  const [secondaryMeasurementValue, setSecondaryMeasurementValue] = useState("");  // For items like brake fluid with moisture content
  const [photos, setPhotos] = useState([]);
  const [testerPhoto, setTesterPhoto] = useState(null);
  const [testerPhotoLoading, setTesterPhotoLoading] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState([]);  // Changed to array for multi-select
  
  // Photo annotation state
  const [annotatingPhoto, setAnnotatingPhoto] = useState(null);
  const [annotatingIndex, setAnnotatingIndex] = useState(null);
  
  // Disc specs state
  const [discNewThickness, setDiscNewThickness] = useState("");
  const [discMinThickness, setDiscMinThickness] = useState("");
  const [discCondition, setDiscCondition] = useState("");
  
  // Disc specs search state
  const [discSearchQuery, setDiscSearchQuery] = useState("");
  const [discSearchResults, setDiscSearchResults] = useState([]);
  const [discSearchLoading, setDiscSearchLoading] = useState(false);
  const [showDiscSearch, setShowDiscSearch] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  
  // Pad wear percentage (calculated)
  const [wearPercentage, setWearPercentage] = useState(null);
  
  // Tyre percent worn (manual entry)
  const [tyrePercentWorn, setTyrePercentWorn] = useState("");
  
  // Tyre size and brand (mandatory for tyre items)
  const [tyreSize, setTyreSize] = useState("");
  const [tyreBrand, setTyreBrand] = useState("");
  // Wiper blade inspection
  const [wiperBladeType, setWiperBladeType] = useState("");
  // Wiper & Washer unified sub-component data
  const [wiperData, setWiperData] = useState({
    front_left: { status: "", blade_type: "", issues: [] },
    front_right: { status: "", blade_type: "", issues: [] },
    rear: { status: "", blade_type: "", issues: [], not_fitted: false },
    washers: { status: "", issues: [] },
    arm: { status: "", issues: [] },
  });
  
  // Tyre copy modal - to copy measurements to other tyres
  const [tyreCopyModal, setTyreCopyModal] = useState({ show: false, measurementValue: '', tyreSize: '', percentWorn: '' });
  const [copyingTyres, setCopyingTyres] = useState(false);
  
  // Side specification for suspension components (NS = Nearside/Left, OS = Offside/Right)
  const [affectedSide, setAffectedSide] = useState("");
  const [affectedLocations, setAffectedLocations] = useState([]);  // multi-select corner/side picker (e.g. brake hoses)
  const [affectedPosition, setAffectedPosition] = useState("");  // inner / outer / both (for CV joints, track rod ends)
  
  // Multi-position selection for items like indicators
  const [affectedPositions, setAffectedPositions] = useState([]);
  
  // ============== DANGEROUS DESIGNATION STATE ==============
  // Compliance-grade safety classification fields
  const [dangerCategories, setDangerCategories] = useState([]);  // Selected danger category IDs
  const [dangerReasons, setDangerReasons] = useState([]);        // Selected structured reasons
  const [dangerousConfirmed, setDangerousConfirmed] = useState(false);  // Confirmation checkbox for suspension/steering
  const [fleetComplianceRef, setFleetComplianceRef] = useState("");     // Optional fleet compliance reference

  // ============== BATTERY TEST ENHANCED STATE ==============
  const [batteryVoltage, setBatteryVoltage] = useState("");
  const [measuredCca, setMeasuredCca] = useState("");
  const [ratedCca, setRatedCca] = useState("");
  const [healthPercent, setHealthPercent] = useState("");
  const [healthPercentManual, setHealthPercentManual] = useState(false);  // User manually entered vs calculated
  // Battery replacement fields
  const [batteryTypeRequired, setBatteryTypeRequired] = useState("");
  const [batteryRegistrationRequired, setBatteryRegistrationRequired] = useState("");
  const [batteryTechnicianNote, setBatteryTechnicianNote] = useState("");

  // ============== COOLANT ASSESSMENT ENHANCED STATE ==============
  const [coolantStrengthInputType, setCoolantStrengthInputType] = useState("");
  const [coolantBallsFloating, setCoolantBallsFloating] = useState("");
  const [coolantFreezeProtection, setCoolantFreezeProtection] = useState("");
  const [coolantLevel, setCoolantLevel] = useState("");
  const [coolantCondition, setCoolantCondition] = useState("");
  const [coolantNotes, setCoolantNotes] = useState("");
  const [coolantSuggestedStatus, setCoolantSuggestedStatus] = useState(null);

  // ============== BRAKE FLUID ASSESSMENT ENHANCED STATE ==============
  const [brakeFluidMoisture, setBrakeFluidMoisture] = useState("");
  const [brakeFluidBoilingPoint, setBrakeFluidBoilingPoint] = useState("");
  const [brakeFluidVisualCondition, setBrakeFluidVisualCondition] = useState("");
  const [brakeFluidServiceHistory, setBrakeFluidServiceHistory] = useState("");
  const [brakeFluidLastReplacementDate, setBrakeFluidLastReplacementDate] = useState("");
  const [brakeFluidLastReplacementMileage, setBrakeFluidLastReplacementMileage] = useState("");
  const [brakeFluidNotes, setBrakeFluidNotes] = useState("");
  const [brakeFluidCalculatedStatus, setBrakeFluidCalculatedStatus] = useState(null);

  // ============== BRAKE PAD WEAR SENSOR STATE ==============
  const [wearSensorRequired, setWearSensorRequired] = useState(false);

  // ============== WHEEL TORQUE CAPTURE STATE ==============
  const [torqueApplied, setTorqueApplied] = useState("");         // Selected torque value
  const [torqueIsManualEntry, setTorqueIsManualEntry] = useState(false);  // Whether "Other" selected
  const [torqueManualValue, setTorqueManualValue] = useState("");  // Manual entry value
  const [torqueDeviationReason, setTorqueDeviationReason] = useState("");  // Reason for deviation
  const [torqueUnable, setTorqueUnable] = useState(false);         // "Unable to torque" checked
  const [torqueUnableReason, setTorqueUnableReason] = useState(""); // Reason for unable
  const [torqueUnableToCheck, setTorqueUnableToCheck] = useState(false);  // "Unable to Check/Access"
  const [torqueUnableToCheckReason, setTorqueUnableToCheckReason] = useState(""); // Reason for unable to check
  const [torqueManufacturerSpec, setTorqueManufacturerSpec] = useState(null);  // Manufacturer spec
  const [torqueSpecSource, setTorqueSpecSource] = useState("");    // Source of spec
  const [torqueSpecLoading, setTorqueSpecLoading] = useState(false);
  const [torqueOutOfRangeConfirmed, setTorqueOutOfRangeConfirmed] = useState(false);  // Warning confirmation
  const [torqueConfidence, setTorqueConfidence] = useState("");    // high/medium/low
  const [torqueRangeMin, setTorqueRangeMin] = useState(null);     // Range min for estimates
  const [torqueRangeMax, setTorqueRangeMax] = useState(null);     // Range max for estimates
  const [torqueConfirmed, setTorqueConfirmed] = useState(false);  // Tick: "Torqued to spec"
  const [torqueSelectionSource, setTorqueSelectionSource] = useState("");  // "manufacturer" | "quick_select" | "manual"
  // Quick-select torque values — universal MVP set covering passenger, premium, and commercial
  // Architecture supports future dynamic sets by vehicle class
  const TORQUE_QUICK_SELECT = [100, 110, 120, 140, 160, 180];
  // Future: dynamic sets per vehicle class (not hardcoded into UI)
  // const TORQUE_BY_VEHICLE_CLASS = {
  //   passenger: [90, 100, 110, 120, 140],
  //   suv_premium: [110, 120, 130, 140, 150],
  //   van_lcv: [130, 140, 160, 180, 200],
  // };

  // ============== TYRE PRESSURE CAPTURE STATE ==============
  const [pressureMeasured, setPressureMeasured] = useState({ fl: "", fr: "", rl: "", rr: "" });
  const [pressureAdjusted, setPressureAdjusted] = useState({ fl: "", fr: "", rl: "", rr: "" });
  const [pressureSpec, setPressureSpec] = useState({ front: null, rear: null, source: "" });
  const [pressureSpecLoading, setPressureSpecLoading] = useState(false);
  const [pressureUnableAdjust, setPressureUnableAdjust] = useState(false);
  const [pressureUnableReason, setPressureUnableReason] = useState("");
  const [pressureDeviationReason, setPressureDeviationReason] = useState("");
  const PRESSURE_POSITIONS = ["fl", "fr", "rl", "rr"];
  const PRESSURE_LABELS = { fl: "Front Left", fr: "Front Right", rl: "Rear Left", rr: "Rear Right" };

  // ============== AC GAS TYPE CAPTURE STATE ==============
  const [acGasType, setAcGasType] = useState("");                      // R134a or R1234yf
  const [acPerformance, setAcPerformance] = useState("");              // cold, weak, not_cold
  const [acFlags, setAcFlags] = useState({
    system_empty: false,
    compressor_not_engaging: false,
    noisy_operation: false
  });

  // ============== STATUS OVERRIDE STATE ==============
  const [statusOverride, setStatusOverride] = useState(null); // Tech override of auto-calculated status
  // MOT bridge — by_item_key map for the CURRENT MOT outcome on this job.
  // Used to block a "green" mark on items the very same MOT just flagged
  // unless the tech writes a short rectified-note.
  const [currentMotItemFlags, setCurrentMotItemFlags] = useState({});
  // Tech-provided short note explaining a green mark on an MOT-flagged item.
  const [motGreenOverrideNote, setMotGreenOverrideNote] = useState("");
  // Resolved bridge bucket for this exact VI item — or null when the
  // current MOT outcome has no findings against it.
  const motFlagsForThisItem = useMemo(() => {
    if (!item?.item_key) return null;
    const bucket = currentMotItemFlags?.[item.item_key];
    if (!bucket || !bucket.defects?.length) return null;
    return bucket;
  }, [currentMotItemFlags, item?.item_key]);

  // ============== LOCKING WHEEL KEY CAPTURE STATE ==============
  const [lockingKeyPresent, setLockingKeyPresent] = useState("");      // yes, no, na
  const [lockingKeyLocation, setLockingKeyLocation] = useState("");    // Glovebox, Boot, etc.
  const [lockingKeyNotes, setLockingKeyNotes] = useState("");          // Additional notes

  // ============== MEASUREMENT-BASED STATUS WARNING ==============
  const [statusWarning, setStatusWarning] = useState(null);  // Warning message when status conflicts with measurements

  // ============== SUSPENSION STRUCTURED REPAIR DATA ==============
  const [structuredRepairSchema, setStructuredRepairSchema] = useState(null);  // Schema from backend
  const [repairConstructionType, setRepairConstructionType] = useState("");
  const [repairMethod, setRepairMethod] = useState("");
  const [repairArmCondition, setRepairArmCondition] = useState("");
  const [repairRemovalRisk, setRepairRemovalRisk] = useState("standard");
  const [repairOppositeSide, setRepairOppositeSide] = useState("");
  const [repairAlignmentImplication, setRepairAlignmentImplication] = useState("");
  const [repairJointLevel, setRepairJointLevel] = useState("na");

  // Define suspension/steering items that need enhanced dangerous validation
  const SUSPENSION_STEERING_ITEMS = [
    "front_springs", "front_shock_absorbers", "front_top_mounts",
    "track_rod_ends", "ball_joints", "steering_rack", "cv_joints_boots",
    "anti_roll_bar_links", "rear_springs", "rear_shock_absorbers", 
    "rear_anti_roll_bar_links"
  ];

  // Enhanced capture items (non-suspension items with structured assessment)
  const ENHANCED_CAPTURE_ITEMS = [
    "wheel_bearings", "exhaust_system", "engine_transmission_leaks",
    "brake_hoses_lines", "handbrake_shoes", "drive_belts"
  ];
  const [enhancedItemSchema, setEnhancedItemSchema] = useState(null);
  const [enhancedItemData, setEnhancedItemData] = useState({});

  // Fetch structured repair schema when status changes to non-green for suspension items
  useEffect(() => {
    if (!item?.item_key) return;
    if (!SUSPENSION_STEERING_ITEMS.includes(item.item_key)) return;
    if (!status || status === 'green' || status === 'blue') return;
    // Fetch schema if not already loaded
    if (!structuredRepairSchema) {
      axios.get(`${API}/suspension/repair-schema/${item.item_key}`).then(schemaRes => {
        if (schemaRes.data?.supported) {
          setStructuredRepairSchema(schemaRes.data.schema);
          if (!repairAlignmentImplication && schemaRes.data.schema.alignment_default) {
            setRepairAlignmentImplication(schemaRes.data.schema.alignment_default);
          }
        }
      }).catch(() => {});
    }
  }, [item?.item_key, status]);

  // Fetch enhanced item schema for non-suspension items
  useEffect(() => {
    if (!item?.item_key) return;
    if (!ENHANCED_CAPTURE_ITEMS.includes(item.item_key)) {
      setEnhancedItemSchema(null);
      return;
    }
    if (!status || status === 'green' || status === 'blue') {
      setEnhancedItemSchema(null);
      return;
    }
    axios.get(`${API}/enhanced-item/schema/${item.item_key}`).then(res => {
      if (res.data?.supported) {
        setEnhancedItemSchema(res.data);
        // Pre-populate from existing data if item was previously saved
        if (item.enhanced_item_data) {
          setEnhancedItemData(item.enhanced_item_data);
        }
      } else {
        setEnhancedItemSchema(null);
      }
    }).catch(() => setEnhancedItemSchema(null));
  }, [item?.item_key, status]);

  useEffect(() => {
    // First fetch job data to get vehicle make/model
    axios.get(`${API}/jobs/${id}`).then(jobRes => {
      setJob(jobRes.data);
      // Auto-populate disc search query with vehicle make/model
      if (jobRes.data?.make && jobRes.data?.model) {
        setDiscSearchQuery(`${jobRes.data.make} ${jobRes.data.model}`);
      } else if (jobRes.data?.make) {
        setDiscSearchQuery(jobRes.data.make);
      }
    }).catch(err => {
      console.log("Could not fetch job for vehicle info:", err);
    });

    // MOT-VI bridge — pull current-MOT flags grouped by VI item_key so we
    // can block a "green" mark when this very test failed/advised the
    // matching item. Silent-fail: it's enrichment, never a blocker on
    // load. (Validation re-runs server-side on save.)
    axios.get(`${API}/jobs/${id}/mot-vi-flags`)
      .then(res => setCurrentMotItemFlags(res.data?.by_item_key || {}))
      .catch(() => setCurrentMotItemFlags({}));
    
    axios.get(`${API}/jobs/${id}/inspection`).then(res => {
      if (!res.data) {
        navigate(`/app/tech/job/${id}/start`);
        return;
      }
      setInspection(res.data);
      axios.get(`${API}/inspections/${res.data.id}/items/${itemId}`).then(itemRes => {
        const itemData = itemRes.data;
        setItem(itemData);
        // === DRAFT RESTORE PROMPT (#1) ===
        // If there's a localStorage draft saved AFTER the last DB save, offer to restore.
        try {
          const draftRaw = localStorage.getItem(`vi-draft-${itemId}`);
          if (draftRaw) {
            const draft = JSON.parse(draftRaw);
            const dbUpdatedAt = itemData.updated_at ? new Date(itemData.updated_at).getTime() : 0;
            if (draft.savedAt && draft.savedAt > dbUpdatedAt + 2000) {
              setDraftRestorePrompt(draft);
            } else {
              localStorage.removeItem(`vi-draft-${itemId}`);
            }
          }
        } catch (e) { /* draft corrupt — ignore */ }
        
        // Reset all form fields first
        setStatus(null);
        setNotes("");
        setMeasurementValue("");
        setSecondaryMeasurementValue("");
        setPhotos([]);
        setTesterPhoto(null);
        setTyrePercentWorn("");
        setTyreSize("");
        setTyreBrand("");
        setWiperBladeType("");
        setWiperData({
          front_left: { status: "", blade_type: "", issues: [] },
          front_right: { status: "", blade_type: "", issues: [] },
          rear: { status: "", blade_type: "", issues: [], not_fitted: false },
          washers: { status: "", issues: [] },
          arm: { status: "", issues: [] },
        });
        setAffectedSide("");
        setAffectedLocations([]);
        setAffectedPosition("");
        setAffectedPositions([]);
        setBatteryVoltage("");
        setMeasuredCca("");
        setRatedCca("");
        setHealthPercent("");
        // Battery replacement reset
        setBatteryTypeRequired("");
        setBatteryRegistrationRequired("");
        setBatteryTechnicianNote("");
        // Coolant assessment reset
        setCoolantStrengthInputType("");
        setCoolantBallsFloating("");
        setCoolantFreezeProtection("");
        setCoolantLevel("");
        setCoolantCondition("");
        setCoolantNotes("");
        setCoolantSuggestedStatus(null);
        // Brake fluid assessment reset
        setBrakeFluidMoisture("");
        setBrakeFluidBoilingPoint("");
        setBrakeFluidVisualCondition("");
        setBrakeFluidServiceHistory("");
        setBrakeFluidLastReplacementDate("");
        setBrakeFluidLastReplacementMileage("");
        setBrakeFluidNotes("");
        setBrakeFluidCalculatedStatus(null);
        // Brake pad wear sensor reset
        setWearSensorRequired(false);
        setHealthPercentManual(false);
        setDangerCategories([]);
        setDangerReasons([]);
        setDangerousConfirmed(false);
        setFleetComplianceRef("");
        // Wheel Torque reset
        setTorqueApplied("");
        setTorqueIsManualEntry(false);
        setTorqueManualValue("");
        setTorqueDeviationReason("");
        setTorqueUnable(false);
        setTorqueUnableReason("");
        setTorqueManufacturerSpec(null);
        setTorqueSpecSource("");
        setTorqueOutOfRangeConfirmed(false);
        setTorqueConfidence("");
        setTorqueRangeMin(null);
        setTorqueRangeMax(null);
        setTorqueConfirmed(false);
        setTorqueSelectionSource("");
        // Tyre Pressure reset
        setPressureMeasured({ fl: "", fr: "", rl: "", rr: "" });
        setPressureAdjusted({ fl: "", fr: "", rl: "", rr: "" });
        setPressureSpec({ front: null, rear: null, source: "" });
        setPressureUnableAdjust(false);
        setPressureUnableReason("");
        setPressureDeviationReason("");
        // Enhanced item data reset
        setEnhancedItemSchema(null);
        setEnhancedItemData({});
        // MOT green-block override note reset (re-populated below if persisted)
        setMotGreenOverrideNote("");
        
        // Populate form with existing data
        if (itemData.status) setStatus(itemData.status);
        if (itemData.notes) setNotes(itemData.notes);
        if (itemData.mot_green_override_note) setMotGreenOverrideNote(itemData.mot_green_override_note);
        if (itemData.measurement_value) setMeasurementValue(itemData.measurement_value.toString());
        if (itemData.secondary_measurement_value) setSecondaryMeasurementValue(itemData.secondary_measurement_value.toString());
        if (itemData.photo_urls?.length) setPhotos(itemData.photo_urls);
        if (itemData.tester_photo_url) setTesterPhoto(itemData.tester_photo_url);
        
        // Load tyre percent worn if applicable
        if (itemData.percent_worn !== null && itemData.percent_worn !== undefined) {
          setTyrePercentWorn(itemData.percent_worn.toString());
        }
        
        // Load tyre size and brand if applicable
        if (itemData.tyre_size) setTyreSize(itemData.tyre_size);
        if (itemData.tyre_brand) setTyreBrand(itemData.tyre_brand);
        if (itemData.wiper_blade_type) setWiperBladeType(itemData.wiper_blade_type);
        if (itemData.wiper_data) setWiperData(prev => ({ ...prev, ...itemData.wiper_data }));
        
        // Load affected side if applicable (for suspension components)
        if (itemData.affected_side) setAffectedSide(itemData.affected_side);
        // Hydrate multi-location list. Prefer the array; fall back to legacy single value.
        // ALWAYS normalise to uppercase so the toggle comparisons stay consistent across
        // saves (DB may store lowercase from side_options config, UI compares uppercase).
        if (Array.isArray(itemData.affected_locations) && itemData.affected_locations.length > 0) {
          setAffectedLocations(itemData.affected_locations.map(s => String(s).toUpperCase()));
        } else if (itemData.affected_side) {
          setAffectedLocations([String(itemData.affected_side).toUpperCase()]);
        }
        if (itemData.affected_position) setAffectedPosition(itemData.affected_position);
        // Hydrate tech_intent (#7)
        if (itemData.tech_intent) setTechIntent(itemData.tech_intent);
        // If status was already set on the saved record, treat it as user-set so
        // future measurement edits don't auto-mutate it (#4).
        if (itemData.status) setStatusUserSet(true);
        
        // Load affected positions if applicable (for multi-position items like indicators)
        if (itemData.affected_positions) setAffectedPositions(itemData.affected_positions);
        
        // Load structured repair data if applicable (suspension items)
        if (itemData.structured_repair_data) {
          const srd = itemData.structured_repair_data;
          if (srd.construction_type) setRepairConstructionType(srd.construction_type);
          if (srd.repair_method) setRepairMethod(srd.repair_method);
          if (srd.arm_condition) setRepairArmCondition(srd.arm_condition);
          if (srd.removal_risk) setRepairRemovalRisk(srd.removal_risk);
          if (srd.opposite_side_condition) setRepairOppositeSide(srd.opposite_side_condition);
          if (srd.alignment_implication) setRepairAlignmentImplication(srd.alignment_implication);
          if (srd.joint_level) setRepairJointLevel(srd.joint_level);
        }
        
        // Fetch structured repair schema for suspension items
        if (SUSPENSION_STEERING_ITEMS.includes(itemData.item_key)) {
          axios.get(`${API}/suspension/repair-schema/${itemData.item_key}`).then(schemaRes => {
            if (schemaRes.data?.supported) {
              setStructuredRepairSchema(schemaRes.data.schema);
              // Set alignment default if not already set
              if (!itemData.structured_repair_data?.alignment_implication && schemaRes.data.schema.alignment_default) {
                setRepairAlignmentImplication(schemaRes.data.schema.alignment_default);
              }
            }
          }).catch(() => {});
        }
        
        // Load enhanced item data if applicable (non-suspension enhanced items)
        if (itemData.enhanced_item_data) {
          setEnhancedItemData(itemData.enhanced_item_data);
        }
        
        // Load disc specs if applicable
        if (itemData.disc_new_thickness) setDiscNewThickness(itemData.disc_new_thickness.toString());
        if (itemData.disc_min_thickness) setDiscMinThickness(itemData.disc_min_thickness.toString());
        if (itemData.disc_condition) setDiscCondition(itemData.disc_condition);
        
        // Load dangerous designation data if present
        if (itemData.danger_categories) setDangerCategories(itemData.danger_categories);
        if (itemData.danger_reasons) setDangerReasons(itemData.danger_reasons);
        
        // Load battery test data if applicable
        if (itemData.battery_voltage_v !== null && itemData.battery_voltage_v !== undefined) {
          setBatteryVoltage(itemData.battery_voltage_v.toString());
        }
        if (itemData.measured_cca_a !== null && itemData.measured_cca_a !== undefined) {
          setMeasuredCca(itemData.measured_cca_a.toString());
        }
        if (itemData.rated_cca_a !== null && itemData.rated_cca_a !== undefined) {
          setRatedCca(itemData.rated_cca_a.toString());
        }
        if (itemData.health_percent !== null && itemData.health_percent !== undefined) {
          setHealthPercent(itemData.health_percent.toString());
          setHealthPercentManual(true);  // If loaded from DB, treat as manual entry
        }
        
        // Load battery replacement data if applicable
        if (itemData.battery_type_required) {
          setBatteryTypeRequired(itemData.battery_type_required);
        }
        if (itemData.battery_registration_required !== undefined && itemData.battery_registration_required !== null) {
          // Normalize to string "yes"/"no" - handle legacy boolean values
          const regValue = itemData.battery_registration_required;
          if (regValue === true || regValue === "yes") {
            setBatteryRegistrationRequired("yes");
          } else if (regValue === false || regValue === "no") {
            setBatteryRegistrationRequired("no");
          } else {
            setBatteryRegistrationRequired(regValue);
          }
        }
        if (itemData.battery_technician_note) {
          setBatteryTechnicianNote(itemData.battery_technician_note);
        }
        
        // Load coolant assessment data if applicable
        if (itemData.coolant_strength_input_type) {
          setCoolantStrengthInputType(itemData.coolant_strength_input_type);
        }
        if (itemData.coolant_balls_floating !== null && itemData.coolant_balls_floating !== undefined) {
          setCoolantBallsFloating(itemData.coolant_balls_floating.toString());
        }
        if (itemData.coolant_freeze_protection_c !== null && itemData.coolant_freeze_protection_c !== undefined) {
          setCoolantFreezeProtection(itemData.coolant_freeze_protection_c.toString());
        }
        if (itemData.coolant_level) {
          setCoolantLevel(itemData.coolant_level);
        }
        if (itemData.coolant_condition) {
          setCoolantCondition(itemData.coolant_condition);
        }
        if (itemData.coolant_notes) {
          setCoolantNotes(itemData.coolant_notes);
        }
        if (itemData.coolant_suggested_status) {
          setCoolantSuggestedStatus(itemData.coolant_suggested_status);
        }
        
        // Load brake fluid assessment data if applicable
        if (itemData.brake_fluid_moisture !== null && itemData.brake_fluid_moisture !== undefined) {
          setBrakeFluidMoisture(itemData.brake_fluid_moisture.toString());
        }
        if (itemData.brake_fluid_boiling_point !== null && itemData.brake_fluid_boiling_point !== undefined) {
          setBrakeFluidBoilingPoint(itemData.brake_fluid_boiling_point.toString());
        }
        if (itemData.brake_fluid_visual_condition) {
          setBrakeFluidVisualCondition(itemData.brake_fluid_visual_condition);
        }
        if (itemData.brake_fluid_service_history) {
          setBrakeFluidServiceHistory(itemData.brake_fluid_service_history);
        }
        if (itemData.brake_fluid_last_replacement_date) {
          setBrakeFluidLastReplacementDate(itemData.brake_fluid_last_replacement_date);
        }
        if (itemData.brake_fluid_last_replacement_mileage !== null && itemData.brake_fluid_last_replacement_mileage !== undefined) {
          setBrakeFluidLastReplacementMileage(itemData.brake_fluid_last_replacement_mileage.toString());
        }
        if (itemData.brake_fluid_notes) {
          setBrakeFluidNotes(itemData.brake_fluid_notes);
        }
        if (itemData.brake_fluid_calculated_status) {
          setBrakeFluidCalculatedStatus(itemData.brake_fluid_calculated_status);
        }
        
        // Load brake pad wear sensor data
        if (itemData.wear_sensor_required) {
          setWearSensorRequired(true);
        }
        
        // Load wheel torque data if applicable
        if (itemData.torque_applied) {
          setTorqueApplied(itemData.torque_applied.toString());
        }
        if (itemData.torque_is_manual_entry) {
          setTorqueIsManualEntry(true);
          setTorqueManualValue(itemData.torque_applied?.toString() || "");
        }
        if (itemData.torque_deviation_reason) {
          setTorqueDeviationReason(itemData.torque_deviation_reason);
        }
        if (itemData.torque_unable) {
          setTorqueUnable(true);
        }
        if (itemData.torque_unable_reason) {
          setTorqueUnableReason(itemData.torque_unable_reason);
        }
        if (itemData.torque_unable_to_check) {
          setTorqueUnableToCheck(true);
        }
        if (itemData.torque_unable_to_check_reason) {
          setTorqueUnableToCheckReason(itemData.torque_unable_to_check_reason);
        }
        if (itemData.torque_manufacturer_spec) {
          setTorqueManufacturerSpec(itemData.torque_manufacturer_spec);
        }
        
        // Fetch manufacturer torque spec if this is the torque confirmation item
        if (itemData.item_key === "wheel_torque_confirmation") {
          setTorqueSpecLoading(true);
          axios.get(`${API}/inspections/${res.data.id}/torque-spec`).then(specRes => {
            if (specRes.data?.manufacturer_torque) {
              setTorqueManufacturerSpec(specRes.data.manufacturer_torque);
              setTorqueSpecSource(specRes.data.source);
              setTorqueConfidence(specRes.data.confidence || "high");
              setTorqueRangeMin(specRes.data.range_min);
              setTorqueRangeMax(specRes.data.range_max);
              // Pre-select manufacturer spec if no existing selection
              if (!itemData.torque_applied && !itemData.torque_unable) {
                setTorqueApplied(specRes.data.manufacturer_torque.toString());
              }
            } else {
              // Level 3 fallback — default 120 Nm
              setTorqueManufacturerSpec(120);
              setTorqueSpecSource("default_safe_value");
              setTorqueConfidence("low");
            }
            // Load existing confirmation state
            if (itemData.torque_confirmed !== undefined) {
              setTorqueConfirmed(!!itemData.torque_confirmed);
            }
          }).catch(err => {
            console.log("Could not fetch torque spec:", err);
            setTorqueManufacturerSpec(120);
            setTorqueSpecSource("default_safe_value");
            setTorqueConfidence("low");
          }).finally(() => {
            setTorqueSpecLoading(false);
          });
        }
        
        // Load tyre pressure data if applicable
        if (itemData.pressure_measured_fl !== undefined) {
          setPressureMeasured({
            fl: itemData.pressure_measured_fl?.toString() || "",
            fr: itemData.pressure_measured_fr?.toString() || "",
            rl: itemData.pressure_measured_rl?.toString() || "",
            rr: itemData.pressure_measured_rr?.toString() || ""
          });
        }
        if (itemData.pressure_adjusted_fl !== undefined) {
          setPressureAdjusted({
            fl: itemData.pressure_adjusted_fl?.toString() || "",
            fr: itemData.pressure_adjusted_fr?.toString() || "",
            rl: itemData.pressure_adjusted_rl?.toString() || "",
            rr: itemData.pressure_adjusted_rr?.toString() || ""
          });
        }
        if (itemData.pressure_unable_adjust) {
          setPressureUnableAdjust(true);
        }
        if (itemData.pressure_unable_reason) {
          setPressureUnableReason(itemData.pressure_unable_reason);
        }
        if (itemData.pressure_deviation_reason) {
          setPressureDeviationReason(itemData.pressure_deviation_reason);
        }
        if (itemData.pressure_spec_front || itemData.pressure_spec_rear) {
          setPressureSpec({
            front: itemData.pressure_spec_front,
            rear: itemData.pressure_spec_rear,
            source: "saved"
          });
        }
        
        // Fetch manufacturer pressure spec if this is the tyre pressure item
        if (itemData.item_key === "tyre_pressures_equalised") {
          setPressureSpecLoading(true);
          axios.get(`${API}/inspections/${res.data.id}/pressure-spec`).then(specRes => {
            let frontSpec = 2.2;
            let rearSpec = 2.4;
            let source = "default";
            
            if (specRes.data?.front_pressure || specRes.data?.rear_pressure) {
              frontSpec = specRes.data.front_pressure || 2.2;
              rearSpec = specRes.data.rear_pressure || 2.4;
              source = specRes.data.source || "manufacturer";
            }
            
            setPressureSpec({ front: frontSpec, rear: rearSpec, source });
            
            // Auto-fill adjusted pressures with spec if not already set
            if (!itemData.pressure_adjusted_fl && !itemData.pressure_adjusted_fr) {
              setPressureAdjusted({
                fl: frontSpec.toString(),
                fr: frontSpec.toString(),
                rl: rearSpec.toString(),
                rr: rearSpec.toString()
              });
            }
          }).catch(err => {
            console.log("Could not fetch pressure spec:", err);
            setPressureSpec({ front: 2.2, rear: 2.4, source: "default" });
            // Auto-fill with defaults if no existing data
            if (!itemData.pressure_adjusted_fl && !itemData.pressure_adjusted_fr) {
              setPressureAdjusted({ fl: "2.2", fr: "2.2", rl: "2.4", rr: "2.4" });
            }
          }).finally(() => {
            setPressureSpecLoading(false);
          });
        }
        
        // Load AC gas type data if this is the aircon item
        if (itemData.item_key === "aircon_operation") {
          setAcGasType(itemData.ac_gas_type || "");
          setAcPerformance(itemData.ac_performance || "");
          setAcFlags({
            system_empty: itemData.ac_system_empty || false,
            compressor_not_engaging: itemData.ac_compressor_not_engaging || false,
            noisy_operation: itemData.ac_noisy_operation || false
          });
        }
        
        // Fetch common issues for this item (if it has them)
        if (itemData.has_common_issues) {
          axios.get(`${API}/common-issues/${itemData.item_key}`).then(issuesRes => {
            setCommonIssues(issuesRes.data);
          }).catch(() => {
            setCommonIssues({ amber: [], red: [] });
          });
        } else {
          setCommonIssues({ amber: [], red: [] });
        }
      });
    }).finally(() => setLoading(false));
  }, [id, itemId, navigate]);

  // Calculate wear percentage when measurement changes (for pads)
  useEffect(() => {
    if (item?.has_wear_percentage && measurementValue && item?.new_thickness_mm) {
      const currentMm = parseFloat(measurementValue);
      const newMm = parseFloat(item.new_thickness_mm);
      const minMm = 2; // Minimum safe pad thickness
      
      if (!isNaN(currentMm) && newMm > minMm) {
        // Calculate percentage worn (inverted - how much is gone)
        const usableMaterial = newMm - minMm;
        const remainingMaterial = currentMm - minMm;
        const percentRemaining = Math.max(0, Math.min(100, (remainingMaterial / usableMaterial) * 100));
        const percentWorn = 100 - percentRemaining;
        setWearPercentage(Math.round(percentWorn));
      } else {
        setWearPercentage(null);
      }
    } else {
      setWearPercentage(null);
    }
  }, [measurementValue, item]);

  // ============== TECH-INTENT + AUTOSAVE SCAFFOLDING (#4, #1, #7) ==============
  // Status mutation flow:
  //   1) Tech enters a measurement → compute a suggestion stored in `statusSuggestion`
  //   2) If the tech has NOT yet manually set the status (statusUserSet=false), auto-apply
  //      it (back-compat for the first reading on a fresh item)
  //   3) If the tech HAS set it, never mutate. Render a "Use suggested" pill below the
  //      status row instead so the tech can opt in.
  const [statusUserSet, setStatusUserSet] = useState(false);
  const [statusSuggestion, setStatusSuggestion] = useState(null);
  const [techIntent, setTechIntent] = useState("");
  const [techIntentOptions, setTechIntentOptions] = useState([]);
  // Restore prompt for autosaved draft (#1)
  const [draftRestorePrompt, setDraftRestorePrompt] = useState(null);
  
  const setStatusByUser = (val) => {
    setStatusUserSet(true);
    setStatusSuggestion(null);
    setStatus(val);
  };
  
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/tech-intent-options`).then(res => {
      if (alive) setTechIntentOptions(res.data?.options || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  
  // ============== AUTOSAVE TO localStorage (#1) ==============
  // Debounced 800ms — every meaningful field change is persisted to localStorage so
  // a refresh, accidental back-tap, or network blip never loses progress. The draft
  // is cleared on a successful DB save and on Submit. Photo strings are intentionally
  // EXCLUDED from the draft to keep localStorage <5 MB (the legal limit on most browsers).
  useEffect(() => {
    if (!item?.id || !itemId) return;
    const handle = setTimeout(() => {
      try {
        const draft = {
          savedAt: Date.now(),
          status, notes, measurementValue, secondaryMeasurementValue,
          affectedSide, affectedLocations, affectedPosition, affectedPositions,
          tyrePercentWorn, tyreSize, tyreBrand,
          techIntent, selectedIssues,
          // Photos NOT saved (size); tech is reminded if needed.
        };
        localStorage.setItem(`vi-draft-${itemId}`, JSON.stringify(draft));
      } catch (e) { /* quota exceeded — silently noop */ }
    }, 800);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    item?.id, itemId,
    status, notes, measurementValue, secondaryMeasurementValue,
    affectedSide, affectedLocations, affectedPosition, affectedPositions,
    tyrePercentWorn, tyreSize, tyreBrand, techIntent, selectedIssues
  ]);
  
  // ============== BRAKE PAD AUTO-SUGGESTION ==============
  // 0-3mm = RED, 3-6mm = AMBER, 6mm+ = GREEN
  useEffect(() => {
    const brakePadItems = ['front_brake_pads', 'rear_brake_pads'];
    if (brakePadItems.includes(item?.item_key) && measurementValue) {
      const thickness = parseFloat(measurementValue);
      if (!isNaN(thickness) && thickness >= 0) {
        let suggestedStatus;
        let reason;
        if (thickness <= 3.0) { suggestedStatus = 'red'; reason = `${thickness} mm — below 3 mm`; }
        else if (thickness < 6.0) { suggestedStatus = 'amber'; reason = `${thickness} mm — between 3 and 6 mm`; }
        else { suggestedStatus = 'green'; reason = `${thickness} mm — above 6 mm`; }
        setStatusSuggestion({ value: suggestedStatus, reason });
        // First-touch auto-apply: if tech has not yet picked a status, set it.
        if (!statusUserSet && status !== 'dangerous' && status !== suggestedStatus) {
          setStatus(suggestedStatus);
        }
      }
    }
  }, [measurementValue, item?.item_key]);

  // Auto-calculate tyre wear percentage when tread depth measurement changes
  useEffect(() => {
    if (item?.requires_percent_worn && measurementValue) {
      const currentMm = parseFloat(measurementValue);
      const newTreadMm = 8.0; // New tyre tread depth
      const legalMinMm = 1.6; // UK legal minimum tread depth
      
      if (!isNaN(currentMm) && currentMm >= 0) {
        // Calculate percentage worn
        const usableTread = newTreadMm - legalMinMm; // 6.4mm usable tread
        const remainingTread = Math.max(0, currentMm - legalMinMm);
        const percentRemaining = Math.min(100, (remainingTread / usableTread) * 100);
        const percentWorn = Math.round(100 - percentRemaining);
        
        // Clamp between 0-100
        const clampedPercent = Math.max(0, Math.min(100, percentWorn));
        setTyrePercentWorn(clampedPercent.toString());
      }
    }
  }, [measurementValue, item?.requires_percent_worn]);

  // ============== TYRE TREAD DEPTH AUTO-SUGGESTION ==============
  // ≤1.6mm = RED (below legal limit), 1.6-3mm = AMBER (advisory), ≥3mm = GREEN
  // Only first-touch auto-applies (#4)
  useEffect(() => {
    const tyreItems = ['tyre_fl', 'tyre_fr', 'tyre_rl', 'tyre_rr'];
    if (tyreItems.includes(item?.item_key) && measurementValue && !statusOverride) {
      const treadDepth = parseFloat(measurementValue);
      if (!isNaN(treadDepth) && treadDepth >= 0) {
        let suggestedStatus;
        let reason;
        if (treadDepth <= 1.6) { suggestedStatus = 'red'; reason = `${treadDepth} mm — below legal 1.6 mm`; }
        else if (treadDepth <= 3.0) { suggestedStatus = 'amber'; reason = `${treadDepth} mm — advisory band`; }
        else { suggestedStatus = 'green'; reason = `${treadDepth} mm — above 3 mm`; }
        setStatusSuggestion({ value: suggestedStatus, reason });
        if (!statusUserSet && status !== 'dangerous' && status !== suggestedStatus) {
          setStatus(suggestedStatus);
        }
      }
    }
  }, [measurementValue, item?.item_key, statusOverride]);

  // ============== WIPER & WASHER SYSTEM AUTO-STATUS ==============
  // Overall status = worst status among all sub-components
  useEffect(() => {
    if (item?.is_wiper_washer_system && !statusOverride) {
      const statusPriority = { red: 3, amber: 2, green: 1, blue: 0, '': -1 };
      const subStatuses = [
        wiperData.front_left.status,
        wiperData.front_right.status,
        ...(wiperData.rear.not_fitted ? [] : [wiperData.rear.status]),
        wiperData.washers.status,
        wiperData.arm.status,
      ].filter(Boolean);
      
      if (subStatuses.length > 0) {
        const worst = subStatuses.reduce((a, b) => 
          (statusPriority[a] || 0) >= (statusPriority[b] || 0) ? a : b
        );
        if (worst && status !== worst && status !== 'dangerous') {
          setStatus(worst);
        }
      }
    }
  }, [wiperData, item?.is_wiper_washer_system, statusOverride]);

  // Auto-calculate battery health percentage when CCA values change
  useEffect(() => {
    if (item?.is_battery_test && !healthPercentManual && measuredCca && ratedCca) {
      const measured = parseFloat(measuredCca);
      const rated = parseFloat(ratedCca);
      
      if (!isNaN(measured) && !isNaN(rated) && rated > 0) {
        const calculatedHealth = ((measured / rated) * 100).toFixed(1);
        setHealthPercent(calculatedHealth);
      }
    }
  }, [measuredCca, ratedCca, item?.is_battery_test, healthPercentManual]);

  // ============== BATTERY TEST AUTO-STATUS ALLOCATION ==============
  // Mirrors the tyre pattern: measurement-first → auto-allocate RAG → tech
  // can override with reason via the shared override card. Skipped when
  // the tech has already overridden or manually set the status, or when
  // status is "dangerous" (that's a separate manual workflow).
  //
  // Priority: health % (if present) > voltage. Health % is the most
  // diagnostic of long-term battery condition; voltage on its own can be
  // misleading (e.g. surface charge right off the alternator).
  useEffect(() => {
    if (!item?.is_battery_test || statusOverride || status === 'dangerous') return;
    let suggestedStatus = null;
    let reason = null;
    const health = parseFloat(healthPercent);
    if (!isNaN(health) && healthPercent !== "") {
      if (health < 60) { suggestedStatus = 'red'; reason = `Health ${health}% — below 60% threshold`; }
      else if (health < 80) { suggestedStatus = 'amber'; reason = `Health ${health}% — monitor`; }
      else { suggestedStatus = 'green'; reason = `Health ${health}% — good`; }
    } else {
      const v = parseFloat(batteryVoltage);
      if (!isNaN(v) && batteryVoltage !== "") {
        if (v < 12.2) { suggestedStatus = 'red'; reason = `${v}V — below 12.2V`; }
        else if (v < 12.4) { suggestedStatus = 'amber'; reason = `${v}V — monitor`; }
        else { suggestedStatus = 'green'; reason = `${v}V — healthy`; }
      }
    }
    if (suggestedStatus) {
      setStatusSuggestion({ value: suggestedStatus, reason });
      if (!statusUserSet && status !== suggestedStatus) {
        setStatus(suggestedStatus);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batteryVoltage, healthPercent, item?.is_battery_test, statusOverride, statusUserSet]);

  // Auto-calculate coolant suggested status based on sub-fields
  useEffect(() => {
    if (!item?.is_coolant_assessment) return;
    
    let strengthStatus = null;
    let levelStatus = null;
    let conditionStatus = null;
    
    // 1) Strength mapping — freeze protection is the primary input
    if (coolantFreezeProtection) {
      const temp = parseFloat(coolantFreezeProtection);
      if (!isNaN(temp)) {
        if (temp <= -30) strengthStatus = "green";
        else if (temp <= -15) strengthStatus = "amber";
        else strengthStatus = "red";
      }
    } else if (coolantStrengthInputType === "balls_tester" && coolantBallsFloating) {
      // Fallback: if no degrees entered but balls count available
      const balls = parseInt(coolantBallsFloating);
      if (!isNaN(balls)) {
        if (balls >= 3) strengthStatus = "green";
        else if (balls === 2) strengthStatus = "amber";
        else strengthStatus = "red";
      }
    }
    
    // 2) Level mapping
    if (coolantLevel === "ok") levelStatus = "green";
    else if (coolantLevel === "low") levelStatus = "amber";
    else if (coolantLevel === "critically_low") levelStatus = "red";
    
    // 3) Condition mapping
    if (coolantCondition === "normal") conditionStatus = "green";
    else if (coolantCondition === "discoloured") conditionStatus = "amber";
    else if (coolantCondition === "contaminated") conditionStatus = "red";
    
    // Calculate overall suggested status
    const allStatuses = [strengthStatus, levelStatus, conditionStatus].filter(s => s !== null);
    let suggested = null;
    if (allStatuses.includes("red")) suggested = "red";
    else if (allStatuses.includes("amber")) suggested = "amber";
    else if (allStatuses.includes("green")) suggested = "green";
    
    setCoolantSuggestedStatus(suggested);
    // Surface as a suggestion chip so the tech can see WHY the RAG was picked.
    if (suggested) {
      const bits = [];
      if (strengthStatus) bits.push(`strength ${strengthStatus}`);
      if (levelStatus) bits.push(`level ${levelStatus}`);
      if (conditionStatus) bits.push(`condition ${conditionStatus}`);
      setStatusSuggestion({
        value: suggested,
        reason: bits.length ? bits.join(" · ") : "Coolant assessment",
      });
    }
    
    // Auto-set overall status if blank and we have a suggestion — UNLESS the
    // tech has already taken ownership via the override card.
    if (suggested && !status && !statusOverride) {
      setStatus(suggested);
    }
  }, [item?.is_coolant_assessment, coolantStrengthInputType, coolantFreezeProtection, coolantBallsFloating, coolantLevel, coolantCondition, status, statusOverride]);

  // ============== BRAKE FLUID ASSESSMENT AUTO-STATUS CALCULATION ==============
  useEffect(() => {
    if (!item?.is_brake_fluid_assessment) return;
    // Respect tech override — once they've taken ownership of the status
    // with a reason, we never re-overwrite their pick.
    if (statusOverride) return;
    
    const moisture = parseFloat(brakeFluidMoisture);
    const visual = brakeFluidVisualCondition;
    
    if (isNaN(moisture) || !visual) {
      setBrakeFluidCalculatedStatus(null);
      return;
    }
    
    // Calculate status based on the rules:
    // RED: moisture > 2.5% OR contamination
    // GREEN: moisture <= 1.5% AND clean AND no red flags
    // AMBER: everything else
    
    let calculated = null;
    
    if (moisture > 2.5 || visual === "contaminated") {
      calculated = "red";
    } else if (moisture <= 1.5 && visual === "clean") {
      calculated = "green";
    } else {
      // AMBER for: 1.51-2.5% moisture, dark/degraded, unknown history
      calculated = "amber";
    }
    
    setBrakeFluidCalculatedStatus(calculated);
    // Surface as a suggestion so the tech can see WHY the RAG was picked.
    setStatusSuggestion({
      value: calculated,
      reason:
        calculated === "red"
          ? `Moisture ${moisture}% / ${visual} — replace now`
          : calculated === "amber"
            ? `Moisture ${moisture}% / ${visual} — replacement recommended`
            : `Moisture ${moisture}% / ${visual} — good`,
    });
    
    // Auto-set the overall status if not already set
    if (calculated && !status) {
      setStatus(calculated);
    }
    // If technician tries to set a "better" status, enforce the calculated one
    else if (calculated === "red" && status !== "red" && status !== "dangerous" && status) {
      setStatus("red");
    }
    else if (calculated === "amber" && status === "green" && status) {
      setStatus("amber");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.is_brake_fluid_assessment, brakeFluidMoisture, brakeFluidVisualCondition, brakeFluidServiceHistory, status, statusOverride]);

  // ============== CLEAR ERROR WHEN DANGEROUS-RELATED FIELDS CHANGE ==============
  // When user makes progress on filling required fields, clear validation errors
  useEffect(() => {
    if (status !== "dangerous" || !error) return;
    
    // Check if this is a suspension/steering item (uses enhanced UI)
    const isSuspensionSteering = SUSPENSION_STEERING_ITEMS.includes(item?.item_key);
    
    // For suspension/steering items: clear error when justification is selected and confirmed
    if (isSuspensionSteering) {
      const isPreDefinedReason = item?.dangerous_config?.reasons?.includes(notes);
      const hasValidJustification = notes && (isPreDefinedReason || notes.length >= 25);
      const hasPhoto = photos && photos.length > 0;
      
      // Clear error if user has made progress on the validation requirements
      if (hasValidJustification || dangerousConfirmed || hasPhoto) {
        setError("");
      }
    } else {
      // For non-suspension items: clear error when danger categories or reasons are selected
      if ((dangerCategories && dangerCategories.length > 0) || 
          (dangerReasons && dangerReasons.length > 0) ||
          (photos && photos.length > 0)) {
        setError("");
      }
    }
  }, [status, notes, dangerCategories, dangerReasons, dangerousConfirmed, photos, error, item?.item_key, item?.dangerous_config?.reasons]);

  // ============== SCROLL TO ERROR WHEN SET ==============
  // Auto-scroll to error message when validation fails
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  // Disc specs search handler
  const handleDiscSearch = async (query) => {
    setDiscSearchQuery(query);
    if (query.length < 2) {
      setDiscSearchResults([]);
      return;
    }
    
    setDiscSearchLoading(true);
    try {
      const res = await axios.get(`${API}/disc-specs/search?q=${encodeURIComponent(query)}`);
      setDiscSearchResults(res.data.results || []);
    } catch (err) {
      setDiscSearchResults([]);
    } finally {
      setDiscSearchLoading(false);
    }
  };

  // Select disc specs from search results
  const handleSelectDiscSpecs = (vehicle, position) => {
    // position is either 'front' or 'rear' based on item key
    const isFront = item.item_key?.includes('front');
    const specs = isFront ? vehicle.front : vehicle.rear;
    
    if (specs) {
      setDiscNewThickness(specs.new_thickness?.toString() || "");
      setDiscMinThickness(specs.min_thickness?.toString() || "");
      setSelectedVehicle(vehicle);
      setShowDiscSearch(false);
      setDiscSearchQuery("");
      setDiscSearchResults([]);
    }
  };

  // Toggle issue selection (multi-select)
  const handleIssueToggle = (issue) => {
    setSelectedIssues(prev => {
      const isSelected = prev.includes(issue);
      let newSelected;
      if (isSelected) {
        newSelected = prev.filter(i => i !== issue);
      } else {
        newSelected = [...prev, issue];
      }
      // Update notes with all selected issues
      if (newSelected.length > 0) {
        setNotes(newSelected.join("\n"));
      }
      return newSelected;
    });
  };
  
  // Clear all selected issues
  const handleClearIssues = () => {
    setSelectedIssues([]);
    setNotes("");
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      // Add photo directly first, then user can annotate if they want
      setPhotos(prev => [...prev, reader.result]);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  };
  
  const handleAnnotationSave = (annotatedPhoto) => {
    if (annotatingIndex !== null && annotatingIndex < photos.length) {
      // Editing existing photo - replace with annotated version
      setPhotos(prev => prev.map((p, i) => i === annotatingIndex ? annotatedPhoto : p));
    }
    setAnnotatingPhoto(null);
    setAnnotatingIndex(null);
  };
  
  const handleAnnotationCancel = () => {
    setAnnotatingPhoto(null);
    setAnnotatingIndex(null);
  };
  
  const handleEditPhoto = (index) => {
    setAnnotatingPhoto(photos[index]);
    setAnnotatingIndex(index);
  };

  const handleTesterPhotoCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setTesterPhotoLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setTesterPhoto(reader.result);
      setTesterPhotoLoading(false);
    };
    reader.onerror = () => {
      setError("Failed to load photo. Please try again.");
      setTesterPhotoLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setError("");
    
    if (!status) {
      setError("Please select a status");
      return;
    }

    // ============== MOT GREEN-BLOCK GUARD ==============
    // Block "green" when the CURRENT MOT has flagged this exact item
    // unless the tech wrote a short override-note ("rectified", "replaced",
    // "re-checked, within limits", etc.). Server re-runs this validation.
    if (status === "green" && motFlagsForThisItem) {
      const noteTrimmed = (motGreenOverrideNote || "").trim();
      if (noteTrimmed.length < 4) {
        setError(
          motFlagsForThisItem.counts.terminal > 0
            ? `The current MOT recorded ${motFlagsForThisItem.counts.terminal} failure(s) against this item. Before marking it green, please describe what was rectified.`
            : `The current MOT recorded ${motFlagsForThisItem.counts.advisory} advisory/-ies against this item. Before marking it green, please describe what was rectified.`
        );
        return;
      }
    }

    // ============== BLUE STATUS — FAST PATH ==============
    // Blue (N/A) requires NO measurements, NO photos, NO notes
    // Just save status with optional notes and proceed
    if (status === "blue") {
      // Skip all validation — go straight to submit
      try {
        setSubmitting(true);
        const payload = {
          status: "blue",
          notes: notes || item.blue_reason || "Not applicable",
          photo_urls: photos.length > 0 ? photos : [],
          selected_issues: selectedIssues.length > 0 ? selectedIssues : [],
          status_override: statusOverride || null,
        };
        await axios.put(`${API}/inspections/${inspection.id}/items/${itemId}`, payload);
        navigate(`/app/tech/job/${id}/section/${item.section}`);
      } catch (err) {
        setError(getErrorMessage(err, "Failed to save item"));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Get evidence type for this item (defaults to visual_wear for safety)
    const evidenceType = item.evidence_type || "visual_wear";

    // ============== WIPER BLADE TYPE VALIDATION ==============
    if (item?.is_wiper_blade && !item?.is_wiper_washer_system && status !== "blue") {
      if (!wiperBladeType) {
        setError("Please select the blade type before saving");
        return;
      }
    }
    
    // ============== WIPER & WASHER SYSTEM VALIDATION ==============
    if (item?.is_wiper_washer_system) {
      // Check each sub-component has a status (except rear if not fitted)
      const components = [
        { key: "front_left", label: "Front Left Blade" },
        { key: "front_right", label: "Front Right Blade" },
        { key: "rear", label: "Rear Blade", allowNotFitted: true },
        { key: "washers", label: "Washer System" },
        { key: "arm", label: "Wiper Arm / Mechanical" },
      ];
      for (const comp of components) {
        const sub = wiperData[comp.key];
        if (comp.allowNotFitted && sub.not_fitted) continue;
        if (!sub.status) {
          setError(`Please select a status for ${comp.label}`);
          return;
        }
        if ((sub.status === 'amber' || sub.status === 'red') && (!sub.issues || sub.issues.length === 0)) {
          setError(`Please select at least one issue for ${comp.label} (${sub.status.toUpperCase()})`);
          return;
        }
      }
    }
    
    // ============== EVIDENCE VALIDATION RULES ==============
    // 1. measured_test: ALWAYS require tester photo showing device
    // 2. visual_wear: Photo required ONLY for amber/red
    // 3. fluid_level: Conditional - photo if critically low, bypass allowed with "not capturable"
    // 4. functional_check: NO photo ever
    // 5. confirmation_only: NEVER photo

    // Validation for amber/red - notes OR dropdown selection required
    if (status === "amber" || status === "red") {
      // Check if any form of description is provided (no minimum length):
      // 1. Manual notes (non-empty)
      // 2. Selected common issues from dropdown
      // 3. Disc condition dropdown selection
      const hasManualNotes = !!(notes && notes.trim().length > 0);
      const hasSelectedIssues = selectedIssues && selectedIssues.length > 0;
      const hasDiscCondition = discCondition && discCondition.length > 0;
      
      if (!hasManualNotes && !hasSelectedIssues && !hasDiscCondition) {
        setError("Description required: select an issue, condition or enter a note.");
        return;
      }
      
      // Photo requirements based on evidence type
      if (evidenceType === "measured_test") {
        // Measured tests - require at least one photo (tester photo or regular photos)
        if (testerPhotoLoading) {
          setError("Please wait for the photo to finish loading");
          return;
        }
        if (!testerPhoto && photos.length < 1) {
          setError("Photo of measuring device showing result is required. Please capture at least one photo.");
          return;
        }
      } else if (evidenceType === "visual_wear") {
        // Visual wear items - Photo required for amber/red
        // Accept either regular photos OR tester photo (for disc items)
        if (photos.length < 1 && !testerPhoto) {
          setError("Photo evidence required for this failed item");
          return;
        }
      } else if (evidenceType === "fluid_level") {
        // Fluid levels - Conditional photo, can bypass with "not capturable"
        if (photos.length < 1 && !notes.toLowerCase().includes("not capturable")) {
          setError("Photo required for low fluid level. Add 'not capturable' to notes if photo cannot be taken.");
          return;
        }
      }
      // functional_check and confirmation_only - NO photo required
    }

    // Validation for measurement - for items that require it (measured_test or visual_wear with requires_measurement)
    // EXCLUDE wheel_torque_confirmation - it has its own torque_applied validation below
    if (item.requires_measurement && status !== "blue" && item?.item_key !== "wheel_torque_confirmation") {
      if (!measurementValue || isNaN(parseFloat(measurementValue))) {
        setError("Measurement value is required for this item");
        return;
      }
    }
    
    // Green status still requires measurement if item needs it
    // EXCLUDE wheel_torque_confirmation - uses torque_applied instead of measurementValue
    if (status === "green" && item.requires_measurement && item?.item_key !== "wheel_torque_confirmation") {
      if (!measurementValue || isNaN(parseFloat(measurementValue))) {
        setError("Measurement value required even for green status on this item");
        return;
      }
    }

    // ============== BRAKE PAD STATUS VALIDATION ==============
    // ≤3mm=RED, >3-<4=AMBER/REPLACE_SOON, ≥4-<6=AMBER/MONITOR, ≥6=GREEN
    // DANGEROUS status overrides — tech has discretion
    // statusOverride allows tech to override for condition-based defects (cracking, corrosion, etc.)
    const brakePadItems = ['front_brake_pads', 'rear_brake_pads'];
    if (brakePadItems.includes(item.item_key) && measurementValue && status !== 'dangerous' && !statusOverride) {
      const measurement = parseFloat(measurementValue);
      let requiredStatus;
      if (measurement <= 3.0) {
        requiredStatus = 'red';
      } else if (measurement < 6.0) {
        requiredStatus = 'amber';
      } else {
        requiredStatus = 'green';
      }
      
      if (status !== requiredStatus) {
        setError(`Brake pad measurement of ${measurement}mm requires ${requiredStatus.toUpperCase()} status. (≤3mm=Red, 3-6mm=Amber, ≥6mm=Green). Use the status override if reporting a condition-based defect.`);
        return;
      }
    }

    // ============== TYRE ITEM VALIDATION ==============
    // For ALL tyre items, require tyre_size and tyre_brand
    const tyreItems = ["tyre_fl", "tyre_fr", "tyre_rl", "tyre_rr"];  // Exclude spare_tyre - often an inflation kit
    const isTyreItem = tyreItems.includes(item.item_key);
    
    if (isTyreItem) {
      if (!tyreSize || !tyreSize.trim()) {
        setError("Tyre size is required (e.g., 205/55R16)");
        return;
      }
      if (!tyreBrand || !tyreBrand.trim()) {
        setError("Tyre brand is required (e.g., Michelin, Continental)");
        return;
      }
      
      // ============== TYRE TREAD DEPTH STATUS VALIDATION ==============
      // Enforce correct status based on tread depth measurements
      // <= 1.6mm = RED (below legal limit), 1.6-3mm = AMBER (advisory), 3mm+ = GREEN
      // statusOverride allows condition-based override (e.g. sidewall damage, uneven wear)
      if (measurementValue && status !== 'dangerous' && !statusOverride) {
        const treadDepth = parseFloat(measurementValue);
        if (!isNaN(treadDepth)) {
          let requiredStatus;
          if (treadDepth <= 1.6) {
            requiredStatus = 'red';
          } else if (treadDepth <= 3) {
            requiredStatus = 'amber';
          } else {
            requiredStatus = 'green';
          }
          
          // Block selecting a "better" status than measurement indicates
          if (requiredStatus === 'red' && status === 'green') {
            setError(`⚠️ GATEWAY BLOCKED: Tread depth is ${treadDepth}mm (at or below 1.6mm legal minimum). Cannot mark as GREEN. Please select RED status.`);
            return;
          }
          if (requiredStatus === 'red' && status === 'amber') {
            setError(`⚠️ GATEWAY BLOCKED: Tread depth is ${treadDepth}mm (at or below 1.6mm legal minimum). Cannot mark as AMBER. Please select RED status.`);
            return;
          }
          if (requiredStatus === 'amber' && status === 'green') {
            setError(`⚠️ GATEWAY BLOCKED: Tread depth is ${treadDepth}mm (approaching 1.6mm legal minimum). Cannot mark as GREEN. Please select AMBER or RED status.`);
            return;
          }
        }
      }
    }

    // ============== BRAKE FLUID BOILING POINT STATUS VALIDATION ==============
    // Enforce correct status based on boiling point measurement
    // < 180°C = RED (needs immediate replacement), 180-200°C = AMBER (monitor), 200°C+ = GREEN
    // statusOverride allows condition-based override
    if (item?.is_brake_fluid_assessment && brakeFluidBoilingPoint && status !== 'dangerous' && !statusOverride) {
      const boilingPoint = parseInt(brakeFluidBoilingPoint);
      if (!isNaN(boilingPoint)) {
        let requiredStatus;
        if (boilingPoint < 180) {
          requiredStatus = 'red';
        } else if (boilingPoint < 200) {
          requiredStatus = 'amber';
        } else {
          requiredStatus = 'green';
        }
        
        // Block selecting a "better" status than measurement indicates
        if (requiredStatus === 'red' && status === 'green') {
          setError(`⚠️ GATEWAY BLOCKED: Brake fluid boiling point is ${boilingPoint}°C (below 180°C threshold). Cannot mark as GREEN. Brake fluid requires immediate replacement. Please select RED status.`);
          return;
        }
        if (requiredStatus === 'red' && status === 'amber') {
          setError(`⚠️ GATEWAY BLOCKED: Brake fluid boiling point is ${boilingPoint}°C (below 180°C threshold). Cannot mark as AMBER. Brake fluid requires immediate replacement. Please select RED status.`);
          return;
        }
        if (requiredStatus === 'amber' && status === 'green') {
          setError(`⚠️ GATEWAY BLOCKED: Brake fluid boiling point is ${boilingPoint}°C (180-200°C range). Cannot mark as GREEN. Please select AMBER status.`);
          return;
        }
      }
    }

    // ============== COOLANT ASSESSMENT STATUS VALIDATION ==============
    // Block selecting a "better" status than the assessment suggests — UNLESS
    // the tech has explicitly overridden via the override card (in which case
    // the override carries a reason and the gateway lets it through).
    if (item?.is_coolant_assessment && coolantSuggestedStatus && status !== 'dangerous' && !statusOverride) {
      if (coolantSuggestedStatus === 'red' && status === 'green') {
        setError(`⚠️ GATEWAY BLOCKED: Coolant assessment indicates RED status (critical condition detected). Cannot mark as GREEN. Please select the correct status.`);
        return;
      }
      if (coolantSuggestedStatus === 'red' && status === 'amber') {
        setError(`⚠️ GATEWAY BLOCKED: Coolant assessment indicates RED status (critical condition detected). Cannot mark as AMBER. Please select RED status.`);
        return;
      }
      if (coolantSuggestedStatus === 'amber' && status === 'green') {
        setError(`⚠️ GATEWAY BLOCKED: Coolant assessment indicates AMBER status (condition needs attention). Cannot mark as GREEN. Please select AMBER or RED status.`);
        return;
      }
    }

    // ============== SUSPENSION SIDE SPECIFICATION VALIDATION ==============
    // For suspension items with amber/red/dangerous status, require at least one location.
    if (item.requires_side_specification && (status === "amber" || status === "red" || status === "dangerous")) {
      if (!affectedSide && affectedLocations.length === 0) {
        setError("Please specify which side or corner is affected (you can pick more than one)");
        return;
      }
    }

    // ============== INNER/OUTER POSITION VALIDATION ==============
    // For CV joints, track rod ends — when item exposes position_options, require selection.
    if (item.position_options && item.position_options.length > 0 && item.requires_side_specification && !item.requires_position_specification && (status === "amber" || status === "red" || status === "dangerous")) {
      if (!affectedPosition) {
        setError("Please specify whether it's the Inner, Outer, or Both position");
        return;
      }
    }

    // ============== BATTERY TEST ENHANCED VALIDATION ==============
    if (item?.is_battery_test) {
      if (status === "blue") {
        // Not Applicable - requires explanation in notes
        if (!notes || notes.trim().length < 10) {
          setError("Please provide a reason for marking Battery Test as Not Applicable (e.g., 'Battery not accessible')");
          return;
        }
      } else if (status === "amber" || status === "red") {
        // Amber/Red require all measurements and tester photo
        if (!batteryVoltage || isNaN(parseFloat(batteryVoltage))) {
          setError("Battery voltage (V) is required for amber/red status");
          return;
        }
        const voltage = parseFloat(batteryVoltage);
        if (voltage < 0 || voltage > 20) {
          setError("Battery voltage must be between 0 and 20 volts");
          return;
        }
        
        if (!measuredCca || isNaN(parseInt(measuredCca))) {
          setError("Measured CCA (A) from tester is required for amber/red status");
          return;
        }
        const measCca = parseInt(measuredCca);
        if (measCca < 0 || measCca > 2000) {
          setError("Measured CCA must be between 0 and 2000 amps");
          return;
        }
        
        if (!ratedCca || isNaN(parseInt(ratedCca))) {
          setError("Rated CCA (A) from battery label is required for amber/red status");
          return;
        }
        const ratCca = parseInt(ratedCca);
        if (ratCca < 0 || ratCca > 2000) {
          setError("Rated CCA must be between 0 and 2000 amps");
          return;
        }
        
        if (!testerPhoto) {
          setError("Tester photo showing battery test result is required for amber/red status");
          return;
        }
        
        if (!notes || notes.trim().length < 10) {
          setError("Notes required (minimum 10 characters) for amber/red battery status");
          return;
        }
      } else if (status === "green") {
        // Green still requires measurements
        if (!batteryVoltage || isNaN(parseFloat(batteryVoltage))) {
          setError("Battery voltage (V) is required even for green status");
          return;
        }
        const voltage = parseFloat(batteryVoltage);
        if (voltage < 0 || voltage > 20) {
          setError("Battery voltage must be between 0 and 20 volts");
          return;
        }
        
        if (!measuredCca || isNaN(parseInt(measuredCca))) {
          setError("Measured CCA (A) from tester is required even for green status");
          return;
        }
        
        if (!ratedCca || isNaN(parseInt(ratedCca))) {
          setError("Rated CCA (A) from battery label is required even for green status");
          return;
        }
        
        // GATEWAY: Check if health percentage indicates poor battery but user marked green
        // statusOverride bypasses this gate
        const health = healthPercent ? parseFloat(healthPercent) : null;
        if (health !== null && health < 60 && !statusOverride) {
          setError(`⚠️ GATEWAY BLOCKED: Battery health is ${health.toFixed(0)}% (Poor - Replace). Cannot mark as GREEN when measurements indicate replacement is required. Please select AMBER or RED status, or use status override.`);
          return;
        }
        
        // GATEWAY: Check if voltage indicates failing battery
        if (voltage < 12.2 && !statusOverride) {
          setError(`⚠️ GATEWAY BLOCKED: Battery voltage is ${voltage.toFixed(2)}V (Below minimum 12.2V). Cannot mark as GREEN. Battery requires charging or replacement. Please select AMBER or RED status, or use status override.`);
          return;
        }
      }
    }

    // ============== DANGEROUS DESIGNATION VALIDATION (COMPLIANCE-GRADE) ==============
    // Dangerous is a restricted, compliance-grade classification - enforce all requirements
    if (status === "dangerous") {
      if (!item.allows_dangerous) {
        setError("DANGEROUS status is not permitted for this inspection item.");
        return;
      }
      
      // 1. MANDATORY: At least one photo
      if (photos.length < 1) {
        setError("DANGEROUS status requires at least one photo as evidence. This is mandatory for compliance.");
        return;
      }
      
      // Check if this is a suspension/steering item (uses enhanced UI)
      const isSuspensionSteering = SUSPENSION_STEERING_ITEMS.includes(item.item_key);
      
      // 2. MANDATORY: At least one danger category selected (skip for suspension/steering - they use enhanced UI)
      if (!isSuspensionSteering && (!dangerCategories || dangerCategories.length < 1)) {
        setError("Please select at least one danger category (e.g., 'Immediate danger to road safety')");
        return;
      }
      
      // 3. MANDATORY: At least one structured danger reason selected (skip for suspension/steering - they use enhanced UI)
      if (!isSuspensionSteering && (!dangerReasons || dangerReasons.length < 1)) {
        setError("Please select at least one structured danger reason for this item.");
        return;
      }
      
      // 4. ENHANCED VALIDATION FOR SUSPENSION/STEERING ITEMS
      if (isSuspensionSteering) {
        // Require justification - either pre-defined reason OR min 25 chars for custom
        const isPreDefinedReason = item.dangerous_config?.reasons?.includes(notes);
        if (!notes) {
          setError("Please select a safety justification reason or enter a custom description.");
          return;
        }
        if (!isPreDefinedReason && notes.length < 25) {
          setError("Custom justification requires minimum 25 characters. Or select a pre-defined reason above.");
          return;
        }
        // Require confirmation checkbox
        if (!dangerousConfirmed) {
          setError("You must confirm that this item presents an immediate safety risk before saving.");
          return;
        }
      }
    }

    // ============== WHEEL TORQUE CAPTURE VALIDATION ==============
    if (item?.item_key === "wheel_torque_confirmation") {
      // Soft compliance v1: Log but do not block VI completion
      // Exception paths still require reasons (for audit trail)
      if (torqueUnableToCheck) {
        if (!torqueUnableToCheckReason || torqueUnableToCheckReason.trim().length < 3) {
          setError("Please select or enter a reason for being unable to check/access wheel nuts");
          return;
        }
      } else if (torqueUnable) {
        if (!torqueUnableReason || torqueUnableReason.trim().length < 5) {
          setError("Please provide a reason why wheels could not be torqued");
          return;
        }
      }
      // Normal path: confirmation tick + optional value — NOT blocking
    }

    // ============== AC GAS TYPE VALIDATION ==============
    if (item?.item_key === "aircon_operation" && status !== "blue") {
      // AC gas type is MANDATORY for all statuses (needed for service interval pricing)
      if (!acGasType) {
        setError("Refrigerant type (R134a or R1234yf) must be selected. Check the under-bonnet label.");
        return;
      }
      // AC performance should be set for context
      if (!acPerformance) {
        setError("Please select the AC performance level (Cold/Weak/Not Cold)");
        return;
      }
    }

    // ============== TYRE PRESSURE CAPTURE VALIDATION ==============
    if (item?.item_key === "tyre_pressures_equalised") {
      // Check completion gate: pressures OR unable-to-adjust required
      if (!pressureUnableAdjust) {
        // All 4 adjusted pressures required (measured pressures removed - only adjusted matters)
        const missingAdjusted = PRESSURE_POSITIONS.filter(pos => !pressureAdjusted[pos]);
        if (missingAdjusted.length > 0) {
          setError(`All 4 adjusted pressures required. Missing: ${missingAdjusted.map(p => PRESSURE_LABELS[p]).join(", ")}`);
          return;
        }
        
        // Validate adjusted pressure range (15-60 PSI)
        for (const pos of PRESSURE_POSITIONS) {
          const val = parseFloat(pressureAdjusted[pos]);
          if (isNaN(val) || val < 15 || val > 60) {
            setError(`${PRESSURE_LABELS[pos]} adjusted pressure must be between 15 and 60 PSI`);
            return;
          }
        }
        
        // Check if adjusted pressures deviate from spec - require reason
        const specFront = pressureSpec.front || 32;
        const specRear = pressureSpec.rear || 35;
        const DEVIATION_THRESHOLD = 3;
        
        const hasDeviation = PRESSURE_POSITIONS.some(pos => {
          const adjVal = parseFloat(pressureAdjusted[pos]);
          const spec = pos.startsWith("f") ? specFront : specRear;
          return Math.abs(adjVal - spec) > DEVIATION_THRESHOLD;
        });
        
        if (hasDeviation && (!pressureDeviationReason || pressureDeviationReason.trim().length < 10)) {
          setError("Adjusted pressures differ from specification. Please provide a reason (minimum 10 characters).");
          return;
        }
      } else {
        // Exception path - reason required
        if (!pressureUnableReason || pressureUnableReason.trim().length < 5) {
          setError("Please provide a reason why pressures could not be adjusted");
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      // Build notes from available sources if manual notes are empty
      let finalNotes = notes;
      if (!notes || !notes.trim()) {
        // Use disc condition if available
        if (discCondition) {
          finalNotes = `Disc condition: ${discCondition}`;
          // Add measurement context if available
          if (measurementValue && discMinThickness) {
            const current = parseFloat(measurementValue);
            const min = parseFloat(discMinThickness);
            if (current < min) {
              finalNotes += ` (${measurementValue}mm - below minimum ${discMinThickness}mm)`;
            } else {
              finalNotes += ` (${measurementValue}mm)`;
            }
          }
        }
        // Use selected issues if available
        else if (selectedIssues && selectedIssues.length > 0) {
          finalNotes = selectedIssues.join("\n");
        }
      }
      
      const isSuspensionItem = SUSPENSION_STEERING_ITEMS.includes(item?.item_key);
      
      const response = await axios.put(`${API}/inspections/${inspection.id}/items/${itemId}`, {
        status,
        notes: finalNotes || null,
        measurement_value: measurementValue ? parseFloat(measurementValue) : null,
        measurement_unit: item.measurement_unit_type || item.measurement_unit,
        secondary_measurement_value: secondaryMeasurementValue ? parseFloat(secondaryMeasurementValue) : null,
        secondary_measurement_unit: item.secondary_measurement_unit || null,
        photo_urls: photos,
        tester_photo_url: testerPhoto,
        // Disc specs
        disc_new_thickness: discNewThickness ? parseFloat(discNewThickness) : null,
        disc_min_thickness: discMinThickness ? parseFloat(discMinThickness) : null,
        disc_condition: discCondition || null,
        // Pad wear percentage
        wear_percentage: wearPercentage,
        // Tyre percent worn (manual entry)
        percent_worn: tyrePercentWorn ? parseInt(tyrePercentWorn) : null,
        // Tyre size and brand (for tyre items)
        tyre_size: tyreSize || null,
        tyre_brand: tyreBrand || null,
        wiper_blade_type: wiperBladeType || null,
        wiper_data: item?.is_wiper_washer_system ? wiperData : null,
        // Side specification (for suspension components)
        affected_side: affectedSide || (affectedLocations[0] || null),
        affected_locations: affectedLocations.length > 0 ? affectedLocations : null,
        affected_position: affectedPosition || null,
        // Tech intent — small persuasion-context label for FOH (#7)
        tech_intent: techIntent || null,
        // Multi-position specification (for items like indicators)
        affected_positions: affectedPositions.length > 0 ? affectedPositions : null,
        // Dangerous designation fields (compliance-grade)
        danger_categories: status === "dangerous" ? dangerCategories : null,
        danger_reasons: status === "dangerous" ? dangerReasons : null,
        // Enhanced dangerous fields for suspension/steering
        dangerous_confirmed: status === "dangerous" ? dangerousConfirmed : null,
        fleet_compliance_ref: status === "dangerous" ? fleetComplianceRef : null,
        // Battery test enhanced fields
        battery_voltage_v: item?.is_battery_test && batteryVoltage ? parseFloat(batteryVoltage) : null,
        measured_cca_a: item?.is_battery_test && measuredCca ? parseInt(measuredCca) : null,
        rated_cca_a: item?.is_battery_test && ratedCca ? parseInt(ratedCca) : null,
        health_percent: item?.is_battery_test && healthPercent ? parseFloat(healthPercent) : null,
        // Battery replacement fields
        battery_type_required: item?.is_battery_test ? batteryTypeRequired : null,
        battery_registration_required: item?.is_battery_test ? batteryRegistrationRequired : null,
        battery_technician_note: item?.is_battery_test ? batteryTechnicianNote : null,
        // Coolant assessment enhanced fields
        coolant_strength_input_type: item?.is_coolant_assessment ? coolantStrengthInputType : null,
        coolant_balls_floating: item?.is_coolant_assessment && coolantBallsFloating ? parseInt(coolantBallsFloating) : null,
        coolant_freeze_protection_c: item?.is_coolant_assessment && coolantFreezeProtection ? parseFloat(coolantFreezeProtection) : null,
        coolant_level: item?.is_coolant_assessment ? coolantLevel : null,
        coolant_condition: item?.is_coolant_assessment ? coolantCondition : null,
        coolant_notes: item?.is_coolant_assessment ? coolantNotes : null,
        // Brake fluid assessment enhanced fields
        brake_fluid_moisture: item?.is_brake_fluid_assessment && brakeFluidMoisture ? parseFloat(brakeFluidMoisture) : null,
        brake_fluid_boiling_point: item?.is_brake_fluid_assessment && brakeFluidBoilingPoint ? parseInt(brakeFluidBoilingPoint) : null,
        brake_fluid_visual_condition: item?.is_brake_fluid_assessment ? brakeFluidVisualCondition : null,
        brake_fluid_service_history: item?.is_brake_fluid_assessment ? brakeFluidServiceHistory : null,
        brake_fluid_last_replacement_date: item?.is_brake_fluid_assessment ? brakeFluidLastReplacementDate : null,
        brake_fluid_last_replacement_mileage: item?.is_brake_fluid_assessment && brakeFluidLastReplacementMileage ? parseInt(brakeFluidLastReplacementMileage) : null,
        brake_fluid_notes: item?.is_brake_fluid_assessment ? brakeFluidNotes : null,
        // Brake pad wear sensor
        wear_sensor_required: (item?.item_key === 'front_brake_pads' || item?.item_key === 'rear_brake_pads') ? wearSensorRequired : null,
        // Wheel Torque Capture fields
        torque_applied: item?.item_key === "wheel_torque_confirmation" && !torqueUnable ? 
          (torqueIsManualEntry ? parseInt(torqueManualValue) : (torqueApplied ? parseInt(torqueApplied) : null)) : null,
        torque_is_manual_entry: item?.item_key === "wheel_torque_confirmation" ? torqueIsManualEntry : null,
        torque_deviation_reason: item?.item_key === "wheel_torque_confirmation" ? torqueDeviationReason : null,
        torque_unable: item?.item_key === "wheel_torque_confirmation" ? torqueUnable : null,
        torque_unable_reason: item?.item_key === "wheel_torque_confirmation" && torqueUnable ? torqueUnableReason : null,
        torque_unable_to_check: item?.item_key === "wheel_torque_confirmation" ? torqueUnableToCheck : null,
        torque_unable_to_check_reason: item?.item_key === "wheel_torque_confirmation" && torqueUnableToCheck ? torqueUnableToCheckReason : null,
        torque_manufacturer_spec: item?.item_key === "wheel_torque_confirmation" ? torqueManufacturerSpec : null,
        torque_confirmed: item?.item_key === "wheel_torque_confirmation" ? torqueConfirmed : null,
        torque_selection_source: item?.item_key === "wheel_torque_confirmation" ? torqueSelectionSource : null,
        torque_confidence: item?.item_key === "wheel_torque_confirmation" ? torqueConfidence : null,
        torque_spec_source: item?.item_key === "wheel_torque_confirmation" ? torqueSpecSource : null,
        // Tyre Pressure Capture fields - measured pressures removed, only adjusted values captured
        pressure_measured_fl: null,  // No longer captured
        pressure_measured_fr: null,  // No longer captured
        pressure_measured_rl: null,  // No longer captured
        pressure_measured_rr: null,  // No longer captured
        pressure_adjusted_fl: item?.item_key === "tyre_pressures_equalised" && !pressureUnableAdjust ? parseFloat(pressureAdjusted.fl) || null : null,
        pressure_adjusted_fr: item?.item_key === "tyre_pressures_equalised" && !pressureUnableAdjust ? parseFloat(pressureAdjusted.fr) || null : null,
        pressure_adjusted_rl: item?.item_key === "tyre_pressures_equalised" && !pressureUnableAdjust ? parseFloat(pressureAdjusted.rl) || null : null,
        pressure_adjusted_rr: item?.item_key === "tyre_pressures_equalised" && !pressureUnableAdjust ? parseFloat(pressureAdjusted.rr) || null : null,
        pressure_spec_front: item?.item_key === "tyre_pressures_equalised" ? pressureSpec.front : null,
        pressure_spec_rear: item?.item_key === "tyre_pressures_equalised" ? pressureSpec.rear : null,
        pressure_unable_adjust: item?.item_key === "tyre_pressures_equalised" ? pressureUnableAdjust : null,
        pressure_unable_reason: item?.item_key === "tyre_pressures_equalised" && pressureUnableAdjust ? pressureUnableReason : null,
        pressure_deviation_reason: item?.item_key === "tyre_pressures_equalised" ? pressureDeviationReason : null,
        // AC Gas Type data
        ac_gas_type: item?.item_key === "aircon_operation" ? acGasType : null,
        ac_performance: item?.item_key === "aircon_operation" ? acPerformance : null,
        ac_system_empty: item?.item_key === "aircon_operation" ? acFlags.system_empty : null,
        ac_compressor_not_engaging: item?.item_key === "aircon_operation" ? acFlags.compressor_not_engaging : null,
        ac_noisy_operation: item?.item_key === "aircon_operation" ? acFlags.noisy_operation : null,
        // Status Override - when tech overrides auto-calculated status
        status_override: statusOverride || null,
        // MOT green-block override note — only meaningful when status === green
        // AND the current MOT had flagged this item. Saved on the item for audit.
        mot_green_override_note: status === "green" && motFlagsForThisItem
          ? (motGreenOverrideNote || "").trim()
          : null,
        // Locking Wheel Key data
        locking_key_present: item?.item_key === "locking_wheel_key" ? lockingKeyPresent : null,
        locking_key_location: item?.item_key === "locking_wheel_key" ? lockingKeyLocation : null,
        locking_key_notes: item?.item_key === "locking_wheel_key" ? lockingKeyNotes : null,
        // Suspension structured repair data
        construction_type: isSuspensionItem ? repairConstructionType || null : null,
        repair_method: isSuspensionItem ? repairMethod || null : null,
        arm_condition: isSuspensionItem ? repairArmCondition || null : null,
        removal_risk: isSuspensionItem ? repairRemovalRisk || null : null,
        opposite_side_condition: isSuspensionItem ? repairOppositeSide || null : null,
        alignment_implication: isSuspensionItem ? repairAlignmentImplication || null : null,
        joint_level: isSuspensionItem ? repairJointLevel || null : null,
        // Enhanced item assessment data (non-suspension items)
        enhanced_item_data: ENHANCED_CAPTURE_ITEMS.includes(item?.item_key) && Object.keys(enhancedItemData).length > 0 ? enhancedItemData : null
      });
      
      // Check if wheel alignment was auto-triggered
      if (response.data?.alignment_triggered) {
        toast.info(
          response.data.alignment_reason || "Wheel alignment check added to pricing",
          { 
            duration: 5000,
            icon: "🛞"
          }
        );
      }
      
      // Clear the autosave draft now that the DB has the canonical version (#1)
      try { localStorage.removeItem(`vi-draft-${itemId}`); } catch (e) { /* ignore */ }
      
      // Check if this is a main tyre item (not spare) - offer to copy SIZE to other tyres
      // Only show copy modal if:
      // 1. This is a main tyre item
      // 2. User entered a tyre size
      // 3. Other tyres don't already have sizes set (first tyre entry)
      const mainTyreItems = ["tyre_fl", "tyre_fr", "tyre_rl", "tyre_rr"];
      const isTyreItem = mainTyreItems.includes(item.item_key);
      
      if (isTyreItem && tyreSize) {
        // Check if other tyres already have sizes
        try {
          const itemsRes = await axios.get(`${API}/inspections/${inspection.id}/sections/tyres/items`);
          const tyreItems = itemsRes.data;
          const otherTyres = mainTyreItems.filter(t => t !== item.item_key);
          const otherTyreItems = tyreItems.filter(i => otherTyres.includes(i.item_key));
          
          // Check if any other tyre already has a size set
          const otherTyresHaveSizes = otherTyreItems.some(t => t.tyre_size && t.tyre_size.trim());
          
          // Only show copy modal if other tyres DON'T have sizes yet (first tyre being sized)
          if (!otherTyresHaveSizes) {
            setTyreCopyModal({
              show: true,
              measurementValue: measurementValue || '',
              tyreSize: tyreSize || '',
              percentWorn: tyrePercentWorn || '',
              currentTyre: item.item_key
            });
          } else {
            // Other tyres already have sizes - just navigate back
            navigate(`/app/tech/job/${id}/section/${item.section}`);
          }
        } catch (err) {
          // If check fails, just navigate back
          console.error("Failed to check other tyre sizes:", err);
          navigate(`/app/tech/job/${id}/section/${item.section}`);
        }
      } else {
        navigate(`/app/tech/job/${id}/section/${item.section}`);
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to save"));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Copy tyre measurements to other tyres
  const handleCopyToOtherTyres = async () => {
    const mainTyreItems = ["tyre_fl", "tyre_fr", "tyre_rl", "tyre_rr"];
    const otherTyres = mainTyreItems.filter(t => t !== tyreCopyModal.currentTyre);
    
    setCopyingTyres(true);
    try {
      // Get tyre section items
      const itemsRes = await axios.get(`${API}/inspections/${inspection.id}/sections/tyres/items`);
      const tyreItems = itemsRes.data;
      
      // Find the other tyre items (exclude current one and spare)
      const otherTyreItems = tyreItems.filter(i => otherTyres.includes(i.item_key));
      
      // Update each other tyre with ONLY the tyre size using the new PATCH endpoint
      // This does NOT mark items as inspected/complete - only copies the size
      for (const tyreItem of otherTyreItems) {
        await axios.patch(`${API}/inspections/${inspection.id}/items/${tyreItem.id}/tyre-size`, {
          tyre_size: tyreCopyModal.tyreSize
        });
      }
      
      setTyreCopyModal({ show: false, measurementValue: '', tyreSize: '', percentWorn: '', currentTyre: '' });
      navigate(`/app/tech/job/${id}/section/${item.section}`);
    } catch (err) {
      setError("Failed to copy tyre size to other tyres");
      console.error(err);
    } finally {
      setCopyingTyres(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!item) return <div className="p-8 text-center">Item not found</div>;

  // Check if this is a tyre item (still used for other tyre-specific logic)
  const isTyreItem = item.item_key?.startsWith('tyre_');

  const statusOptions = [
    { value: "green", label: "Green - Pass", color: "bg-green-500" },
    { value: "amber", label: "Amber - Advisory", color: "bg-amber-500" },
    { value: "red", label: "Red - Fail", color: "bg-red-500" },
  ];

  // "Dangerous" option ONLY available on approved safety-critical items
  // This is a compliance-grade classification, not just "higher than red"
  if (item.allows_dangerous) {
    statusOptions.push({ value: "dangerous", label: "⚠️ DANGEROUS", color: "bg-black" });
  }

  if (item.allows_blue) {
    statusOptions.push({ 
      value: "blue", 
      label: item.blue_reason ? `Blue - ${item.blue_reason}` : "Blue - N/A", 
      color: "bg-blue-500",
      reason: item.blue_reason 
    });
  }

  // Get common issues for current status
  const currentIssues = status === "amber" ? commonIssues.amber : status === "red" ? commonIssues.red : status === "dangerous" ? commonIssues.red : status === "blue" ? (commonIssues.blue || []) : [];

  // Items that should show data entry IMMEDIATELY (before status selection)
  // These items have auto-calculated status based on measurements/readings
  const isMeasurementFirstItem = item.requires_measurement || item.is_battery_test || item.is_coolant_assessment || item.is_brake_fluid_assessment;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      {/* Photo Annotation Modal */}
      {annotatingPhoto && (
        <PhotoAnnotator
          photo={annotatingPhoto}
          onSave={handleAnnotationSave}
          onCancel={handleAnnotationCancel}
        />
      )}
      
      <div className="p-4 max-w-2xl mx-auto">
        <Button 
          variant="ghost" 
          className="mb-2"
          onClick={() => navigate(`/app/tech/job/${id}/section/${item.section}`)}
          data-testid="back-to-items-btn"
        >
          ← Back
        </Button>
        
        <Card 
          data-testid="item-detail-card"
          className={status === "dangerous" ? "border-2 border-red-600 bg-black/5" : ""}
        >
          <CardHeader className={status === "dangerous" ? "bg-black text-white rounded-t-lg" : ""}>
            <div className="flex items-center justify-between">
              <CardTitle className={status === "dangerous" ? "text-white" : ""}>{item.item_label}</CardTitle>
              {status === "dangerous" && (
                <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  DANGEROUS
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6 flex flex-col">
            {/* DRAFT RESTORE PROMPT (#1) */}
            {draftRestorePrompt && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded flex items-start gap-3" data-testid="draft-restore-prompt">
                <Save className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900">Unsaved progress found</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    A draft of this item was auto-saved on this device {(() => {
                      const m = Math.floor((Date.now() - draftRestorePrompt.savedAt) / 60000);
                      return m < 1 ? "just now" : m === 1 ? "1 minute ago" : `${m} minutes ago`;
                    })()}. Restore?
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => {
                        const d = draftRestorePrompt;
                        if (d.status) { setStatus(d.status); setStatusUserSet(true); }
                        if (d.notes != null) setNotes(d.notes);
                        if (d.measurementValue != null) setMeasurementValue(d.measurementValue);
                        if (d.secondaryMeasurementValue != null) setSecondaryMeasurementValue(d.secondaryMeasurementValue);
                        if (d.affectedSide) setAffectedSide(d.affectedSide);
                        if (Array.isArray(d.affectedLocations)) setAffectedLocations(d.affectedLocations);
                        if (d.affectedPosition) setAffectedPosition(d.affectedPosition);
                        if (Array.isArray(d.affectedPositions)) setAffectedPositions(d.affectedPositions);
                        if (d.tyrePercentWorn != null) setTyrePercentWorn(d.tyrePercentWorn);
                        if (d.tyreSize != null) setTyreSize(d.tyreSize);
                        if (d.tyreBrand != null) setTyreBrand(d.tyreBrand);
                        if (d.techIntent) setTechIntent(d.techIntent);
                        if (Array.isArray(d.selectedIssues)) setSelectedIssues(d.selectedIssues);
                        setDraftRestorePrompt(null);
                        toast.success("Draft restored");
                      }}
                      data-testid="restore-draft-btn"
                    >
                      Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        try { localStorage.removeItem(`vi-draft-${itemId}`); } catch (e) { /* ignore */ }
                        setDraftRestorePrompt(null);
                      }}
                      data-testid="discard-draft-btn"
                    >
                      Discard
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {error && (
              <div ref={errorRef} className="p-3 bg-red-100 text-red-700 rounded flex items-center gap-2" data-testid="item-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* TYRE ITEMS: Measurement FIRST — above status for fastest tech workflow */}
            {['tyre_fl', 'tyre_fr', 'tyre_rl', 'tyre_rr'].includes(item?.item_key) && (
              <div className="bg-slate-50 border-2 border-slate-300 rounded-lg p-4 space-y-4">
                <div>
                  <Label className="font-bold text-lg text-slate-800 mb-1 block">
                    Tread Depth (mm) <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-slate-500 mb-3">Enter lowest tread depth reading — status will auto-assign</p>
                  <Input
                    type="number"
                    step="0.1"
                    value={measurementValue}
                    onChange={(e) => setMeasurementValue(e.target.value)}
                    placeholder="e.g. 3.5"
                    className={`text-2xl font-bold h-14 ${!measurementValue ? 'border-red-400 border-2 bg-red-50' : 'border-green-400 bg-green-50'}`}
                    data-testid="tyre-measurement-input"
                  />
                </div>
                {measurementValue && (
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${
                    parseFloat(measurementValue) <= 1.6 ? 'bg-red-100 border border-red-300' :
                    parseFloat(measurementValue) <= 3.0 ? 'bg-amber-100 border border-amber-300' :
                    'bg-green-100 border border-green-300'
                  }`}>
                    <div className={`w-4 h-4 rounded-full ${
                      parseFloat(measurementValue) <= 1.6 ? 'bg-red-500' :
                      parseFloat(measurementValue) <= 3.0 ? 'bg-amber-500' :
                      'bg-green-500'
                    }`} />
                    <span className="font-semibold text-sm">
                      {parseFloat(measurementValue) <= 1.6 ? 'RED — Below legal minimum (1.6mm)' :
                       parseFloat(measurementValue) <= 3.0 ? 'AMBER — Approaching legal minimum' :
                       'GREEN — Adequate tread depth'}
                    </span>
                    {tyrePercentWorn && <span className="ml-auto text-xs text-slate-600">{tyrePercentWorn}% worn</span>}
                  </div>
                )}
                
                {/* Tyre Size and Brand — inline with measurement */}
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                  <div>
                    <Label className="text-sm text-slate-700 mb-1 block">
                      Tyre Size <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      value={tyreSize}
                      onChange={(e) => setTyreSize(e.target.value.toUpperCase())}
                      placeholder="e.g. 205/55R16"
                      className={`${!tyreSize ? 'border-red-300 bg-red-50' : ''}`}
                      data-testid="tyre-size-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Found on tyre sidewall</p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-700 mb-1 block">
                      Tyre Brand <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      value={tyreBrand}
                      onChange={(e) => setTyreBrand(e.target.value)}
                      placeholder="e.g. Michelin"
                      className={`${!tyreBrand ? 'border-red-300 bg-red-50' : ''}`}
                      data-testid="tyre-brand-input"
                    />
                    <div className="flex flex-wrap gap-1 mt-2">
                      {['Budget', 'Michelin', 'Continental', 'Pirelli', 'Bridgestone', 'Goodyear'].map(brand => (
                        <Button
                          key={brand}
                          type="button"
                          variant="outline"
                          size="sm"
                          className={`text-xs px-2 py-1 ${tyreBrand === brand ? 'bg-blue-100 border-blue-400' : ''}`}
                          onClick={() => setTyreBrand(brand)}
                        >
                          {brand}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* WIPER & WASHER SYSTEM — Unified multi-component inspection */}
            {item?.is_wiper_washer_system && (
              <div className="space-y-3">
                {/* Sub-component sections */}
                {[
                  { key: "front_left", label: "Front Left Blade", showBladeType: true,
                    issueGroups: {
                      amber: [
                        { name: "Wipe Quality", items: ["Smearing", "Streaking", "Minor streaking", "Reduced wipe quality", "Poor screen contact"] },
                        { name: "Mechanical", items: ["Slight judder", "Skipping across screen", "Excessive noise"] },
                        { name: "Wear", items: ["Early rubber deterioration"] },
                      ],
                      red: [
                        { name: "Physical Damage", items: ["Split rubber edge", "Torn blade", "Missing rubber section", "Perished rubber"] },
                        { name: "Structural", items: ["Distorted beam structure", "Corroded frame", "Metal contacting screen"] },
                        { name: "Safety", items: ["Severe smearing", "Dangerous visibility reduction", "Ineffective wiping in wet conditions", "Poor clearing in rain simulation"] },
                      ],
                    }},
                  { key: "front_right", label: "Front Right Blade", showBladeType: true,
                    issueGroups: {
                      amber: [
                        { name: "Wipe Quality", items: ["Smearing", "Streaking", "Minor streaking", "Reduced wipe quality", "Poor screen contact"] },
                        { name: "Mechanical", items: ["Slight judder", "Skipping across screen", "Excessive noise"] },
                        { name: "Wear", items: ["Early rubber deterioration"] },
                      ],
                      red: [
                        { name: "Physical Damage", items: ["Split rubber edge", "Torn blade", "Missing rubber section", "Perished rubber"] },
                        { name: "Structural", items: ["Distorted beam structure", "Corroded frame", "Metal contacting screen"] },
                        { name: "Safety", items: ["Severe smearing", "Dangerous visibility reduction", "Ineffective wiping in wet conditions", "Poor clearing in rain simulation"] },
                      ],
                    }},
                  { key: "rear", label: "Rear Blade", showBladeType: true, allowNotFitted: true,
                    issueGroups: {
                      amber: [
                        { name: "Wipe Quality", items: ["Smearing", "Streaking", "Reduced wipe quality"] },
                        { name: "Mechanical", items: ["Slight judder", "Excessive noise"] },
                        { name: "Wear", items: ["Early rubber deterioration"] },
                      ],
                      red: [
                        { name: "Physical Damage", items: ["Split rubber edge", "Torn blade", "Missing rubber section", "Perished rubber"] },
                        { name: "Safety", items: ["Rear blade ineffective", "Corroded frame", "Metal contacting screen"] },
                      ],
                    }},
                  { key: "washers", label: "Washer System", showBladeType: false,
                    issueGroups: {
                      amber: [
                        { name: "Performance", items: ["Weak washer pressure", "Washer misaligned", "Pump noisy"] },
                        { name: "Fluid", items: ["Washer bottle low"] },
                        { name: "Rear", items: ["Rear washer not operating"] },
                      ],
                      red: [
                        { name: "Not Working", items: ["Washer inoperative", "Washer pump failed", "Jets blocked"] },
                        { name: "Other", items: ["Washer bottle empty", "Leak detected", "Washer issue affecting performance"] },
                      ],
                    }},
                  { key: "arm", label: "Wiper Arm / Mechanical", showBladeType: false,
                    issueGroups: {
                      amber: [
                        { name: "Wear", items: ["Excessive play", "Linkage noise", "Arm alignment slightly off"] },
                      ],
                      red: [
                        { name: "Structural", items: ["Arm loose", "Arm bent", "Excessive movement"] },
                        { name: "Mechanical", items: ["Linkage noisy - requires attention", "Binding mechanism"] },
                      ],
                    }},
                ].map(comp => {
                  const sub = wiperData[comp.key];
                  const isNotFitted = comp.allowNotFitted && sub.not_fitted;
                  const bladeTypes = [
                    { value: "conventional", label: "Conventional" },
                    { value: "beam_aero", label: "Beam/Aero" },
                    { value: "hybrid", label: "Hybrid" },
                    ...(comp.key === "rear" ? [{ value: "rear_specific", label: "Rear Specific" }] : []),
                    { value: "unknown", label: "Unknown" },
                  ];
                  const currentGroups = comp.issueGroups[sub.status] || [];

                  const updateSub = (field, value) => {
                    setWiperData(prev => ({
                      ...prev,
                      [comp.key]: { ...prev[comp.key], [field]: value }
                    }));
                  };
                  const toggleIssue = (issue) => {
                    setWiperData(prev => {
                      const current = prev[comp.key].issues || [];
                      return {
                        ...prev,
                        [comp.key]: {
                          ...prev[comp.key],
                          issues: current.includes(issue) ? current.filter(i => i !== issue) : [...current, issue]
                        }
                      };
                    });
                  };

                  return (
                    <div key={comp.key} className={`rounded-lg border-2 overflow-hidden ${
                      isNotFitted ? 'border-blue-200 bg-blue-50/30' :
                      sub.status === 'red' ? 'border-red-300 bg-red-50/30' :
                      sub.status === 'amber' ? 'border-amber-300 bg-amber-50/30' :
                      sub.status === 'green' ? 'border-green-300 bg-green-50/30' :
                      'border-slate-200'
                    }`}>
                      {/* Header */}
                      <div className="p-3 bg-white/60 flex items-center justify-between">
                        <span className="font-semibold text-slate-800 text-sm">{comp.label}</span>
                        {sub.status && !isNotFitted && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                            sub.status === 'red' ? 'bg-red-500 text-white' :
                            sub.status === 'amber' ? 'bg-amber-500 text-white' :
                            'bg-green-500 text-white'
                          }`}>{sub.status.toUpperCase()}</span>
                        )}
                        {isNotFitted && <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-500 text-white">NOT FITTED</span>}
                      </div>
                      
                      <div className="p-3 space-y-3">
                        {/* Not Fitted toggle for rear */}
                        {comp.allowNotFitted && (
                          <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${isNotFitted ? 'bg-blue-100 border-blue-300' : 'border-slate-200'}`}>
                            <input type="checkbox" checked={isNotFitted} onChange={(e) => {
                              updateSub('not_fitted', e.target.checked);
                              if (e.target.checked) { updateSub('status', ''); updateSub('issues', []); updateSub('blade_type', ''); }
                            }} className="w-4 h-4" />
                            <span className="text-sm text-slate-700">Not fitted to vehicle</span>
                          </label>
                        )}

                        {!isNotFitted && (
                          <>
                            {/* Status buttons */}
                            <div className="grid grid-cols-3 gap-2">
                              {['green', 'amber', 'red'].map(s => (
                                <button key={s} type="button" onClick={() => { updateSub('status', s); if (s === 'green') updateSub('issues', []); }}
                                  className={`p-2 rounded text-sm font-bold transition-all ${
                                    sub.status === s
                                      ? s === 'green' ? 'bg-green-500 text-white' : s === 'amber' ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'
                                      : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'
                                  }`}
                                  data-testid={`wiper-${comp.key}-${s}`}
                                >
                                  {s.toUpperCase()}
                                </button>
                              ))}
                            </div>

                            {/* Blade type for blade items */}
                            {comp.showBladeType && sub.status && (
                              <div>
                                <Label className="text-xs text-slate-600 mb-1 block">Blade Type</Label>
                                <div className="flex flex-wrap gap-1">
                                  {bladeTypes.map(bt => (
                                    <button key={bt.value} type="button" onClick={() => updateSub('blade_type', bt.value)}
                                      className={`text-xs px-2 py-1 rounded transition-all ${
                                        sub.blade_type === bt.value ? 'bg-blue-500 text-white' : 'bg-white border border-slate-200 text-slate-600'
                                      }`}
                                      data-testid={`wiper-${comp.key}-type-${bt.value}`}
                                    >
                                      {bt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Grouped issues — same UI as all other items */}
                            {(sub.status === 'amber' || sub.status === 'red') && currentGroups.length > 0 && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <Label className="text-xs text-slate-600">
                                    Issues Found <span className="text-red-500">*</span>
                                  </Label>
                                  {(sub.issues || []).length > 0 && (
                                    <button type="button" onClick={() => updateSub('issues', [])} className="text-xs text-red-500 hover:text-red-700">
                                      Clear ({(sub.issues || []).length})
                                    </button>
                                  )}
                                </div>
                                <div className="max-h-52 overflow-y-auto border rounded-lg p-2 space-y-3 bg-white">
                                  {currentGroups.map((group, gIdx) => (
                                    <div key={gIdx} className="space-y-1">
                                      <p className={`text-xs font-bold uppercase tracking-wide px-2 py-1 rounded ${
                                        sub.status === "red" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                                      }`}>
                                        {group.name}
                                      </p>
                                      {group.items.map((issue, iIdx) => (
                                        <label key={iIdx} className={`flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 transition-colors ${
                                          (sub.issues || []).includes(issue) ? (sub.status === "red" ? "bg-red-50" : "bg-amber-50") : ""
                                        }`}>
                                          <input
                                            type="checkbox"
                                            checked={(sub.issues || []).includes(issue)}
                                            onChange={() => toggleIssue(issue)}
                                            className="mt-0.5 rounded"
                                          />
                                          <span className="text-sm">{issue}</span>
                                        </label>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {(sub.issues || []).length === 0 
                                    ? "Select one or more issues"
                                    : `${(sub.issues || []).length} issue${(sub.issues || []).length > 1 ? "s" : ""} selected`
                                  }
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Pair replacement suggestion */}
                {(wiperData.front_left.status === 'amber' || wiperData.front_left.status === 'red' ||
                  wiperData.front_right.status === 'amber' || wiperData.front_right.status === 'red') && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm text-blue-800 font-medium">Front blades recommended as a pair for even wipe performance</span>
                  </div>
                )}

                {/* Overall status indicator */}
                {status && (
                  <div className={`p-2 rounded-lg text-center text-sm font-bold ${
                    status === 'red' ? 'bg-red-100 text-red-800' :
                    status === 'amber' ? 'bg-amber-100 text-amber-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    Overall: {status.toUpperCase()} (auto-calculated from worst sub-component)
                  </div>
                )}
              </div>
            )}

            {/* WIPER BLADE ITEMS (legacy individual): Blade type selection at top */}
            {item?.is_wiper_blade && !item?.is_wiper_washer_system && (
              <div className="bg-slate-50 border-2 border-slate-300 rounded-lg p-4 space-y-3">
                <Label className="font-bold text-lg text-slate-800 block">Blade Type <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "conventional", label: "Conventional / Frame" },
                    { value: "beam_aero", label: "Beam / Aero" },
                    { value: "hybrid", label: "Hybrid" },
                    ...(item.wiper_position === "rear" ? [{ value: "rear_specific", label: "Rear Specific" }] : []),
                    { value: "unknown", label: "Unknown" },
                  ].map(bt => (
                    <button
                      key={bt.value}
                      type="button"
                      className={`p-3 rounded border-2 text-center text-sm font-medium transition-all ${
                        wiperBladeType === bt.value ? "border-blue-500 bg-blue-50 text-blue-800" : "border-gray-200 hover:border-gray-400"
                      }`}
                      onClick={() => setWiperBladeType(bt.value)}
                      data-testid={`blade-type-${bt.value}`}
                    >
                      {bt.label}
                    </button>
                  ))}
                </div>
                {!wiperBladeType && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Select blade type before saving
                  </p>
                )}
              </div>
            )}

            {/* Status Selection */}
            <div className={isMeasurementFirstItem ? "order-2" : ""} data-testid="status-selection-wrapper">
              <Label className="font-semibold mb-2 block">Status *</Label>
              <div className="grid grid-cols-2 gap-2">
                {statusOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`p-3 rounded border-2 flex items-center gap-2 transition-all ${
                      status === opt.value ? "border-black" : "border-gray-200"
                    }`}
                    onClick={() => {
                      setStatusUserSet(true);
                      setStatusSuggestion(null);
                      setStatus(opt.value);
                      setSelectedIssues([]);
                      setError("");
                      if (opt.value === "green" || opt.value === "blue") {
                        setNotes("");
                      }
                    }}
                    data-testid={`status-btn-${opt.value}`}
                  >
                    <div className={`w-5 h-5 rounded-full ${opt.color} flex items-center justify-center text-white text-[10px] font-bold ring-1 ring-black/10`}>
                      {opt.value === "green" ? "G" : opt.value === "amber" ? "A" : opt.value === "red" ? "R" : opt.value === "blue" ? "B" : opt.value === "dangerous" ? "D" : ""}
                    </div>
                    <span className="text-sm">{opt.label}</span>
                  </button>
                ))}
              </div>
              {/* MOT current-test flag banner — surfaces a warm warning when
                  the CURRENT MOT recorded a failure / advisory against this
                  exact item. Always rendered when flagged so the tech sees
                  the wording, regardless of status. Only blocks "green"
                  unless the tech writes a short rectified-note. */}
              {motFlagsForThisItem && (
                <div
                  className={`mt-3 rounded-md border p-3 ${
                    motFlagsForThisItem.counts.terminal > 0
                      ? "bg-red-50 border-red-200"
                      : "bg-amber-50 border-amber-200"
                  }`}
                  data-testid="mot-current-flag-banner"
                >
                  <p className={`text-xs font-semibold ${
                    motFlagsForThisItem.counts.terminal > 0 ? "text-red-800" : "text-amber-800"
                  }`}>
                    Flagged on the current MOT
                    {motFlagsForThisItem.counts.terminal > 0
                      ? ` · ${motFlagsForThisItem.counts.terminal} failure${motFlagsForThisItem.counts.terminal === 1 ? "" : "s"}`
                      : ""}
                    {motFlagsForThisItem.counts.advisory > 0
                      ? ` · ${motFlagsForThisItem.counts.advisory} advisory/-ies`
                      : ""}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {motFlagsForThisItem.defects.slice(0, 3).map((d, idx) => (
                      <li
                        key={idx}
                        className={`text-[11px] ${
                          d.is_terminal ? "text-red-900" : "text-amber-900"
                        }`}
                        data-testid={`mot-current-flag-defect-${idx}`}
                      >
                        <span className="font-semibold uppercase text-[9.5px] tracking-wide mr-1">
                          {d.severity_label}
                        </span>
                        {d.text}
                      </li>
                    ))}
                  </ul>
                  {status === "green" && (
                    <div className="mt-3" data-testid="mot-green-override-section">
                      <Label className={`text-xs font-semibold mb-1 block ${
                        motFlagsForThisItem.counts.terminal > 0 ? "text-red-800" : "text-amber-800"
                      }`}>
                        Required: how was this rectified?
                      </Label>
                      <Textarea
                        value={motGreenOverrideNote}
                        onChange={(e) => setMotGreenOverrideNote(e.target.value)}
                        placeholder="e.g. Pads replaced today — within spec. Or: re-checked on lift, drum reading within limits."
                        rows={2}
                        className="text-sm"
                        data-testid="mot-green-override-note-input"
                      />
                      <p className="mt-1 text-[10.5px] italic text-slate-500">
                        Saved against this item for the audit trail.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Status suggestion chip (#4) — only when a measurement-derived suggestion differs from the tech's pick */}
              {statusSuggestion && statusSuggestion.value !== status && status !== "dangerous" && (
                <div
                  className="mt-2 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2"
                  data-testid="status-suggestion-chip"
                >
                  <p className="text-xs text-blue-800">
                    <span className="font-semibold">Suggested:</span> {statusSuggestion.value.toUpperCase()} —{" "}
                    <span className="italic">{statusSuggestion.reason}</span>
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => {
                      setStatusUserSet(true);
                      setStatus(statusSuggestion.value);
                      setStatusSuggestion(null);
                    }}
                    data-testid="apply-status-suggestion-btn"
                  >
                    Use suggested
                  </Button>
                </div>
              )}
              {/* Tech intent radio (#7) — only when status is amber/red/dangerous */}
              {(status === "amber" || status === "red" || status === "dangerous") && techIntentOptions.length > 0 && (
                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="tech-intent-section">
                  <Label className="text-xs font-semibold text-slate-700 mb-2 block">Why this status? (optional — helps FOH)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {techIntentOptions.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`px-2.5 py-1 rounded text-xs border transition-all ${
                          techIntent === opt.value
                            ? "bg-slate-800 text-white border-slate-800"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                        }`}
                        title={opt.hint}
                        onClick={() => setTechIntent(techIntent === opt.value ? "" : opt.value)}
                        data-testid={`tech-intent-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Conditional Fields - show immediately for measurement items, or after status selection */}
            {(status || isMeasurementFirstItem) && (
              <div className={(isMeasurementFirstItem ? "order-1 " : "") + "space-y-6"} data-testid="conditional-fields-wrapper">
                {/* Side / Corner Specification — MULTI-SELECT for items like brake hoses,
                    brake pipes, wheel bearings, shock absorbers, etc. Tech can pick multiple
                    corners simultaneously when several locations are affected. */}
                {item.requires_side_specification && (status === "amber" || status === "red" || status === "dangerous") && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <Label className="font-semibold mb-3 block">
                      {/* For exhaust-section items the natural language is "fault location" not "side/corner" */}
                      {["exhaust_system","catalytic_converter","dpf_condition","underside_corrosion"].includes(item.item_key)
                        ? "Which fault location(s)? *"
                        : "Which side(s) / corner(s) are affected? *"}
                      {" "}
                      <span className="font-normal text-xs text-slate-500">(tap multiple to select more than one)</span>
                    </Label>
                    {/* Use dynamic side_options if available, otherwise fall back to default NS/OS/Both */}
                    {item.side_options && item.side_options.length > 0 ? (
                      <div className={`grid gap-2 ${item.side_options.length <= 3 ? 'grid-cols-3' : item.side_options.length <= 4 ? 'grid-cols-4' : 'grid-cols-4'}`}>
                        {item.side_options.map((opt) => {
                          const val = opt.value.toUpperCase();
                          const selected = affectedLocations.includes(val);
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`p-3 rounded border-2 flex flex-col items-center gap-1 transition-all ${
                                selected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                              }`}
                              onClick={() => {
                                setAffectedLocations(prev => {
                                  // "all" / "both" are mutually exclusive shortcuts — replace the selection
                                  if (val === "ALL" || val === "BOTH" || val === "FRONT_BOTH" || val === "REAR_BOTH") {
                                    return prev.includes(val) ? [] : [val];
                                  }
                                  // Toggle the value; remove any "all"/"both" shortcuts if present
                                  const next = prev.includes(val)
                                    ? prev.filter(v => v !== val)
                                    : [...prev.filter(v => !["ALL","BOTH","FRONT_BOTH","REAR_BOTH"].includes(v)), val];
                                  return next;
                                });
                                // Keep legacy affectedSide in sync with first selected for backward compat
                                setAffectedSide(prev => {
                                  if (val === "ALL" || val === "BOTH" || val === "FRONT_BOTH" || val === "REAR_BOTH") {
                                    return affectedLocations.includes(val) ? "" : val;
                                  }
                                  return val;
                                });
                              }}
                              data-testid={`side-${opt.value}-btn`}
                              aria-pressed={selected}
                            >
                              <span className="text-sm font-bold">{opt.label}</span>
                              {selected && <Check className="w-3 h-3 text-blue-600" />}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { val: "NS", label: "NS", sub: "Nearside (Left)" },
                          { val: "OS", label: "OS", sub: "Offside (Right)" },
                          { val: "BOTH", label: "Both", sub: "Both Sides" },
                        ].map(opt => {
                          const selected = affectedLocations.includes(opt.val);
                          return (
                            <button
                              key={opt.val}
                              type="button"
                              className={`p-3 rounded border-2 flex flex-col items-center gap-1 transition-all ${
                                selected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                              }`}
                              onClick={() => {
                                setAffectedLocations(prev => {
                                  if (opt.val === "BOTH") return prev.includes("BOTH") ? [] : ["BOTH"];
                                  const next = prev.includes(opt.val)
                                    ? prev.filter(v => v !== opt.val)
                                    : [...prev.filter(v => v !== "BOTH"), opt.val];
                                  return next;
                                });
                                setAffectedSide(opt.val);
                              }}
                              data-testid={`side-${opt.val.toLowerCase()}-btn`}
                              aria-pressed={selected}
                            >
                              <span className="text-lg font-bold">{opt.label}</span>
                              <span className="text-xs text-slate-500">{opt.sub}</span>
                              {selected && <Check className="w-3 h-3 text-blue-600" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">NS = Passenger side (UK), OS = Driver side (UK). Select all affected corners if more than one.</p>
                    {affectedLocations.length > 1 && (
                      <p className="text-xs text-blue-700 mt-1 font-medium" data-testid="multi-location-summary">{affectedLocations.length} locations selected: {affectedLocations.join(' + ')}</p>
                    )}
                  </div>
                )}

                {/* Inner/Outer Position - for items like CV joints & boots, track rod ends.
                    Single value (inner/outer/both), shown when item has position_options
                    AND requires_side_specification (i.e. it's NOT the multi-position indicator case). */}
                {item.position_options && item.position_options.length > 0 && item.requires_side_specification && !item.requires_position_specification && (status === "amber" || status === "red" || status === "dangerous") && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="inner-outer-picker">
                    <Label className="font-semibold mb-3 block">Inner or Outer? *</Label>
                    <div className={`grid gap-2 ${item.position_options.length <= 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                      {item.position_options.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`p-3 rounded border-2 flex flex-col items-center gap-1 transition-all ${
                            affectedPosition === opt.value ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => setAffectedPosition(opt.value)}
                          data-testid={`position-${opt.value}-btn`}
                        >
                          <span className="text-sm font-bold">{opt.label}</span>
                          {opt.hint && <span className="text-[10px] text-slate-500 text-center">{opt.hint}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Position Specification - for multi-position items like indicators */}
                {item.requires_position_specification && (status === "amber" || status === "red" || status === "dangerous") && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <Label className="font-semibold mb-3 block">
                      Which position(s) affected? * 
                      {item.allows_multi_position && <span className="text-xs font-normal text-slate-500 ml-2">(Select all that apply)</span>}
                    </Label>
                    {item.position_options && item.position_options.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {item.position_options.map((opt) => {
                          const isSelected = affectedPositions.includes(opt.value);
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`p-3 rounded border-2 flex flex-col items-center gap-1 transition-all ${
                                isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                              }`}
                              onClick={() => {
                                if (item.allows_multi_position) {
                                  // Multi-select: toggle the position
                                  if (isSelected) {
                                    setAffectedPositions(affectedPositions.filter(p => p !== opt.value));
                                  } else {
                                    setAffectedPositions([...affectedPositions, opt.value]);
                                  }
                                } else {
                                  // Single-select: replace
                                  setAffectedPositions([opt.value]);
                                }
                              }}
                              data-testid={`position-${opt.value}-btn`}
                            >
                              <span className="text-sm font-bold">{opt.label}</span>
                              {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {affectedPositions.length > 0 && (
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Selected: {affectedPositions.map(p => {
                          const opt = item.position_options?.find(o => o.value === p);
                          return opt?.label || p;
                        }).join(", ")}
                      </p>
                    )}
                  </div>
                )}

                {/* ============== DANGEROUS DESIGNATION UI (COMPLIANCE-GRADE) ============== */}
                {status === "dangerous" && item.allows_dangerous && (
                  <div className="bg-red-50 border-2 border-red-400 rounded-lg p-5 space-y-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-red-800 text-lg">
                          Dangerous Designation
                          {item.dangerous_config?.is_fire_risk && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-600 text-white text-xs font-bold align-middle"
                              data-testid={`fire-risk-badge-${item.id}`}
                            >
                              🔥 FIRE RISK
                            </span>
                          )}
                        </h3>
                        <p className="text-red-700 text-sm mt-1">
                          This is a compliance-grade safety classification indicating immediate risk.
                          {item.dangerous_config?.condition_note && (
                            <span className="block font-medium mt-1 text-red-600">
                              ⚠️ {item.dangerous_config.condition_note}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Danger Category Selection - MANDATORY (hide for suspension/steering items which have enhanced UI) */}
                    {!SUSPENSION_STEERING_ITEMS.includes(item.item_key) && (
                      <div className="border-t border-red-200 pt-4">
                        <Label className="font-semibold text-red-800 mb-3 block">
                          Risk Category * <span className="text-xs font-normal">(Select all that apply)</span>
                        </Label>
                        <div className="space-y-2">
                          {item.danger_category_options?.map(cat => (
                            <label
                              key={cat.id}
                              className={`flex items-center gap-3 p-3 rounded border-2 cursor-pointer transition-all ${
                                dangerCategories.includes(cat.id) 
                                  ? "border-red-500 bg-red-100" 
                                  : "border-gray-200 hover:border-red-300"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={dangerCategories.includes(cat.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setDangerCategories([...dangerCategories, cat.id]);
                                  } else {
                                    setDangerCategories(dangerCategories.filter(c => c !== cat.id));
                                  }
                                }}
                                className="w-5 h-5 rounded border-2 border-red-400"
                                data-testid={`danger-cat-${cat.id}`}
                              />
                              <span className="text-sm font-medium text-red-800">{cat.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Structured Danger Reasons - MANDATORY (hide for suspension/steering items which have enhanced UI) */}
                    {!SUSPENSION_STEERING_ITEMS.includes(item.item_key) && (
                      <div className="border-t border-red-200 pt-4">
                        <Label className="font-semibold text-red-800 mb-3 block">
                          Specific Danger Reason * <span className="text-xs font-normal">(Select all that apply)</span>
                        </Label>
                        <div className="space-y-2">
                          {item.dangerous_config?.reasons?.map((reason, idx) => (
                            <label
                              key={idx}
                              className={`flex items-center gap-3 p-3 rounded border-2 cursor-pointer transition-all ${
                                dangerReasons.includes(reason) 
                                  ? "border-red-500 bg-red-100" 
                                  : "border-gray-200 hover:border-red-300"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={dangerReasons.includes(reason)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setDangerReasons([...dangerReasons, reason]);
                                  } else {
                                    setDangerReasons(dangerReasons.filter(r => r !== reason));
                                  }
                                }}
                                className="w-5 h-5 rounded border-2 border-red-400"
                                data-testid={`danger-reason-${idx}`}
                              />
                              <span className="text-sm text-red-800">{reason}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ============== SUSPENSION/STEERING ENHANCED DANGEROUS UI ============== */}
                    {SUSPENSION_STEERING_ITEMS.includes(item.item_key) && (
                      <>
                        {/* Warning Banner */}
                        <div className="border-t border-red-200 pt-4">
                          <div className="bg-black border-2 border-red-600 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                              <div className="text-white">
                                <h4 className="font-bold text-red-400">⚠️ DANGEROUS DESIGNATION WARNING</h4>
                                <p className="text-sm mt-2 text-gray-200">
                                  You are marking this item as <strong>Dangerous</strong>. This indicates an <strong>immediate safety risk</strong> and potential loss of steering, suspension support, or wheel control.
                                </p>
                                <p className="text-sm mt-2 text-gray-300">
                                  Confirm this designation reflects an <strong>urgent safety condition</strong>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Mandatory Justification - Dropdown Selection */}
                        <div className="border-t border-red-200 pt-4">
                          <Label className="font-semibold text-red-800 mb-2 block">
                            Safety Justification * <span className="text-xs font-normal">(select or type)</span>
                          </Label>
                          <p className="text-xs text-red-600 mb-3">
                            Select the condition that best describes the immediate safety risk:
                          </p>
                          
                          {/* Dropdown options from danger reasons */}
                          <div className="space-y-2 mb-3">
                            {item.dangerous_config?.reasons?.map((reason, idx) => (
                              <label
                                key={idx}
                                className={`flex items-center gap-3 p-3 rounded border-2 cursor-pointer transition-all ${
                                  notes === reason 
                                    ? "border-red-500 bg-red-100" 
                                    : "border-gray-200 hover:border-red-300 bg-white"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="justification-reason"
                                  checked={notes === reason}
                                  onChange={() => setNotes(reason)}
                                  className="w-5 h-5 text-red-600"
                                  data-testid={`justification-option-${idx}`}
                                />
                                <span className="text-sm text-red-800">{reason}</span>
                              </label>
                            ))}
                            
                            {/* Other option for manual entry */}
                            <label
                              className={`flex items-center gap-3 p-3 rounded border-2 cursor-pointer transition-all ${
                                notes && !item.dangerous_config?.reasons?.includes(notes)
                                  ? "border-red-500 bg-red-100" 
                                  : "border-gray-200 hover:border-red-300 bg-white"
                              }`}
                            >
                              <input
                                type="radio"
                                name="justification-reason"
                                checked={notes && !item.dangerous_config?.reasons?.includes(notes)}
                                onChange={() => setNotes("")}
                                className="w-5 h-5 text-red-600"
                                data-testid="justification-option-other"
                              />
                              <span className="text-sm text-red-800 font-medium">Other (specify below)</span>
                            </label>
                          </div>
                          
                          {/* Manual entry field - shown when "Other" is selected or for additional context */}
                          {(notes === "" || (notes && !item.dangerous_config?.reasons?.includes(notes))) && (
                            <div className="mt-3">
                              <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Describe the dangerous condition in detail..."
                                rows={2}
                                className="border-red-300 focus:border-red-500"
                                data-testid="dangerous-justification-input"
                              />
                              <p className={`text-xs mt-1 ${notes.length >= 25 ? "text-green-600" : "text-red-600"}`}>
                                {notes.length}/25 characters {notes.length >= 25 ? "✓" : "minimum required"}
                              </p>
                            </div>
                          )}
                          
                          {/* Show selected justification */}
                          {notes && item.dangerous_config?.reasons?.includes(notes) && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                              <CheckCircle2 className="w-4 h-4 inline mr-1" />
                              <strong>Selected:</strong> {notes}
                            </div>
                          )}
                        </div>

                        {/* Confirmation Checkbox */}
                        <div className="border-t border-red-200 pt-4">
                          <label className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            dangerousConfirmed 
                              ? "border-red-600 bg-red-100" 
                              : "border-gray-300 bg-white hover:border-red-400"
                          }`}>
                            <input
                              type="checkbox"
                              checked={dangerousConfirmed}
                              onChange={(e) => setDangerousConfirmed(e.target.checked)}
                              className="w-6 h-6 mt-0.5 rounded border-2 border-red-500"
                              data-testid="dangerous-confirm-checkbox"
                            />
                            <div>
                              <span className="font-bold text-red-800">
                                I confirm this item presents an immediate safety risk
                              </span>
                              <p className="text-xs text-red-600 mt-1">
                                This confirmation will be logged with your name and timestamp for audit purposes.
                              </p>
                            </div>
                          </label>
                        </div>

                        {/* Fleet Compliance Reference (Optional) */}
                        {job?.customer_type === "fleet" && (
                          <div className="border-t border-red-200 pt-4">
                            <Label className="font-semibold text-slate-700 mb-2 block">
                              Compliance Reference <span className="text-xs font-normal text-slate-400">(optional)</span>
                            </Label>
                            <Input
                              value={fleetComplianceRef}
                              onChange={(e) => setFleetComplianceRef(e.target.value)}
                              placeholder="E.g., MOT failure category 4.1.D, Manufacturer threshold ref..."
                              className="border-slate-300"
                              data-testid="fleet-compliance-ref-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Include MOT failure category reference or manufacturer threshold reference if applicable.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Photo Required Reminder */}
                    <div className="border-t border-red-200 pt-4 bg-yellow-50 -mx-5 -mb-5 px-5 py-4 rounded-b-lg">
                      <div className="flex items-center gap-2 text-yellow-800">
                        <Camera className="w-5 h-5" />
                        <span className="font-semibold">Photo Evidence Required</span>
                      </div>
                      <p className="text-yellow-700 text-sm mt-1">
                        At least one clear photo documenting the dangerous condition is mandatory.
                      </p>
                    </div>
                  </div>
                )}

                {/* ============== SUSPENSION STRUCTURED REPAIR PANEL ============== */}
                {/* Shown when suspension/steering item is RED/AMBER/DANGEROUS and schema is available */}
                {SUSPENSION_STEERING_ITEMS.includes(item.item_key) && 
                 structuredRepairSchema && 
                 (status === 'red' || status === 'amber' || status === 'dangerous') && (
                  <div className="border-t border-slate-200 pt-4 mt-4" data-testid="structured-repair-panel">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <Wrench className="w-4 h-4" />
                        Repair Assessment
                      </h4>
                      
                      <div className="grid grid-cols-1 gap-3">
                        {/* Joint Level — only for ball joints */}
                        {structuredRepairSchema.joint_levels && (
                          <div>
                            <Label className="text-xs font-medium text-slate-600 mb-1 block">Joint Position</Label>
                            <div className="flex gap-2">
                              {structuredRepairSchema.joint_levels.map(j => (
                                <button
                                  key={j.id}
                                  type="button"
                                  onClick={() => setRepairJointLevel(j.id)}
                                  className={`px-3 py-1.5 text-xs rounded-md border ${
                                    repairJointLevel === j.id 
                                      ? 'bg-slate-800 text-white border-slate-800' 
                                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                                  }`}
                                  data-testid={`repair-joint-level-${j.id}`}
                                >
                                  {j.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Construction Type — only for ball joints etc */}
                        {structuredRepairSchema.construction_types && (
                          <div>
                            <Label className="text-xs font-medium text-slate-600 mb-1 block">Construction Type</Label>
                            <select 
                              value={repairConstructionType} 
                              onChange={e => setRepairConstructionType(e.target.value)}
                              className="w-full p-2 text-sm border border-slate-300 rounded-md bg-white"
                              data-testid="repair-construction-type"
                            >
                              <option value="">Select...</option>
                              {structuredRepairSchema.construction_types.map(c => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {/* Repair Method — only for ball joints etc */}
                        {structuredRepairSchema.repair_methods && (
                          <div>
                            <Label className="text-xs font-medium text-slate-600 mb-1 block">Recommended Repair</Label>
                            <select 
                              value={repairMethod} 
                              onChange={e => setRepairMethod(e.target.value)}
                              className="w-full p-2 text-sm border border-slate-300 rounded-md bg-white"
                              data-testid="repair-method"
                            >
                              <option value="">Select...</option>
                              {structuredRepairSchema.repair_methods.map(r => (
                                <option key={r.id} value={r.id}>{r.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {/* Arm Condition — only for ball joints etc */}
                        {structuredRepairSchema.arm_conditions && (
                          <div>
                            <Label className="text-xs font-medium text-slate-600 mb-1 block">Arm Condition</Label>
                            <select 
                              value={repairArmCondition} 
                              onChange={e => setRepairArmCondition(e.target.value)}
                              className="w-full p-2 text-sm border border-slate-300 rounded-md bg-white"
                              data-testid="repair-arm-condition"
                            >
                              <option value="">Select...</option>
                              {structuredRepairSchema.arm_conditions.map(a => (
                                <option key={a.id} value={a.id}>{a.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {/* Removal Risk */}
                        <div>
                          <Label className="text-xs font-medium text-slate-600 mb-1 block">Removal Risk</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {structuredRepairSchema.removal_risks.map(r => (
                              <button
                                key={r.id}
                                type="button"
                                onClick={() => setRepairRemovalRisk(r.id)}
                                className={`px-2.5 py-1.5 text-xs rounded-md border ${
                                  repairRemovalRisk === r.id 
                                    ? r.id === 'high_risk' ? 'bg-red-600 text-white border-red-600'
                                      : r.id === 'corrosion_risk' || r.id === 'access_limited' ? 'bg-amber-500 text-white border-amber-500'
                                      : 'bg-slate-800 text-white border-slate-800'
                                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                                }`}
                                data-testid={`repair-removal-risk-${r.id}`}
                              >
                                {r.label.split(' — ')[0]}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Opposite Side Condition */}
                        <div>
                          <Label className="text-xs font-medium text-slate-600 mb-1 block">Opposite Side Condition</Label>
                          <select 
                            value={repairOppositeSide} 
                            onChange={e => setRepairOppositeSide(e.target.value)}
                            className="w-full p-2 text-sm border border-slate-300 rounded-md bg-white"
                            data-testid="repair-opposite-side"
                          >
                            <option value="">Select...</option>
                            {structuredRepairSchema.opposite_side_conditions.map(o => (
                              <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Alignment Implication */}
                        <div>
                          <Label className="text-xs font-medium text-slate-600 mb-1 block">Alignment Requirement</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {structuredRepairSchema.alignment_options.map(a => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={() => setRepairAlignmentImplication(a.id)}
                                className={`px-2.5 py-1.5 text-xs rounded-md border ${
                                  repairAlignmentImplication === a.id 
                                    ? a.id === 'REQUIRED' ? 'bg-red-600 text-white border-red-600'
                                      : a.id === 'STRONGLY_RECOMMENDED' ? 'bg-amber-500 text-white border-amber-500'
                                      : a.id === 'CHECK_AND_ADVISE' ? 'bg-blue-500 text-white border-blue-500'
                                      : 'bg-slate-600 text-white border-slate-600'
                                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                                }`}
                                data-testid={`repair-alignment-${a.id}`}
                              >
                                {a.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ============== ENHANCED ITEM ASSESSMENT PANEL ============== */}
                {/* Generic schema-driven panel for wheel bearings, exhaust, engine leaks, etc. */}
                {ENHANCED_CAPTURE_ITEMS.includes(item.item_key) && 
                 enhancedItemSchema?.supported && 
                 (status === 'red' || status === 'amber' || status === 'dangerous') && (
                  <div className="border-t border-slate-200 pt-4 mt-4" data-testid="enhanced-item-panel">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <Wrench className="w-4 h-4" />
                        {enhancedItemSchema.panel_title}
                      </h4>
                      
                      <div className="grid grid-cols-1 gap-3">
                        {enhancedItemSchema.fields.map(field => {
                          if (field.type === 'select') {
                            return (
                              <div key={field.id}>
                                <Label className="text-xs font-medium text-slate-600 mb-1 block">{field.label}</Label>
                                <select 
                                  value={enhancedItemData[field.id] || ''} 
                                  onChange={e => setEnhancedItemData(prev => ({...prev, [field.id]: e.target.value}))}
                                  className="w-full p-2 text-sm border border-slate-300 rounded-md bg-white"
                                  data-testid={`enhanced-${field.id}`}
                                >
                                  <option value="">Select...</option>
                                  {field.options.map(opt => (
                                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                            );
                          }
                          if (field.type === 'buttons') {
                            return (
                              <div key={field.id}>
                                <Label className="text-xs font-medium text-slate-600 mb-1 block">{field.label}</Label>
                                <div className="flex flex-wrap gap-2">
                                  {field.options.map(opt => (
                                    <button
                                      key={opt.id}
                                      type="button"
                                      onClick={() => setEnhancedItemData(prev => ({...prev, [field.id]: opt.id}))}
                                      className={`px-3 py-1.5 text-xs rounded-md border ${
                                        enhancedItemData[field.id] === opt.id 
                                          ? 'bg-slate-800 text-white border-slate-800' 
                                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                                      }`}
                                      data-testid={`enhanced-${field.id}-${opt.id}`}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          if (field.type === 'toggle') {
                            return (
                              <div key={field.id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <input
                                  type="checkbox"
                                  checked={enhancedItemData[field.id] === true}
                                  onChange={e => setEnhancedItemData(prev => ({...prev, [field.id]: e.target.checked}))}
                                  className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                                  data-testid={`enhanced-${field.id}`}
                                />
                                <div>
                                  <span className="text-sm font-medium text-amber-800">{field.label}</span>
                                  {field.description && (
                                    <p className="text-xs text-amber-600 mt-0.5">{field.description}</p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Measurement - shown if required (but NOT for wheel torque or tyre items which have their own top section) */}
                {item.requires_measurement && status !== "blue" && item?.item_key !== "wheel_torque_confirmation" && !['tyre_fl', 'tyre_fr', 'tyre_rl', 'tyre_rr'].includes(item?.item_key) && (
                  <div className="space-y-4">
                    {/* Measurement Required Banner */}
                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-red-700 font-semibold text-sm">⚠️ MEASUREMENT REQUIRED - Enter value below</span>
                    </div>
                    
                    {/* Disc Specs - Vehicle Lookup (for disc items) */}
                    {item.has_disc_specs && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <Label className="font-semibold text-blue-800">Disc Specifications</Label>
                          <Button 
                            type="button"
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              const newShowState = !showDiscSearch;
                              setShowDiscSearch(newShowState);
                              // Auto-search when opening if we have a pre-filled query
                              if (newShowState && discSearchQuery && discSearchQuery.length >= 2) {
                                handleDiscSearch(discSearchQuery);
                              }
                            }}
                            className="text-blue-600 border-blue-300 hover:bg-blue-100"
                            data-testid="disc-lookup-btn"
                          >
                            <Search className="w-3 h-3 mr-1" />
                            {showDiscSearch ? 'Close' : 'Lookup Vehicle'}
                          </Button>
                        </div>
                        
                        {/* Vehicle Search Panel */}
                        {showDiscSearch && (
                          <div className="mb-4 p-3 bg-white rounded-lg border border-blue-200">
                            <Label className="text-xs text-blue-600 mb-2 block">Search by Make/Model</Label>
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                value={discSearchQuery}
                                onChange={(e) => handleDiscSearch(e.target.value)}
                                placeholder="e.g. Ford Focus, VW Golf, BMW 3 Series..."
                                className="flex-1"
                                data-testid="disc-search-input"
                              />
                              {discSearchQuery && discSearchResults.length === 0 && !discSearchLoading && (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleDiscSearch(discSearchQuery)}
                                  className="bg-blue-500 hover:bg-blue-600"
                                >
                                  Search
                                </Button>
                              )}
                            </div>
                            {discSearchQuery && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Pre-filled from job sheet: {discSearchQuery}
                              </p>
                            )}
                            
                            {discSearchLoading && (
                              <p className="text-sm text-blue-500">Searching...</p>
                            )}
                            
                            {discSearchResults.length > 0 && (
                              <div className="max-h-48 overflow-y-auto space-y-2">
                                {discSearchResults.map((vehicle, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleSelectDiscSpecs(vehicle)}
                                    className="w-full text-left p-2 rounded border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                                    data-testid={`disc-result-${idx}`}
                                  >
                                    <p className="font-medium text-sm">{vehicle.display_name}</p>
                                    <p className="text-xs text-gray-500">
                                      {item.item_key?.includes('front') ? 'Front' : 'Rear'}: 
                                      New {item.item_key?.includes('front') ? vehicle.front?.new_thickness : vehicle.rear?.new_thickness}mm, 
                                      Min {item.item_key?.includes('front') ? vehicle.front?.min_thickness : vehicle.rear?.min_thickness}mm
                                    </p>
                                    {vehicle.variants?.length > 0 && (
                                      <p className="text-xs text-gray-400 mt-1">
                                        Variants: {vehicle.variants.slice(0, 3).join(', ')}{vehicle.variants.length > 3 ? '...' : ''}
                                      </p>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {discSearchQuery.length >= 2 && !discSearchLoading && discSearchResults.length === 0 && (
                              <p className="text-sm text-amber-600">
                                No matching vehicles found. Please enter specs manually below.
                              </p>
                            )}
                            
                            <p className="text-xs text-gray-500 mt-2">
                              Can't find your vehicle? Enter the specifications manually below.
                            </p>
                          </div>
                        )}
                        
                        {/* Selected Vehicle Badge */}
                        {selectedVehicle && (
                          <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                            <span className="text-sm text-green-700">
                              ✓ Specs loaded: <strong>{selectedVehicle.display_name}</strong>
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedVehicle(null);
                                setDiscNewThickness("");
                                setDiscMinThickness("");
                              }}
                              className="text-xs text-green-600 hover:text-green-800"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                        
                        {/* Manual Entry Fields */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-blue-600">New Disc Thickness (mm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={discNewThickness}
                              onChange={(e) => {
                                setDiscNewThickness(e.target.value);
                                if (selectedVehicle) setSelectedVehicle(null);
                              }}
                              placeholder="e.g. 28.0"
                              className="bg-white"
                              data-testid="disc-new-thickness"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-blue-600">Minimum Permitted (mm)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={discMinThickness}
                              onChange={(e) => {
                                setDiscMinThickness(e.target.value);
                                if (selectedVehicle) setSelectedVehicle(null);
                              }}
                              placeholder="e.g. 25.0"
                              className="bg-white"
                              data-testid="disc-min-thickness"
                            />
                          </div>
                        </div>
                        {!selectedVehicle && (
                          <p className="text-xs text-blue-500 mt-2">
                            💡 Use "Lookup Vehicle" to auto-fill specs or enter manually
                          </p>
                        )}
                      </div>
                    )}

                    {/* Current Measurement Input */}
                    <div>
                      <div className={item.has_secondary_measurement ? "grid grid-cols-2 gap-4" : ""}>
                        <div>
                          <Label className="font-semibold text-red-700">
                            {item.has_secondary_measurement ? "Boiling Point" : "Current Measurement"} ({item.measurement_unit_type || item.measurement_unit || 'mm'}) <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={measurementValue}
                            onChange={(e) => setMeasurementValue(e.target.value)}
                            placeholder={`Enter value in ${item.measurement_unit_type || item.measurement_unit || 'mm'}`}
                            className={`${!measurementValue ? 'border-red-400 border-2 bg-red-50 focus:border-red-500 focus:ring-red-500' : 'border-green-400 bg-green-50'}`}
                            data-testid="measurement-input"
                          />
                        </div>
                        
                        {/* Secondary Measurement (e.g., Moisture Content for Brake Fluid) */}
                        {item.has_secondary_measurement && (
                          <div>
                            <Label className="font-semibold">
                              {item.secondary_measurement_label || "Secondary"} ({item.secondary_measurement_unit || '%'})
                            </Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={secondaryMeasurementValue}
                              onChange={(e) => setSecondaryMeasurementValue(e.target.value)}
                              placeholder={`Enter ${item.secondary_measurement_label?.toLowerCase() || 'value'}`}
                              data-testid="secondary-measurement-input"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Brake Pad Status Suggestion - Based on measurement thresholds */}
                      {(item.item_key === 'front_brake_pads' || item.item_key === 'rear_brake_pads') && measurementValue && (
                        (() => {
                          const padMm = parseFloat(measurementValue);
                          const isRed = padMm <= 3.0;
                          const isReplaceSoon = padMm > 3.0 && padMm < 4.0;
                          const isMonitor = padMm >= 4.0 && padMm < 6.0;
                          const bgClass = isRed ? 'bg-red-100 border-red-400'
                            : (isReplaceSoon ? 'bg-amber-100 border-amber-400'
                            : isMonitor ? 'bg-amber-50 border-amber-300'
                            : 'bg-green-100 border-green-400');
                          const textClass = isRed ? 'text-red-700'
                            : (isReplaceSoon || isMonitor) ? 'text-amber-700'
                            : 'text-green-700';
                          const label = isRed ? 'RED - Replace Now'
                            : isReplaceSoon ? 'AMBER - Replace Soon'
                            : isMonitor ? 'AMBER - Monitor'
                            : 'GREEN - Good Condition';
                          const expectedStatus = isRed ? 'red' : (isReplaceSoon || isMonitor) ? 'amber' : 'green';
                          return (
                            <div className={`mt-2 p-3 rounded-lg border-2 ${bgClass}`}>
                              <div className="flex items-center justify-between">
                                <span className={`font-bold ${textClass}`}>{label}</span>
                              </div>
                              <p className="text-xs mt-1 text-slate-600">
                                Thresholds: ≤3mm=Red, 3-4mm=Amber (Replace Soon), 4-6mm=Amber (Monitor), ≥6mm=Green
                              </p>
                              {status && status !== expectedStatus && status !== 'dangerous' && (
                                <p className="text-xs mt-1 font-medium text-orange-600">
                                  Status will auto-update to match measurement threshold
                                </p>
                              )}
                              {status === 'dangerous' && (
                                <p className="text-xs mt-1 font-medium text-purple-600">
                                  DANGEROUS status overrides measurement trigger
                                </p>
                              )}
                            </div>
                          );
                        })()
                      )}
                      
                      {/* Brake Pad Wear Sensor Checkbox */}
                      {(item.item_key === 'front_brake_pads' || item.item_key === 'rear_brake_pads') && status && status !== 'blue' && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="wear-sensor-checkbox-container">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={wearSensorRequired}
                              onChange={(e) => setWearSensorRequired(e.target.checked)}
                              className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              data-testid="wear-sensor-required-checkbox"
                            />
                            <div>
                              <span className="font-semibold text-sm text-slate-800">Wear Sensor Required</span>
                              <p className="text-xs text-slate-500 mt-0.5">
                                Vehicle requires brake pad wear sensor. This will be auto-added to pricing.
                              </p>
                            </div>
                          </label>
                        </div>
                      )}
                      
                      {/* Pad Wear Percentage Display */}
                      {item.has_wear_percentage && measurementValue && wearPercentage !== null && (
                        <div className={`mt-2 p-3 rounded-lg ${
                          wearPercentage >= 75 ? 'bg-red-100 border border-red-300' :
                          wearPercentage >= 50 ? 'bg-amber-100 border border-amber-300' :
                          'bg-green-100 border border-green-300'
                        }`}>
                          <div className="flex items-center justify-between">
                            <span className={`font-semibold ${
                              wearPercentage >= 75 ? 'text-red-700' :
                              wearPercentage >= 50 ? 'text-amber-700' :
                              'text-green-700'
                            }`}>
                              {wearPercentage}% Worn
                            </span>
                            <span className="text-sm">
                              ({100 - wearPercentage}% remaining)
                            </span>
                          </div>
                          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                wearPercentage >= 75 ? 'bg-red-500' :
                                wearPercentage >= 50 ? 'bg-amber-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: `${wearPercentage}%` }}
                            />
                          </div>
                          <p className="text-xs mt-1 opacity-75">
                            Based on {item.new_thickness_mm}mm new thickness
                          </p>
                        </div>
                      )}

                      {/* Disc condition vs minimum check */}
                      {item.has_disc_specs && measurementValue && discMinThickness && (
                        <div className={`mt-2 p-3 rounded-lg ${
                          parseFloat(measurementValue) <= parseFloat(discMinThickness) 
                            ? 'bg-red-100 border border-red-300' 
                            : parseFloat(measurementValue) <= parseFloat(discMinThickness) + 1
                            ? 'bg-amber-100 border border-amber-300'
                            : 'bg-green-100 border border-green-300'
                        }`}>
                          <span className={`font-semibold ${
                            parseFloat(measurementValue) <= parseFloat(discMinThickness) 
                              ? 'text-red-700' 
                              : parseFloat(measurementValue) <= parseFloat(discMinThickness) + 1
                              ? 'text-amber-700'
                              : 'text-green-700'
                          }`}>
                            {parseFloat(measurementValue) <= parseFloat(discMinThickness) 
                              ? '⚠️ BELOW MINIMUM - Replacement Required' 
                              : parseFloat(measurementValue) <= parseFloat(discMinThickness) + 1
                              ? '⚡ Approaching Minimum - Monitor'
                              : '✓ Within Specification'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Tyre Percent Worn Input */}
                    {item.requires_percent_worn && (
                      <div className="mt-4">
                        <Label className="font-semibold">Tyre Wear</Label>
                        
                        {/* Auto-calculated info */}
                        {measurementValue && (
                          <p className="text-xs text-blue-600 mb-2">
                            📐 Auto-calculated from {measurementValue}mm tread depth (New: 8mm, Legal min: 1.6mm)
                          </p>
                        )}
                        
                        <div className="flex items-center gap-3 mt-1">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={tyrePercentWorn}
                            onChange={(e) => setTyrePercentWorn(e.target.value)}
                            placeholder="e.g. 60"
                            className="w-24"
                            data-testid="tyre-percent-worn-input"
                          />
                          <span className="text-slate-500">% worn</span>
                          
                          {/* Quick buttons for common values */}
                          <div className="flex gap-1">
                            {[25, 50, 75, 90].map(val => (
                              <Button
                                key={val}
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`text-xs px-2 ${tyrePercentWorn === val.toString() ? 'bg-slate-100' : ''}`}
                                onClick={() => setTyrePercentWorn(val.toString())}
                              >
                                {val}%
                              </Button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Simple Tyre Wear Bar */}
                        {tyrePercentWorn && (
                          <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700">Tread Wear</span>
                              <span className={`text-sm font-bold ${
                                parseInt(tyrePercentWorn) >= 80 ? 'text-red-600' :
                                parseInt(tyrePercentWorn) >= 60 ? 'text-amber-600' :
                                'text-green-600'
                              }`}>
                                {parseInt(tyrePercentWorn)}% worn
                              </span>
                            </div>
                            
                            {/* Wear bar */}
                            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  parseInt(tyrePercentWorn) >= 80 ? 'bg-red-500' :
                                  parseInt(tyrePercentWorn) >= 60 ? 'bg-amber-500' :
                                  'bg-green-500'
                                }`}
                                style={{ width: `${tyrePercentWorn}%` }}
                              />
                            </div>
                            
                            {/* Labels */}
                            <div className="flex justify-between mt-1 text-xs text-slate-500">
                              <span>New</span>
                              <span>Replace</span>
                            </div>
                            
                            {/* Status message */}
                            <p className={`mt-3 text-sm font-medium text-center ${
                              parseInt(tyrePercentWorn) >= 80 ? 'text-red-600' :
                              parseInt(tyrePercentWorn) >= 60 ? 'text-amber-600' :
                              'text-green-600'
                            }`}>
                              {parseInt(tyrePercentWorn) >= 80 
                                ? '⛔ Replacement required'
                                : parseInt(tyrePercentWorn) >= 60 
                                  ? '⚠️ Monitor - replace soon'
                                  : '✓ Good condition'}
                            </p>
                          </div>
                        )}
                        
                        {/* Tyre Size and Brand - NOW HANDLED IN TOP BLOCK for FL/FR/RL/RR */}
                        {false && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                              <span>🛞</span> Tyre Details (Required)
                            </h4>
                            
                            <div className="grid grid-cols-2 gap-4">
                              {/* Tyre Size */}
                              <div>
                                <Label className="text-sm text-blue-700 mb-1 block">
                                  Tyre Size <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                  type="text"
                                  value={tyreSize}
                                  onChange={(e) => setTyreSize(e.target.value.toUpperCase())}
                                  placeholder="e.g. 205/55R16"
                                  className={`${!tyreSize ? 'border-red-300 bg-red-50' : ''}`}
                                  data-testid="tyre-size-input"
                                />
                                <p className="text-xs text-slate-500 mt-1">Found on tyre sidewall</p>
                              </div>
                              
                              {/* Tyre Brand */}
                              <div>
                                <Label className="text-sm text-blue-700 mb-1 block">
                                  Tyre Brand <span className="text-red-500">*</span>
                                </Label>
                                <div className="space-y-2">
                                  <Input
                                    type="text"
                                    value={tyreBrand}
                                    onChange={(e) => setTyreBrand(e.target.value)}
                                    placeholder="e.g. Michelin, Continental..."
                                    className={`${!tyreBrand ? 'border-red-300 bg-red-50' : ''}`}
                                    data-testid="tyre-brand-input"
                                  />
                                  {/* Quick brand buttons */}
                                  <div className="flex flex-wrap gap-1">
                                    {['Budget', 'Michelin', 'Continental', 'Pirelli', 'Bridgestone', 'Goodyear'].map(brand => (
                                      <Button
                                        key={brand}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className={`text-xs px-2 py-1 ${tyreBrand === brand ? 'bg-blue-100 border-blue-400' : ''}`}
                                        onClick={() => setTyreBrand(brand)}
                                      >
                                        {brand}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {(!tyreSize || !tyreBrand) && (
                              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Please enter tyre size and brand before saving
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Disc Condition Dropdown */}
                    {item.has_condition_dropdown && item.condition_options && (
                      <div>
                        <Label className="font-semibold">Disc Condition *</Label>
                        <Select value={discCondition} onValueChange={setDiscCondition}>
                          <SelectTrigger className="w-full mt-1" data-testid="disc-condition-dropdown">
                            <SelectValue placeholder="Select disc condition..." />
                          </SelectTrigger>
                          <SelectContent>
                            {item.condition_options.map((condition, idx) => (
                              <SelectItem key={idx} value={condition}>
                                {condition}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                {/* ============== BATTERY TEST ENHANCED UI ============== */}
                {/* Moved outside requires_measurement block since battery test has requires_measurement: false */}
                {item?.is_battery_test && status !== "blue" && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
                    <h4 className="font-bold text-yellow-800 mb-4 flex items-center gap-2">
                      <span>🔋</span> Battery Test Results {(status === "amber" || status === "red") ? "(Required)" : ""}
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* Battery Voltage */}
                      <div>
                        <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
                          Voltage (V) <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="20"
                          value={batteryVoltage}
                          onChange={(e) => setBatteryVoltage(e.target.value)}
                          placeholder="e.g. 12.4"
                          className="bg-white"
                          data-testid="battery-voltage-input"
                        />
                        {batteryVoltage && (
                          <p className={`text-xs mt-1 ${
                            parseFloat(batteryVoltage) >= 12.6 ? 'text-green-600' :
                            parseFloat(batteryVoltage) >= 12.4 ? 'text-green-500' :
                            parseFloat(batteryVoltage) >= 12.2 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {parseFloat(batteryVoltage) >= 12.6 ? '✓ Excellent' :
                             parseFloat(batteryVoltage) >= 12.4 ? '✓ Good' :
                             parseFloat(batteryVoltage) >= 12.2 ? '⚠ Fair' :
                             parseFloat(batteryVoltage) >= 12.0 ? '⚠ Low' :
                             '⛔ Critical'}
                          </p>
                        )}
                      </div>
                      
                      {/* Measured CCA */}
                      <div>
                        <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
                          Measured CCA (A) <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="2000"
                          value={measuredCca}
                          onChange={(e) => setMeasuredCca(e.target.value)}
                          placeholder="From tester"
                          className="bg-white"
                          data-testid="measured-cca-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">From battery tester</p>
                      </div>
                      
                      {/* Rated CCA */}
                      <div>
                        <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
                          Rated CCA (A) <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="2000"
                          value={ratedCca}
                          onChange={(e) => setRatedCca(e.target.value)}
                          placeholder="From battery label"
                          className="bg-white"
                          data-testid="rated-cca-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">From battery label</p>
                      </div>
                      
                      {/* Health Percentage */}
                      <div>
                        <Label className="text-sm font-semibold text-yellow-700 mb-1 block">
                          Health %
                          <span className="text-xs font-normal text-gray-500 ml-2">
                            {healthPercentManual ? "(Manual)" : "(Auto-calculated)"}
                          </span>
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="200"
                            value={healthPercent}
                            onChange={(e) => {
                              setHealthPercent(e.target.value);
                              setHealthPercentManual(true);
                            }}
                            placeholder={measuredCca && ratedCca ? "Auto-calc" : "Enter %"}
                            className="bg-white"
                            disabled={!healthPercentManual && measuredCca && ratedCca}
                            data-testid="health-percent-input"
                          />
                          {!healthPercentManual && measuredCca && ratedCca && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setHealthPercentManual(true)}
                              className="text-xs text-yellow-700 hover:bg-yellow-100"
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                        {healthPercent && (
                          <p className={`text-xs mt-1 ${
                            parseFloat(healthPercent) >= 80 ? 'text-green-600' :
                            parseFloat(healthPercent) >= 60 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {parseFloat(healthPercent) >= 80 ? '✓ Good health' :
                             parseFloat(healthPercent) >= 60 ? '⚠ Fair - monitor' :
                             '⛔ Poor - replacement recommended'}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Battery Replacement Fields - shown only for amber/red */}
                    {(status === "amber" || status === "red") && (
                      <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <h5 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                          <Wrench className="w-4 h-4" />
                          Battery Replacement Info (for FOH)
                        </h5>
                        
                        {/* Battery Type Required */}
                        <div className="mb-3">
                          <Label className="text-sm font-medium text-orange-700 mb-2 block">
                            Battery Type Required
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: "standard", label: "Standard Lead-Acid" },
                              { value: "efb", label: "EFB (Enhanced Flooded)" },
                              { value: "agm", label: "AGM (Absorbent Glass Mat)" },
                              { value: "unknown", label: "Unknown / Check Spec" }
                            ].map(option => (
                              <Button
                                key={option.value}
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`text-xs ${
                                  batteryTypeRequired === option.value 
                                    ? 'bg-orange-200 border-orange-400 text-orange-800' 
                                    : 'hover:bg-orange-100'
                                }`}
                                onClick={() => setBatteryTypeRequired(option.value)}
                                data-testid={`battery-type-${option.value}`}
                              >
                                {option.label}
                              </Button>
                            ))}
                          </div>
                          <p className="text-xs text-orange-600 mt-1">
                            Check battery label or vehicle spec for type
                          </p>
                        </div>
                        
                        {/* Battery Registration Required */}
                        <div className="mb-3">
                          <Label className="text-sm font-medium text-orange-700 mb-2 block">
                            Registration Required?
                          </Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={`text-xs ${
                                batteryRegistrationRequired === "yes"
                                  ? 'bg-red-200 border-red-400 text-red-800' 
                                  : 'hover:bg-gray-100'
                              }`}
                              onClick={() => setBatteryRegistrationRequired("yes")}
                              data-testid="battery-reg-yes"
                            >
                              Yes - Diagnostic Required
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={`text-xs ${
                                batteryRegistrationRequired === "no"
                                  ? 'bg-green-200 border-green-400 text-green-800' 
                                  : 'hover:bg-gray-100'
                              }`}
                              onClick={() => setBatteryRegistrationRequired("no")}
                              data-testid="battery-reg-no"
                            >
                              No - Simple Swap
                            </Button>
                          </div>
                          <p className="text-xs text-orange-600 mt-1">
                            Most modern vehicles (2012+) require battery registration via diagnostic tool
                          </p>
                        </div>
                        
                        {/* Additional Tech Notes */}
                        <div>
                          <Label className="text-sm font-medium text-orange-700 mb-1 block">
                            Tech Notes for FOH (optional)
                          </Label>
                          <Input
                            type="text"
                            value={batteryTechnicianNote}
                            onChange={(e) => setBatteryTechnicianNote(e.target.value)}
                            placeholder="e.g. Located in boot, requires trim removal..."
                            className="bg-white text-sm"
                            data-testid="battery-tech-note"
                          />
                        </div>
                        
                        {/* Info about FOH visibility */}
                        {batteryTypeRequired && batteryTypeRequired !== "unknown" && (
                          <div className="mt-3 p-2 bg-white rounded border border-orange-200">
                            <p className="text-xs text-orange-700">
                              📋 This information will be displayed to FOH when pricing the battery replacement.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Tester Photo Prompt */}
                    {(status === "amber" || status === "red") && !testerPhoto && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700 font-medium">
                          📷 Tester photo required for {status.toUpperCase()} status - capture photo of tester screen showing results
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Blue (Not Applicable) explanation for Battery Test */}
                {item?.is_battery_test && status === "blue" && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-300">
                    <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                      <span>ℹ️</span> Battery Test Not Applicable
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Please explain why the battery test could not be performed:
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        "Battery not accessible",
                        "Remote battery location",
                        "Hybrid/EV - specialist equipment required",
                        "Tester unavailable"
                      ].map(reason => (
                        <Button
                          key={reason}
                          type="button"
                          variant="outline"
                          size="sm"
                          className={`text-xs ${notes?.includes(reason) ? 'bg-blue-100 border-blue-400' : ''}`}
                          onClick={() => setNotes(reason)}
                        >
                          {reason}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ============== COOLANT ASSESSMENT ENHANCED UI ============== */}
                {item?.is_coolant_assessment && (status || isMeasurementFirstItem) && (
                  <div className="mt-4 p-4 bg-cyan-50 rounded-lg border border-cyan-300">
                    <h4 className="font-bold text-cyan-800 mb-4 flex items-center gap-2">
                      <span>💧</span> Coolant Assessment
                      {coolantSuggestedStatus && (
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                          coolantSuggestedStatus === 'green' ? 'bg-green-100 text-green-700' :
                          coolantSuggestedStatus === 'amber' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          Suggested: {coolantSuggestedStatus.toUpperCase()}
                        </span>
                      )}
                    </h4>
                    
                    {/* Coolant Strength Section */}
                    <div className="mb-4 p-3 bg-white rounded border">
                      <Label className="text-sm font-semibold text-cyan-700 mb-2 block">
                        1. Coolant Strength (Freeze Protection)
                      </Label>
                      <div className="space-y-3">
                        {/* Degrees of Protection - Primary Input */}
                        <div>
                          <Label className="text-xs text-gray-600 mb-1 block">Degrees of Protection (°C) <span className="text-red-500">*</span></Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="1"
                              value={coolantFreezeProtection}
                              onChange={(e) => setCoolantFreezeProtection(e.target.value)}
                              placeholder="e.g. -35"
                              className="bg-white w-32"
                              data-testid="coolant-freeze-protection-input"
                            />
                            <span className="text-sm text-slate-500">°C</span>
                          </div>
                          {coolantFreezeProtection && (
                            <p className={`text-xs mt-1 font-medium ${
                              parseFloat(coolantFreezeProtection) <= -30 ? 'text-green-600' :
                              parseFloat(coolantFreezeProtection) <= -15 ? 'text-amber-600' :
                              'text-red-600'
                            }`}>
                              {parseFloat(coolantFreezeProtection) <= -30 ? '✓ Good protection (below -30°C)' :
                               parseFloat(coolantFreezeProtection) <= -15 ? '⚠ Weak protection - replacement recommended' :
                               '⛔ Insufficient protection - replacement required'}
                            </p>
                          )}
                        </div>

                        {/* Test Method - Secondary selector */}
                        <div>
                          <Label className="text-xs text-gray-600 mb-1 block">Test Method Used</Label>
                          <select
                            className="w-full p-2 border rounded text-sm"
                            value={coolantStrengthInputType}
                            onChange={(e) => {
                              setCoolantStrengthInputType(e.target.value);
                              setCoolantBallsFloating("");
                            }}
                            data-testid="coolant-strength-type-select"
                          >
                            <option value="">Select test method...</option>
                            <option value="refractometer">Refractometer</option>
                            <option value="balls_tester">Balls tester</option>
                            <option value="test_strip">Test strip</option>
                            <option value="not_tested">Not tested</option>
                          </select>
                        </div>
                        
                        {/* Balls Tester Input - Additional data */}
                        {coolantStrengthInputType === "balls_tester" && (
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Balls Floating</Label>
                            <select
                              className="w-full p-2 border rounded text-sm"
                              value={coolantBallsFloating}
                              onChange={(e) => setCoolantBallsFloating(e.target.value)}
                              data-testid="coolant-balls-select"
                            >
                              <option value="">Select number...</option>
                              {[0, 1, 2, 3, 4].map(n => (
                                <option key={n} value={n}>{n} ball{n !== 1 ? 's' : ''}</option>
                              ))}
                            </select>
                            {coolantBallsFloating && (
                              <p className={`text-xs mt-1 font-medium ${
                                parseInt(coolantBallsFloating) >= 3 ? 'text-green-600' :
                                parseInt(coolantBallsFloating) === 2 ? 'text-amber-600' :
                                'text-red-600'
                              }`}>
                                {parseInt(coolantBallsFloating) >= 3 ? '✓ Good protection' :
                                 parseInt(coolantBallsFloating) === 2 ? '⚠ Weak - replacement recommended' :
                                 '⛔ Failed - replacement required'}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {coolantStrengthInputType === "not_tested" && (
                          <p className="text-xs text-gray-500 italic">Strength not tested - only level and condition checked</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Coolant Level Section */}
                    <div className="mb-4 p-3 bg-white rounded border">
                      <Label className="text-sm font-semibold text-cyan-700 mb-2 block">
                        2. Coolant Level
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          {value: "ok", label: "OK", color: "green"},
                          {value: "low", label: "Low", color: "amber"},
                          {value: "critically_low", label: "Critically Low", color: "red"},
                          {value: "not_checked", label: "Not Checked", color: "gray"}
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            className={`p-2 rounded border-2 text-sm font-medium transition-all ${
                              coolantLevel === opt.value 
                                ? opt.color === 'green' ? 'border-green-500 bg-green-50 text-green-700' :
                                  opt.color === 'amber' ? 'border-amber-500 bg-amber-50 text-amber-700' :
                                  opt.color === 'red' ? 'border-red-500 bg-red-50 text-red-700' :
                                  'border-gray-400 bg-gray-50 text-gray-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setCoolantLevel(opt.value)}
                            data-testid={`coolant-level-${opt.value}-btn`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {coolantLevel === "low" && (
                        <p className="text-xs text-amber-600 mt-2">⚠ Should be topped up and checked for leaks</p>
                      )}
                      {coolantLevel === "critically_low" && (
                        <p className="text-xs text-red-600 mt-2">⛔ Requires attention before further driving</p>
                      )}
                    </div>
                    
                    {/* Coolant Condition Section */}
                    <div className="mb-4 p-3 bg-white rounded border">
                      <Label className="text-sm font-semibold text-cyan-700 mb-2 block">
                        3. Coolant Condition
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          {value: "normal", label: "Normal", color: "green"},
                          {value: "discoloured", label: "Discoloured", color: "amber"},
                          {value: "contaminated", label: "Contaminated", color: "red"},
                          {value: "not_checked", label: "Not Checked", color: "gray"}
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            className={`p-2 rounded border-2 text-sm font-medium transition-all ${
                              coolantCondition === opt.value 
                                ? opt.color === 'green' ? 'border-green-500 bg-green-50 text-green-700' :
                                  opt.color === 'amber' ? 'border-amber-500 bg-amber-50 text-amber-700' :
                                  opt.color === 'red' ? 'border-red-500 bg-red-50 text-red-700' :
                                  'border-gray-400 bg-gray-50 text-gray-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setCoolantCondition(opt.value)}
                            data-testid={`coolant-condition-${opt.value}-btn`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {coolantCondition === "discoloured" && (
                        <p className="text-xs text-amber-600 mt-2">⚠ May indicate coolant is past service life</p>
                      )}
                      {coolantCondition === "contaminated" && (
                        <p className="text-xs text-red-600 mt-2">⛔ Further checks required - potential head gasket issue</p>
                      )}
                    </div>
                    
                    {/* Coolant Notes */}
                    <div className="mb-3">
                      <Label className="text-sm font-semibold text-cyan-700 mb-1 block">
                        Notes {(status === "amber" || status === "red") && <span className="text-red-500">*</span>}
                      </Label>
                      <textarea
                        className="w-full p-2 border rounded text-sm"
                        rows={2}
                        value={coolantNotes}
                        onChange={(e) => setCoolantNotes(e.target.value)}
                        placeholder={status === "amber" || status === "red" 
                          ? "Required for amber/red status - minimum 10 characters" 
                          : "Additional observations (optional)"
                        }
                        data-testid="coolant-notes-input"
                      />
                      {(status === "amber" || status === "red") && coolantNotes.length < 10 && (
                        <p className="text-xs text-red-500 mt-1">Minimum 10 characters required ({coolantNotes.length}/10)</p>
                      )}
                    </div>
                    
                    {/* Photo Requirement Warning - check both regular photos and tester photo */}
                    {(status === "amber" || status === "red") && photos.length === 0 && !testerPhoto && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700 font-medium">
                          📷 At least one photo is required for {status.toUpperCase()} status
                        </p>
                      </div>
                    )}
                    
                    {/* Status Override Warning */}
                    {coolantSuggestedStatus && status && status !== coolantSuggestedStatus && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                        <p className="text-sm text-yellow-700 font-medium">
                          ⚠ Status override: Suggested {coolantSuggestedStatus.toUpperCase()} but selected {status.toUpperCase()}
                        </p>
                        <p className="text-xs text-yellow-600 mt-1">
                          Please ensure notes explain the reason for this override
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* ============== BRAKE FLUID ASSESSMENT ENHANCED UI ============== */}
                {item?.is_brake_fluid_assessment && status !== "blue" && (
                  <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-300" data-testid="brake-fluid-assessment">
                    <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                      <span>🔶</span> Brake Fluid Assessment
                      <span className="text-xs font-normal text-red-500 ml-2">(Required)</span>
                    </h4>
                    
                    {/* Technician Guidance */}
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Technician Guidance:</p>
                      <ul className="text-xs text-blue-600 list-disc list-inside space-y-1">
                        <li>Test brake fluid moisture using a moisture tester</li>
                        <li>Record the moisture percentage (0-10%)</li>
                        <li>Inspect colour and contamination</li>
                        <li>Record any abnormal findings in notes</li>
                      </ul>
                    </div>
                    
                    {/* Moisture Reading - REQUIRED */}
                    <div className="mb-4">
                      <Label className="text-sm font-semibold text-amber-700 mb-2 block">
                        Moisture Content (%) <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={brakeFluidMoisture}
                          onChange={(e) => setBrakeFluidMoisture(e.target.value)}
                          placeholder="e.g. 1.2"
                          className="bg-white w-32"
                          data-testid="brake-fluid-moisture-input"
                        />
                        <span className="text-sm text-amber-700">%</span>
                        {brakeFluidMoisture && (
                          <div className={`px-2 py-1 rounded text-xs font-semibold ${
                            parseFloat(brakeFluidMoisture) <= 1.5 ? 'bg-green-100 text-green-700' :
                            parseFloat(brakeFluidMoisture) <= 2.5 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {parseFloat(brakeFluidMoisture) <= 1.5 ? '✓ Serviceable' :
                             parseFloat(brakeFluidMoisture) <= 2.5 ? '⚠ Replacement Recommended' :
                             '⛔ Replace Now'}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-amber-600 mt-1">
                        GREEN: ≤1.5% | AMBER: 1.5-2.5% | RED: &gt;2.5%
                      </p>
                    </div>
                    
                    {/* Boiling Point - REQUIRED */}
                    <div className="mb-4">
                      <Label className="text-sm font-semibold text-amber-700 mb-2 block">
                        Boiling Point (°C) <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          step="1"
                          min="100"
                          max="300"
                          value={brakeFluidBoilingPoint}
                          onChange={(e) => setBrakeFluidBoilingPoint(e.target.value)}
                          placeholder="e.g. 180"
                          className="bg-white w-32"
                          data-testid="brake-fluid-boiling-point-input"
                        />
                        <span className="text-sm text-amber-700">°C</span>
                        {brakeFluidBoilingPoint && (
                          <div className={`px-2 py-1 rounded text-xs font-semibold ${
                            parseInt(brakeFluidBoilingPoint) >= 200 ? 'bg-green-100 text-green-700' :
                            parseInt(brakeFluidBoilingPoint) >= 180 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {parseInt(brakeFluidBoilingPoint) >= 200 ? '✓ Good' :
                             parseInt(brakeFluidBoilingPoint) >= 180 ? '⚠ Monitor' :
                             '⛔ Replace'}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        <span className="text-green-600 font-medium">GREEN: ≥200°C</span> | <span className="text-amber-600 font-medium">AMBER: 180-199°C</span> | <span className="text-red-600 font-medium">RED: &lt;180°C</span>
                      </p>
                    </div>
                    
                    {/* Visual Condition - REQUIRED */}
                    <div className="mb-4">
                      <Label className="text-sm font-semibold text-amber-700 mb-2 block">
                        Visual Condition <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: "clean", label: "Clean", color: "green" },
                          { value: "dark_degraded", label: "Dark / Degraded", color: "amber" },
                          { value: "contaminated", label: "Contaminated", color: "red" }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              brakeFluidVisualCondition === opt.value
                                ? opt.color === "green" ? 'bg-green-500 text-white ring-2 ring-green-300' :
                                  opt.color === "amber" ? 'bg-amber-500 text-white ring-2 ring-amber-300' :
                                  'bg-red-500 text-white ring-2 ring-red-300'
                                : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                            }`}
                            onClick={() => setBrakeFluidVisualCondition(opt.value)}
                            data-testid={`brake-fluid-condition-${opt.value}-btn`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {brakeFluidVisualCondition === "contaminated" && (
                        <p className="text-xs text-red-600 mt-2">⛔ Contamination detected - immediate replacement required</p>
                      )}
                      {brakeFluidVisualCondition === "dark_degraded" && (
                        <p className="text-xs text-amber-600 mt-2">⚠ Degraded fluid indicates replacement is due</p>
                      )}
                    </div>
                    
                    {/* Service History - REQUIRED */}
                    <div className="mb-4">
                      <Label className="text-sm font-semibold text-amber-700 mb-2 block">
                        Service History <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            brakeFluidServiceHistory === "confirmed"
                              ? 'bg-green-500 text-white ring-2 ring-green-300'
                              : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                          }`}
                          onClick={() => setBrakeFluidServiceHistory("confirmed")}
                          data-testid="brake-fluid-history-confirmed-btn"
                        >
                          Confirmed
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            brakeFluidServiceHistory === "unknown"
                              ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                              : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                          }`}
                          onClick={() => setBrakeFluidServiceHistory("unknown")}
                          data-testid="brake-fluid-history-unknown-btn"
                        >
                          Unknown
                        </button>
                      </div>
                      {brakeFluidServiceHistory === "unknown" && (
                        <p className="text-xs text-amber-600 mt-2">⚠ Unknown service history - defaults to AMBER if vehicle &gt;2 years old</p>
                      )}
                    </div>
                    
                    {/* Optional: Last Replacement Details */}
                    {brakeFluidServiceHistory === "confirmed" && (
                      <div className="mb-4 p-3 bg-white rounded-lg border border-amber-200">
                        <p className="text-xs font-semibold text-amber-700 mb-2">Last Replacement Details (Optional)</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">Date</Label>
                            <Input
                              type="date"
                              value={brakeFluidLastReplacementDate}
                              onChange={(e) => setBrakeFluidLastReplacementDate(e.target.value)}
                              className="text-sm"
                              data-testid="brake-fluid-last-date"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Mileage</Label>
                            <Input
                              type="number"
                              value={brakeFluidLastReplacementMileage}
                              onChange={(e) => setBrakeFluidLastReplacementMileage(e.target.value)}
                              placeholder="e.g. 45000"
                              className="text-sm"
                              data-testid="brake-fluid-last-mileage"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Notes */}
                    <div className="mb-4">
                      <Label className="text-sm font-semibold text-amber-700 mb-1 block">
                        Technician Notes
                      </Label>
                      <textarea
                        className="w-full p-2 border rounded text-sm"
                        rows={2}
                        value={brakeFluidNotes}
                        onChange={(e) => setBrakeFluidNotes(e.target.value)}
                        placeholder="e.g. Evidence of contamination, reservoir corrosion..."
                        data-testid="brake-fluid-notes-input"
                      />
                    </div>
                    
                    {/* Calculated Status Display */}
                    {brakeFluidCalculatedStatus && (
                      <div className={`p-3 rounded-lg border ${
                        brakeFluidCalculatedStatus === "green" ? 'bg-green-50 border-green-300' :
                        brakeFluidCalculatedStatus === "amber" ? 'bg-amber-50 border-amber-300' :
                        'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${
                            brakeFluidCalculatedStatus === "green" ? 'bg-green-500' :
                            brakeFluidCalculatedStatus === "amber" ? 'bg-amber-500' :
                            'bg-red-500'
                          }`} />
                          <span className={`font-semibold ${
                            brakeFluidCalculatedStatus === "green" ? 'text-green-700' :
                            brakeFluidCalculatedStatus === "amber" ? 'text-amber-700' :
                            'text-red-700'
                          }`}>
                            System Status: {brakeFluidCalculatedStatus.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-xs mt-1 text-slate-600">
                          {brakeFluidCalculatedStatus === "green" 
                            ? "Brake fluid moisture level is within acceptable limits."
                            : brakeFluidCalculatedStatus === "amber"
                            ? "Brake fluid absorbs moisture over time which lowers its boiling point. Replacement recommended."
                            : "Brake fluid moisture level exceeds recommended operating limits. Replacement required."
                          }
                        </p>
                      </div>
                    )}
                    
                    {/* Tester Photo Reminder */}
                    {!testerPhoto && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700 font-medium">
                          📷 Tester photo required - capture photo of moisture tester display
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Blue (Not Applicable) for Brake Fluid */}
                {item?.is_brake_fluid_assessment && status === "blue" && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-300">
                    <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                      <span>ℹ️</span> Brake Fluid Test Not Applicable
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Please explain why the brake fluid test could not be performed:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Reservoir not accessible",
                        "Tester unavailable",
                        "Vehicle on ramp - cannot access",
                        "Contaminated reservoir - cannot test"
                      ].map(reason => (
                        <Button
                          key={reason}
                          type="button"
                          variant="outline"
                          size="sm"
                          className={`text-xs ${notes?.includes(reason) ? 'bg-blue-100 border-blue-400' : ''}`}
                          onClick={() => setNotes(reason)}
                        >
                          {reason}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════════
                    WHEEL TORQUE GUIDANCE & CONFIRMATION (VI-Only)
                    Provides manufacturer-correct torque with fallback hierarchy
                    Soft compliance v1: confirms but does not block
                ═══════════════════════════════════════════════════════════════════ */}
                {item?.item_key === "wheel_torque_confirmation" && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border-2 border-slate-300">
                    <h4 className="font-bold text-slate-800 mb-1 flex items-center gap-2 text-lg">
                      <Wrench className="w-5 h-5" /> Wheel Torque Setting
                    </h4>
                    <p className="text-xs text-slate-500 mb-4">Reference & Confirmation</p>
                    
                    {/* Torque Guidance Display */}
                    <div className={`mb-4 p-4 rounded-lg border-2 ${
                      torqueConfidence === 'high' ? 'bg-green-50 border-green-200' :
                      torqueConfidence === 'medium' ? 'bg-amber-50 border-amber-200' :
                      'bg-red-50 border-red-200'
                    }`}>
                      {torqueSpecLoading ? (
                        <div className="flex items-center gap-2 text-slate-500">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Looking up torque specification...</span>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                              {torqueRangeMin && torqueRangeMax 
                                ? `${torqueRangeMin}–${torqueRangeMax} Nm`
                                : `${torqueManufacturerSpec || 120} Nm`
                              }
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {torqueConfidence === 'high' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-testid="torque-source-badge">
                                <Check className="w-3 h-3" /> Manufacturer Spec
                              </span>
                            ) : torqueConfidence === 'medium' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="torque-source-badge">
                                <AlertTriangle className="w-3 h-3" /> Estimated Range
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="torque-source-badge">
                                <AlertCircle className="w-3 h-3" /> Default Value
                              </span>
                            )}
                            {torqueSpecSource === 'exact_match' && (
                              <span className="text-xs text-green-600">Vehicle matched</span>
                            )}
                            {torqueSpecSource === 'make_default' && (
                              <span className="text-xs text-green-600">Make default</span>
                            )}
                          </div>
                          {torqueConfidence !== 'high' && (
                            <p className="text-xs text-slate-500 mt-2">
                              {torqueConfidence === 'medium'
                                ? "Based on vehicle type. Check door jamb sticker or manufacturer data for exact spec."
                                : "No manufacturer data available. Using safe default. Verify before applying."
                              }
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Unable to Torque / Unable to Check toggles */}
                    <div className="space-y-2 mb-4">
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        torqueUnable ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input
                          type="checkbox"
                          checked={torqueUnable}
                          onChange={(e) => {
                            setTorqueUnable(e.target.checked);
                            if (e.target.checked) {
                              setTorqueApplied("");
                              setTorqueConfirmed(false);
                              setTorqueUnableToCheck(false);
                              setTorqueUnableToCheckReason("");
                            }
                          }}
                          className="w-5 h-5 rounded border-2"
                          data-testid="torque-unable-checkbox"
                        />
                        <div>
                          <span className="font-medium text-slate-800">Unable to torque wheels</span>
                          <p className="text-xs text-slate-500">Stripped bolts, missing nuts, damage etc.</p>
                        </div>
                      </label>
                      
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        torqueUnableToCheck ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input
                          type="checkbox"
                          checked={torqueUnableToCheck}
                          onChange={(e) => {
                            setTorqueUnableToCheck(e.target.checked);
                            if (e.target.checked) {
                              setTorqueApplied("");
                              setTorqueConfirmed(false);
                              setTorqueUnable(false);
                              setTorqueUnableReason("");
                            }
                          }}
                          className="w-5 h-5 rounded border-2"
                          data-testid="torque-unable-to-check-checkbox"
                        />
                        <div>
                          <span className="font-medium text-slate-800">Unable to Check / Access</span>
                          <p className="text-xs text-slate-500">Wheel trims fitted, time constraints, or other access issues</p>
                        </div>
                      </label>
                    </div>
                    
                    {torqueUnableToCheck ? (
                      /* Exception Path - Unable to Check/Access */
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <Label className="font-semibold text-amber-800 mb-2 block">
                          Reason unable to check / access <span className="text-red-500">*</span>
                        </Label>
                        <div className="space-y-2 mb-3">
                          {[
                            "Wheel trims securely fitted - removal not possible",
                            "Wheel trims fitted - time needed to remove",
                            "Locking wheel covers preventing access",
                            "Customer declined removal of trims",
                            "Alloy centre caps preventing access",
                            "Insufficient time allocated"
                          ].map(reason => (
                            <Button
                              key={reason}
                              type="button"
                              variant="outline"
                              size="sm"
                              className={`text-xs w-full justify-start ${
                                torqueUnableToCheckReason === reason ? 'bg-amber-100 border-amber-400 text-amber-800' : ''
                              }`}
                              onClick={() => setTorqueUnableToCheckReason(reason)}
                              data-testid={`torque-check-reason-${reason.slice(0,20)}`}
                            >
                              {reason}
                            </Button>
                          ))}
                        </div>
                        <Textarea
                          value={torqueUnableToCheckReason}
                          onChange={(e) => setTorqueUnableToCheckReason(e.target.value)}
                          placeholder="Or enter custom reason..."
                          className="min-h-[60px]"
                          data-testid="torque-unable-to-check-reason"
                        />
                      </div>
                    ) : torqueUnable ? (
                      /* Exception Path - Unable to Torque */
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <Label className="font-semibold text-red-800 mb-2 block">
                          Reason torque could not be completed <span className="text-red-500">*</span>
                        </Label>
                        <div className="space-y-2 mb-3">
                          {[
                            "Locking wheel nut key not available",
                            "Customer declined torque check",
                            "Wheels not removed during service",
                            "Alloy wheel damage risk - customer informed",
                            "Vehicle collected before completion"
                          ].map(reason => (
                            <Button
                              key={reason}
                              type="button"
                              variant="outline"
                              size="sm"
                              className={`text-xs w-full justify-start ${
                                torqueUnableReason === reason ? 'bg-red-100 border-red-400 text-red-800' : ''
                              }`}
                              onClick={() => setTorqueUnableReason(reason)}
                            >
                              {reason}
                            </Button>
                          ))}
                        </div>
                        <Textarea
                          value={torqueUnableReason}
                          onChange={(e) => setTorqueUnableReason(e.target.value)}
                          placeholder="Or enter custom reason..."
                          className="min-h-[60px]"
                          data-testid="torque-unable-reason"
                        />
                      </div>
                    ) : (
                      /* Normal Path — Structured Torque Recording */
                      <div className="space-y-4">
                        
                        {/* ── TIER 1: Manufacturer Specification (Primary) ── */}
                        {torqueManufacturerSpec && torqueConfidence === 'high' && (
                          <div className="p-4 rounded-lg border-2 border-green-400 bg-green-50">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-xs font-bold uppercase tracking-wider text-green-700">Manufacturer Specification</Label>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold flex items-center gap-1">
                                <Check className="w-3 h-3" /> Verified
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setTorqueApplied(torqueManufacturerSpec.toString());
                                setTorqueSelectionSource("manufacturer");
                                setTorqueConfirmed(true);
                              }}
                              className={`w-full p-4 rounded-lg text-center transition-all ${
                                torqueSelectionSource === "manufacturer" && parseInt(torqueApplied) === torqueManufacturerSpec
                                  ? 'bg-green-600 text-white shadow-lg'
                                  : 'bg-white border-2 border-green-400 text-green-800 hover:bg-green-100'
                              }`}
                              data-testid="torque-manufacturer-select"
                            >
                              <span className="text-3xl font-black">{torqueManufacturerSpec}</span>
                              <span className="text-lg font-medium ml-1">Nm</span>
                              {torqueSelectionSource === "manufacturer" && parseInt(torqueApplied) === torqueManufacturerSpec && (
                                <span className="block text-sm mt-1 font-medium opacity-90">Selected</span>
                              )}
                            </button>
                            {torqueSpecSource === 'exact_match' && (
                              <p className="text-xs text-green-600 mt-2">Matched to this vehicle</p>
                            )}
                          </div>
                        )}

                        {/* ── TIER 2: Quick Select (Secondary) ── */}
                        <div className="p-3 bg-white rounded-lg border border-slate-200">
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Quick Select</Label>
                          <div className="grid grid-cols-3 gap-2">
                            {TORQUE_QUICK_SELECT.map(val => {
                              const isSelected = parseInt(torqueApplied) === val && torqueSelectionSource === "quick_select";
                              const isSpec = val === torqueManufacturerSpec;
                              return (
                                <button
                                  key={val}
                                  type="button"
                                  onClick={() => {
                                    setTorqueApplied(val.toString());
                                    setTorqueSelectionSource(isSpec ? "manufacturer" : "quick_select");
                                    setTorqueConfirmed(true);
                                  }}
                                  className={`relative p-3 rounded-lg text-center font-bold transition-all ${
                                    isSelected ? 'bg-green-600 text-white shadow-md' :
                                    (parseInt(torqueApplied) === val && torqueSelectionSource === "manufacturer") ? 'bg-green-600 text-white shadow-md' :
                                    isSpec ? 'bg-green-50 border-2 border-green-400 text-green-700' :
                                    'bg-slate-50 border border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-100'
                                  }`}
                                  data-testid={`torque-quick-${val}`}
                                >
                                  {val}
                                  <span className="text-xs font-normal ml-0.5">Nm</span>
                                  {isSpec && (
                                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                                      <Check className="w-3 h-3 text-white" />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* ── TIER 3: Manual Input (Fallback) ── */}
                        <div className="p-3 bg-white rounded-lg border border-slate-200">
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Custom Value</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="50"
                              max="300"
                              value={torqueSelectionSource === "manual" ? torqueApplied : ""}
                              onChange={(e) => {
                                setTorqueApplied(e.target.value);
                                setTorqueSelectionSource("manual");
                                if (e.target.value) setTorqueConfirmed(true);
                              }}
                              placeholder="Enter torque value"
                              className="w-32 text-lg font-bold"
                              data-testid="torque-value-input"
                            />
                            <span className="text-slate-600 font-medium">Nm</span>
                            <span className="text-xs text-slate-400">(50–300)</span>
                          </div>
                        </div>

                        {/* ── Selection Summary ── */}
                        {torqueApplied && (
                          <div className={`p-3 rounded-lg flex items-center justify-between ${
                            torqueSelectionSource === "manufacturer" ? 'bg-green-50 border border-green-300' :
                            'bg-slate-50 border border-slate-300'
                          }`}>
                            <div className="flex items-center gap-2">
                              <Check className={`w-5 h-5 ${torqueSelectionSource === "manufacturer" ? 'text-green-600' : 'text-slate-500'}`} />
                              <span className="font-bold text-lg">{torqueApplied} Nm</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              torqueSelectionSource === "manufacturer" ? 'bg-green-100 text-green-700' :
                              torqueSelectionSource === "quick_select" ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {torqueSelectionSource === "manufacturer" ? "Manufacturer Spec" :
                               torqueSelectionSource === "quick_select" ? "Quick Select" :
                               "Manual Entry"}
                            </span>
                          </div>
                        )}
                        
                        {/* Skipped Warning */}
                        {!torqueConfirmed && (
                          <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            <span className="text-xs text-amber-700">Not confirmed — will be logged as skipped</span>
                          </div>
                        )}

                        {/* Photo Evidence */}
                        <div className="p-3 bg-white rounded-lg border border-slate-200">
                          <Label className="text-sm text-slate-600 mb-2 block">
                            Torque wrench photo (optional)
                          </Label>
                          <p className="text-xs text-slate-400 mb-2">Photo of torque wrench on wheel as evidence</p>
                          {photos.length > 0 ? (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {photos.map((photo, idx) => (
                                <div key={idx} className="relative w-20 h-20 rounded overflow-hidden border">
                                  <img src={photo} alt={`Torque evidence ${idx + 1}`} className="w-full h-full object-cover" />
                                  <button
                                    type="button"
                                    className="absolute top-0 right-0 bg-red-500 text-white rounded-bl px-1 text-xs"
                                    onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                                  >x</button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <label className="flex items-center gap-2 p-2 border border-dashed border-slate-300 rounded cursor-pointer hover:border-blue-400 transition-colors">
                            <Camera className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-500">{photos.length > 0 ? 'Add photo' : 'Capture photo'}</span>
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              onChange={handlePhotoCapture}
                              data-testid="torque-photo-input"
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════════
                    LOCKING WHEEL NUT KEY CHECK - Capture presence and location
                ═══════════════════════════════════════════════════════════════════ */}
                {item?.item_key === "locking_wheel_key" && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border-2 border-slate-300">
                    <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-lg">
                      <Key className="w-5 h-5" /> Locking Wheel Nut Key Check
                    </h4>
                    
                    <p className="text-sm text-slate-600 mb-4">
                      Check if the vehicle has locking wheel nuts and if the key is present. 
                      This information is critical for pricing wheel/tyre work.
                    </p>
                    
                    {/* Key Present Options */}
                    <div className="space-y-3 mb-4">
                      <Label className="font-semibold">Is the locking wheel nut key present? <span className="text-red-500">*</span></Label>
                      
                      <div className="grid grid-cols-1 gap-2">
                        <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          lockingKeyPresent === 'yes' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                        }`}>
                          <input
                            type="radio"
                            name="lockingKeyPresent"
                            value="yes"
                            checked={lockingKeyPresent === 'yes'}
                            onChange={(e) => setLockingKeyPresent(e.target.value)}
                            className="w-5 h-5"
                            data-testid="locking-key-yes"
                          />
                          <div>
                            <span className="font-medium text-green-800">Yes - Key present and located</span>
                            <p className="text-xs text-green-600">Locking key found in vehicle</p>
                          </div>
                        </label>
                        
                        <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          lockingKeyPresent === 'no' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'
                        }`}>
                          <input
                            type="radio"
                            name="lockingKeyPresent"
                            value="no"
                            checked={lockingKeyPresent === 'no'}
                            onChange={(e) => setLockingKeyPresent(e.target.value)}
                            className="w-5 h-5"
                            data-testid="locking-key-no"
                          />
                          <div>
                            <span className="font-medium text-red-800">No - Key NOT present</span>
                            <p className="text-xs text-red-600">Locking key missing - removal labour will be quoted</p>
                          </div>
                        </label>
                        
                        <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          lockingKeyPresent === 'na' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                        }`}>
                          <input
                            type="radio"
                            name="lockingKeyPresent"
                            value="na"
                            checked={lockingKeyPresent === 'na'}
                            onChange={(e) => setLockingKeyPresent(e.target.value)}
                            className="w-5 h-5"
                            data-testid="locking-key-na"
                          />
                          <div>
                            <span className="font-medium text-blue-800">N/A - No locking wheel nuts fitted</span>
                            <p className="text-xs text-blue-600">Standard wheel nuts only on this vehicle</p>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Key Location - Only show if key is present */}
                    {lockingKeyPresent === 'yes' && (
                      <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <Label className="font-semibold text-green-800 mb-2 block">
                          Where is the key stored?
                        </Label>
                        <Select value={lockingKeyLocation} onValueChange={setLockingKeyLocation}>
                          <SelectTrigger className="bg-white" data-testid="locking-key-location">
                            <SelectValue placeholder="Select location..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="glovebox">Glovebox</SelectItem>
                            <SelectItem value="boot">Boot/Trunk</SelectItem>
                            <SelectItem value="spare_wheel">With spare wheel</SelectItem>
                            <SelectItem value="under_seat">Under seat</SelectItem>
                            <SelectItem value="toolkit">In toolkit</SelectItem>
                            <SelectItem value="other">Other location</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Notes for missing key or access issues */}
                    {(lockingKeyPresent === 'no' || lockingKeyPresent === 'yes') && (
                      <div className="mb-4">
                        <Label className="font-semibold mb-2 block">
                          Additional Notes {lockingKeyPresent === 'no' && <span className="text-red-500">*</span>}
                        </Label>
                        <Textarea
                          value={lockingKeyNotes}
                          onChange={(e) => setLockingKeyNotes(e.target.value)}
                          placeholder={lockingKeyPresent === 'no' 
                            ? "e.g., Customer aware key is missing, spoke to customer, locking nut type identified as..." 
                            : "Any additional notes about wheel access, key condition, etc."
                          }
                          className="min-h-[80px]"
                          data-testid="locking-key-notes"
                        />
                      </div>
                    )}
                    
                    {/* Warning for missing key */}
                    {lockingKeyPresent === 'no' && (
                      <div className="p-3 bg-red-100 border border-red-300 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-red-800 font-medium">
                              Locking wheel nut removal will be required
                            </p>
                            <p className="text-xs text-red-700 mt-1">
                              FOH will be prompted to add removal labour (approx. 1 hour), 
                              removal kit charge, and optionally a new wheel nut set.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════════
                    Enforces structured, validated, and auditable tyre pressure capture
                ═══════════════════════════════════════════════════════════════════ */}
                {item?.item_key === "tyre_pressures_equalised" && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border-2 border-slate-300">
                    <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-lg">
                      <Thermometer className="w-5 h-5" /> Tyre Pressure Check & Adjustment
                    </h4>
                    
                    {/* Manufacturer Spec Display */}
                    <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                      <Label className="text-sm text-slate-600 mb-1 block">Manufacturer Specification</Label>
                      {pressureSpecLoading ? (
                        <div className="flex items-center gap-2 text-slate-500">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Looking up specification...</span>
                        </div>
                      ) : pressureSpec.front ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-green-700">{pressureSpec.front} PSI</span>
                            <span className="text-xs text-slate-500">Front</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-green-700">{pressureSpec.rear} PSI</span>
                            <span className="text-xs text-slate-500">Rear</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded col-span-2 w-fit ${
                            pressureSpec.source === 'exact_match' || pressureSpec.source === 'manufacturer'
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {pressureSpec.source === 'exact_match' || pressureSpec.source === 'manufacturer' 
                              ? 'Vehicle matched' 
                              : pressureSpec.source === 'saved' ? 'Previously saved' : 'Default values'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Using default pressures (Front: 32 PSI, Rear: 35 PSI)</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Unable to Adjust Toggle */}
                    <div className="mb-4">
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        pressureUnableAdjust ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input
                          type="checkbox"
                          checked={pressureUnableAdjust}
                          onChange={(e) => {
                            setPressureUnableAdjust(e.target.checked);
                            if (e.target.checked) {
                              setPressureMeasured({ fl: "", fr: "", rl: "", rr: "" });
                              setPressureAdjusted({ fl: "", fr: "", rl: "", rr: "" });
                            }
                          }}
                          className="w-5 h-5 rounded border-2"
                          data-testid="pressure-unable-checkbox"
                        />
                        <div>
                          <span className="font-medium text-slate-800">Unable to adjust pressures</span>
                          <p className="text-xs text-slate-500">Select if tyre pressures could not be adjusted for any reason</p>
                        </div>
                      </label>
                    </div>
                    
                    {pressureUnableAdjust ? (
                      /* Exception Path - Unable to Adjust */
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <Label className="font-semibold text-red-800 mb-2 block">
                          Reason pressures could not be adjusted <span className="text-red-500">*</span>
                        </Label>
                        <div className="space-y-2 mb-3">
                          {[
                            "TPMS sensors not responding",
                            "Valve damage - cannot seal",
                            "Vehicle collected before completion",
                            "Customer declined adjustment",
                            "Equipment unavailable"
                          ].map(reason => (
                            <Button
                              key={reason}
                              type="button"
                              variant="outline"
                              size="sm"
                              className={`text-xs w-full justify-start ${
                                pressureUnableReason === reason ? 'bg-red-100 border-red-400 text-red-800' : ''
                              }`}
                              onClick={() => setPressureUnableReason(reason)}
                            >
                              {reason}
                            </Button>
                          ))}
                        </div>
                        <Textarea
                          value={pressureUnableReason}
                          onChange={(e) => setPressureUnableReason(e.target.value)}
                          placeholder="Or enter custom reason..."
                          className="min-h-[60px]"
                          data-testid="pressure-unable-reason"
                        />
                      </div>
                    ) : (
                      /* Normal Path - Pressure Entry */
                      <>
                        {/* Quick Preset Buttons - DEPRECATED: Now using "Set All After to Spec" button instead */}
                        {/* <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <Label className="text-xs text-slate-600 mb-2 block font-medium">Quick Select</Label>
                          ... removed - measured pressures no longer required ...
                        </div> */}
                        
                        {/* Auto-fill Adjusted to Spec Button */}
                        <div className="mb-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={`w-full ${
                              pressureAdjusted.fl && pressureAdjusted.fr && pressureAdjusted.rl && pressureAdjusted.rr
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-green-300 text-green-700 hover:bg-green-50'
                            }`}
                            onClick={() => {
                              const frontSpec = pressureSpec.front || 2.2;
                              const rearSpec = pressureSpec.rear || 2.4;
                              setPressureAdjusted({
                                fl: frontSpec.toString(),
                                fr: frontSpec.toString(),
                                rl: rearSpec.toString(),
                                rr: rearSpec.toString()
                              });
                            }}
                            data-testid="pressure-fill-spec"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            {pressureAdjusted.fl && pressureAdjusted.fr && pressureAdjusted.rl && pressureAdjusted.rr
                              ? `✓ Adjusted set to spec (F: ${pressureSpec.front || 32}, R: ${pressureSpec.rear || 35} PSI)`
                              : `Set All "After" to Spec (F: ${pressureSpec.front || 32}, R: ${pressureSpec.rear || 35} PSI)`
                            }
                          </Button>
                        </div>
                        
                        {/* 4-Corner Grid - Adjusted Pressures */}
                        <div className="mb-4">
                          <Label className="font-semibold text-slate-800 mb-3 block">
                            Adjusted Pressures (After) <span className="text-red-500">*</span>
                          </Label>
                          <div className="grid grid-cols-2 gap-3">
                            {/* Front Left */}
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <Label className="text-xs text-green-700 mb-1 block font-medium">Front Left</Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0.5"
                                  max="5.0"
                                  value={pressureAdjusted.fl}
                                  onChange={(e) => setPressureAdjusted({ ...pressureAdjusted, fl: e.target.value })}
                                  placeholder="0.0"
                                  className="w-20 text-center font-mono"
                                  data-testid="pressure-adjusted-fl"
                                />
                                <span className="text-slate-500 text-sm">PSI</span>
                                {pressureAdjusted.fl && pressureSpec.front && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    Math.abs(parseFloat(pressureAdjusted.fl) - pressureSpec.front) <= 0.2 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {Math.abs(parseFloat(pressureAdjusted.fl) - pressureSpec.front) <= 0.2 ? '✓ Spec' : 'Deviation'}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Front Right */}
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <Label className="text-xs text-green-700 mb-1 block font-medium">Front Right</Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0.5"
                                  max="5.0"
                                  value={pressureAdjusted.fr}
                                  onChange={(e) => setPressureAdjusted({ ...pressureAdjusted, fr: e.target.value })}
                                  placeholder="0.0"
                                  className="w-20 text-center font-mono"
                                  data-testid="pressure-adjusted-fr"
                                />
                                <span className="text-slate-500 text-sm">PSI</span>
                                {pressureAdjusted.fr && pressureSpec.front && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    Math.abs(parseFloat(pressureAdjusted.fr) - pressureSpec.front) <= 0.2 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {Math.abs(parseFloat(pressureAdjusted.fr) - pressureSpec.front) <= 0.2 ? '✓ Spec' : 'Deviation'}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Rear Left */}
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <Label className="text-xs text-green-700 mb-1 block font-medium">Rear Left</Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0.5"
                                  max="5.0"
                                  value={pressureAdjusted.rl}
                                  onChange={(e) => setPressureAdjusted({ ...pressureAdjusted, rl: e.target.value })}
                                  placeholder="0.0"
                                  className="w-20 text-center font-mono"
                                  data-testid="pressure-adjusted-rl"
                                />
                                <span className="text-slate-500 text-sm">PSI</span>
                                {pressureAdjusted.rl && pressureSpec.rear && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    Math.abs(parseFloat(pressureAdjusted.rl) - pressureSpec.rear) <= 0.2 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {Math.abs(parseFloat(pressureAdjusted.rl) - pressureSpec.rear) <= 0.2 ? '✓ Spec' : 'Deviation'}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Rear Right */}
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <Label className="text-xs text-green-700 mb-1 block font-medium">Rear Right</Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0.5"
                                  max="5.0"
                                  value={pressureAdjusted.rr}
                                  onChange={(e) => setPressureAdjusted({ ...pressureAdjusted, rr: e.target.value })}
                                  placeholder="0.0"
                                  className="w-20 text-center font-mono"
                                  data-testid="pressure-adjusted-rr"
                                />
                                <span className="text-slate-500 text-sm">PSI</span>
                                {pressureAdjusted.rr && pressureSpec.rear && (
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    Math.abs(parseFloat(pressureAdjusted.rr) - pressureSpec.rear) <= 0.2 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {Math.abs(parseFloat(pressureAdjusted.rr) - pressureSpec.rear) <= 0.2 ? '✓ Spec' : 'Deviation'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Deviation Reason - Required if adjusted pressures differ from spec */}
                        {(() => {
                          const specFront = pressureSpec.front || 2.2;
                          const specRear = pressureSpec.rear || 2.4;
                          const hasDeviation = PRESSURE_POSITIONS.some(pos => {
                            const adjVal = parseFloat(pressureAdjusted[pos]);
                            if (!adjVal) return false;
                            const spec = pos.startsWith("f") ? specFront : specRear;
                            return Math.abs(adjVal - spec) > 0.2;
                          });
                          
                          if (!hasDeviation) return null;
                          
                          return (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-800">
                                  Adjusted pressures differ from specification
                                </span>
                              </div>
                              <Label className="text-sm text-amber-700 mb-1 block">
                                Reason for Deviation <span className="text-red-500">*</span>
                              </Label>
                              <div className="space-y-2 mb-3">
                                {[
                                  "Customer requested specific pressures",
                                  "Heavy load - increased rear pressures",
                                  "Towing setup - adjusted for trailer",
                                  "Worn tyre - reduced for safety"
                                ].map(reason => (
                                  <Button
                                    key={reason}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className={`text-xs w-full justify-start ${
                                      pressureDeviationReason === reason ? 'bg-amber-100 border-amber-400 text-amber-800' : ''
                                    }`}
                                    onClick={() => setPressureDeviationReason(reason)}
                                  >
                                    {reason}
                                  </Button>
                                ))}
                              </div>
                              <Textarea
                                value={pressureDeviationReason}
                                onChange={(e) => setPressureDeviationReason(e.target.value)}
                                placeholder="Or enter custom reason (minimum 10 characters)..."
                                className="min-h-[60px]"
                                data-testid="pressure-deviation-reason"
                              />
                              <p className={`text-xs mt-1 ${
                                pressureDeviationReason.length >= 10 ? 'text-green-600' : 'text-slate-500'
                              }`}>
                                {pressureDeviationReason.length}/10 characters minimum
                              </p>
                            </div>
                          );
                        })()}
                        
                        {/* Status Summary */}
                        {Object.values(pressureAdjusted).every(v => v) && (
                          <div className={`p-3 rounded-lg flex items-center gap-2 ${
                            (() => {
                              const specFront = pressureSpec.front || 2.2;
                              const specRear = pressureSpec.rear || 2.4;
                              const allWithinSpec = PRESSURE_POSITIONS.every(pos => {
                                const adjVal = parseFloat(pressureAdjusted[pos]);
                                const spec = pos.startsWith("f") ? specFront : specRear;
                                return Math.abs(adjVal - spec) <= 0.2;
                              });
                              return allWithinSpec ? 'bg-green-50 border border-green-300' : 'bg-amber-50 border border-amber-300';
                            })()
                          }`}>
                            {(() => {
                              const specFront = pressureSpec.front || 2.2;
                              const specRear = pressureSpec.rear || 2.4;
                              const allWithinSpec = PRESSURE_POSITIONS.every(pos => {
                                const adjVal = parseFloat(pressureAdjusted[pos]);
                                const spec = pos.startsWith("f") ? specFront : specRear;
                                return Math.abs(adjVal - spec) <= 0.2;
                              });
                              
                              return allWithinSpec ? (
                                <>
                                  <Check className="w-5 h-5 text-green-600" />
                                  <span className="text-green-800 font-medium">All pressures within specification</span>
                                </>
                              ) : (
                                <>
                                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                                  <span className="text-amber-800 font-medium">Deviation from spec - reason required</span>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* AC Gas Type Selection - for aircon_operation items */}
                {item?.item_key === "aircon_operation" && (
                  <div className="space-y-4 p-4 bg-cyan-50 rounded-lg border border-cyan-200" data-testid="ac-gas-type-section">
                    <div className="flex items-center gap-2 text-cyan-800">
                      <Snowflake className="w-5 h-5" />
                      <h4 className="font-semibold">Air Conditioning System Details</h4>
                    </div>
                    
                    {/* AC Performance Selection */}
                    <div>
                      <Label className="text-sm text-slate-700 mb-2 block font-medium">
                        AC Performance <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { value: "cold", label: "Cold - Working", color: "green" },
                          { value: "weak", label: "Weak - Reduced Cooling", color: "amber" },
                          { value: "not_cold", label: "Not Cold - No Cooling", color: "red" }
                        ].map(opt => (
                          <Button
                            key={opt.value}
                            type="button"
                            variant={acPerformance === opt.value ? "default" : "outline"}
                            size="sm"
                            className={acPerformance === opt.value 
                              ? `bg-${opt.color}-600 hover:bg-${opt.color}-700 text-white`
                              : `border-${opt.color}-300 text-${opt.color}-700 hover:bg-${opt.color}-50`
                            }
                            onClick={() => setAcPerformance(opt.value)}
                            data-testid={`ac-performance-${opt.value}`}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Refrigerant Type - ALWAYS required for all statuses */}
                    <div className="p-3 bg-white rounded-lg border-2 border-cyan-300">
                        <Label className="text-sm text-slate-700 mb-2 block font-medium">
                          Refrigerant Type <span className="text-red-500">* MANDATORY</span>
                          <span className="ml-2 text-xs text-slate-500">(Check under-bonnet label)</span>
                        </Label>
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            variant={acGasType === "R134a" ? "default" : "outline"}
                            size="lg"
                            className={`flex-1 h-16 ${acGasType === "R134a" 
                              ? 'bg-blue-600 hover:bg-blue-700 text-white ring-2 ring-blue-400'
                              : 'border-blue-300 text-blue-700 hover:bg-blue-50'
                            }`}
                            onClick={() => setAcGasType("R134a")}
                            data-testid="ac-gas-r134a"
                          >
                            <div className="text-center">
                              <div className="font-bold text-lg">R134a</div>
                              <div className="text-xs opacity-80">Older systems (pre-2017)</div>
                            </div>
                          </Button>
                          <Button
                            type="button"
                            variant={acGasType === "R1234yf" ? "default" : "outline"}
                            size="lg"
                            className={`flex-1 h-16 ${acGasType === "R1234yf" 
                              ? 'bg-green-600 hover:bg-green-700 text-white ring-2 ring-green-400'
                              : 'border-green-300 text-green-700 hover:bg-green-50'
                            }`}
                            onClick={() => setAcGasType("R1234yf")}
                            data-testid="ac-gas-r1234yf"
                          >
                            <div className="text-center">
                              <div className="font-bold text-lg">R1234yf</div>
                              <div className="text-xs opacity-80">Newer systems (2017+)</div>
                            </div>
                          </Button>
                        </div>
                        {!acGasType && (
                          <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Refrigerant type must be selected before saving
                          </p>
                        )}
                      </div>
                    
                    {/* Optional AC Flags */}
                    <div>
                      <Label className="text-sm text-slate-700 mb-2 block font-medium">Additional Observations</Label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox"
                            checked={acFlags.system_empty}
                            onChange={(e) => setAcFlags({ ...acFlags, system_empty: e.target.checked })}
                            className="w-4 h-4 rounded border-red-300 text-red-600"
                          />
                          <span className={acFlags.system_empty ? 'text-red-700 font-medium' : 'text-slate-600'}>
                            System Empty (leak test required)
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox"
                            checked={acFlags.compressor_not_engaging}
                            onChange={(e) => setAcFlags({ ...acFlags, compressor_not_engaging: e.target.checked })}
                            className="w-4 h-4 rounded border-amber-300 text-amber-600"
                          />
                          <span className={acFlags.compressor_not_engaging ? 'text-amber-700 font-medium' : 'text-slate-600'}>
                            Compressor Not Engaging
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox"
                            checked={acFlags.noisy_operation}
                            onChange={(e) => setAcFlags({ ...acFlags, noisy_operation: e.target.checked })}
                            className="w-4 h-4 rounded border-amber-300 text-amber-600"
                          />
                          <span className={acFlags.noisy_operation ? 'text-amber-700 font-medium' : 'text-slate-600'}>
                            Noisy Operation
                          </span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Summary */}
                    {acGasType && (
                      <div className="p-3 bg-cyan-100 rounded-lg border border-cyan-300">
                        <p className="text-cyan-800 font-medium flex items-center gap-2">
                          <Check className="w-5 h-5" />
                          Vehicle uses <strong className={acGasType === "R134a" ? "text-blue-700" : "text-green-700"}>{acGasType}</strong> refrigerant
                        </p>
                        {(status === "amber" || status === "red") && (
                          <p className="text-cyan-700 text-sm mt-1">
                            Package includes: Regas, leak test, compressor oil, pollen filter & sanitiser
                          </p>
                        )}
                        {status === "green" && (
                          <p className="text-cyan-700 text-sm mt-1">
                            AC working — gas type recorded for service interval pricing
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Common Issues Multi-Select - for amber/red/blue */}
                {(status === "amber" || status === "red" || status === "blue") && currentIssues.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-semibold">Select Issue(s)</Label>
                      {selectedIssues.length > 0 && (
                        <button 
                          type="button"
                          onClick={handleClearIssues}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Clear all ({selectedIssues.length})
                        </button>
                      )}
                    </div>
                    
                    {/* Check if we have grouped issues */}
                    {commonIssues.groups && commonIssues.groups[status] ? (
                      <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-3 bg-white">
                        {commonIssues.groups[status].map((group, gIdx) => (
                          <div key={gIdx} className="space-y-1">
                            <p className={`text-xs font-bold uppercase tracking-wide px-2 py-1 rounded ${
                              status === "red" ? "bg-red-100 text-red-800" : status === "blue" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"
                            }`}>
                              {group.name}
                            </p>
                            {group.items.map((issue, iIdx) => (
                              <label 
                                key={iIdx} 
                                className={`flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 transition-colors ${
                                  selectedIssues.includes(issue) ? (status === "red" ? "bg-red-50" : status === "blue" ? "bg-blue-50" : "bg-amber-50") : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedIssues.includes(issue)}
                                  onChange={() => handleIssueToggle(issue)}
                                  className="mt-0.5 rounded"
                                />
                                <span className="text-sm">{issue}</span>
                              </label>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Ungrouped issues - simple list */
                      <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1 bg-white">
                        {currentIssues.map((issue, idx) => (
                          <label 
                            key={idx} 
                            className={`flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 transition-colors ${
                              selectedIssues.includes(issue) ? (status === "red" ? "bg-red-50" : "bg-amber-50") : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIssues.includes(issue)}
                              onChange={() => handleIssueToggle(issue)}
                              className="mt-0.5 rounded"
                              data-testid={`issue-checkbox-${idx}`}
                            />
                            <span className="text-sm">{issue}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500 mt-2">
                      {selectedIssues.length === 0 
                        ? "Select one or more issues, or type a custom note below"
                        : `${selectedIssues.length} issue${selectedIssues.length > 1 ? "s" : ""} selected`
                      }
                    </p>
                  </div>
                )}

                {/* ============== STATUS OVERRIDE (Issue 4) ============== */}
                {/* When auto-status is calculated from measurements, allow tech to override */}
                {isMeasurementFirstItem && status && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-700">Auto-assigned: <span className={`uppercase font-bold ${
                          status === 'green' ? 'text-green-600' : status === 'amber' ? 'text-amber-600' : status === 'red' ? 'text-red-600' : 'text-black'
                        }`}>{status}</span></p>
                        <p className="text-xs text-slate-500 mt-1">Override if there's a non-measurement defect</p>
                      </div>
                      {!statusOverride ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => setStatusOverride(status)}
                          data-testid="override-status-btn"
                        >
                          Override Status
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs text-slate-500"
                          onClick={() => { setStatusOverride(null); }}
                          data-testid="cancel-override-btn"
                        >
                          Cancel Override
                        </Button>
                      )}
                    </div>
                    {statusOverride && (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {[
                          { value: "amber", label: "Amber", color: "bg-amber-500" },
                          { value: "red", label: "Red", color: "bg-red-500" },
                          { value: "dangerous", label: "Dangerous", color: "bg-black" },
                        ].filter(opt => opt.value !== status).map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            className={`p-2 rounded border-2 text-center text-sm font-bold transition-all ${
                              statusOverride === opt.value
                                ? `${opt.color} text-white border-transparent`
                                : "border-gray-200 hover:border-gray-400"
                            }`}
                            onClick={() => {
                              setStatusOverride(opt.value);
                              setStatus(opt.value);
                            }}
                            data-testid={`override-${opt.value}-btn`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Notes - required for amber/red unless dropdown selected; OPTIONAL for dangerous */}
                {(status === "amber" || status === "red" || status === "dangerous") && (
                  <div>
                    <Label className="font-semibold">
                      {status === "dangerous" ? (
                        <>
                          Additional Notes <span className="text-slate-400 text-xs font-normal">(optional)</span>
                          <span className="block text-xs text-slate-500 font-normal mt-1">
                            Your risk category and reason selections above will be used as the primary record
                          </span>
                        </>
                      ) : (
                        <>
                          Notes {!(selectedIssues.length > 0 || discCondition) && "*"}
                          {selectedIssues.length > 0 && (
                            <span className="text-green-600 ml-2 text-xs font-normal">✓ Pre-filled from selection</span>
                          )}
                          {discCondition && !selectedIssues.length && (
                            <span className="text-green-600 ml-2 text-xs font-normal">✓ Disc condition selected</span>
                          )}
                        </>
                      )}
                    </Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => {
                        setNotes(e.target.value);
                        // If user edits notes manually, keep selections but allow custom additions
                      }}
                      placeholder={
                        status === "dangerous"
                          ? "Optional: Add any additional context or observations..."
                          : item.evidence_type === "fluid_level" 
                            ? "Describe the issue. Add 'not capturable' if photo cannot be taken..."
                            : (selectedIssues.length > 0 || discCondition) 
                              ? "Optional: Add additional notes..."
                              : "Select issues above or type custom notes here..."
                      }
                      rows={status === "dangerous" ? 2 : 3}
                      className={status === "dangerous" ? "border-slate-200" : ""}
                      data-testid="notes-input"
                    />
                    {status !== "dangerous" && (
                      <>
                        {!(selectedIssues.length > 0 || discCondition) && notes.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">{notes.length} characters</p>
                        )}
                        {(selectedIssues.length > 0 || discCondition) && notes.length === 0 && (
                          <p className="text-xs text-green-600 mt-1">Notes will be auto-generated from your selection</p>
                        )}
                        {item.evidence_type === "fluid_level" && (
                          <p className="text-xs text-blue-600 mt-1">
                            💡 Tip: If the low level cannot be photographed, include "not capturable" in your notes
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Tester Photo - shown if required (moved after notes for consistency) */}
                {item.requires_tester_photo && status !== "blue" && (
                  <div className="space-y-2">
                    {/* Tester Photo Required Banner */}
                    {photos.length === 0 && !testerPhoto && (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-red-700 font-semibold text-sm">TESTER PHOTO REQUIRED - Capture equipment reading</span>
                      </div>
                    )}
                    <Label className={`font-semibold ${(!testerPhoto && photos.length === 0) ? 'text-red-700' : 'text-green-700'}`}>
                      Photos <span className="text-red-500">*</span>
                      <span className="text-xs font-normal text-slate-500 ml-2">First photo should show tester/device reading</span>
                    </Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {/* Existing tester photo (legacy single) */}
                      {testerPhoto && (
                        <div className="relative group">
                          <img src={testerPhoto} alt="Tester" className="w-full h-24 object-cover rounded border-2 border-green-300" />
                          <button
                            className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs z-10"
                            onClick={() => setTesterPhoto(null)}
                          >x</button>
                          <div className="absolute bottom-0 left-0 right-0 bg-green-800/80 text-white text-xs text-center py-0.5">Tester</div>
                        </div>
                      )}
                      {/* Additional photos */}
                      {photos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={photo} alt={`Photo ${idx + 1}`}
                            className="w-full h-24 object-cover rounded cursor-pointer border-2 border-transparent hover:border-blue-500 transition-colors"
                            onClick={() => handleEditPhoto(idx)}
                          />
                          <button
                            className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs z-10"
                            onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                          >x</button>
                        </div>
                      ))}
                      {/* Add photo button — always visible */}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          id="tester-evidence-photo"
                          onChange={!testerPhoto && photos.length === 0 ? handleTesterPhotoCapture : handlePhotoCapture}
                          data-testid="tester-photo-input"
                        />
                        <Button
                          variant="outline"
                          className="w-full h-24 border-2 border-dashed"
                          onClick={() => document.getElementById("tester-evidence-photo").click()}
                          disabled={testerPhotoLoading}
                          data-testid="capture-tester-photo-btn"
                        >
                          <div className="flex flex-col items-center gap-1">
                            {testerPhotoLoading ? (
                              <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : (
                              <>
                                <Camera className="w-5 h-5 text-slate-500" />
                                <span className="text-xs text-slate-500">{(!testerPhoto && photos.length === 0) ? 'Capture Tester' : 'Add Photo'}</span>
                              </>
                            )}
                          </div>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Evidence Photos - based on evidence_type */}
                {(status === "amber" || status === "red" || status === "dangerous") && (
                  <>
                    {/* DANGEROUS status ALWAYS requires photos with annotation support */}
                    {status === "dangerous" ? (
                      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                        <Label className="font-bold text-red-800 flex items-center gap-2">
                          <Camera className="w-5 h-5" />
                          Evidence Photos * (MANDATORY for DANGEROUS)
                        </Label>
                        <p className="text-xs text-red-700 mb-3">
                          📷 Capture photo evidence, then <strong>tap the photo to add annotations</strong> highlighting the dangerous condition
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {photos.map((photo, idx) => (
                            <div key={idx} className="relative group">
                              <img 
                                src={photo} 
                                alt={`Evidence ${idx + 1}`} 
                                className="w-full h-24 object-cover rounded cursor-pointer border-2 border-red-300 hover:border-red-500 transition-colors" 
                                onClick={() => handleEditPhoto(idx)}
                                data-testid={`dangerous-photo-${idx}`}
                              />
                              <button
                                className="absolute top-1 right-1 bg-red-600 text-white w-5 h-5 rounded-full text-xs z-10 hover:bg-red-700"
                                onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                              >
                                ×
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-red-900/80 text-white text-xs text-center py-1 opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                                ✏️ Tap to annotate
                              </div>
                            </div>
                          ))}
                          <div>
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              id="dangerous-evidence-photo"
                              onChange={handlePhotoCapture}
                              data-testid="dangerous-evidence-photo-input"
                            />
                            <Button
                              variant="outline"
                              className="w-full h-24 border-2 border-dashed border-red-400 hover:border-red-500 hover:bg-red-50"
                              onClick={() => document.getElementById("dangerous-evidence-photo").click()}
                              data-testid="add-dangerous-evidence-photo-btn"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <Camera className="w-6 h-6 text-red-600" />
                                <span className="text-xs text-red-600 font-medium">Add Photo</span>
                              </div>
                            </Button>
                          </div>
                        </div>
                        {photos.length === 0 && (
                          <p className="text-xs text-red-600 mt-2 font-medium">
                            ⚠️ At least 1 photo required for dangerous designation
                          </p>
                        )}
                      </div>
                    ) : /* Show evidence requirement based on type for amber/red */
                    item.evidence_type === "functional_check" || item.evidence_type === "confirmation_only" ? (
                      <div className="space-y-3">
                        <div className="bg-slate-50 border border-slate-200 rounded p-3">
                          <p className="text-sm text-slate-600">
                            <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />
                            <strong>No photo required</strong> for this item type (pass/fail only)
                          </p>
                        </div>
                        {/* Optional photos — tech can still attach evidence if they wish */}
                        <div>
                          <Label className="text-sm text-slate-600">Photos (optional)</Label>
                          <p className="text-xs text-slate-400 mb-2">Add a photo if you want to capture supporting evidence</p>
                          <div className="mt-1 grid grid-cols-3 gap-2">
                            {photos.map((photo, idx) => (
                              <div key={idx} className="relative group">
                                <img
                                  src={photo}
                                  alt={`Photo ${idx + 1}`}
                                  className="w-full h-20 object-cover rounded cursor-pointer border-2 border-transparent hover:border-blue-500 transition-colors"
                                  onClick={() => handleEditPhoto(idx)}
                                />
                                <button
                                  className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs z-10"
                                  onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                                  data-testid={`remove-functional-photo-${idx}`}
                                >x</button>
                              </div>
                            ))}
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                id="functional-optional-photo"
                                onChange={handlePhotoCapture}
                                data-testid="functional-optional-photo-input"
                              />
                              <Button
                                variant="outline"
                                className="w-full h-20 border-dashed"
                                onClick={() => document.getElementById("functional-optional-photo").click()}
                                data-testid="add-functional-optional-photo-btn"
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <Camera className="w-4 h-4 text-slate-400" />
                                  <span className="text-xs text-slate-400">Add Photo</span>
                                </div>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : item.evidence_type === "measured_test" ? (
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded p-3">
                          <p className="text-sm text-blue-700 mb-2">
                            <Camera className="w-4 h-4 inline mr-1" />
                            <strong>Device photo required</strong> - must show measuring device displaying result
                          </p>
                          {/* Tester photo is handled in the measurement section above */}
                        </div>
                        {/* Additional evidence photos for measured_test items */}
                        <div>
                          <Label className="font-semibold">Additional Photos</Label>
                          <p className="text-xs text-gray-500 mb-2">Add extra evidence photos if needed</p>
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {photos.map((photo, idx) => (
                              <div key={idx} className="relative group">
                                <img 
                                  src={photo} 
                                  alt={`Evidence ${idx + 1}`} 
                                  className="w-full h-20 object-cover rounded cursor-pointer border-2 border-transparent hover:border-blue-500 transition-colors" 
                                  onClick={() => handleEditPhoto(idx)}
                                />
                                <button
                                  className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs z-10"
                                  onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                                >
                                  x
                                </button>
                              </div>
                            ))}
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                id="measured-evidence-photo"
                                onChange={handlePhotoCapture}
                                data-testid="measured-evidence-photo-input"
                              />
                              <Button
                                variant="outline"
                                className="w-full h-20 border-dashed"
                                onClick={() => document.getElementById("measured-evidence-photo").click()}
                                data-testid="add-measured-evidence-photo-btn"
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <Camera className="w-4 h-4" />
                                  <span className="text-xs">Add Photo</span>
                                </div>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : item.evidence_type === "fluid_level" ? (
                      <div>
                        <Label className="font-semibold">
                          Evidence Photo {notes.toLowerCase().includes("not capturable") ? "(bypassed)" : "*"}
                        </Label>
                        {notes.toLowerCase().includes("not capturable") ? (
                          <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
                            <p className="text-sm text-amber-700">
                              Photo bypassed - "not capturable" noted
                            </p>
                          </div>
                        ) : (
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {photos.map((photo, idx) => (
                              <div key={idx} className="relative">
                                <img src={photo} alt={`Evidence ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                                <button
                                  className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs"
                                  onClick={() => removePhoto(idx)}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                id="evidence-photo"
                                onChange={handlePhotoCapture}
                                data-testid="evidence-photo-input"
                              />
                              <Button
                                variant="outline"
                                className="w-full h-20"
                                onClick={() => document.getElementById("evidence-photo").click()}
                                data-testid="add-evidence-photo-btn"
                              >
                                <Camera className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* visual_wear - standard photo requirement */
                      <div>
                        <Label className="font-semibold">Evidence Photos * (min 1)</Label>
                        <p className="text-xs text-gray-500 mb-2">📷 Capture photo, then tap it to add annotations (optional)</p>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {photos.map((photo, idx) => (
                            <div key={idx} className="relative group">
                              <img 
                                src={photo} 
                                alt={`Evidence ${idx + 1}`} 
                                className="w-full h-20 object-cover rounded cursor-pointer border-2 border-transparent hover:border-blue-500 transition-colors" 
                                onClick={() => handleEditPhoto(idx)}
                              />
                              <button
                                className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs z-10"
                                onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                              >
                                ×
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                Tap to annotate
                              </div>
                            </div>
                          ))}
                          <div>
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              id="evidence-photo"
                              onChange={handlePhotoCapture}
                              data-testid="evidence-photo-input"
                            />
                            <Button
                              variant="outline"
                              className="w-full h-20"
                              onClick={() => document.getElementById("evidence-photo").click()}
                              data-testid="add-evidence-photo-btn"
                            >
                              <Camera className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Optional notes and photos for green/blue */}
                {(status === "green" || status === "blue") && (
                  <div className="space-y-3">
                    <Label className="font-semibold">Notes (optional)</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any additional notes..."
                      rows={2}
                      data-testid="optional-notes-input"
                    />
                    {/* Optional photos for green/blue — always available */}
                    <div>
                      <Label className="text-sm text-slate-600">Photos (optional)</Label>
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        {photos.map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={photo} alt={`Photo ${idx + 1}`}
                              className="w-full h-20 object-cover rounded cursor-pointer border-2 border-transparent hover:border-blue-500 transition-colors"
                              onClick={() => handleEditPhoto(idx)}
                            />
                            <button
                              className="absolute top-1 right-1 bg-red-500 text-white w-5 h-5 rounded-full text-xs z-10"
                              onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                            >x</button>
                          </div>
                        ))}
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            id="green-evidence-photo"
                            onChange={handlePhotoCapture}
                            data-testid="green-evidence-photo-input"
                          />
                          <Button
                            variant="outline"
                            className="w-full h-20 border-dashed"
                            onClick={() => document.getElementById("green-evidence-photo").click()}
                            data-testid="add-green-evidence-photo-btn"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <Camera className="w-4 h-4 text-slate-400" />
                              <span className="text-xs text-slate-400">Add Photo</span>
                            </div>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Save & Back wrappers explicitly given the LAST order
                slots so they always render at the foot of the item,
                even when measurement-first items push the conditional /
                status wrappers to ``order-1`` / ``order-2`` (which
                previously left the default-order ``order-0`` Save bar
                rendering visually ABOVE them — the friction the techs
                were complaining about). */}
            <div className="order-last mt-6" data-testid="save-bar-wrapper">
              <Button 
                className="w-full" 
                disabled={!status || submitting}
                onClick={handleSubmit}
                data-testid="save-item-btn"
              >
                {submitting ? "Saving..." : "Save & Continue"}
              </Button>
              <Button 
                variant="ghost" 
                className="w-full mt-2 text-slate-500"
                onClick={() => navigate(`/app/tech/job/${id}/section/${item.section}`)}
                data-testid="back-without-saving-btn"
              >
                ← Back without saving
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tyre Copy Modal - Copy measurements to other tyres */}
      {tyreCopyModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Copy className="w-5 h-5" />
                Copy to Other Tyres?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Would you like to copy the <strong>tyre size</strong> to the other tyres?
              </p>
              <div className="bg-blue-50 p-3 rounded-lg space-y-2 mb-4">
                {tyreCopyModal.tyreSize && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tyre Size:</span>
                    <span className="font-semibold">{tyreCopyModal.tyreSize}</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-amber-600 flex items-center gap-1 mb-2">
                <AlertCircle className="w-4 h-4" />
                Tread depth and wear % must be measured separately for each tyre
              </p>
              <p className="text-xs text-slate-500">
                This will update: Front Left, Front Right, Rear Left, Rear Right (except the one you just saved)
              </p>
            </CardContent>
            <div className="flex flex-col gap-2 p-6 pt-0">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleCopyToOtherTyres}
                disabled={copyingTyres}
                data-testid="copy-tyres-btn"
              >
                {copyingTyres ? "Copying..." : "Yes, Copy Size to All Tyres"}
              </Button>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => {
                  setTyreCopyModal({ show: false, measurementValue: '', tyreSize: '', percentWorn: '', currentTyre: '' });
                  navigate(`/app/tech/job/${id}/section/${item.section}`);
                }}
                disabled={copyingTyres}
              >
                No, Different Sizes (Staggered Fitment)
              </Button>
              <p className="text-xs text-center text-slate-500 mt-1">
                Select "Different Sizes" if this vehicle has staggered tyre sizes (e.g., wider rear tyres on performance cars)
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============== SERVICE ADVISOR DASHBOARD (Status Board View) ==============
const FOHDashboard = () => {
  const { user } = useAuth();
  const [statusBoard, setStatusBoard] = useState(null);
  const [awaitingAuth, setAwaitingAuth] = useState({ tasks: [], total: 0, high_priority_count: 0 });
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  
  // Track active job locks across all jobs
  const { locks: activeLocks, refresh: refreshLocks } = useActiveLocks();
  const [resendingTask, setResendingTask] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [garageLogo, setGarageLogo] = useState(null);
  const [garageContact, setGarageContact] = useState({ name: "", phone: "", email: "" });
  const [savingContactField, setSavingContactField] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Admin Override Mode state
  const [adminOverrideMode, setAdminOverrideMode] = useState({ enabled: false, enabled_by: null });
  const [togglingOverride, setTogglingOverride] = useState(false);
  // Call Companion state
  const [callCompanion, setCallCompanion] = useState(null);
  const [showCallCompanion, setShowCallCompanion] = useState(false);
  
  // Archive state
  const [archivingJob, setArchivingJob] = useState(null);
  
  // Authorised Jobsheet modal state
  const [showJobsheet, setShowJobsheet] = useState(false);
  const [jobsheetData, setJobsheetData] = useState(null);
  const [loadingJobsheet, setLoadingJobsheet] = useState(false);
  // iter211ab — GarageHive tick tracker. Keyed line_key → true when FOH
  // has re-keyed that line into GH. Persisted server-side per job via
  // GET/PATCH /api/foh/job/{job_id}/gh-ticks.
  const [ghTicks, setGhTicks] = useState({});
  const [ghTicksJobId, setGhTicksJobId] = useState(null);
  const [copiedGhLine, setCopiedGhLine] = useState(null);
  
  // Follow-ups count for header indicator
  const [followUpCounts, setFollowUpCounts] = useState({ urgent: 0, total: 0 });
  
  // Follow-up scheduling modal
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpTarget, setFollowUpTarget] = useState(null);
  const [followUpDays, setFollowUpDays] = useState(14);
  const [creatingFollowUp, setCreatingFollowUp] = useState(false);
  
  // Call Companion Compliance State
  const [callTone, setCallTone] = useState(null); // 'reassuring', 'neutral', 'direct'
  const [compliancePhases, setCompliancePhases] = useState({
    phase1: { completed: false, notes: '' },
    phase2: { completed: false, notes: '' },
    phase3: { completed: false, notes: '' },
    phase4: { completed: false, notes: '' },
    phase5: { completed: false, notes: '' },
    phase6: { completed: false, notes: '' },
    phase7: { completed: false, notes: '' }
  });
  const [currentPhase, setCurrentPhase] = useState(0); // 0 = tone selection, 1-7 = phases
  const [callOutcome, setCallOutcome] = useState({ redAuthorised: null, amberPercent: 0 });
  const [showComplianceWarning, setShowComplianceWarning] = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null); // Audit trail: call start time
  
  // Objection Handling State
  const [showObjectionPanel, setShowObjectionPanel] = useState(false);
  const [activeObjection, setActiveObjection] = useState(null);
  const [loggedObjections, setLoggedObjections] = useState([]);
  
  // Send Back to Tech modal state
  const [sendBackModal, setSendBackModal] = useState({ show: false, card: null });
  const [sendBackOptions, setSendBackOptions] = useState({ addDiagnostic: false, addVI: false, notes: '' });
  const [sendingBack, setSendingBack] = useState(false);
  
  // Objection Matrix - Complete & Controlled
  const OBJECTION_MATRIX = {
    cost: {
      id: 'cost',
      title: '💰 Cost & Value',
      color: 'red',
      buttons: [
        { id: 'too_expensive', label: '"Too expensive"' },
        { id: 'didnt_expect', label: '"Didn\'t expect this cost"' },
        { id: 'car_not_worth', label: '"Car not worth that much"' },
        { id: 'cheaper_elsewhere', label: '"Cheaper elsewhere"' },
        { id: 'cant_afford', label: '"I can\'t afford it"' }
      ],
      objective: 'Reframe cost into priority, safety, and control — not negotiation.',
      guidance: {
        reassuring: '"I completely understand — unexpected costs are never easy. What we\'ve identified here are the items that affect your safety or could lead to bigger repair bills if left. Would it help if we looked at prioritising just the critical items first?"',
        neutral: '"These are the findings from the inspection. The costs reflect the work required. We can discuss which items to prioritise based on safety and urgency."',
        direct: '"I hear you. The price reflects the actual work needed. Let\'s focus on what\'s essential for safety, and you can decide on the rest."'
      },
      doNotSay: ['Offer discounts', 'Apologise for the price', 'Defend or justify pricing', 'Compare to competitors'],
      checkboxLabel: 'Cost objection handled compliantly'
    },
    trust: {
      id: 'trust',
      title: '🔍 Trust & Credibility',
      color: 'orange',
      buttons: [
        { id: 'do_i_need', label: '"Do I really need this?"' },
        { id: 'another_garage', label: '"Another garage said it\'s fine"' },
        { id: 'passed_mot', label: '"It passed its MOT"' },
        { id: 'just_upselling', label: '"You\'re just upselling"' },
        { id: 'feels_excessive', label: '"Feels excessive"' }
      ],
      objective: 'Anchor trust to evidence and documentation — not opinion.',
      guidance: {
        reassuring: '"That\'s a fair question. Everything we\'ve flagged is documented with photos and technician notes. The MOT is a minimum legal standard on the day — our inspection looks at what\'s safe and reliable going forward. Would you like me to walk through the evidence?"',
        neutral: '"Our findings are documented with evidence. The MOT checks legal minimums at the time of test. Our inspection identifies what needs attention for ongoing safety and reliability."',
        direct: '"These findings are fact-based with photo evidence. MOT is a minimum standard. This inspection shows the full picture. I can show you the documentation if helpful."'
      },
      doNotSay: ['Argue with the customer', 'Make comparisons to other garages', 'Say "trust me"', 'Dismiss their concerns'],
      checkboxLabel: 'Credibility objection handled compliantly'
    },
    timing: {
      id: 'timing',
      title: '⏰ Timing & Delay',
      color: 'amber',
      buttons: [
        { id: 'need_time', label: '"I need time to think"' },
        { id: 'sort_later', label: '"I\'ll sort it later"' },
        { id: 'not_urgent', label: '"Not urgent"' },
        { id: 'come_back', label: '"I\'ll come back to it"' },
        { id: 'drive_for_now', label: '"Can I drive it for now?"' }
      ],
      objective: 'Prevent delay becoming silent refusal. Must set a decision boundary.',
      guidance: {
        reassuring: '"Of course, take the time you need. Just so I can keep things moving, when would be a good time to check back in? I want to make sure we don\'t lose track of the safety items."',
        neutral: '"Understood. To manage this properly, can we agree on a follow-up time? The safety items should ideally be addressed before continued driving."',
        direct: '"That\'s fine. When should I call you back for a decision? I need to flag that the safety items carry risk if driven on."'
      },
      doNotSay: ['Leave it completely open-ended', 'Allow indefinite delay', 'Say "no rush"', 'Minimise urgency of safety items'],
      checkboxLabel: 'Timing objection handled compliantly'
    },
    thirdParty: {
      id: 'thirdParty',
      title: '👥 Third Party / Second Opinion',
      color: 'purple',
      buttons: [
        { id: 'check_another', label: '"I\'ll check with another garage"' },
        { id: 'mate_knows', label: '"My mate knows about cars"' },
        { id: 'dealer_said', label: '"Dealer said something different"' },
        { id: 'second_opinion', label: '"I\'ll get a second opinion"' }
      ],
      objective: 'Protect credibility without defensiveness.',
      guidance: {
        reassuring: '"Absolutely, getting another view is sensible for big decisions. Just so you know, our report includes photos and technician notes if you\'d like to share it with whoever you\'re consulting."',
        neutral: '"That\'s your choice. Our documented findings are available for you to share. Different inspectors may focus on different things."',
        direct: '"No problem. Take the report with you — it has the evidence. Just be aware that different garages may have different standards of inspection."'
      },
      doNotSay: ['Criticise other garages', 'Badmouth dealers', 'Undermine your own inspection', 'Get defensive'],
      checkboxLabel: 'Third-party objection handled compliantly'
    },
    usage: {
      id: 'usage',
      title: '🚗 Vehicle Use / Circumstances',
      color: 'blue',
      buttons: [
        { id: 'dont_use_much', label: '"I don\'t use the car much"' },
        { id: 'short_journeys', label: '"Only short journeys"' },
        { id: 'need_for_bit', label: '"Just need it for a bit"' },
        { id: 'selling_soon', label: '"Selling it soon"' },
        { id: 'just_runaround', label: '"It\'s only a runaround"' }
      ],
      objective: 'Reframe around risk and responsibility — not usage.',
      guidance: {
        reassuring: '"I understand. Even for occasional use, the safety items still carry risk — brakes, steering, tyres don\'t know if you\'re doing 5 miles or 50. The items flagged are about keeping you safe regardless of journey length."',
        neutral: '"Usage patterns don\'t change the condition of safety-critical components. The items we\'ve flagged need attention regardless of mileage."',
        direct: '"Lower usage doesn\'t reduce the safety risk. These items are about condition, not mileage. The risk is still there when you drive."'
      },
      doNotSay: ['Minimise safety concerns', 'Judge their car choices', 'Say "it\'s up to you"', 'Agree that it doesn\'t matter'],
      checkboxLabel: 'Usage objection handled compliantly'
    },
    history: {
      id: 'history',
      title: '📋 History / Previous Work',
      color: 'slate',
      buttons: [
        { id: 'done_recently', label: '"It was done recently"' },
        { id: 'another_worked', label: '"Another garage just worked on it"' },
        { id: 'parts_replaced', label: '"Parts were already replaced"' },
        { id: 'never_issue', label: '"Never had an issue before"' }
      ],
      objective: 'Return focus to current condition — not past reassurance.',
      guidance: {
        reassuring: '"I hear you — it\'s frustrating when something comes up after recent work. What we\'re showing you is the current condition as we found it today. Sometimes things can change quickly, especially with wear items."',
        neutral: '"Our inspection reflects the current condition. Previous work may have been correct at the time. This is what we found today."',
        direct: '"What matters is the condition now. Things change. This is what our technician documented today."'
      },
      doNotSay: ['Criticise previous work', 'Make assumptions about other garages', 'Dismiss their history', 'Argue about what was done before'],
      checkboxLabel: 'History objection handled compliantly'
    },
    warranty: {
      id: 'warranty',
      title: '📄 Warranty / Coverage',
      color: 'teal',
      buttons: [
        { id: 'under_warranty', label: '"Isn\'t this under warranty?"' },
        { id: 'should_be_covered', label: '"Shouldn\'t this be covered?"' },
        { id: 'breakdown_cover', label: '"I\'ve got breakdown cover"' },
        { id: 'extended_warranty', label: '"Extended warranty should pay"' }
      ],
      objective: 'Clarify responsibility vs coverage — without promises.',
      guidance: {
        reassuring: '"That\'s worth checking. Warranty and breakdown cover have specific terms. I\'d suggest contacting your provider with our report to see what\'s covered. In the meantime, the safety items still need a decision."',
        neutral: '"Warranty coverage depends on your specific policy. We can provide documentation for any claim. The work decision is separate from the claim process."',
        direct: '"You\'d need to check with your provider. We can document everything for a claim. But the safety decision can\'t wait for that process."'
      },
      doNotSay: ['Promise coverage or reimbursement', 'Make legal claims', 'Advise on warranty disputes', 'Delay the safety decision indefinitely'],
      checkboxLabel: 'Warranty objection handled compliantly'
    },
    emotional: {
      id: 'emotional',
      title: '😰 Emotional / Defensive',
      color: 'pink',
      buttons: [
        { id: 'feel_pressured', label: '"I feel pressured"' },
        { id: 'stressful', label: '"This is stressful"' },
        { id: 'dont_trust', label: '"I don\'t trust garages"' },
        { id: 'bad_experience', label: '"I\'ve had bad experiences before"' },
        { id: 'feels_unfair', label: '"This feels unfair"' }
      ],
      objective: 'De-escalate emotion without surrendering structure.',
      guidance: {
        reassuring: '"I\'m really sorry you\'re feeling that way — that\'s not our intention at all. I want to make sure you feel informed, not pressured. Let\'s slow down. Is there anything specific I can clarify to help?"',
        neutral: '"I understand this can be stressful. I\'m not here to pressure you — just to give you the information. Take a moment if you need. What questions can I answer?"',
        direct: '"I get it. Garage experiences can be frustrating. I\'m giving you facts, not pressure. Tell me what would help you feel more comfortable with this."'
      },
      doNotSay: ['Get defensive', 'Dismiss their feelings', 'Appease by reducing safety recommendations', 'Argue or push back'],
      checkboxLabel: 'Emotional objection handled compliantly'
    }
  };
  
  const [loadingCompanion, setLoadingCompanion] = useState(false);
  const [currentCallTask, setCurrentCallTask] = useState(null);
  // Quick Pricing Summary state
  const [showQuickPricingSummary, setShowQuickPricingSummary] = useState(false);
  const [quickPricingResult, setQuickPricingResult] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    // Clear all auth-related storage
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    sessionStorage.clear();
    
    // Call logout to clear React state
    logout();
    
    // Force a complete page reload with cache bypass
    window.location.replace("/app/login?t=" + Date.now());
  };

  // Load authorised jobsheet data
  const loadJobsheet = async (jobId) => {
    setLoadingJobsheet(true);
    try {
      // iter211ab — Pull the jobsheet AND the GarageHive-tick map in
      // parallel so the modal opens with the FOH's saved progress
      // already visible (no ticks flickering in a beat later).
      const [jsRes, ghRes] = await Promise.all([
        axios.get(`${API}/foh/job/${jobId}/authorised-jobsheet`),
        axios.get(`${API}/foh/job/${jobId}/gh-ticks`).catch(() => ({ data: { ticks: {} } })),
      ]);
      setJobsheetData(jsRes.data);
      setGhTicks(ghRes.data?.ticks || {});
      setGhTicksJobId(jobId);
      setShowJobsheet(true);
    } catch (err) {
      alert("Failed to load jobsheet: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingJobsheet(false);
    }
  };

  // iter211ab — GarageHive-tick helpers.
  //
  // Line keys the FE constructs (opaque to backend):
  //   ${jgId}::labour::${line.id || 'idx'+i}
  //   ${jgId}::parts::${line.id || 'idx'+i}
  //   ${jgId}::charge::${charge.id || 'idx'+i}
  //   workshop::consumables    /   workshop::environmental
  //   already::${idx}          (already-authorised baseline lines)
  const ghKeyFor = (jgId, kind, id, idx) => `${jgId}::${kind}::${id ?? "idx" + idx}`;

  const persistGhTicks = useCallback(async (delta) => {
    if (!ghTicksJobId) return;
    try {
      await axios.patch(`${API}/foh/job/${ghTicksJobId}/gh-ticks`, { ticks: delta });
    } catch (e) {
      // Non-blocking — the FE state is source-of-truth for the session.
      console.warn("gh-ticks persist failed", e);
    }
  }, [ghTicksJobId]);

  const toggleGhTick = (lineKey) => {
    setGhTicks((prev) => {
      const next = { ...prev };
      if (next[lineKey]) delete next[lineKey];
      else next[lineKey] = true;
      persistGhTicks({ [lineKey]: !prev[lineKey] });
      return next;
    });
  };

  const copyGhLine = async (line, kind, lineKey) => {
    // Format each line for one-line paste into GH:
    //   Labour: "Description | 0.50h @ £75.00 = £37.50"
    //   Parts:  "Description | qty 1 × £45.00 = £45.00"
    //   Charge: "Label | £12.50"
    let text = "";
    if (kind === "labour") {
      const hrs = Number(line.hours || 0).toFixed(2);
      const rate = Number(line.rate || 0).toFixed(2);
      const sub = Number(line.subtotal || (line.hours || 0) * (line.rate || 0)).toFixed(2);
      text = `${line.description || ""} | ${hrs}h @ £${rate} = £${sub}`;
    } else if (kind === "parts") {
      const qty = line.quantity || 1;
      const unit = Number(line.unit_price || line.total / (qty || 1) || 0).toFixed(2);
      const total = Number(line.total || (qty * (line.unit_price || 0))).toFixed(2);
      text = `${line.description || ""} | qty ${qty} × £${unit} = £${total}`;
    } else if (kind === "charge") {
      const amt = Number(line.amount || line.total || line.total_inc_vat || 0).toFixed(2);
      text = `${line.label || line.name || line.description || ""} | £${amt}`;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedGhLine(lineKey);
      setTimeout(() => setCopiedGhLine((k) => (k === lineKey ? null : k)), 1500);
    } catch {
      /* clipboard may be unavailable in the preview iframe — silent */
    }
  };

  const ghOutstandingCount = useMemo(() => {
    if (!jobsheetData) return { total: 0, done: 0, remaining: 0 };
    const items = jobsheetData.authorised_items || [];
    let total = 0;
    let done = 0;
    for (const jg of items) {
      const jgId = jg.job_group_id || jg.id;
      (jg.labour_lines || []).forEach((l, i) => {
        total++;
        if (ghTicks[ghKeyFor(jgId, "labour", l.id, i)]) done++;
      });
      (jg.parts_lines || []).forEach((p, i) => {
        total++;
        if (ghTicks[ghKeyFor(jgId, "parts", p.id, i)]) done++;
      });
      (jg.charges || []).forEach((c, i) => {
        total++;
        if (ghTicks[ghKeyFor(jgId, "charge", c.id, i)]) done++;
      });
    }
    (jobsheetData.workshop_charges || []).forEach((c, i) => {
      total++;
      if (ghTicks[`workshop::${c.id || "idx" + i}`]) done++;
    });
    return { total, done, remaining: total - done };
  }, [jobsheetData, ghTicks]);

  const markAllGhTicks = () => {
    if (!jobsheetData) return;
    const delta = {};
    for (const jg of (jobsheetData.authorised_items || [])) {
      const jgId = jg.job_group_id || jg.id;
      (jg.labour_lines || []).forEach((l, i) => { delta[ghKeyFor(jgId, "labour", l.id, i)] = true; });
      (jg.parts_lines || []).forEach((p, i) => { delta[ghKeyFor(jgId, "parts", p.id, i)] = true; });
      (jg.charges || []).forEach((c, i) => { delta[ghKeyFor(jgId, "charge", c.id, i)] = true; });
    }
    (jobsheetData.workshop_charges || []).forEach((c, i) => { delta[`workshop::${c.id || "idx" + i}`] = true; });
    setGhTicks((prev) => ({ ...prev, ...delta }));
    persistGhTicks(delta);
  };

  const clearAllGhTicks = async () => {
    if (!ghTicksJobId) return;
    setGhTicks({});
    try {
      await axios.patch(`${API}/foh/job/${ghTicksJobId}/gh-ticks`, { clear_all: true });
    } catch (e) {
      console.warn("gh-ticks clear_all failed", e);
    }
  };

  const startWizard = () => {
    // This function would start the pricing wizard
    // For now, it's a placeholder that could navigate to a wizard page
    console.log("Starting pricing wizard...");
  };

  useEffect(() => {
    fetchData();
    fetchAwaitingAuth();
    fetchGarageLogo();
    fetchGarageContact();
    fetchAdminOverrideMode();
    // Refresh awaiting auth every 60 seconds
    const interval = setInterval(() => {
      fetchAwaitingAuth();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Auto-open Create Inspection form when URL has reg/mileage/jobref params
  useEffect(() => {
    const regParam = searchParams.get('reg');
    const mileageParam = searchParams.get('mileage');
    const jobrefParam = searchParams.get('jobref');
    
    if (regParam || mileageParam || jobrefParam) {
      // Build query string and navigate to /foh/intake (Add Vehicle form)
      const queryString = searchParams.toString();
      navigate(`/app/foh/intake?${queryString}`);
    }
  }, [searchParams, navigate]);

  const fetchGarageLogo = async () => {
    try {
      const res = await axios.get(`${API}/admin/garage/logo`);
      if (res.data.has_logo) {
        setGarageLogo(res.data);
      }
    } catch (err) {
      console.log("No garage logo configured");
    }
  };

  const fetchGarageContact = async () => {
    try {
      const res = await axios.get(`${API}/admin/garage/contact`);
      setGarageContact({
        name: res.data?.name || "",
        phone: res.data?.phone || "",
        email: res.data?.email || "",
      });
    } catch (err) {
      console.log("Could not load garage contact settings");
    }
  };

  const saveGarageContactField = async (field, value) => {
    // Persist the whole contact block (backend expects all fields together).
    const next = { ...garageContact, [field]: value };
    setSavingContactField(field);
    try {
      await axios.put(`${API}/admin/garage/contact`, {
        name: next.name || null,
        phone: next.phone || null,
        email: next.email || null,
      });
      setGarageContact(next);
      toast.success("Garage contact updated");
    } catch (err) {
      toast.error("Failed to update contact");
    } finally {
      setSavingContactField(null);
    }
  };

  const fetchAdminOverrideMode = async () => {
    try {
      const res = await axios.get(`${API}/admin/override-mode`);
      setAdminOverrideMode(res.data);
    } catch (err) {
      console.log("Could not fetch admin override mode");
    }
  };

  const toggleAdminOverrideMode = async (enabled) => {
    setTogglingOverride(true);
    try {
      const res = await axios.put(`${API}/admin/override-mode`, {
        enabled: enabled,
        reason: enabled ? "Testing mode enabled" : ""
      });
      if (res.data.success) {
        setAdminOverrideMode(prev => ({ ...prev, enabled: enabled }));
        toast.success(res.data.message);
      }
    } catch (err) {
      console.error("Override toggle error:", err.response?.data || err.message);
      const errorMsg = err.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Failed to toggle override mode: ${errorMsg}`);
    } finally {
      setTogglingOverride(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Please upload PNG, JPG, or SVG.");
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      alert("File too large. Maximum size is 2MB.");
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/admin/garage/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      fetchGarageLogo();
      toast.success("Logo uploaded!");
    } catch (err) {
      toast.error("Failed to upload logo", {
        description: err.response?.data?.detail
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!confirm("Are you sure you want to remove the garage logo?")) return;
    
    try {
      await axios.delete(`${API}/admin/garage/logo`);
      setGarageLogo(null);
      toast.success("Logo removed");
    } catch (err) {
      toast.error("Failed to remove logo");
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/foh/status-board`);
      setStatusBoard(res.data);
      
      // Also fetch follow-up counts for header indicator
      try {
        const fuRes = await axios.get(`${API}/follow-ups`);
        const urgent = (fuRes.data.book_requested?.length || 0) + 
                      (fuRes.data.chase_overdue?.length || 0) + 
                      (fuRes.data.chase_due_today?.length || 0);
        const total = fuRes.data.summary?.total_active || 0;
        setFollowUpCounts({ urgent, total });
      } catch (fuErr) {
        console.log("Follow-ups fetch skipped:", fuErr.message);
      }
    } catch (err) {
      console.error("Failed to fetch status board:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAwaitingAuth = async () => {
    try {
      const res = await axios.get(`${API}/foh/awaiting-authorisation`);
      setAwaitingAuth(res.data);
    } catch (err) {
      console.error("Failed to fetch awaiting auth:", err);
    }
  };

  const handleResendLink = async (taskId) => {
    setResendingTask(taskId);
    try {
      const res = await axios.post(`${API}/foh/task/${taskId}/resend`);
      if (res.data.success) {
        toast.success("Reminder SMS sent successfully!", {
          description: "Customer will receive the link shortly"
        });
      } else {
        toast.warning("SMS not sent", {
          description: res.data.message || "Check Twilio configuration"
        });
      }
      fetchAwaitingAuth();
    } catch (err) {
      console.error("Failed to resend:", err);
      toast.error("Failed to send reminder", {
        description: err.response?.data?.detail || "Please try again"
      });
    } finally {
      setResendingTask(null);
    }
  };

  const handleMarkContacted = async (taskId) => {
    try {
      await axios.put(`${API}/foh/task/${taskId}/status`, { status: "contacted" });
      toast.success("Status updated", {
        description: "Marked as contacted"
      });
      fetchAwaitingAuth();
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Failed to update status");
    }
  };

  // Helper function to handle phase completion with auto-advance
  const handlePhaseComplete = (phaseNum) => {
    const phaseKey = `phase${phaseNum}`;
    const wasCompleted = compliancePhases[phaseKey]?.completed;
    
    // Update the phase completion status
    setCompliancePhases(prev => ({
      ...prev,
      [phaseKey]: { ...prev[phaseKey], completed: !wasCompleted }
    }));
    
    // If marking as completed (not uncompleting), auto-advance to next phase
    if (!wasCompleted && phaseNum < 7) {
      // Small delay so user sees the checkmark before advancing
      setTimeout(() => {
        setCurrentPhase(phaseNum + 1);
      }, 400);
    }
  };

  // Load Call Companion for a task and show modal
  const handleCallNow = async (task) => {
    setCurrentCallTask(task);
    setLoadingCompanion(true);
    setShowCallCompanion(true);
    
    // Reset compliance state for new call
    setCallTone(null);
    setCompliancePhases({
      phase1: { completed: false, notes: '' },
      phase2: { completed: false, notes: '' },
      phase3: { completed: false, notes: '' },
      phase4: { completed: false, notes: '' },
      phase5: { completed: false, notes: '' },
      phase6: { completed: false, notes: '' },
      phase7: { completed: false, notes: '' }
    });
    setCurrentPhase(0);
    setCallOutcome({ redAuthorised: null, amberPercent: 0 });
    setShowComplianceWarning(null);
    setShowObjectionPanel(false);
    setActiveObjection(null);
    setLoggedObjections([]);
    // Track call start time for audit trail
    setCallStartedAt(new Date().toISOString());
    
    try {
      // iter211ac — MOT-only bookings never have an inspection record,
      // so the previous "resolve inspection_id then fetch companion"
      // dance blew up with "No inspection found for this task" (see
      // PJ15UUX). The backend now exposes a job-first endpoint that
      // internally delegates to the inspection-based builder for full
      // VI jobs and falls back to the yestimate's job_groups for
      // MOT-only ones. Prefer that; keep the legacy inspection path as
      // a safety net for any FOH task shapes that still lack a job_id.
      let companionRes;
      if (task.job_id) {
        companionRes = await axios.get(`${API}/foh/job/${task.job_id}/call-companion`);
      } else {
        let inspectionId = task.inspection_id;
        if (!inspectionId && task.yestimate_id) {
          try {
            const yestimateRes = await axios.get(`${API}/foh/yestimate/${task.yestimate_id}`);
            inspectionId = yestimateRes.data.inspection_id;
          } catch (e) { /* fall through */ }
        }
        if (!inspectionId) {
          throw new Error("No inspection found for this task");
        }
        companionRes = await axios.get(`${API}/foh/inspection/${inspectionId}/call-companion`);
      }

      setCallCompanion(companionRes.data);
      
      // Don't mark as contacted here - let the advisor complete the call first
      // Status will be updated when they finish the call or mark as resolved
    } catch (err) {
      console.error("Failed to load Call Companion:", err);
      alert("Failed to load Call Companion: " + (err.response?.data?.detail || err.message));
      setShowCallCompanion(false);
    } finally {
      setLoadingCompanion(false);
    }
  };

  // Submit Call Companion audit trail
  const submitCallCompanionAudit = async (outcomeStatus) => {
    if (!currentCallTask || !callCompanion) return;
    
    const callEndedAt = new Date().toISOString();
    const callDurationSeconds = Math.round(
      (new Date(callEndedAt) - new Date(callStartedAt)) / 1000
    );
    
    // Calculate phases data
    const phasesData = Object.entries(compliancePhases).map(([key, value]) => ({
      phase_number: parseInt(key.replace('phase', '')),
      completed: value.completed,
      completed_at: value.completed ? callEndedAt : null,
      notes: value.notes || null
    }));
    
    // Format objections data
    const objectionsData = loggedObjections.map(obj => ({
      objection_type: obj.type,
      objection_button: obj.button,
      handled_compliantly: obj.handled || false,
      logged_at: obj.loggedAt || callEndedAt
    }));
    
    const auditPayload = {
      task_id: currentCallTask.id,
      inspection_id: callCompanion.inspection_id,
      job_id: currentCallTask.job_id,
      vehicle_reg: currentCallTask.vehicle_reg || callCompanion.vehicle?.reg || '',
      customer_name: currentCallTask.customer_name || callCompanion.customer?.name || null,
      customer_phone: currentCallTask.customer_phone || callCompanion.customer?.phone || null,
      call_tone: callTone || 'unknown',
      phases: phasesData,
      objections: objectionsData,
      outcome: {
        red_authorised: callOutcome.redAuthorised,
        amber_percent: callOutcome.amberPercent,
        outcome_status: outcomeStatus
      },
      call_started_at: callStartedAt,
      call_ended_at: callEndedAt,
      call_duration_seconds: callDurationSeconds,
      compliance_score: Math.round((phasesData.filter(p => p.completed).length / 7) * 100),
      was_compliant: phasesData.filter(p => p.completed).length === 7
    };
    
    try {
      await axios.post(`${API}/foh/call-companion/audit`, auditPayload);
      console.log("Call Companion audit saved successfully");
    } catch (err) {
      console.error("Failed to save Call Companion audit:", err);
      // Don't block the user flow if audit fails
    }
  };

  const handleResolveTask = async (taskId) => {
    try {
      await axios.put(`${API}/foh/task/${taskId}/status`, { status: "resolved" });
      toast.success("Task resolved", {
        description: "Moved to resolved status"
      });
      fetchAwaitingAuth();
    } catch (err) {
      console.error("Failed to resolve task:", err);
      toast.error("Failed to resolve task");
    }
  };

  // Delete awaiting task and its inspection
  const [deleteAwaitingConfirm, setDeleteAwaitingConfirm] = useState({ show: false, task: null, step: 1 });
  
  const handleDeleteAwaitingTask = (task) => {
    setDeleteAwaitingConfirm({ show: true, task, step: 1 });
  };
  
  const confirmDeleteAwaitingTask = async () => {
    if (deleteAwaitingConfirm.step === 1) {
      setDeleteAwaitingConfirm(prev => ({ ...prev, step: 2 }));
      return;
    }
    
    try {
      const task = deleteAwaitingConfirm.task;
      
      // Delete the inspection (this cascades to delete job, yestimate, items, etc.)
      if (task.inspection_id) {
        await axios.delete(`${API}/foh/inspection/${task.inspection_id}`);
      }
      
      // Also delete the FOH task
      await axios.delete(`${API}/foh/task/${task.id}`);
      
      toast.success("Deleted successfully", {
        description: "Inspection and all related data removed"
      });
      setDeleteAwaitingConfirm({ show: false, task: null, step: 1 });
      fetchAwaitingAuth();
      fetchData(); // Refresh completed inspections too
    } catch (err) {
      toast.error("Failed to delete", {
        description: err.response?.data?.detail || "Please try again"
      });
    }
  };

  // Delete inspection with double confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, item: null, step: 1 });
  
  const handleDeleteInspection = (item, e) => {
    e.stopPropagation(); // Prevent navigation
    setDeleteConfirmation({ show: true, item, step: 1 });
  };
  
  const confirmDelete = async () => {
    if (deleteConfirmation.step === 1) {
      // First confirmation - ask again
      setDeleteConfirmation(prev => ({ ...prev, step: 2 }));
      return;
    }
    
    // Second confirmation - actually delete
    try {
      const item = deleteConfirmation.item;
      
      // Use inspection endpoint if inspection_id exists, otherwise use job endpoint
      if (item.inspection_id) {
        await axios.delete(`${API}/foh/inspection/${item.inspection_id}`);
      } else if (item.job_id) {
        await axios.delete(`${API}/foh/job/${item.job_id}`);
      } else {
        throw new Error("No valid ID found for deletion");
      }
      
      toast.success("Deleted successfully", {
        description: `${item.reg} has been removed`
      });
      setDeleteConfirmation({ show: false, item: null, step: 1 });
      fetchData(); // Refresh the list
    } catch (err) {
      toast.error("Failed to delete", {
        description: err.response?.data?.detail || err.message || "Please try again"
      });
    }
  };
  
  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, item: null, step: 1 });
  };

  // Delete pending job (Awaiting Technician) with double confirmation
  const [deletePendingConfirm, setDeletePendingConfirm] = useState({ show: false, job: null, step: 1 });
  
  // Timing Belt Replacement Modal State
  const [timingBeltModal, setTimingBeltModal] = useState({ show: false, card: null, mileage: '' });
  const [recordingBeltReplacement, setRecordingBeltReplacement] = useState(false);
  
  const handleMarkTimingBeltReplaced = async () => {
    if (!timingBeltModal.card || !timingBeltModal.mileage) return;
    
    setRecordingBeltReplacement(true);
    try {
      await axios.post(`${API}/vehicle/timing-belt-replacement`, {
        reg_number: timingBeltModal.card.reg,
        replacement_mileage: parseInt(timingBeltModal.mileage),
        replacement_date: new Date().toISOString().split('T')[0],
        notes: `Marked as replaced via FOH dashboard`
      });
      toast.success("Timing belt replacement recorded", {
        description: `${timingBeltModal.card.reg} - timing belt marked as replaced at ${parseInt(timingBeltModal.mileage).toLocaleString()} miles`
      });
      setTimingBeltModal({ show: false, card: null, mileage: '' });
      fetchData(); // Refresh to update the status
    } catch (err) {
      toast.error("Failed to record replacement", {
        description: err.response?.data?.detail || "Please try again"
      });
    } finally {
      setRecordingBeltReplacement(false);
    }
  };

  const handleDeletePendingJob = (job) => {
    setDeletePendingConfirm({ show: true, job, step: 1 });
  };
  
  const confirmDeletePendingJob = async () => {
    if (deletePendingConfirm.step === 1) {
      setDeletePendingConfirm(prev => ({ ...prev, step: 2 }));
      return;
    }
    
    try {
      // Handle both job.id and job.job_id for different card types
      const jobId = deletePendingConfirm.job.job_id || deletePendingConfirm.job.id;
      await axios.delete(`${API}/foh/job/${jobId}`);
      toast.success("Job deleted", {
        description: `${deletePendingConfirm.job.reg} has been removed`
      });
      setDeletePendingConfirm({ show: false, job: null, step: 1 });
      fetchData(); // Refresh the list
    } catch (err) {
      toast.error("Failed to delete job", {
        description: err.response?.data?.detail || "Please try again"
      });
    }
  };

  // Archive a job (mark as completed)
  const handleArchiveJob = async (jobId, reg, e) => {
    if (e) e.stopPropagation(); // Prevent card click navigation
    setArchivingJob(jobId);
    try {
      await axios.post(`${API}/foh/job/${jobId}/complete`);
      toast.success("Job archived", {
        description: `${reg} has been moved to archive`
      });
      fetchData(); // Refresh the dashboard
    } catch (err) {
      toast.error("Failed to archive job", {
        description: err.response?.data?.detail || "Please try again"
      });
    } finally {
      setArchivingJob(null);
    }
  };

  // Send job back to tech for additional work
  const handleSendBackToTech = (card, e) => {
    if (e) e.stopPropagation();
    setSendBackModal({ show: true, card });
    setSendBackOptions({ addDiagnostic: false, addVI: false, notes: '' });
  };
  
  const confirmSendBackToTech = async () => {
    if (!sendBackModal.card) return;
    
    setSendingBack(true);
    try {
      const jobId = sendBackModal.card.job_id || sendBackModal.card.id;
      await axios.post(`${API}/foh/job/${jobId}/send-back-to-tech`, {
        add_diagnostic_vi: sendBackOptions.addDiagnostic,
        add_vehicle_inspection: sendBackOptions.addVI,
        notes: sendBackOptions.notes || null
      });
      toast.success("Sent back to technician", {
        description: `${sendBackModal.card.reg} is now in the tech queue`
      });
      setSendBackModal({ show: false, card: null });
      fetchData(); // Refresh the dashboard
    } catch (err) {
      toast.error("Failed to send back", {
        description: err.response?.data?.detail || "Please try again"
      });
    } finally {
      setSendingBack(false);
    }
  };

  // Schedule follow-up for declined work
  const handleScheduleFollowUp = (card, e) => {
    if (e) e.stopPropagation();
    setFollowUpTarget(card);
    setFollowUpDays(14);
    setShowFollowUpModal(true);
  };
  
  // Toggle vehicle on site status
  const handleToggleVehicleOnSite = async (jobId, newStatus) => {
    try {
      await axios.patch(`${API}/jobs/${jobId}/vehicle-on-site?vehicle_on_site=${newStatus}`);
      // Update local state immediately for responsive UI
      setStatusBoard(prev => {
        const updated = { ...prev };
        Object.keys(updated.columns || {}).forEach(column => {
          updated.columns[column] = updated.columns[column].map(card => 
            card.job_id === jobId ? { ...card, vehicle_on_site: newStatus } : card
          );
        });
        return updated;
      });
      toast.success(newStatus ? "Vehicle marked as on site" : "Vehicle marked as not on site");
    } catch (err) {
      toast.error("Failed to update vehicle status");
      console.error(err);
    }
  };
  
  const createFollowUp = async () => {
    if (!followUpTarget) return;
    
    setCreatingFollowUp(true);
    try {
      // Build declined items from the card data
      const declinedItems = [];
      
      // Try to get declined items from the yestimate/approval data
      if (followUpTarget.declined_value > 0) {
        declinedItems.push({
          item_name: "Declined work",
          priority: "amber",
          quoted_value: followUpTarget.declined_value || 0,
          reason: followUpTarget.decline_reason || null
        });
      }
      
      // If no specific declined value, use any available outstanding amount
      if (declinedItems.length === 0 && followUpTarget.outstanding_value) {
        declinedItems.push({
          item_name: "Outstanding work",
          priority: "amber", 
          quoted_value: followUpTarget.outstanding_value || 0
        });
      }
      
      await axios.post(`${API}/follow-ups`, {
        job_id: followUpTarget.job_id,
        inspection_id: followUpTarget.inspection_id || null,
        yestimate_id: followUpTarget.yestimate_id || null,
        customer_name: followUpTarget.customer_name || "Customer",
        customer_phone: followUpTarget.customer_phone || "",
        vehicle_reg: followUpTarget.reg,
        vehicle_make: followUpTarget.make || null,
        vehicle_model: followUpTarget.model || null,
        declined_items: declinedItems,
        total_outstanding_value: followUpTarget.declined_value || followUpTarget.outstanding_value || 0,
        follow_up_days: followUpDays,
        notes: followUpTarget.decline_reason || null
      });
      
      toast.success("Follow-up scheduled", {
        description: `SMS will be sent in ${followUpDays} days`
      });
      
      setShowFollowUpModal(false);
      setFollowUpTarget(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to schedule follow-up", {
        description: err.response?.data?.detail || "Please try again"
      });
    } finally {
      setCreatingFollowUp(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-100 text-gray-700",
      sent: "bg-blue-100 text-blue-700",
      viewed: "bg-purple-100 text-purple-700",
      authorised: "bg-green-100 text-green-700"
    };
    return styles[status] || styles.draft;
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-6 py-4">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <img 
              src="/yestimate-logo.jpeg" 
              alt="Yestimate" 
              className="h-28 w-auto"
              style={{ maxHeight: '120px' }}
            />
            <p className="text-xs text-slate-500">Service Advisor</p>
          </div>
          <div className="flex gap-2">
            {(user?.role || "").toUpperCase() === "ADMIN" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app/admin/staff")}
                className="border-slate-300"
                data-testid="header-admin-staff-btn"
                title="Staff & permissions"
              >
                <ShieldCheck className="w-4 h-4 mr-1" />
                Staff
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/app/profile")}
              className="text-slate-600"
              data-testid="header-profile-link"
              title="Profile & password"
            >
              {user?.name || "Profile"}
            </Button>
            <Button 
              variant={followUpCounts.urgent > 0 ? "default" : "outline"} 
              size="sm" 
              onClick={() => navigate("/app/foh/follow-ups")}
              className={followUpCounts.urgent > 0 ? "bg-amber-500 hover:bg-amber-600" : ""}
              data-testid="follow-ups-btn"
            >
              <Bell className="w-4 h-4 mr-1" />
              Follow-Ups
              {followUpCounts.urgent > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs">{followUpCounts.urgent}</Badge>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/app/foh/analytics/declines")}
              data-testid="decline-analytics-btn"
              title="See why customers are skipping items"
            >
              <TrendingDown className="w-4 h-4 mr-1" />
              Declines
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setShowSettings(true); fetchGarageContact(); }} data-testid="settings-btn">
              <Settings className="w-4 h-4 mr-1" />
              Settings
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="foh-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Settings Modal */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSettings(false); }}
          data-testid="settings-modal-backdrop"
        >
          <Card className="w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white border-b z-10 shrink-0">
              <CardTitle>Garage Settings</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(false)}
                data-testid="settings-close-x"
                aria-label="Close settings"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6 overflow-y-auto flex-1">
              {/* Garage Logo Section */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Customer Portal Logo</Label>
                <p className="text-xs text-gray-500 mb-3">
                  Upload your garage logo to display at the top of customer inspection reports. 
                  Supported: PNG, JPG, SVG (max 2MB)
                </p>
                
                {garageLogo?.has_logo ? (
                  <div className="space-y-3">
                    <div className="border rounded-lg p-4 bg-gray-50 flex items-center justify-center">
                      <img 
                        src={garageLogo.logo_data} 
                        alt="Current Logo" 
                        className="max-h-16 object-contain"
                        data-testid="current-logo-preview"
                      />
                    </div>
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>{garageLogo.filename}</span>
                      <span>{(garageLogo.size_bytes / 1024).toFixed(1)} KB</span>
                    </div>
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="logo-upload-input"
                        />
                        <Button variant="outline" size="sm" className="w-full" asChild disabled={uploadingLogo}>
                          <span>
                            <Upload className="w-3 h-3 mr-1" />
                            {uploadingLogo ? "Uploading..." : "Replace Logo"}
                          </span>
                        </Button>
                      </label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleLogoDelete}
                        className="text-red-600 hover:text-red-700"
                        data-testid="delete-logo-btn"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-3">No logo uploaded</p>
                    <label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="logo-upload-input"
                      />
                      <Button variant="outline" size="sm" asChild disabled={uploadingLogo}>
                        <span>
                          <Upload className="w-3 h-3 mr-1" />
                          {uploadingLogo ? "Uploading..." : "Upload Logo"}
                        </span>
                      </Button>
                    </label>
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Garage Contact Settings */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Garage Contact Details</Label>
                <p className="text-xs text-gray-500 mb-3">
                  Phone number and name shown on the customer portal "Talk to us" button.
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Garage Name</Label>
                    <Input
                      placeholder="e.g. ABC Motor Services"
                      value={garageContact.name}
                      onChange={(e) => setGarageContact((c) => ({ ...c, name: e.target.value }))}
                      onBlur={(e) => saveGarageContactField("name", e.target.value)}
                      disabled={savingContactField === "name"}
                      data-testid="garage-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Phone Number (for "Talk to us" button)</Label>
                    <Input
                      id="garage-phone-input"
                      placeholder="e.g. +44 123 456 7890"
                      value={garageContact.phone}
                      onChange={(e) => setGarageContact((c) => ({ ...c, phone: e.target.value }))}
                      onBlur={(e) => saveGarageContactField("phone", e.target.value)}
                      disabled={savingContactField === "phone"}
                      data-testid="garage-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Email (optional)</Label>
                    <Input
                      id="garage-email-input"
                      placeholder="e.g. service@garage.com"
                      value={garageContact.email}
                      onChange={(e) => setGarageContact((c) => ({ ...c, email: e.target.value }))}
                      onBlur={(e) => saveGarageContactField("email", e.target.value)}
                      disabled={savingContactField === "email"}
                      data-testid="garage-email-input"
                    />
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Admin: Staff & Permissions — only ADMIN role */}
              {(user?.role || "").toUpperCase() === "ADMIN" && (
                <>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Staff &amp; Permissions</Label>
                    <p className="text-xs text-gray-500 mb-3">
                      Add staff logins, reset passwords, change roles, and review the admin activity log.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => { setShowSettings(false); navigate("/app/admin/staff"); }}
                      data-testid="settings-manage-staff-btn"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Manage Staff Logins
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full mt-2"
                      onClick={() => { setShowSettings(false); navigate("/app/profile"); }}
                      data-testid="settings-profile-btn"
                    >
                      <User className="w-4 h-4 mr-2" />
                      My Profile &amp; Password
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full mt-2"
                      onClick={() => { setShowSettings(false); navigate("/app/admin/integration-health"); }}
                      data-testid="settings-integration-health-btn"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Integration Health
                    </Button>
                  </div>
                  <Separator />
                </>
              )}
              
              {/* Common Issues Management Link */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Common Issues Library</Label>
                <p className="text-xs text-gray-500 mb-3">
                  Manage the predefined issues that technicians can select during inspections.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => { setShowSettings(false); navigate("/app/admin/common-issues"); }}
                  data-testid="manage-common-issues-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Common Issues
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => { setShowSettings(false); navigate("/app/admin/price-book"); }}
                  data-testid="manage-price-book-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Manage Price Book
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => { setShowSettings(false); navigate("/app/admin/pricing-packages"); }}
                  data-testid="manage-pricing-packages-btn"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Pre-Pricing Packages
                </Button>
              </div>
              
              <Separator />
              
              {/* Previously Advised Lookback Period */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Previously Advised Lookback Period</Label>
                <p className="text-xs text-gray-500 mb-3">
                  How far back to check for previously advised items when reviewing an inspection.
                </p>
                <div className="flex gap-2">
                  {[3, 6, 12].map((months) => (
                    <Button 
                      key={months}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/admin/settings/lookback-period?lookback_months=${months}`);
                          alert(`Lookback period set to ${months} months`);
                        } catch (err) {
                          alert("Failed to update setting");
                        }
                      }}
                      data-testid={`lookback-${months}-btn`}
                    >
                      {months} months
                    </Button>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              {/* Admin Override Mode - Testing Bypass */}
              <div>
                <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600" />
                  Admin Override Mode
                </Label>
                <p className="text-xs text-gray-500 mb-3">
                  When enabled, bypasses JCU validation checks when sending estimates to customer portal. 
                  <span className="text-amber-600 font-semibold"> Use for testing only.</span>
                </p>
                <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${adminOverrideMode.enabled ? 'bg-amber-500 animate-pulse' : 'bg-gray-300'}`} />
                    <span className="text-sm font-medium">
                      {adminOverrideMode.enabled ? 'Override ACTIVE' : 'Override Disabled'}
                    </span>
                  </div>
                  <Button
                    variant={adminOverrideMode.enabled ? "destructive" : "outline"}
                    size="sm"
                    onClick={() => toggleAdminOverrideMode(!adminOverrideMode.enabled)}
                    disabled={togglingOverride}
                    data-testid="admin-override-toggle"
                  >
                    {togglingOverride ? 'Updating...' : adminOverrideMode.enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>
                {adminOverrideMode.enabled && adminOverrideMode.enabled_by && (
                  <p className="text-xs text-amber-600 mt-2">
                    Enabled by {adminOverrideMode.enabled_by}
                  </p>
                )}
              </div>
              
              <Separator />
              
              <div className="text-xs text-gray-500">
                <p>The logo will appear as a trust badge at the top of all customer-facing inspection reports.</p>
              </div>
            </CardContent>
            {/* Sticky footer — always-visible exit */}
            <div className="border-t bg-white px-6 py-3 flex justify-end shrink-0">
              <Button
                onClick={() => setShowSettings(false)}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="settings-done-btn"
              >
                Done
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Follow-Up Scheduling Modal */}
      {showFollowUpModal && followUpTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Schedule Follow-Up</CardTitle>
              <p className="text-sm text-slate-600">
                {followUpTarget.reg} - {followUpTarget.customer_name || 'Customer'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-2 block">Outstanding Value</Label>
                <p className="text-2xl font-bold text-slate-900">
                  £{(followUpTarget.declined_value || followUpTarget.outstanding_value || 0).toFixed(0)}
                </p>
              </div>
              
              <div>
                <Label className="mb-2 block">Send SMS reminder in:</Label>
                <div className="flex gap-2 flex-wrap">
                  {[7, 14, 21, 30].map(days => (
                    <Button
                      key={days}
                      variant={followUpDays === days ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFollowUpDays(days)}
                      className={followUpDays === days ? "bg-blue-600" : ""}
                    >
                      {days} days
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  SMS will be sent on {new Date(Date.now() + followUpDays * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              
              <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-1">What happens:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>SMS sent to customer with "Book Now" link</li>
                  <li>If customer clicks, you get an alert to call them</li>
                  <li>If no response after 2 days, you get a reminder to chase</li>
                </ol>
              </div>
            </CardContent>
            <div className="flex gap-2 p-4 border-t">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowFollowUpModal(false);
                  setFollowUpTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={createFollowUp}
                disabled={creatingFollowUp}
              >
                {creatingFollowUp ? 'Scheduling...' : 'Schedule Follow-Up'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ======== FOH DASHBOARD ======== */}
      <div className="p-4">
        {/* Header with actions */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-slate-800">Service Advisor Dashboard</h1>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/app/foh/start-intake")}
              className="bg-teal-700 hover:bg-teal-800 text-white shadow-md shadow-teal-900/15"
              data-testid="start-intake-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Start Intake
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/app/foh/archive")} data-testid="archive-btn">
              <History className="w-4 h-4 mr-1" /> Archive
            </Button>
            <Button variant="outline" size="sm" onClick={() => { fetchData(); fetchAwaitingAuth(); refreshLocks(); }}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : !statusBoard ? (
          <div className="text-center text-slate-500 py-10">Failed to load dashboard</div>
        ) : (
          <div className="space-y-6">
            {/* Status Summary Bar */}
            <div className="grid grid-cols-6 gap-3">
              <div className={`bg-red-200 rounded-lg p-3 border-2 border-red-500 text-center ring-2 ring-red-400 ring-offset-1 ${(statusBoard.counts?.foh_action || 0) > 0 ? 'animate-pulse' : ''}`}>
                <p className="text-2xl font-bold text-red-800">{statusBoard.counts?.foh_action || 0}</p>
                <p className="text-xs text-red-700 font-semibold">FOH Action</p>
              </div>
              <div className="bg-orange-100 rounded-lg p-3 border-2 border-orange-400 text-center">
                <p className="text-2xl font-bold text-orange-700">{awaitingAuth.total || 0}</p>
                <p className="text-xs text-orange-600 font-medium">With Customer</p>
              </div>
              <div className={`bg-green-100 rounded-lg p-3 border-2 border-green-400 text-center ${(statusBoard.counts?.authorised || 0) > 0 ? 'animate-pulse' : ''}`}>
                <p className="text-2xl font-bold text-green-700">{statusBoard.counts?.authorised || 0}</p>
                <p className="text-xs text-green-600 font-medium">Authorised</p>
              </div>
              <div className="bg-yellow-100 rounded-lg p-3 border-2 border-yellow-400 text-center">
                <p className="text-2xl font-bold text-yellow-700">{statusBoard.counts?.awaiting_tech || 0}</p>
                <p className="text-xs text-yellow-600 font-medium">Awaiting Tech</p>
              </div>
              <div className="bg-blue-100 rounded-lg p-3 border-2 border-blue-300 text-center">
                <p className="text-2xl font-bold text-blue-700">{statusBoard.counts?.in_progress || 0}</p>
                <p className="text-xs text-blue-600 font-medium">In Progress</p>
              </div>
              <div className="bg-slate-200 rounded-lg p-3 border-2 border-slate-400 text-center">
                <p className="text-2xl font-bold text-slate-700">{statusBoard.counts?.declined_hold || 0}</p>
                <p className="text-xs text-slate-600 font-medium">Declined/Hold</p>
              </div>
            </div>

            {/* MOT day load — quiet stat tile showing today's MOT bay activity. */}
            <MOTDayLoadTile />

            {/* MOT Auto-Detection Stage 2 — sessions where the background
                DVSA poller has imported a result (price-now) OR reached a
                hard-stop and needs manual review. Self-fetches & polls. */}
            <MOTResultsRequiringActionTile />

            {/* Awaiting Customer Authorisation Queue - FULL FEATURED */}
            {awaitingAuth.tasks.length > 0 && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-slate-800" data-testid="awaiting-auth-title">
                      Awaiting Customer Authorisation ({awaitingAuth.total})
                    </h2>
                    {awaitingAuth.high_priority_count > 0 && (
                      <Badge className="bg-red-100 text-red-700">
                        {awaitingAuth.high_priority_count} need follow-up
                      </Badge>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchAwaitingAuth}>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Refresh
                  </Button>
                </div>
                {/* 2-column grid layout for better eye travel on wide screens */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {awaitingAuth.tasks.map(task => (
                    <Card 
                      key={task.id} 
                      className={`border-2 ${task.priority === 'high' ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'} cursor-pointer hover:shadow-md transition-shadow`}
                      onClick={() => task.inspection_id && navigate(`/app/foh/inspection/${task.inspection_id}`)}
                      data-testid={`awaiting-auth-task-${task.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex flex-col">
                          {/* Header Row - Vehicle Info */}
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Car className={`w-4 h-4 ${task.priority === 'high' ? 'text-red-600' : 'text-orange-600'}`} />
                            <span className={`font-bold ${task.priority === 'high' ? 'text-red-900' : 'text-orange-900'}`}>
                              {task.vehicle_reg}
                            </span>
                            <span className={`text-sm ${task.priority === 'high' ? 'text-red-700' : 'text-orange-700'}`}>
                              {formatVehicleNameFromObject(task)}
                            </span>
                            {task.job_sheet_number && (
                              <Badge variant="outline" className="text-xs font-mono bg-white/50">
                                {task.job_sheet_number}
                              </Badge>
                            )}
                            {task.priority === 'high' && (
                              <Badge className="bg-red-600 text-white text-xs">HIGH</Badge>
                            )}
                          </div>
                          
                          {/* Vehicle Details Row */}
                          {(task.year || task.colour || task.fuel_type) && (
                            <p className={`text-xs mb-1 ${task.priority === 'high' ? 'text-red-500' : 'text-orange-500'}`}>
                              {task.year && <span>{task.year}</span>}
                              {task.colour && <span> • {task.colour}</span>}
                              {task.fuel_type && <span> • {task.fuel_type}</span>}
                              {task.engine_capacity && <span> • {(task.engine_capacity / 1000).toFixed(1)}L</span>}
                            </p>
                          )}
                          
                          {/* Customer & Status Info */}
                          <div className={`text-xs ${task.priority === 'high' ? 'text-red-700' : 'text-orange-700'} space-y-0.5`}>
                            {task.customer_name && (
                              <p><span className="font-medium">Customer:</span> {task.customer_name}</p>
                            )}
                            <p>
                              <span className="font-medium">Sent:</span> {task.wait_display} ago • Opens: {task.open_count || 0}
                            </p>
                            {task.reason === 'no_response' && (
                              <p className="text-red-600 font-medium">⚠️ Not opened</p>
                            )}
                            {task.reason === 'opened_no_decision' && (
                              <p className="text-orange-600 font-medium">⚠️ Viewed, no response</p>
                            )}
                            {task.reason === 'sent_awaiting_response' && task.wait_minutes >= 30 && (
                              <p className="text-red-600 font-medium">⚠️ Follow up needed!</p>
                            )}
                          </div>
                          
                          {/* Action Buttons Row */}
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            {task.portal_token && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); window.open(`/approve/${task.portal_token}`, '_blank'); }}
                                className="text-xs h-7 px-2 text-purple-600 hover:text-purple-700 border-purple-200 bg-purple-50 hover:bg-purple-100"
                                data-testid={`view-portal-btn-${task.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View Portal
                              </Button>
                            )}
                            {task.yestimate_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); navigate(`/app/foh/yestimate/${task.yestimate_id}/pricing`); }}
                                className="text-xs h-7 px-2 text-blue-600 hover:text-blue-700 border-blue-200"
                                data-testid={`edit-pricing-btn-${task.id}`}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Pricing
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleResendLink(task.id); }}
                              disabled={resendingTask === task.id}
                              className={`text-xs h-7 px-2 ${
                                (task.reason === 'no_response' && task.wait_minutes >= 30) ||
                                (task.reason === 'sent_awaiting_response' && task.wait_minutes >= 30)
                                  ? 'bg-red-600 border-red-600 text-white hover:bg-red-700 animate-pulse' 
                                  : ''
                              }`}
                              data-testid={`resend-btn-${task.id}`}
                            >
                              {resendingTask === task.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <MessageSquare className="w-3 h-3 mr-1" />
                                  Resend
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleCallNow(task); }}
                              className={`text-xs h-7 px-2 ${
                                (task.reason === 'no_response' && task.wait_minutes >= 30) ||
                                (task.reason === 'sent_awaiting_response' && task.wait_minutes >= 30) ||
                                (task.reason === 'opened_no_decision' && task.minutes_since_viewed >= 30)
                                  ? 'bg-red-600 border-red-600 text-white hover:bg-red-700 animate-pulse' 
                                  : ''
                              }`}
                              data-testid={`call-now-btn-${task.id}`}
                            >
                              <Phone className="w-3 h-3 mr-1" />
                              Call
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleResolveTask(task.id); }}
                                className="text-xs text-green-600 hover:text-green-700"
                                data-testid={`resolve-btn-${task.id}`}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Resolved
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleDeleteAwaitingTask(task);
                                }}
                                className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-awaiting-btn-${task.id}`}
                                title="Delete inspection"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Three-Column Grid: FOH Action, Awaiting Tech, In Progress */}
            <div className="grid grid-cols-3 gap-4">
              {/* FOH Action Required - RED - ATTENTION GRABBING */}
              <div className="bg-red-200 rounded-lg p-4 border-2 border-red-500 ring-2 ring-red-400 ring-offset-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-red-900">FOH Action Required</h3>
                  <Badge className="bg-red-600 text-white">{statusBoard.counts?.foh_action || 0}</Badge>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {statusBoard.columns?.foh_action?.map(card => (
                    <Card 
                      key={card.job_id} 
                      className="border-2 border-red-400 bg-white cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => {
                        // MOT-only result imported → land FOH directly on
                        // the pricing workspace where the auto-imported
                        // defects are already drafted as job_groups.
                        if (card.is_mot_only && card.yestimate_id) {
                          navigate(`/app/foh/yestimate/${card.yestimate_id}/pricing`);
                        } else if (card.inspection_id) {
                          navigate(`/app/foh/inspection/${card.inspection_id}`);
                        } else if (card.job_id) {
                          // For diagnostic-only jobs without inspection, navigate to job view
                          navigate(`/app/foh/job/${card.job_id}`);
                        }
                      }}
                      data-testid={`foh-action-${card.job_id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-800">{card.reg}</p>
                              {card.job_sheet_number && (
                                <Badge variant="outline" className="text-xs font-mono bg-white/50">{card.job_sheet_number}</Badge>
                              )}
                              {/* Key Tag — inline editable across FOH dashboard, MOT tester
                                  workspace and FOH pricing screen so the physical key tag
                                  number can be added at any point in the lifecycle. */}
                              <div onClick={(e) => e.stopPropagation()}>
                                <KeyTagInline
                                  jobId={card.job_id}
                                  initial={card.key_tag_number}
                                  onSaved={() => fetchData?.()}
                                />
                              </div>
                              {activeLocks[card.job_id] && (
                                <LockIndicator 
                                  lockedByName={activeLocks[card.job_id].locked_by_name}
                                  isCurrentUser={activeLocks[card.job_id].is_current_user}
                                />
                              )}
                            </div>
                            {/* iter197gg — Hide "Unknown" model placeholder gracefully.
                                The MOT-only intake fallback stamps model="Unknown" when
                                DVLA returns make-only. Backend now backfills from DVSA on
                                first workflow poll, but for legacy jobs (and the brief
                                pre-poll window) we don't want the FOH card to read e.g.
                                "Toyota Unknown". */}
                            <p className="text-xs text-slate-600">
                              {card.make}
                              {card.model && card.model.toLowerCase() !== 'unknown'
                                ? ` ${card.model}`
                                : ''}
                            </p>
                            <p className="text-xs text-slate-500">{card.customer_name || 'No customer'}</p>
                            {/* Vehicle Here For pill */}
                            {card.vehicle_here_for && (() => {
                              const isServicePriority = card.vehicle_here_for === 'service' || card.vehicle_here_for === 'mot_service';
                              return (
                                <span 
                                  className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    isServicePriority ? 'animate-pulse bg-red-100 text-red-700 ring-1 ring-red-300' :
                                    card.vehicle_here_for === 'mot' ? 'bg-teal-100 text-teal-700' :
                                    card.vehicle_here_for === 'diagnostic' ? 'bg-purple-100 text-purple-700' :
                                    'bg-slate-100 text-slate-600'
                                  }`}
                                  data-testid={`here-for-pill-${card.job_id}`}
                                >
                                  {card.vehicle_here_for === 'service' ? 'SERVICE' :
                                   card.vehicle_here_for === 'mot_service' ? 'MOT + SERVICE' :
                                   card.vehicle_here_for === 'mot' ? 'MOT' :
                                   card.vehicle_here_for === 'diagnostic' ? 'DIAGNOSTIC' :
                                   'OTHER'}
                                </span>
                              );
                            })()}
                            {/* MOT result auto-import banner — appears
                                only on MOT-only jobs once the DVSA poll
                                has imported a result and promoted the
                                job to FOH Action Required. */}
                            {card.is_mot_only && card.mot_result_test_result && (
                              <div
                                className="mt-1.5 flex flex-wrap items-center gap-1.5"
                                data-testid={`mot-result-banner-${card.job_id}`}
                              >
                                <Badge
                                  className={`text-[10px] font-bold ${
                                    card.mot_result_test_result === 'PASSED'
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-red-600 text-white'
                                  }`}
                                  data-testid={`mot-result-pill-${card.job_id}`}
                                >
                                  MOT {card.mot_result_test_result}
                                </Badge>
                                {typeof card.mot_result_defect_count === 'number' && card.mot_result_defect_count > 0 && (
                                  <span
                                    className="text-[10px] font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded"
                                    data-testid={`mot-result-defects-${card.job_id}`}
                                  >
                                    {card.mot_result_defect_count} defect{card.mot_result_defect_count === 1 ? '' : 's'}
                                  </span>
                                )}
                                {typeof card.mot_result_pricing_lines === 'number' && card.mot_result_pricing_lines > 0 && (
                                  <span
                                    className="text-[10px] font-semibold text-amber-800 bg-amber-100 ring-1 ring-amber-300 px-1.5 py-0.5 rounded"
                                    data-testid={`mot-result-pricing-lines-${card.job_id}`}
                                  >
                                    {card.mot_result_pricing_lines} to price
                                  </span>
                                )}
                                <span className="text-[10px] text-teal-700 italic">
                                  Click to commence pricing →
                                </span>
                              </div>
                            )}
                            {/* Vehicle On Site Toggle */}
                            <div className="flex items-center gap-2 mt-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleVehicleOnSite(card.job_id, !card.vehicle_on_site);
                                }}
                                className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                                  card.vehicle_on_site 
                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                                title={card.vehicle_on_site ? 'Click to mark as not on site' : 'Click to mark as on site'}
                                data-testid={`vehicle-on-site-${card.job_id}`}
                              >
                                {card.vehicle_on_site ? '🚗 Here' : '⏳ Not here'}
                              </button>
                            </div>
                            {/* Inspection Status Indicators */}
                            <InspectionStatusIndicator card={card} />
                            {/* Timing Belt Status */}
                            {card.timing_belt?.is_belt && card.timing_belt?.is_due && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className={`text-xs px-2 py-0.5 rounded ${
                                  card.timing_belt.is_overdue 
                                    ? 'bg-red-100 text-red-700 font-semibold' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  ⏱ {card.timing_belt.status_label}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTimingBeltModal({ show: true, card, mileage: '' });
                                  }}
                                  className="h-5 text-xs px-1.5 py-0 text-green-600 hover:text-green-700 hover:bg-green-50 border border-green-200"
                                  title="Mark timing belt as replaced"
                                  data-testid={`mark-belt-replaced-${card.job_id}`}
                                >
                                  ✓ Replaced
                                </Button>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="flex gap-1 mb-1 flex-wrap">
                              {card.dangerous_count > 0 && <Badge className="bg-black text-white text-xs">{card.dangerous_count} DANGER</Badge>}
                              {card.red_count > 0 && <Badge className="bg-red-500 text-white text-xs">{card.red_count} RED</Badge>}
                              {card.amber_count > 0 && <Badge className="bg-amber-500 text-white text-xs">{card.amber_count} AMBER</Badge>}
                            </div>
                            {card.estimated_value > 0 && (
                              <p className="text-sm font-bold text-green-700">£{card.estimated_value?.toFixed(2)}</p>
                            )}
                            <div className="flex items-center gap-1 justify-end">
                              <p className="text-xs text-red-600 font-bold">{card.time_in_state}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleSendBackToTech(card, e)}
                                className="h-6 w-6 p-0 text-amber-500 hover:text-amber-700 hover:bg-amber-100"
                                title="Send back to technician"
                                data-testid={`send-back-${card.job_id}`}
                              >
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDeleteInspection(card, e)}
                                className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-100"
                                title="Delete job"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(!statusBoard.columns?.foh_action?.length) && (
                    <p className="text-sm text-red-700 text-center py-4">No pending actions</p>
                  )}
                </div>
              </div>

              {/* Awaiting Tech - YELLOW */}
              <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-300">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-yellow-800">Awaiting Technician</h3>
                  <Badge className="bg-yellow-500 text-white">{statusBoard.counts?.awaiting_tech || 0}</Badge>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {statusBoard.columns?.awaiting_tech?.map(card => (
                    <Card 
                      key={card.job_id} 
                      className="border border-yellow-300 bg-white"
                      data-testid={`awaiting-tech-${card.job_id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-800">{card.reg}</p>
                              {card.job_sheet_number && (
                                <Badge variant="outline" className="text-xs font-mono bg-white/50">{card.job_sheet_number}</Badge>
                              )}
                              {/* Key Tag — inline editable across FOH dashboard, MOT tester
                                  workspace and FOH pricing screen so the physical key tag
                                  number can be added at any point in the lifecycle. */}
                              <div onClick={(e) => e.stopPropagation()}>
                                <KeyTagInline
                                  jobId={card.job_id}
                                  initial={card.key_tag_number}
                                  onSaved={() => fetchData?.()}
                                />
                              </div>
                            </div>
                            <p className="text-xs text-slate-600">
                              {card.make}
                              {card.model && card.model.toLowerCase() !== 'unknown'
                                ? ` ${card.model}`
                                : ''}
                            </p>
                            <p className="text-xs text-slate-500">{card.customer_name || 'No customer'}</p>
                            {/* Vehicle Here For pill - In Workshop column */}
                            {card.vehicle_here_for && (() => {
                              const isServicePriority = card.vehicle_here_for === 'service' || card.vehicle_here_for === 'mot_service';
                              return (
                                <span 
                                  className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    isServicePriority ? 'animate-pulse bg-red-100 text-red-700 ring-1 ring-red-300' :
                                    card.vehicle_here_for === 'mot' ? 'bg-teal-100 text-teal-700' :
                                    card.vehicle_here_for === 'diagnostic' ? 'bg-purple-100 text-purple-700' :
                                    'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {card.vehicle_here_for === 'service' ? 'SERVICE' :
                                   card.vehicle_here_for === 'mot_service' ? 'MOT + SERVICE' :
                                   card.vehicle_here_for === 'mot' ? 'MOT' :
                                   card.vehicle_here_for === 'diagnostic' ? 'DIAGNOSTIC' :
                                   'OTHER'}
                                </span>
                              );
                            })()}
                            {/* Vehicle On Site Toggle */}
                            <div className="flex items-center gap-2 mt-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleVehicleOnSite(card.job_id, !card.vehicle_on_site);
                                }}
                                className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                                  card.vehicle_on_site 
                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                                title={card.vehicle_on_site ? 'Click to mark as not on site' : 'Click to mark as on site'}
                                data-testid={`vehicle-on-site-${card.job_id}`}
                              >
                                {card.vehicle_on_site ? '🚗 Here' : '⏳ Not here'}
                              </button>
                            </div>
                            {/* Inspection Status Indicators - VI / Diag status */}
                            <InspectionStatusIndicator card={card} showDefault={true} />
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-yellow-700 font-medium mb-1">Waiting: {card.time_in_state}</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setDeletePendingConfirm({ show: true, job: card, step: 1 });
                              }}
                              className="h-6 w-6 p-0 text-yellow-600 hover:text-red-600 hover:bg-red-100"
                              title="Delete job"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(!statusBoard.columns?.awaiting_tech?.length) && (
                    <p className="text-sm text-yellow-700 text-center py-4">No vehicles waiting</p>
                  )}
                </div>
              </div>

              {/* In Progress - BLUE */}
              <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-blue-900">In Progress</h3>
                  <Badge className="bg-blue-500 text-white">{statusBoard.counts?.in_progress || 0}</Badge>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {statusBoard.columns?.in_progress?.map(card => (
                    <Card 
                      key={card.job_id} 
                      className={`border border-blue-200 ${card.inspection_id ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                      onClick={() => card.inspection_id && navigate(`/app/foh/inspection/${card.inspection_id}`)}
                      data-testid={`in-progress-${card.job_id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-800">{card.reg}</p>
                              {card.job_sheet_number && (
                                <Badge variant="outline" className="text-xs font-mono bg-white/50">{card.job_sheet_number}</Badge>
                              )}
                              {/* Key Tag — inline editable across FOH dashboard, MOT tester
                                  workspace and FOH pricing screen so the physical key tag
                                  number can be added at any point in the lifecycle. */}
                              <div onClick={(e) => e.stopPropagation()}>
                                <KeyTagInline
                                  jobId={card.job_id}
                                  initial={card.key_tag_number}
                                  onSaved={() => fetchData?.()}
                                />
                              </div>
                            </div>
                            <p className="text-xs text-slate-600">{formatVehicleNameFromObject(card)}</p>
                            <p className="text-xs text-blue-600 font-medium">Tech: {card.technician_name}</p>
                            {/* Vehicle Here For pill - In Workshop column */}
                            {card.vehicle_here_for && (() => {
                              const isServicePriority = card.vehicle_here_for === 'service' || card.vehicle_here_for === 'mot_service';
                              return (
                                <span 
                                  className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    isServicePriority ? 'animate-pulse bg-red-100 text-red-700 ring-1 ring-red-300' :
                                    card.vehicle_here_for === 'mot' ? 'bg-teal-100 text-teal-700' :
                                    card.vehicle_here_for === 'diagnostic' ? 'bg-purple-100 text-purple-700' :
                                    'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {card.vehicle_here_for === 'service' ? 'SERVICE' :
                                   card.vehicle_here_for === 'mot_service' ? 'MOT + SERVICE' :
                                   card.vehicle_here_for === 'mot' ? 'MOT' :
                                   card.vehicle_here_for === 'diagnostic' ? 'DIAGNOSTIC' :
                                   'OTHER'}
                                </span>
                              );
                            })()}
                            {/* Vehicle On Site Toggle */}
                            <div className="flex items-center gap-2 mt-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleVehicleOnSite(card.job_id, !card.vehicle_on_site);
                                }}
                                className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                                  card.vehicle_on_site 
                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                                title={card.vehicle_on_site ? 'Click to mark as not on site' : 'Click to mark as on site'}
                                data-testid={`vehicle-on-site-${card.job_id}`}
                              >
                                {card.vehicle_on_site ? '🚗 Here' : '⏳ Not here'}
                              </button>
                            </div>
                            {/* Inspection Status Indicators */}
                            <InspectionStatusIndicator card={card} />
                            {/* Timing Belt Status */}
                            {card.timing_belt?.is_belt && card.timing_belt?.is_due && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className={`text-xs px-2 py-0.5 rounded ${
                                  card.timing_belt.is_overdue 
                                    ? 'bg-red-100 text-red-700 font-semibold' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  ⏱ {card.timing_belt.status_label}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTimingBeltModal({ show: true, card, mileage: '' });
                                  }}
                                  className="h-5 text-xs px-1.5 py-0 text-green-600 hover:text-green-700 hover:bg-green-50 border border-green-200"
                                  title="Mark timing belt as replaced"
                                  data-testid={`mark-belt-replaced-${card.job_id}`}
                                >
                                  ✓ Replaced
                                </Button>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 justify-end">
                              <p className="text-xs text-blue-600">{card.time_in_state}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDeleteInspection(card, e)}
                                className="h-6 w-6 p-0 text-blue-400 hover:text-red-600 hover:bg-red-100"
                                title="Delete job"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(!statusBoard.columns?.in_progress?.length) && (
                    <p className="text-sm text-blue-700 text-center py-4">No active inspections</p>
                  )}
                </div>
              </div>
            </div>

            {/* Two-Column Grid: Authorised & Declined */}
            <div className="grid grid-cols-2 gap-4">
              {/* Authorised - GREEN */}
              <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-green-900">Authorised - Ready for Work</h3>
                  <Badge className="bg-green-600 text-white">{statusBoard.counts?.authorised || 0}</Badge>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {statusBoard.columns?.authorised?.map(card => (
                    <Card 
                      key={card.job_id} 
                      className="border border-green-300 hover:shadow-md transition-shadow"
                      data-testid={`authorised-${card.job_id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => card.inspection_id && navigate(`/app/foh/inspection/${card.inspection_id}`)}
                          >
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-800">{card.reg}</p>
                              {card.job_sheet_number && (
                                <Badge variant="outline" className="text-xs font-mono bg-white/50">{card.job_sheet_number}</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-600">{card.customer_name || 'No customer'}</p>
                            {/* Inspection Status Indicators */}
                            <InspectionStatusIndicator card={card} />
                          </div>
                          <div className="text-right flex flex-col items-end gap-1">
                            <p className="text-sm font-bold text-green-700">£{card.authorised_value?.toFixed(2) || '0.00'}</p>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 px-2 border-blue-500 text-blue-700 hover:bg-blue-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadJobsheet(card.job_id);
                                }}
                                disabled={loadingJobsheet}
                                data-testid={`print-jobsheet-${card.job_id}`}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Work Order
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 px-2 border-green-500 text-green-700 hover:bg-green-100"
                                onClick={(e) => handleArchiveJob(card.job_id, card.reg, e)}
                                disabled={archivingJob === card.job_id}
                                data-testid={`archive-job-${card.job_id}`}
                              >
                                {archivingJob === card.job_id ? (
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-3 h-3 mr-1" />
                                    Archive
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(!statusBoard.columns?.authorised?.length) && (
                    <p className="text-sm text-green-700 text-center py-4">None authorised</p>
                  )}
                </div>
              </div>

              {/* Declined/On Hold - GREY */}
              <div className="bg-slate-100 rounded-lg p-4 border-2 border-slate-300">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-700">Declined / On Hold</h3>
                  <Badge className="bg-slate-500 text-white">{statusBoard.counts?.declined_hold || 0}</Badge>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {statusBoard.columns?.declined_hold?.map(card => (
                    <Card 
                      key={card.job_id} 
                      className="border border-slate-300 hover:shadow-md transition-shadow"
                      data-testid={`declined-${card.job_id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => card.inspection_id && navigate(`/app/foh/inspection/${card.inspection_id}`)}
                          >
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-800">{card.reg}</p>
                              {card.job_sheet_number && (
                                <Badge variant="outline" className="text-xs font-mono bg-white/50">{card.job_sheet_number}</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-600">{card.customer_name || 'No customer'}</p>
                            {/* Inspection Status Indicators */}
                            <InspectionStatusIndicator card={card} />
                            {card.decline_reason && (
                              <p className="text-xs text-slate-500 mt-1">Reason: {card.decline_reason}</p>
                            )}
                            {/* Per-item decline reasons from customer portal */}
                            {card.declined_items?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {card.is_partial_decline && (
                                  <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-[10px] mb-1">Partial — some items approved</Badge>
                                )}
                                {card.declined_items.map((item, idx) => (
                                  <div key={idx} className="flex items-start gap-1.5 text-xs bg-white/60 rounded px-2 py-1 border border-slate-200">
                                    <span className="text-slate-700 font-medium truncate flex-1">{item.item_label || item.item_id}</span>
                                    <span className="text-slate-400 whitespace-nowrap italic">{
                                      item.decline_reason === 'cost' ? 'Cost/Budget' :
                                      item.decline_reason === 'credibility' ? 'Not convinced' :
                                      item.decline_reason === 'timing' ? 'Not now' :
                                      item.decline_reason === 'third_party' ? '2nd opinion' :
                                      item.decline_reason === 'usage' ? 'Low usage' :
                                      item.decline_reason === 'history' ? 'Done recently' :
                                      item.decline_reason === 'warranty' ? 'Warranty' :
                                      item.decline_reason === 'emotional' ? 'Overwhelmed' :
                                      item.decline_reason || 'No reason'
                                    }</span>
                                    {item.price_inc_vat > 0 && (
                                      <span className="text-slate-500 font-mono whitespace-nowrap">£{item.price_inc_vat?.toFixed(0)}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end gap-1">
                            {card.is_partial && <Badge className="bg-amber-500 text-white text-xs">PARTIAL</Badge>}
                            {card.declined_value > 0 && (
                              <p className="text-sm font-bold text-slate-700">£{card.declined_value?.toFixed(0)}</p>
                            )}
                            <p className="text-xs text-slate-500">{card.time_in_state}</p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 px-2 mt-1 border-blue-500 text-blue-700 hover:bg-blue-50"
                              onClick={(e) => handleScheduleFollowUp(card, e)}
                              data-testid={`follow-up-${card.job_id}`}
                            >
                              <Clock className="w-3 h-3 mr-1" />
                              Follow Up
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(!statusBoard.columns?.declined_hold?.length) && (
                    <p className="text-sm text-slate-500 text-center py-4">None declined</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        {/* Delete Awaiting Task Confirmation Modal */}
        {deleteAwaitingConfirm.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  {deleteAwaitingConfirm.step === 1 ? "Delete This Inspection?" : "Final Confirmation Required"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deleteAwaitingConfirm.step === 1 ? (
                  <div>
                    <p className="text-slate-600 mb-4">
                      You are about to delete the inspection for:
                    </p>
                    <div className="bg-slate-100 p-3 rounded-lg mb-4">
                      <p className="font-semibold">{deleteAwaitingConfirm.task?.vehicle_reg}</p>
                      <p className="text-sm text-slate-500">{formatVehicleNameFromObject(deleteAwaitingConfirm.task)}</p>
                      {deleteAwaitingConfirm.task?.customer_name && (
                        <p className="text-sm text-slate-500">{deleteAwaitingConfirm.task?.customer_name}</p>
                      )}
                      {deleteAwaitingConfirm.task?.job_sheet_number && (
                        <Badge variant="outline" className="mt-2 font-mono">{deleteAwaitingConfirm.task?.job_sheet_number}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      This will remove the inspection from both FOH and Technician views.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-red-600 font-semibold mb-4">
                      ⚠️ This action cannot be undone!
                    </p>
                    <p className="text-slate-600 mb-4">
                      All data for <strong>{deleteAwaitingConfirm.task?.vehicle_reg}</strong> will be permanently deleted:
                    </p>
                    <ul className="list-disc list-inside text-sm text-slate-500 space-y-1 mb-4">
                      <li>Inspection findings, photos, and technician notes</li>
                      <li>Pricing estimate and job groups</li>
                      <li>Customer approval portal and tokens</li>
                      <li>Job record and delivery history</li>
                      <li><strong>Removed from technician's job list</strong></li>
                    </ul>
                  </div>
                )}
              </CardContent>
              <div className="flex gap-3 p-6 pt-0">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setDeleteAwaitingConfirm({ show: false, task: null, step: 1 })}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={confirmDeleteAwaitingTask}
                  data-testid="confirm-delete-awaiting-btn"
                >
                  {deleteAwaitingConfirm.step === 1 ? "Yes, Delete" : "Delete Permanently"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Delete Pending Job (Awaiting Technician) Confirmation Modal */}
        {deletePendingConfirm.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  {deletePendingConfirm.step === 1 ? "Delete This Job?" : "Final Confirmation Required"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deletePendingConfirm.step === 1 ? (
                  <div>
                    <p className="text-slate-600 mb-4">
                      You are about to delete the job awaiting technician pickup for:
                    </p>
                    <div className="bg-slate-100 p-3 rounded-lg mb-4">
                      <p className="font-semibold">{deletePendingConfirm.job?.reg}</p>
                      <p className="text-sm text-slate-500">{formatVehicleNameFromObject(deletePendingConfirm.job)}</p>
                    </div>
                    <p className="text-sm text-slate-500">
                      This will remove the job from the technician's pending queue.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-red-600 font-semibold mb-4">
                      ⚠️ This action cannot be undone!
                    </p>
                    <p className="text-slate-600 mb-4">
                      The job for <strong>{deletePendingConfirm.job?.reg}</strong> will be permanently deleted:
                    </p>
                    <ul className="list-disc list-inside text-sm text-slate-500 space-y-1 mb-4">
                      <li>Job record and vehicle details</li>
                      <li>Any associated inspection data (if started)</li>
                      <li><strong>Removed from technician's pending list</strong></li>
                    </ul>
                  </div>
                )}
              </CardContent>
              <div className="flex gap-3 p-6 pt-0">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setDeletePendingConfirm({ show: false, job: null, step: 1 })}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={confirmDeletePendingJob}
                  data-testid="confirm-delete-pending-btn"
                >
                  {deletePendingConfirm.step === 1 ? "Yes, Delete" : "Delete Permanently"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Timing Belt Replacement Modal */}
        {timingBeltModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <Check className="w-5 h-5" />
                  Record Timing Belt Replacement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <p className="font-semibold">{timingBeltModal.card?.reg}</p>
                    <p className="text-sm text-slate-500">{formatVehicleNameFromObject(timingBeltModal.card)}</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700 block mb-2">
                      Current Mileage <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      value={timingBeltModal.mileage}
                      onChange={(e) => setTimingBeltModal(prev => ({ ...prev, mileage: e.target.value }))}
                      placeholder="Enter current mileage"
                      className="w-full"
                      data-testid="timing-belt-mileage-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Enter the mileage when the timing belt was replaced
                    </p>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700">
                      <strong>Note:</strong> Recording this replacement will reset the timing belt interval tracker for this vehicle.
                    </p>
                  </div>
                </div>
              </CardContent>
              <div className="flex gap-3 p-6 pt-0">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setTimingBeltModal({ show: false, card: null, mileage: '' })}
                  disabled={recordingBeltReplacement}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleMarkTimingBeltReplaced}
                  disabled={!timingBeltModal.mileage || recordingBeltReplacement}
                  data-testid="confirm-belt-replaced-btn"
                >
                  {recordingBeltReplacement ? "Recording..." : "Confirm Replacement"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Send Back to Tech Modal */}
        {sendBackModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-600">
                  <RotateCcw className="w-5 h-5" />
                  Send Back to Technician
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <p className="font-semibold">{sendBackModal.card?.reg}</p>
                    <p className="text-sm text-slate-500">{formatVehicleNameFromObject(sendBackModal.card)}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-slate-700 block">
                      Select additional work required:
                    </Label>
                    
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={sendBackOptions.addDiagnostic}
                          onChange={(e) => setSendBackOptions(prev => ({ ...prev, addDiagnostic: e.target.checked }))}
                          className="w-4 h-4 text-amber-600"
                        />
                        <div>
                          <p className="font-medium text-slate-800">Diagnostic VI</p>
                          <p className="text-xs text-slate-500">Request diagnostic investigation</p>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={sendBackOptions.addVI}
                          onChange={(e) => setSendBackOptions(prev => ({ ...prev, addVI: e.target.checked }))}
                          className="w-4 h-4 text-amber-600"
                        />
                        <div>
                          <p className="font-medium text-slate-800">Vehicle Inspection</p>
                          <p className="text-xs text-slate-500">Request full vehicle inspection</p>
                        </div>
                      </label>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-slate-700 block mb-2">
                        Notes for technician (optional)
                      </Label>
                      <textarea
                        value={sendBackOptions.notes}
                        onChange={(e) => setSendBackOptions(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="E.g., Customer wants additional check on..."
                        className="w-full p-2 border rounded-lg text-sm min-h-[80px]"
                        data-testid="send-back-notes"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-700">
                      <strong>Note:</strong> The job will be moved back to the technician queue for the selected work.
                    </p>
                  </div>
                </div>
              </CardContent>
              <div className="flex gap-3 p-6 pt-0">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setSendBackModal({ show: false, card: null })}
                  disabled={sendingBack}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={confirmSendBackToTech}
                  disabled={(!sendBackOptions.addDiagnostic && !sendBackOptions.addVI) || sendingBack}
                  data-testid="confirm-send-back-btn"
                >
                  {sendingBack ? "Sending..." : "Send to Tech"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Delete Inspection Confirmation Modal */}
        {deleteConfirmation.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  {deleteConfirmation.step === 1 ? "Delete This Inspection?" : "Final Confirmation Required"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deleteConfirmation.step === 1 ? (
                  <div>
                    <p className="text-slate-600 mb-4">
                      You are about to delete the inspection for:
                    </p>
                    <div className="bg-slate-100 p-3 rounded-lg mb-4">
                      <p className="font-semibold">{deleteConfirmation.item?.reg}</p>
                      <p className="text-sm text-slate-500">{formatVehicleNameFromObject(deleteConfirmation.item)}</p>
                      {deleteConfirmation.item?.customer_name && (
                        <p className="text-sm text-slate-500">Customer: {deleteConfirmation.item?.customer_name}</p>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      This will remove the inspection and all related data.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-red-600 font-semibold mb-4">
                      ⚠️ This action cannot be undone!
                    </p>
                    <p className="text-slate-600 mb-4">
                      The inspection for <strong>{deleteConfirmation.item?.reg}</strong> will be permanently deleted:
                    </p>
                    <ul className="list-disc list-inside text-sm text-slate-500 space-y-1 mb-4">
                      <li>Job and inspection records</li>
                      <li>All inspection items and photos</li>
                      <li>Any yestimate/pricing data</li>
                      <li>Customer approval records</li>
                    </ul>
                  </div>
                )}
              </CardContent>
              <div className="flex gap-3 p-6 pt-0">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={cancelDelete}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={confirmDelete}
                  data-testid="confirm-delete-inspection-btn"
                >
                  {deleteConfirmation.step === 1 ? "Yes, Delete" : "Delete Permanently"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Authorised Jobsheet Modal */}
        {showJobsheet && jobsheetData && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-testid="jobsheet-modal">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-auto print:max-h-none print:shadow-none print:rounded-none">
              {/* Print Header - Only visible when printing */}
              <div className="hidden print:block p-6 border-b-2 border-slate-300">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-800">AUTHORISED WORK ORDER</h1>
                    <p className="text-slate-600">Customer Approved Jobsheet</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-lg font-bold">{jobsheetData.job_sheet_number || 'N/A'}</p>
                    <p className="text-sm text-slate-600">
                      Approved: {jobsheetData.approved_at ? new Date(jobsheetData.approved_at).toLocaleDateString('en-GB') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Modal Header - Hidden when printing */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 flex justify-between items-center print:hidden">
                <div>
                  <h2 className="font-bold text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Authorised Work Order
                  </h2>
                  <p className="text-green-200 text-sm">{jobsheetData.job_sheet_number || 'No JSN'}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.print()}
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowJobsheet(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Jobsheet Content */}
              <div className="p-6">
                {/* Vehicle & Customer Info */}
                <div className="grid grid-cols-2 gap-6 mb-6 pb-6 border-b">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 mb-2">VEHICLE DETAILS</h3>
                    <p className="text-2xl font-bold text-slate-800">{jobsheetData.reg}</p>
                    <p className="text-slate-600">{formatVehicleNameFromObject(jobsheetData)} ({jobsheetData.year})</p>
                    <p className="text-slate-500 text-sm">{jobsheetData.mileage?.toLocaleString()} miles</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 mb-2">CUSTOMER</h3>
                    <p className="text-lg font-semibold text-slate-800">{jobsheetData.customer_name || 'N/A'}</p>
                    {jobsheetData.customer_phone && (
                      <p className="text-slate-600">{jobsheetData.customer_phone}</p>
                    )}
                    <p className="text-green-600 font-medium text-sm mt-1">
                      ✓ Approved {jobsheetData.approved_at ? new Date(jobsheetData.approved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>
                
                {/* AUTHORISED ITEMS - Grouped by Job */}
                <h3 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  AUTHORISED WORK
                </h3>

                {/* iter211ab — GarageHive tick tracker banner + toolbar.
                    Sits above the work order so FOH sees progress at a
                    glance while re-keying into GH. The `no-print` class
                    keeps this off the paper copy (the tick boxes on
                    every row survive the print). */}
                {ghOutstandingCount.total > 0 && (
                  <div
                    className="no-print rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 mb-3 flex flex-wrap items-center gap-2 text-sm"
                    data-testid="gh-tick-banner"
                  >
                    <span className="font-semibold text-blue-900">
                      GarageHive transfer:{" "}
                      <span data-testid="gh-tick-progress">
                        {ghOutstandingCount.done} of {ghOutstandingCount.total}
                      </span>{" "}
                      lines added
                    </span>
                    {ghOutstandingCount.remaining > 0 ? (
                      <span className="text-blue-700 text-xs">
                        · {ghOutstandingCount.remaining} still to add
                      </span>
                    ) : (
                      <span className="text-emerald-700 text-xs font-semibold">
                        · All lines transferred
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-1.5">
                      <button
                        onClick={markAllGhTicks}
                        disabled={ghOutstandingCount.remaining === 0}
                        className="text-xs font-semibold px-2.5 py-1 rounded-md border border-blue-300 bg-white hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="gh-tick-mark-all"
                        title="Mark every line as added to GH"
                      >
                        Mark all added
                      </button>
                      <button
                        onClick={clearAllGhTicks}
                        disabled={ghOutstandingCount.done === 0}
                        className="text-xs font-semibold px-2.5 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="gh-tick-reset"
                        title="Reset — clear every tick for this job"
                      >
                        Reset
                      </button>
                    </div>
                    <p className="w-full text-[11px] text-blue-700 mt-1">
                      Tip: click the box next to any labour or parts line
                      to tick it off as added to GarageHive. Use the copy
                      icon to grab a paste-ready line.
                    </p>
                  </div>
                )}

                {jobsheetData.authorised_items?.length > 0 ? (
                  <div className="space-y-4 mb-6">
                    {jobsheetData.authorised_items.map((item, itemIdx) => {
                      const _jgId = item.job_group_id || item.id || `idx${itemIdx}`;
                      const _lines = [
                        ...(item.labour_lines || []).map((l, i) => ghKeyFor(_jgId, "labour", l.id, i)),
                        ...(item.parts_lines  || []).map((p, i) => ghKeyFor(_jgId, "parts",  p.id, i)),
                        ...(item.charges      || []).map((c, i) => ghKeyFor(_jgId, "charge", c.id, i)),
                      ];
                      const _totalLines = _lines.length;
                      const _doneLines  = _lines.filter((k) => ghTicks[k]).length;
                      const _allDone    = _totalLines > 0 && _doneLines === _totalLines;
                      return (
                      <div key={item.id || itemIdx} className="border rounded-lg overflow-hidden" data-testid={`jobsheet-item-${itemIdx}`}>
                        {/* Job Header */}
                        <div className="bg-slate-100 px-4 py-2 flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="bg-slate-700 text-white text-xs font-bold px-2 py-1 rounded">{itemIdx + 1}</span>
                            <span className="font-semibold text-slate-800">{item.title}</span>
                            {item.category && <span className="text-slate-500 text-sm">({item.category})</span>}
                            {/* iter211ab — JG-level "all added" auto-tick
                                (derived, not stored) — turns green once
                                every line inside this item is ticked. */}
                            {_totalLines > 0 && (
                              <span
                                className={`no-print ml-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                                  _allDone
                                    ? "bg-emerald-100 text-emerald-800"
                                    : "bg-slate-200 text-slate-600"
                                }`}
                                data-testid={`jobsheet-item-${itemIdx}-gh-rollup`}
                              >
                                {_allDone ? "✓ All added to GH" : `${_doneLines}/${_totalLines} added`}
                              </span>
                            )}
                          </div>
                          <span className="font-bold text-slate-700">£{(item.total || 0).toFixed(2)}</span>
                        </div>

                        {/* Labour Lines for this job */}
                        {item.labour_lines?.length > 0 && (
                          <table className="w-full text-sm">
                            <thead className="bg-amber-50 text-slate-600">
                              <tr>
                                <th className="w-8 py-1.5 px-2 font-medium text-xs">✓</th>
                                <th className="text-left py-1.5 px-3 font-medium text-xs">LABOUR</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Hours</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Rate</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Ex VAT</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">VAT</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Total</th>
                                <th className="w-10 py-1.5 px-2 font-medium text-xs no-print"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.labour_lines.map((line, lineIdx) => {
                                const _k = ghKeyFor(_jgId, "labour", line.id, lineIdx);
                                const _isTicked = !!ghTicks[_k];
                                return (
                                <tr
                                  key={line.id || lineIdx}
                                  className={`border-b border-amber-100 ${_isTicked ? "bg-emerald-50/60" : ""}`}
                                  data-testid={`gh-line-labour-${itemIdx}-${lineIdx}`}
                                >
                                  <td className="w-8 py-1.5 px-2 text-center">
                                    <label className="inline-flex items-center justify-center cursor-pointer">
                                      {/* Print-visible tick box + digital
                                          checkbox. Screens see the input;
                                          paper sees the small square with
                                          a checkmark when set. */}
                                      <input
                                        type="checkbox"
                                        checked={_isTicked}
                                        onChange={() => toggleGhTick(_k)}
                                        className="no-print w-4 h-4 accent-emerald-600"
                                        aria-label="Added to GarageHive"
                                        data-testid={`gh-tick-${_k}`}
                                      />
                                      <span className="hidden print:inline-block w-4 h-4 border border-slate-500 rounded-sm relative">
                                        {_isTicked && (
                                          <span className="absolute inset-0 flex items-center justify-center text-slate-900 text-xs leading-none">✓</span>
                                        )}
                                      </span>
                                    </label>
                                  </td>
                                  <td className={`py-1.5 px-3 ${_isTicked ? "line-through text-slate-500" : ""}`}>{line.description}</td>
                                  <td className="py-1.5 px-3 text-right font-mono text-slate-600">{line.hours ? `${line.hours}h` : '-'}</td>
                                  <td className="py-1.5 px-3 text-right font-mono text-slate-600">{line.rate ? `£${line.rate.toFixed(2)}` : '-'}</td>
                                  <td className="py-1.5 px-3 text-right font-mono">£{(line.subtotal || 0).toFixed(2)}</td>
                                  <td className="py-1.5 px-3 text-right font-mono text-slate-500">£{(line.vat || 0).toFixed(2)}</td>
                                  <td className="py-1.5 px-3 text-right font-mono font-semibold">£{(line.total || 0).toFixed(2)}</td>
                                  <td className="w-10 py-1.5 px-2 text-right no-print">
                                    <button
                                      type="button"
                                      onClick={() => copyGhLine(line, "labour", _k)}
                                      className="text-slate-400 hover:text-blue-600"
                                      aria-label="Copy line for GarageHive"
                                      title="Copy line to clipboard"
                                      data-testid={`gh-copy-${_k}`}
                                    >
                                      {copiedGhLine === _k ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}

                        {/* Parts Lines for this job */}
                        {item.parts_lines?.length > 0 && (
                          <table className="w-full text-sm">
                            <thead className="bg-blue-50 text-slate-600">
                              <tr>
                                <th className="w-8 py-1.5 px-2 font-medium text-xs">✓</th>
                                <th className="text-left py-1.5 px-3 font-medium text-xs">PARTS</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Qty</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Unit</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Ex VAT</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">VAT</th>
                                <th className="text-right py-1.5 px-3 font-medium text-xs">Total</th>
                                <th className="w-10 py-1.5 px-2 font-medium text-xs no-print"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.parts_lines.map((part, partIdx) => {
                                const _k = ghKeyFor(_jgId, "parts", part.id, partIdx);
                                const _isTicked = !!ghTicks[_k];
                                return (
                                <tr
                                  key={part.id || partIdx}
                                  className={`border-b border-blue-100 ${_isTicked ? "bg-emerald-50/60" : ""}`}
                                  data-testid={`gh-line-parts-${itemIdx}-${partIdx}`}
                                >
                                  <td className="w-8 py-1.5 px-2 text-center">
                                    <label className="inline-flex items-center justify-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={_isTicked}
                                        onChange={() => toggleGhTick(_k)}
                                        className="no-print w-4 h-4 accent-emerald-600"
                                        aria-label="Added to GarageHive"
                                        data-testid={`gh-tick-${_k}`}
                                      />
                                      <span className="hidden print:inline-block w-4 h-4 border border-slate-500 rounded-sm relative">
                                        {_isTicked && (
                                          <span className="absolute inset-0 flex items-center justify-center text-slate-900 text-xs leading-none">✓</span>
                                        )}
                                      </span>
                                    </label>
                                  </td>
                                  <td className={`py-1.5 px-3 ${_isTicked ? "line-through text-slate-500" : ""}`}>{part.description}</td>
                                  <td className="py-1.5 px-3 text-right font-mono text-slate-600">{part.qty || 1}</td>
                                  <td className="py-1.5 px-3 text-right font-mono text-slate-600">£{(part.unit_price || 0).toFixed(2)}</td>
                                  <td className="py-1.5 px-3 text-right font-mono">£{(part.subtotal || 0).toFixed(2)}</td>
                                  <td className="py-1.5 px-3 text-right font-mono text-slate-500">£{(part.vat || 0).toFixed(2)}</td>
                                  <td className="py-1.5 px-3 text-right font-mono font-semibold">£{(part.total || 0).toFixed(2)}</td>
                                  <td className="w-10 py-1.5 px-2 text-right no-print">
                                    <button
                                      type="button"
                                      onClick={() => copyGhLine(part, "parts", _k)}
                                      className="text-slate-400 hover:text-blue-600"
                                      aria-label="Copy line for GarageHive"
                                      title="Copy line to clipboard"
                                      data-testid={`gh-copy-${_k}`}
                                    >
                                      {copiedGhLine === _k ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}

                        {/* Job Subtotal */}
                        <div className="bg-slate-50 px-4 py-2 flex justify-between items-center text-sm border-t">
                          <span className="text-slate-600">Subtotal for {item.title}:</span>
                          <div className="flex gap-4">
                            <span className="text-slate-500">Ex VAT: £{(item.subtotal || 0).toFixed(2)}</span>
                            <span className="text-slate-500">VAT: £{(item.vat || 0).toFixed(2)}</span>
                            <span className="font-bold">£{(item.total || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm mb-6">No authorised items</p>
                )}

                {/* iter197g — Already authorised baseline (MOT booking / Service etc).
                    Shown as a discrete section above the grand total so the
                    work order matches what the customer saw on the portal. */}
                {jobsheetData.already_authorised && (jobsheetData.already_authorised.total > 0) && (
                  <div className="border rounded-lg overflow-hidden mb-3" data-testid="jobsheet-already-authorised-section">
                    <div className="bg-emerald-50 px-4 py-2 flex justify-between items-center border-l-4 border-emerald-500">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span className="font-semibold text-slate-800">{jobsheetData.already_authorised.label} (already booked)</span>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-slate-500">Ex VAT: £{jobsheetData.already_authorised.subtotal.toFixed(2)}</span>
                        <span className="text-slate-500">VAT: £{jobsheetData.already_authorised.vat.toFixed(2)}</span>
                        <span className="font-bold text-slate-800">£{jobsheetData.already_authorised.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* iter197g — Workshop charges (consumables + environmental).
                    Only present when FOH applied them in pricing AND additional
                    work was authorised. Listed line-by-line so the GMS re-key
                    is straightforward. */}
                {jobsheetData.workshop_charges?.length > 0 && (
                  <div className="border rounded-lg overflow-hidden mb-3" data-testid="jobsheet-workshop-charges-section">
                    <div className="bg-slate-100 px-4 py-2 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-slate-600" />
                        <span className="font-semibold text-slate-800">Workshop Charges &amp; Disposal</span>
                      </div>
                      <span className="font-bold text-slate-700">£{(jobsheetData.workshop_charges_totals?.total || 0).toFixed(2)}</span>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left py-1.5 px-3 font-medium text-xs">CHARGE</th>
                          <th className="text-right py-1.5 px-3 font-medium text-xs">Ex VAT</th>
                          <th className="text-right py-1.5 px-3 font-medium text-xs">VAT</th>
                          <th className="text-right py-1.5 px-3 font-medium text-xs">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobsheetData.workshop_charges.map((c, idx) => (
                          <tr key={c.id || idx} className="border-b border-slate-100" data-testid={`jobsheet-workshop-charge-${c.id || idx}`}>
                            <td className="py-1.5 px-3">{c.description}</td>
                            <td className="py-1.5 px-3 text-right font-mono">£{(c.subtotal || 0).toFixed(2)}</td>
                            <td className="py-1.5 px-3 text-right font-mono text-slate-500">£{(c.vat || 0).toFixed(2)}</td>
                            <td className="py-1.5 px-3 text-right font-mono font-semibold">£{(c.total || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* GRAND TOTALS */}
                <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-slate-700">GRAND TOTAL (incl. already authorised + workshop charges)</span>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Ex VAT: £{jobsheetData.totals?.subtotal?.toFixed(2) || '0.00'}</p>
                      <p className="text-sm text-slate-600">VAT (20%): £{jobsheetData.totals?.vat?.toFixed(2) || '0.00'}</p>
                      <p className="text-2xl font-bold text-green-700" data-testid="jobsheet-grand-total">£{jobsheetData.totals?.total?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Signature Area - For Print */}
                <div className="mt-8 pt-6 border-t grid grid-cols-2 gap-8 print:grid">
                  <div>
                    <p className="text-sm text-slate-500 mb-8">Customer Signature:</p>
                    <div className="border-b border-slate-400 h-12"></div>
                    <p className="text-xs text-slate-500 mt-1">Date: _______________</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-8">Technician Signature:</p>
                    <div className="border-b border-slate-400 h-12"></div>
                    <p className="text-xs text-slate-500 mt-1">Date: _______________</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call Companion Modal - With Live Compliance Checklist */}
        {showCallCompanion && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="call-companion-modal-dashboard">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
              {/* Modal Header - Dynamic based on phase */}
              <div className={`px-6 py-4 flex justify-between items-center shrink-0 ${
                currentPhase === 0 ? 'bg-slate-700' :
                currentPhase <= 3 ? 'bg-blue-600' :
                currentPhase === 4 ? 'bg-red-600' :
                currentPhase === 5 ? 'bg-amber-600' :
                currentPhase === 6 ? 'bg-purple-600' :
                'bg-green-600'
              } text-white`}>
                <div>
                  <h2 className="font-semibold text-lg flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Call Companion — {currentPhase === 0 ? 'Select Tone' : `Phase ${currentPhase} of 7`}
                  </h2>
                  <p className="text-white/80 text-sm mt-1">
                    {currentCallTask?.vehicle_reg} — {formatVehicleNameFromObject(callCompanion?.vehicle)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Progress indicator */}
                  <div className="flex gap-1">
                    {[1,2,3,4,5,6,7].map(p => (
                      <div 
                        key={p} 
                        className={`w-2 h-2 rounded-full ${
                          compliancePhases[`phase${p}`]?.completed ? 'bg-green-400' :
                          currentPhase === p ? 'bg-white' :
                          currentPhase > p ? 'bg-white/50' : 'bg-white/30'
                        }`}
                      />
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => { 
                      if (Object.values(compliancePhases).some(p => p.completed) && !Object.values(compliancePhases).every(p => p.completed)) {
                        if (!confirm("⚠️ Closing now will mark this call as NON-COMPLIANT. Continue?")) return;
                      }
                      // Save audit as abandoned if call was started (tone selected)
                      if (callTone && currentCallTask) {
                        await submitCallCompanionAudit('abandoned');
                      }
                      setShowCallCompanion(false); 
                      setCallCompanion(null); 
                      setCurrentCallTask(null); 
                    }}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="overflow-y-auto flex-1 p-6">
                {loadingCompanion ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading call guidance...</span>
                  </div>
                ) : callCompanion ? (
                  <div className="space-y-6">
                    {/* Vehicle & Customer Info - Always visible */}
                    <div className="bg-slate-50 rounded-lg p-4 border">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Vehicle:</span>
                          <p className="font-medium">{callCompanion.vehicle?.reg} — {formatVehicleNameFromObject(callCompanion.vehicle)}</p>
                          {callCompanion.vehicle?.year && <p className="text-slate-500">{callCompanion.vehicle.year} • {callCompanion.vehicle?.mileage?.toLocaleString()} miles</p>}
                        </div>
                        <div>
                          <span className="text-slate-500">Customer:</span>
                          <p className="font-medium">{callCompanion.customer?.name || currentCallTask?.customer_name || "Not specified"}</p>
                          {(callCompanion.customer?.phone || currentCallTask?.customer_phone) && (
                            <p className="text-slate-500 flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {callCompanion.customer?.phone || currentCallTask?.customer_phone}
                            </p>
                          )}
                        </div>
                      </div>
                      {callTone && (
                        <div className="mt-3 pt-3 border-t">
                          <Badge className={`${
                            callTone === 'reassuring' ? 'bg-blue-100 text-blue-700' :
                            callTone === 'neutral' ? 'bg-slate-100 text-slate-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            Tone: {callTone.charAt(0).toUpperCase() + callTone.slice(1)}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* PHASE 0: Tone Selection */}
                    {currentPhase === 0 && (
                      <div className="bg-slate-100 rounded-lg p-6 border-2 border-slate-300">
                        <h3 className="text-lg font-semibold text-slate-800 mb-2">🎯 Step 0: Select Call Tone</h3>
                        <p className="text-slate-600 text-sm mb-4">Before the call begins, select the tone that best suits this customer.</p>
                        
                        <div className="grid grid-cols-3 gap-4">
                          {[
                            { id: 'reassuring', label: 'Reassuring', desc: 'Calm, supportive, empathetic', icon: '💙', color: 'blue' },
                            { id: 'neutral', label: 'Neutral', desc: 'Professional, balanced, factual', icon: '⚖️', color: 'slate' },
                            { id: 'direct', label: 'Direct', desc: 'Confident, clear, action-focused', icon: '🎯', color: 'orange' }
                          ].map(tone => (
                            <button
                              key={tone.id}
                              onClick={() => setCallTone(tone.id)}
                              className={`p-4 rounded-lg border-2 transition-all text-left ${
                                callTone === tone.id 
                                  ? `border-${tone.color}-500 bg-${tone.color}-50 ring-2 ring-${tone.color}-200` 
                                  : 'border-slate-200 hover:border-slate-300 bg-white'
                              }`}
                              data-testid={`tone-${tone.id}`}
                            >
                              <div className="text-2xl mb-2">{tone.icon}</div>
                              <p className="font-semibold text-slate-800">{tone.label}</p>
                              <p className="text-xs text-slate-500 mt-1">{tone.desc}</p>
                            </button>
                          ))}
                        </div>

                        <Button
                          onClick={() => setCurrentPhase(1)}
                          disabled={!callTone}
                          className="w-full mt-6 bg-slate-700 hover:bg-slate-800"
                          data-testid="start-call-btn"
                        >
                          {callTone ? 'Begin Call →' : 'Select a tone to continue'}
                        </Button>
                      </div>
                    )}

                    {/* PHASE 1: Permission-Based Opening */}
                    {currentPhase === 1 && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">1</div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-blue-800 text-lg">Phase 1: Permission-Based Opening</h3>
                              <p className="text-blue-700 text-sm mt-1"><strong>Objective:</strong> Confirm timing and gain permission to proceed.</p>
                            </div>
                          </div>
                          
                          <div className="mt-4 bg-white rounded p-4 border border-blue-100">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Suggested Phrasing:</p>
                            <p className="text-slate-700 italic">
                              {callTone === 'reassuring' && "Hi [Name], this is [Advisor] from [Garage]. I hope I'm not catching you at a bad time? I wanted to give you a quick call about your [Vehicle] — is now okay, or would another time suit you better?"}
                              {callTone === 'neutral' && "Good [morning/afternoon], [Name]. This is [Advisor] calling from [Garage] regarding your [Vehicle]. Do you have a few minutes to discuss the inspection results?"}
                              {callTone === 'direct' && "Hi [Name], [Advisor] here from [Garage]. I'm calling about your [Vehicle] inspection. Is now a good time, or shall I call back?"}
                            </p>
                          </div>

                          <div className="mt-4 bg-blue-100 rounded p-3">
                            <p className="text-sm text-blue-800 font-medium">✓ Compliance requires you to:</p>
                            <ul className="text-sm text-blue-700 mt-1 space-y-1">
                              <li>• Ask if now is a good time</li>
                              <li>• Offer to reschedule if not</li>
                            </ul>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <button
                            onClick={() => handlePhaseComplete(1)}
                            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                              compliancePhases.phase1.completed 
                                ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                            }`}
                            data-testid="phase1-checkbox"
                          >
                            {compliancePhases.phase1.completed ? (
                              <Check className="w-6 h-6" />
                            ) : (
                              <div className="w-6 h-6 border-2 border-current rounded" />
                            )}
                            <span>Phase 1 Completed</span>
                          </button>
                          
                          <details className="mt-3">
                            <summary className="text-sm text-slate-500 cursor-pointer">Anything notable? (optional)</summary>
                            <textarea
                              value={compliancePhases.phase1.notes}
                              onChange={(e) => setCompliancePhases(prev => ({
                                ...prev,
                                phase1: { ...prev.phase1, notes: e.target.value }
                              }))}
                              placeholder="Internal notes..."
                              className="mt-2 w-full p-2 text-sm border rounded resize-none"
                              rows={2}
                            />
                          </details>
                        </div>
                      </div>
                    )}

                    {/* PHASE 2: Context Reminder */}
                    {currentPhase === 2 && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">2</div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-blue-800 text-lg">Phase 2: Context Reminder</h3>
                              <p className="text-blue-700 text-sm mt-1"><strong>Objective:</strong> Re-anchor the call to the Safety & Reliability Inspection.</p>
                            </div>
                          </div>
                          
                          <div className="mt-4 bg-white rounded p-4 border border-blue-100">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Suggested Phrasing:</p>
                            <p className="text-slate-700 italic">
                              "Just to remind you, when your {formatVehicleNameFromObject(callCompanion.vehicle)} was with us, our technician carried out a Safety & Reliability Inspection. This is a comprehensive check that goes beyond the MOT."
                            </p>
                          </div>

                          <div className="mt-4 bg-blue-100 rounded p-3">
                            <p className="text-sm text-blue-800 font-medium">✓ Compliance requires you to:</p>
                            <ul className="text-sm text-blue-700 mt-1 space-y-1">
                              <li>• Reference the inspection by name</li>
                              <li>• Remind customer their vehicle was with you</li>
                            </ul>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <button
                            onClick={() => handlePhaseComplete(2)}
                            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                              compliancePhases.phase2.completed 
                                ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                            }`}
                            data-testid="phase2-checkbox"
                          >
                            {compliancePhases.phase2.completed ? (
                              <Check className="w-6 h-6" />
                            ) : (
                              <div className="w-6 h-6 border-2 border-current rounded" />
                            )}
                            <span>Phase 2 Completed</span>
                          </button>
                          
                          <details className="mt-3">
                            <summary className="text-sm text-slate-500 cursor-pointer">Anything notable? (optional)</summary>
                            <textarea
                              value={compliancePhases.phase2.notes}
                              onChange={(e) => setCompliancePhases(prev => ({
                                ...prev,
                                phase2: { ...prev.phase2, notes: e.target.value }
                              }))}
                              placeholder="Internal notes..."
                              className="mt-2 w-full p-2 text-sm border rounded resize-none"
                              rows={2}
                            />
                          </details>
                        </div>
                      </div>
                    )}

                    {/* PHASE 3: Confirmation of Receipt */}
                    {currentPhase === 3 && (
                      <div className="space-y-4">
                        <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">3</div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-blue-800 text-lg">Phase 3: Confirmation of Receipt</h3>
                              <p className="text-blue-700 text-sm mt-1"><strong>Objective:</strong> Ensure the customer has seen the written inspection.</p>
                            </div>
                          </div>
                          
                          <div className="mt-4 bg-white rounded p-4 border border-blue-100">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Suggested Phrasing:</p>
                            <p className="text-slate-700 italic">
                              "Did you manage to have a look at the inspection report we sent through? I just want to make sure you've had a chance to see the findings before we discuss them."
                            </p>
                          </div>

                          <div className="mt-4 bg-yellow-100 border border-yellow-300 rounded p-3">
                            <p className="text-sm text-yellow-800 font-medium">⚠️ CRITICAL: If customer hasn't read the report</p>
                            <p className="text-sm text-yellow-700 mt-1">
                              Pause or reschedule. Say: "No problem — would you like me to resend it and call back in 10 minutes so you can have a quick look first?"
                            </p>
                          </div>

                          <div className="mt-4 bg-blue-100 rounded p-3">
                            <p className="text-sm text-blue-800 font-medium">✓ Compliance requires you to:</p>
                            <ul className="text-sm text-blue-700 mt-1 space-y-1">
                              <li>• Confirm email was received and opened</li>
                              <li>• Pause or reschedule if not read</li>
                            </ul>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <button
                            onClick={() => handlePhaseComplete(3)}
                            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                              compliancePhases.phase3.completed 
                                ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                            }`}
                            data-testid="phase3-checkbox"
                          >
                            {compliancePhases.phase3.completed ? (
                              <Check className="w-6 h-6" />
                            ) : (
                              <div className="w-6 h-6 border-2 border-current rounded" />
                            )}
                            <span>Phase 3 Completed</span>
                          </button>
                          
                          <details className="mt-3">
                            <summary className="text-sm text-slate-500 cursor-pointer">Anything notable? (optional)</summary>
                            <textarea
                              value={compliancePhases.phase3.notes}
                              onChange={(e) => setCompliancePhases(prev => ({
                                ...prev,
                                phase3: { ...prev.phase3, notes: e.target.value }
                              }))}
                              placeholder="Internal notes..."
                              className="mt-2 w-full p-2 text-sm border rounded resize-none"
                              rows={2}
                            />
                          </details>
                        </div>
                      </div>
                    )}

                    {/* PHASE 4: RED Items (Safety-Critical) */}
                    {currentPhase === 4 && (
                      <div className="space-y-4">
                        <div className="bg-red-50 rounded-lg p-5 border border-red-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">4</div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-red-800 text-lg">Phase 4: RED Items (Safety-Critical)</h3>
                              <p className="text-red-700 text-sm mt-1"><strong>Objective:</strong> Explain safety-critical items clearly and calmly.</p>
                            </div>
                          </div>
                          
                          {callCompanion.red_items?.count > 0 ? (
                            <div className="mt-4 space-y-3">
                              <p className="text-xs font-medium text-slate-500 uppercase">Items to discuss ({callCompanion.red_items.count}):</p>
                              {callCompanion.red_items.items.map((item, idx) => (
                                <div key={idx} className="bg-white rounded p-3 border border-red-100">
                                  <p className="font-medium text-red-800">{item.item}</p>
                                  <p className="text-red-700 text-sm mt-1">{item.explanation}</p>
                                  {item.notes && (
                                    <p className="text-slate-600 mt-2 text-xs bg-red-50 rounded p-2">
                                      <span className="font-medium">Tech notes:</span> {item.notes}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-4 bg-green-50 border border-green-200 rounded p-4 text-center">
                              <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
                              <p className="text-green-700 font-medium">No safety-critical (RED) items</p>
                              <p className="text-green-600 text-sm">You can proceed to the next phase.</p>
                            </div>
                          )}

                          <div className="mt-4 bg-red-100 rounded p-3">
                            <p className="text-sm text-red-800 font-medium">✓ Compliance requires you to:</p>
                            <ul className="text-sm text-red-700 mt-1 space-y-1">
                              <li>• Explain RED items only from documented evidence</li>
                              <li>• Avoid speculation or alarmist language</li>
                              <li>• Frame as safety, not opinion</li>
                            </ul>
                          </div>

                          <div className="mt-4 bg-slate-100 rounded p-3 border">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2">🚫 Avoid saying:</p>
                            <p className="text-sm text-slate-600 italic">"This is dangerous", "You shouldn't be driving", "This could kill someone"</p>
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2 mt-3">✅ Instead say:</p>
                            <p className="text-sm text-slate-600 italic">"The technician noted...", "This has been flagged because...", "For your safety, we recommend..."</p>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <button
                            onClick={() => handlePhaseComplete(4)}
                            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                              compliancePhases.phase4.completed 
                                ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                            }`}
                            data-testid="phase4-checkbox"
                          >
                            {compliancePhases.phase4.completed ? (
                              <Check className="w-6 h-6" />
                            ) : (
                              <div className="w-6 h-6 border-2 border-current rounded" />
                            )}
                            <span>Phase 4 Completed</span>
                          </button>
                          
                          <details className="mt-3">
                            <summary className="text-sm text-slate-500 cursor-pointer">Anything notable? (optional)</summary>
                            <textarea
                              value={compliancePhases.phase4.notes}
                              onChange={(e) => setCompliancePhases(prev => ({
                                ...prev,
                                phase4: { ...prev.phase4, notes: e.target.value }
                              }))}
                              placeholder="Internal notes..."
                              className="mt-2 w-full p-2 text-sm border rounded resize-none"
                              rows={2}
                            />
                          </details>
                        </div>
                      </div>
                    )}

                    {/* PHASE 5: AMBER Items (Benefit Framing) */}
                    {currentPhase === 5 && (
                      <div className="space-y-4">
                        <div className="bg-amber-50 rounded-lg p-5 border border-amber-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">5</div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-amber-800 text-lg">Phase 5: AMBER Items (Benefit Framing)</h3>
                              <p className="text-amber-700 text-sm mt-1"><strong>Objective:</strong> Explain AMBER items using planning and value logic.</p>
                            </div>
                          </div>
                          
                          {callCompanion.amber_items?.count > 0 ? (
                            <div className="mt-4 space-y-3">
                              <p className="text-xs font-medium text-slate-500 uppercase">Items to discuss ({callCompanion.amber_items.count}):</p>
                              {callCompanion.amber_items.items.map((item, idx) => (
                                <div key={idx} className="bg-white rounded p-3 border border-amber-100">
                                  <p className="font-medium text-amber-800">{item.item}</p>
                                  <p className="text-amber-700 text-sm mt-1">{item.explanation}</p>
                                  {item.notes && (
                                    <p className="text-slate-600 mt-2 text-xs bg-amber-50 rounded p-2">
                                      <span className="font-medium">Tech notes:</span> {item.notes}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-4 bg-green-50 border border-green-200 rounded p-4 text-center">
                              <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
                              <p className="text-green-700 font-medium">No advisory (AMBER) items</p>
                              <p className="text-green-600 text-sm">You can proceed to the next phase.</p>
                            </div>
                          )}

                          <div className="mt-4 bg-amber-100 rounded p-3">
                            <p className="text-sm text-amber-800 font-medium">✓ Compliance requires you to:</p>
                            <ul className="text-sm text-amber-700 mt-1 space-y-1">
                              <li>• Explain why items are flagged now</li>
                              <li>• Use benefit framing (prevention, cost control, convenience)</li>
                              <li>• Avoid "while we're there" language</li>
                            </ul>
                          </div>

                          <div className="mt-4 bg-slate-100 rounded p-3 border">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Benefit framing examples:</p>
                            <ul className="text-sm text-slate-600 space-y-1">
                              <li>• <strong>Prevention:</strong> "Addressing this now prevents a larger issue later"</li>
                              <li>• <strong>Cost control:</strong> "It's more economical to do this while we have the car"</li>
                              <li>• <strong>Convenience:</strong> "This saves you an extra trip to us"</li>
                            </ul>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <button
                            onClick={() => handlePhaseComplete(5)}
                            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                              compliancePhases.phase5.completed 
                                ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                            }`}
                            data-testid="phase5-checkbox"
                          >
                            {compliancePhases.phase5.completed ? (
                              <Check className="w-6 h-6" />
                            ) : (
                              <div className="w-6 h-6 border-2 border-current rounded" />
                            )}
                            <span>Phase 5 Completed</span>
                          </button>
                          
                          <details className="mt-3">
                            <summary className="text-sm text-slate-500 cursor-pointer">Anything notable? (optional)</summary>
                            <textarea
                              value={compliancePhases.phase5.notes}
                              onChange={(e) => setCompliancePhases(prev => ({
                                ...prev,
                                phase5: { ...prev.phase5, notes: e.target.value }
                              }))}
                              placeholder="Internal notes..."
                              className="mt-2 w-full p-2 text-sm border rounded resize-none"
                              rows={2}
                            />
                          </details>
                        </div>
                      </div>
                    )}

                    {/* PHASE 6: Objection Handling - Full Matrix */}
                    {currentPhase === 6 && (
                      <div className="space-y-4">
                        <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">6</div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-purple-800 text-lg">Phase 6: Objection Handling</h3>
                              <p className="text-purple-700 text-sm mt-1"><strong>Objective:</strong> Address objections using the controlled matrix below.</p>
                            </div>
                          </div>
                          
                          {/* Objection Matrix - 8 Categories */}
                          <div className="mt-4">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-3">Select customer objection to handle:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.values(OBJECTION_MATRIX).map(category => (
                                <button
                                  key={category.id}
                                  onClick={() => {
                                    setActiveObjection({ category: category.id, button: null });
                                    setShowObjectionPanel(true);
                                  }}
                                  className={`p-3 rounded-lg border-2 text-left transition-all hover:shadow-md ${
                                    loggedObjections.some(o => o.category === category.id)
                                      ? 'bg-green-50 border-green-300'
                                      : 'bg-white border-slate-200 hover:border-purple-300'
                                  }`}
                                  data-testid={`objection-category-${category.id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-slate-700">{category.title}</span>
                                    {loggedObjections.some(o => o.category === category.id) && (
                                      <Check className="w-4 h-4 text-green-600" />
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">{category.buttons.length} objections</p>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Logged Objections Summary */}
                          {loggedObjections.length > 0 && (
                            <div className="mt-4 bg-green-50 border border-green-200 rounded p-3">
                              <p className="text-sm font-medium text-green-800">✓ Objections Handled: {loggedObjections.length}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {loggedObjections.map((obj, idx) => (
                                  <Badge key={idx} className="bg-green-100 text-green-700 text-xs">
                                    {OBJECTION_MATRIX[obj.category]?.title?.split(' ')[1]} — {obj.buttonLabel}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="mt-4 bg-purple-100 rounded p-3">
                            <p className="text-sm text-purple-800 font-medium">✓ Compliance requires you to:</p>
                            <ul className="text-sm text-purple-700 mt-1 space-y-1">
                              <li>• Select matching objection from the matrix</li>
                              <li>• Follow the auto-displayed guidance script</li>
                              <li>• Confirm completion before resuming</li>
                              <li>• Never handle objections "off-script"</li>
                            </ul>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <button
                            onClick={() => handlePhaseComplete(6)}
                            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                              compliancePhases.phase6.completed 
                                ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                            }`}
                            data-testid="phase6-checkbox"
                          >
                            {compliancePhases.phase6.completed ? (
                              <Check className="w-6 h-6" />
                            ) : (
                              <div className="w-6 h-6 border-2 border-current rounded" />
                            )}
                            <span>Phase 6 Completed</span>
                          </button>
                          
                          <details className="mt-3">
                            <summary className="text-sm text-slate-500 cursor-pointer">Anything notable? (optional)</summary>
                            <textarea
                              value={compliancePhases.phase6.notes}
                              onChange={(e) => setCompliancePhases(prev => ({
                                ...prev,
                                phase6: { ...prev.phase6, notes: e.target.value }
                              }))}
                              placeholder="Internal notes..."
                              className="mt-2 w-full p-2 text-sm border rounded resize-none"
                              rows={2}
                            />
                          </details>
                        </div>
                      </div>
                    )}

                    {/* PHASE 7: Forced-Choice Close */}
                    {currentPhase === 7 && (
                      <div className="space-y-4">
                        <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">7</div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-green-800 text-lg">Phase 7: Forced-Choice Close</h3>
                              <p className="text-green-700 text-sm mt-1"><strong>Objective:</strong> Convert understanding into a decision.</p>
                            </div>
                          </div>
                          
                          <div className="mt-4 bg-white rounded p-4 border border-green-100">
                            <p className="text-xs font-medium text-slate-500 uppercase mb-2">Suggested Closing:</p>
                            <p className="text-slate-700 italic">
                              "So to summarise, we have the {callCompanion.red_items?.count || 0} safety items and {callCompanion.amber_items?.count || 0} advisory items. Would you like us to go ahead with everything, or shall we focus on the safety-critical items first and schedule the advisory work for later?"
                            </p>
                          </div>

                          <div className="mt-4 bg-green-100 rounded p-3">
                            <p className="text-sm text-green-800 font-medium">✓ Compliance requires you to:</p>
                            <ul className="text-sm text-green-700 mt-1 space-y-1">
                              <li>• Present two clear options (not an open question)</li>
                              <li>• Target 100% RED and ≥80% AMBER authorisation</li>
                              <li>• Avoid "Would you like to go ahead?"</li>
                            </ul>
                          </div>

                          <div className="mt-4 bg-red-50 border border-red-200 rounded p-3">
                            <p className="text-sm text-red-700 font-medium">🚫 Never ask:</p>
                            <p className="text-sm text-red-600 italic">"Would you like to go ahead?" or "What do you think?"</p>
                          </div>

                          {/* Outcome Recording */}
                          <div className="mt-6 bg-slate-100 rounded-lg p-4 border">
                            <p className="font-medium text-slate-800 mb-3">Record Outcome:</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm text-slate-600 block mb-2">RED Items (Safety-Critical):</label>
                                <select
                                  value={callOutcome.redAuthorised || ''}
                                  onChange={(e) => setCallOutcome(prev => ({ ...prev, redAuthorised: e.target.value }))}
                                  className="w-full p-2 border rounded"
                                  data-testid="red-outcome-select"
                                >
                                  <option value="">Select...</option>
                                  <option value="authorised">✅ Authorised</option>
                                  <option value="declined">❌ Declined</option>
                                  <option value="partial">⚠️ Partial</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-sm text-slate-600 block mb-2">AMBER Items (% Authorised):</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="10"
                                    value={callOutcome.amberPercent}
                                    onChange={(e) => setCallOutcome(prev => ({ ...prev, amberPercent: parseInt(e.target.value) }))}
                                    className="flex-1"
                                    data-testid="amber-outcome-slider"
                                  />
                                  <span className="font-mono text-lg font-bold text-amber-600 w-14 text-right">{callOutcome.amberPercent}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 border">
                          <button
                            onClick={() => handlePhaseComplete(7)}
                            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                              compliancePhases.phase7.completed 
                                ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                            }`}
                            data-testid="phase7-checkbox"
                          >
                            {compliancePhases.phase7.completed ? (
                              <Check className="w-6 h-6" />
                            ) : (
                              <div className="w-6 h-6 border-2 border-current rounded" />
                            )}
                            <span>Phase 7 Completed</span>
                          </button>
                          
                          <details className="mt-3">
                            <summary className="text-sm text-slate-500 cursor-pointer">Anything notable? (optional)</summary>
                            <textarea
                              value={compliancePhases.phase7.notes}
                              onChange={(e) => setCompliancePhases(prev => ({
                                ...prev,
                                phase7: { ...prev.phase7, notes: e.target.value }
                              }))}
                              placeholder="Internal notes..."
                              className="mt-2 w-full p-2 text-sm border rounded resize-none"
                              rows={2}
                            />
                          </details>
                        </div>
                      </div>
                    )}

                    {/* FLOATING OBJECTION BUTTON - Visible during phases 4-7 */}
                    {currentPhase >= 4 && currentPhase <= 7 && !showObjectionPanel && (
                      <div className="fixed bottom-24 right-8 z-50">
                        <Button
                          onClick={() => setShowObjectionPanel(true)}
                          className="bg-purple-600 hover:bg-purple-700 text-white shadow-lg rounded-full px-6 py-3 flex items-center gap-2"
                          data-testid="open-objection-panel-btn"
                        >
                          <MessageSquare className="w-5 h-5" />
                          Customer Objection
                          {loggedObjections.length > 0 && (
                            <Badge className="bg-white text-purple-600 ml-1">{loggedObjections.length}</Badge>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* OBJECTION HANDLING PANEL - Modal overlay */}
                    {showObjectionPanel && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="objection-panel">
                        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                          {/* Panel Header */}
                          <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center shrink-0">
                            <div>
                              <h3 className="font-semibold text-lg">🛡️ Objection Handling Panel</h3>
                              <p className="text-purple-200 text-sm">Select and handle customer objections compliantly</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (activeObjection?.button && !activeObjection?.completed) {
                                  if (!confirm("⚠️ You have an unconfirmed objection. Close anyway?")) return;
                                }
                                setShowObjectionPanel(false);
                                setActiveObjection(null);
                              }}
                              className="text-white hover:bg-purple-700"
                            >
                              <X className="w-5 h-5" />
                            </Button>
                          </div>

                          {/* Panel Content */}
                          <div className="overflow-y-auto flex-1 p-6">
                            {!activeObjection?.button ? (
                              /* Category & Button Selection */
                              <div className="space-y-4">
                                <p className="text-sm text-slate-600 mb-4">
                                  When a customer raises an objection, select the closest matching category and button below.
                                </p>
                                
                                {Object.values(OBJECTION_MATRIX).map(category => (
                                  <div key={category.id} className="border rounded-lg overflow-hidden">
                                    <div className={`px-4 py-3 bg-${category.color}-50 border-b`}>
                                      <h4 className="font-semibold text-slate-800">{category.title}</h4>
                                      <p className="text-xs text-slate-500">{category.objective}</p>
                                    </div>
                                    <div className="p-3 grid grid-cols-2 gap-2">
                                      {category.buttons.map(btn => (
                                        <button
                                          key={btn.id}
                                          onClick={() => setActiveObjection({ 
                                            category: category.id, 
                                            button: btn.id, 
                                            buttonLabel: btn.label,
                                            completed: false 
                                          })}
                                          className={`p-2 text-sm text-left rounded border transition-all ${
                                            loggedObjections.some(o => o.category === category.id && o.button === btn.id)
                                              ? 'bg-green-50 border-green-300 text-green-700'
                                              : 'bg-white border-slate-200 hover:border-purple-300 hover:bg-purple-50'
                                          }`}
                                          data-testid={`objection-btn-${btn.id}`}
                                        >
                                          {btn.label}
                                          {loggedObjections.some(o => o.category === category.id && o.button === btn.id) && (
                                            <Check className="w-3 h-3 inline ml-1 text-green-600" />
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              /* Active Objection Handling */
                              <div className="space-y-4">
                                {(() => {
                                  const category = OBJECTION_MATRIX[activeObjection.category];
                                  const button = category?.buttons.find(b => b.id === activeObjection.button);
                                  return (
                                    <>
                                      <div className="flex items-center gap-2 mb-4">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setActiveObjection({ ...activeObjection, button: null })}
                                          className="text-slate-500"
                                        >
                                          ← Back
                                        </Button>
                                        <Badge className="bg-purple-100 text-purple-700">{category?.title}</Badge>
                                        <Badge className="bg-slate-100 text-slate-700">{button?.label}</Badge>
                                      </div>

                                      {/* Objective */}
                                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                        <p className="text-xs font-medium text-purple-600 uppercase mb-1">Objective</p>
                                        <p className="text-purple-800 font-medium">{category?.objective}</p>
                                      </div>

                                      {/* Guidance Script */}
                                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <p className="text-xs font-medium text-green-600 uppercase mb-2">
                                          ✅ Safe Phrasing ({callTone?.charAt(0).toUpperCase() + callTone?.slice(1)} Tone)
                                        </p>
                                        <p className="text-green-800 italic leading-relaxed">
                                          {category?.guidance[callTone] || category?.guidance.neutral}
                                        </p>
                                      </div>

                                      {/* Do Not Say */}
                                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <p className="text-xs font-medium text-red-600 uppercase mb-2">🚫 Do NOT Say</p>
                                        <ul className="space-y-1">
                                          {category?.doNotSay.map((rule, idx) => (
                                            <li key={idx} className="text-red-700 text-sm flex items-center gap-2">
                                              <X className="w-3 h-3 text-red-500 shrink-0" />
                                              {rule}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>

                                      {/* Confirmation */}
                                      <div className="bg-slate-100 border border-slate-300 rounded-lg p-4">
                                        <button
                                          onClick={() => setActiveObjection(prev => ({ ...prev, completed: !prev.completed }))}
                                          className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                                            activeObjection.completed 
                                              ? 'bg-green-500 text-white shadow-lg hover:bg-green-600' 
                                              : 'bg-white border-2 border-slate-300 text-slate-700 hover:border-green-400 hover:bg-green-50'
                                          }`}
                                          data-testid="objection-compliance-checkbox"
                                        >
                                          {activeObjection.completed ? (
                                            <Check className="w-6 h-6" />
                                          ) : (
                                            <div className="w-6 h-6 border-2 border-current rounded" />
                                          )}
                                          <span>{category?.checkboxLabel}</span>
                                        </button>
                                      </div>

                                      {/* Log & Continue Button */}
                                      <Button
                                        onClick={() => {
                                          if (!activeObjection.completed) {
                                            alert("You must confirm compliance before logging this objection.");
                                            return;
                                          }
                                          setLoggedObjections(prev => [
                                            ...prev.filter(o => !(o.category === activeObjection.category && o.button === activeObjection.button)),
                                            {
                                              category: activeObjection.category,
                                              button: activeObjection.button,
                                              buttonLabel: activeObjection.buttonLabel,
                                              completed: true,
                                              timestamp: new Date().toISOString()
                                            }
                                          ]);
                                          setActiveObjection(null);
                                          setShowObjectionPanel(false);
                                        }}
                                        disabled={!activeObjection.completed}
                                        className="w-full bg-purple-600 hover:bg-purple-700"
                                        data-testid="log-objection-btn"
                                      >
                                        <Check className="w-4 h-4 mr-2" />
                                        Log Objection & Return to Call
                                      </Button>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>

                          {/* Panel Footer - Logged Objections Summary */}
                          {loggedObjections.length > 0 && (
                            <div className="border-t px-6 py-3 bg-green-50 shrink-0">
                              <p className="text-sm font-medium text-green-800">
                                ✓ {loggedObjections.length} objection(s) handled compliantly
                              </p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {loggedObjections.map((obj, idx) => (
                                  <Badge key={idx} className="bg-green-100 text-green-700 text-xs">
                                    {obj.buttonLabel}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* POST-CALL STATUS - After Phase 7 completion */}
                    {currentPhase === 8 && (
                      <div className="space-y-4">
                        <div className={`rounded-lg p-6 border-2 ${
                          Object.values(compliancePhases).every(p => p.completed)
                            ? 'bg-green-50 border-green-300'
                            : 'bg-red-50 border-red-300'
                        }`}>
                          <div className="text-center mb-6">
                            {Object.values(compliancePhases).every(p => p.completed) ? (
                              <>
                                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <Check className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-green-800">✅ COMPLIANT CALL</h3>
                                <p className="text-green-600 mt-1">All 7 phases completed in line with guidance</p>
                              </>
                            ) : (
                              <>
                                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <X className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-red-800">⚠️ NON-COMPLIANT CALL</h3>
                                <p className="text-red-600 mt-1">Some phases were not confirmed</p>
                              </>
                            )}
                          </div>

                          {/* Compliance Summary */}
                          <div className="bg-white rounded-lg p-4 border mb-4">
                            <p className="text-sm font-medium text-slate-600 mb-3">Phase Completion:</p>
                            <div className="grid grid-cols-7 gap-2">
                              {[1,2,3,4,5,6,7].map(p => (
                                <div key={p} className={`text-center p-2 rounded ${
                                  compliancePhases[`phase${p}`]?.completed 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  <div className="font-bold">{p}</div>
                                  <div className="text-xs">{compliancePhases[`phase${p}`]?.completed ? '✓' : '✗'}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Authorisation Outcome */}
                          <div className="bg-white rounded-lg p-4 border">
                            <p className="text-sm font-medium text-slate-600 mb-3">📊 Authorisation Outcome:</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div className={`p-3 rounded ${
                                callOutcome.redAuthorised === 'authorised' ? 'bg-green-100' :
                                callOutcome.redAuthorised === 'declined' ? 'bg-red-100' : 'bg-amber-100'
                              }`}>
                                <p className="text-xs text-slate-500 uppercase">RED Items</p>
                                <p className="font-bold text-lg">
                                  {callOutcome.redAuthorised === 'authorised' ? '✅ Authorised' :
                                   callOutcome.redAuthorised === 'declined' ? '❌ Declined' :
                                   callOutcome.redAuthorised === 'partial' ? '⚠️ Partial' : '—'}
                                </p>
                              </div>
                              <div className={`p-3 rounded ${
                                callOutcome.amberPercent >= 80 ? 'bg-green-100' :
                                callOutcome.amberPercent >= 50 ? 'bg-amber-100' : 'bg-red-100'
                              }`}>
                                <p className="text-xs text-slate-500 uppercase">AMBER Items</p>
                                <p className="font-bold text-lg">{callOutcome.amberPercent}% Authorised</p>
                              </div>
                            </div>
                          </div>

                          {/* Objection Analytics */}
                          <div className="bg-white rounded-lg p-4 border mt-4">
                            <p className="text-sm font-medium text-slate-600 mb-3">🛡️ Objection Handling Summary:</p>
                            {loggedObjections.length > 0 ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between bg-purple-50 p-3 rounded">
                                  <span className="text-purple-700 font-medium">Total Objections Raised:</span>
                                  <span className="text-purple-800 font-bold text-lg">{loggedObjections.length}</span>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 uppercase mb-2">Categories Triggered:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {[...new Set(loggedObjections.map(o => o.category))].map(cat => (
                                      <Badge key={cat} className="bg-purple-100 text-purple-700">
                                        {OBJECTION_MATRIX[cat]?.title || cat}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 uppercase mb-2">Objections Logged:</p>
                                  <div className="space-y-1">
                                    {loggedObjections.map((obj, idx) => (
                                      <div key={idx} className="flex items-center justify-between text-sm bg-green-50 p-2 rounded">
                                        <span className="text-slate-700">{obj.buttonLabel}</span>
                                        <Badge className="bg-green-100 text-green-700 text-xs">✓ Compliant</Badge>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-4 bg-slate-50 rounded">
                                <p className="text-slate-500">No objections raised during this call</p>
                              </div>
                            )}
                          </div>

                          {/* Internal Only Notice */}
                          <div className="mt-4 bg-slate-100 border border-slate-200 rounded p-3 text-center">
                            <p className="text-xs text-slate-600">
                              🔒 This report is INTERNAL ONLY — suitable for QA, coaching, or analytics
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Internal Only Disclaimer - Always show */}
                    {currentPhase > 0 && currentPhase < 8 && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
                        <p className="text-xs text-blue-700 font-medium">
                          🔒 INTERNAL USE ONLY — This information is for staff reference and must not be shared with customers
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No call companion data available</p>
                  </div>
                )}
              </div>

              {/* Modal Footer - Navigation */}
              <div className="border-t px-6 py-4 bg-slate-50 shrink-0">
                <div className="flex gap-3">
                  {currentPhase > 0 && currentPhase < 8 && (
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPhase(prev => prev - 1)}
                      className="flex-1"
                      data-testid="prev-phase-btn"
                    >
                      ← Previous
                    </Button>
                  )}
                  
                  {currentPhase === 0 && (
                    <Button
                      variant="outline"
                      onClick={() => { 
                        setShowCallCompanion(false); 
                        setCallCompanion(null); 
                        setCurrentCallTask(null); 
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  )}
                  
                  {currentPhase >= 1 && currentPhase <= 7 && (
                    <Button
                      onClick={() => {
                        if (!compliancePhases[`phase${currentPhase}`]?.completed) {
                          setShowComplianceWarning(`You must confirm Phase ${currentPhase} compliance before proceeding.`);
                          return;
                        }
                        setCurrentPhase(prev => prev + 1);
                      }}
                      disabled={!compliancePhases[`phase${currentPhase}`]?.completed}
                      className={`flex-1 ${
                        currentPhase === 4 ? 'bg-red-600 hover:bg-red-700' :
                        currentPhase === 5 ? 'bg-amber-600 hover:bg-amber-700' :
                        currentPhase === 6 ? 'bg-purple-600 hover:bg-purple-700' :
                        currentPhase === 7 ? 'bg-green-600 hover:bg-green-700' :
                        'bg-blue-600 hover:bg-blue-700'
                      }`}
                      data-testid="next-phase-btn"
                    >
                      {currentPhase === 7 ? 'Complete Call →' : 'Next Phase →'}
                    </Button>
                  )}
                  
                  {currentPhase === 8 && (
                    <div className="flex gap-2">
                      <Button
                        onClick={async () => {
                          await submitCallCompanionAudit('contacted');
                          handleMarkContacted(currentCallTask?.id);
                          setShowCallCompanion(false);
                          setCallCompanion(null);
                          setCurrentCallTask(null);
                        }}
                        variant="outline"
                        className="flex-1"
                        data-testid="mark-contacted-btn"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Mark Called (Follow-up needed)
                      </Button>
                      <Button
                        onClick={async () => {
                          await submitCallCompanionAudit('completed');
                          handleResolveTask(currentCallTask?.id);
                          setShowCallCompanion(false);
                          setCallCompanion(null);
                          setCurrentCallTask(null);
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid="complete-call-btn"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Mark Resolved & Close
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Compliance Warning */}
                {showComplianceWarning && (
                  <div className="mt-3 bg-red-100 border border-red-300 rounded p-3 text-center">
                    <p className="text-sm text-red-700">{showComplianceWarning}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowComplianceWarning(null)}
                      className="mt-2 text-red-600"
                    >
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Quick Pricing Summary Modal */}
      {showQuickPricingSummary && quickPricingResult && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="quick-pricing-modal">
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 shadow-2xl overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold">Auto-Quote Complete!</h2>
              <p className="text-green-100 mt-1">
                Created {quickPricingResult.jobsCreated} job groups in seconds
              </p>
            </div>
            
            {/* Stats Grid */}
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{quickPricingResult.redItems}</p>
                  <p className="text-xs text-red-500">RED Items</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{quickPricingResult.amberItems}</p>
                  <p className="text-xs text-amber-500">AMBER Items</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{quickPricingResult.totalJobGroups}</p>
                  <p className="text-xs text-blue-500">Total Jobs</p>
                </div>
              </div>
              
              {/* Estimate Total */}
              <div className="bg-slate-800 text-white rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-slate-400 text-sm">Estimated Total</p>
                    <p className="text-3xl font-bold">£{quickPricingResult.total.toFixed(2)}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-slate-400">Subtotal: £{quickPricingResult.subtotal.toFixed(2)}</p>
                    <p className="text-slate-400">VAT: £{quickPricingResult.vat.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              
              {/* Status */}
              {quickPricingResult.canSend ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Ready to Review</p>
                    <p className="text-sm text-green-600">All required items have been priced</p>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Needs Attention</p>
                    <p className="text-sm text-amber-600">
                      {quickPricingResult.issuesCount} issues, {quickPricingResult.warningsCount} warnings to review
                    </p>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowQuickPricingSummary(false);
                    startWizard();
                  }}
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Open Wizard
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => setShowQuickPricingSummary(false)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Review & Edit
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== FOH INSPECTION INTAKE SCREEN ==============
// Send Vehicle to Workshop - Creates inspection request for technician queue

const FOHInspectionIntake = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [createdJob, setCreatedJob] = useState(null); // Store created job for success view
  
  // Form state - Updated for multi-select inspection types
  const [formData, setFormData] = useState({
    reg: "",
    make: "",
    model: "",
    colour: "",
    fuel_type: "",
    year: "",
    engine_capacity: "",
    tax_status: "",
    mot_status: "",
    requires_diagnostic_vi: false,
    requires_vehicle_inspection: false,
    // iter199af — Explicit MOT-test checkbox so MOT can be requested
    // alongside Service and/or Diag in any combination.
    requires_mot_test: false,
    customer_concern: "",  // Required when diagnostic VI is selected
    vehicle_on_site: true,
    key_tag_number: "",
    gh_job_ref: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    vehicle_here_for: "",
    job_intents: [],
    // Yestimate Intake handover — forwarded to backend so the resulting job
    // is back-linked to the originating intake session.
    intake_session_id: null,
  });

  // Pre-populate form from URL query parameters on mount
  useEffect(() => {
    // Helper to decode URL params (handles both %20 and + as space)
    const decodeParam = (value) => {
      if (!value) return '';
      // Replace + with space first, then decode URI components
      return decodeURIComponent(value.replace(/\+/g, ' '));
    };
    
    // Support both old and new parameter names
    const regParam = searchParams.get('registration') || searchParams.get('reg');
    const jobrefParam = searchParams.get('jobref');
    const modelParam = searchParams.get('vehiclemodel') || searchParams.get('model');
    const customerNameParam = searchParams.get('customername');
    const customerPhoneParam = searchParams.get('customerphone');
    // Yestimate Intake handover params (Send to workshop → this screen)
    const makeParam = searchParams.get('make');
    const yearParam = searchParams.get('year');
    const colourParam = searchParams.get('colour');
    const fuelTypeParam = searchParams.get('fueltype');
    const customerEmailParam = searchParams.get('customeremail');
    const concernParam = searchParams.get('concern');
    const hereForParam = searchParams.get('hereFor');
    const intakeSessionIdParam = searchParams.get('intake_session_id');
    const diagParam = searchParams.get('diag');
    const viParam = searchParams.get('vi');
    // iter199ah — Multi-service add-on. Pre-tick the new MOT Test
    // checkbox when the upstream Yestimate intake passed mot=1.
    const motParam = searchParams.get('mot');
    
    // Track if any param was provided for auto-focus logic
    let hasParams = false;
    
    setFormData(prev => {
      const updates = { ...prev };
      
      // Only prefill if field is currently empty (don't overwrite manual typing)
      if (regParam && !prev.reg) {
        updates.reg = decodeParam(regParam).toUpperCase().replace(/\s/g, '');
        hasParams = true;
      }
      
      if (jobrefParam && !prev.gh_job_ref) {
        updates.gh_job_ref = decodeParam(jobrefParam);
        hasParams = true;
      }
      
      if (modelParam && !prev.model) {
        updates.model = decodeParam(modelParam);
        hasParams = true;
      }
      
      if (customerNameParam && !prev.customer_name) {
        updates.customer_name = decodeParam(customerNameParam);
        hasParams = true;
      }
      
      if (customerPhoneParam && !prev.customer_phone) {
        updates.customer_phone = decodeParam(customerPhoneParam);
        hasParams = true;
      }

      // Intake handover prefill — vehicle metadata
      if (makeParam && !prev.make) {
        updates.make = decodeParam(makeParam);
        hasParams = true;
      }
      if (yearParam && !prev.year) {
        updates.year = decodeParam(yearParam);
        hasParams = true;
      }
      if (colourParam && !prev.colour) {
        updates.colour = decodeParam(colourParam);
        hasParams = true;
      }
      if (fuelTypeParam && !prev.fuel_type) {
        updates.fuel_type = decodeParam(fuelTypeParam);
        hasParams = true;
      }
      if (customerEmailParam && !prev.customer_email) {
        updates.customer_email = decodeParam(customerEmailParam);
        hasParams = true;
      }

      // Intake handover prefill — inspection type + concern
      if (diagParam === "1" && !prev.requires_diagnostic_vi) {
        updates.requires_diagnostic_vi = true;
        hasParams = true;
      }
      if (viParam === "1" && !prev.requires_vehicle_inspection) {
        updates.requires_vehicle_inspection = true;
        hasParams = true;
      }
      // iter199ah — Pre-tick MOT Test when the wizard signalled it.
      if (motParam === "1" && !prev.requires_mot_test) {
        updates.requires_mot_test = true;
        hasParams = true;
      }
      if (concernParam && !prev.customer_concern) {
        updates.customer_concern = decodeParam(concernParam);
        hasParams = true;
      }
      if (hereForParam && !prev.vehicle_here_for) {
        updates.vehicle_here_for = decodeParam(hereForParam);
        hasParams = true;
      }
      if (intakeSessionIdParam && !prev.intake_session_id) {
        updates.intake_session_id = decodeParam(intakeSessionIdParam);
        hasParams = true;
      }
      
      return updates;
    });
    
    // Auto-focus on the first empty required field after a short delay
    if (regParam || jobrefParam || modelParam || customerNameParam || customerPhoneParam) {
      setTimeout(() => {
        const currentReg = document.querySelector('[data-testid="intake-reg"]')?.value;
        const currentMake = document.querySelector('[data-testid="intake-make"]')?.value;
        
        if (!currentReg) {
          document.querySelector('[data-testid="intake-reg"]')?.focus();
        } else if (!currentMake) {
          document.querySelector('[data-testid="intake-make"]')?.focus();
        }
      }, 100);
    }
  }, [searchParams]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
    // Reset lookup status if reg changes
    if (field === "reg") {
      setLookupSuccess(false);
      setLookupError("");
    }
  };

  // DVLA Reg Lookup
  const handleRegLookup = async () => {
    const reg = formData.reg.trim().toUpperCase().replace(/\s/g, "");
    if (!reg || reg.length < 2) {
      setLookupError("Please enter a valid registration");
      return;
    }
    
    setLookupLoading(true);
    setLookupError("");
    setLookupSuccess(false);
    
    try {
      const res = await axios.post(`${API}/vehicle/lookup`, { registration: reg });
      
      if (res.data.success && res.data.data) {
        const vehicle = res.data.data;
        setFormData(prev => ({
          ...prev,
          reg: vehicle.registration || prev.reg,
          make: vehicle.make || prev.make,
          model: vehicle.model || prev.model,
          colour: vehicle.colour || "",
          fuel_type: vehicle.fuel_type || "",
          year: vehicle.year ? vehicle.year.toString() : "",
          engine_capacity: vehicle.engine_capacity ? vehicle.engine_capacity.toString() : "",
          tax_status: vehicle.tax_status || "",
          mot_status: vehicle.mot_status || ""
        }));
        setLookupSuccess(true);
        setLookupError("");
      } else {
        setLookupError(res.data.error || "Vehicle not found");
      }
    } catch (err) {
      setLookupError(err.response?.data?.detail || "Lookup failed. Please enter details manually.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation
    if (!formData.reg.trim()) {
      setError("Vehicle Registration is required");
      return;
    }
    if (!formData.make.trim()) {
      setError("Make is required");
      return;
    }
    if (!formData.model.trim()) {
      setError("Model is required");
      return;
    }
    if (!formData.year || !formData.year.toString().trim()) {
      setError("Vehicle Year is required");
      return;
    }
    // Validate year is reasonable
    const yearNum = parseInt(formData.year);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear() + 1) {
      setError("Please enter a valid vehicle year");
      return;
    }
    // At least one inspection type or job intent must be selected
    // iter199af — Also accept a standalone MOT-test request.
    const hasIntents = (formData.job_intents || []).length > 0;
    if (!hasIntents && !formData.requires_diagnostic_vi && !formData.requires_vehicle_inspection && !formData.requires_mot_test) {
      setError("Please select at least one visit intent (MOT, Service or Diagnostic)");
      return;
    }
    // MOT-only: doesn't require VI — but non-MOT intents need at least one VI type
    const motOnlyIntents = ['mot', 'mot_failure_repair', 'mot_retest'];
    const hasNonMotIntent = (formData.job_intents || []).some(i => !motOnlyIntents.includes(i));
    if (hasNonMotIntent && !formData.requires_diagnostic_vi && !formData.requires_vehicle_inspection) {
      // Auto-set VI requirement from intents
      const viIntents = ['service', 'safety_inspection', 'pre_mot_check'];
      if ((formData.job_intents || []).some(i => viIntents.includes(i))) {
        formData.requires_vehicle_inspection = true;
      }
    }
    // Customer concern is required for diagnostic VI
    if (formData.requires_diagnostic_vi && !formData.customer_concern.trim()) {
      setError("Customer Reported Concern is required for Diagnostic Inspection");
      return;
    }

    setSubmitting(true);
    try {
      // Clean up form data - convert empty strings to null for optional int fields
      const cleanedData = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        engine_capacity: formData.engine_capacity ? parseInt(formData.engine_capacity) : null
      };
      const res = await axios.post(`${API}/foh/intake/send-to-workshop`, cleanedData);
      // Store created job to show success view with tech link
      setCreatedJob({
        ...res.data.job,
        message: res.data.message
      });
    } catch (err) {
      setError(getErrorMessage(err, "Failed to send to workshop"));
    } finally {
      setSubmitting(false);
    }
  };

  // Copy link to clipboard
  const copyTechLink = () => {
    if (createdJob) {
      const link = `${window.location.origin}/tech/job/${createdJob.id}/start`;
      navigator.clipboard.writeText(link).then(() => {
        toast.success("Link copied to clipboard!", { duration: 2000 });
      }).catch(() => {
        toast.error("Failed to copy link");
      });
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  // Success view after job creation
  if (createdJob) {
    const techLink = `${window.location.origin}/tech/job/${createdJob.id}/start`;
    return (
      <div className="min-h-screen bg-slate-100">
        <header className="bg-white border-b px-6 py-4">
          <div className="max-w-lg mx-auto">
            <h1 className="font-bold text-xl text-slate-800">Vehicle Sent to Workshop</h1>
          </div>
        </header>
        <main className="p-4">
          <div className="max-w-lg mx-auto">
            <Card>
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-green-700">Success!</h2>
                  <p className="text-slate-600 mt-1">{createdJob.message}</p>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-slate-500">Registration:</span>
                    <span className="font-semibold">{createdJob.reg || '-'}</span>
                    <span className="text-slate-500">Vehicle:</span>
                    <span className="font-medium">{formatVehicleNameFromObject(createdJob)}</span>
                    {createdJob.gh_job_ref && typeof createdJob.gh_job_ref === 'string' && (
                      <>
                        <span className="text-slate-500">Job Ref:</span>
                        <span className="font-medium">{createdJob.gh_job_ref}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Tech Link Section */}
                <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-4 mb-4">
                  <Label className="text-blue-800 font-semibold text-sm">Technician Link (copy to Garage Hive)</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input 
                      value={techLink}
                      readOnly
                      className="bg-white text-sm font-mono flex-1"
                      onClick={(e) => e.target.select()}
                      data-testid="tech-link-input"
                    />
                    <Button 
                      type="button"
                      onClick={copyTechLink}
                      className="bg-blue-600 hover:bg-blue-700 shrink-0"
                      data-testid="copy-link-btn"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Paste this link into Garage Hive. Technicians can click it to go directly to this inspection.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setCreatedJob(null);
                      // Reset form
                      setFormData({
                        reg: "", make: "", model: "", colour: "", fuel_type: "",
                        year: "", engine_capacity: "", tax_status: "", mot_status: "",
                        requires_diagnostic_vi: false, requires_vehicle_inspection: false,
                        requires_mot_test: false,
                        customer_concern: "", vehicle_on_site: true, key_tag_number: "",
                        gh_job_ref: "", customer_name: "", customer_phone: ""
                      });
                      setLookupSuccess(false);
                    }}
                    data-testid="create-another-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create Another
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={() => navigate("/app/foh")}
                    data-testid="back-to-dashboard-btn"
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-lg mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/foh")} className="mb-2">
            ← Back to Dashboard
          </Button>
          <h1 className="font-bold text-xl text-slate-800">Inspection Intake</h1>
          <p className="text-sm text-slate-500">Send to Workshop</p>
        </div>
      </header>

      <div className="max-w-lg mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Vehicle Details</CardTitle>
            <CardDescription>Enter the vehicle requiring inspection</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Vehicle Registration with Lookup */}
              <div>
                <Label htmlFor="reg" className="font-medium">Vehicle Registration *</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="reg"
                    value={formData.reg}
                    onChange={(e) => handleChange("reg", e.target.value.toUpperCase())}
                    placeholder="e.g. AB12 CDE"
                    className="uppercase flex-1"
                    data-testid="intake-reg"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleRegLookup}
                    disabled={lookupLoading || !formData.reg.trim()}
                    className="whitespace-nowrap"
                    data-testid="reg-lookup-btn"
                  >
                    {lookupLoading ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Looking up...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-1" />
                        Lookup
                      </>
                    )}
                  </Button>
                </div>
                {lookupSuccess && (
                  <div className="mt-1">
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" /> Vehicle details found and populated
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Note: DVLA does not provide model data - please enter manually
                    </p>
                  </div>
                )}
                {lookupError && (
                  <p className="text-sm text-amber-600 mt-1">{lookupError}</p>
                )}
              </div>

              {/* Make & Model */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="make" className="font-medium">Make *</Label>
                  <Input
                    id="make"
                    value={formData.make}
                    onChange={(e) => handleChange("make", e.target.value)}
                    placeholder="e.g. Ford"
                    className="mt-1"
                    data-testid="intake-make"
                  />
                </div>
                <div>
                  <Label htmlFor="model" className="font-medium">Model *</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => handleChange("model", e.target.value)}
                    placeholder="e.g. Focus"
                    className="mt-1"
                    data-testid="intake-model"
                  />
                </div>
                <div>
                  <Label htmlFor="year" className="font-medium">Year *</Label>
                  <Input
                    id="year"
                    type="number"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={formData.year}
                    onChange={(e) => handleChange("year", e.target.value)}
                    placeholder="e.g. 2019"
                    className="mt-1"
                    data-testid="intake-year"
                  />
                </div>
              </div>
              
              {/* Additional Vehicle Info (from lookup) */}
              {(formData.colour || formData.fuel_type) && (
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 mb-2 font-medium">Vehicle Details (from DVLA)</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {formData.colour && (
                      <div>
                        <span className="text-slate-500">Colour:</span>{" "}
                        <span className="font-medium">{formData.colour}</span>
                      </div>
                    )}
                    {formData.fuel_type && (
                      <div>
                        <span className="text-slate-500">Fuel:</span>{" "}
                        <span className="font-medium">{formData.fuel_type}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Separator />

              {/* Job Intent Selector — Multi-select visit intents */}
              <div>
                <Label className="font-medium text-base">Visit Intent *</Label>
                <p className="text-sm text-slate-500 mb-3">Select all that apply — this controls workflows, pricing scope, and MOT readiness</p>
                
                {/* Primary intents */}
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Primary</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { id: 'mot', label: 'MOT', color: 'teal' },
                    { id: 'service', label: 'Service', color: 'emerald' },
                    { id: 'safety_inspection', label: 'Safety & Reliability Inspection', color: 'blue' },
                    { id: 'diagnostic', label: 'Diagnostic', color: 'purple' },
                  ].map(opt => {
                    const isSelected = (formData.job_intents || []).includes(opt.id);
                    const colorMap = {
                      emerald: 'border-emerald-500 bg-emerald-50 text-emerald-700',
                      blue: 'border-blue-500 bg-blue-50 text-blue-700',
                      teal: 'border-teal-500 bg-teal-50 text-teal-700',
                      purple: 'border-purple-500 bg-purple-50 text-purple-700',
                    };
                    return (
                      <button key={opt.id} type="button"
                        onClick={() => {
                          const current = formData.job_intents || [];
                          const updated = isSelected ? current.filter(i => i !== opt.id) : [...current, opt.id];
                          handleChange('job_intents', updated);
                          // Auto-set legacy flags for backwards compatibility
                          if (opt.id === 'diagnostic') handleChange('requires_diagnostic_vi', !isSelected);
                          if (['service', 'safety_inspection'].includes(opt.id)) handleChange('requires_vehicle_inspection', updated.some(i => ['service', 'safety_inspection', 'pre_mot_check'].includes(i)));
                        }}
                        className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                          isSelected ? colorMap[opt.color] : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                        data-testid={`intent-${opt.id}`}
                      >{opt.label}</button>
                    );
                  })}
                </div>
                
                {/* MOT Related */}
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">MOT Related</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { id: 'mot_failure_repair', label: 'MOT Failure Repair', color: 'border-red-400 bg-red-50 text-red-700' },
                    { id: 'mot_retest', label: 'MOT Retest', color: 'border-orange-400 bg-orange-50 text-orange-700' },
                    { id: 'pre_mot_check', label: 'Pre-MOT Check', color: 'border-cyan-400 bg-cyan-50 text-cyan-700' },
                  ].map(opt => {
                    const isSelected = (formData.job_intents || []).includes(opt.id);
                    return (
                      <button key={opt.id} type="button"
                        onClick={() => {
                          const current = formData.job_intents || [];
                          const updated = isSelected ? current.filter(i => i !== opt.id) : [...current, opt.id];
                          handleChange('job_intents', updated);
                          if (opt.id === 'pre_mot_check') handleChange('requires_vehicle_inspection', updated.some(i => ['service', 'safety_inspection', 'pre_mot_check'].includes(i)));
                        }}
                        className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${isSelected ? opt.color : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                        data-testid={`intent-${opt.id}`}
                      >{opt.label}</button>
                    );
                  })}
                </div>
                
                {/* Specific work */}
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Specific Work</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { id: 'tyres', label: 'Tyres' },
                    { id: 'brakes', label: 'Brakes' },
                    { id: 'air_conditioning', label: 'Air Conditioning' },
                    { id: 'customer_concern', label: 'Customer Concern' },
                    { id: 'other', label: 'Other' },
                  ].map(opt => {
                    const isSelected = (formData.job_intents || []).includes(opt.id);
                    return (
                      <button key={opt.id} type="button"
                        onClick={() => {
                          const current = formData.job_intents || [];
                          const updated = isSelected ? current.filter(i => i !== opt.id) : [...current, opt.id];
                          handleChange('job_intents', updated);
                          if (opt.id === 'customer_concern') handleChange('requires_diagnostic_vi', updated.includes('diagnostic') || updated.includes('customer_concern'));
                        }}
                        className={`px-3 py-1.5 rounded-full border-2 text-sm font-medium transition-all ${isSelected ? 'border-slate-500 bg-slate-100 text-slate-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                        data-testid={`intent-${opt.id}`}
                      >{opt.label}</button>
                    );
                  })}
                </div>
                
                {/* MOT Workflow indicator */}
                {(formData.job_intents || []).some(i => ['mot', 'mot_failure_repair', 'mot_retest'].includes(i)) && (
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center gap-2 mt-2">
                    <AlertTriangle className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <span className="text-sm text-teal-800">MOT workflow will be activated — MOT status will appear on pricing page</span>
                  </div>
                )}
                
                {/* No VI for MOT-only */}
                {(formData.job_intents || []).length > 0 && 
                 (formData.job_intents || []).every(i => ['mot', 'mot_failure_repair', 'mot_retest'].includes(i)) && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 mt-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-xs text-amber-700">MOT-only job — Standard VI will not be auto-created</span>
                  </div>
                )}
              </div>

              <Separator />

              {/* Inspection Type Selection - Multi-select checkboxes */}
              <div>
                <Label className="font-medium text-base">Inspection Request *</Label>
                <p className="text-sm text-slate-500 mb-3">Select one or more inspection types required</p>
                
                <div className="space-y-3">
                  {/* iter199af — MOT Test Checkbox.
                      MOT is a parallel service to VI/Diag — ticking it
                      creates an MCC allocation on the tester dash even
                      when Service and/or Diag are also ticked. The
                      three checkboxes are fully independent. */}
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.requires_mot_test
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => {
                      const next = !formData.requires_mot_test;
                      handleChange("requires_mot_test", next);
                      // Keep job_intents legacy field in sync so existing
                      // FOH dashboard pills (MOT / MOT+SERVICE etc) stay
                      // accurate.
                      const intents = formData.job_intents || [];
                      const withoutMot = intents.filter(i => i !== 'mot');
                      handleChange("job_intents", next ? [...withoutMot, 'mot'] : withoutMot);
                    }}
                    data-testid="intake-mot-test-checkbox"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        formData.requires_mot_test ? 'border-teal-500 bg-teal-500' : 'border-slate-300'
                      }`}>
                        {formData.requires_mot_test && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="font-medium">MOT Test</span>
                        <p className="text-sm text-slate-500">Sends an MCC task to the tester dashboard. Combine with Service/Diag below for a multi-allocation intake.</p>
                      </div>
                    </div>
                  </div>

                  {/* Diagnostic Inspection Checkbox */}
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.requires_diagnostic_vi 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleChange("requires_diagnostic_vi", !formData.requires_diagnostic_vi)}
                    data-testid="intake-diag-vi-checkbox"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        formData.requires_diagnostic_vi ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
                      }`}>
                        {formData.requires_diagnostic_vi && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="font-medium">Diagnostic Inspection (Diag VI)</span>
                        <p className="text-sm text-slate-500">Investigate a specific customer-reported concern</p>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Inspection Checkbox */}
                  <div 
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.requires_vehicle_inspection 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleChange("requires_vehicle_inspection", !formData.requires_vehicle_inspection)}
                    data-testid="intake-vi-checkbox"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        formData.requires_vehicle_inspection ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                      }`}>
                        {formData.requires_vehicle_inspection && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <span className="font-medium">Vehicle Inspection (VI – Safety & Reliability)</span>
                        <p className="text-sm text-slate-500">Full multi-point safety and reliability inspection</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Reported Concern - Only shown when Diagnostic VI is selected */}
              {formData.requires_diagnostic_vi && (
                <div className="border-l-4 border-purple-500 pl-4 bg-purple-50 p-4 rounded-r">
                  <Label htmlFor="customer_concern" className="font-medium text-purple-800">
                    Customer Reported Concern *
                  </Label>
                  <p className="text-sm text-purple-600 mb-2">
                    What issue has the customer reported? This will be shown to the technician.
                  </p>
                  <Textarea
                    id="customer_concern"
                    value={formData.customer_concern}
                    onChange={(e) => handleChange("customer_concern", e.target.value)}
                    placeholder="e.g. Customer reports oil smell and smoke from engine bay when driving"
                    className="mt-1 min-h-[100px] bg-white"
                    data-testid="intake-customer-concern"
                  />
                </div>
              )}

              {/* Vehicle Status & References */}
              <div className="grid grid-cols-3 gap-4">
                {/* Vehicle On Site Toggle - Prominent */}
                <div>
                  <Label className="font-medium">Vehicle On Site</Label>
                  <div 
                    className={`mt-1 p-3 rounded border-2 cursor-pointer transition-all ${
                      formData.vehicle_on_site 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-red-400 bg-red-50'
                    }`}
                    onClick={() => handleChange("vehicle_on_site", !formData.vehicle_on_site)}
                    data-testid="vehicle-on-site-toggle"
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${formData.vehicle_on_site ? 'text-green-700' : 'text-red-700'}`}>
                        {formData.vehicle_on_site ? '✓ Here' : '✗ Not Yet'}
                      </span>
                      <div className={`relative w-10 h-5 rounded-full transition-colors ${
                        formData.vehicle_on_site ? 'bg-green-500' : 'bg-red-400'
                      }`}>
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          formData.vehicle_on_site ? 'translate-x-5' : 'translate-x-0.5'
                        }`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Tag Number */}
                <div>
                  <Label htmlFor="key_tag" className="font-medium">Key Tag Number</Label>
                  <Input
                    id="key_tag"
                    value={formData.key_tag_number}
                    onChange={(e) => handleChange("key_tag_number", e.target.value)}
                    placeholder="e.g. KT-001"
                    className="mt-1"
                    data-testid="intake-key-tag"
                  />
                  <p className="text-xs text-slate-500 mt-1">Optional</p>
                </div>

                {/* GH Job Reference */}
                <div>
                  <Label htmlFor="gh_job_ref" className="font-medium">GH Job Reference</Label>
                  <Input
                    id="gh_job_ref"
                    value={formData.gh_job_ref}
                    onChange={(e) => handleChange("gh_job_ref", e.target.value)}
                    placeholder="e.g. SJ012345"
                    className="mt-1"
                    data-testid="intake-gh-job-ref"
                  />
                  <p className="text-xs text-slate-500 mt-1">Optional</p>
                </div>
              </div>

              <Separator />

              {/* Customer Details Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700">Customer Details (Optional)</h3>
                <p className="text-xs text-slate-500">These details will persist through the entire workflow</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name" className="font-medium">Customer Name</Label>
                    <Input
                      id="customer_name"
                      value={formData.customer_name}
                      onChange={(e) => handleChange("customer_name", e.target.value)}
                      placeholder="e.g. John Smith"
                      className="mt-1"
                      data-testid="intake-customer-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_phone" className="font-medium">Customer Phone</Label>
                    <Input
                      id="customer_phone"
                      value={formData.customer_phone}
                      onChange={(e) => handleChange("customer_phone", e.target.value)}
                      placeholder="e.g. 07123 456789"
                      className="mt-1"
                      data-testid="intake-customer-phone"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate("/app/foh")}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={submitting || (!formData.requires_diagnostic_vi && !formData.requires_vehicle_inspection)}
                  data-testid="send-to-workshop-btn"
                >
                  {submitting ? "Sending..." : "Send to Workshop"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== FOH INSPECTION REVIEW (Read-Only) ==============
const FOHInspectionReview = () => {
  const { inspectionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previouslyAdvised, setPreviouslyAdvised] = useState({});
  const [lookbackMonths, setLookbackMonths] = useState(6);
  const [declineHistory, setDeclineHistory] = useState([]);
  const [showDeclineHistory, setShowDeclineHistory] = useState(false);
  const [callCompanion, setCallCompanion] = useState(null);
  const [showCallCompanion, setShowCallCompanion] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  
  // Call Companion Compliance State
  const [callTone, setCallTone] = useState(null); // 'reassuring', 'neutral', 'direct'
  const [compliancePhases, setCompliancePhases] = useState({
    phase1: { completed: false, notes: '' },
    phase2: { completed: false, notes: '' },
    phase3: { completed: false, notes: '' },
    phase4: { completed: false, notes: '' },
    phase5: { completed: false, notes: '' },
    phase6: { completed: false, notes: '' },
    phase7: { completed: false, notes: '' }
  });
  const [currentPhase, setCurrentPhase] = useState(0); // 0 = tone selection, 1-7 = phases
  const [callOutcome, setCallOutcome] = useState({ redAuthorised: null, amberPercent: 0 });
  const [showComplianceWarning, setShowComplianceWarning] = useState(null);
  const [loadingCompanion, setLoadingCompanion] = useState(false);
  const [lockWarningDismissed, setLockWarningDismissed] = useState(false);
  const [showPremiumDiagReport, setShowPremiumDiagReport] = useState(false);
  const [statusPopup, setStatusPopup] = useState(null); // 'dangerous', 'red', 'amber', 'green', 'blue', 'mot'
  const [motSummary, setMotSummary] = useState(null);   // {failures, dangerous, major, minor, advisory, latest_test}
  const navigate = useNavigate();
  
  // Job Lock - acquire lock when viewing this inspection
  const jobId = data?.job?.id;
  const {
    isLocked: isJobLocked,
    isCurrentUser: hasLock,
    lockedByName,
    lockedAt,
    warning: lockWarning,
    loading: lockLoading
  } = useJobLock(jobId, 'inspection-review', !!jobId);

  useEffect(() => {
    axios.get(`${API}/foh/inspection/${inspectionId}`)
      .then(res => {
        setData(res.data);
        
        // Fetch decline history for this vehicle
        if (res.data.job?.reg) {
          axios.get(`${API}/foh/vehicle/${res.data.job.reg}/decline-history`)
            .then(histRes => setDeclineHistory(histRes.data.history || []))
            .catch(err => console.log("No decline history:", err));
        }

        // MOT summary tile — sourced FIRST from the in-house MOT outcome
        // captured today by the tester (job's mcc endpoint). DVSA's
        // live-lookup returns historical tests only and won't reflect
        // a freshly-completed in-house MOT, which is why the user kept
        // seeing "0 / Clean" on jobs that had just FAILED with multiple
        // defects (e.g. LB12PUX). Fall back to DVSA only when no in-
        // house outcome exists yet.
        const motJobId = res.data?.job?.id;
        if (motJobId) {
          const _countDefects = (defects) => {
            const counts = { failures: 0, dangerous: 0, major: 0, minor: 0, advisory: 0 };
            (defects || []).forEach(d => {
              // In-house outcomes carry ``severity`` (lower-case);
              // DVSA's live-lookup payload carries ``type`` (upper-case).
              const sev = (d.severity || d.type || "").toLowerCase();
              const t = (d.type || "").toUpperCase();
              if (sev === "dangerous" || t === "DANGEROUS") counts.dangerous += 1;
              else if (sev === "major" || t === "MAJOR") counts.major += 1;
              else if (sev === "minor" || t === "MINOR") counts.minor += 1;
              else if (sev === "advisory" || t === "ADVISORY" || t === "PASS ADVISORY") counts.advisory += 1;
              else if (t === "FAIL") counts.failures += 1;
            });
            return counts;
          };

          // 1) Try the in-house outcome first (today's captured test).
          axios.get(`${API}/jobs/${motJobId}/mcc`)
            .then(mccRes => {
              const out = mccRes.data || {};
              const defects = out.defects || [];
              if (!out.outcome_id || defects.length === 0) {
                // No in-house outcome — fall through to DVSA live below.
                return Promise.reject({ __fallthrough: true });
              }
              const counts = _countDefects(defects);
              const total = Object.values(counts).reduce((a, b) => a + b, 0);
              setMotSummary({
                ...counts,
                total,
                defects,
                source: "in_house",
                latest_test: {
                  testResult: out.test_result,
                  // In-house outcome has no completedDate/odometer surface
                  // these from the outcome record when needed.
                },
              });
            })
            .catch((err) => {
              if (!err?.__fallthrough) {
                // Surface non-fallthrough errors (auth, 5xx) but still
                // attempt DVSA below so the tile never gets stuck.
                console.warn("in-house MOT lookup failed:", err?.message);
              }
              // 2) Fall back to live DVSA history when no in-house result.
              return axios.post(`${API}/jobs/${motJobId}/mot-lookup-live`, {})
                .then(motRes => {
                  const latest = motRes.data?.latest_test;
                  if (!latest) { setMotSummary(null); return; }
                  const defects = latest.defects || latest.rfrAndComments || [];
                  const counts = _countDefects(defects);
                  const total = Object.values(counts).reduce((a, b) => a + b, 0);
                  setMotSummary({
                    ...counts,
                    total,
                    defects,
                    source: "dvsa_live",
                    latest_test: {
                      completedDate: latest.completedDate,
                      expiryDate: latest.expiryDate,
                      testResult: latest.testResult,
                      odometerValue: latest.odometerValue,
                      odometerUnit: latest.odometerUnit,
                    },
                  });
                })
                .catch(() => setMotSummary(null));
            });
        }
      })
      .catch(err => {
        alert(err.response?.data?.detail || "Failed to load inspection");
        navigate("/app/foh");
      })
      .finally(() => setLoading(false));
    
    // Fetch previously advised items
    axios.get(`${API}/admin/settings/lookback-period`)
      .then(res => {
        const months = res.data.lookback_months || 6;
        setLookbackMonths(months);
        return axios.get(`${API}/foh/previously-advised/${inspectionId}?lookback_months=${months}`);
      })
      .then(res => setPreviouslyAdvised(res.data.previously_advised || {}))
      .catch(err => console.log("No previous history:", err));
  }, [inspectionId, navigate]);

  // Load Call Companion data
  const loadCallCompanion = async () => {
    setLoadingCompanion(true);
    try {
      const res = await axios.get(`${API}/foh/inspection/${inspectionId}/call-companion`);
      setCallCompanion(res.data);
      setShowCallCompanion(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to load Call Companion");
    } finally {
      setLoadingCompanion(false);
    }
  };

  const handleCreateYestimate = async () => {
    try {
      const res = await axios.post(`${API}/foh/yestimate`, { inspection_id: inspectionId });
      navigate(`/app/foh/yestimate/${res.data.id}/pricing`);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create Yestimate");
    }
  };

  if (loading) return <div className="p-8 text-center">Loading inspection...</div>;
  if (!data) return <div className="p-8 text-center">Inspection not found</div>;

  const sectionNames = {
    in_car_checks: "In-Car Checks",
    under_bonnet: "Under-Bonnet",
    brakes_wheels: "Brakes & Wheels",
    tyres: "Tyres",
    suspension_steering: "Suspension & Steering",
    exhaust_underside: "Exhaust & Underside",
    lights_electrical: "Lights & Electrical",
    final_checks: "Final Checks"
  };

  const getStatusColor = (status) => {
    const colors = {
      red: "bg-red-500",
      amber: "bg-amber-500",
      green: "bg-green-500",
      blue: "bg-blue-500",
      dangerous: "bg-black"
    };
    return colors[status] || "bg-gray-300";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/foh")} className="mb-2">
            ← Back to Queue
          </Button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="font-bold text-xl text-slate-800">{data.job?.reg || 'N/A'}</h1>
              <p className="text-slate-500">{formatVehicleNameFromObject(data.job)} • {data.inspection?.mileage || 0} miles</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Print Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="border-slate-200 text-slate-700 hover:bg-slate-50 print:hidden"
                data-testid="print-inspection-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Print
              </Button>
              {/* Download VI PDF — Customer variant */}
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/i/${inspectionId}/pdf?variant=customer`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    const reg = (data.job?.reg || 'report').replace(/\s+/g, '');
                    const date = new Date().toISOString().slice(0,10).replace(/-/g,'');
                    link.setAttribute('download', `VI_${reg}_${date}_customer.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (err) {
                    // Try to surface the real error so users (and the dev console) can
                    // see status/detail instead of a generic message.
                    let msg = "Failed to download Customer VI PDF";
                    try {
                      const status = err?.response?.status;
                      if (status) msg += ` (HTTP ${status})`;
                      // The error body is a Blob because we asked for responseType:blob.
                      // Decode it to text so we can read the FastAPI 'detail' field.
                      const blob = err?.response?.data;
                      if (blob && typeof blob.text === 'function') {
                        const txt = await blob.text();
                        if (txt) {
                          try {
                            const j = JSON.parse(txt);
                            if (j?.detail) msg += `: ${j.detail}`;
                          } catch {
                            msg += `: ${txt.slice(0, 200)}`;
                          }
                        }
                      } else if (err?.message) {
                        msg += `: ${err.message}`;
                      }
                    } catch { /* ignore */ }
                    console.error("Customer VI PDF download failed:", err);
                    alert(msg);
                  }
                }}
                className="border-green-200 text-green-700 hover:bg-green-50 print:hidden"
                data-testid="download-vi-pdf-customer-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                VI PDF (Customer)
              </Button>
              {/* Download VI PDF — Archive variant (Garage Hive copy) */}
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/i/${inspectionId}/pdf?variant=archive`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    const reg = (data.job?.reg || 'report').replace(/\s+/g, '');
                    const date = new Date().toISOString().slice(0,10).replace(/-/g,'');
                    link.setAttribute('download', `VI_${reg}_${date}_archive.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (err) {
                    alert("Failed to download Archive VI PDF");
                  }
                }}
                className="border-slate-300 text-slate-700 hover:bg-slate-50 print:hidden"
                data-testid="download-vi-pdf-archive-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                VI PDF (Archive)
              </Button>
              {/* Download Photo ZIP — every photo on file (arrival + per-item) */}
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/i/${inspectionId}/photos.zip`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                    const link = document.createElement('a');
                    link.href = url;
                    const reg = (data.job?.reg || 'report').replace(/\s+/g, '');
                    const date = new Date().toISOString().slice(0,10).replace(/-/g,'');
                    link.setAttribute('download', `VI_${reg}_${date}_photos.zip`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (err) {
                    alert("Failed to download Photo ZIP");
                  }
                }}
                className="border-blue-200 text-blue-700 hover:bg-blue-50 print:hidden"
                data-testid="download-vi-photo-zip-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Photo ZIP
              </Button>
              {/* Download Diagnostic PDF - only show if diagnostic exists */}
              {data.diagnostic_info && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/jobs/${data.job?.id}/diagnostic-report/pdf`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `Diagnostic_Report_${data.job?.reg || 'report'}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (err) {
                      alert("Failed to download Diagnostic PDF");
                    }
                  }}
                  className="border-purple-200 text-purple-700 hover:bg-purple-50 print:hidden"
                  data-testid="download-diag-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Diag PDF
                </Button>
              )}
              {/* Premium Diagnostic Report Button */}
              {data.diagnostic_info && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPremiumDiagReport(true)}
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 print:hidden"
                  data-testid="premium-diag-report-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Premium Report
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={loadCallCompanion}
                disabled={loadingCompanion}
                className="border-blue-200 text-blue-700 hover:bg-blue-50 print:hidden"
                data-testid="call-companion-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                {loadingCompanion ? "Loading..." : "Call Companion"}
              </Button>
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 print:hidden">
                <Lock className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-700 font-medium">Read-Only View</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        {/* Job Lock Warning Banner */}
        {isJobLocked && !hasLock && !lockWarningDismissed && (
          <LockWarningBanner 
            lockedByName={lockedByName}
            lockedAt={lockedAt}
            onDismiss={() => setLockWarningDismissed(true)}
          />
        )}
        
        {/* Active Lock Indicator - shows when current user has the lock */}
        {hasLock && !lockLoading && (
          <ActiveLockBanner screen="Inspection Review" />
        )}

        {/* Technician Attribution */}
        <Card className="mb-6 bg-slate-100 border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-600">
              Inspection completed by <strong>{data.technician.name}</strong> on {new Date(data.inspection.submitted_at).toLocaleDateString()}
            </p>
          </CardContent>
        </Card>

        {/* DIAGNOSTIC FINDINGS SECTION - Show if diagnostic was completed */}
        {data.diagnostic_info && (
          <Card className="mb-6 border-2 border-purple-300 bg-purple-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                <FileText className="w-5 h-5" />
                Diagnostic Findings
                <Badge className="bg-red-500 text-white ml-2">PRICING REQUIRED</Badge>
              </CardTitle>
              <CardDescription className="text-purple-600">
                Diagnostic completed by {data.diagnostic_info.diagnostic_submitted_by_name} on {new Date(data.diagnostic_info.diagnostic_submitted_at).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Customer Concern */}
              <div className="mb-4 p-3 bg-white rounded border">
                <p className="text-xs font-semibold text-purple-600 uppercase mb-1">Customer Reported Concern</p>
                <p className="text-slate-700">{data.diagnostic_info.customer_concern}</p>
              </div>
              
              {/* Polished Report */}
              <div className="mb-4 p-4 bg-white rounded border">
                <p className="text-xs font-semibold text-purple-600 uppercase mb-2">AI-Polished Diagnostic Report</p>
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {data.diagnostic_info.diagnostic_polished_report}
                </pre>
              </div>

              {/* Warning */}
              <div className="p-3 bg-red-100 border border-red-200 rounded flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Mandatory Pricing Required</p>
                  <p className="text-sm text-red-700">
                    Diagnostic rectification must be added to pricing as a RED item. 
                    SI Email cannot be generated until this is priced.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Previously Declined Maintenance Items Alert */}
        {declineHistory.length > 0 && (
          <Card className="mb-6 border-2 border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <AlertCircle className="w-5 h-5" />
                  Previously Declined Maintenance ({declineHistory.length})
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowDeclineHistory(!showDeclineHistory)}
                >
                  {showDeclineHistory ? "Hide" : "Show"} Details
                </Button>
              </div>
              <p className="text-sm text-amber-700">
                This vehicle has previously declined maintenance items — review for continuity
              </p>
            </CardHeader>
            
            {showDeclineHistory && (
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {declineHistory.map((decline, idx) => (
                    <div key={idx} className="p-3 bg-white rounded border border-amber-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium text-slate-800">{decline.item_label}</span>
                          <span className="ml-2 text-sm text-amber-600">
                            £{decline.price_declined?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(decline.declined_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric"
                          })}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        <span className="text-amber-700">Reason:</span> {decline.decline_reason_label}
                      </div>
                      {decline.decline_notes && (
                        <p className="mt-1 text-xs text-slate-500 italic">
                          "{decline.decline_notes}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-600 mt-3">
                  * This information is provided for continuity and liability protection
                </p>
              </CardContent>
            )}
          </Card>
        )}

        {/* Status Summary - Clickable Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card 
            className="bg-black border-black cursor-pointer hover:ring-2 hover:ring-black hover:ring-offset-2 transition-all"
            onClick={() => data.status_counts.dangerous > 0 && setStatusPopup('dangerous')}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{data.status_counts.dangerous || 0}</div>
              <div className="text-sm text-gray-300">Dangerous</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-red-50 border-red-200 cursor-pointer hover:ring-2 hover:ring-red-400 hover:ring-offset-2 transition-all"
            onClick={() => data.status_counts.red > 0 && setStatusPopup('red')}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-700">{data.status_counts.red}</div>
              <div className="text-sm text-red-600">Requires Attention</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-amber-50 border-amber-200 cursor-pointer hover:ring-2 hover:ring-amber-400 hover:ring-offset-2 transition-all"
            onClick={() => data.status_counts.amber > 0 && setStatusPopup('amber')}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">{data.status_counts.amber}</div>
              <div className="text-sm text-amber-600">Advisory</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-green-50 border-green-200 cursor-pointer hover:ring-2 hover:ring-green-400 hover:ring-offset-2 transition-all"
            onClick={() => data.status_counts.green > 0 && setStatusPopup('green')}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{data.status_counts.green}</div>
              <div className="text-sm text-green-600">Passed</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-blue-50 border-blue-200 cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-offset-2 transition-all"
            onClick={() => data.status_counts.blue > 0 && setStatusPopup('blue')}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{data.status_counts.blue}</div>
              <div className="text-sm text-blue-600">N/A</div>
            </CardContent>
          </Card>
          {/* MOT tile — at-a-glance DVSA latest-test breakdown */}
          <Card
            className="bg-teal-50 border-teal-200 cursor-pointer hover:ring-2 hover:ring-teal-400 hover:ring-offset-2 transition-all"
            onClick={() => motSummary && motSummary.total > 0 && setStatusPopup('mot')}
            data-testid="mot-tile"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-teal-700" data-testid="mot-tile-total">
                {motSummary ? motSummary.total : '–'}
              </div>
              <div className="text-sm text-teal-700 font-medium">MOT</div>
              {motSummary && motSummary.total > 0 ? (
                <div className="text-[10px] text-teal-700 mt-1 flex flex-wrap justify-center gap-x-1 leading-tight">
                  {motSummary.failures > 0 && <span><b>{motSummary.failures}</b>F</span>}
                  {motSummary.dangerous > 0 && <span><b>{motSummary.dangerous}</b>D</span>}
                  {motSummary.major > 0 && <span><b>{motSummary.major}</b>Maj</span>}
                  {motSummary.minor > 0 && <span><b>{motSummary.minor}</b>Min</span>}
                  {motSummary.advisory > 0 && <span><b>{motSummary.advisory}</b>Adv</span>}
                </div>
              ) : (
                <div className="text-[10px] text-teal-600 mt-1">
                  {motSummary === null ? 'Loading…' : 'Clean'}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Status Items Popup Modal */}
        {statusPopup && (
          <div 
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 cursor-pointer" 
            onClick={() => setStatusPopup(null)}
            data-testid="status-popup-overlay"
          >
            <div 
              className={`bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden cursor-default ${
                statusPopup === 'dangerous' ? 'border-4 border-black' :
                statusPopup === 'red' ? 'border-4 border-red-500' :
                statusPopup === 'amber' ? 'border-4 border-amber-500' :
                statusPopup === 'green' ? 'border-4 border-green-500' :
                statusPopup === 'mot' ? 'border-4 border-teal-500' :
                'border-4 border-blue-500'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`px-6 py-4 ${
                statusPopup === 'dangerous' ? 'bg-black text-white' :
                statusPopup === 'red' ? 'bg-red-500 text-white' :
                statusPopup === 'amber' ? 'bg-amber-500 text-white' :
                statusPopup === 'green' ? 'bg-green-500 text-white' :
                statusPopup === 'mot' ? 'bg-teal-600 text-white' :
                'bg-blue-500 text-white'
              }`}>
                <h3 className="font-bold text-lg">
                  {statusPopup === 'dangerous' ? 'Dangerous Items' :
                   statusPopup === 'red' ? 'Items Requiring Attention' :
                   statusPopup === 'amber' ? 'Advisory Items' :
                   statusPopup === 'green' ? 'Passed Items' :
                   statusPopup === 'mot' ? 'Latest MOT (DVSA)' :
                   'N/A Items'}
                </h3>
                <p className="text-xs opacity-80 mt-1">
                  {statusPopup === 'mot' && motSummary?.latest_test
                    ? `${(motSummary.latest_test.completedDate || '').slice(0,10)} · ${motSummary.latest_test.testResult || ''} · ${motSummary.latest_test.odometerValue || ''} ${motSummary.latest_test.odometerUnit || ''}`
                    : 'Click outside to close'}
                </p>
              </div>
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {statusPopup === 'mot' ? (
                  <>
                    {/* MOT defects breakdown — pulled from latest DVSA test */}
                    {(motSummary?.defects || []).length === 0 ? (
                      <p className="text-sm text-slate-600 italic">No defects recorded on the latest MOT.</p>
                    ) : (
                      (motSummary.defects || []).map((d, idx) => {
                        // Accept BOTH the DVSA live-lookup wire shape
                        // (``type`` uppercase) and the in-house outcome
                        // shape (``severity`` lowercase) so the popup
                        // colour-codes correctly regardless of source.
                        const sev = (d.severity || d.type || '').toLowerCase();
                        const displayType = (
                          d.type || (d.severity ? d.severity.charAt(0).toUpperCase() + d.severity.slice(1) : '—')
                        );
                        const colour =
                          sev === 'fail' || sev === 'dangerous' ? 'bg-red-50 border-red-300 text-red-800' :
                          sev === 'major' ? 'bg-red-50 border-red-200 text-red-700' :
                          sev === 'minor' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                          'bg-amber-50/60 border-amber-100 text-amber-700';
                        return (
                          <div
                            key={`mot-d-${idx}`}
                            className={`p-3 mb-2 rounded-lg border ${colour}`}
                            data-testid={`mot-defect-${idx}`}
                          >
                            <div className="flex items-start gap-2">
                              <Badge variant="outline" className="shrink-0 text-xs font-bold">
                                {displayType}
                              </Badge>
                              <span className="text-sm">{d.text || d.defect_text || '(no description)'}</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </>
                ) : (
                  Object.entries(data.sections || {}).flatMap(([sectionId, sectionItems]) => 
                  (sectionItems || [])
                    .filter(item => item.status === statusPopup)
                    .map(item => (
                      <div 
                        key={item.id || item.item_key}
                        className={`p-3 mb-2 rounded-lg border ${
                          statusPopup === 'dangerous' ? 'bg-black/5 border-black/20' :
                          statusPopup === 'red' ? 'bg-red-50 border-red-200' :
                          statusPopup === 'amber' ? 'bg-amber-50 border-amber-200' :
                          statusPopup === 'green' ? 'bg-green-50 border-green-200' :
                          'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-800">{item.label || item.item_key}</p>
                            <p className="text-xs text-slate-500">{sectionNames[sectionId] || sectionId}</p>
                          </div>
                          {/* Render one chip per affected location (multi-select aware); fall back to legacy single side */}
                          {(() => {
                            const labelMap = {
                              ns: 'Nearside', os: 'Offside', both: 'Both Sides',
                              front_ns: 'Front NS', front_os: 'Front OS', front_both: 'Both Front',
                              rear_ns: 'Rear NS', rear_os: 'Rear OS', rear_both: 'Both Rear',
                              all: 'All Corners', nsf: 'NSF', osf: 'OSF', nsr: 'NSR', osr: 'OSR',
                            };
                            const locs = Array.isArray(item.affected_locations) && item.affected_locations.length > 0
                              ? item.affected_locations
                              : (item.affected_side ? [item.affected_side] : []);
                            return locs.map(loc => {
                              const lc = String(loc).toLowerCase();
                              return (
                                <Badge key={loc} variant="outline" className="text-xs">
                                  {labelMap[lc] || String(loc).toUpperCase()}
                                </Badge>
                              );
                            });
                          })()}
                          {item.affected_position && (
                            <Badge variant="outline" className="text-xs bg-purple-50 border-purple-300 text-purple-700">
                              {item.affected_position === 'inner' ? 'Inner' :
                               item.affected_position === 'outer' ? 'Outer' :
                               'Inner & Outer'}
                            </Badge>
                          )}
                        </div>
                        {/* Dangerous Reasons */}
                        {statusPopup === 'dangerous' && item.danger_reasons && item.danger_reasons.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-red-700 mb-1">Dangerous Reasons:</p>
                            <div className="flex flex-wrap gap-1">
                              {item.danger_reasons.map((reason, idx) => (
                                <Badge key={idx} className="bg-red-600 text-white text-xs">
                                  {reason}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Danger Categories */}
                        {statusPopup === 'dangerous' && item.danger_categories && item.danger_categories.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {item.danger_categories.map((cat, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs border-red-400 text-red-700">
                                {cat.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {item.notes && (
                          <p className="text-sm text-slate-600 mt-2 italic">"{item.notes}"</p>
                        )}
                        {item.selected_issues && item.selected_issues.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {item.selected_issues.map((issue, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {issue}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                ))}
                {statusPopup !== 'mot' && Object.entries(data.sections || {}).flatMap(([_, sectionItems]) => 
                  (sectionItems || []).filter(item => item.status === statusPopup)
                ).length === 0 && (
                  <p className="text-center text-slate-500 py-4">No items with this status</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Arrival Photos - Lazy Loaded */}
        {data.arrival_photos?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Arrival Photos (4 Corners)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {data.arrival_photos.map((photo, idx) => (
                  <LazyArrivalPhoto 
                    key={idx}
                    inspectionId={inspectionId}
                    corner={photo.corner}
                    hasPhoto={photo.has_photo}
                    onPhotoClick={setEnlargedPhoto}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inspection Items by Section */}
        {Object.entries(data.sections).map(([sectionId, items]) => (
          <Card key={sectionId} className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{sectionNames[sectionId] || sectionId}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {items.map(item => {
                const prevAdvised = previouslyAdvised[item.id];
                return (
                  <div key={item.id} className="border rounded p-3 bg-white">
                    <div className="flex items-start gap-3">
                      <div className={`w-3 h-3 rounded-full mt-1 ${getStatusColor(item.status)}`} />
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{item.item_label || item.label || item.item_key}</p>
                        
                        {/* Previously Advised Indicator - Neutral, factual styling */}
                        {prevAdvised && (
                          <div className="mt-1 flex items-center gap-1.5 text-xs">
                            <History className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-500">
                              Previously advised on {new Date(prevAdvised.previous_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                              {prevAdvised.has_progressed && (
                                <span className="ml-1 text-slate-600">— condition has progressed</span>
                              )}
                            </span>
                          </div>
                        )}
                        
                        {item.notes && (
                          <p className="text-sm text-slate-600 mt-1 bg-slate-50 p-2 rounded">{item.notes}</p>
                        )}
                        {item.measurement_value && (
                          <p className="text-sm text-slate-500 mt-1">
                            Measurement: <strong>{item.measurement_value} {item.measurement_unit}</strong>
                          </p>
                        )}
                        
                        {/* Dangerous Item Justification - Show why tech marked as dangerous */}
                        {item.status === "dangerous" && (
                          <div className="mt-2 bg-black/5 border border-black/20 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-black" />
                              <span className="text-sm font-semibold text-black">Dangerous Designation</span>
                            </div>
                            
                            {/* Risk Categories */}
                            {item.danger_categories?.length > 0 && (
                              <div className="mb-2">
                                <p className="text-xs font-medium text-slate-600 mb-1">Risk Category:</p>
                                <div className="flex flex-wrap gap-1">
                                  {item.danger_categories.map((cat, idx) => (
                                    <span key={idx} className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                                      {cat}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Danger Reasons / Justification */}
                            {item.danger_reasons?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-slate-600 mb-1">Technician Justification:</p>
                                <ul className="text-sm text-slate-700 space-y-0.5">
                                  {item.danger_reasons.map((reason, idx) => (
                                    <li key={idx} className="flex items-start gap-1.5">
                                      <span className="text-red-500 mt-0.5">•</span>
                                      <span>{reason}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Lazy-loaded item photos */}
                        {item.has_photos && (
                          <LazyItemPhotos 
                            inspectionId={inspectionId}
                            itemId={item.id}
                            photoCount={item.photo_count}
                            onPhotoClick={setEnlargedPhoto}
                          />
                        )}
                        
                        {/* Wheel Torque FOH Visibility — internal use only */}
                        {item.item_key === "wheel_torque_confirmation" && (
                          <div className="mt-2 p-3 bg-slate-100 rounded-lg border border-slate-200" data-testid="torque-foh-summary">
                            <div className="flex items-center gap-2 mb-1">
                              <Wrench className="w-4 h-4 text-slate-500" />
                              <span className="text-sm font-semibold text-slate-700">Wheel Torque</span>
                            </div>
                            {item.torque_manufacturer_spec && (
                              <p className="text-sm text-slate-600">
                                Torque setting provided: <strong>{item.torque_applied || item.torque_manufacturer_spec} Nm</strong>
                                {item.torque_confidence === 'high' && <span className="ml-1 text-xs text-green-600">(Manufacturer)</span>}
                                {item.torque_confidence === 'medium' && <span className="ml-1 text-xs text-amber-600">(Estimated)</span>}
                                {item.torque_confidence === 'low' && <span className="ml-1 text-xs text-red-600">(Default)</span>}
                              </p>
                            )}
                            <p className="text-sm text-slate-600">
                              Technician confirmation: {item.torque_confirmed 
                                ? <strong className="text-green-700">Yes</strong>
                                : item.torque_unable 
                                  ? <strong className="text-red-600">Unable</strong>
                                  : item.torque_unable_to_check 
                                    ? <strong className="text-amber-600">Unable to check</strong>
                                    : <strong className="text-amber-600">Not confirmed</strong>
                              }
                            </p>
                          </div>
                        )}
                        
                        {/* Wiper pair replacement recommendation */}
                        {(item.item_key === "front_wiper_left" || item.item_key === "front_wiper_right") && 
                         (item.status === "amber" || item.status === "red") && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2" data-testid="wiper-pair-note">
                            <AlertTriangle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <span className="text-xs text-blue-700 font-medium">Front blades recommended as a pair for even wipe performance</span>
                          </div>
                        )}
                        
                        {/* Wiper blade type display (legacy single items) */}
                        {item.wiper_blade_type && !item.wiper_data && (
                          <div className="mt-1">
                            <span className="text-xs text-slate-500">Blade type: <strong className="text-slate-700">{
                              item.wiper_blade_type === 'conventional' ? 'Conventional / Frame' :
                              item.wiper_blade_type === 'beam_aero' ? 'Beam / Aero' :
                              item.wiper_blade_type === 'hybrid' ? 'Hybrid' :
                              item.wiper_blade_type === 'rear_specific' ? 'Rear Specific' :
                              'Unknown'
                            }</strong></span>
                          </div>
                        )}
                        
                        {/* Wiper & Washer System — detailed sub-component breakdown for FOH */}
                        {item.wiper_data && (
                          <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-testid="wiper-foh-summary">
                            {[
                              { key: "front_left", label: "Front L" },
                              { key: "front_right", label: "Front R" },
                              { key: "rear", label: "Rear" },
                              { key: "washers", label: "Washers" },
                              { key: "arm", label: "Arm/Mech" },
                            ].map(comp => {
                              const sub = item.wiper_data[comp.key];
                              if (!sub) return null;
                              if (sub.not_fitted) return (
                                <div key={comp.key} className="flex items-center gap-2 text-xs">
                                  <span className="font-medium text-slate-600 w-16">{comp.label}:</span>
                                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">N/F</span>
                                </div>
                              );
                              if (!sub.status) return null;
                              const bladeTypeLabel = sub.blade_type ? (
                                sub.blade_type === 'conventional' ? 'Conv' :
                                sub.blade_type === 'beam_aero' ? 'Aero' :
                                sub.blade_type === 'hybrid' ? 'Hybrid' :
                                sub.blade_type === 'rear_specific' ? 'Rear' : '?'
                              ) : null;
                              return (
                                <div key={comp.key} className="flex items-center gap-2 text-xs flex-wrap">
                                  <span className="font-medium text-slate-600 w-16">{comp.label}:</span>
                                  <span className={`px-1.5 py-0.5 rounded font-bold ${
                                    sub.status === 'red' ? 'bg-red-500 text-white' :
                                    sub.status === 'amber' ? 'bg-amber-500 text-white' :
                                    'bg-green-500 text-white'
                                  }`}>{sub.status.toUpperCase()}</span>
                                  {bladeTypeLabel && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{bladeTypeLabel}</span>}
                                  {(sub.issues || []).length > 0 && (
                                    <span className="text-slate-500 truncate">{sub.issues.slice(0, 2).join(", ")}{sub.issues.length > 2 ? "..." : ""}</span>
                                  )}
                                </div>
                              );
                            })}
                            {/* Pair replacement note */}
                            {(item.wiper_data.front_left?.status === 'amber' || item.wiper_data.front_left?.status === 'red' ||
                              item.wiper_data.front_right?.status === 'amber' || item.wiper_data.front_right?.status === 'red') && (
                              <div className="flex items-center gap-1 text-xs text-blue-700 font-medium pt-1 border-t border-slate-200">
                                <AlertTriangle className="w-3 h-3" />
                                Front blades recommended as a pair
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}

        {/* Action Button */}
        <div className="sticky bottom-0 bg-slate-50 pt-4 pb-6">
          <Button 
            className="w-full" 
            size="lg"
            onClick={handleCreateYestimate}
            data-testid="create-yestimate-btn"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Create Yestimate & Price Work
          </Button>
        </div>
      </div>

      {/* Call Companion Modal - INTERNAL USE ONLY */}
      {showCallCompanion && callCompanion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="call-companion-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center shrink-0">
              <div>
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call Companion
                </h2>
                <p className="text-blue-100 text-sm mt-1">Internal guidance for customer follow-up call</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCallCompanion(false)}
                className="text-white hover:bg-blue-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="overflow-y-auto flex-1 p-6 space-y-6">
              {/* Vehicle & Customer Info */}
              <div className="bg-slate-50 rounded-lg p-4 border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Vehicle:</span>
                    <p className="font-medium">{callCompanion.vehicle?.reg} — {formatVehicleNameFromObject(callCompanion.vehicle)}</p>
                    {callCompanion.vehicle?.year && <p className="text-slate-500">{callCompanion.vehicle.year} • {callCompanion.vehicle?.mileage?.toLocaleString()} miles</p>}
                  </div>
                  <div>
                    <span className="text-slate-500">Customer:</span>
                    <p className="font-medium">{callCompanion.customer?.name || "Not specified"}</p>
                    {callCompanion.customer?.phone && <p className="text-slate-500">{callCompanion.customer.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Red Items - Priority Attention */}
              {callCompanion.red_items?.count > 0 && (
                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="font-semibold text-red-700 flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4" />
                    {callCompanion.red_items.title} ({callCompanion.red_items.count})
                  </h3>
                  <div className="space-y-3">
                    {callCompanion.red_items.items.map((item, idx) => (
                      <div key={idx} className="bg-red-50 rounded p-3 text-sm">
                        <p className="font-medium text-red-800">{item.item}</p>
                        <p className="text-red-700 mt-1">{item.explanation}</p>
                        {item.notes && (
                          <p className="text-slate-600 mt-2 text-xs bg-white rounded p-2 border border-red-100">
                            <span className="font-medium">Tech notes:</span> {item.notes}
                          </p>
                        )}
                        {item.has_photo && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500 mt-2">
                            <Camera className="w-3 h-3" /> Photo evidence available
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Amber Items - Advisory */}
              {callCompanion.amber_items?.count > 0 && (
                <div className="border-l-4 border-amber-500 pl-4">
                  <h3 className="font-semibold text-amber-700 flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" />
                    {callCompanion.amber_items.title} ({callCompanion.amber_items.count})
                  </h3>
                  <div className="space-y-3">
                    {callCompanion.amber_items.items.map((item, idx) => (
                      <div key={idx} className="bg-amber-50 rounded p-3 text-sm">
                        <p className="font-medium text-amber-800">{item.item}</p>
                        <p className="text-amber-700 mt-1">{item.explanation}</p>
                        {item.notes && (
                          <p className="text-slate-600 mt-2 text-xs bg-white rounded p-2 border border-amber-100">
                            <span className="font-medium">Tech notes:</span> {item.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Historical Context */}
              {(callCompanion.historical_context?.previously_advised?.length > 0 || 
                callCompanion.historical_context?.previously_declined?.length > 0) && (
                <div className="border-l-4 border-slate-400 pl-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                    <History className="w-4 h-4" />
                    Historical Context
                  </h3>
                  
                  {/* Previously Advised */}
                  {callCompanion.historical_context?.previously_advised?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Previously Advised</p>
                      <div className="space-y-2">
                        {callCompanion.historical_context.previously_advised.map((item, idx) => (
                          <div key={idx} className="bg-slate-50 rounded p-2 text-sm flex justify-between items-center">
                            <span className="text-slate-700">{item.item}</span>
                            <span className="text-xs text-slate-500">
                              {item.progression && <span className="text-slate-600 mr-2">({item.progression})</span>}
                              {item.date}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Previously Declined */}
                  {callCompanion.historical_context?.previously_declined?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Previously Declined</p>
                      <div className="space-y-2">
                        {callCompanion.historical_context.previously_declined.map((item, idx) => (
                          <div key={idx} className="bg-orange-50 rounded p-2 text-sm">
                            <div className="flex justify-between items-start">
                              <span className="text-slate-700 font-medium">{item.item}</span>
                              <span className="text-xs text-slate-500">{item.date}</span>
                            </div>
                            <p className="text-xs text-orange-700 mt-1">Reason: {item.reason}</p>
                            {item.notes && <p className="text-xs text-slate-500 mt-1">{item.notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Likely Objections */}
              {callCompanion.likely_objections?.length > 0 && (
                <div className="border-l-4 border-purple-400 pl-4">
                  <h3 className="font-semibold text-purple-700 flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4" />
                    Likely Objections
                  </h3>
                  <div className="space-y-2">
                    {callCompanion.likely_objections.map((obj, idx) => (
                      <div key={idx} className="bg-purple-50 rounded p-3 text-sm">
                        <p className="font-medium text-purple-800">{obj.type}</p>
                        <p className="text-purple-600 text-xs mt-1">{obj.indicator}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Wording Discipline */}
              {callCompanion.wording_discipline?.length > 0 && (
                <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-3 text-sm">
                    <FileText className="w-4 h-4" />
                    Wording Discipline (Read-Only)
                  </h3>
                  <ul className="space-y-2">
                    {callCompanion.wording_discipline.map((rule, idx) => (
                      <li key={idx} className="text-xs text-slate-600 flex items-start gap-2">
                        <span className="text-slate-400">•</span>
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Internal Only Disclaimer */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
                <p className="text-xs text-blue-700 font-medium">
                  🔒 INTERNAL USE ONLY — This information is for staff reference and must not be shared with customers
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 bg-slate-50 shrink-0">
              <Button
                variant="outline"
                onClick={() => setShowCallCompanion(false)}
                className="w-full"
                data-testid="close-call-companion-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Photo Modal */}
      {enlargedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setEnlargedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setEnlargedPhoto(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 flex items-center gap-2"
            >
              <X className="w-6 h-6" />
              <span>Close</span>
            </button>
            <img 
              src={enlargedPhoto} 
              alt="Enlarged evidence" 
              className="max-w-full max-h-[85vh] object-contain mx-auto rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Premium Diagnostic Report Modal */}
      {showPremiumDiagReport && data?.job?.id && (
        <PremiumDiagnosticReport 
          jobId={data.job.id} 
          onClose={() => setShowPremiumDiagReport(false)} 
        />
      )}
    </div>
  );
};

// ============== MARGIN PRICE ROW COMPONENT ==============
// Handles bidirectional margin/retail calculation smoothly
const MarginPriceRow = ({ line, jobGroupId, jobIdx, lineIdx, buyIn, retail, marginPct, profitPerUnit, updatePartsLine }) => {
  const [localMargin, setLocalMargin] = useState('');
  const [isEditingMargin, setIsEditingMargin] = useState(false);

  // Sync local margin with calculated value when not editing
  useEffect(() => {
    if (!isEditingMargin && buyIn > 0) {
      setLocalMargin(marginPct.toFixed(1));
    }
  }, [marginPct, buyIn, isEditingMargin]);

  const handleMarginChange = (e) => {
    setLocalMargin(e.target.value);
  };

  const handleMarginBlur = () => {
    setIsEditingMargin(false);
    const desiredMargin = parseFloat(localMargin);
    if (!isNaN(desiredMargin) && buyIn > 0) {
      // Retail = Buy-In * (1 + margin/100)
      const newRetail = buyIn * (1 + desiredMargin / 100);
      updatePartsLine(jobGroupId, line.id, "unit_price", Math.round(newRetail * 100) / 100);
    }
  };

  const handleMarginFocus = () => {
    setIsEditingMargin(true);
  };

  const handleMarginKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <div className="grid grid-cols-12 gap-2 items-end bg-slate-50 rounded p-2 -mx-1">
      <div className="col-span-3">
        <Label className="text-xs text-slate-500">Buy-In £ (cost)</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={line.buy_in_price || ""}
          onChange={(e) => updatePartsLine(jobGroupId, line.id, "buy_in_price", parseFloat(e.target.value) || 0)}
          placeholder="0.00"
          className="bg-white"
          data-testid={`parts-buyin-${jobIdx}-${lineIdx}`}
        />
      </div>
      <div className="col-span-3">
        <Label className="text-xs text-slate-500">Retail £ (sell)</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={line.unit_price || ""}
          onChange={(e) => updatePartsLine(jobGroupId, line.id, "unit_price", parseFloat(e.target.value) || 0)}
          placeholder="0.00"
          className="bg-white"
          data-testid={`parts-retail-${jobIdx}-${lineIdx}`}
        />
      </div>
      <div className="col-span-3">
        <Label className="text-xs text-slate-500">Margin %</Label>
        <Input
          type="number"
          min="0"
          step="1"
          value={isEditingMargin ? localMargin : (buyIn > 0 ? marginPct.toFixed(1) : "")}
          onChange={handleMarginChange}
          onFocus={handleMarginFocus}
          onBlur={handleMarginBlur}
          onKeyDown={handleMarginKeyDown}
          placeholder="0"
          className={`bg-white ${
            marginPct >= 30 ? 'border-green-300 focus:border-green-500' :
            marginPct >= 15 ? 'border-amber-300 focus:border-amber-500' :
            buyIn > 0 ? 'border-red-300 focus:border-red-500' : ''
          }`}
          disabled={!buyIn || buyIn <= 0}
          data-testid={`parts-margin-${jobIdx}-${lineIdx}`}
        />
      </div>
      <div className="col-span-3 text-right">
        <Label className="text-xs text-slate-500">Profit/unit</Label>
        <div className={`text-sm font-medium ${profitPerUnit > 0 ? 'text-green-600' : 'text-slate-500'}`}>
          {buyIn > 0 ? `£${profitPerUnit.toFixed(2)}` : '-'}
        </div>
      </div>
    </div>
  );
};

// ============== FOH PRICING & SCOPE BUILDER (Job-Group Based) ==============
// Labels for estimate scopes (used in MOT Orchestration UI)
const SCOPE_LABELS = {
  inspection_only: 'VI Findings Only',
  diagnostic_only: 'Diagnostic Findings Only',
  pre_mot_repair: 'Pre-MOT Repair',
  partial_mot_pending: 'Partial (MOT Pending)',
  full_mot_inclusive: 'Full (MOT Inclusive)',
  mot_failure_repair: 'MOT Failure Repair',
  mot_retest: 'MOT Retest',
};

const FOHPricingBuilder = () => {
  const { yestimateId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobGroups, setJobGroups] = useState([]);
  // Iter184 — Bump on every successful recommendation save so the
  // soft-suggest banner re-checks (e.g. hides itself when alignment is
  // now present).
  const [recommendationRefreshKey, setRecommendationRefreshKey] = useState(0);
  const [tone, setTone] = useState("neutral");
  const [customerName, setCustomerName] = useState("");  // Customer name for the quote header
  const [currentJobValue, setCurrentJobValue] = useState("");  // Current job value for FOH reference
  const [labourCategories, setLabourCategories] = useState({});
  const [labourRoles, setLabourRoles] = useState({});
  const [priceBookRates, setPriceBookRates] = useState({});  // Price Book rates keyed by labour_category
  const [expandedJobs, setExpandedJobs] = useState({});

  // iter198pp — FOH MOT-row photo annotator. Holds the in-flight
  // photo + the JG/outcome/entry IDs needed to POST the annotated
  // version back to the MCC photo endpoint. `null` means the
  // annotator modal is closed.
  const [annotatingMotPhoto, setAnnotatingMotPhoto] = useState(null);
  // ^^ shape: { jgId, outcomeId, entryId, photoUrl, photoLabel }

  // iter211 — Photo annotation gap fix: FOH VI-row annotator.
  // Captures the in-flight inspection-item photo + the inspection_id /
  // item_id needed to POST the annotated copy back to the inspection
  // items endpoint. `null` means the annotator modal is closed.
  const [annotatingViPhoto,  setAnnotatingViPhoto]  = useState(null);
  // ^^ shape: { itemId, inspectionId, photoUrl }

  // MCC Viewer drawer — opened by the "View MCC" button at the top
  // of the Pricing screen. Pulls every MCC entry for the linked job
  // in one read-only view so FOH has the full tester evidence
  // without having to expand each JG individually.
  const [showMCCViewer, setShowMCCViewer] = useState(false);
  const [hasMCCEvidence, setHasMCCEvidence] = useState(false);

  // Helper: get labour rate from Price Book, with fallback to labourCategories or hardcoded defaults
  const getLabourRate = (category, fallback = 65) => {
    // Priority: Price Book rate > labourCategories default_rate > hardcoded fallback
    if (priceBookRates[category]?.rate_ex_vat != null) return priceBookRates[category].rate_ex_vat;
    if (labourCategories[category]?.default_rate != null) return labourCategories[category].default_rate;
    return fallback;
  };
  const [selectedJobForJCE, setSelectedJobForJCE] = useState(null);  // Track selected job for JCE context
  
  // Keep selectedJobForJCE in sync with jobGroups — when parts/labour are added,
  // the JCEV2Panel filter needs the updated parts_lines to properly filter "Consider Adding"
  useEffect(() => {
    if (selectedJobForJCE) {
      const updated = jobGroups.find(jg => jg.id === selectedJobForJCE.id);
      if (updated && updated !== selectedJobForJCE) {
        setSelectedJobForJCE(updated);
      }
    }
  }, [jobGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const jobGroupRefs = useRef({});  // Refs for job group cards for scroll-into-view
  
  // Battery Radio Code Alert state
  const [showBatteryRadioCodeAlert, setShowBatteryRadioCodeAlert] = useState(false);
  
  // Locking Wheel Key Alert state
  const [showLockingKeyAlert, setShowLockingKeyAlert] = useState(false);
  const [lockingKeyAlertShown, setLockingKeyAlertShown] = useState(false);  // Only show once per session
  
  // MOT Orchestration state
  const [jobReadiness, setJobReadiness] = useState(null);
  const [showMotPendingModal, setShowMotPendingModal] = useState(false);
  const [estimateScopeOverrideReason, setEstimateScopeOverrideReason] = useState("");
  const [motLookupLoading, setMotLookupLoading] = useState(false);
  // MOT Phase 2 — DVSA defects + lookup workflow
  const [motDefects, setMotDefects] = useState({ groups: { required_for_pass: [], minor: [], advisory: [], needs_review: [] }, counts: {}, total: 0 });
  const [motLookupPreview, setMotLookupPreview] = useState(null);  // raw lookup-live response
  const [showRelevanceModal, setShowRelevanceModal] = useState(false);
  const [relevanceOverrideReason, setRelevanceOverrideReason] = useState("");
  const [motDuplicates, setMotDuplicates] = useState([]);
  const [editingDefect, setEditingDefect] = useState(null);
  const [linkingDefect, setLinkingDefect] = useState(null);
  const [expandedMotGroups, setExpandedMotGroups] = useState({ required_for_pass: true, needs_review: true, minor: false, advisory: false });
  const [showUnreviewedSendModal, setShowUnreviewedSendModal] = useState(false);
  const [unreviewedOverrideReason, setUnreviewedOverrideReason] = useState("");
  // DVSA configuration status (admin indicator)
  const [dvsaStatus, setDvsaStatus] = useState(null);
  const [showDvsaStatusModal, setShowDvsaStatusModal] = useState(false);
  // Unified MOT panel — extra UI state
  const [motActionsOpen, setMotActionsOpen] = useState(null); // defect.id currently showing ⋯ menu
  const [motRecurring, setMotRecurring] = useState([]);       // classifier-grouped recurring advisories
  const [motAllTests, setMotAllTests] = useState([]);          // full DVSA test history (for the toggle)
  const [motHistoryExpanded, setMotHistoryExpanded] = useState(false);

  // iter197l — Timing Service Intelligence Library
  const [showTimingServiceModal, setShowTimingServiceModal] = useState(false);
  const [timingSuggestion, setTimingSuggestion] = useState(null);
  const [timingSuggestionDismissed, setTimingSuggestionDismissed] = useState(false);

  // iter197o — Diagnostic battery gateway one-click quote
  const [batteryQuoting, setBatteryQuoting] = useState(false);
  const [batteryQuoteDismissed, setBatteryQuoteDismissed] = useState(false);

  // Helper function to sanitize job groups - ensures all required fields have valid values
  const sanitizeJobGroups = (groups) => {
    if (!Array.isArray(groups)) return [];

    // iter197ee — Hide JGs that were bundled into another by a
    // pricing-overlay merger (e.g. headlamp polish pair). The records
    // survive in the DB for audit/JCE/CAE bindings, but the pricing
    // screen and customer portal should only render the surviving JG.
    const visibleGroups = groups.filter(
      (jg) => !jg?.hidden_in_pricing
    );

    // DEDUPLICATION: Remove duplicate job groups with same inspection_item_id
    const seenItemIds = new Set();
    const dedupedGroups = visibleGroups.filter(jg => {
      if (jg.inspection_item_id) {
        if (seenItemIds.has(jg.inspection_item_id)) {
          console.warn(`Removing duplicate job group for inspection_item_id: ${jg.inspection_item_id}`);
          return false;
        }
        seenItemIds.add(jg.inspection_item_id);
      }
      return true;
    });
    
    return dedupedGroups.map(jg => ({
      ...jg,
      title: jg.title || 'Untitled Job',
      labour_lines: (jg.labour_lines || []).filter(l => l).map(l => ({
        ...l,
        category: l.category || 'repair_labour',
        hours: parseFloat(l.hours) || 0,
        rate: parseFloat(l.rate) || 0,
        description: l.description || '',
      })),
      parts_lines: (jg.parts_lines || []).filter(p => p).map(p => ({
        ...p,
        description: p.description || 'Part',
        quantity: parseFloat(p.quantity) || 1,
        unit_price: parseFloat(p.unit_price) || 0,
      })),
      charges: (jg.charges || []).filter(c => c).map(c => ({
        ...c,
        charge_type: c.charge_type || 'consumables',
        description: c.description || 'Charge',
        amount: parseFloat(c.amount) || 0,
      })),
    }));
  };
  const [suggestions, setSuggestions] = useState({});  // Per job group suggestions
  const [suggestionActions, setSuggestionActions] = useState({});  // Track actions taken
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);  // Diagnostic info if applicable
  const [hasDiagnosticJobGroup, setHasDiagnosticJobGroup] = useState(false);  // Track if diagnostic job group exists
  
  // Service Interval State - V2 with due_soon and suppressed
  const [intervalItems, setIntervalItems] = useState([]);
  const [dueIntervals, setDueIntervals] = useState([]);
  const [dueSoonIntervals, setDueSoonIntervals] = useState([]);  // V2: Items approaching due date
  const [suppressedIntervals, setSuppressedIntervals] = useState([]);  // V2: Declined items in suppression window
  const [showIntervalPanel, setShowIntervalPanel] = useState(false);
  const [showSuppressedItems, setShowSuppressedItems] = useState(false);  // V2: Toggle to show suppressed
  const [declineModal, setDeclineModal] = useState({ show: false, item: null });
  const [declineReasons, setDeclineReasons] = useState([]);
  const [intervalV2Config, setIntervalV2Config] = useState(null);  // V2: Config from backend
  const [useNewRecommendationEngine, setUseNewRecommendationEngine] = useState(true);  // V3: Toggle for new recommendation engine
  
  // Service History State
  const [serviceHistory, setServiceHistory] = useState([]);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [historyModal, setHistoryModal] = useState({ show: false, item: null, mode: 'add' });
  const [historySummary, setHistorySummary] = useState({ confirmed_count: 0, partial_count: 0, declined_count: 0 });
  
  // Service History Paste Import State
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [importPasteText, setImportPasteText] = useState('');
  
  // Item-Level Validation State - for showing inline warnings when pricing complete fails
  const [jobValidationErrors, setJobValidationErrors] = useState({});
  const [parsedImportItems, setParsedImportItems] = useState([]);
  const [serviceVisits, setServiceVisits] = useState([]);
  const [parsingImport, setParsingImport] = useState(false);
  const [savingImport, setSavingImport] = useState(false);
  const [importGroupByVisit, setImportGroupByVisit] = useState(true);
  
  // Job Packages State
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [jobPackages, setJobPackages] = useState({ packages: [], categories: {} });
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [selectedPackageCategory, setSelectedPackageCategory] = useState(null);
  const [applyingPackage, setApplyingPackage] = useState(null);
  
  // Price Book Modal State
  const [showPriceBookModal, setShowPriceBookModal] = useState(false);
  
  // Package Management State
  const [showPackageEditor, setShowPackageEditor] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [savingPackage, setSavingPackage] = useState(false);
  
  // Auto-Populate State
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [showAutoPopulateOptions, setShowAutoPopulateOptions] = useState(false);
  const [autoPopulateIncludeAmber, setAutoPopulateIncludeAmber] = useState(true);
  const [autoPopulateUsePackages, setAutoPopulateUsePackages] = useState(true);
  
  // Not Quoted Modal State
  const [showNotQuotedModal, setShowNotQuotedModal] = useState(false);
  const [notQuotedJobGroup, setNotQuotedJobGroup] = useState(null);
  const [notQuotedReason, setNotQuotedReason] = useState("");
  const [notQuotedNotes, setNotQuotedNotes] = useState("");
  // iter211d — guard so rapid Confirm clicks don't fire duplicate POSTs
  // and so the button can show a live "Confirming…" label while the
  // autosave-flush + not-quoted POST + refresh GET are in flight.
  const [isSubmittingNotQuoted, setIsSubmittingNotQuoted] = useState(false);
  
  // Smart Estimate Wizard State
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardFindings, setWizardFindings] = useState({ red: [], amber: [], green: [] });
  const [wizardSelectedItems, setWizardSelectedItems] = useState([]);
  const [wizardSuggestedPackages, setWizardSuggestedPackages] = useState([]);
  const [wizardValidation, setWizardValidation] = useState(null);
  const [wizardLoading, setWizardLoading] = useState(false);
  
  // Auto-Save State for Pricing Persistence
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autoSaveTimerRef = useRef(null);
  
  // Ignored Items State (items FOH chose to not quote with reason)
  const [ignoredItems, setIgnoredItems] = useState([]);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [ignoreTarget, setIgnoreTarget] = useState(null); // { type: 'inspection' | 'diagnostic', itemId, label }
  const [ignoreReason, setIgnoreReason] = useState('');
  const [ignoreNotes, setIgnoreNotes] = useState('');
  
  // Custom Job Modal State
  const [showCustomJobModal, setShowCustomJobModal] = useState(false);
  const [customJobTitle, setCustomJobTitle] = useState('');
  const [customJobDescription, setCustomJobDescription] = useState('');
  const [wizardQuotePreview, setWizardQuotePreview] = useState(null);
  
  // Quick Pricing Mode State
  const [quickPricingLoading, setQuickPricingLoading] = useState(false);
  const [showQuickPricingSummary, setShowQuickPricingSummary] = useState(false);
  const [quickPricingResult, setQuickPricingResult] = useState(null);
  
  // Lock Warning State
  const [lockWarningDismissed, setLockWarningDismissed] = useState(false);
  
  // Margin/Print Sheet Modal State  
  const [showMarginSheet, setShowMarginSheet] = useState(false);
  
  // ============== JOB COMPLETENESS ENGINE (JCE) STATE ==============
  // JCE replaces the old Pricing Integrity panel - single source of truth
  const [showJCEPanel, setShowJCEPanel] = useState(true);  // JCE panel visibility (default open)
  const [useJobCentricWorkspace, setUseJobCentricWorkspace] = useState(false);  // DISABLED: Reverted to previous working layout
  const [useThreeColumnLayout, setUseThreeColumnLayout] = useState(true);  // NEW: Three-column layout using existing components
  const [activeJobId, setActiveJobId] = useState(null);  // NEW: Currently focused job for right panel context
  const [jceAutoOpenTimer, setJceAutoOpenTimer] = useState(null);  // Timer for auto-open
  const [showJCESendModal, setShowJCESendModal] = useState(false);  // Send confirmation modal
  const [jcePendingSave, setJcePendingSave] = useState(false);  // Flag to track pending save after modal
  
  // ============== JOB CONTROL UNIT (JCU) STATE ==============
  // JCU provides real-time integrity scoring for estimates
  const [jcuEvaluation, setJcuEvaluation] = useState(null);  // Current JCU evaluation result
  const [jcuLoading, setJcuLoading] = useState(false);  // Loading state for JCU fetch
  const [showJCUDetails, setShowJCUDetails] = useState(false);  // Show detailed breakdown modal
  const [jcuBlockedModal, setJcuBlockedModal] = useState({ show: false, details: null });  // Blocked estimate modal
  const [jcuOverrideModal, setJcuOverrideModal] = useState({ show: false, details: null });  // Override confirmation modal
  const [jcuOverrideReason, setJcuOverrideReason] = useState('');  // Override reason text
  const [jcuOverrideApplied, setJcuOverrideApplied] = useState(false);  // Track if override was applied
  
  // ============== DRE - DIAGNOSTIC REQUIREMENT ENGINE STATE ==============
  const [dreResults, setDreResults] = useState({});  // DRE results per job group
  const [dreOverrideModal, setDreOverrideModal] = useState({ show: false, jobGroupId: null, jobTitle: null });
  const [dreOverrideReason, setDreOverrideReason] = useState('');
  
  // ============== PIE - PRICING INTEGRITY ENGINE STATE (LEGACY - Being replaced) ==============
  const [pieWarnings, setPieWarnings] = useState({});  // Warnings per job group: { jobGroupId: [warnings] }
  const [pieValidating, setPieValidating] = useState(false);  // Loading state for PIE validation
  const [pieSummary, setPieSummary] = useState(null);  // Overall PIE summary for yestimate
  const [showPiePanel, setShowPiePanel] = useState(false);  // Toggle PIE warnings panel - NOW DEFAULT HIDDEN
  
  // ============== JOB INTEGRITY ENGINE STATE (NEW) ==============
  const [jobIntegrityData, setJobIntegrityData] = useState({});  // Integrity state per job: { jobGroupId: JobIntegritySummary }
  const [estimateReadiness, setEstimateReadiness] = useState(null);  // Overall estimate readiness
  const [sendGateData, setSendGateData] = useState(null);  // Send gate check result
  const [showSendGateModal, setShowSendGateModal] = useState(false);  // Send gate modal visibility
  const [integrityLoading, setIntegrityLoading] = useState(false);  // Loading state for integrity calculations
  
  // ============== GLOBAL CHARGES STATE ==============
  const [globalCharges, setGlobalCharges] = useState({
    consumables: { applied: true, amount: 12.50, disapplyReason: null },
    environmental: { applied: true, amount: 6.00, disapplyReason: null }
  });
  const [globalChargesConfig, setGlobalChargesConfig] = useState(null);  // Loaded from API
  
  // ============== PAIR POLICY MODAL STATE ==============
  // Handles pair replacement recommendations (shocks, springs, etc.)
  // Replaced legacy pairsReminderModal with new pair policy system
  const [pairPolicyModal, setPairPolicyModal] = useState({ 
    show: false, 
    promptType: null,  // INFO_BANNER, RECOMMEND_MODAL, OVERRIDE_MODAL, AUTO_DRAFT
    promptData: null,
    pendingItem: null,
    isMandatoryBundle: false,
    bundleData: null
  });
  
  // ============== AUTHORISED VALUE GATE STATE ==============
  // Blocking modal to ensure FOH confirms pre-authorised job value before building estimate
  const [showAuthorisedValueGate, setShowAuthorisedValueGate] = useState(false);
  const [authorisedValueData, setAuthorisedValueData] = useState({
    value: null,
    tags: [],
    confirmed_at: null,
    confirmed_by: null,
    confirmed_zero: false
  });
  const [isChangingAuthorisedValue, setIsChangingAuthorisedValue] = useState(false);
  
  // ============== CUSTOMER INTENT MODE STATE ==============
  // Customer intent configuration for presentation layer
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [intentConfig, setIntentConfig] = useState(null);
  const [isIntentLocked, setIsIntentLocked] = useState(false);
  const [isChangingIntent, setIsChangingIntent] = useState(false);
  
  // ============== GLOBAL DEDUPE VALIDATION STATE ==============
  // Pre-save validation to catch duplicate operations across job groups
  const [showDedupeModal, setShowDedupeModal] = useState(false);
  const [dedupeValidationResult, setDedupeValidationResult] = useState(null);
  const [dedupeValidating, setDedupeValidating] = useState(false);
  
  const navigate = useNavigate();
  
  // Job Lock - acquire lock when editing this yestimate
  const jobId = data?.job?.id;
  const {
    isLocked: isJobLocked,
    isCurrentUser: hasLock,
    lockedByName,
    lockedAt,
    warning: lockWarning,
    loading: lockLoading
  } = useJobLock(jobId, 'pricing-builder', !!jobId);

  // iter198x — Detect whether this job has MCC evidence so the
  // "View MCC" button can be conditionally rendered. We use a
  // lightweight HEAD-style check: if /jobs/{id}/mcc returns either
  // mcc_entries or defects, FOH can usefully open the viewer.
  useEffect(() => {
    if (!jobId) {
      setHasMCCEvidence(false);
      return;
    }
    axios.get(`${API}/jobs/${jobId}/mcc`)
      .then((r) => {
        const hasEntries = (r.data?.mcc_entries || []).length > 0;
        const hasDefects = (r.data?.defects || []).length > 0;
        setHasMCCEvidence(hasEntries || hasDefects);
      })
      .catch(() => setHasMCCEvidence(false));
  }, [jobId]);

  // iter197l — Timing service auto-trigger: ask backend if vehicle's
  // timing system is due replacement by age/mileage. Returns suggested:false
  // (silent) for most jobs.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sg } = await axios.get(`${API}/timing-services/auto-trigger/${jobId}`);
        if (!cancelled) setTimingSuggestion(sg);
      } catch {
        // Silent — auto-suggest is advisory only.
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  // iter199aa — Auto-generate CAE on every pricing page mount AND
  // poll until the background backfill finishes.
  //
  // Lifecycle:
  //   1. Backend already auto-fires CAE generation when intake
  //      transitions to PRICING (see services/cae/auto_backfill.py),
  //      so by the time FOH lands here most JGs usually have advice.
  //   2. We still call /cae/generate-all on mount to cover (a) JGs
  //      missing CAE from before this fix shipped, (b) JGs added
  //      after the initial transition.
  //   3. When the call reports queued > 0 OR any JG carries the
  //      `generating` sentinel, we poll GET /yestimate every 5s
  //      until every JG either has a summary, was FOH-deleted, or
  //      errored. iter204h — Extended ceiling from 60s to 120s so
  //      the 3-concurrent parallel backfill comfortably finishes a
  //      4-5 JG yestimate before the FE gives up polling.
  //   4. iter204h — Stale-recovery: if a JG still has a
  //      `generating` sentinel older than ~40s on the second poll
  //      (the backend backfill should have moved past it by then),
  //      we re-trigger the auto-CAE endpoint with `force: true` so
  //      the next backend worker re-claims the stale row.
  //   5. Per-card Regenerate stays as the manual fallback.
  useEffect(() => {
    if (!yestimateId) return;
    if (!Array.isArray(jobGroups) || jobGroups.length === 0) return;
    const isPending = (jg) => {
      const c = jg?.customer_advice || {};
      return !c.summary && !c.deleted && !c.error;
    };
    const isStaleGenerating = (jg) => {
      const c = jg?.customer_advice || {};
      if (!c.generating) return false;
      const qa = c.queued_at;
      if (!qa) return true;
      try {
        const ageMs = Date.now() - new Date(qa).getTime();
        return ageMs > 40_000;
      } catch { return true; }
    };
    const anyPending = jobGroups.some(isPending);
    if (!anyPending) return;
    let cancelled = false;
    let pollAttempts = 0;
    let staleRecoveryFired = false;
    const POLL_INTERVAL_MS = 5000;
    const POLL_MAX_ATTEMPTS = 24; // ~120s ceiling
    const refetchAndMaybePoll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
        if (cancelled) return;
        const next = r.data?.yestimate?.job_groups || [];
        if (next.length) setJobGroups(sanitizeJobGroups(next));
        const stillPending = next.some(isPending);
        if (stillPending) {
          // iter204h — One-shot stale-recovery: if anything still
          // looks stuck after the second poll (~10s in), kick the
          // backend with `force: true` so the parallel backfill
          // re-claims any orphaned sentinel.
          if (!staleRecoveryFired && pollAttempts >= 2 && next.some(isStaleGenerating)) {
            staleRecoveryFired = true;
            try {
              await axios.post(
                `${API}/foh/yestimate/${yestimateId}/cae/generate-all`,
                { force: true },
              );
            } catch { /* swallow */ }
          }
          if (pollAttempts < POLL_MAX_ATTEMPTS) {
            pollAttempts += 1;
            setTimeout(refetchAndMaybePoll, POLL_INTERVAL_MS);
          }
        }
      } catch {
        // silent — Regenerate button remains the manual fallback
      }
    };
    (async () => {
      try {
        const res = await axios.post(
          `${API}/foh/yestimate/${yestimateId}/cae/generate-all`,
          { force: false },
        );
        if (cancelled) return;
        const queued = res?.data?.queued || 0;
        // Always do one immediate refetch so the `generating` sentinel
        // becomes visible to the FE (the backend stamps it before
        // returning the queued count).
        if (queued > 0) {
          setTimeout(refetchAndMaybePoll, 1500);
        } else {
          // Backend says nothing to do — but we know SOMETHING was
          // pending or this effect wouldn't have fired. Refetch once
          // to pick up advice the auto-trigger may have completed
          // between our jobGroups snapshot and this call.
          refetchAndMaybePoll();
        }
      } catch {
        // silent — the per-card Regenerate button is the manual fallback
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yestimateId, jobGroups.length]);

  // Refresh job groups after timing pack applied.
  const refreshAfterTimingApplied = async () => {
    try {
      const refresh = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setJobGroups(sanitizeJobGroups(refresh.data.yestimate.job_groups || []));
      setData(refresh.data);
      toast.success("Timing service added to estimate");
    } catch (e) {
      toast.error("Applied but failed to refresh — reload page to see new line");
    }
  };

  // iter197o — Battery gateway one-click quote.
  const batteryOutcome = diagnosticInfo?.diagnostic_battery_outcome;
  const batteryNeedsReplacement = !!batteryOutcome?.blocks_reliable_findings;
  const existingBatteryJg = jobGroups.find(jg => jg?.source === "diagnostic_battery_gateway");
  const showBatteryQuoteCta = batteryNeedsReplacement && !existingBatteryJg && !batteryQuoteDismissed;

  const handleQuoteBatteryReplacement = async () => {
    if (batteryQuoting) return;
    setBatteryQuoting(true);
    try {
      const { data: resp } = await axios.post(
        `${API}/foh/yestimate/${yestimateId}/quote-battery-replacement`
      );
      const refresh = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setJobGroups(sanitizeJobGroups(refresh.data.yestimate.job_groups || []));
      setData(refresh.data);
      toast.success(
        resp.created
          ? `Battery replacement quoted${resp.needs_coding ? " (incl. battery coding)" : ""} — confirm part & price before sending`
          : "Battery replacement already quoted on this estimate"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to quote battery replacement");
    } finally {
      setBatteryQuoting(false);
    }
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/foh/yestimate/${yestimateId}`),
      axios.get(`${API}/foh/labour-categories`),
      axios.get(`${API}/foh/labour-roles`),
      axios.get(`${API}/foh/service-intervals/check/${yestimateId}`),
      axios.get(`${API}/pie/global-charges`),  // Load global charges config
      axios.get(`${API}/price-book/labour-rates-by-category`)  // Load Price Book rates
    ]).then(([yestimateRes, categoriesRes, rolesRes, intervalsRes, globalChargesRes, priceBookRes]) => {
      setData(yestimateRes.data);
      
      // Load Price Book rates by category
      if (priceBookRes.data) {
        setPriceBookRates(priceBookRes.data);
      }
      
      // Load global charges configuration
      if (globalChargesRes.data) {
        setGlobalChargesConfig(globalChargesRes.data);
        // Initialize global charges state from saved data or defaults
        const savedYestimate = yestimateRes.data.yestimate;
        setGlobalCharges({
          consumables: {
            applied: savedYestimate?.global_consumables_applied !== false,
            amount: globalChargesRes.data.consumables?.amount || 12.50,
            disapplyReason: savedYestimate?.global_consumables_disapply_reason || null
          },
          environmental: {
            applied: savedYestimate?.global_environmental_applied !== false,
            amount: globalChargesRes.data.environmental?.amount || 6.00,
            disapplyReason: savedYestimate?.global_environmental_disapply_reason || null
          }
        });
      }
      
      // Customer name - prefer yestimate, fall back to job (from FOH intake)
      const yestCustomerName = yestimateRes.data.yestimate?.customer_name;
      const jobCustomerName = yestimateRes.data.job?.customer_name;
      setCustomerName(yestCustomerName || jobCustomerName || "");
      setCurrentJobValue(yestimateRes.data.yestimate?.current_job_value || "");
      const existingJobGroups = yestimateRes.data.yestimate.job_groups || [];
      
      // Build a map from inspection_item_id to item_key for vi_item_key enrichment
      const inspectionItems = yestimateRes.data.items || [];
      const itemKeyMap = {};
      inspectionItems.forEach(item => {
        if (item.id && item.item_key) {
          itemKeyMap[item.id] = item.item_key;
        }
      });
      
      // Ensure diagnostic job groups have is_mandatory flag set
      // Also enrich job groups with vi_item_key if missing (legacy data fix)
      const processedJobGroups = existingJobGroups.map(jg => {
        let enrichedJg = jg;
        
        // Diagnostic job processing
        if (jg.source === "diagnostic" || jg.title?.toLowerCase().includes("diagnostic rectification")) {
          enrichedJg = { ...enrichedJg, is_mandatory: true, source: "diagnostic" };
        }
        
        // Legacy data fix: If vi_item_key is missing but we have inspection_item_id,
        // try to derive vi_item_key from the inspection item's item_key
        if (!enrichedJg.vi_item_key && enrichedJg.inspection_item_id && itemKeyMap[enrichedJg.inspection_item_id]) {
          enrichedJg = { ...enrichedJg, vi_item_key: itemKeyMap[enrichedJg.inspection_item_id] };
        }
        
        return enrichedJg;
      });
      
      // Sanitize job groups to ensure required fields have values
      const sanitizedJobGroups = sanitizeJobGroups(processedJobGroups);
      
      setJobGroups(sanitizedJobGroups);
      setTone(yestimateRes.data.yestimate.tone || "neutral");
      setLabourCategories(() => {
        // Start with backend's LABOUR_CATEGORIES (merged with Price Book)
        const merged = { ...(categoriesRes.data || {}) };
        // Add any Price Book entries that don't map to a standard category
        // (user-created labour types like "Welding & Fabrication")
        if (priceBookRes.data) {
          for (const [catKey, rate] of Object.entries(priceBookRes.data)) {
            if (!merged[catKey]) {
              merged[catKey] = {
                label: rate.name || catKey,
                default_rate: rate.rate_ex_vat,
                price_book_code: rate.code,
                price_book_name: rate.name
              };
            }
          }
        }
        return merged;
      });
      setLabourRoles(rolesRes.data);
      
      // Service intervals - V2 structure
      // V2: due_items is now flat list of due_now items grouped by category
      const dueItemsData = intervalsRes.data.due_items || [];
      // Flatten if grouped by category (legacy format) or use as-is (V2 flat format)
      const flatDueItems = Array.isArray(dueItemsData) && dueItemsData.length > 0 && dueItemsData[0]?.items
        ? dueItemsData.flatMap(cat => cat.items || [])
        : dueItemsData;
      setDueIntervals(flatDueItems);
      setDueSoonIntervals(intervalsRes.data.due_soon_items || []);
      setSuppressedIntervals(intervalsRes.data.suppressed_items || []);
      setDeclineReasons(intervalsRes.data.decline_reasons || []);
      setIntervalItems(yestimateRes.data.yestimate.interval_items || []);
      setIntervalV2Config(intervalsRes.data.v2_config || null);
      
      // Check for diagnostic info in the job
      const job = yestimateRes.data.job;
      if (job?.requires_diagnostic_vi && job?.diagnostic_status === "completed") {
        setDiagnosticInfo({
          customer_concern: job.customer_concern,
          diagnostic_conclusion: job.diagnostic_conclusion,
          diagnostic_polished_report: job.diagnostic_polished_report,
          diagnostic_submitted_by_name: job.diagnostic_submitted_by_name,
          // iter197o — gateway test outcome so FOH can one-click quote
          // battery replacement when interpretation = fail / marginal.
          diagnostic_battery_outcome: job.diagnostic_battery_outcome,
        });
        // Check if a diagnostic job group already exists (by source field or title)
        const diagJobGroup = existingJobGroups.find(jg => 
          jg.source === "diagnostic" || 
          jg.title?.toLowerCase().includes("diagnostic")
        );
        setHasDiagnosticJobGroup(!!diagJobGroup);
      }
      
      // Fetch suggestions for existing job groups
      existingJobGroups.forEach(jg => {
        fetchSuggestions(jg.id, jg.title, jg.title);
      });
      
      // Fetch service history for this vehicle
      const vehicleReg = yestimateRes.data.job?.reg;
      if (vehicleReg) {
        axios.get(`${API}/foh/vehicle/${vehicleReg}/service-history`)
          .then(histRes => {
            setServiceHistory(histRes.data.history || []);
            setHistorySummary({
              confirmed_count: (histRes.data.history || []).filter(h => h.confidence_state === 'confirmed').length,
              partial_count: (histRes.data.history || []).filter(h => h.confidence_state === 'partial').length,
              declined_count: histRes.data.decline_history?.length || 0
            });
          })
          .catch(err => console.log("No service history:", err));
      }
      
      // Check if JCU override was already applied
      if (yestimateRes.data.jcu_override_applied) {
        setJcuOverrideApplied(true);
      }
      
      // ============== AUTHORISED VALUE GATE CHECK ==============
      // Check if the authorised value has been confirmed - if not, show the gate modal
      const yest = yestimateRes.data.yestimate;
      
      // ============== CUSTOMER INTENT MODE CHECK ==============
      // Check if intent mode is configured - this is now the primary entry point
      const intentCfg = yest.intent_config || {};
      if (intentCfg.intent_mode) {
        // Intent is configured - store the data
        setIntentConfig({
          intent_mode: intentCfg.intent_mode,
          communication_style: intentCfg.communication_style || 'neutral',
          already_authorised_value: yest.already_authorised_value || 0,
          already_authorised_tags: yest.already_authorised_tags || []
        });
        setIsIntentLocked(intentCfg.locked || false);
        setShowIntentModal(false);
        
        // Also populate authorised value data for backward compatibility
        setAuthorisedValueData({
          value: yest.already_authorised_value || 0,
          tags: yest.already_authorised_tags || [],
          confirmed_at: yest.already_authorised_confirmed_at || intentCfg.configured_at,
          confirmed_by: yest.already_authorised_confirmed_by,
          confirmed_zero: yest.already_authorised_confirmed_zero || false
        });
        setShowAuthorisedValueGate(false);
      } else if (yest.already_authorised_confirmed_at) {
        // Legacy: Value was confirmed via old modal but no intent config
        // Migrate to new system by showing intent modal
        setAuthorisedValueData({
          value: yest.already_authorised_value || 0,
          tags: yest.already_authorised_tags || [],
          confirmed_at: yest.already_authorised_confirmed_at,
          confirmed_by: yest.already_authorised_confirmed_by,
          confirmed_zero: yest.already_authorised_confirmed_zero || false
        });
        // Still show intent modal to complete configuration
        setShowIntentModal(true);
        setShowAuthorisedValueGate(false);
      } else {
        // Nothing configured - show intent modal (new primary entry point)
        setShowIntentModal(true);
        setShowAuthorisedValueGate(false);
      }
      
      // Fetch JCU evaluation for the yestimate
      axios.get(`${API}/jcu/v2/yestimate/${yestimateId}/evaluation`)
        .then(jcuRes => {
          if (jcuRes.data.found) {
            const evaluation = jcuRes.data.evaluation;
            // Package evaluations are now at the top level of the response
            const packageEvaluations = jcuRes.data.package_evaluations || evaluation.package_evaluations || [];
            
            // Extract deduction summary for compact display
            const deductionSummary = [];
            for (const pkg of packageEvaluations) {
              if (pkg.deductions) {
                for (const d of pkg.deductions) {
                  deductionSummary.push({
                    text: d.description || d.item || d.type,
                    package: pkg.package_name || pkg.job_title,
                    penalty: d.final_penalty || d.points || 0
                  });
                }
              }
            }
            
            setJcuEvaluation({
              ...evaluation,
              package_evaluations: packageEvaluations,
              deduction_summary: deductionSummary.slice(0, 5)
            });
          }
        })
        .catch(err => console.log("No JCU evaluation:", err));
    }).catch(err => {
      alert(err.response?.data?.detail || "Failed to load Yestimate");
      navigate("/app/foh");
    }).finally(() => setLoading(false));
    
    // Load job readiness (MOT orchestration) separately — non-blocking
    axios.get(`${API}/foh/yestimate/${yestimateId}`).then(yRes => {
      const jobId = yRes.data?.job?.id;
      if (jobId) {
        axios.get(`${API}/jobs/${jobId}/readiness`).then(rRes => {
          setJobReadiness(rRes.data);
        }).catch(() => {});
        // Load MOT defects (Phase 2)
        axios.get(`${API}/jobs/${jobId}/mot-defects`).then(dRes => {
          setMotDefects(dRes.data);
        }).catch(() => {});
        // Load duplicate candidates
        axios.get(`${API}/jobs/${jobId}/mot-defects/duplicate-candidates`).then(cRes => {
          setMotDuplicates(cRes.data?.matches || []);
        }).catch(() => {});
        // Hydrate full DVSA test history (for the "Show full history" toggle on the unified panel)
        axios.post(`${API}/jobs/${jobId}/mot-lookup-live`, {}).then(mRes => {
          setMotAllTests(mRes.data?.all_tests || (mRes.data?.latest_test ? [mRes.data.latest_test] : []));
        }).catch(() => setMotAllTests([]));
        // Recurring classifier groups
        axios.get(`${API}/jobs/${jobId}/mot-recurring-advisories`).then(rrRes => {
          setMotRecurring(rrRes.data?.groups || []);
        }).catch(() => setMotRecurring([]));
      }
    }).catch(() => {});
    // Load DVSA admin status (configuration indicator)
    axios.get(`${API}/admin/dvsa/status`).then(sRes => {
      setDvsaStatus(sRes.data);
    }).catch(() => {});
  }, [yestimateId, navigate]);

  // ============== FOCUS JOB FROM URL PARAMETER ==============
  // Handle 'focusJob' URL parameter from FOHSendPortal "Jump to Job" feature
  useEffect(() => {
    if (!data || jobGroups.length === 0) return;
    
    const searchParams = new URLSearchParams(window.location.search);
    const focusJobId = searchParams.get('focusJob');
    
    if (focusJobId) {
      // Find the job group by ID
      const targetJobGroup = jobGroups.find(jg => jg.id === focusJobId);
      
      if (targetJobGroup) {
        // Small delay to ensure refs are populated after render
        setTimeout(() => {
          // Expand the job group
          setExpandedJobs(prev => ({ ...prev, [targetJobGroup.id]: true }));
          
          // Scroll into view
          if (jobGroupRefs.current[targetJobGroup.id]) {
            jobGroupRefs.current[targetJobGroup.id].scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            
            // Flash highlight effect
            const element = jobGroupRefs.current[targetJobGroup.id];
            element.style.transition = 'box-shadow 0.3s ease-in-out';
            element.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)';
            setTimeout(() => {
              element.style.boxShadow = '';
            }, 2000);
          }
          
          toast.info(`Scrolled to: ${targetJobGroup.title?.substring(0, 40)}...`);
        }, 300);
        
        // Clean up URL parameter after handling
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [data, jobGroups]);

  // ============== JOB COMPLETENESS ENGINE (JCE) EFFECTS ==============
  // JCE now handles all completeness logic previously in Pricing Integrity
  // Job tags are extracted from job_groups automatically
  // No separate loading needed - JCE evaluates based on job_groups

  // Fetch suggestions when a job group is created or title changes
  // Now uses JCU-based commonly required suggestions API
  const fetchSuggestions = async (jobGroupId, title, itemLabel) => {
    try {
      const encodedTitle = encodeURIComponent(title);
      const encodedLabel = encodeURIComponent(itemLabel || "");
      // Use the commonly-required rules endpoint which has seeded data
      const res = await axios.post(`${API}/foh/job-group/suggestions?title=${encodedTitle}&inspection_item_label=${encodedLabel}`);
      setSuggestions(prev => ({ ...prev, [jobGroupId]: res.data.suggestions }));
    } catch (err) {
      console.error("Failed to fetch suggestions:", err);
    }
  };

  // ============== AUTHORISED VALUE GATE HANDLER ==============
  // Handler to confirm the already authorised value
  const handleAuthorisedValueConfirm = async (confirmData) => {
    try {
      // Save the confirmed value to the backend
      await axios.put(`${API}/foh/yestimate/${yestimateId}`, {
        already_authorised_value: confirmData.value,
        already_authorised_confirmed: true,
        already_authorised_tags: confirmData.tags || [],
        already_authorised_confirmed_zero: confirmData.confirmed_zero || false
      });
      
      // Update local state with the confirmed data
      setAuthorisedValueData({
        value: confirmData.value,
        tags: confirmData.tags || [],
        confirmed_at: new Date().toISOString(),
        confirmed_by: 'current_user', // Backend will set actual user
        confirmed_zero: confirmData.confirmed_zero || false
      });
      
      // Close the gate modal
      setShowAuthorisedValueGate(false);
      setIsChangingAuthorisedValue(false);
      
      toast.success(`Authorised value confirmed: £${confirmData.value.toFixed(2)}`);
    } catch (err) {
      console.error("Failed to confirm authorised value:", err);
      toast.error("Failed to confirm authorised value");
    }
  };
  
  // Handler to open the change authorised value modal
  const handleChangeAuthorisedValue = () => {
    setIsChangingAuthorisedValue(true);
    setShowAuthorisedValueGate(true);
  };

  // ============== CUSTOMER INTENT MODE HANDLER ==============
  // Handler to configure customer intent mode
  const handleIntentConfirm = async (intentData) => {
    try {
      // Save to the new intent-mode API
      await axios.post(`${API}/intent-mode/configure/${yestimateId}`, {
        intent_mode: intentData.intent_mode,
        communication_style: intentData.communication_style,
        already_authorised_value: intentData.already_authorised_value,
        already_authorised_tags: intentData.already_authorised_tags || []
      });
      
      // Update local state
      setIntentConfig({
        intent_mode: intentData.intent_mode,
        communication_style: intentData.communication_style,
        already_authorised_value: intentData.already_authorised_value,
        already_authorised_tags: intentData.already_authorised_tags || []
      });
      
      // Also update authorised value data for backward compatibility
      setAuthorisedValueData({
        value: intentData.already_authorised_value,
        tags: intentData.already_authorised_tags || [],
        confirmed_at: new Date().toISOString(),
        confirmed_by: 'current_user',
        confirmed_zero: intentData.confirmed_zero || false
      });
      
      // Close modal
      setShowIntentModal(false);
      setIsChangingIntent(false);
      
      const intentLabels = { service: 'Service', mot_service: 'MOT + Service', diagnostic: 'Diagnostic' };
      toast.success(`Intent configured: ${intentLabels[intentData.intent_mode] || intentData.intent_mode} (£${intentData.already_authorised_value.toFixed(2)} authorised)`);
    } catch (err) {
      console.error("Failed to configure intent:", err);
      toast.error(err.response?.data?.detail || "Failed to configure intent");
    }
  };
  
  // Handler to open intent change modal
  const handleChangeIntent = () => {
    if (isIntentLocked) {
      toast.error("Intent configuration is locked - customer output has been generated");
      return;
    }
    setIsChangingIntent(true);
    setShowIntentModal(true);
  };

  // ============== AUTO-SAVE FUNCTIONALITY ==============
  // Debounced auto-save - saves pricing after 2 seconds of inactivity
  // iter211d — Extracted the PUT body into a helper so callers like
  // `submitNotQuoted` can do an IMMEDIATE save (the not-quoted POST
  // requires the JG to already exist on the backend, but the debounced
  // auto-save fires 2s later — without flushing, the POST hits before
  // the JG is persisted and returns "Job group not found").
  const buildYestimatePayload = useCallback(() => ({
    tone,
    customer_name: customerName,
    current_job_value: currentJobValue ? parseFloat(currentJobValue) : null,
    ignored_items: ignoredItems,
    job_groups: jobGroups.map(jg => ({
      id: jg.id,
      inspection_item_id: jg.inspection_item_id,
      vi_item_key: jg.vi_item_key,
      title: jg.title || 'Untitled Job',
      source: jg.source || "inspection",
      status: jg.status,
      consolidated: jg.consolidated || false,
      jcu_package_id: jg.jcu_package_id || null,
      pricing_confirmed: jg.pricing_confirmed || false,
      not_quoted_reason: jg.not_quoted_reason || null,
      labour_lines: (jg.labour_lines || []).filter(l => l).map(l => ({
        id: l.id,
        category: l.category || 'repair_labour',
        description: l.description || '',
        hours: parseFloat(l.hours) || 0,
        rate: parseFloat(l.rate) || 0,
        dedupe_key: l.dedupe_key,
        source: l.source,
        source_type: l.source_type,
      })),
      parts_lines: (jg.parts_lines || []).filter(p => p).map(p => ({
        id: p.id,
        description: p.description || 'Part',
        quantity: parseFloat(p.quantity) || 1,
        unit_price: parseFloat(p.unit_price) || 0,
        buy_in_price: parseFloat(p.buy_in_price) || 0,
        vat_rate: parseFloat(p.vat_rate) || 20,
        dedupe_key: p.dedupe_key,
        source: p.source,
        source_type: p.source_type,
        tyre_size:     p.tyre_size || null,
        tyre_brand:    p.tyre_brand || null,
        tyre_model:    p.tyre_model || null,
        tyre_spec:     p.tyre_spec || null,
        tyre_position: p.tyre_position || null,
        tyre_child_id: p.tyre_child_id || null,
      })),
      charges: (jg.charges || []).filter(c => c).map(c => ({
        id: c.id,
        charge_type: c.charge_type || 'consumables',
        description: c.description || 'Charge',
        amount: parseFloat(c.amount) || 0,
        vat_rate: parseFloat(c.vat_rate) || 20,
      })),
      applied_rules: jg.applied_rules || [],
      contextual_customer_note: jg.contextual_customer_note || null,
      customer_notes:           jg.customer_notes || [],
      sate:                     jg.sate || null,
      customer_advice:          jg.customer_advice || null,
    })),
  }), [tone, customerName, currentJobValue, ignoredItems, jobGroups]);

  const flushAutoSave = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (jobGroups.length === 0) return;
    setAutoSaveStatus('saving');
    try {
      await axios.put(`${API}/foh/yestimate/${yestimateId}`, buildYestimatePayload());
      setAutoSaveStatus('saved');
      setLastSavedAt(new Date());
    } catch (err) {
      setAutoSaveStatus('error');
      throw err;
    }
  }, [yestimateId, jobGroups, buildYestimatePayload]);

  const triggerAutoSave = useCallback(() => {
    setAutoSaveStatus('unsaved');
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer for auto-save after 2 seconds
    autoSaveTimerRef.current = setTimeout(async () => {
      if (jobGroups.length === 0) return; // Don't save if no job groups
      
      setAutoSaveStatus('saving');
      try {
        await axios.put(`${API}/foh/yestimate/${yestimateId}`, buildYestimatePayload());
        setAutoSaveStatus('saved');
        setLastSavedAt(new Date());
        
        // Refresh JCU evaluation after save
        refreshJCUEvaluation();
      } catch (err) {
        console.error('Auto-save failed:', err);
        setAutoSaveStatus('error');
        // Show helpful error message
        const errorMsg = getErrorMessage(err, 'Auto-save failed');
        if (errorMsg && errorMsg !== 'Auto-save failed') {
          toast.error(`Save failed: ${errorMsg}`, { duration: 5000 });
        }
      }
    }, 2000);
  }, [yestimateId, jobGroups, tone, customerName, currentJobValue, ignoredItems]);

  // Trigger auto-save when pricing data changes
  useEffect(() => {
    if (data && jobGroups.length > 0) {
      triggerAutoSave();
    }
    
    // Cleanup on unmount
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [jobGroups, triggerAutoSave]);
  
  // ============== PIE VALIDATION EFFECT ==============
  // Trigger PIE validation when job groups change (debounced)
  useEffect(() => {
    if (!data || jobGroups.length === 0) return;
    
    // Debounce PIE validation to avoid too many API calls
    const timer = setTimeout(() => {
      validateAllJobGroupsWithPIE();
    }, 1500);  // Wait 1.5 seconds after last change
    
    return () => clearTimeout(timer);
  }, [jobGroups]);

  // ============== JCE ITEM SELECTION WITH SCROLL ==============
  // When JCE selects an item, scroll the middle panel to show that item
  const handleJCESelectJob = useCallback((item) => {
    if (!item) {
      setSelectedJobForJCE(null);
      return;
    }
    
    setSelectedJobForJCE(item);
    
    // Find the matching job group in the middle panel
    const itemKey = item.job_key || item.item_key;
    const matchingJobGroup = jobGroups.find(jg => 
      jg.id === item.id ||
      jg.vi_item_key === itemKey ||
      jg.inspection_item_id === item.id
    );
    
    if (matchingJobGroup && jobGroupRefs.current[matchingJobGroup.id]) {
      // Scroll the job group card into view
      jobGroupRefs.current[matchingJobGroup.id].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      // Expand the job group
      setExpandedJobs(prev => ({ ...prev, [matchingJobGroup.id]: true }));
    }
  }, [jobGroups]);

  // ============== IGNORE ITEM FUNCTIONALITY ==============
  const handleIgnoreItem = (type, itemId, label) => {
    setIgnoreTarget({ type, itemId, label });
    setIgnoreReason('');
    setIgnoreNotes('');
    setShowIgnoreModal(true);
  };

  const confirmIgnoreItem = async () => {
    if (!ignoreTarget || !ignoreReason) return;
    
    const newIgnoredItem = {
      id: ignoreTarget.itemId,
      type: ignoreTarget.type,
      label: ignoreTarget.label,
      reason: ignoreReason,
      notes: ignoreNotes,
      ignored_by: 'FOH',
      ignored_at: new Date().toISOString()
    };
    
    setIgnoredItems(prev => [...prev, newIgnoredItem]);
    
    // Remove any existing job group for this item
    if (ignoreTarget.type === 'inspection') {
      setJobGroups(prev => prev.filter(jg => jg.inspection_item_id !== ignoreTarget.itemId));
    } else if (ignoreTarget.type === 'diagnostic') {
      setJobGroups(prev => prev.filter(jg => jg.source !== 'diagnostic'));
      setHasDiagnosticJobGroup(false);
    }
    
    setShowIgnoreModal(false);
    setIgnoreTarget(null);
    triggerAutoSave();
  };

  const unignoreItem = (itemId) => {
    setIgnoredItems(prev => prev.filter(i => i.id !== itemId));
    triggerAutoSave();
  };

  // ============== CUSTOM JOB FUNCTIONALITY ==============
  const handleAddCustomJob = () => {
    setCustomJobTitle('');
    setCustomJobDescription('');
    setShowCustomJobModal(true);
  };

  const confirmAddCustomJob = () => {
    if (!customJobTitle.trim()) return;
    
    const newJobGroup = {
      id: `custom-${Date.now()}`,
      inspection_item_id: null, // Not linked to any inspection item
      title: customJobTitle.trim(),
      source: "custom", // Mark as custom job
      status: null,
      description: customJobDescription,
      labour_lines: [{
        id: Date.now().toString(),
        category: "strip_renew",
        description: "",
        hours: 0,
        rate: getLabourRate("strip_renew", 65)
      }],
      parts_lines: [],
      charges: []
    };
    
    setJobGroups(prev => [...prev, newJobGroup]);
    setExpandedJobs(prev => ({ ...prev, [newJobGroup.id]: true }));
    setShowCustomJobModal(false);
    
    // Fetch suggestions for the custom job
    fetchSuggestions(newJobGroup.id, customJobTitle.trim(), customJobTitle.trim());
    triggerAutoSave();
  };

  // Change inspection item status (Red <-> Amber)
  const changeItemStatus = async (itemId, newStatus) => {
    try {
      await axios.put(`${API}/foh/inspection/${data.yestimate.inspection_id}/item/${itemId}/status`, {
        status: newStatus
      });
      
      // Update local data
      setData(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.id === itemId ? { ...item, status: newStatus } : item
        )
      }));
      
      // Also update any existing job groups linked to this item
      setJobGroups(prev => prev.map(jg => 
        jg.inspection_item_id === itemId ? { ...jg, status: newStatus } : jg
      ));
      
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to change item status");
    }
  };

  // Handle suggestion action - now uses JCU apply endpoint for "add" action
  const handleSuggestionAction = async (jobGroupId, suggestionId, action, reason = null) => {
    setSuggestionActions(prev => ({
      ...prev,
      [jobGroupId]: {
        ...(prev[jobGroupId] || {}),
        [suggestionId]: { action, reason }
      }
    }));

    // If "add" action, call the JCU apply endpoint
    if (action === "add") {
      const suggestion = suggestions[jobGroupId]?.find(s => s.id === suggestionId);
      if (suggestion && yestimateId) {
        try {
          // Call the JCU apply suggestion endpoint
          await axios.post(`${API}/jcu/v2/yestimate/${yestimateId}/suggestions/apply`, {
            job_group_id: jobGroupId,
            suggestion_id: suggestionId,
            suggestion_category: suggestion.category,
            suggestion_name: suggestion.name,
            suggestion_reason: suggestion.reason,
            default_hours: suggestion.default_hours
          });
          
          // Optimistically update local state for immediate UI feedback
          if (suggestion.category === "labour") {
            setJobGroups(prev => prev.map(jg => {
              if (jg.id !== jobGroupId) return jg;
              return {
                ...jg,
                labour_lines: [...jg.labour_lines, {
                  id: Date.now().toString(),
                  category: "repair_labour",
                  description: suggestion.name,
                  hours: suggestion.default_hours || 0.5,
                  rate: getLabourRate("strip_renew", 65),
                  source: "jcu_suggestion"
                }]
              };
            }));
          } else if (suggestion.category === "parts") {
            setJobGroups(prev => prev.map(jg => {
              if (jg.id !== jobGroupId) return jg;
              return {
                ...jg,
                parts_lines: [...jg.parts_lines, {
                  id: Date.now().toString(),
                  description: suggestion.name,
                  quantity: 1,
                  unit_price: 0,
                  buy_in_price: 0,
                  vat_rate: 20,
                  source: "jcu_suggestion"
                }]
              };
            }));
          } else if (suggestion.category === "consumables") {
            setJobGroups(prev => prev.map(jg => {
              if (jg.id !== jobGroupId) return jg;
              return {
                ...jg,
                charges: [...jg.charges, {
                  id: Date.now().toString(),
                  charge_type: "consumables",
                  description: suggestion.name,
                  amount: 0,
                  vat_rate: 20,
                  source: "jcu_suggestion"
                }]
              };
            }));
          }
          
          // Refresh JCU evaluation
          refreshJCUEvaluation();
          
          // Show success toast
          toast.success(`Added "${suggestion.name}" to job`);
        } catch (err) {
          console.error("Failed to apply JCU suggestion:", err);
          // Revert the action state on error
          setSuggestionActions(prev => ({
            ...prev,
            [jobGroupId]: {
              ...(prev[jobGroupId] || {}),
              [suggestionId]: undefined
            }
          }));
          toast.error("Failed to add suggestion");
        }
      }
    }
  };

  // Auto-apply pricing templates from Rule Engine for a job group
  const autoApplyRulePricing = async (jobGroupId, viItemKey) => {
    if (!viItemKey || !yestimateId) return;
    
    try {
      // First, resolve the rule for this VI item
      const resolveRes = await axios.get(`${API}/api/rules/resolve/${viItemKey}`);
      const { rule, is_draft } = resolveRes.data;
      
      // Only auto-apply for active (non-draft) rules with pricing templates
      if (is_draft || !rule || !rule.pricing_templates || rule.pricing_templates.length === 0) {
        return;
      }
      
      // iter214b — Flush the debounced autosave BEFORE hitting the
      // Rule Engine. The Rule Engine looks up the job group by id on
      // the persisted yestimate; if the JG only exists locally
      // (created moments ago in createJobGroup), the apply either
      // silently no-ops or targets a stale row. Flushing guarantees
      // the JG is server-side before we ask the engine to touch it,
      // and — critically — guarantees the follow-up GET refresh
      // (below) returns a job_groups list that INCLUDES the JG the
      // user just added. Prior to this fix the refresh replaced the
      // in-memory list with a server list that had never seen the JG,
      // making it appear to "disappear after 2 seconds".
      try {
        await flushAutoSave();
      } catch (flushErr) {
        // If the flush fails, don't proceed — leaving local state
        // intact is safer than a partial rules-apply + refresh cycle.
        console.log('Auto-apply: pre-flush failed, skipping.', flushErr?.message);
        return;
      }

      // Apply the rule's pricing templates
      const applyRes = await axios.post(`${API}/api/rules/apply`, {
        yestimate_id: yestimateId,
        job_group_id: jobGroupId,
        rule_id: rule.rule_id,
        selected_side: null,
        source: 'AUTO_POPULATE'
      });
      
      if (applyRes.data.success && applyRes.data.lines_added > 0) {
        // Targeted merge — replace ONLY the job group we just
        // pricing-applied. Never wholesale-replace jobGroups from a
        // server GET here: if any other JG was added by the user in
        // the same tick, a wholesale replace would wipe it too.
        const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
        const serverGroups = yestimateRes.data?.job_groups || [];
        const serverJg = serverGroups.find(g => g.id === jobGroupId);
        if (serverJg) {
          const merged = sanitizeJobGroups([serverJg]);
          setJobGroups(prev => prev.map(jg => jg.id === jobGroupId ? merged[0] : jg));
          toast.success(`Auto-populated: ${rule.canonical_name}`, { duration: 2000 });
        } else {
          // Server does not know about this JG yet — leave local
          // state untouched. Autosave will persist it on its next
          // debounced tick.
          console.log('Auto-apply: server has no matching JG yet; skipping refresh to preserve local state.');
        }
      }
    } catch (err) {
      // Silently fail for auto-population - user can still manually apply
      console.log('Auto-apply pricing skipped:', err.message);
    }
  };

  // Create new job group from inspection item
  const createJobGroup = async (item) => {
    // DEDUPLICATION CHECK: Don't create if a job group already exists for this inspection item
    const existingJobGroup = jobGroups.find(jg => jg.inspection_item_id === item.id);
    if (existingJobGroup) {
      console.log(`Job group already exists for inspection item ${item.id}, focusing on existing`);
      // Focus on existing job group instead of creating duplicate
      const newExpanded = {};
      jobGroups.forEach(jg => { newExpanded[jg.id] = false; });
      newExpanded[existingJobGroup.id] = true;
      setExpandedJobs(newExpanded);
      setSelectedJobForJCE(existingJobGroup);
      return;
    }

    // iter211 — Tyre items live inside the single tyre_replacement_group
    // JG, not as standalone job groups. If a tyre group is already on the
    // pricing screen, clicking a tyre row in the left panel must add the
    // tyre to THAT group (via the existing upsert tyre-children endpoint)
    // instead of spawning a duplicate standalone tyre JG.
    const tyreGroup = jobGroups.find(jg => jg.subtype === "tyre_replacement_group");
    const looksLikeTyre =
      /^tyre(_|s$|$)|^tire(_|s$|$)|_tyre$|_tire$/i.test(item.item_key || "") ||
      /\btyre\b|\btire\b/i.test(item.item_label || item.label || "");
    if (tyreGroup && looksLikeTyre) {
      // Resolve which corner this item refers to. We try (in order):
      // 1. an explicit position field, 2. item.affected_side, 3. the
      // item_key, 4. the item_label / notes free text.
      const blob = [
        item.position, item.tyre_position, item.affected_side,
        item.item_key, item.item_label, item.label, item.notes,
      ].filter(Boolean).join(" ").toLowerCase();
      let pos = null;
      const checks = [
        [/\b(nsf|n\.?s\.?f|front[\s_-]*left|left[\s_-]*front|fl)\b/, "NSF"],
        [/\b(osf|o\.?s\.?f|front[\s_-]*right|right[\s_-]*front|fr)\b/, "OSF"],
        [/\b(nsr|n\.?s\.?r|rear[\s_-]*left|left[\s_-]*rear|rl)\b/, "NSR"],
        [/\b(osr|o\.?s\.?r|rear[\s_-]*right|right[\s_-]*rear|rr)\b/, "OSR"],
      ];
      for (const [re, p] of checks) {
        if (re.test(blob)) { pos = p; break; }
      }
      // If a position is resolved and that corner ISN'T already priced
      // in the tyre group, upsert it. Otherwise just focus the group.
      if (pos) {
        const existingChildren = Array.isArray(tyreGroup.tyres) ? tyreGroup.tyres : [];
        const alreadyOnGroup = existingChildren.some(c =>
          (c?.position || "").toUpperCase() === pos
          && c?.replacement_required !== false
        );
        if (!alreadyOnGroup) {
          try {
            await axios.post(
              `${API}/foh/yestimate/${yestimateId}/job-group/${tyreGroup.id}/tyre-children`,
              {
                position: pos,
                severity: item.severity || "advisory",
                replacement_required: true,
                reason: item.notes || `Added from VI item — ${item.item_label || item.label || ""}`.trim(),
              },
            );
            toast.success(`${pos} added to Tyre Replacement group.`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add tyre to group.");
          }
          await (async () => {
            try {
              const refresh = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
              setData(refresh.data);
            } catch { /* refresh failure is non-blocking */ }
          })();
        } else {
          toast.info(`${pos} is already on the Tyre Replacement group.`);
        }
      } else {
        toast.info("Tyre Replacement group already in place — open it to add another corner.");
      }
      // Always focus the existing group so FOH lands on the right card.
      const newExpanded = {};
      jobGroups.forEach(jg => { newExpanded[jg.id] = false; });
      newExpanded[tyreGroup.id] = true;
      setExpandedJobs(newExpanded);
      setSelectedJobForJCE(tyreGroup);
      return;
    }
    
    // Use technician notes as title if available, otherwise fall back to item_label
    let jobTitle = item.item_label;
    
    // For consolidated brake items, always use the combined label
    const isConsolidatedBrake = item.consolidated && item.item_key?.includes('brake_discs');
    if (isConsolidatedBrake) {
      jobTitle = item.item_label; // "Front Brake Discs & Pads" or "Rear Brake Discs & Pads"
    } else if (item.notes && item.notes.trim()) {
      const trimmedNotes = item.notes.trim();
      jobTitle = trimmedNotes.length > 60 
        ? trimmedNotes.substring(0, 60) + "..." 
        : trimmedNotes;
    }
    
    // Get vehicle context from current yestimate/job
    const vehicleContext = {
      make: data?.job?.make || '',
      model: data?.job?.model || '',
      mileage: data?.job?.mileage || 0,
      year: data?.job?.year || null
    };
    
    // Try PIE auto-populate first
    let pieData = null;
    let usedPIE = false;
    
    // Check if this is an Air Con job - needs special handling for gas type
    const isAirConItem = item.item_key === 'aircon_operation' || 
                         /air.*con|aircon|a\/c|ac.*service|climate/i.test(item.item_key || '') ||
                         /air.*con|aircon|a\/c|climate/i.test(item.item_label || '');
    
    if (item.item_key) {
      try {
        // Build PIE request payload
        const piePayload = {
          vi_item_key: item.item_key,
          job_title: jobTitle,
          vehicle_make: vehicleContext.make,
          vehicle_model: vehicleContext.model,
          vehicle_mileage: vehicleContext.mileage,
          vehicle_year: vehicleContext.year
        };
        
        // For Air Con jobs, pass the refrigerant type from VI data
        if (isAirConItem && item.ac_gas_type) {
          piePayload.refrigerant_type = item.ac_gas_type;
          piePayload.ac_performance = item.ac_performance || null;
        }
        
        const pieResponse = await axios.post(`${API}/pie/auto-populate-by-vi-key`, piePayload);
        
        if (pieResponse.data?.has_pie_data) {
          pieData = pieResponse.data;
          usedPIE = true;
          console.log('PIE auto-populate successful:', pieData.repair_intent_code);
        }
      } catch (pieErr) {
        console.log('PIE auto-populate not available for this item:', pieErr.message);
      }
    }
    
    // Initialize labour, parts, and charges
    let autoLabour = [];
    let autoParts = [];
    let autoCharges = [];
    let pieTitle = jobTitle;
    let repairIntentCode = null;
    let appliedRules = [];
    
    if (usedPIE && pieData) {
      // Use PIE data
      autoLabour = pieData.labour_lines || [];
      autoParts = pieData.parts_lines || [];
      autoCharges = pieData.charges || [];
      pieTitle = pieData.title || jobTitle;
      repairIntentCode = pieData.repair_intent_code;
      appliedRules = pieData.applied_rules || [];
      
      // Show toast with PIE explanation
      const labourCount = autoLabour.length;
      const partsCount = autoParts.length;
      const chargesCount = autoCharges.length;
      if (labourCount + partsCount + chargesCount > 0) {
        toast.success(`PIE auto-populated: ${labourCount} labour, ${partsCount} parts, ${chargesCount} charges`, {
          duration: 4000
        });
      }
    } else {
      // === LEGACY FALLBACK: Use hardcoded auto-population for specific job types ===
      const isTyreJob = /tyre|tire/i.test(jobTitle) || /tyre|tire/i.test(item.item_label) || item.item_key?.includes('tyre');
      const isBrakeFluidJob = /brake.*fluid|brake_fluid/i.test(item.item_key || '') || /brake.*fluid/i.test(item.item_label || '');
      const isCoolantJob = /coolant|antifreeze/i.test(item.item_key || '') || /coolant|antifreeze/i.test(item.item_label || '');
      const isAirConJob = /air.*con|aircon|a\/c|ac.*service|climate/i.test(item.item_key || '') || /air.*con|aircon|a\/c|climate/i.test(item.item_label || '');
      const isBatteryJob = /battery/i.test(item.item_key || '') || /battery/i.test(item.item_label || '');
      
      // Tyre package
      if (isTyreJob) {
        autoCharges = [
          { id: `${Date.now()}-fitting`, charge_type: 'labour', description: "Tyre fitting", amount: 12.00, vat_rate: 20 },
          { id: `${Date.now()}-valve`, charge_type: 'parts', description: "New tyre valve", amount: 2.50, vat_rate: 20 },
          { id: `${Date.now()}-balancing`, charge_type: 'labour', description: "Wheel balancing", amount: 8.00, vat_rate: 20 },
          { id: `${Date.now()}-disposal`, charge_type: 'disposal', description: "Old tyre disposal", amount: 3.50, vat_rate: 20 }
        ];
        toast.info('Tyre package added: Fitting + Valve + Balancing + Disposal', { duration: 3000 });
      }
      
      // Brake fluid package
      if (isBrakeFluidJob) {
        autoLabour = [{
          id: `${Date.now()}-bf-labour`,
          category: "strip_renew",
          description: "Brake fluid change - bleed all four corners",
          hours: 0.5,
          rate: getLabourRate("strip_renew", 65)
        }];
        autoParts = [{
          id: `${Date.now()}-bf-part`,
          description: "Brake fluid DOT 4 (1 litre)",
          quantity: 1,
          unit_price: 15.00
        }];
      }
      
      // Coolant package
      if (isCoolantJob) {
        autoLabour = [{
          id: `${Date.now()}-cool-labour`,
          category: "strip_renew",
          description: "Coolant drain, flush and refill",
          hours: 0.75,
          rate: getLabourRate("strip_renew", 65)
        }];
        autoParts = [{
          id: `${Date.now()}-cool-part`,
          description: "Coolant/Antifreeze (5 litres)",
          quantity: 1,
          unit_price: 25.00
        }];
      }
      
      // Air con package - based on gas type from VI
      if (isAirConJob) {
        // Determine gas type from VI data - default to R134a for older vehicles
        const gasType = item.ac_gas_type || 'R134a';
        const isNewGas = gasType === 'R1234yf';
        
        // Labour: A/C service + pollen filter labour
        autoLabour = [
          {
            id: `${Date.now()}-ac-labour`,
            category: "aircon_work",
            description: `Air conditioning service - evacuate, leak test and recharge (${gasType})`,
            hours: 1.0,
            rate: getLabourRate("aircon_work", 85)
          },
          {
            id: `${Date.now()}-pollen-labour`,
            category: "strip_renew",
            description: "Pollen filter replacement",
            hours: 0.25,
            rate: getLabourRate("strip_renew", 65)
          }
        ];
        
        // Parts: Gas, compressor oil, pollen filter, sanitiser
        autoParts = [
          {
            id: `${Date.now()}-ac-gas`,
            description: isNewGas ? "Refrigerant R1234yf (per gram)" : "Refrigerant R134a (per gram)",
            quantity: 1,
            unit_price: isNewGas ? 85.00 : 35.00  // R1234yf is more expensive
          },
          {
            id: `${Date.now()}-ac-oil`,
            description: isNewGas ? "A/C Compressor Oil (PAG)" : "A/C Compressor Oil",
            quantity: 1,
            unit_price: 12.00
          },
          {
            id: `${Date.now()}-pollen-filter`,
            description: "Pollen Filter (Cabin Filter)",
            quantity: 1,
            unit_price: 18.00
          },
          {
            id: `${Date.now()}-ac-sanitiser`,
            description: "A/C System Sanitiser",
            quantity: 1,
            unit_price: 8.00
          }
        ];
        
        // Charges: UV dye for leak detection
        autoCharges = [
          { id: `${Date.now()}-ac-dye`, charge_type: 'consumables', description: "UV dye for leak detection", amount: 5.00, vat_rate: 20 }
        ];
        
        toast.info(`A/C Service Package (${gasType}): Regas + Pollen Filter + Sanitiser`, { duration: 4000 });
      }
      
      // Battery package
      if (isBatteryJob) {
        autoLabour = [{
          id: `${Date.now()}-batt-labour`,
          category: "strip_renew",
          description: "Battery replacement - remove and fit",
          hours: 0.5,
          rate: getLabourRate("strip_renew", 65)
        }];
        if (item.battery_registration_required === 'yes') {
          autoLabour.push({
            id: `${Date.now()}-batt-coding`,
            category: "programming",
            description: "Battery registration/coding via diagnostic",
            hours: 0.25,
            rate: getLabourRate("programming_coding", 95)
          });
        }
      }
    }
    
    const jobGroupId = Date.now().toString();
    
    // =====================================================================
    // AUTO-ADD DIAGNOSTIC ASSESSMENT FOR ITEMS REQUIRING INVESTIGATION
    // =====================================================================
    // Items that require diagnostic to establish the nature of issue/compile repair plan
    const ITEMS_REQUIRING_DIAGNOSTIC = [
      // Warning lights & Electronic faults
      'dash_warning_lamps',
      'engine_management_light',
      'eml',
      // DPF / Emissions
      'dpf_condition',
      'catalytic_converter',
      // Leaks (source unknown requires diagnosis)
      'engine_transmission_leaks',
      // Air conditioning (if not working - needs diagnosis)
      'aircon_operation',
      // Noise/vibration investigations
      'wheel_bearings',  // When suspected but not confirmed
      // Electrical systems
      'horn_operation',  // If fault found
      'heater_fan_controls',  // If fault found
    ];
    
    // Check if this item requires diagnostic assessment
    const requiresDiagnostic = ITEMS_REQUIRING_DIAGNOSTIC.some(key => 
      item.item_key?.toLowerCase().includes(key.toLowerCase())
    );
    
    // Also check for symptom-based issues in the notes/status
    const hasSymptomBasedIssue = 
      item.notes?.toLowerCase().includes('noise') ||
      item.notes?.toLowerCase().includes('vibration') ||
      item.notes?.toLowerCase().includes('intermittent') ||
      item.notes?.toLowerCase().includes('suspected') ||
      item.notes?.toLowerCase().includes('investigation') ||
      item.notes?.toLowerCase().includes('unknown') ||
      item.notes?.toLowerCase().includes('fault code') ||
      item.notes?.toLowerCase().includes('warning light');
    
    // Auto-add diagnostic labour line if required
    if ((requiresDiagnostic || hasSymptomBasedIssue) && item.status !== 'blue') {
      // Check if diagnostic already added via PIE
      const hasDiagnosticAlready = autoLabour.some(l => 
        l.description?.toLowerCase().includes('diagnostic') ||
        l.description?.toLowerCase().includes('investigation') ||
        l.category === 'diagnostic_labour'
      );
      
      if (!hasDiagnosticAlready) {
        // Default diagnostic: 1 hour @ £125 inc VAT (£104.17 ex VAT)
        // Rate pulled from Price Book (LAB_DIAG → diagnostics category)
        const diagnosticRate = getLabourRate("diagnostics", 104.17);
        const diagnosticLabour = {
          id: `${jobGroupId}-diag-${Date.now()}`,
          category: "diagnostic_labour",
          description: "Diagnostic investigation to establish fault and compile repair plan",
          hours: 1.0,
          rate: diagnosticRate,
          is_auto_diagnostic: true,  // Flag to identify auto-added diagnostic
          reason: requiresDiagnostic 
            ? `Auto-added: ${item.item_key} requires diagnostic assessment`
            : `Auto-added: Issue requires investigation (${item.notes?.substring(0, 50)}...)`
        };
        autoLabour.push(diagnosticLabour);
        
        toast.info('Diagnostic assessment added - investigation required to establish fault', {
          duration: 4000
        });
      }
    }
    
    const newJobGroup = {
      id: jobGroupId,
      inspection_item_id: item.id,
      title: usedPIE ? pieTitle : jobTitle,
      source: "inspection",  // Track source: inspection or diagnostic
      status: item.status || "amber",  // Track the inspection status
      vi_status: item.status || "amber",  // Track the VI status for priority sorting
      vi_item_key: item.item_key, // Store the item key for Rule Engine mapping
      affected_side: item.affected_side || null, // Store the affected side for pairs checking
      consolidated: item.consolidated || false, // Mark if this is a consolidated item
      original_items: item.original_items || null, // Store original items for consolidated
      // Battery replacement fields from inspection
      battery_type_required: item.battery_type_required || null,
      battery_registration_required: item.battery_registration_required || null,
      battery_technician_note: item.battery_technician_note || null,
      // Battery test results
      battery_voltage_v: item.battery_voltage_v || null,
      measured_cca_a: item.measured_cca_a || null,
      rated_cca_a: item.rated_cca_a || null,
      health_percent: item.health_percent || null,
      notes: item.notes || null,  // General tech notes
      // PIE tracking
      repair_intent_code: repairIntentCode,
      pie_applied_rules: appliedRules,
      pie_populated: usedPIE,
      // Pricing lines
      labour_lines: autoLabour,
      parts_lines: autoParts,
      charges: autoCharges
    };
    
    // ============== WEAR SENSOR AUTO-INJECTION ==============
    // For brake pad / disc items, check if the corresponding pad item has wear_sensor_required
    const isBrakePadOrDiscJob = item.item_key?.includes('brake_pads') || item.item_key?.includes('brake_discs');
    if (isBrakePadOrDiscJob) {
      // Determine which pad item to check
      let padItemKey = item.item_key;
      if (item.item_key?.includes('brake_discs')) {
        // Disc job: check corresponding pads item for wear sensor
        padItemKey = item.item_key.replace('brake_discs', 'brake_pads');
      }
      
      // Find the pad inspection item to check wear_sensor_required
      // For consolidated items, check original items
      let wearSensor = item.wear_sensor_required;
      if (!wearSensor && item.consolidated && item.original_items) {
        const padOriginal = item.original_items.find(oi => oi.item_key === padItemKey);
        if (padOriginal?.wear_sensor_required) wearSensor = true;
      }
      // Also check from the data.items array (inspection items loaded in pricing)
      if (!wearSensor && data?.items) {
        const padInspItem = data.items.find(ii => ii.item_key === padItemKey);
        if (padInspItem?.wear_sensor_required) wearSensor = true;
      }
      
      if (wearSensor) {
        const side = padItemKey.includes('front') ? 'Front' : 'Rear';
        const hasWearSensorAlready = newJobGroup.parts_lines.some(
          p => p.description?.toLowerCase().includes('wear sensor')
        );
        if (!hasWearSensorAlready) {
          newJobGroup.parts_lines.push({
            id: `${jobGroupId}-wear-sensor`,
            description: `${side} Brake Pad Wear Sensor`,
            quantity: 1,
            unit_price: 0,
            is_placeholder: true,
            wear_sensor_auto_added: true
          });
          toast.info(`Wear sensor added - marked as required by technician`, { duration: 3000 });
        }
      }
    }
    
    // ============== BATTERY TYPE ENRICHMENT ==============
    // Append battery type to parts description so FOH knows exactly what to price
    if (item.battery_type_required && item.item_key === 'battery_test') {
      const typeLabel = item.battery_type_required === 'standard' ? 'Standard' :
                        item.battery_type_required === 'efb' ? 'EFB' :
                        item.battery_type_required === 'agm' ? 'AGM' : item.battery_type_required;
      const regNote = (item.battery_registration_required === 'yes' || item.battery_registration_required === true) 
        ? ' (Registration Required)' : '';
      
      // Find or create the battery parts line and append the type
      const batteryPartIdx = newJobGroup.parts_lines.findIndex(p => 
        p.description?.toLowerCase().includes('battery')
      );
      if (batteryPartIdx >= 0) {
        // Update existing battery part description
        const part = newJobGroup.parts_lines[batteryPartIdx];
        if (!part.description.includes(typeLabel)) {
          newJobGroup.parts_lines[batteryPartIdx] = {
            ...part,
            description: `${part.description} - ${typeLabel}${regNote}`
          };
        }
      } else {
        // No battery part exists - create one with the type indicated
        newJobGroup.parts_lines.push({
          id: `${jobGroupId}-battery-part`,
          description: `Vehicle Battery - ${typeLabel}${regNote}`,
          quantity: 1,
          unit_price: 0,
          is_placeholder: true,
          battery_type: item.battery_type_required
        });
      }
    }
    
    setJobGroups([...jobGroups, newJobGroup]);
    // Focus on new job group - collapse all others
    const newExpanded = {};
    jobGroups.forEach(jg => { newExpanded[jg.id] = false; });
    newExpanded[newJobGroup.id] = true;
    setExpandedJobs(newExpanded);
    setSelectedJobForJCE(newJobGroup);  // Set as active job for Job Intelligence panel
    // Fetch suggestions for the new job group
    fetchSuggestions(newJobGroup.id, jobTitle, item.item_label);
    
    // Show battery type notification if battery job
    if (item.battery_type_required && item.item_key === 'battery_test') {
      const typeLabel = item.battery_type_required === 'standard' ? 'Standard' :
                        item.battery_type_required === 'efb' ? 'EFB' :
                        item.battery_type_required === 'agm' ? 'AGM' : 'Unknown';
      toast.info(`Battery type required: ${typeLabel}${(item.battery_registration_required === 'yes' || item.battery_registration_required === true) ? ' (Registration needed)' : ''}`, {
        duration: 5000
      });
    }
    
    // Show RADIO CODE ALERT for battery replacement jobs
    const isBatteryJob = item.item_key === 'battery_test' || 
                         item.item_label?.toLowerCase().includes('battery') ||
                         jobTitle?.toLowerCase().includes('battery');
    if (isBatteryJob) {
      // Show the radio code alert popup
      setShowBatteryRadioCodeAlert(true);
    }
    
    // Auto-apply pricing templates from Rule Engine (async, non-blocking)
    // Only if PIE didn't already populate
    if (item.item_key && !usedPIE) {
      autoApplyRulePricing(jobGroupId, item.item_key);
    }
    
    // Show toast for consolidated brake items
    if (isConsolidatedBrake) {
      toast.info('Discs & Pads combined - discs are never fitted without pads', {
        duration: 3000
      });
    }
    
    // JCE: Auto-open panel 300-500ms after job add (random delay for natural feel)
    if (jceAutoOpenTimer) clearTimeout(jceAutoOpenTimer);
    const delay = 300 + Math.random() * 200; // 300-500ms
    const timer = setTimeout(() => {
      setShowJCEPanel(true);
    }, delay);
    setJceAutoOpenTimer(timer);
    
    // Check for "commonly done in pairs" reminder (async, non-blocking)
    // Using new pair policy API - handles mandatory bundles and pair policies
    const itemKeyLower = item.item_key?.toLowerCase() || '';
    const itemLabelLower = item.item_label?.toLowerCase() || '';
    
    try {
      const pairCheckRes = await axios.post(`${API}/pair-policy/check-add-item`, {
        vi_item_key: item.item_key || '',
        vi_item_label: item.item_label || '',
        vi_item_notes: item.notes || '',
        vi_item_status: item.status || '',
        affected_side: item.affected_side || null,
        inspection_id: null,
        yestimate_id: yestimateId,
        current_job_groups: jobGroups.map(jg => ({
          id: jg.id,
          vi_item_key: jg.vi_item_key || '',
          title: jg.title || '',
          affected_side: jg.affected_side || '',
          position: jg.position || '',
          component_family: jg.component_family || '',
          pair_override: jg.pair_override || null
        }))
      });
      
      const pairData = pairCheckRes.data;
      
      // Handle mandatory bundle (brakes) - just show info toast
      if (pairData.is_mandatory_bundle) {
        toast.info(`${pairData.prompt_data?.display_name || 'Item'} added as axle set`, {
          duration: 3000
        });
        // No modal needed - brakes are auto-bundled
        return;
      }
      
      // Handle pair policy prompt
      if (pairData.needs_prompt && pairData.prompt_type) {
        // For INFO_BANNER, show toast instead of modal
        if (pairData.prompt_type === 'INFO_BANNER') {
          toast.info(pairData.prompt_data?.foh_prompt || 'Consider if the opposite side also needs attention', {
            duration: 4000
          });
        } else {
          // Show modal for RECOMMEND_MODAL, OVERRIDE_MODAL, AUTO_DRAFT
          setPairPolicyModal({
            show: true,
            promptType: pairData.prompt_type,
            promptData: pairData.prompt_data,
            pendingItem: item,
            isMandatoryBundle: false,
            bundleData: null,
            pairResolution: pairData.pair_resolution
          });
        }
      }
    } catch (err) {
      console.warn('Pair policy check failed:', err);
      // Fallback: Show reminder for safety-critical items (shocks/springs)
      const isSpringItem = itemKeyLower.includes('spring') || itemLabelLower.includes('spring');
      const isShockItem = itemKeyLower.includes('shock') || itemLabelLower.includes('shock');
      
      if (isSpringItem || isShockItem) {
        const pairName = isSpringItem 
          ? (itemKeyLower.includes('front') ? 'Front Springs' : 'Rear Springs')
          : (itemKeyLower.includes('front') ? 'Front Shock Absorbers' : 'Rear Shock Absorbers');
        setPairPolicyModal({
          show: true,
          promptType: 'OVERRIDE_MODAL',
          promptData: {
            display_name: pairName,
            foh_prompt: isSpringItem 
              ? 'Springs should be replaced in pairs to maintain correct ride height and handling balance.'
              : 'Shock absorbers should be replaced in pairs for balanced handling and braking.',
            override_reasons: {
              customer_request: "Customer specifically requested single side",
              budget_constraint: "Budget constraint discussed with customer",
              opposite_recently_done: "Opposite side replaced within last 12 months",
              opposite_inspected_ok: "Opposite side inspected and confirmed serviceable",
              will_monitor: "Will monitor opposite side at next service",
              other: "Other reason"
            }
          },
          pendingItem: item,
          isMandatoryBundle: false,
          bundleData: null
        });
      }
    }
  };

  // Create diagnostic rectification job group (MANDATORY)
  const createDiagnosticJobGroup = () => {
    if (hasDiagnosticJobGroup) return; // Already exists
    
    // Use diagnostic conclusion as the title if available
    const diagTitle = diagnosticInfo?.diagnostic_conclusion 
      ? diagnosticInfo.diagnostic_conclusion.substring(0, 60) + (diagnosticInfo.diagnostic_conclusion.length > 60 ? "..." : "")
      : "Diagnostic Rectification";
    
    const newJobGroup = {
      id: `diag-${Date.now()}`,
      inspection_item_id: null,
      title: diagTitle,
      source: "diagnostic",  // Mark as diagnostic source
      status: "red",  // Diagnostic items are ALWAYS RED
      is_mandatory: true,  // Cannot be removed
      diagnostic_conclusion: diagnosticInfo?.diagnostic_conclusion,
      customer_concern: diagnosticInfo?.customer_concern,
      labour_lines: [{
        id: Date.now().toString(),
        category: "diagnostic_labour",
        description: "Diagnostic investigation to establish fault and compile repair plan",
        hours: 1,
        rate: getLabourRate("diagnostics", 104.17),
        is_auto_diagnostic: true
      }],
      parts_lines: [],
      charges: []
    };
    setJobGroups([newJobGroup, ...jobGroups]);  // Add at the top (first)
    setExpandedJobs({ ...expandedJobs, [newJobGroup.id]: true });
    setHasDiagnosticJobGroup(true);
  };

  // ============== SERVICE INTERVAL FUNCTIONS ==============
  
  // Add a service interval item as a job group (same as inspection items)
  // Now with Price Book snapshot pricing support
  const addIntervalItemAsJob = async (categoryId, item) => {
    // Create a job group for this service interval item
    const jobGroupId = `interval-${Date.now()}`;
    const jobTitle = item.label;
    
    let labourLines = [];
    let partsLines = [];
    let priceBookSource = null;
    
    // Check if this service item has a linked Price Book package
    try {
      const mappingRes = await axios.get(`${API}/service-mappings/${item.item_id}`);
      const mapping = mappingRes.data;
      
      if (mapping.price_book_package) {
        // Get snapshotted pricing from Price Book
        const linesRes = await axios.post(`${API}/price-book/generate-estimate-lines/${mapping.price_book_package}`, []);
        const lines = linesRes.data.lines || [];
        
        // Convert Price Book lines to job group format
        lines.forEach(line => {
          if (line.category === 'labour') {
            labourLines.push({
              id: line.id,
              category: line.category || 'strip_renew',
              description: line.description,
              hours: line.hours || line.qty,
              rate: line.rate || line.unit_price_ex_vat,
              source: 'price_book',
              source_type: line.source_type,
              source_code: line.source_code,
              dedupe_key: `price_book_${line.source_code}`,
            });
          } else if (line.category === 'part' || line.category === 'fee') {
            partsLines.push({
              id: line.id,
              description: line.description,
              quantity: line.qty || 1,
              unit_price: line.unit_price_ex_vat,
              vat_rate: (line.vat_rate || 0.2) * 100,
              source: 'price_book',
              source_type: line.source_type,
              source_code: line.source_code,
              dedupe_key: `price_book_${line.source_code}`,
            });
          } else if (line.category === 'package') {
            // Fixed price package - add as labour line
            labourLines.push({
              id: line.id,
              category: 'fixed_price',
              description: line.description,
              hours: 1,
              rate: line.unit_price_ex_vat,
              source: 'price_book',
              source_type: 'package',
              source_code: line.source_code,
              dedupe_key: `price_book_${line.source_code}`,
            });
          }
        });
        
        priceBookSource = {
          package_code: mapping.price_book_package,
          snapshot_at: new Date().toISOString(),
        };
        
        toast.success(`Added "${jobTitle}" with Price Book pricing`, { duration: 2500 });
      }
    } catch (err) {
      console.log("No Price Book package linked or error fetching:", err.message);
      // Fallback: Try service template resolution from backend
      try {
        const templateRes = await axios.get(`${API}/foh/resolve-service-template/${item.item_id}`);
        const tplData = templateRes.data;
        if (tplData.template_found) {
          labourLines = (tplData.labour_lines || []).map(ll => ({
            id: ll.id,
            category: ll.category || 'service_labour',
            description: ll.description,
            hours: ll.hours || 0,
            rate: ll.rate || 0,
            source: 'service_template',
            template_source: ll.template_source,
          }));
          partsLines = (tplData.parts_lines || []).map(pl => ({
            id: pl.id,
            description: pl.description,
            part_number: pl.part_number,
            quantity: pl.quantity || 1,
            unit_price: pl.unit_price || 0,
            vat_rate: pl.vat_rate || 20,
            is_placeholder: pl.is_placeholder,
            is_consumable: pl.is_consumable,
            source: 'service_template',
            template_source: pl.template_source,
          }));
          toast.success(`Added "${tplData.title || jobTitle}" with template pricing`, { duration: 2500 });
        }
      } catch (tplErr) {
        console.log("Service template resolution failed:", tplErr.message);
      }
    }
    
    const newJobGroup = {
      id: jobGroupId,
      inspection_item_id: null,
      title: jobTitle,
      source: "interval",  // Mark as service interval source
      status: "amber",  // Service items are advisory
      vi_status: "amber",
      vi_item_key: item.item_id,  // Use the interval item ID
      category_id: categoryId,
      interval_item_id: item.item_id,
      interval_description: item.description,
      is_interval_item: true,  // Flag to identify interval items
      // Carry AC gas type from VI for service interval AC pricing
      ac_gas_type: item.item_id === 'ac_service' || item.item_id === 'aircon_service' || item.item_id === 'aircon_service_interval'
        ? data?.vi_items?.find(i => i.item_key === 'aircon_operation')?.ac_gas_type || null
        : null,
      labour_lines: labourLines,
      parts_lines: partsLines,
      charges: [],
      price_book_source: priceBookSource,  // Track snapshot origin
    };
    
    setJobGroups([...jobGroups, newJobGroup]);
    // Focus on new service item - collapse all others
    const newExpanded = {};
    jobGroups.forEach(jg => { newExpanded[jg.id] = false; });
    newExpanded[newJobGroup.id] = true;
    setExpandedJobs(newExpanded);
    setSelectedJobForJCE(newJobGroup);
    
    // Mark the interval item as added in state
    setIntervalItems([...intervalItems, {
      id: jobGroupId,
      item_id: item.item_id,
      category_id: categoryId,
      label: item.label,
      description: item.description,
      status: "added_as_job",
      job_group_id: jobGroupId
    }]);
    
    // Refresh due intervals to update the UI
    refreshIntervals();
    
    if (!priceBookSource) {
      toast.success(`Added "${jobTitle}" to pricing`, { duration: 2000 });
    }
  };
  
  // Refresh service intervals data
  const refreshIntervals = async () => {
    if (!yestimateId) return;
    try {
      const intervalsRes = await axios.get(`${API}/foh/service-intervals/check/${yestimateId}`);
      const dueItemsData = intervalsRes.data.due_items || [];
      const flatDueItems = Array.isArray(dueItemsData) && dueItemsData.length > 0 && dueItemsData[0]?.items
        ? dueItemsData.flatMap(cat => cat.items || [])
        : dueItemsData;
      setDueIntervals(flatDueItems);
      setDueSoonIntervals(intervalsRes.data.due_soon_items || []);
      setSuppressedIntervals(intervalsRes.data.suppressed_items || []);
    } catch (err) {
      console.error("Failed to refresh intervals:", err);
    }
  };
  
  // Legacy function - kept for backwards compatibility
  const addIntervalItem = async (categoryId, item, price) => {
    // Redirect to new function that creates job groups
    await addIntervalItemAsJob(categoryId, item);
  };
  
  // Decline a mandatory interval item
  const declineIntervalItem = async (itemId, reason, notes) => {
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/interval-item/${itemId}/decline`, {
        reason,
        notes
      });
      
      // Update local state
      setIntervalItems(intervalItems.map(item => 
        item.id === itemId 
          ? { ...item, status: "declined", decline_record: { reason, notes } }
          : item
      ));
      
      setDeclineModal({ show: false, item: null });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to record decline");
    }
  };
  
  // Calculate interval items total
  const intervalItemsTotal = intervalItems
    .filter(item => item.status !== "declined")
    .reduce((sum, item) => sum + (item.price_inc_vat || 0), 0);

  // ============== JOB PACKAGES FUNCTIONS ==============
  
  // Load available job packages
  const loadJobPackages = async () => {
    setLoadingPackages(true);
    try {
      const make = data?.job?.make;
      const url = make 
        ? `${API}/foh/job-packages?make=${encodeURIComponent(make)}`
        : `${API}/foh/job-packages`;
      const res = await axios.get(url);
      setJobPackages(res.data);
    } catch (err) {
      console.error("Failed to load job packages:", err);
    } finally {
      setLoadingPackages(false);
    }
  };
  
  // Open packages modal
  const openPackagesModal = () => {
    setShowPackagesModal(true);
    loadJobPackages();
  };
  
  // Apply a package to create a new job group
  const applyJobPackage = async (pkg) => {
    setApplyingPackage(pkg.id);
    try {
      const res = await axios.post(`${API}/foh/yestimate/${yestimateId}/apply-package`, {
        package_id: pkg.id,
        job_group_id: null  // Create new job group
      });
      
      // Refresh the yestimate data
      const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setData(yestimateRes.data);
      // Fix: job_groups is nested inside yestimate object
      setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate?.job_groups || []));
      
      // Expand the new job group
      if (res.data.job_group_id) {
        setExpandedJobs(prev => ({ ...prev, [res.data.job_group_id]: true }));
      }
      
      setShowPackagesModal(false);
      toast.success(`Package applied`, {
        description: `"${pkg.name}" added to estimate`
      });
    } catch (err) {
      toast.error("Failed to apply package", {
        description: err.response?.data?.detail
      });
    } finally {
      setApplyingPackage(null);
    }
  };

  // ============== SERVICE HISTORY FUNCTIONS ==============
  
  // Add or update service history
  const saveServiceHistory = async (itemId, historyData) => {
    try {
      const reg = data?.job?.reg;
      if (!reg) return;
      
      const res = await axios.post(`${API}/foh/vehicle/${reg}/service-history`, {
        item_id: itemId,
        confidence_state: historyData.confidence_state,
        completed_date: historyData.completed_date || null,
        completed_mileage: historyData.completed_mileage ? parseInt(historyData.completed_mileage) : null,
        evidence_description: historyData.evidence_description || null,
        evidence_urls: historyData.evidence_urls || [],
        source: historyData.source || null,
        notes: historyData.notes || null
      });
      
      // Refresh service history
      const histRes = await axios.get(`${API}/foh/vehicle/${reg}/service-history`);
      setServiceHistory(histRes.data.history || []);
      setHistorySummary({
        confirmed_count: (histRes.data.history || []).filter(h => h.confidence_state === 'confirmed').length,
        partial_count: (histRes.data.history || []).filter(h => h.confidence_state === 'partial').length,
        declined_count: histRes.data.decline_history?.length || 0
      });
      
      // Refresh due intervals with history context - V2 structure
      const intervalsRes = await axios.get(`${API}/foh/service-intervals/check-with-history/${yestimateId}`);
      setDueIntervals(intervalsRes.data.due_items || []);
      setDueSoonIntervals(intervalsRes.data.due_soon_items || []);
      setSuppressedIntervals(intervalsRes.data.suppressed_items || []);
      
      setHistoryModal({ show: false, item: null, mode: 'add' });
      toast.success("Service history saved");
    } catch (err) {
      toast.error("Failed to save", {
        description: err.response?.data?.detail
      });
    }
  };
  
  // Get history state badge color
  const getHistoryStateBadge = (state) => {
    switch (state) {
      case 'confirmed':
        return { bg: 'bg-green-100', text: 'text-green-800', icon: '✓', label: 'Confirmed' };
      case 'partial':
        return { bg: 'bg-amber-100', text: 'text-amber-800', icon: '⚠', label: 'Unverified' };
      case 'previously_declined':
        return { bg: 'bg-red-100', text: 'text-red-800', icon: '⛔', label: 'Previously Declined' };
      default:
        return { bg: 'bg-slate-100', text: 'text-slate-600', icon: '?', label: 'Unknown' };
    }
  };

  // ============== SERVICE HISTORY PASTE IMPORT ==============
  
  const parseServiceHistoryImport = async () => {
    if (!importPasteText.trim()) {
      toast.error("Please paste service history text first");
      return;
    }
    
    setParsingImport(true);
    try {
      const reg = data?.job?.reg || null; // iter213d — jobCard not defined in this scope
      if (!reg) {
        toast.error("No vehicle registration found");
        return;
      }
      
      const res = await axios.post(`${API}/foh/vehicle/${reg}/service-history/parse`, {
        raw_text: importPasteText,
        group_by_visit: importGroupByVisit
      });
      
      setParsedImportItems(res.data.parsed_items || []);
      setServiceVisits(res.data.service_visits || []);
      
      toast.success(`Parsed ${res.data.items_detected} service items`, {
        description: `${res.data.items_with_date} with dates, ${res.data.items_with_mileage} with mileage`
      });
    } catch (err) {
      toast.error("Failed to parse service history", {
        description: err.response?.data?.detail
      });
    } finally {
      setParsingImport(false);
    }
  };
  
  const saveServiceHistoryImport = async () => {
    if (parsedImportItems.length === 0) {
      toast.error("No items to import");
      return;
    }
    
    setSavingImport(true);
    try {
      const reg = data?.job?.reg || null; // iter213d — jobCard not defined in this scope
      if (!reg) {
        toast.error("No vehicle registration found");
        return;
      }
      
      const res = await axios.post(`${API}/foh/vehicle/${reg}/service-history/import`, {
        items: parsedImportItems.filter(item => item.include !== false),
        raw_paste_text: importPasteText
      });
      
      // Refresh service history
      const historyRes = await axios.get(`${API}/foh/vehicle/${reg}/service-history`);
      setServiceHistory(historyRes.data.history || []);
      setHistorySummary({
        confirmed_count: historyRes.data.history?.filter(h => h.confidence_state === 'confirmed').length || 0,
        partial_count: historyRes.data.history?.filter(h => h.confidence_state === 'partial').length || 0,
        declined_count: historyRes.data.declined_count || 0
      });
      
      // Refresh due intervals with history
      const intervalsRes = await axios.get(`${API}/foh/service-intervals/check-with-history/${yestimateId}`);
      setDueIntervals(intervalsRes.data.due_items || []);
      setDueSoonIntervals(intervalsRes.data.due_soon_items || []);
      setSuppressedIntervals(intervalsRes.data.suppressed_items || []);
      
      // Clear import state
      setImportPasteText('');
      setParsedImportItems([]);
      setServiceVisits([]);
      setShowImportPanel(false);
      
      toast.success(`Imported service history successfully`, {
        description: `${res.data.summary.imported} created, ${res.data.summary.updated} updated, ${res.data.summary.skipped} skipped`
      });
    } catch (err) {
      toast.error("Failed to save service history", {
        description: err.response?.data?.detail
      });
    } finally {
      setSavingImport(false);
    }
  };
  
  const toggleImportItemInclude = (itemIndex) => {
    const updated = [...parsedImportItems];
    updated[itemIndex].include = !updated[itemIndex].include;
    setParsedImportItems(updated);
  };
  
  const updateImportItemField = (itemIndex, field, value) => {
    const updated = [...parsedImportItems];
    updated[itemIndex][field] = value;
    // Recalculate confidence state
    if (updated[itemIndex].detected_date && updated[itemIndex].detected_mileage) {
      updated[itemIndex].confidence_state = 'confirmed';
    } else if (updated[itemIndex].detected_date || updated[itemIndex].detected_mileage) {
      updated[itemIndex].confidence_state = 'partial';
    }
    setParsedImportItems(updated);
  };
  
  const confirmServiceVisit = (visitIndex) => {
    const visit = serviceVisits[visitIndex];
    if (!visit) return;
    
    // Mark all items in visit as included with visit date/mileage
    const updatedItems = parsedImportItems.map(item => {
      if (visit.items.some(vi => vi.line_number === item.line_number)) {
        return {
          ...item,
          include: true,
          detected_date: visit.visit_date || item.detected_date,
          detected_mileage: visit.visit_mileage || item.detected_mileage,
          confidence_state: (visit.visit_date && visit.visit_mileage) ? 'confirmed' : 'partial'
        };
      }
      return item;
    });
    setParsedImportItems(updatedItems);
    toast.success(`Confirmed ${visit.items.length} items from service visit`);
  };
  
  const clearImportPanel = () => {
    setImportPasteText('');
    setParsedImportItems([]);
    setServiceVisits([]);
  };

  // ============== AUTO-POPULATE PRICING FROM VI ==============
  
  const autoPopulatePricing = async () => {
    setAutoPopulating(true);
    try {
      const res = await axios.post(`${API}/foh/yestimate/${yestimateId}/auto-populate`, {
        include_amber: autoPopulateIncludeAmber,
        use_linked_packages: autoPopulateUsePackages
      });
      
      // Refresh yestimate data
      const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setData(yestimateRes.data);
      setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate?.job_groups || []));
      
      // Expand all new job groups
      const newExpandedJobs = { ...expandedJobs };
      res.data.items_processed?.forEach(item => {
        if (item.job_group_id) {
          newExpandedJobs[item.job_group_id] = true;
        }
      });
      setExpandedJobs(newExpandedJobs);
      
      setShowAutoPopulateOptions(false);
      toast.success(`Auto-populated ${res.data.job_groups_created} job groups`, {
        description: `${res.data.red_items} RED items, ${res.data.amber_items} AMBER items`
      });
    } catch (err) {
      toast.error("Failed to auto-populate", {
        description: err.response?.data?.detail
      });
    } finally {
      setAutoPopulating(false);
    }
  };

  // ============== PRICING RECONCILER — pull MOT/MCC/Diag into yestimate ==============
  // Manual catch-up button on the pricing screen. Fires the backend
  // ``/foh/job/{job_id}/reconcile-pricing`` endpoint, then re-loads the
  // yestimate so the FOH instantly sees any newly bridged rows.
  const [reconciling, setReconciling] = useState(false);
  const reconcilePricing = async () => {
    const jid = data?.job?.id || data?.yestimate?.job_id;
    if (!jid) {
      toast.error("No job context — cannot refresh pricing");
      return;
    }
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/foh/job/${jid}/reconcile-pricing`);
      const s = res.data?.summary || {};
      const mot = s.mot?.created || 0;
      const diag = s.diag?.created || 0;
      const mcc = s.mcc?.attached || 0;
      // Re-load the yestimate so the new rows show immediately.
      const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setData(yestimateRes.data);
      setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate?.job_groups || []));
      const created = mot + diag;
      if (created > 0 || mcc > 0) {
        toast.success(`Refreshed pricing — added ${created} new row${created === 1 ? "" : "s"}`, {
          description: `${mot} from MOT, ${diag} from Diagnostic, ${mcc} MCC notes attached`,
        });
      } else {
        toast.info("Pricing is already up to date", {
          description: "No new MOT, MCC or Diagnostic findings to import.",
        });
      }
    } catch (err) {
      toast.error("Failed to refresh pricing", {
        description: err.response?.data?.detail,
      });
    } finally {
      setReconciling(false);
    }
  };

  // ============== NOT QUOTED HANDLING ==============
  
  const openNotQuotedModal = (jobGroup) => {
    setNotQuotedJobGroup(jobGroup);
    setNotQuotedReason("");
    setNotQuotedNotes("");
    setShowNotQuotedModal(true);
  };

  const submitNotQuoted = async () => {
    if (!notQuotedReason) {
      toast.error("Please select a reason");
      return;
    }
    if (isSubmittingNotQuoted) return;
    setIsSubmittingNotQuoted(true);

    // Snapshot the IDs we'll act on so React state changes during the
    // async flow (e.g. an in-flight auto-save mutating jobGroups) can't
    // strand us with a stale id.
    const jgId   = notQuotedJobGroup?.id;
    const reason = notQuotedReason;
    const notes  = notQuotedNotes || null;
    if (!jgId) {
      setIsSubmittingNotQuoted(false);
      toast.error("Missing job group id — please retry.");
      return;
    }

    try {
      // iter211d — Flush any pending auto-save first so the JG
      // definitely exists on the backend. Without this, JGs created
      // moments before clicking "Not Quoting" haven't yet hit the
      // server (auto-save is debounced 2s), and the not-quoted POST
      // fails with "Job group not found".
      try { await flushAutoSave(); } catch (_) { /* surfaced below */ }

      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/job-group/${jgId}/not-quoted`,
        { job_group_id: jgId, reason, notes },
      );

      // Refresh data
      const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate?.job_groups || []));

      // Close the modal FIRST so the user sees the action land instantly
      // (the toast is non-blocking, the previous alert() was a Safari
      // pop-up that can be silently suppressed inside an async handler).
      setShowNotQuotedModal(false);
      setNotQuotedJobGroup(null);
      setNotQuotedReason("");
      setNotQuotedNotes("");
      toast.success("Job marked as not quoted");
    } catch (err) {
      toast.error(err.response?.data?.detail || err?.message || "Failed to mark as not quoted");
    } finally {
      setIsSubmittingNotQuoted(false);
    }
  };

  // ============== PACKAGE MANAGEMENT ==============
  
  const openPackageEditor = (pkg = null) => {
    if (pkg) {
      setEditingPackage({
        ...pkg,
        labour_lines: pkg.labour_lines || [],
        part_lines: pkg.part_lines || [],
        notes: pkg.notes || []
      });
    } else {
      setEditingPackage({
        name: "",
        category: "General",
        variant: "",
        description: "",
        labour_lines: [{ description: "", category: "strip_renew", hours: 1.0 }],
        part_lines: [{ description: "", part_number: "", quantity: 1, unit_price: 0, is_consumable: false, is_mandatory_fixing: false }],
        notes: []
      });
    }
    setShowPackageEditor(true);
    setShowPackagesModal(false);
  };

  const savePackage = async () => {
    if (!editingPackage.name) {
      toast.warning("Name required", {
        description: "Please enter a package name"
      });
      return;
    }
    
    setSavingPackage(true);
    try {
      if (editingPackage.id) {
        // Update existing
        await axios.put(`${API}/admin/packages/${editingPackage.id}`, {
          name: editingPackage.name,
          category: editingPackage.category,
          variant: editingPackage.variant || null,
          description: editingPackage.description || null,
          labour_lines: editingPackage.labour_lines.filter(l => l.description),
          part_lines: editingPackage.part_lines.filter(p => p.description),
          notes: editingPackage.notes.filter(n => n.text)
        });
        toast.success("Package updated");
      } else {
        // Create new
        await axios.post(`${API}/admin/packages`, {
          name: editingPackage.name,
          category: editingPackage.category,
          variant: editingPackage.variant || null,
          description: editingPackage.description || null,
          labour_lines: editingPackage.labour_lines.filter(l => l.description),
          part_lines: editingPackage.part_lines.filter(p => p.description),
          notes: editingPackage.notes.filter(n => n.text),
          is_active: true
        });
        toast.success("Package created");
      }
      
      setShowPackageEditor(false);
      setEditingPackage(null);
      
      // Refresh packages list
      loadJobPackages();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save package");
    } finally {
      setSavingPackage(false);
    }
  };

  const deletePackage = async (packageId) => {
    if (!window.confirm("Are you sure you want to delete this package?")) return;
    
    try {
      await axios.delete(`${API}/admin/packages/${packageId}`);
      alert("Package deleted");
      loadJobPackages();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete package");
    }
  };

  const updateEditingPackageLabour = (index, field, value) => {
    const updated = [...editingPackage.labour_lines];
    updated[index] = { ...updated[index], [field]: value };
    setEditingPackage({ ...editingPackage, labour_lines: updated });
  };

  const updateEditingPackagePart = (index, field, value) => {
    const updated = [...editingPackage.part_lines];
    updated[index] = { ...updated[index], [field]: value };
    setEditingPackage({ ...editingPackage, part_lines: updated });
  };

  const addEditingPackageLabourLine = () => {
    setEditingPackage({
      ...editingPackage,
      labour_lines: [...editingPackage.labour_lines, { description: "", category: "strip_renew", hours: 1.0 }]
    });
  };

  const addEditingPackagePartLine = () => {
    setEditingPackage({
      ...editingPackage,
      part_lines: [...editingPackage.part_lines, { description: "", part_number: "", quantity: 1, unit_price: 0, is_consumable: false, is_mandatory_fixing: false }]
    });
  };

  const addEditingPackageNote = () => {
    setEditingPackage({
      ...editingPackage,
      notes: [...(editingPackage.notes || []), { text: "", note_type: "info" }]
    });
  };

  const removeEditingPackageLabourLine = (index) => {
    const updated = editingPackage.labour_lines.filter((_, i) => i !== index);
    setEditingPackage({ ...editingPackage, labour_lines: updated });
  };

  const removeEditingPackagePartLine = (index) => {
    const updated = editingPackage.part_lines.filter((_, i) => i !== index);
    setEditingPackage({ ...editingPackage, part_lines: updated });
  };

  // ============== SMART ESTIMATE WIZARD ==============
  
  const WIZARD_STEPS = [
    { id: 1, title: "Review Findings", icon: "🔍" },
    { id: 2, title: "Select Items", icon: "✓" },
    { id: 3, title: "Apply Packages", icon: "📦" },
    { id: 4, title: "Complete Pricing", icon: "💰" },
    { id: 5, title: "Review & Send", icon: "📤" }
  ];

  const startWizard = async () => {
    setWizardLoading(true);
    setShowWizard(true);
    setWizardStep(1);
    
    try {
      // Fetch inspection items to categorize
      const items = data?.items || [];
      // Include "dangerous" items with red items (they are treated the same in pricing)
      const red = items.filter(i => i.status === "red" || i.status === "dangerous");
      const amber = items.filter(i => i.status === "amber");
      const green = items.filter(i => i.status === "green" || !i.status);
      
      setWizardFindings({ red, amber, green });
      
      // Pre-select all red/dangerous items (mandatory) and amber items (optional)
      const preSelected = [
        ...red.map(i => ({ ...i, selected: true, mandatory: true })),
        ...amber.map(i => ({ ...i, selected: true, mandatory: false }))
      ];
      setWizardSelectedItems(preSelected);
      
      // Load packages for suggestions
      if (jobPackages.packages.length === 0) {
        await loadJobPackages();
      }
      
    } catch (err) {
      console.error("Error starting wizard:", err);
    } finally {
      setWizardLoading(false);
    }
  };

  const toggleWizardItem = (itemKey) => {
    setWizardSelectedItems(prev => 
      prev.map(item => 
        item.item_key === itemKey && !item.mandatory
          ? { ...item, selected: !item.selected }
          : item
      )
    );
  };

  const wizardAutoPopulate = async () => {
    setWizardLoading(true);
    try {
      // Filter to only selected items
      const selectedKeys = wizardSelectedItems
        .filter(i => i.selected)
        .map(i => i.item_key);
      
      // Call auto-populate with include_amber based on selection
      const hasAmber = wizardSelectedItems.some(i => i.selected && !i.mandatory);
      
      const res = await axios.post(`${API}/foh/yestimate/${yestimateId}/auto-populate`, {
        include_amber: hasAmber,
        use_linked_packages: true
      });
      
      // Refresh data
      const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setData(yestimateRes.data);
      setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate?.job_groups || []));
      
      // Find relevant packages to suggest
      const suggestedPkgs = [];
      const appliedCategories = new Set();
      
      res.data.items_processed?.forEach(item => {
        // Suggest packages that match the categories of auto-populated items
        if (item.item_key?.includes("brake")) appliedCategories.add("Brakes");
        if (item.item_key?.includes("tyre")) appliedCategories.add("Tyres");
        if (item.item_key?.includes("suspension") || item.item_key?.includes("shock")) appliedCategories.add("Suspension");
        if (item.item_key?.includes("service") || item.item_key?.includes("filter")) appliedCategories.add("Service");
      });
      
      // Get matching packages
      (jobPackages.packages || []).forEach(pkg => {
        if (appliedCategories.has(pkg.category) && !pkg.id.startsWith("pkg_custom")) {
          suggestedPkgs.push(pkg);
        }
      });
      
      setWizardSuggestedPackages(suggestedPkgs.slice(0, 4));
      
      // Move to next step
      setWizardStep(3);
      
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to auto-populate");
    } finally {
      setWizardLoading(false);
    }
  };

  const wizardApplyPackage = async (pkg) => {
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/apply-package`, {
        package_id: pkg.id,
        job_group_id: null
      });
      
      // Refresh data
      const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setData(yestimateRes.data);
      setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate?.job_groups || []));
      
      // Remove from suggestions
      setWizardSuggestedPackages(prev => prev.filter(p => p.id !== pkg.id));
      
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to apply package");
    }
  };

  const wizardValidatePricing = async () => {
    setWizardLoading(true);
    try {
      const res = await axios.get(`${API}/foh/yestimate/${yestimateId}/pricing-validation`);
      setWizardValidation(res.data);
      
      // Generate quote preview
      const totals = calculateGrandTotal();
      // iter197h — Carry the consumables / environmental breakdown plus
      // a backstop on already_authorised_value into wizardQuotePreview
      // so the review screen can render every component of the math.
      // WX12FYD surfaced the gap: review screen showed Subtotal+VAT
      // only, leaving FOH wondering where the missing £18.50 and the
      // £54.85 went. The customer-facing total still derived from
      // calculateGrandTotal() so was correct, but the breakdown was
      // opaque.
      const yest = data?.yestimate || {};
      const alreadyAuthVal = (
        intentConfig?.already_authorised_value
        ?? yest.already_authorised_value
        ?? 0
      );
      const alreadyAuthTagsResolved = (
        intentConfig?.already_authorised_tags
        || yest.already_authorised_tags
        || []
      );
      const consumablesApplied = globalCharges.consumables.applied && jobGroups.length > 0;
      const envApplied = globalCharges.environmental.applied && jobGroups.length > 0;
      const consumablesAmt = consumablesApplied ? (globalCharges.consumables.amount || 12.50) : 0;
      const envAmt = envApplied ? (globalCharges.environmental.amount || 6.00) : 0;
      // jobGroups-only subtotal (excludes global charges) — used to break
      // out "Jobs" vs "Consumables" vs "Environmental" cleanly.
      const jobsOnlySubtotal = totals.subtotal - consumablesAmt - envAmt;
      setWizardQuotePreview({
        vehicle: `${formatVehicleNameFromObject(data?.job)}`,
        reg: data?.job?.reg,
        customer: customerName || "Customer",
        jobGroups: jobGroups.length,
        redItems: res.data.red_items,
        amberItems: res.data.amber_items,
        subtotal: totals.subtotal,
        vat: totals.vat,
        newWorkTotal: totals.newWorkTotal,
        alreadyAuthorised: Number(alreadyAuthVal || 0),
        alreadyAuthorisedTags: alreadyAuthTagsResolved,
        total: totals.newWorkTotal + Number(alreadyAuthVal || 0),
        // iter197h — itemised breakdown for the review screen.
        breakdown: {
          jobsSubtotal:     jobsOnlySubtotal,
          consumablesAmt:   consumablesAmt,
          consumablesApplied,
          envAmt:           envAmt,
          envApplied,
        },
        issues: res.data.issues,
        warnings: res.data.warnings,
        canSend: res.data.can_send
      });
      
      setWizardStep(5);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to validate pricing");
    } finally {
      setWizardLoading(false);
    }
  };

  const wizardFinish = async () => {
    // Save the yestimate
    try {
      // iter213d — saveYestimate helper not defined in this scope; wizard save is a no-op stub for now
      // await saveYestimate();
      setShowWizard(false);
      setWizardStep(1);
      toast.success("Estimate saved!", {
        description: "You can now send it to the customer"
      });
    } catch (err) {
      toast.error("Failed to save estimate");
    }
  };

  const closeWizard = () => {
    if (wizardStep > 2) {
      if (!confirm("You have unsaved progress. Are you sure you want to exit the wizard?")) {
        return;
      }
    }
    setShowWizard(false);
    setWizardStep(1);
    setWizardSelectedItems([]);
    setWizardSuggestedPackages([]);
    setWizardValidation(null);
    setWizardQuotePreview(null);
  };

  // ============== QUICK PRICING MODE ==============
  // Single-click auto-quote for experienced FOH users
  
  const quickAutoQuote = async () => {
    setQuickPricingLoading(true);
    
    try {
      // Step 1: Get inspection findings count (include dangerous with red)
      const items = data?.items || [];
      const redCount = items.filter(i => i.status === "red" || i.status === "dangerous").length;
      const amberCount = items.filter(i => i.status === "amber").length;
      
      if (redCount === 0 && amberCount === 0) {
        alert("No RED/DANGEROUS or AMBER items found in the inspection. Nothing to auto-quote.");
        setQuickPricingLoading(false);
        return;
      }
      
      // Step 2: Auto-populate all red, dangerous and amber items
      const autoPopRes = await axios.post(`${API}/foh/yestimate/${yestimateId}/auto-populate`, {
        include_amber: true,
        use_linked_packages: true
      });
      
      // Step 3: Refresh yestimate data
      const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setData(yestimateRes.data);
      setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate?.job_groups || []));
      
      // Step 4: Get validation status
      const validationRes = await axios.get(`${API}/foh/yestimate/${yestimateId}/pricing-validation`);
      
      // Step 5: Calculate totals
      const newJobGroups = yestimateRes.data.yestimate?.job_groups || [];
      let subtotal = 0;
      let vat = 0;
      newJobGroups.forEach(jg => {
        const jobTotal = calculateJobTotal(jg);
        subtotal += jobTotal.subtotal;
        vat += jobTotal.vat;
      });
      
      // Step 6: Expand all job groups for review
      const newExpandedJobs = {};
      newJobGroups.forEach(jg => {
        newExpandedJobs[jg.id] = false; // Start collapsed for cleaner view
      });
      setExpandedJobs(newExpandedJobs);
      
      // Step 7: Show summary modal
      setQuickPricingResult({
        jobsCreated: autoPopRes.data.job_groups_created,
        redItems: autoPopRes.data.red_items,
        amberItems: autoPopRes.data.amber_items,
        totalJobGroups: newJobGroups.length,
        subtotal: subtotal,
        vat: vat,
        total: subtotal + vat,
        canSend: validationRes.data.can_send,
        issuesCount: validationRes.data.issues?.length || 0,
        warningsCount: validationRes.data.warnings?.length || 0,
        itemsProcessed: autoPopRes.data.items_processed || []
      });
      
      setShowQuickPricingSummary(true);
      
    } catch (err) {
      toast.error("Quick auto-quote failed", {
        description: err.response?.data?.detail || "Please try the wizard instead"
      });
    } finally {
      setQuickPricingLoading(false);
    }
  };

  // Remove job group (and all its lines)
  const removeJobGroup = (jobGroupId) => {
    const jobGroup = jobGroups.find(jg => jg.id === jobGroupId);
    
    // Check if this brake pads job is redundant (covered by discs & pads package)
    const isBrakePadsJob = jobGroup?.vi_item_key?.includes("brake_pads") || 
                           jobGroup?.title?.toLowerCase().includes("brake pad");
    const hasDiscsAndPadsPackage = jobGroups.some(jg => 
      jg.id !== jobGroupId && 
      ((jg.title?.toLowerCase().includes("disc") && jg.title?.toLowerCase().includes("pad")) ||
       jg.package_id?.includes("discs_pads"))
    );
    const isPotentiallyRedundant = isBrakePadsJob && hasDiscsAndPadsPackage;
    
    // Build confirmation message based on job type
    let confirmMsg = "Remove this job and all its lines?";
    if (isPotentiallyRedundant) {
      confirmMsg = "This brake pads job may already be covered by the Discs & Pads package.\n\nRemove this redundant item?";
    } else if (jobGroup?.is_mandatory || jobGroup?.source === "diagnostic" || jobGroup?.status === "red") {
      confirmMsg = "⚠️ WARNING: This is a mandatory/safety-critical item.\n\nRemoving it will exclude it from the customer estimate. Are you sure you want to remove this job?";
    }
    
    if (confirm(confirmMsg)) {
      setJobGroups(jobGroups.filter(jg => jg.id !== jobGroupId));
      // If it was a diagnostic job group, reset the flag
      if (jobGroup?.source === "diagnostic") {
        setHasDiagnosticJobGroup(false);
      }
    }
  };

  // Update job group title
  const updateJobGroupTitle = (jobGroupId, title) => {
    setJobGroups(jobGroups.map(jg => 
      jg.id === jobGroupId ? { ...jg, title } : jg
    ));
    // Fetch new suggestions based on updated title
    const jobGroup = jobGroups.find(jg => jg.id === jobGroupId);
    if (jobGroup) {
      fetchSuggestions(jobGroupId, title, jobGroup.title);
    }
  };

  // Add labour line to job group
  const addLabourLine = (jobGroupId) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        labour_lines: [...jg.labour_lines, {
          id: Date.now().toString(),
          category: "repair_labour",
          description: "",
          hours: 1,
          rate: 65
        }]
      };
    }));
  };

  // Add parts line to job group
  const addPartsLine = (jobGroupId) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        parts_lines: [...jg.parts_lines, {
          id: Date.now().toString(),
          description: "",
          quantity: 1,
          unit_price: 0,
          buy_in_price: 0,  // Parts buy-in cost (for margin calculation)
          vat_rate: 20
        }]
      };
    }));
  };

  // Add charge to job group
  const addCharge = (jobGroupId, chargeType) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        charges: [...jg.charges, {
          id: Date.now().toString(),
          charge_type: chargeType,
          description: chargeType === "consumables" ? "Consumables" : "Environmental / Disposal Charge",
          amount: chargeType === "consumables" ? 5 : 3,
          vat_rate: 20
        }]
      };
    }));
  };

  // Update labour line
  const updateLabourLine = (jobGroupId, lineId, field, value) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        labour_lines: jg.labour_lines.map(line => {
          if (line.id !== lineId) return line;
          const updated = { ...line, [field]: value };
          // When category changes, auto-update rate from Price Book
          if (field === "category" && value) {
            updated.rate = getLabourRate(value, line.rate || 65);
          }
          // iter197w — As soon as FOH touches a tester-seeded line, drop
          // the provenance flag so the "from tester" chip disappears.
          // iter197bb — Also stamp foh_touched so the backend
          // tester-estimate seed/overwrite logic NEVER re-overwrites
          // this line on subsequent yestimate fetches.
          if (updated.source === "mcc_tester_estimate") {
            updated.source = "foh_edited";
          }
          updated.foh_touched = true;
          return updated;
        })
      };
    }));
  };

  // Update parts line
  const updatePartsLine = (jobGroupId, lineId, field, value) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      
      return {
        ...jg,
        parts_lines: jg.parts_lines.map(line => {
          if (line.id !== lineId) return line;
          
          const updatedLine = { ...line, [field]: value };
          
          // Auto-populate retail price when buy-in price is set for tyres
          // Adds £40 markup per tyre - FOH can still manually adjust
          if (field === 'buy_in_price' && value > 0) {
            const isTyre = (line.description || '').toLowerCase().includes('tyre') ||
                          (line.description || '').toLowerCase().includes('tire') ||
                          (jg.title || '').toLowerCase().includes('tyre') ||
                          (jg.job_name || '').toLowerCase().includes('tyre');
            
            if (isTyre) {
              // Only auto-set if retail is currently 0 or empty (don't overwrite manual entries)
              if (!line.unit_price || line.unit_price === 0) {
                updatedLine.unit_price = Math.round((value + 40) * 100) / 100;
              }
            }
          }
          
          return updatedLine;
        })
      };
    }));
  };

  // Update charge
  const updateCharge = (jobGroupId, chargeId, field, value) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        charges: jg.charges.map(charge => 
          charge.id === chargeId ? { ...charge, [field]: value } : charge
        )
      };
    }));
  };

  // Remove labour line
  const removeLabourLine = (jobGroupId, lineId) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        labour_lines: jg.labour_lines.filter(line => line.id !== lineId)
      };
    }));
  };

  // Remove parts line
  const removePartsLine = (jobGroupId, lineId) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        parts_lines: jg.parts_lines.filter(line => line.id !== lineId)
      };
    }));
  };

  // Remove charge
  const removeCharge = (jobGroupId, chargeId) => {
    clearJobValidationErrors(jobGroupId); // Clear errors when editing
    setJobGroups(jobGroups.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        charges: jg.charges.filter(charge => charge.id !== chargeId)
      };
    }));
  };

  // Calculate job group subtotal
  const calculateJobTotal = (jobGroup) => {
    let subtotal = 0;
    let vat = 0;
    
    jobGroup.labour_lines?.forEach(l => {
      const lineTotal = (l.hours || 0) * (l.rate || 0);
      subtotal += lineTotal;
      vat += lineTotal * 0.2;
    });
    
    jobGroup.parts_lines?.forEach(p => {
      const lineTotal = (p.quantity || 0) * (p.unit_price || 0);
      subtotal += lineTotal;
      vat += lineTotal * ((p.vat_rate || 20) / 100);  // Default to 20% VAT
    });
    
    jobGroup.charges?.forEach(c => {
      subtotal += c.amount || 0;
      vat += (c.amount || 0) * ((c.vat_rate || 20) / 100);  // Default to 20% VAT
    });
    
    return { subtotal, vat, total: subtotal + vat };
  };

  // Calculate grand total
  const calculateGrandTotal = () => {
    let subtotal = 0;
    let vat = 0;
    jobGroups.forEach(jg => {
      // iter211e — Not Quoting JGs are visible on the FOH screen for
      // audit but MUST NOT contribute to the customer-facing grand
      // total. Mirrors the backend totals recomputation in the
      // /not-quoted POST and the PUT save path.
      if (jg.not_quoted_reason) return;
      const jobTotals = calculateJobTotal(jg);
      subtotal += jobTotals.subtotal;
      vat += jobTotals.vat;
    });
    
    // Add global charges (consumables and environmental)
    if (globalCharges.consumables.applied && jobGroups.length > 0) {
      const consumablesAmount = globalCharges.consumables.amount || 12.50;
      subtotal += consumablesAmount;
      vat += consumablesAmount * 0.20;  // 20% VAT
    }
    if (globalCharges.environmental.applied && jobGroups.length > 0) {
      const environmentalAmount = globalCharges.environmental.amount || 6.00;
      subtotal += environmentalAmount;
      vat += environmentalAmount * 0.20;  // 20% VAT
    }

    // iter197d — Roll any "previously authorised" work (MOT fee, prior
    // service authorisation, etc.) into the customer-facing total so
    // FOH sees the FINAL figure the customer is actually obligated
    // to pay — not just the new outstanding work. The value is stored
    // gross (MOT fees are zero-rated for VAT, prior auths are already
    // inc-VAT money). Without this, every screen that reads
    // grandTotal silently understated the customer total by the
    // already_authorised amount (SL59LYZ surfaced this — £54.85 MOT
    // fee was invisible on the FOH pricing review).
    const alreadyAuthorised = Number(intentConfig?.already_authorised_value || 0);
    const newWorkTotal = subtotal + vat;
    return {
      subtotal,
      vat,
      newWorkTotal,                                  // outstanding work only
      alreadyAuthorised,                             // previously authorised £
      total: newWorkTotal + alreadyAuthorised,      // customer-facing final
    };
  };

  // Job tag detection patterns - maps keywords to JCE job tags
  const JOB_TAG_PATTERNS = {
    'brake pad': 'BRAKE_PADS_FRONT',
    'front brake pad': 'BRAKE_PADS_FRONT',
    'rear brake pad': 'BRAKE_PADS_REAR',
    'brake disc': 'BRAKE_DISCS_PADS_FRONT',
    'front brake disc': 'BRAKE_DISCS_PADS_FRONT',
    'rear brake disc': 'BRAKE_DISCS_PADS_REAR',
    'front brakes': 'BRAKE_DISCS_PADS_FRONT',
    'rear brakes': 'BRAKE_DISCS_PADS_REAR',
    'front spring': 'SHOCKS_FRONT_PAIR',
    'rear spring': 'SHOCKS_REAR_PAIR',
    'front shock': 'SHOCKS_FRONT_PAIR',
    'rear shock': 'SHOCKS_REAR_PAIR',
    'shock absorber': 'SHOCKS_FRONT_PAIR',
    'damper': 'SHOCKS_FRONT_PAIR',
    'lower arm': 'LOWER_ARM',
    'wishbone': 'LOWER_ARM',
    'control arm': 'LOWER_ARM',
    'wheel bearing': 'WHEEL_BEARING_FRONT',
    'timing belt': 'TIMING_BELT_KIT',
    'cambelt': 'TIMING_BELT_KIT',
    // Note: drive belt/auxiliary belt are NOT timing belts
    'clutch': 'CLUTCH_KIT',
    'cv joint': 'WHEEL_BEARING_FRONT',
    'dpf': 'DPF_DIAG_OR_REGEN',
    'egr': 'EGR_CLEAN_OR_REPLACE',
    'injector': 'INJECTOR_REPLACEMENT',
    'aircon': 'AIRCON_SERVICE',
    'air con': 'AIRCON_SERVICE',
    'weld': 'WELDING_STRUCTURAL',
  };

  // Extract job tags from job groups - auto-detect from titles
  const extractJobTags = () => {
    const tags = new Set();
    jobGroups.forEach(jg => {
      if (jg.job_tag) {
        tags.add(jg.job_tag);
        return;
      }
      const searchText = `${jg.title || ''} ${jg.job_name || ''} ${jg.description || ''}`.toLowerCase();
      for (const [pattern, tag] of Object.entries(JOB_TAG_PATTERNS)) {
        if (searchText.includes(pattern)) {
          tags.add(tag);
        }
      }
    });
    return Array.from(tags);
  };

  // Check if there are undecided critical JCE items before saving
  const checkJCEBeforeSave = async () => {
    // Extract job tags from job_groups with auto-detection
    const jobTags = extractJobTags();
    
    // If no job tags, proceed directly to save
    if (jobTags.length === 0) {
      return true; // No JCE items to check
    }
    
    try {
      const response = await axios.post(`${API}/jce/evaluate`, {
        estimate_id: data?.yestimate?.id,
        job_tags: jobTags,
        vehicle_context: {
          reg: data?.job?.reg || '',
          age_years: data?.job?.year ? new Date().getFullYear() - data.job.year : 0,
          mileage: data?.yestimate?.mileage || data?.job?.mileage || 0,
          mileage_band: getMileageBand(data?.yestimate?.mileage || data?.job?.mileage || 0),
          fuel_type: (data?.job?.fuel_type || 'petrol').toLowerCase(),
          make: data?.job?.make,
          model: data?.job?.model,
          year: data?.job?.year
        },
        current_lines: jobGroups.map(jg => ({
          description: jg.job_name || jg.title || '',
          parts: jg.parts_lines || []
        }))
      });
      
      // Check if there are undecided critical items
      const critical = response.data.visible_critical || [];
      return critical.length === 0; // Return true if no critical items need review
    } catch (err) {
      console.error('JCE check failed, proceeding with save:', err);
      return true; // On error, don't block - proceed with save
    }
  };
  
  // Helper for mileage band (used in JCE check)
  const getMileageBand = (mileage) => {
    if (mileage < 40000) return '<40k';
    if (mileage < 80000) return '40-80k';
    if (mileage < 120000) return '80-120k';
    return '120k+';
  };

  // Perform the actual save operation
  const performSave = async (skipDedupeCheck = false) => {
    setSaving(true);
    
    // Step 1: Global Dedupe Validation (pre-save check)
    if (!skipDedupeCheck) {
      try {
        setDedupeValidating(true);
        const dedupeRes = await axios.get(`${API}/yestimates/${data?.yestimate?.id}/validate-duplicates`);
        const validation = dedupeRes.data;
        
        if (validation.has_duplicates) {
          // Show dedupe modal - either blocking or warning
          setDedupeValidationResult(validation);
          setShowDedupeModal(true);
          setDedupeValidating(false);
          setSaving(false);
          
          // If can_proceed is false (blocking duplicates), stop here
          // If can_proceed is true (warnings only), modal will let user acknowledge and continue
          return;
        }
      } catch (err) {
        console.log("Dedupe validation failed, proceeding with save:", err);
        // On error, don't block - proceed with save
      } finally {
        setDedupeValidating(false);
      }
    }
    
    // Step 2: Actual Save
    try {
      // Use sanitized job groups with proper defaults
      const sanitizedGroups = sanitizeJobGroups(jobGroups);
      
      await axios.put(`${API}/foh/yestimate/${yestimateId}`, {
        tone,
        customer_name: customerName,
        current_job_value: currentJobValue ? parseFloat(currentJobValue) : null,
        // Global charges persistence (consumables + environmental).
        global_consumables_applied: globalCharges?.consumables?.applied ?? true,
        global_consumables_disapply_reason: globalCharges?.consumables?.disapplyReason || null,
        global_environmental_applied: globalCharges?.environmental?.applied ?? true,
        global_environmental_disapply_reason: globalCharges?.environmental?.disapplyReason || null,
        job_groups: sanitizedGroups.map(jg => ({
          id: jg.id,
          inspection_item_id: jg.inspection_item_id,
          vi_item_key: jg.vi_item_key,
          title: jg.title || 'Untitled Job',
          source: jg.source || "inspection",
          status: jg.status,
          jcu_package_id: jg.jcu_package_id || null,
          pricing_confirmed: jg.pricing_confirmed || false,
          ...buildMotProvenancePayload(jg),
          labour_lines: (jg.labour_lines || []).map(l => ({
            id: l.id,
            category: l.category || 'repair_labour',
            description: l.description || '',
            hours: parseFloat(l.hours) || 0,
            rate: parseFloat(l.rate) || 0
          })),
          parts_lines: (jg.parts_lines || []).map(p => ({
            id: p.id,
            description: p.description || 'Part',
            quantity: parseFloat(p.quantity) || 1,
            unit_price: parseFloat(p.unit_price) || 0,
            buy_in_price: parseFloat(p.buy_in_price) || 0,
            vat_rate: parseFloat(p.vat_rate) || 20
          })),
          charges: (jg.charges || []).map(c => ({
            id: c.id,
            charge_type: c.charge_type || 'consumables',
            description: c.description || 'Charge',
            amount: parseFloat(c.amount) || 0,
            vat_rate: parseFloat(c.vat_rate) || 20
          }))
        }))
      });
      navigate(`/app/foh/yestimate/${yestimateId}/preview`);
    } catch (err) {
      // Handle DRE blocked errors (403) - mandatory diagnostic missing
      // Note: This is a CORE WORKFLOW block, not a JCU/rule-engine block
      const errorData = err.response?.data;
      if (err.response?.status === 403 && errorData?.detail?.error === 'DRE_DIAGNOSTIC_REQUIRED') {
        // Show alert with blocker details
        const blockers = errorData.detail.blockers || [];
        const blockerMessages = blockers.map(b => `• ${b.job_title}: ${b.reason}`).join('\n');
        alert(`Estimate blocked: Mandatory diagnostic investigation required.\n\n${blockerMessages}\n\nAdd diagnostic labour lines or provide override reasons to proceed.`);
        return;
      }
      
      // Handle Pydantic validation errors (422) which have a different structure
      const errorMessage = getErrorMessage(err, "Failed to save");
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle dedupe modal - when user acknowledges warnings and proceeds
  const handleDedupeAcknowledgeAndProceed = async () => {
    setShowDedupeModal(false);
    setDedupeValidationResult(null);
    // Proceed with save, skipping dedupe check since user acknowledged
    await performSave(true);
  };
  
  // Handle dedupe modal - when user wants to resolve a duplicate
  const handleDedupeResolve = async (dedupeKey, resolution, locations) => {
    try {
      // iter211 — "Smart" resolutions are remove-from-X PLUS an
      // automatic Not-Quoting stamp on the JG that just got emptied,
      // so the FOH doesn't have to make a second click. Translate
      // back to the backend's existing remove vocabulary for the
      // resolve-duplicate call.
      let backendResolution = resolution;
      let smartMarkNotQuotingJgId = null;
      if (resolution === "smart_remove_first") {
        backendResolution = "remove_from_first";
        smartMarkNotQuotingJgId = locations[0]?.job_group_id;
      } else if (resolution === "smart_remove_second") {
        backendResolution = "remove_from_second";
        smartMarkNotQuotingJgId = locations[1]?.job_group_id;
      }

      // Determine which job group to remove from
      const sourceGroupId = locations[0]?.job_group_id;
      const targetGroupId = locations[1]?.job_group_id;
      
      // Build query params for the API call
      const params = new URLSearchParams({
        dedupe_key: dedupeKey,
        resolution: backendResolution,
        source_job_group_id: sourceGroupId,
      });
      if (targetGroupId) {
        params.append('target_job_group_id', targetGroupId);
      }
      
      await axios.post(`${API}/yestimates/${data?.yestimate?.id}/resolve-duplicate?${params.toString()}`);

      // iter211 — Smart variant: also stamp Not-Quoting on the JG
      // that got the line removed. Best-effort — if it fails we still
      // continue (the duplicate has been resolved).
      if (smartMarkNotQuotingJgId) {
        try {
          await axios.post(
            `${API}/foh/yestimate/${data?.yestimate?.id}/job-group/${smartMarkNotQuotingJgId}/not-quoted`,
            {
              job_group_id: smartMarkNotQuotingJgId,
              reason: "pads_included_in_disc_combo",
              notes: "Auto-stamped by Smart Resolve — the work is already covered by the combo / bundle job that includes these parts.",
            },
          );
        } catch (e) {
          // Non-fatal — surface a toast so FOH can mark it manually.
          toast.info("Duplicate removed — mark the empty job as Not Quoting if needed.");
        }
      }

      // Refresh the estimate data to reflect the removal
      const refreshRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
      setJobGroups(sanitizeJobGroups(refreshRes.data.yestimate.job_groups || []));
      
      // Re-run validation to update the modal
      const dedupeRes = await axios.get(`${API}/yestimates/${data?.yestimate?.id}/validate-duplicates`);
      const newValidation = dedupeRes.data;
      
      if (newValidation.has_duplicates) {
        setDedupeValidationResult(newValidation);
      } else {
        // All duplicates resolved - close modal and proceed
        setShowDedupeModal(false);
        setDedupeValidationResult(null);
        toast.success("Duplicates resolved", { description: "You can now save the estimate" });
      }
    } catch (err) {
      toast.error("Failed to resolve duplicate", { description: err.response?.data?.detail || err.message });
    }
  };
  
  // Handle dedupe modal close
  const handleDedupeClose = () => {
    setShowDedupeModal(false);
    setDedupeValidationResult(null);
  };
  
  // Refresh JCU evaluation after data changes
  const refreshJCUEvaluation = async () => {
    try {
      setJcuLoading(true);
      const res = await axios.get(`${API}/jcu/v2/yestimate/${yestimateId}/evaluation`);
      if (res.data.found) {
        const evaluation = res.data.evaluation;
        
        // Extract top deduction reasons from package evaluations for compact display
        const deductionSummary = [];
        if (evaluation.package_evaluations) {
          for (const pkg of evaluation.package_evaluations) {
            if (pkg.deductions) {
              for (const d of pkg.deductions) {
                deductionSummary.push(d.description || d.item || d.type);
              }
            }
          }
        }
        
        setJcuEvaluation({
          ...evaluation,
          deduction_summary: deductionSummary.slice(0, 5)  // Store top 5 for display
        });
      }
    } catch (err) {
      console.log("Failed to refresh JCU:", err);
    } finally {
      setJcuLoading(false);
    }
  };
  
  // Compute DRE results for a job group
  const computeDREForJobGroup = async (jobGroup) => {
    try {
      // Extract job type from template_key or title
      const jobType = jobGroup.job_type || jobGroup.template_key || 
                      jobGroup.title?.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and') || 'unknown';
      const evidenceLevel = jobGroup.evidence_level || 'unconfirmed';
      
      const res = await axios.post(`${API}/dre/compute`, {
        job_type: jobType,
        evidence_level: evidenceLevel,
        job_category: jobGroup.job_category
      });
      
      return res.data.result;
    } catch (err) {
      console.log("Failed to compute DRE:", err);
      return null;
    }
  };
  
  // Compute DRE for all job groups
  const refreshDREResults = async () => {
    const results = {};
    for (const jg of jobGroups) {
      const result = await computeDREForJobGroup(jg);
      if (result) {
        results[jg.id] = result;
      }
    }
    setDreResults(results);
  };
  
  // Handle DRE override for removing diagnostic
  const handleDREOverrideConfirm = async () => {
    if (!dreOverrideReason.trim()) {
      alert("Please provide a reason for removing the diagnostic");
      return;
    }
    
    try {
      await axios.post(`${API}/dre/diagnostic-override`, {
        yestimate_id: yestimateId,
        job_group_id: dreOverrideModal.jobGroupId,
        override_reason: dreOverrideReason
      });
      
      // Update local state
      setJobGroups(jobGroups.map(jg => {
        if (jg.id === dreOverrideModal.jobGroupId) {
          return {
            ...jg,
            diagnostic_removed: true,
            diagnostic_override_reason: dreOverrideReason,
            // Remove diagnostic labour lines
            labour_lines: jg.labour_lines.filter(l => !l.is_diagnostic && l.category !== 'diagnostics')
          };
        }
        return jg;
      }));
      
      setDreOverrideModal({ show: false, jobGroupId: null, jobTitle: null });
      setDreOverrideReason('');
      
      // Refresh JCU after override
      refreshJCUEvaluation();
    } catch (err) {
      console.error("Failed to log DRE override:", err);
      alert("Failed to process override. Please try again.");
    }
  };
  
  // Add diagnostic labour line to job group
  const addDiagnosticToJobGroup = (jobGroupId, dreResult) => {
    setJobGroups(jobGroups.map(jg => {
      if (jg.id === jobGroupId) {
        // Check if diagnostic already exists
        const hasDiag = jg.labour_lines.some(l => l.is_diagnostic || l.category === 'diagnostics');
        if (hasDiag) return jg;
        
        // Add diagnostic labour line
        const diagLine = {
          id: `diag_${jobGroupId}`,
          category: 'diagnostics',
          description: `Diagnostic Investigation – ${dreResult.mode === 'mandatory_before_repair' ? 'Critical Assessment' : 'Root Cause Investigation'}`,
          hours: dreResult.diag_hours,
          rate: getLabourRate("diagnostics", 104.17),
          is_diagnostic: true,
          dre_mode: dreResult.mode,
          auto_added: true
        };
        
        return {
          ...jg,
          labour_lines: [...jg.labour_lines, diagLine]
        };
      }
      return jg;
    }));
  };
  
  // Handle JCU override confirmation and send
  const handleJCUOverrideConfirm = async () => {
    if (!jcuOverrideReason.trim()) {
      alert("Please provide a reason for the override");
      return;
    }
    
    try {
      // Log the override
      await axios.post(`${API}/jcu/v2/override`, {
        yestimate_id: yestimateId,
        override_reason: jcuOverrideReason,
        original_score: jcuOverrideModal.details?.score || 0,
        original_level: jcuOverrideModal.details?.level || 'red'
      });
      
      setJcuOverrideApplied(true);
      setJcuOverrideModal({ show: false, details: null });
      setJcuOverrideReason('');
      
      // Now proceed with the actual send
      await performSaveWithOverride();
    } catch (err) {
      console.error("Failed to log override:", err);
      alert("Failed to process override. Please try again.");
    }
  };
  
  // Send with JCU override applied
  const performSaveWithOverride = async () => {
    setSaving(true);
    try {
      // Use sanitized job groups with proper defaults
      const sanitizedGroups = sanitizeJobGroups(jobGroups);
      
      await axios.put(`${API}/foh/yestimate/${yestimateId}`, {
        tone,
        customer_name: customerName,
        current_job_value: currentJobValue ? parseFloat(currentJobValue) : null,
        // Global charges persistence (consumables + environmental).
        global_consumables_applied: globalCharges?.consumables?.applied ?? true,
        global_consumables_disapply_reason: globalCharges?.consumables?.disapplyReason || null,
        global_environmental_applied: globalCharges?.environmental?.applied ?? true,
        global_environmental_disapply_reason: globalCharges?.environmental?.disapplyReason || null,
        job_groups: sanitizedGroups.map(jg => ({
          id: jg.id,
          inspection_item_id: jg.inspection_item_id,
          vi_item_key: jg.vi_item_key,
          title: jg.title || 'Untitled Job',
          source: jg.source || "inspection",
          status: jg.status,
          jcu_package_id: jg.jcu_package_id || null,
          pricing_confirmed: jg.pricing_confirmed || false,
          ...buildMotProvenancePayload(jg),
          labour_lines: (jg.labour_lines || []).map(l => ({
            id: l.id,
            category: l.category || 'repair_labour',
            description: l.description || '',
            hours: parseFloat(l.hours) || 0,
            rate: parseFloat(l.rate) || 0
          })),
          parts_lines: (jg.parts_lines || []).map(p => ({
            id: p.id,
            description: p.description || 'Part',
            quantity: parseFloat(p.quantity) || 1,
            unit_price: parseFloat(p.unit_price) || 0,
            buy_in_price: parseFloat(p.buy_in_price) || 0,
            vat_rate: parseFloat(p.vat_rate) || 20
          })),
          charges: (jg.charges || []).map(c => ({
            id: c.id,
            charge_type: c.charge_type || 'consumables',
            description: c.description || 'Charge',
            amount: parseFloat(c.amount) || 0,
            vat_rate: parseFloat(c.vat_rate) || 20
          }))
        })),
        jcu_override_applied: true,
        jcu_override_reason: jcuOverrideReason
      });
      navigate(`/app/foh/yestimate/${yestimateId}/preview`);
    } catch (err) {
      const errorMessage = getErrorMessage(err, "Failed to save");
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Main save handler - directly saves, JCE is advisory only
  const handleSave = async () => {
    // JCE/JCU suggestions are ADVISORY ONLY - they should never block saving
    // Hard blocks only for: missing inspection, no price, missing contact details
    console.log('handleSave - Proceeding directly (JCE is advisory only)');
    await performSave();
  };
  
  // Handler for JCE modal confirmation - legacy, kept for compatibility
  const handleJCESendConfirm = async (decisions) => {
    setShowJCESendModal(false);
    // Decisions are already recorded by the modal, proceed with save
    await performSave();
  };
  
  // Handler for JCE modal close without confirmation
  const handleJCESendClose = () => {
    setShowJCESendModal(false);
    // User closed modal - don't save, let them continue editing
  };
  
  // ============== JOB INTEGRITY CALCULATION EFFECT ==============
  // Calculate job integrity when job groups change (debounced)
  // Note: This needs to be before the early returns but uses functions defined after
  // So we use a ref pattern to avoid the conditional hook issue
  const integrityCalculationRef = useRef(null);
  useEffect(() => {
    if (!jobGroups || jobGroups.length === 0 || !data) return;
    
    // Clear previous timer
    if (integrityCalculationRef.current) {
      clearTimeout(integrityCalculationRef.current);
    }
    
    // Debounce integrity calculation
    integrityCalculationRef.current = setTimeout(async () => {
      // Calculate integrity inline instead of calling the function
      setIntegrityLoading(true);
      try {
        // Calculate integrity for each job in parallel
        const integrityPromises = jobGroups.map(async (jg) => {
          try {
            const response = await axios.post(`${API}/integrity/job/calculate`, {
              job_group: jg,
              vi_context: {
                ac_gas_type: data?.vi_items?.find(i => i.item_key === 'aircon_operation')?.ac_gas_type
              }
            });
            return { jobId: jg.id, data: response.data };
          } catch (err) {
            console.error('Error calculating job integrity:', err);
            return { jobId: jg.id, data: null };
          }
        });
        
        const results = await Promise.all(integrityPromises);
        
        // Build integrity data map
        const newIntegrityData = {};
        results.forEach(result => {
          if (result.data) {
            newIntegrityData[result.jobId] = result.data;
          }
        });
        setJobIntegrityData(newIntegrityData);
        
        // Calculate estimate-level readiness
        const readinessResponse = await axios.post(`${API}/integrity/estimate/readiness`, {
          job_groups: jobGroups,
          estimate_id: yestimateId || 'preview',
          vi_context: {
            ac_gas_type: data?.vi_items?.find(i => i.item_key === 'aircon_operation')?.ac_gas_type
          }
        });
        setEstimateReadiness(readinessResponse.data);
      } catch (err) {
        console.error('Error calculating job integrity:', err);
      } finally {
        setIntegrityLoading(false);
      }
    }, 800);
    
    return () => {
      if (integrityCalculationRef.current) {
        clearTimeout(integrityCalculationRef.current);
      }
    };
  }, [jobGroups, data, yestimateId]);

  // Memoize vehicleContext to prevent JCE panel from flickering/re-fetching constantly
  // This object is used by JCEV2Panel and should only change when actual vehicle data changes
  // MUST be defined before early returns to comply with React hooks rules
  const memoizedVehicleContext = useMemo(() => ({
    reg: data?.job?.reg,
    age_years: data?.job?.year ? new Date().getFullYear() - data.job.year : 0,
    mileage: data?.yestimate?.mileage || data?.job?.mileage || 0,
    fuel_type: data?.job?.fuel_type,
    make: data?.job?.make,
    model: data?.job?.model,
    year: data?.job?.year
  }), [data?.job?.reg, data?.job?.year, data?.yestimate?.mileage, data?.job?.mileage, data?.job?.fuel_type, data?.job?.make, data?.job?.model]);
  
  // Get service interval items (timing belt, etc.) for the left panel
  // MUST be defined before early returns to comply with React hooks rules
  const serviceItemsFromJobGroups = useMemo(() => {
    return jobGroups.filter(jg => 
      jg.is_interval_item || 
      jg.source === "interval" || 
      jg.title?.toLowerCase().includes('timing belt') ||
      jg.title?.toLowerCase().includes('cambelt')
    );
  }, [jobGroups]);

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!data) return <div className="p-8 text-center">Yestimate not found</div>;

  const grandTotal = calculateGrandTotal();
  // Include "dangerous" items with red items (they are treated the same in pricing)
  const rawRedItems = data.items.filter(i => i.status === "red" || i.status === "dangerous");
  const rawAmberItems = data.items.filter(i => i.status === "amber");
  
  // =========================================================================
  // BRAKE DISC + PADS CONSOLIDATION LOGIC
  // When brake discs are flagged, they should auto-include pads as a combined item
  // Discs are never fitted without pads - they're always done together
  // =========================================================================
  const consolidateBrakeItems = (items) => {
    const consolidated = [];
    const processedKeys = new Set();
    
    // Define brake item keys from the filtered items
    const frontDiscs = items.find(i => i.item_key === 'front_brake_discs');
    const frontPads = items.find(i => i.item_key === 'front_brake_pads');
    const rearDiscs = items.find(i => i.item_key === 'rear_brake_discs');
    const rearPads = items.find(i => i.item_key === 'rear_brake_pads');
    
    // Also look for pads in the full data.items array (for wear_sensor_required)
    // This is needed because pads might be amber while discs are red
    const allFrontPads = data.items.find(i => i.item_key === 'front_brake_pads');
    const allRearPads = data.items.find(i => i.item_key === 'rear_brake_pads');
    
    // Front brakes consolidation
    if (frontDiscs) {
      // Discs flagged - always combine with pads, always RED status (safety-critical)
      // IMPORTANT: Keep item_key as 'front_brake_discs' for Rule Engine mapping
      // The VI mapping maps front_brake_discs -> rule_front_brake_discs_pads
      const isDiscRedOrDangerous = frontDiscs.status === 'red' || frontDiscs.status === 'dangerous';
      // Use allFrontPads for original_items to preserve wear_sensor_required from amber pads
      const padsForOriginal = frontPads || allFrontPads;
      consolidated.push({
        ...frontDiscs,
        id: `consolidated_front_brake_discs_pads_${frontDiscs.id}`,
        item_key: 'front_brake_discs', // Keep original key for Rule Engine!
        item_label: 'Front Brake Discs & Pads',
        status: isDiscRedOrDangerous ? 'red' : frontDiscs.status,
        consolidated: true,
        original_items: [frontDiscs, padsForOriginal].filter(Boolean),
        notes: [frontDiscs.notes, padsForOriginal?.notes].filter(Boolean).join(' | '),
        photo_urls: [...(frontDiscs.photo_urls || []), ...(padsForOriginal?.photo_urls || [])]
      });
      processedKeys.add('front_brake_discs');
      processedKeys.add('front_brake_pads');
    } else if (frontPads && !frontDiscs) {
      // Only pads flagged (discs OK) - show pads alone
      consolidated.push(frontPads);
      processedKeys.add('front_brake_pads');
    }
    
    // Rear brakes consolidation
    if (rearDiscs) {
      // Discs flagged - always combine with pads, always RED status (safety-critical)
      // IMPORTANT: Keep item_key as 'rear_brake_discs' for Rule Engine mapping
      // The VI mapping maps rear_brake_discs -> rule_rear_brake_discs_pads
      const isDiscRedOrDangerous = rearDiscs.status === 'red' || rearDiscs.status === 'dangerous';
      // Use allRearPads for original_items to preserve wear_sensor_required from amber pads
      const padsForOriginal = rearPads || allRearPads;
      consolidated.push({
        ...rearDiscs,
        id: `consolidated_rear_brake_discs_pads_${rearDiscs.id}`,
        item_key: 'rear_brake_discs', // Keep original key for Rule Engine!
        item_label: 'Rear Brake Discs & Pads',
        status: isDiscRedOrDangerous ? 'red' : rearDiscs.status,
        consolidated: true,
        original_items: [rearDiscs, padsForOriginal].filter(Boolean),
        notes: [rearDiscs.notes, padsForOriginal?.notes].filter(Boolean).join(' | '),
        photo_urls: [...(rearDiscs.photo_urls || []), ...(padsForOriginal?.photo_urls || [])]
      });
      processedKeys.add('rear_brake_discs');
      processedKeys.add('rear_brake_pads');
    } else if (rearPads && !rearDiscs) {
      // Only pads flagged (discs OK) - show pads alone
      consolidated.push(rearPads);
      processedKeys.add('rear_brake_pads');
    }
    
    // Add all other non-brake items
    items.forEach(item => {
      if (!processedKeys.has(item.item_key)) {
        consolidated.push(item);
      }
    });
    
    return consolidated;
  };
  
  // Apply consolidation to both red and amber items
  const redItems = consolidateBrakeItems(rawRedItems);
  const consolidatedAmberItems = consolidateBrakeItems(rawAmberItems);
  
  // =========================================================================
  // CROSS-CATEGORY SUBSUMPTION
  // When a parent item (e.g. front_brake_discs) is in Red, its child items
  // (e.g. front_brake_pads) MUST NOT appear separately in Amber.
  // The parent's consolidated item already covers the child's work scope.
  // =========================================================================
  const SUBSUMPTION_MAP = {
    'front_brake_discs': ['front_brake_pads'],
    'rear_brake_discs': ['rear_brake_pads'],
    'clutch_kit': ['clutch_friction_plate', 'clutch_release_bearing'],
  };
  
  // Collect all item_keys present in red (including originals from consolidated items)
  const redItemKeys = new Set();
  redItems.forEach(item => {
    if (item.item_key) redItemKeys.add(item.item_key);
    if (item.original_items) {
      item.original_items.forEach(oi => { if (oi.item_key) redItemKeys.add(oi.item_key); });
    }
  });
  
  // Build set of child keys subsumed by red parents
  const subsumedByRed = new Set();
  for (const [parentKey, childKeys] of Object.entries(SUBSUMPTION_MAP)) {
    if (redItemKeys.has(parentKey)) {
      childKeys.forEach(ck => subsumedByRed.add(ck));
    }
  }
  
  // Filter out amber items that are subsumed by a red parent
  const amberItemsRaw = consolidatedAmberItems.filter(item => !subsumedByRed.has(item.item_key));
  
  // iter198x — MOT ↔ VI dedup. When a defect appears BOTH as a DVSA
  // MOT JG (source startswith "dvsa_mot_result") AND as a VI inspection
  // finding, FOH should only see the MOT representation (it's the
  // canonical source). Service techs routinely flag the VI item AFTER
  // seeing the MOT result — without this dedup the same defect would
  // appear twice in the left panel.
  // We dedupe by ``vi_item_key`` since the MOT-to-VI bridge stamps the
  // canonical VI key on every MOT JG it routes. Items without a
  // resolvable ``vi_item_key`` on the MOT side stay unfiltered.
  const motCoveredViKeys = new Set(
    (jobGroups || [])
      .filter(jg => ((jg.source || '').toLowerCase()).includes('dvsa_mot_result'))
      .map(jg => (jg.vi_item_key || '').toLowerCase())
      .filter(Boolean)
  );
  const _dedupViByMot = (items) =>
    items.filter(it => {
      const k = (it.item_key || '').toLowerCase();
      return !k || !motCoveredViKeys.has(k);
    });
  const redItemsDeduped = _dedupViByMot(redItems);
  const amberItems = _dedupViByMot(amberItemsRaw);
  
  // Get items that have job groups created (for linking)
  const itemsWithJobGroups = new Set(jobGroups.map(jg => jg.inspection_item_id));

  // iter211 — Tyre items absorbed into a tyre_replacement_group must
  // also count as "linked" so clicking them in the left panel focuses
  // the group instead of creating a duplicate JG.
  jobGroups
    .filter(jg => jg.subtype === "tyre_replacement_group")
    .forEach(jg => {
      const corners = new Set(
        (jg.tyres || [])
          .filter(c => c && c.replacement_required !== false)
          .map(c => (c.position || "").toUpperCase())
          .filter(Boolean)
      );
      if (!corners.size) return;
      (data.items || []).forEach(it => {
        const k = (it?.item_key || "").toLowerCase();
        const l = (it?.item_label || it?.label || "").toLowerCase();
        if (!(/\btyre\b|\btire\b/.test(k) || /\btyre\b|\btire\b/.test(l))) return;
        const blob = [it.item_key, it.item_label, it.label, it.affected_side, it.notes]
          .filter(Boolean).join(" ").toLowerCase();
        let c = null;
        if (/\b(nsf|n\.?s\.?f|front[\s_-]*left|left[\s_-]*front|fl)\b/.test(blob)) c = "NSF";
        else if (/\b(osf|o\.?s\.?f|front[\s_-]*right|right[\s_-]*front|fr)\b/.test(blob)) c = "OSF";
        else if (/\b(nsr|n\.?s\.?r|rear[\s_-]*left|left[\s_-]*rear|rl)\b/.test(blob)) c = "NSR";
        else if (/\b(osr|o\.?s\.?r|rear[\s_-]*right|right[\s_-]*rear|rr)\b/.test(blob)) c = "OSR";
        if (c && corners.has(c)) itemsWithJobGroups.add(it.id);
      });
    });
  
  // Get items that have CONFIRMED pricing (FOH explicitly confirmed they're done)
  // An item is only "done" when FOH clicks "Confirm Pricing Complete" button
  const pricedItemIds = new Set(
    jobGroups
      .filter(jg => jg.pricing_confirmed === true)
      .map(jg => jg.inspection_item_id)
  );

  // iter211 — A confirmed tyre_replacement_group absorbs MULTIPLE tyre
  // VI items (one per corner) into a single JG, but only carries ONE
  // ``inspection_item_id`` (the primary one). Without this back-fill the
  // OTHER tyre rows in the left panel stay amber/red even though FOH
  // has clearly confirmed pricing for the whole group. Walk every
  // confirmed tyre group, resolve each child's corner, then mark the
  // matching tyre inspection item as priced too.
  const _resolveCornerFromText = (text) => {
    const blob = (text || "").toLowerCase();
    if (/\b(nsf|n\.?s\.?f|front[\s_-]*left|left[\s_-]*front|fl)\b/.test(blob)) return "NSF";
    if (/\b(osf|o\.?s\.?f|front[\s_-]*right|right[\s_-]*front|fr)\b/.test(blob)) return "OSF";
    if (/\b(nsr|n\.?s\.?r|rear[\s_-]*left|left[\s_-]*rear|rl)\b/.test(blob)) return "NSR";
    if (/\b(osr|o\.?s\.?r|rear[\s_-]*right|right[\s_-]*rear|rr)\b/.test(blob)) return "OSR";
    return null;
  };
  const _tyreItemCorner = (it) => _resolveCornerFromText(
    [it?.item_key, it?.item_label, it?.label, it?.affected_side, it?.notes]
      .filter(Boolean).join(" ")
  );
  const _isTyreItem = (it) => {
    const k = (it?.item_key || "").toLowerCase();
    const l = (it?.item_label || it?.label || "").toLowerCase();
    return /\btyre\b|\btire\b/.test(k) || /\btyre\b|\btire\b/.test(l);
  };
  jobGroups
    .filter(jg => jg.pricing_confirmed === true && jg.subtype === "tyre_replacement_group")
    .forEach(jg => {
      const corners = new Set(
        (jg.tyres || [])
          .filter(c => c && c.replacement_required !== false)
          .map(c => (c.position || "").toUpperCase())
          .filter(Boolean)
      );
      if (!corners.size) return;
      (data.items || []).forEach(it => {
        if (!_isTyreItem(it)) return;
        const corner = _tyreItemCorner(it);
        if (corner && corners.has(corner)) {
          pricedItemIds.add(it.id);
        }
      });
    });
  
  // Get items that have job groups but pricing NOT yet confirmed (in progress)
  const inProgressItemIds = new Set(
    jobGroups
      .filter(jg => jg.pricing_confirmed !== true && jg.inspection_item_id)
      .map(jg => jg.inspection_item_id)
  );

  // iter211 — Same back-fill as pricedItemIds but for the un-confirmed
  // tyre groups so the other corners surface as "In Progress" instead
  // of stuck on "Price".
  jobGroups
    .filter(jg => jg.pricing_confirmed !== true && jg.subtype === "tyre_replacement_group")
    .forEach(jg => {
      const corners = new Set(
        (jg.tyres || [])
          .filter(c => c && c.replacement_required !== false)
          .map(c => (c.position || "").toUpperCase())
          .filter(Boolean)
      );
      if (!corners.size) return;
      (data.items || []).forEach(it => {
        if (!_isTyreItem(it)) return;
        if (pricedItemIds.has(it.id)) return;
        const corner = _tyreItemCorner(it);
        if (corner && corners.has(corner)) {
          inProgressItemIds.add(it.id);
        }
      });
    });

  // iter211e — Surface "Not Quoting" status to the left-panel inspection
  // items so FOH can see at a glance which items have been deliberately
  // excluded from the customer quote. Greyed-out card + Not Quoting
  // chip render off this set.
  const notQuotedItemIds = new Set(
    jobGroups
      .filter(jg => jg.not_quoted_reason && jg.inspection_item_id)
      .map(jg => jg.inspection_item_id)
  );

  // Helper to identify paired items (springs, shocks, etc.) for visual indicator
  // Now using API-driven pair policy instead of hardcoded keywords
  const PAIRED_ITEM_KEYWORDS = [
    'shock', 'spring', 'top_mount', 'track_rod', 'ball_joint', 'drop_link', 'cv_boot',
    // Suspension arms — pair policy is RECOMMEND (not mandatory), but the JG
    // still earns the pair-advisory pill so FOH considers replacing the
    // opposite side before sending to customer.
    'suspension_arm', 'wishbone', 'control_arm', 'trailing_arm', 'lower_arm',
  ];
  const isPairedItem = (item) => {
    const itemKeyLower = item.item_key?.toLowerCase() || '';
    const itemLabelLower = item.item_label?.toLowerCase() || '';
    // Exclude brakes - they're mandatory bundles, not pair policy items
    if (itemKeyLower.includes('brake') || itemLabelLower.includes('brake')) {
      return false;
    }
    const isPaired = PAIRED_ITEM_KEYWORDS.some(kw => itemKeyLower.includes(kw) || itemLabelLower.includes(kw));
    const isSingleSide = item.affected_side && item.affected_side.toLowerCase() !== 'both';
    const hasNoSide = !item.affected_side;
    return isPaired && (isSingleSide || hasNoSide);
  };
  
  // Check if diagnostic job group has CONFIRMED pricing
  const isDiagnosticPricingComplete = jobGroups.some(jg => {
    if (jg.source !== 'diagnostic') return false;
    return jg.pricing_confirmed === true;
  });
  
  // Check if diagnostic is priced (required before SI email)
  const isDiagnosticPriced = !diagnosticInfo || hasDiagnosticJobGroup;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ITEM-LEVEL COMPLETENESS VALIDATION
  // Validates job group completeness when FOH attempts to mark "Pricing Complete"
  // This is the primary enforcement point - NOT the send screen
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Clear validation errors for a job when it's edited
  const clearJobValidationErrors = (jobGroupId) => {
    setJobValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[jobGroupId];
      return updated;
    });
  };
  
  // Validate a job group for completeness
  // Returns { valid: boolean, errors: string[] }
  const validateJobGroupCompleteness = (jobGroup) => {
    const errors = [];
    
    // Basic validation - must have at least some pricing content
    const hasLabour = jobGroup.labour_lines && jobGroup.labour_lines.length > 0;
    const hasParts = jobGroup.parts_lines && jobGroup.parts_lines.length > 0;
    const hasCharges = jobGroup.charges && jobGroup.charges.length > 0;
    const hasPricing = hasLabour || hasParts || hasCharges;
    
    // If marked as "not quoted", skip validation
    if (jobGroup.not_quoted_reason) {
      return { valid: true, errors: [] };
    }
    
    // Iter189 — Emissions BLOCKED_TEST / UNKNOWN_CAUSE JGs legitimately
    // have zero parts/labour (the staged-routing engine suppressed
    // them because the gas reading isn't trustworthy yet). Skip the
    // "must have pricing" gate so FOH can mark these as complete
    // without faking parts.
    if (jobGroup.emissions_suppress_parts === true
        && ['BLOCKED_TEST', 'UNKNOWN_CAUSE'].includes(jobGroup.emissions_state)) {
      return { valid: true, errors: [] };
    }
    
    // Rule 1: Must have at least labour OR parts (something to charge for)
    if (!hasPricing) {
      errors.push("No pricing content added - add labour, parts, or mark as 'Not Quoting'");
    }
    
    // Rule 2: If has labour, validate labour lines have valid values
    if (hasLabour) {
      const invalidLabour = jobGroup.labour_lines.some(l => {
        const hours = parseFloat(l.hours) || 0;
        const rate = parseFloat(l.rate) || 0;
        return hours <= 0 || rate <= 0;
      });
      if (invalidLabour) {
        errors.push("Labour line(s) have invalid hours or rate - check all labour entries");
      }
    }
    
    // Rule 3: If has parts, validate parts have valid prices
    if (hasParts) {
      const invalidParts = jobGroup.parts_lines.some(p => {
        const qty = parseFloat(p.quantity) || 0;
        const price = parseFloat(p.unit_price) || 0;
        return qty <= 0 || price <= 0;
      });
      if (invalidParts) {
        errors.push("Part(s) have invalid quantity or price - check all part entries");
      }
    }
    
    // Rule 4: Job-specific completeness rules based on item type
    const itemKey = jobGroup.vi_item_key || jobGroup.interval_item_id || '';
    const title = (jobGroup.title || '').toLowerCase();
    
    // Skip parts validation if explicitly marked as labour-only or if diagnostic job
    const diagTitlePatterns = /diagnostic|fault\s*(?:investigation|finding|diagnosis)|diagnose\s*before|warning\s*light.*(?:diagnos|investigat)|(?:diagnos|investigat).*(?:fault|warning)|electrical\s*(?:diagnos|investigat)|abs.*(?:diagnos|investigat)|airbag.*(?:diagnos|investigat)/i;
    const hasDiagLabour = (jobGroup.labour_lines || []).some(l => l.category === 'diagnostics' || l.category === 'diagnostic_labour' || l.category === 'inspection_testing');
    const isJobDiagnostic = jobGroup.source === "diagnostic" || 
      itemKey.includes('warning') || itemKey.includes('dash_warning') ||
      diagTitlePatterns.test(jobGroup.title || '') || hasDiagLabour;
    
    const skipPartsCheck = jobGroup.is_labour_only || isJobDiagnostic;
    
    // Brake-related jobs
    if (title.includes('brake') || itemKey.includes('brake')) {
      if (!hasParts && hasLabour && !skipPartsCheck) {
        errors.push("Brake jobs typically require parts (pads, discs, etc.) - add parts or confirm intentional labour-only");
      }
    }
    
    // Battery-related jobs
    if (title.includes('battery') || itemKey.includes('battery')) {
      if (!hasParts && !title.includes('test') && !title.includes('charge') && !skipPartsCheck) {
        errors.push("Battery replacement requires a battery part");
      }
    }
    
    // Oil/Service-related jobs
    if (title.includes('oil') || title.includes('service') || itemKey.includes('oil_filter')) {
      if (hasLabour && !hasParts && !skipPartsCheck) {
        errors.push("Oil service typically requires oil and filter parts");
      }
    }
    
    // Timing belt jobs
    if (title.includes('timing') && (title.includes('belt') || title.includes('chain'))) {
      if (hasLabour && (!hasParts || jobGroup.parts_lines.length < 2) && !skipPartsCheck) {
        errors.push("Timing belt/chain jobs typically require multiple parts (belt/chain, tensioner, etc.)");
      }
    }
    
    // Clutch jobs
    if (title.includes('clutch')) {
      if (hasLabour && !hasParts && !skipPartsCheck) {
        errors.push("Clutch jobs require parts (clutch kit, release bearing, etc.)");
      }
    }
    
    // Suspension jobs
    if (title.includes('spring') || title.includes('shock') || title.includes('strut')) {
      if (hasLabour && !hasParts && !skipPartsCheck) {
        errors.push("Suspension jobs typically require parts");
      }
    }
    
    // Rule 5: Check for zero-value totals (warning, not blocking)
    if (hasPricing) {
      const labourTotal = (jobGroup.labour_lines || []).reduce((sum, l) => {
        return sum + ((parseFloat(l.hours) || 0) * (parseFloat(l.rate) || 0));
      }, 0);
      const partsTotal = (jobGroup.parts_lines || []).reduce((sum, p) => {
        return sum + ((parseFloat(p.quantity) || 0) * (parseFloat(p.unit_price) || 0));
      }, 0);
      const chargesTotal = (jobGroup.charges || []).reduce((sum, c) => {
        return sum + (parseFloat(c.amount) || 0);
      }, 0);
      const total = labourTotal + partsTotal + chargesTotal;
      
      if (total === 0) {
        errors.push("Total value is £0.00 - is this intentional?");
      }
    }
    
    return {
      valid: errors.length === 0,
      errors: errors
    };
  };
  
  // ============== PIE VALIDATION FUNCTIONS ==============
  // Validate job groups using the Pricing Integrity Engine (PIE)
  const validateJobGroupWithPIE = async (jobGroup) => {
    if (!jobGroup || !jobGroup.id) return null;
    
    try {
      const vehicleContext = {
        make: data?.job?.make || '',
        model: data?.job?.model || '',
        mileage: data?.job?.mileage || 0,
        year: data?.job?.year || null
      };
      
      const response = await axios.post(`${API}/pie/validate/job-group`, {
        job_group: {
          id: jobGroup.id,
          title: jobGroup.title,
          vi_item_key: jobGroup.vi_item_key,
          labour_lines: jobGroup.labour_lines || [],
          parts_lines: jobGroup.parts_lines || [],
          charges: jobGroup.charges || []
        },
        yestimate_id: yestimateId || 'unknown',
        vehicle_context: vehicleContext
      });
      
      return response.data;
    } catch (err) {
      console.error('PIE validation failed:', err);
      return null;
    }
  };
  
  // Validate all job groups and update PIE warnings state
  const validateAllJobGroupsWithPIE = async () => {
    if (!jobGroups || jobGroups.length === 0) return;
    
    setPieValidating(true);
    const newWarnings = {};
    let totalWarnings = 0;
    let criticalWarnings = 0;
    let estimatedMissedRevenue = 0;
    
    for (const jg of jobGroups) {
      // Skip jobs marked as "not quoting"
      if (jg.not_quoted_reason) continue;
      
      const result = await validateJobGroupWithPIE(jg);
      if (result && result.warnings && result.warnings.length > 0) {
        newWarnings[jg.id] = result.warnings;
        totalWarnings += result.warnings.length;
        criticalWarnings += result.warnings.filter(w => w.severity === 'critical').length;
        estimatedMissedRevenue += result.warnings.reduce((sum, w) => sum + (w.estimated_revenue_impact || 0), 0);
      }
    }
    
    setPieWarnings(newWarnings);
    setPieSummary({
      totalWarnings,
      criticalWarnings,
      estimatedMissedRevenue,
      integrityScore: totalWarnings === 0 ? 100 : Math.max(0, 100 - (criticalWarnings * 25) - ((totalWarnings - criticalWarnings) * 10))
    });
    setPieValidating(false);
  };
  
  // Dismiss a PIE warning
  const dismissPieWarning = (jobGroupId, warningId) => {
    setPieWarnings(prev => {
      const jobWarnings = prev[jobGroupId] || [];
      return {
        ...prev,
        [jobGroupId]: jobWarnings.filter(w => w.id !== warningId)
      };
    });
    toast.success('Warning dismissed');
  };
  
  // Apply a PIE suggested fix
  const applyPieSuggestedFix = (jobGroupId, warning) => {
    if (!warning.suggested_fix) return;
    
    const { action, item_description, suggested_value, suggested_quantity } = warning.suggested_fix;
    
    setJobGroups(prev => prev.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      
      const updated = { ...jg };
      
      if (action === 'add_labour') {
        const newLabour = {
          id: `${Date.now()}-pie-labour`,
          category: 'strip_renew',
          description: item_description,
          hours: suggested_value || 1.0,
          rate: 65
        };
        updated.labour_lines = [...(updated.labour_lines || []), newLabour];
      } else if (action === 'add_part') {
        const newPart = {
          id: `${Date.now()}-pie-part`,
          description: item_description,
          quantity: suggested_quantity || 1,
          unit_price: suggested_value || 0,
          vat_rate: 20
        };
        updated.parts_lines = [...(updated.parts_lines || []), newPart];
      } else if (action === 'add_charge') {
        const newCharge = {
          id: `${Date.now()}-pie-charge`,
          charge_type: 'consumables',
          description: item_description,
          amount: suggested_value || 0,
          vat_rate: 20
        };
        updated.charges = [...(updated.charges || []), newCharge];
      } else if (action === 'adjust_time') {
        // Adjust the first labour line to suggested value
        if (updated.labour_lines && updated.labour_lines.length > 0) {
          updated.labour_lines = updated.labour_lines.map((l, i) => 
            i === 0 ? { ...l, hours: suggested_value || l.hours } : l
          );
        }
      }
      
      return updated;
    }));
    
    // Remove the warning after applying fix
    dismissPieWarning(jobGroupId, warning.id);
    toast.success(`Added: ${item_description}`);
  };

  // ============== JOB INTEGRITY ENGINE FUNCTIONS ==============
  
  // Calculate integrity for a single job group
  const calculateJobIntegrity = async (jobGroup) => {
    try {
      const response = await axios.post(`${API}/integrity/job/calculate`, {
        job_group: jobGroup,
        vi_context: {
          ac_gas_type: data?.vi_items?.find(i => i.item_key === 'aircon_operation')?.ac_gas_type
        }
      });
      return response.data;
    } catch (err) {
      console.error('Error calculating job integrity:', err);
      return null;
    }
  };
  
  // Calculate integrity for all job groups
  const calculateAllJobIntegrity = async () => {
    if (!jobGroups || jobGroups.length === 0) return;
    
    setIntegrityLoading(true);
    try {
      // Calculate integrity for each job in parallel
      const integrityPromises = jobGroups.map(jg => calculateJobIntegrity(jg));
      const results = await Promise.all(integrityPromises);
      
      // Build integrity data map
      const newIntegrityData = {};
      jobGroups.forEach((jg, idx) => {
        if (results[idx]) {
          newIntegrityData[jg.id] = results[idx];
        }
      });
      setJobIntegrityData(newIntegrityData);
      
      // Calculate estimate-level readiness
      const readinessResponse = await axios.post(`${API}/integrity/estimate/readiness`, {
        job_groups: jobGroups,
        estimate_id: yestimateId || 'preview',
        vi_context: {
          ac_gas_type: data?.vi_items?.find(i => i.item_key === 'aircon_operation')?.ac_gas_type
        }
      });
      setEstimateReadiness(readinessResponse.data);
      
    } catch (err) {
      console.error('Error calculating job integrity:', err);
    } finally {
      setIntegrityLoading(false);
    }
  };
  
  // Check send gate before sending quote
  const checkSendGate = async () => {
    try {
      const response = await axios.post(`${API}/integrity/estimate/send-gate`, {
        job_groups: jobGroups,
        estimate_id: yestimateId || 'preview',
        vi_context: {
          ac_gas_type: data?.vi_items?.find(i => i.item_key === 'aircon_operation')?.ac_gas_type
        }
      });
      setSendGateData(response.data);
      return response.data;
    } catch (err) {
      console.error('Error checking send gate:', err);
      return { can_send: false, blockers: [{ blocker_message: 'Error checking quote readiness' }] };
    }
  };
  
  // Add an integrity item to a job
  const addIntegrityItem = (jobGroupId, item) => {
    // Resolve item_type from metadata (backend nests it there) or top-level
    const itemType = item.metadata?.item_type || item.item_type;
    
    setJobGroups(prev => prev.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      
      const updated = { ...jg };
      
      if (itemType === 'labour') {
        const newLabour = {
          id: `${Date.now()}-integrity-labour`,
          category: 'strip_renew',
          description: item.label,
          hours: item.hours || 0.5,
          rate: item.rate || 65,
          integrity_tier: item.metadata?.tier || item.tier,
          source_rule_id: item.metadata?.source_rule_id || item.source_rule_id,
          is_auto_added: false
        };
        updated.labour_lines = [...(updated.labour_lines || []), newLabour];
      } else if (itemType === 'part' || itemType === 'fluid' || itemType === 'consumable') {
        const cost = item.estimated_part_cost || item.unit_price || 0;
        const newPart = {
          id: `${Date.now()}-integrity-part`,
          description: item.label,
          item_code: item.code,
          quantity: item.quantity || 1,
          buy_in_price: cost,
          unit_price: Math.round(cost * 1.4 * 100) / 100,
          vat_rate: 20,
          integrity_tier: item.metadata?.tier || item.tier,
          source_rule_id: item.metadata?.source_rule_id || item.source_rule_id,
          is_auto_added: false
        };
        updated.parts_lines = [...(updated.parts_lines || []), newPart];
      } else if (itemType === 'disposal' || itemType === 'charge') {
        const newCharge = {
          id: `${Date.now()}-integrity-charge`,
          charge_type: 'environmental',
          description: item.label,
          item_code: item.code,
          amount: item.charge_amount || item.unit_price || item.estimated_part_cost || 0,
          vat_rate: 20,
          integrity_tier: item.metadata?.tier || item.tier,
          source_rule_id: item.metadata?.source_rule_id || item.source_rule_id,
          is_auto_added: false
        };
        updated.charges = [...(updated.charges || []), newCharge];
      }
      
      return updated;
    }));
    
    toast.success(`Added: ${item.label}`);
    // Recalculate integrity after adding
    setTimeout(() => calculateAllJobIntegrity(), 500);
  };
  
  // Remove an integrity item from a job (with reason capture)
  const removeIntegrityItem = async (jobGroupId, item, reasonPreset, reasonCustom) => {
    const jobGroup = jobGroups.find(jg => jg.id === jobGroupId);
    if (!jobGroup) return;
    
    // Record removal in backend for audit
    try {
      await axios.post(`${API}/integrity/job/remove-item`, {
        job_group_id: jobGroupId,
        item_id: item.id,
        item_code: item.code,
        reason_preset: reasonPreset,
        reason_custom: reasonCustom,
        removed_by: 'unknown', // iter213d — user not defined in this scope
        estimate_id: yestimateId || 'preview',
        job_group: jobGroup
      });
    } catch (err) {
      console.error('Error recording removal:', err);
    }
    
    // Remove from job
    setJobGroups(prev => prev.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      
      const updated = { ...jg };
      
      // Track removal in job metadata
      if (!updated.integrity_removals) {
        updated.integrity_removals = [];
      }
      updated.integrity_removals.push({
        item_code: item.code,
        item_label: item.label,
        reason_preset: reasonPreset,
        reason_custom: reasonCustom,
        removed_at: new Date().toISOString(),
        removed_by: 'unknown' // iter213d — user not defined in this scope
      });
      
      // Remove from parts_lines
      if (item.code) {
        updated.parts_lines = (updated.parts_lines || []).filter(
          p => p.item_code !== item.code && !p.description?.toLowerCase().includes(item.label?.toLowerCase())
        );
        updated.charges = (updated.charges || []).filter(
          c => c.item_code !== item.code && !c.description?.toLowerCase().includes(item.label?.toLowerCase())
        );
      }
      
      return updated;
    }));
    
    toast.success(`Removed: ${item.label}`);
    // Recalculate integrity after removal
    setTimeout(() => calculateAllJobIntegrity(), 500);
  };
  
  // Restore a previously removed item
  const restoreIntegrityItem = (jobGroupId, item) => {
    // Re-add the item
    addIntegrityItem(jobGroupId, item);
    
    // Remove from removals list
    setJobGroups(prev => prev.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      
      const updated = { ...jg };
      if (updated.integrity_removals) {
        updated.integrity_removals = updated.integrity_removals.filter(
          r => r.item_code !== item.code
        );
      }
      return updated;
    }));
    
    toast.success(`Restored: ${item.label}`);
  };
  
  // Jump to a specific job group (expand and scroll)
  const jumpToJobGroup = (jobGroupId) => {
    // Expand the job
    setExpandedJobs(prev => ({ ...prev, [jobGroupId]: true }));
    
    // Scroll to the job card — use 'start' so the top of the card is visible
    setTimeout(() => {
      const jobElement = jobGroupRefs.current[jobGroupId];
      if (jobElement) {
        jobElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };
  
  // Function to attempt to confirm pricing for a job group
  // This is the PRIMARY ENFORCEMENT POINT for completeness
  const confirmJobGroupPricing = (jobGroupId) => {
    const jobGroup = jobGroups.find(jg => jg.id === jobGroupId);
    if (!jobGroup) return;
    
    // Run validation
    const validation = validateJobGroupCompleteness(jobGroup);
    
    if (!validation.valid) {
      // Store validation errors for this job - show inline in the panel
      setJobValidationErrors(prev => ({
        ...prev,
        [jobGroupId]: validation.errors
      }));
      
      // Show toast notification as well
      toast.error("Cannot mark as complete", {
        description: "This job has missing requirements. See details in the panel below.",
        duration: 4000
      });
      
      // Keep the panel expanded so FOH can see and fix the issues
      setExpandedJobs(prev => ({ ...prev, [jobGroupId]: true }));
      return;
    }
    
    // Validation passed - clear any previous errors and confirm
    clearJobValidationErrors(jobGroupId);
    
    setJobGroups(jobGroups.map(jg => 
      jg.id === jobGroupId 
        ? { ...jg, pricing_confirmed: true }
        : jg
    ));
    
    // Collapse the job card after confirming pricing
    setExpandedJobs(prev => ({ ...prev, [jobGroupId]: false }));
    
    toast.success("Pricing confirmed", { duration: 2000 });
  };
  
  // Function to unconfirm pricing (if they need to edit)
  const unconfirmJobGroupPricing = (jobGroupId) => {
    clearJobValidationErrors(jobGroupId);
    setJobGroups(jobGroups.map(jg => 
      jg.id === jobGroupId 
        ? { ...jg, pricing_confirmed: false }
        : jg
    ));
  };
  
  // Focus on a single job group - collapse all others and expand this one
  // This is the main navigation helper for FOH efficiency
  // ---- DVSA MOT provenance fields that must survive every save ----
  // Without this any FE save (handleSave / performSaveWithOverride)
  // would strip the source_mot_* / auto_quoted / status_color fields
  // and the left-panel "MOT Defects" list would lose its colour-coding
  // on reload. Kept as a tiny pure helper so both save paths use it.
  const buildMotProvenancePayload = (jg) => ({
    source_mot_outcome_id:         jg.source_mot_outcome_id || null,
    source_mot_text:               jg.source_mot_text || null,
    source_mot_severity:           jg.source_mot_severity || null,
    source_mot_test_date:          jg.source_mot_test_date || null,
    source_mot_test_result:        jg.source_mot_test_result || null,
    source_mot_continuity_warning: jg.source_mot_continuity_warning || null,
    source_mot_is_repeat:          jg.source_mot_is_repeat || false,
    category:                      jg.category || null,
    status_color:                  jg.status_color || null,
    rule_id:                       jg.rule_id || null,
    auto_quoted:                   jg.auto_quoted || false,
    pricing_status:                jg.pricing_status || null,
    pricing_complete:              jg.pricing_complete || false,
    internal_notes:                jg.internal_notes || null,
    // iter197r — Structured-recommendation fields that must survive a
    // save cycle (alignment, future ADAS/AC/etc.). Without these the
    // FE save endpoints would strip type/subtype + the recommendation
    // payload and the left-rail "Added Recommendations" filter would
    // silently drop the JG even though it's still priced.
    type:                          jg.type || null,
    subtype:                       jg.subtype || null,
    alignment_type:                jg.alignment_type || null,
    alignment_type_label:          jg.alignment_type_label || null,
    trigger_reasons:               jg.trigger_reasons || null,
    reason_badges:                 jg.reason_badges || null,
    linked_repair_jg_ids:          jg.linked_repair_jg_ids || null,
    integrity_tier:                jg.integrity_tier || null,
    generated_explanation:         jg.generated_explanation || null,
    manually_edited:               jg.manually_edited || false,
    portal_visible:                jg.portal_visible !== false,
    price_override:                jg.price_override != null ? jg.price_override : null,
    // Customer-facing advisory notes (brake-pad → disc warning, bulb →
    // diag warning). FOH toggles ``enabled`` via switch on the JG card;
    // we round-trip the whole array so the backend can merge with
    // freshly-derived notes on save.
    customer_notes:                jg.customer_notes || [],
    // AI-assisted Contextual Customer Note (CCN). Persists on the JG so
    // toggle, raw input and approved final text survive a save cycle.
    contextual_customer_note:      jg.contextual_customer_note || null,
    // iter197p — Customer Advice Engine narrative.
    customer_advice:               jg.customer_advice || null,
    // MCC escalation flag — tester routed the defect to a Secondary
    // Technical Assessment task. Surfaced on the FOH card as a purple
    // "Pending Diagnostic" pill.
    pending_diagnostic_assessment: !!jg.pending_diagnostic_assessment,
    pending_diagnostic_task_id:    jg.pending_diagnostic_task_id || null,
    // Phase-1 MCC Rapid Capture surface fields. Read-only on the FOH
    // pricing screen; backend re-resolves them on every GET so we
    // intentionally don't round-trip them through the save payload.
    mcc_pricing_confidence:        jg.mcc_pricing_confidence || null,
    mcc_repair_route:              jg.mcc_repair_route || null,
    mcc_tester_estimated_hours:    typeof jg.mcc_tester_estimated_hours === 'number' ? jg.mcc_tester_estimated_hours : null,
    mcc_category:                  jg.mcc_category || null,
    mcc_completion_status:         jg.mcc_completion_status || null,
    // Full tester capture surface — fields, photos, free-text note,
    // severity + level. Read-only on FOH (backend re-resolves on
    // every GET). Rendered inside the JG body when expanded so FOH
    // sees exactly what the tester saw at the ramp.
    mcc_fields:                    jg.mcc_fields || null,
    mcc_photos:                    Array.isArray(jg.mcc_photos) ? jg.mcc_photos : [],
    mcc_technician_note:           jg.mcc_technician_note || "",
    mcc_severity:                  jg.mcc_severity || null,
    mcc_level:                     jg.mcc_level || null,
    mcc_entry_id:                  jg.mcc_entry_id || null,
    // SATE (Layer 2) workflow/process explanation. FE owns full
    // lifecycle (auto-suggest, edit, toggle outcomes, suppress); BE
    // persists verbatim.
    sate:                          jg.sate || null,
  });

  // Toggle a single customer note's enabled flag in the local jobGroups
  // state. Persisted on next save.
  const toggleCustomerNote = (jobGroupId, noteId) => {
    setJobGroups(prev => prev.map(jg => {
      if (jg.id !== jobGroupId) return jg;
      return {
        ...jg,
        customer_notes: (jg.customer_notes || []).map(n =>
          (n.id === noteId || n.key === noteId)
            ? { ...n, enabled: !(n.enabled !== false) }
            : n
        ),
      };
    }));
  };

  const focusOnJobGroup = (jobGroupId) => {
    // Collapse all and expand only the target
    const newExpanded = {};
    jobGroups.forEach(jg => {
      newExpanded[jg.id] = jg.id === jobGroupId;
    });
    setExpandedJobs(newExpanded);
    setSelectedJobForJCE(jobGroups.find(jg => jg.id === jobGroupId) || null);
    
    // Scroll to the TOP of the job group card
    setTimeout(() => {
      const element = jobGroupRefs.current[jobGroupId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Customer Intent Modal - Primary entry point for FOH (replaces AuthorisedValueGate) */}
      <CustomerIntentModal
        isOpen={showIntentModal}
        onConfirm={handleIntentConfirm}
        onClose={() => { setShowIntentModal(false); setIsChangingIntent(false); }}
        currentConfig={intentConfig}
        isChangeMode={isChangingIntent}
        isLocked={isIntentLocked}
        yestimateId={yestimateId}
      />
      
      {/* Legacy Authorised Value Gate Modal - Kept for backward compatibility */}
      <AuthorisedValueGate
        isOpen={showAuthorisedValueGate}
        onConfirm={handleAuthorisedValueConfirm}
        onClose={() => setShowAuthorisedValueGate(false)}
        currentValue={authorisedValueData.value}
        isChangeMode={isChangingAuthorisedValue}
      />
      
      {/* Global Dedupe Validation Modal - Pre-save check for duplicate operations */}
      <DedupeValidationModal
        isOpen={showDedupeModal}
        onClose={handleDedupeClose}
        validationResult={dedupeValidationResult}
        onResolve={handleDedupeResolve}
        onProceed={handleDedupeAcknowledgeAndProceed}
      />
      
      {/* NEW: Job-Centric Three-Column Workspace */}
      {useJobCentricWorkspace ? (
        <JobCentricWorkspace
          jobGroups={jobGroups}
          setJobGroups={setJobGroups}
          vehicleData={{
            reg: data?.job?.reg,
            make: data?.job?.make,
            model: data?.job?.model,
            year: data?.job?.year,
            mileage: data?.yestimate?.mileage || data?.job?.mileage,
            image_url: data?.job?.image_url
          }}
          inspectionData={{
            status: data?.inspection?.status || 'Complete',
            date: data?.inspection?.completed_at || data?.inspection?.created_at
          }}
          suggestions={Object.values(suggestions).flat()}
          onSave={handleSave}
          onSend={async () => {
            // MOT Orchestration: block full MOT-inclusive estimate while MOT pending
            const motBlocked = jobReadiness?.job_status?.mot_blocks_full_estimate;
            const scope = jobReadiness?.estimate_scope;
            if (motBlocked && (!scope || scope === 'full_mot_inclusive')) {
              setShowMotPendingModal(true);
              return;
            }

            // Phase 2: if MOT is imported but has unreviewed Dangerous/Major defects, warn
            const unreviewed = motDefects.counts?.unreviewed_failures || 0;
            if (jobReadiness?.mot_status?.status === 'mot_imported' && unreviewed > 0 && (scope === 'full_mot_inclusive' || scope === 'mot_failure_repair')) {
              setShowUnreviewedSendModal(true);
              return;
            }

            // Check send gate before navigating to preview
            const gateResult = await checkSendGate();
            if (gateResult.can_send) {
              // Record estimate send (non-blocking)
              if (data?.job?.id && scope) {
                try { await axios.post(`${API}/jobs/${data.job.id}/estimate-send`, { scope }); } catch (e) {}
              }
              navigate(`/app/foh/yestimate/${yestimateId}/preview`);
            } else {
              // Show send gate modal with blockers
              setSendGateData(gateResult);
              setShowSendGateModal(true);
            }
          }}
          isSaving={saving}
          isSending={false}
          smsReady={!!data?.job?.customer_phone}
          emailReady={!!data?.job?.customer_email}
          onAddJobGroup={() => { /* iter213d — setShowAddCustomItemModal not defined in this scope; no-op */ }}
          labourRate={getLabourRate("strip_renew", 65)}
        />
      ) : (
      <>
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // MOT-only yestimates have no inspection. Sending the FOH to
              // /app/foh/inspection/null 404s with "Inspection not found".
              // Bounce back to the FOH dashboard where the MOT job lives in
              // the "Action Required" column instead.
              if (data.yestimate.inspection_id) {
                navigate(`/app/foh/inspection/${data.yestimate.inspection_id}`);
              } else {
                navigate(`/app/foh`);
              }
            }}
            className="mb-2"
            data-testid="back-to-inspection-btn"
          >
            ← {data.yestimate.inspection_id ? 'Back to Inspection' : 'Back to FOH Dashboard'}
          </Button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-bold text-xl text-slate-800">Pricing & Scope Builder</h1>
              <p className="text-slate-500 inline-flex items-center gap-2 flex-wrap">
                <span>{data.job?.reg || 'N/A'} •</span>
                <VehicleMakeModelEditor
                  jobId={data.job?.id}
                  make={data.job?.make}
                  model={data.job?.model}
                  onSaved={({ make, model }) => {
                    // Iter198v — patch the in-memory snapshot so the
                    // header reflects the new value immediately
                    // without forcing a full reload.
                    setData((d) =>
                      d
                        ? { ...d, job: { ...d.job, make, model } }
                        : d,
                    );
                  }}
                />
                {data.inspection?.job_sheet_number && (
                  <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                    {data.inspection.job_sheet_number}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-Save Status Indicator */}
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                autoSaveStatus === 'saved' ? 'bg-green-100 text-green-700' :
                autoSaveStatus === 'saving' ? 'bg-blue-100 text-blue-700' :
                autoSaveStatus === 'unsaved' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                {autoSaveStatus === 'saved' && <><Check className="w-3 h-3" /> Saved</>}
                {autoSaveStatus === 'saving' && <><RefreshCw className="w-3 h-3 animate-spin" /> Saving...</>}
                {autoSaveStatus === 'unsaved' && <><Clock className="w-3 h-3" /> Unsaved</>}
                {autoSaveStatus === 'error' && <><AlertCircle className="w-3 h-3" /> Save failed</>}
              </div>
              
              {/* JCU Integrity Score Indicator - Advisory Only */}
              {jcuEvaluation && (
                <button
                  onClick={() => setShowJCUDetails(true)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer border ${
                    jcuEvaluation.level === 'green' 
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' 
                      : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                  }`}
                  title={jcuEvaluation.message || 'Click for suggestions'}
                >
                  <Shield className={`w-4 h-4 ${jcuLoading ? 'animate-spin' : ''}`} />
                  <span>{Math.round(jcuEvaluation.score)}%</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                    jcuEvaluation.level === 'green' ? 'bg-emerald-200 text-emerald-800' :
                    'bg-amber-200 text-amber-800'
                  }`}>
                    {jcuEvaluation.level === 'green' ? 'COMPLETE' : 'REVIEW SUGGESTED'}
                  </span>
                </button>
              )}

              {/* View MCC — opens a drawer with all tester evidence
                  (MCC entries + photos + notes) for jobs that have
                  had an MOT result captured. Hidden when there is no
                  MCC content to show. */}
              {hasMCCEvidence && (
                <button
                  type="button"
                  onClick={() => setShowMCCViewer(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 transition-all"
                  title="View tester evidence captured during the MOT (MCC)"
                  data-testid="view-mcc-btn"
                >
                  <Stethoscope className="w-4 h-4" />
                  <span>View MCC</span>
                </button>
              )}
              
              {/* Override Applied Banner */}
              {jcuOverrideApplied && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 border border-orange-300 rounded-lg text-orange-800 text-sm font-medium">
                  <AlertTriangle className="w-4 h-4" />
                  Override Applied
                </div>
              )}
              
              <Badge variant="outline">Version {data.yestimate.version}</Badge>
              <Button
                variant="outline"
                size="sm"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                onClick={() => setShowTimingServiceModal(true)}
                data-testid="add-timing-service-btn"
              >
                <Wrench className="w-4 h-4 mr-1.5" />
                + Add Timing Service
              </Button>
            </div>
          </div>
          
          {/* JCU Advisory Summary - Compact (Top 3) */}
          {jcuEvaluation && jcuEvaluation.level !== 'green' && jcuEvaluation.deduction_summary && jcuEvaluation.deduction_summary.length > 0 && (
            <div className="mt-2 p-2 rounded-lg border text-sm bg-amber-50 border-amber-200">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                <div className="flex-1">
                  <span className="font-medium">Suggested Items (Advisory):</span>
                  <ul className="mt-1 space-y-0.5">
                    {jcuEvaluation.deduction_summary.slice(0, 3).map((d, i) => (
                      <li key={i} className="text-xs">• {typeof d === 'object' ? d.text : d}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {/* iter197o — Battery gateway one-click quote banner */}
          {showBatteryQuoteCta && (
            <div
              className="mt-2 p-3 rounded-lg border-2 bg-amber-50 border-amber-400 flex items-start gap-3"
              data-testid="battery-quote-cta-banner"
            >
              <Battery className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-amber-900 text-sm">
                  Battery test: {(batteryOutcome.interpretation_label || batteryOutcome.interpretation || "").toUpperCase()}
                  {batteryOutcome.reading && (
                    <span className="ml-2 text-xs font-mono bg-amber-100 px-1.5 py-0.5 rounded">
                      {batteryOutcome.reading}
                    </span>
                  )}
                </div>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  Diagnostic gateway flagged the battery as the limiting factor on further reliable testing. One-click adds a pre-priced Battery Replacement job group (labour + part placeholder
                  {data?.job?.make && ["BMW","MINI","Volkswagen","Audi","Skoda","Seat","Porsche","Mercedes","Mercedes-Benz","Volvo","Land Rover","Jaguar","Range Rover"].some(m => m.toLowerCase() === (data.job.make || "").toLowerCase()) ? " + battery coding for this make" : ""}
                  ) — FOH to confirm exact part &amp; sell-out price before sending.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  disabled={batteryQuoting}
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={handleQuoteBatteryReplacement}
                  data-testid="battery-quote-cta-button"
                >
                  {batteryQuoting ? "Adding…" : "Quote battery replacement"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-amber-800"
                  onClick={() => setBatteryQuoteDismissed(true)}
                  data-testid="battery-quote-cta-dismiss"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* iter197l — Timing service auto-suggest banner */}
          {timingSuggestion?.suggested && !timingSuggestionDismissed && (
            <div
              className="mt-2 p-3 rounded-lg border bg-indigo-50 border-indigo-300 flex items-start gap-3"
              data-testid="timing-service-suggest-banner"
            >
              <Wrench className="w-5 h-5 mt-0.5 shrink-0 text-indigo-600" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-indigo-900 text-sm">
                  Timing service due — open the Timing Service Library?
                </div>
                <ul className="mt-1 text-xs text-indigo-800 list-disc list-inside space-y-0.5">
                  {(timingSuggestion.reasons || []).map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
                {timingSuggestion.pack_preview && (
                  <div className="mt-1 text-[11px] text-indigo-700">
                    {timingSuggestion.pack_preview.name} ·{" "}
                    {timingSuggestion.pack_preview.n_mandatory} mandatory ops ·{" "}
                    {timingSuggestion.pack_preview.n_recommended} recommended ·{" "}
                    {timingSuggestion.pack_preview.n_risks} risks
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => setShowTimingServiceModal(true)}
                  data-testid="timing-suggest-open-btn"
                >
                  Open library
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-indigo-700"
                  onClick={() => setTimingSuggestionDismissed(true)}
                  data-testid="timing-suggest-dismiss-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Vehicle Details Panel */}
          {data.job && (data.job.colour || data.job.fuel_type || data.job.year || data.job.engine_capacity || data.job.tax_status || data.job.mot_status || data.job.transmission || data.job.timing_system || data.job.mileage) && (
            <div className="mt-3 bg-slate-50 rounded-lg p-3 border">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {data.job.year && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Year:</span>
                    <span className="font-medium text-slate-700">{data.job.year}</span>
                  </div>
                )}
                {/* Mileage — pulled from job (intake/VI) or backfilled from
                    the latest DVSA MOT outcome odometer reading server-side.
                    A small italic source tag tells FOH where the reading
                    came from when the job itself didn't carry one. */}
                {data.job.mileage && (
                  <div className="flex items-center gap-2" data-testid="foh-pricing-mileage">
                    <span className="text-slate-500">Mileage:</span>
                    <span className="font-medium text-slate-700">
                      {Number(data.job.mileage).toLocaleString()}
                      {' '}{data.job.mileage_source === 'mot_outcome_km' ? 'km' : 'mi'}
                    </span>
                    {data.job.mileage_source && data.job.mileage_source !== 'job' && (
                      <span
                        className="text-[10.5px] italic text-slate-400"
                        title="Mileage backfilled because the job had none on record"
                      >
                        ({data.job.mileage_source === 'vi' ? 'from VI'
                          : data.job.mileage_source?.startsWith('mot_outcome') ? 'from MOT result'
                          : data.job.mileage_source})
                      </span>
                    )}
                  </div>
                )}
                {data.job.colour && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Colour:</span>
                    <span className="font-medium text-slate-700">{data.job.colour}</span>
                  </div>
                )}
                {data.job.fuel_type && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Fuel:</span>
                    <span className="font-medium text-slate-700">{data.job.fuel_type}</span>
                  </div>
                )}
                {data.job.transmission && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Transmission:</span>
                    <span className="font-medium text-slate-700">{data.job.transmission}</span>
                  </div>
                )}
                {data.job.timing_system && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Timing:</span>
                    <span className="font-medium text-slate-700">
                      {data.job.timing_system === 'chain' ? 'Timing Chain' :
                       data.job.timing_system === 'belt' ? 'Timing Belt' :
                       data.job.timing_system === 'wet_belt' ? 'Wet Belt' :
                       data.job.timing_system === 'unknown' ? 'Unknown' :
                       data.job.timing_system}
                    </span>
                  </div>
                )}
                {data.job.engine_capacity && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Engine:</span>
                    <span className="font-medium text-slate-700">{(data.job.engine_capacity / 1000).toFixed(1)}L ({data.job.engine_capacity}cc)</span>
                  </div>
                )}
                {data.job.tax_status && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Tax:</span>
                    <span className={`font-medium ${data.job.tax_status === 'Taxed' ? 'text-green-600' : 'text-red-600'}`}>
                      {data.job.tax_status}
                    </span>
                  </div>
                )}
                {data.job.mot_status && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">MOT:</span>
                    <span className={`font-medium ${data.job.mot_status === 'Valid' ? 'text-green-600' : 'text-red-600'}`}>
                      {data.job.mot_status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* THREE-COLUMN LAYOUT CONTAINER */}
      <div className="flex h-[calc(100vh-180px)]">
        
        {/* iter198pp — FOH MOT-row photo annotator. Renders top-level
            (modal) so the canvas overlays the entire page. Save POSTs
            the annotated JPEG back to the MCC photo endpoint as a new
            entry tagged "FOH annotated" then refreshes the yestimate
            so the new photo shows up in the row's expanded panel. */}
        {annotatingMotPhoto && (
          <PhotoAnnotator
            photo={annotatingMotPhoto.photoUrl}
            onCancel={() => setAnnotatingMotPhoto(null)}
            onSave={async (annotatedUrl, meta) => {
              const { outcomeId, entryId } = annotatingMotPhoto;
              setAnnotatingMotPhoto(null);
              if (!outcomeId || !entryId || !annotatedUrl) return;
              // Only upload if the FOH actually added something — a 0-
              // annotation save is treated as a Cancel to avoid
              // duplicating the original photo into the gallery.
              if (!meta?.annotations_count) return;
              try {
                await axios.post(
                  `${API}/mcc/outcomes/${outcomeId}/entries/${entryId}/photo`,
                  { url: annotatedUrl, label: "FOH annotated" },
                );
                // Refresh the yestimate so jg.mcc_photos picks up the
                // newly-appended annotation. Best-effort — the original
                // photo is still safe on the MCC entry regardless.
                if (yestimateId) {
                  try {
                    const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                    setJobGroups(sanitizeJobGroups(r.data.yestimate?.job_groups || r.data.job_groups || []));
                  } catch (refreshErr) {
                    // Refresh failure is non-fatal; FOH can hit reload.
                  }
                }
                toast.success("Annotation saved to tester evidence");
              } catch (err) {
                toast.error("Could not save annotation: " + (err?.response?.data?.detail || err?.message || "unknown error"));
              }
            }}
          />
        )}

        {/* iter211 — Photo annotation gap fix: FOH VI-row annotator.
            Mirrors the MOT photo annotator but POSTs to the
            inspection-items annotated-photo endpoint and refreshes the
            VI inspection items list so the new photo appears below the
            original. */}
        {annotatingViPhoto && (
          <PhotoAnnotator
            photo={annotatingViPhoto.photoUrl}
            onCancel={() => setAnnotatingViPhoto(null)}
            onSave={async (annotatedUrl, meta) => {
              const { inspectionId, itemId } = annotatingViPhoto;
              setAnnotatingViPhoto(null);
              if (!inspectionId || !itemId || !annotatedUrl) return;
              if (!meta?.annotations_count) return;
              try {
                await axios.post(
                  `${API}/inspections/${inspectionId}/items/${itemId}/annotated-photo`,
                  { url: annotatedUrl, label: "FOH annotated" },
                );
                // Refresh the yestimate so the inspection items list
                // picks up the new photo immediately under the same VI
                // row's Tech Evidence panel. Best-effort.
                try {
                  const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                  setData(r.data);
                } catch (_) {
                  // ignore — FOH can hit reload.
                }
                toast.success("Annotation saved to VI evidence");
              } catch (err) {
                toast.error("Could not save VI annotation: " + (err?.response?.data?.detail || err?.message || "unknown error"));
              }
            }}
          />
        )}

        {/* MCC Viewer Drawer — opens from the "View MCC" button at the
            top of the Pricing screen. Shows every MCC entry the tester
            captured (severity, status, defect text, rapid-capture
            fields, technician note, recommendation, photos). */}
        <MCCViewerModal
          open={showMCCViewer}
          onClose={() => setShowMCCViewer(false)}
          jobId={jobId}
          jobReg={data?.job?.reg}
        />

        {/* LEFT COLUMN - Inspection Findings (reused existing component) */}
        <div className="w-[280px] min-w-[260px] flex-shrink-0 border-r border-slate-200 bg-slate-50 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Inspection Findings</h3>
            
            {/* MOT DEFECTS — auto-imported DVSA defects, shown to FOH on
                MOT-only jobs. Same click-to-focus behaviour as VI items.
                Each row maps 1:1 to a job_group created by the auto-import
                pipeline; severity drives the colour:
                  • dangerous → BLACK (matches VI dangerous)
                  • major     → RED   (matches VI red)
                  • minor + advisory → AMBER (matches VI amber)
                User-requested coalescing of minor + advisory into one bucket
                — they're priced the same way operationally. */}
            {(() => {
              const motJgs = (jobGroups || []).filter(jg => (jg.source || '').includes('dvsa_mot_result'));
              if (motJgs.length === 0) return null;
              const sevOf = (jg) => (jg.source_mot_severity || '').toLowerCase();
              const dangerousJgs = motJgs.filter(jg => sevOf(jg) === 'dangerous');
              const majorJgs     = motJgs.filter(jg => sevOf(jg) === 'major');
              const amberJgs     = motJgs.filter(jg => ['minor','advisory','pass_advisory','pass advisory'].includes(sevOf(jg)));
              // Defects with no severity (legacy) — render in a fallback bucket.
              const unclassifiedJgs = motJgs.filter(jg =>
                !['dangerous','major','minor','advisory','pass_advisory','pass advisory'].includes(sevOf(jg))
              );
              const isPriced = (jg) => {
                // FOH must explicitly tick "pricing complete" on the JG
                // before it turns green. Auto-quoted lines do NOT count —
                // they're suggestions that still need human review.
                //
                // iter204f — Removed the auto-pass for emissions JGs in
                // BLOCKED_TEST / UNKNOWN_CAUSE state. The previous logic
                // turned an MOT FAIL row green just because the routing
                // engine couldn't determine the part. That misled FOH
                // into thinking pricing was complete when investigation
                // was still pending. FOH must explicitly confirm the
                // row (even if zero-cost) so the audit trail is clean.
                return jg.pricing_confirmed === true;
              };
              const renderRow = (jg, tone) => {
                const priced = isPriced(jg);
                // iter191c — for grouped tyre items, surface the tyre
                // size(s) captured by the tester so FOH sees the spec
                // in three places (left panel, pricing card, customer
                // portal) — same number everywhere, no chance to mix
                // it up.
                const tyreSizes = (() => {
                  if (jg.subtype !== "tyre_replacement_group") return [];
                  const sizes = new Set();
                  for (const t of (jg.tyres || [])) {
                    if (t?.tyre_size) sizes.add(t.tyre_size);
                  }
                  return Array.from(sizes);
                })();
                // Tailwind colour map — keep parallel with the VI panel:
                //   dangerous (black) → bg-black + white text
                //   major (red)       → bg-red-100 + red-300 border
                //   amber             → bg-amber-100 + amber-300 border
                const toneClasses = priced
                  ? "bg-green-100 border-2 border-green-400"
                  : tone === 'dangerous'
                    ? "bg-black hover:bg-slate-900 border-2 border-black text-white"
                    : tone === 'major'
                      ? "bg-red-100 hover:bg-red-200 border-2 border-red-300"
                      : "bg-amber-100 hover:bg-amber-200 border-2 border-amber-300";
                const dotClasses = priced
                  ? "bg-green-500"
                  : tone === 'dangerous' ? "bg-white"
                  : tone === 'major'     ? "bg-red-500"
                  : "bg-amber-500";
                // Strip "MOT FAIL: / MOT MINOR: / MOT ADVISORY:" prefix from the
                // panel title so the row stays scannable. The pill at the end
                // carries the severity badge instead.
                const cleanTitle = (jg.title || '').replace(/^MOT\s+(FAIL|MINOR|ADVISORY)\s*:\s*/i, '');
                // iter198pp — Surface tester evidence (MCC) on the FOH
                // pricing left panel. Matches the VI item pattern at
                // line ~19517 so FOH has a single mental model: every
                // row → click chevron to see what the tester saw.
                const hasMccEvidence = (
                  (jg.mcc_photos?.length || 0) > 0
                  || (jg.mcc_technician_note || '').trim()
                  || jg.mcc_severity
                  || (jg.mcc_fields && Object.keys(jg.mcc_fields).length > 0)
                );
                const motExpanded = !!expandedJobs[`mot_${jg.id}`];
                return (
                  <div key={jg.id} className="mb-2">
                    {/* iter204i — Restructured the row layout. Previously
                        the dot + MOT badge + DANGEROUS pill + title +
                        Price badge all fought for ~250px of horizontal
                        space, leaving the title with ~60px before it
                        wrapped awkwardly mid-word and the badges
                        clipped. New layout:
                          Row 1 (header):  dot · MOT · DANGEROUS · Price
                          Row 2 (title):   full-width defect title
                          Row 3 (chip):    Tester evidence (when present)
                        Title now has the full 248px content width to
                        breathe and the badges sit cleanly above. */}
                    <div
                      className={`text-sm py-1.5 px-2 rounded-lg cursor-pointer transition-all group ${toneClasses}`}
                      onClick={() => focusOnJobGroup(jg.id)}
                      data-testid={`left-panel-mot-${jg.id}`}
                    >
                      {/* Row 1 — Header strip: badges + price */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${dotClasses}`} />
                        <LeftPanelSourceBadge source="MOT" dataTestid={`left-panel-source-mot-${jg.id}`} />
                        {tone === 'dangerous' && (
                          <span
                            className="text-[9px] bg-white text-black px-1.5 py-0.5 rounded font-bold tracking-wider shrink-0"
                            data-testid={`left-panel-mot-dangerous-pill-${jg.id}`}
                          >
                            DANGEROUS
                          </span>
                        )}
                        <span className="flex-1" />
                        {priced ? (
                          <Check className={`w-3.5 h-3.5 shrink-0 ${tone === 'dangerous' ? 'text-white' : 'text-green-600'}`} />
                        ) : (
                          <Badge className={`text-[10px] shrink-0 ${
                            tone === 'dangerous' ? 'bg-white text-black' :
                            tone === 'major'     ? 'bg-red-600 text-white animate-pulse' :
                            'bg-amber-500 text-white'
                          }`}>Price</Badge>
                        )}
                      </div>
                      {/* Row 2 — Title (full width, breathable) */}
                      <p className={`mt-1 text-xs leading-snug break-words ${
                        priced ? "text-green-800" :
                        tone === 'dangerous' ? "text-white font-medium" : "text-slate-800"
                      }`}>
                        {cleanTitle || jg.title}
                        {tyreSizes.length > 0 && (
                          <span
                            className="inline-flex items-center gap-1 ml-1.5 px-1 py-0 rounded border border-slate-300 bg-white/90 text-[9.5px] font-mono text-slate-700 align-middle"
                            title="Tyre size — captured by the tester at MCC / VI"
                            data-testid={`left-panel-tyre-size-${jg.id}`}
                          >
                            <Lock className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                            {tyreSizes.length === 1 ? tyreSizes[0] : tyreSizes.join(" + ")}
                          </span>
                        )}
                      </p>
                      {jg.source_mot_text && (
                        <p className={`mt-0.5 text-[10px] leading-tight ${
                          tone === 'dangerous' ? "text-white/75" : "text-slate-500"
                        }`} title={jg.source_mot_text}>
                          {jg.source_mot_text.length > 80
                            ? jg.source_mot_text.slice(0, 80) + '…'
                            : jg.source_mot_text}
                        </p>
                      )}
                      {/* Row 3 — Tester evidence chip */}
                      {hasMccEvidence && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedJobs((prev) => ({
                              ...prev, [`mot_${jg.id}`]: !prev[`mot_${jg.id}`],
                            }));
                          }}
                          className={`inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide transition-all hover:scale-105 active:scale-100 focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                            tone === 'dangerous'
                              ? 'bg-white/20 text-white hover:bg-white/30'
                              : motExpanded
                                ? 'bg-teal-600 text-white border border-teal-600'
                                : 'bg-teal-100 text-teal-800 border border-teal-300 hover:bg-teal-200'
                          }`}
                          title="Click to view tester evidence (photos + MCC capture)"
                          data-testid={`left-panel-mot-evidence-chip-${jg.id}`}
                          aria-expanded={motExpanded}
                        >
                          <Camera className="w-2.5 h-2.5" />
                          Tester evidence
                          {(jg.mcc_photos?.length || 0) > 0 && (
                            <span className="opacity-80">· {jg.mcc_photos.length} photo{jg.mcc_photos.length === 1 ? '' : 's'}</span>
                          )}
                          {motExpanded
                            ? <ChevronDown className="w-2.5 h-2.5 ml-0.5" />
                            : <ChevronRight className="w-2.5 h-2.5 ml-0.5" />}
                        </button>
                      )}
                    </div>
                    {/* ============== Expanded MCC Evidence ============== */}
                    {motExpanded && hasMccEvidence && (
                      <div
                        className="mt-1 ml-2 p-2 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-2"
                        data-testid={`left-panel-mot-details-${jg.id}`}
                      >
                        {/* Severity + level pills */}
                        {(jg.mcc_severity || jg.mcc_level) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {jg.mcc_severity && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-700">
                                Tester: {jg.mcc_severity}
                              </span>
                            )}
                            {jg.mcc_level && (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                jg.mcc_level >= 4 ? 'bg-red-600 text-white' :
                                jg.mcc_level === 3 ? 'bg-amber-500 text-white' :
                                'bg-slate-200 text-slate-700'
                              }`}>
                                Level {jg.mcc_level}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Technician note */}
                        {(jg.mcc_technician_note || '').trim() && (
                          <div>
                            <span className="font-semibold text-slate-600">Tester note:</span>
                            <p className="text-slate-700 mt-0.5 whitespace-pre-wrap">{jg.mcc_technician_note}</p>
                          </div>
                        )}
                        {/* Rapid-capture / structured fields */}
                        {jg.mcc_fields && Object.keys(jg.mcc_fields).length > 0 && (
                          <div>
                            <span className="font-semibold text-slate-600 block mb-1">Capture details:</span>
                            <div className="grid grid-cols-2 gap-1">
                              {Object.entries(jg.mcc_fields).map(([k, v]) => {
                                if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return null;
                                const display = Array.isArray(v) ? v.join(', ') : String(v);
                                return (
                                  <div key={k} className="flex items-baseline gap-1.5 bg-white border border-slate-200 rounded px-1.5 py-1">
                                    <span className="text-slate-500 text-[10px] uppercase tracking-wide">
                                      {k.replace(/_/g, ' ')}
                                    </span>
                                    <span className="text-slate-800 font-medium break-words">{display}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Photos with FOH annotation */}
                        {(jg.mcc_photos?.length || 0) > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-slate-600">
                                Tester photos ({jg.mcc_photos.length})
                              </span>
                              <span className="text-[10px] text-slate-400 italic">
                                Click to annotate
                              </span>
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                              {jg.mcc_photos.map((photo, idx) => {
                                const url = typeof photo === 'string' ? photo : (photo?.url || '');
                                if (!url) return null;
                                return (
                                  <button
                                    type="button"
                                    key={idx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAnnotatingMotPhoto({
                                        jgId:       jg.id,
                                        outcomeId:  jg.source_mot_outcome_id,
                                        entryId:    jg.mcc_entry_id,
                                        photoUrl:   url,
                                        photoLabel: typeof photo === 'object' ? (photo?.label || '') : '',
                                      });
                                    }}
                                    className="relative group/photo focus:outline-none focus:ring-2 focus:ring-teal-400 rounded"
                                    title="Click to annotate this photo"
                                    data-testid={`left-panel-mot-photo-${jg.id}-${idx}`}
                                  >
                                    <img
                                      src={url}
                                      alt={`Tester evidence ${idx + 1}`}
                                      className="w-14 h-14 object-cover rounded border border-slate-300 group-hover/photo:opacity-80 transition-opacity"
                                    />
                                    <span className="absolute bottom-0 right-0 text-[8px] font-bold uppercase tracking-wide bg-teal-600 text-white px-1 py-0.5 rounded-tl">
                                      Mark
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                            {!jg.source_mot_outcome_id || !jg.mcc_entry_id ? (
                              <p className="text-[10px] text-amber-700 mt-1.5 italic">
                                Note: this JG is missing the MCC outcome/entry link, so FOH annotations cannot be saved back to the tester capture. Photos are read-only here.
                              </p>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              };
              return (
                <div className="mb-4 pb-4 border-b border-slate-200" data-testid="left-panel-mot-section">
                  <p className="text-xs font-semibold text-teal-700 mb-2 uppercase tracking-wide">
                    MOT Defects ({motJgs.length})
                  </p>
                  {dangerousJgs.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-bold text-black mb-1.5 uppercase tracking-wider">
                        Dangerous ({dangerousJgs.length})
                      </p>
                      {dangerousJgs.map(jg => renderRow(jg, 'dangerous'))}
                    </div>
                  )}
                  {majorJgs.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-bold text-red-700 mb-1.5 uppercase tracking-wider">
                        Major Failures ({majorJgs.length})
                      </p>
                      {majorJgs.map(jg => renderRow(jg, 'major'))}
                    </div>
                  )}
                  {amberJgs.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-bold text-amber-700 mb-1.5 uppercase tracking-wider">
                        Minor & Advisory ({amberJgs.length})
                      </p>
                      {amberJgs.map(jg => renderRow(jg, 'amber'))}
                    </div>
                  )}
                  {unclassifiedJgs.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-bold text-slate-500 mb-1.5 uppercase tracking-wider">
                        Other ({unclassifiedJgs.length})
                      </p>
                      {unclassifiedJgs.map(jg => renderRow(jg, 'amber'))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* DIAGNOSTIC SECTION — surfaces second per the FOH order
                MOT → DIAG → VI → SERVICE. Only renders when this job
                has a diagnostic outcome (``diagnosticInfo`` present). */}
            {diagnosticInfo && (
              <div className="mb-4 pb-4 border-b border-slate-200" data-testid="left-panel-diag-section">
                <p className="text-xs font-semibold text-purple-700 mb-2 uppercase tracking-wide">DIAG ITEMS (1)</p>
                <div 
                  className={`flex items-center gap-2 text-sm py-2 px-3 rounded-lg cursor-pointer transition-all ${
                    isDiagnosticPricingComplete 
                      ? "bg-green-50 border-2 border-green-500 shadow-sm" 
                      : "bg-amber-50 hover:bg-amber-100 border-2 border-amber-400 hover:border-amber-500 shadow-sm"
                  }`}
                  onClick={() => {
                    if (!hasDiagnosticJobGroup) {
                      createDiagnosticJobGroup();
                    }
                  }}
                  data-testid="diagnostic-pricing-item-left"
                >
                  <div className={`w-5 h-5 rounded shrink-0 flex items-center justify-center border-2 ${
                    isDiagnosticPricingComplete 
                      ? "bg-green-500 border-green-600" 
                      : "bg-amber-400 border-amber-500"
                  }`}>
                    {isDiagnosticPricingComplete && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" title="Mandatory" />
                  <LeftPanelSourceBadge source="DIAG" dataTestid="left-panel-source-diag-rectification" />
                  <span className={`flex-1 font-medium text-xs ${isDiagnosticPricingComplete ? "text-green-800" : "text-slate-800"}`}>
                    Diagnostic Rectification
                  </span>
                  {isDiagnosticPricingComplete ? (
                    <Badge className="bg-green-500 text-white text-[10px] pointer-events-none">Done</Badge>
                  ) : (
                    <Badge className="bg-amber-500 text-white text-[10px] animate-pulse pointer-events-none">Price</Badge>
                  )}
                </div>
              </div>
            )}

            {/* ADDED RECOMMENDATIONS — moved BELOW Service Items per
                FOH order rule: MOT → DIAG → VI → SERVICE → (extras).
                See ``left-panel-added-recommendations`` further down. */}

            {/* DIAGNOSTIC SECTION — moved UP, near top (after MOT)
                per the FOH ordering rule MOT → DIAG → VI → SERVICE.
                See ``left-panel-diag-section`` above. */}
            
            {/* VI ITEMS — wraps Requires Attention + Advisory under
                a single parent header so FOH can see at a glance how
                many vehicle-inspection findings there are vs MOT/Diag/
                Service. Items already represented as MOT JGs are
                deduped upstream via ``motCoveredViKeys``. */}
            {(() => {
              const visibleRed = redItemsDeduped.filter(item => !ignoredItems.some(ig => ig.id === item.id));
              const viTotal = visibleRed.length + amberItems.length;
              if (viTotal === 0) return null;
              return (
                <div className="mb-2" data-testid="left-panel-vi-section">
                  <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide border-b border-slate-300 pb-1">
                    VI Items ({viTotal})
                  </p>
                </div>
              );
            })()}

            {/* RED ITEMS */}
            {redItemsDeduped.filter(item => !ignoredItems.some(ig => ig.id === item.id)).length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-red-600 mb-2">REQUIRES ATTENTION ({redItemsDeduped.filter(i => !ignoredItems.some(ig => ig.id === i.id)).length})</p>
                {redItemsDeduped
                  .filter(item => !ignoredItems.some(ig => ig.id === item.id))
                  .sort((a, b) => {
                    const getPriority = (item) => {
                      const isDiag = item.item_key?.includes('warning') || item.item_key?.includes('dash_warning');
                      if (isDiag) return 0;
                      if (item.status === 'dangerous') return 1;
                      return 2;
                    };
                    return getPriority(a) - getPriority(b);
                  })
                  .map(item => {
                    const isDangerousItem = item.status === 'dangerous';
                    const isNotQuoted = notQuotedItemIds.has(item.id);
                    return (
                      <div key={item.id} className="mb-2">
                        {/* iter204l — Same restructure as iter204i on
                            the MOT rows: badges + status pill stack
                            into a header strip on Row 1, title gets
                            the full panel width on Row 2 so it can
                            breathe instead of wrapping one syllable
                            per line behind the "In Progress" pill. */}
                        <div
                          className={`text-sm py-1.5 px-2 rounded-lg cursor-pointer transition-all group ${
                            isNotQuoted
                              ? "bg-slate-100 border-2 border-slate-300 opacity-60 hover:opacity-80"
                              : pricedItemIds.has(item.id)
                                ? "bg-green-100 border-2 border-green-400"
                                : isDangerousItem
                                  ? "bg-black hover:bg-slate-900 border-2 border-black"
                                  : "bg-red-100 hover:bg-red-200 border-2 border-red-300"
                          }`}
                          onClick={() => {
                            if (!itemsWithJobGroups.has(item.id)) {
                              createJobGroup(item);
                            } else {
                              const existingJob = jobGroups.find(jg => jg.inspection_item_id === item.id);
                              if (existingJob) {
                                focusOnJobGroup(existingJob.id);
                              }
                            }
                          }}
                          data-testid={`left-panel-item-${item.id}`}
                        >
                          {/* Row 1 — Header strip: dot + badges + status pill */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${isNotQuoted ? "bg-slate-400" : isDangerousItem ? "bg-white" : "bg-red-500"}`} />
                            <LeftPanelSourceBadge
                              source={classifyInspectionItemSource(item)}
                              dataTestid={`left-panel-source-red-${item.id}`}
                            />
                            {isDangerousItem && !isNotQuoted && (
                              <span className="text-[9px] bg-white text-black px-1 py-0.5 rounded font-bold shrink-0">⚠️</span>
                            )}
                            {isNotQuoted && (
                              <span
                                className="text-[8px] bg-slate-600 text-white px-1.5 py-0.5 rounded font-bold shrink-0 uppercase tracking-wider"
                                title="This item has been marked as Not Quoting in the customer quote"
                                data-testid={`left-panel-not-quoting-chip-${item.id}`}
                              >
                                Not Quoting
                              </span>
                            )}
                            {isPairedItem(item) && !isNotQuoted && !pricedItemIds.has(item.id) && !itemsWithJobGroups.has(item.id) && (
                              <span
                                className="text-[8px] bg-amber-500 text-white px-1 py-0.5 rounded font-medium shrink-0"
                                title="This item is commonly replaced in pairs - click to add pricing"
                              >
                                PAIRS
                              </span>
                            )}
                            {(item.wear_sensor_required || (item.consolidated && item.original_items?.some(oi => oi.wear_sensor_required))) && (
                              <span
                                className="text-[8px] bg-blue-600 text-white px-1 py-0.5 rounded font-medium shrink-0"
                                title="Wear sensor required - marked by technician"
                                data-testid={`wear-sensor-badge-${item.id}`}
                              >
                                SENSOR
                              </span>
                            )}
                            <span className="flex-1" />
                            {pricedItemIds.has(item.id) ? (
                              <Check className={`w-3.5 h-3.5 shrink-0 ${isDangerousItem ? 'text-white' : 'text-green-600'}`} />
                            ) : inProgressItemIds.has(item.id) ? (
                              <Badge className="bg-blue-500 text-white text-[10px] shrink-0">In Progress</Badge>
                            ) : (
                              <Badge className={`text-[10px] shrink-0 ${isDangerousItem ? "bg-white text-black" : "bg-red-500 text-white"}`}>Price</Badge>
                            )}
                            {!pricedItemIds.has(item.id) && !itemsWithJobGroups.has(item.id) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleIgnoreItem('inspection', item.id, item.item_label || item.label || item.item_key?.replace(/_/g, ' '));
                                }}
                                className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ${
                                  isDangerousItem ? "hover:bg-white/20 text-white/60" : "hover:bg-slate-200 text-slate-400"
                                }`}
                                title="Skip — won't quote this item"
                                data-testid={`dismiss-item-${item.id}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          {/* Row 2 — Title (full width, breathable) */}
                          <p className={`mt-1 text-xs leading-snug break-words ${
                            pricedItemIds.has(item.id) ? "text-green-800" :
                            isDangerousItem ? "text-white font-medium" : "text-slate-800"
                          }`}>
                            {item.item_label || item.label || item.item_key?.replace(/_/g, ' ')}
                            {item.affected_side && item.affected_side !== 'both' && (
                              <span className={`ml-1.5 text-[9px] px-1 py-0.5 rounded ${
                                isDangerousItem ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
                              }`}>
                                {item.affected_side.toUpperCase()}
                              </span>
                            )}
                          </p>
                          {/* Row 3 — Notes (when present) */}
                          {item.notes && (
                            <p
                              className={`mt-0.5 text-[10px] leading-tight ${
                                isDangerousItem ? "text-white/75" : "text-slate-500"
                              }`}
                              title={item.notes}
                            >
                              {item.notes.length > 80 ? item.notes.slice(0, 80) + '…' : item.notes}
                            </p>
                          )}
                          {/* Row 4 — Tech evidence chip */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedJobs(prev => ({...prev, [`item_${item.id}`]: !prev[`item_${item.id}`]}));
                            }}
                            className={`inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide transition-all hover:scale-105 active:scale-100 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                              isDangerousItem
                                ? 'bg-white/20 text-white hover:bg-white/30'
                                : expandedJobs[`item_${item.id}`]
                                  ? 'bg-blue-600 text-white border border-blue-600'
                                  : 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200'
                            }`}
                            title={`View tech notes${Array.isArray(item.photos) && item.photos.length > 0 ? ` & ${item.photos.length} photo${item.photos.length === 1 ? '' : 's'}` : ''}`}
                            data-testid={`left-panel-tech-evidence-chip-${item.id}`}
                            aria-expanded={!!expandedJobs[`item_${item.id}`]}
                          >
                            <ClipboardList className="w-2.5 h-2.5" />
                            Tech evidence
                            {Array.isArray(item.photos) && item.photos.length > 0 && (
                              <span className="opacity-80">· {item.photos.length} photo{item.photos.length === 1 ? '' : 's'}</span>
                            )}
                            {expandedJobs[`item_${item.id}`]
                              ? <ChevronDown className="w-2.5 h-2.5 ml-0.5" />
                              : <ChevronRight className="w-2.5 h-2.5 ml-0.5" />}
                          </button>
                        </div>
                        {/* Expanded Tech Details */}
                        {expandedJobs[`item_${item.id}`] && (
                          <div className="mt-1 ml-2 p-2 bg-slate-50 rounded-lg border text-xs space-y-2">
                            {item.notes && (
                              <div>
                                <span className="font-medium text-slate-600">Tech Notes:</span>
                                <p className="text-slate-700 mt-0.5">{item.notes}</p>
                              </div>
                            )}
                            {item.measurement_value && (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">Measurement:</span>
                                <span className="text-red-700 font-medium">{item.measurement_value} {item.measurement_unit || 'mm'}</span>
                              </div>
                            )}
                            {/* iter204g — Battery test rich evidence.
                                The generic Measurement row above only
                                catches items with a single ``measurement_value``
                                — battery captures voltage + health % +
                                CCA in dedicated fields the FOH needs
                                to see at-a-glance to justify the quote. */}
                            {item.item_key === 'battery_test' && (
                              <div className="bg-red-50 border border-red-200 rounded px-2 py-1.5 space-y-1">
                                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-red-800">
                                  Battery test results
                                </div>
                                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                  {item.battery_voltage_v != null && (
                                    <>
                                      <span className="text-slate-600">Voltage:</span>
                                      <span className="font-bold text-red-700">{item.battery_voltage_v}V</span>
                                    </>
                                  )}
                                  {item.health_percent != null && (
                                    <>
                                      <span className="text-slate-600">Health:</span>
                                      <span className={`font-bold ${
                                        item.health_percent >= 75 ? 'text-emerald-700' :
                                        item.health_percent >= 55 ? 'text-amber-700' : 'text-red-700'
                                      }`}>{item.health_percent}%</span>
                                    </>
                                  )}
                                  {(item.measured_cca_a != null || item.rated_cca_a != null) && (
                                    <>
                                      <span className="text-slate-600">CCA:</span>
                                      <span className="font-bold text-red-700">
                                        {item.measured_cca_a ?? '—'}
                                        {item.rated_cca_a != null && ` / ${item.rated_cca_a}`} A
                                      </span>
                                    </>
                                  )}
                                  {item.battery_type_required && (
                                    <>
                                      <span className="text-slate-600">Type:</span>
                                      <span className="font-semibold uppercase tracking-wider text-slate-700">{item.battery_type_required}</span>
                                    </>
                                  )}
                                  {item.battery_registration_required && (
                                    <>
                                      <span className="text-slate-600">Reg. required:</span>
                                      <span className={`font-semibold ${
                                        item.battery_registration_required === 'yes' ? 'text-amber-700' : 'text-slate-700'
                                      }`}>{String(item.battery_registration_required).toUpperCase()}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* iter204g — Brake fluid test rich evidence. */}
                            {(item.item_key === 'brake_fluid_test'
                              || item.item_key === 'brake_fluid_boiling_point_test') && (
                              <div className="bg-red-50 border border-red-200 rounded px-2 py-1.5 space-y-1">
                                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-red-800">
                                  Brake fluid results
                                </div>
                                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                  {item.brake_fluid_boiling_point != null && (
                                    <>
                                      <span className="text-slate-600">Boiling pt:</span>
                                      <span className={`font-bold ${
                                        item.brake_fluid_boiling_point >= 200 ? 'text-emerald-700' :
                                        item.brake_fluid_boiling_point >= 180 ? 'text-amber-700' : 'text-red-700'
                                      }`}>{item.brake_fluid_boiling_point}°C</span>
                                    </>
                                  )}
                                  {item.brake_fluid_moisture != null && (
                                    <>
                                      <span className="text-slate-600">Moisture:</span>
                                      <span className={`font-bold ${
                                        item.brake_fluid_moisture <= 1.5 ? 'text-emerald-700' :
                                        item.brake_fluid_moisture <= 3 ? 'text-amber-700' : 'text-red-700'
                                      }`}>{item.brake_fluid_moisture}%</span>
                                    </>
                                  )}
                                  {item.brake_fluid_visual_condition && (
                                    <>
                                      <span className="text-slate-600">Visual:</span>
                                      <span className="font-semibold capitalize text-slate-700">{String(item.brake_fluid_visual_condition).replace(/_/g, ' ')}</span>
                                    </>
                                  )}
                                  {item.brake_fluid_service_history && item.brake_fluid_service_history !== 'unknown' && (
                                    <>
                                      <span className="text-slate-600">Last service:</span>
                                      <span className="font-semibold text-slate-700">{String(item.brake_fluid_service_history).replace(/_/g, ' ')}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                            {item.percent_worn !== null && item.percent_worn !== undefined && (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">Tyre Wear:</span>
                                <span className={`font-bold ${
                                  item.percent_worn >= 80 ? 'text-red-600' :
                                  item.percent_worn >= 60 ? 'text-amber-600' : 'text-green-600'
                                }`}>{item.percent_worn}% worn</span>
                              </div>
                            )}
                            {(item.tyre_size || item.tyre_brand) && (
                              <div className="flex items-center gap-2 bg-blue-50 px-2 py-1 rounded">
                                {item.tyre_size && <span className="text-blue-800 font-bold">{item.tyre_size}</span>}
                                {item.tyre_brand && <span className="text-blue-600">{item.tyre_brand}</span>}
                              </div>
                            )}
                            {item.disc_condition && (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">Disc:</span>
                                <Badge className="bg-red-100 text-red-700 text-xs">{item.disc_condition}</Badge>
                              </div>
                            )}
                            {item.photo_urls?.length > 0 && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-slate-600">
                                    Photos ({item.photo_urls.length}):
                                  </span>
                                  <span className="text-[10px] text-blue-600 italic">
                                    Click to annotate
                                  </span>
                                </div>
                                <div className="flex gap-1 flex-wrap">
                                  {item.photo_urls.map((url, idx) => (
                                    <button
                                      type="button"
                                      key={idx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAnnotatingViPhoto({
                                          itemId:       item.id,
                                          inspectionId: item.inspection_id,
                                          photoUrl:     url,
                                        });
                                      }}
                                      className="relative group/vi-photo focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                                      title="Click to annotate this photo"
                                      data-testid={`left-panel-vi-photo-red-${item.id}-${idx}`}
                                    >
                                      <img
                                        src={url}
                                        alt={`Evidence ${idx+1}`}
                                        className="w-10 h-10 object-cover rounded border group-hover/vi-photo:opacity-80 transition-opacity"
                                      />
                                      <span className="absolute bottom-0 right-0 text-[7px] font-bold uppercase tracking-wide bg-blue-600 text-white px-0.5 py-0.5 rounded-tl">
                                        Mark
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {!item.notes && !item.measurement_value && !item.photo_urls?.length
                                && item.battery_voltage_v == null && item.brake_fluid_boiling_point == null && (
                              <p className="text-slate-400 italic">No additional details recorded</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
            
            {/* AMBER ITEMS */}
            {amberItems.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-amber-600 mb-2">ADVISORY ({amberItems.length})</p>
                {amberItems.map(item => {
                  const isNotQuoted = notQuotedItemIds.has(item.id);
                  return (
                  <div key={item.id} className="mb-2">
                    <div 
                      className={`flex items-start gap-2 text-sm py-1.5 px-2 rounded-lg cursor-pointer transition-all group ${
                        isNotQuoted
                          ? "bg-slate-100 border-2 border-slate-300 opacity-60 hover:opacity-80"
                          : pricedItemIds.has(item.id)
                            ? "bg-green-100 border-2 border-green-400"
                            : "bg-amber-100 hover:bg-amber-200 border-2 border-amber-300"
                      }`}
                      onClick={() => {
                        if (!itemsWithJobGroups.has(item.id)) {
                          createJobGroup(item);
                        } else {
                          const existingJob = jobGroups.find(jg => jg.inspection_item_id === item.id);
                          if (existingJob) {
                            focusOnJobGroup(existingJob.id);
                          }
                        }
                      }}
                      data-testid={`left-panel-amber-${item.id}`}
                    >
                      <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${isNotQuoted ? "bg-slate-400" : "bg-amber-500"}`} />
                      <LeftPanelSourceBadge
                        source={classifyInspectionItemSource(item)}
                        dataTestid={`left-panel-source-amber-${item.id}`}
                      />
                      {/* iter211e — Not Quoting chip on left panel. */}
                      {isNotQuoted && (
                        <span
                          className="text-[8px] bg-slate-600 text-white px-1.5 py-0.5 rounded font-bold shrink-0 mt-0.5 uppercase tracking-wider"
                          title="This item has been marked as Not Quoting in the customer quote"
                          data-testid={`left-panel-not-quoting-amber-chip-${item.id}`}
                        >
                          Not Quoting
                        </span>
                      )}
                      {/* Paired item indicator - shows for springs, shocks, etc. */}
                      {isPairedItem(item) && !isNotQuoted && !pricedItemIds.has(item.id) && !itemsWithJobGroups.has(item.id) && (
                        <span 
                          className="text-[8px] bg-amber-600 text-white px-1 py-0.5 rounded font-medium shrink-0" 
                          title="This item is commonly replaced in pairs - click to add pricing"
                        >
                          PAIRS
                        </span>
                      )}
                      {/* Wear Sensor Required indicator */}
                      {(item.wear_sensor_required || (item.consolidated && item.original_items?.some(oi => oi.wear_sensor_required))) && (
                        <span 
                          className="text-[8px] bg-blue-600 text-white px-1 py-0.5 rounded font-medium shrink-0"
                          title="Wear sensor required - marked by technician"
                          data-testid={`wear-sensor-badge-amber-${item.id}`}
                        >
                          SENSOR
                        </span>
                      )}
                      {/* Mirror MOT card layout: label + optional notes
                          subtitle + Tech evidence chip all live INSIDE
                          the flex-1 min-w-0 break-words span. */}
                      <span className={`flex-1 min-w-0 text-xs break-words ${
                        pricedItemIds.has(item.id) ? "text-green-800" : "text-slate-800"
                      }`}>
                        {item.item_label || item.label || item.item_key?.replace(/_/g, ' ')}
                        {/* Show affected side badge */}
                        {item.affected_side && item.affected_side !== 'both' && (
                          <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-slate-200 text-slate-600">
                            {item.affected_side.toUpperCase()}
                          </span>
                        )}
                        {item.notes && (
                          <span
                            className="block text-[10px] mt-0.5 text-slate-500 truncate"
                            title={item.notes}
                          >
                            {item.notes.length > 60 ? item.notes.slice(0, 60) + '…' : item.notes}
                          </span>
                        )}
                        {/* iter211 — Tech Evidence chip mirrors the MOT
                            Tester Evidence chip (sibling inside the
                            title span, mt-1). */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedJobs(prev => ({...prev, [`item_${item.id}`]: !prev[`item_${item.id}`]}));
                          }}
                          className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide transition-all hover:scale-105 active:scale-100 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                            expandedJobs[`item_${item.id}`]
                              ? 'bg-blue-600 text-white border border-blue-600'
                              : 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200'
                          }`}
                          title={`View tech notes${Array.isArray(item.photos) && item.photos.length > 0 ? ` & ${item.photos.length} photo${item.photos.length === 1 ? '' : 's'}` : ''}`}
                          data-testid={`left-panel-tech-evidence-chip-amber-${item.id}`}
                          aria-expanded={!!expandedJobs[`item_${item.id}`]}
                        >
                          <ClipboardList className="w-2.5 h-2.5" />
                          Tech evidence
                          {Array.isArray(item.photos) && item.photos.length > 0 && (
                            <span className="opacity-80">· {item.photos.length} photo{item.photos.length === 1 ? '' : 's'}</span>
                          )}
                          {expandedJobs[`item_${item.id}`]
                            ? <ChevronDown className="w-2.5 h-2.5 ml-0.5" />
                            : <ChevronRight className="w-2.5 h-2.5 ml-0.5" />}
                        </button>
                      </span>
                      {pricedItemIds.has(item.id) ? (
                        <Check className="w-3 h-3 text-green-600 shrink-0 mt-1" />
                      ) : inProgressItemIds.has(item.id) ? (
                        <Badge className="bg-blue-500 text-white text-[10px] shrink-0 mt-0.5">In Progress</Badge>
                      ) : (
                        <Badge className="bg-amber-500 text-white text-[10px] shrink-0 mt-0.5">Price</Badge>
                      )}
                      {/* Quick Dismiss Button for amber items */}
                      {!pricedItemIds.has(item.id) && !itemsWithJobGroups.has(item.id) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIgnoreItem('inspection', item.id, item.item_label || item.label || item.item_key?.replace(/_/g, ' '));
                          }}
                          className="p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5 hover:bg-amber-200 text-slate-400"
                          title="Skip — won't quote this item"
                          data-testid={`dismiss-amber-${item.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {/* Expanded Tech Details */}
                    {expandedJobs[`item_${item.id}`] && (
                      <div className="mt-1 ml-2 p-2 bg-slate-50 rounded-lg border text-xs space-y-2">
                        {item.notes && (
                          <div>
                            <span className="font-medium text-slate-600">Tech Notes:</span>
                            <p className="text-slate-700 mt-0.5">{item.notes}</p>
                          </div>
                        )}
                        {item.measurement_value && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-600">Measurement:</span>
                            <span className="text-amber-700 font-medium">{item.measurement_value} {item.measurement_unit || 'mm'}</span>
                          </div>
                        )}
                        {/* iter204g — Battery test rich evidence (amber). */}
                        {item.item_key === 'battery_test' && (
                          <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1">
                            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-amber-800">
                              Battery test results
                            </div>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                              {item.battery_voltage_v != null && (
                                <>
                                  <span className="text-slate-600">Voltage:</span>
                                  <span className="font-bold text-amber-800">{item.battery_voltage_v}V</span>
                                </>
                              )}
                              {item.health_percent != null && (
                                <>
                                  <span className="text-slate-600">Health:</span>
                                  <span className={`font-bold ${
                                    item.health_percent >= 75 ? 'text-emerald-700' :
                                    item.health_percent >= 55 ? 'text-amber-700' : 'text-red-700'
                                  }`}>{item.health_percent}%</span>
                                </>
                              )}
                              {(item.measured_cca_a != null || item.rated_cca_a != null) && (
                                <>
                                  <span className="text-slate-600">CCA:</span>
                                  <span className="font-bold text-amber-800">
                                    {item.measured_cca_a ?? '—'}
                                    {item.rated_cca_a != null && ` / ${item.rated_cca_a}`} A
                                  </span>
                                </>
                              )}
                              {item.battery_type_required && (
                                <>
                                  <span className="text-slate-600">Type:</span>
                                  <span className="font-semibold uppercase tracking-wider text-slate-700">{item.battery_type_required}</span>
                                </>
                              )}
                              {item.battery_registration_required && (
                                <>
                                  <span className="text-slate-600">Reg. required:</span>
                                  <span className={`font-semibold ${
                                    item.battery_registration_required === 'yes' ? 'text-amber-700' : 'text-slate-700'
                                  }`}>{String(item.battery_registration_required).toUpperCase()}</span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        {/* iter204g — Brake fluid test rich evidence (amber). */}
                        {(item.item_key === 'brake_fluid_test'
                          || item.item_key === 'brake_fluid_boiling_point_test') && (
                          <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1">
                            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-amber-800">
                              Brake fluid results
                            </div>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                              {item.brake_fluid_boiling_point != null && (
                                <>
                                  <span className="text-slate-600">Boiling pt:</span>
                                  <span className={`font-bold ${
                                    item.brake_fluid_boiling_point >= 200 ? 'text-emerald-700' :
                                    item.brake_fluid_boiling_point >= 180 ? 'text-amber-700' : 'text-red-700'
                                  }`}>{item.brake_fluid_boiling_point}°C</span>
                                </>
                              )}
                              {item.brake_fluid_moisture != null && (
                                <>
                                  <span className="text-slate-600">Moisture:</span>
                                  <span className={`font-bold ${
                                    item.brake_fluid_moisture <= 1.5 ? 'text-emerald-700' :
                                    item.brake_fluid_moisture <= 3 ? 'text-amber-700' : 'text-red-700'
                                  }`}>{item.brake_fluid_moisture}%</span>
                                </>
                              )}
                              {item.brake_fluid_visual_condition && (
                                <>
                                  <span className="text-slate-600">Visual:</span>
                                  <span className="font-semibold capitalize text-slate-700">{String(item.brake_fluid_visual_condition).replace(/_/g, ' ')}</span>
                                </>
                              )}
                              {item.brake_fluid_service_history && item.brake_fluid_service_history !== 'unknown' && (
                                <>
                                  <span className="text-slate-600">Last service:</span>
                                  <span className="font-semibold text-slate-700">{String(item.brake_fluid_service_history).replace(/_/g, ' ')}</span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        {/* iter204m — Wiper & Washer System evidence (amber).
                            FOH needs to know which blade type the tech captured
                            so they can quote the right SKU (Conv vs Aero/flat
                            vs Hybrid). */}
                        {item.item_key === 'wiper_washer_system' && (item.wiper_data || item.wiper_blade_type) && (
                          <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1" data-testid={`left-panel-wiper-evidence-${item.id}`}>
                            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-amber-800">
                              Wiper blade detail
                            </div>
                            {item.wiper_blade_type && !item.wiper_data && (
                              <div className="flex items-center gap-2 text-[11px]">
                                <span className="text-slate-600">Blade type:</span>
                                <span className="font-bold text-amber-800">{
                                  item.wiper_blade_type === 'conventional'   ? 'Conventional / Frame' :
                                  item.wiper_blade_type === 'beam_aero'      ? 'Beam / Aero (flat)' :
                                  item.wiper_blade_type === 'hybrid'         ? 'Hybrid' :
                                  item.wiper_blade_type === 'rear_specific'  ? 'Rear-specific' :
                                  String(item.wiper_blade_type)
                                }</span>
                              </div>
                            )}
                            {item.wiper_data && [
                              { key: 'front_left',  label: 'Front L' },
                              { key: 'front_right', label: 'Front R' },
                              { key: 'rear',        label: 'Rear' },
                            ].map(comp => {
                              const sub = item.wiper_data[comp.key];
                              if (!sub) return null;
                              if (sub.not_fitted) return (
                                <div key={comp.key} className="flex items-center gap-1.5 text-[11px]">
                                  <span className="text-slate-600 w-12">{comp.label}:</span>
                                  <span className="px-1 py-0 rounded bg-slate-200 text-slate-700 text-[9.5px] font-bold">NOT FITTED</span>
                                </div>
                              );
                              if (!sub.status) return null;
                              const bladeLabel = sub.blade_type ? (
                                sub.blade_type === 'conventional'   ? 'Conventional' :
                                sub.blade_type === 'beam_aero'      ? 'Beam / Aero' :
                                sub.blade_type === 'hybrid'         ? 'Hybrid' :
                                sub.blade_type === 'rear_specific'  ? 'Rear-specific' : sub.blade_type
                              ) : null;
                              return (
                                <div key={comp.key} className="flex items-center gap-1.5 text-[11px] flex-wrap" data-testid={`left-panel-wiper-${comp.key}-${item.id}`}>
                                  <span className="text-slate-600 w-12">{comp.label}:</span>
                                  <span className={`px-1 py-0 rounded font-bold text-[9.5px] ${
                                    sub.status === 'red'   ? 'bg-red-500 text-white' :
                                    sub.status === 'amber' ? 'bg-amber-500 text-white' :
                                    'bg-emerald-500 text-white'
                                  }`}>{sub.status.toUpperCase()}</span>
                                  {bladeLabel ? (
                                    <span className="px-1 py-0 rounded bg-white border border-amber-300 text-amber-800 text-[9.5px] font-semibold">{bladeLabel}</span>
                                  ) : (
                                    <span className="text-[9.5px] italic text-slate-400">type not set</span>
                                  )}
                                  {(sub.issues || []).length > 0 && (
                                    <span className="text-slate-500 text-[10px] truncate">{sub.issues.slice(0, 2).join(', ')}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {item.percent_worn !== null && item.percent_worn !== undefined && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-600">Tyre Wear:</span>
                            <span className={`font-bold ${
                              item.percent_worn >= 80 ? 'text-red-600' :
                              item.percent_worn >= 60 ? 'text-amber-600' : 'text-green-600'
                            }`}>{item.percent_worn}% worn</span>
                          </div>
                        )}
                        {(item.tyre_size || item.tyre_brand) && (
                          <div className="flex items-center gap-2 bg-blue-50 px-2 py-1 rounded">
                            {item.tyre_size && <span className="text-blue-800 font-bold">{item.tyre_size}</span>}
                            {item.tyre_brand && <span className="text-blue-600">{item.tyre_brand}</span>}
                          </div>
                        )}
                        {item.disc_condition && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-600">Disc:</span>
                            <Badge className="bg-amber-100 text-amber-700 text-xs">{item.disc_condition}</Badge>
                          </div>
                        )}
                        {item.photo_urls?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-slate-600">
                                Photos ({item.photo_urls.length}):
                              </span>
                              <span className="text-[10px] text-blue-600 italic">
                                Click to annotate
                              </span>
                            </div>
                            <div className="flex gap-1 flex-wrap">
                              {item.photo_urls.map((url, idx) => (
                                <button
                                  type="button"
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAnnotatingViPhoto({
                                      itemId:       item.id,
                                      inspectionId: item.inspection_id,
                                      photoUrl:     url,
                                    });
                                  }}
                                  className="relative group/vi-photo focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                                  title="Click to annotate this photo"
                                  data-testid={`left-panel-vi-photo-amber-${item.id}-${idx}`}
                                >
                                  <img
                                    src={url}
                                    alt={`Evidence ${idx+1}`}
                                    className="w-10 h-10 object-cover rounded border group-hover/vi-photo:opacity-80 transition-opacity"
                                  />
                                  <span className="absolute bottom-0 right-0 text-[7px] font-bold uppercase tracking-wide bg-blue-600 text-white px-0.5 py-0.5 rounded-tl">
                                    Mark
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {!item.notes && !item.measurement_value && !item.photo_urls?.length
                            && item.battery_voltage_v == null && item.brake_fluid_boiling_point == null && (
                          <p className="text-slate-400 italic">No additional details recorded</p>
                        )}
                      </div>
                    )}
                  </div>
                );})}
              </div>
            )}
            
            {/* IGNORED ITEMS */}
            {ignoredItems.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-400 mb-2">IGNORED ({ignoredItems.length})</p>
                {ignoredItems.map(item => (
                  <div key={item.id} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded-lg bg-slate-100 mb-1.5">
                    <div className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
                    <span className="flex-1 text-xs text-slate-500 line-through">{item.item_label}</span>
                  </div>
                ))}
              </div>
            )}
            
            {/* SERVICE ITEMS SECTION - Shows added service interval items */}
            {(() => {
              // Get service interval items that have been added to pricing
              // Include items flagged as interval items AND items with service-related titles
              // EXCLUDE items that already appear in inspection findings (red/amber)
              
              // Get IDs of items already shown in inspection findings
              const inspectionItemIds = new Set([
                ...redItems.map(i => i.id),
                ...amberItems.map(i => i.id)
              ]);
              
              // Get VI item keys from inspection items to exclude matching job groups
              const inspectionViKeys = new Set([
                ...redItems.map(i => i.item_key?.toLowerCase()).filter(Boolean),
                ...amberItems.map(i => i.item_key?.toLowerCase()).filter(Boolean)
              ]);
              
              const serviceJobGroups = jobGroups.filter(jg => {
                // Skip if this job group is linked to an inspection item that's already shown
                if (jg.inspection_item_id && inspectionItemIds.has(jg.inspection_item_id)) {
                  return false;
                }
                
                // Skip if this job's vi_item_key matches an inspection item
                if (jg.vi_item_key && inspectionViKeys.has(jg.vi_item_key.toLowerCase())) {
                  return false;
                }
                
                // Skip brake items that would be duplicates (brake pads, discs, etc.)
                // ❗ This is a legacy fuzzy guard for items that lack a
                // ``vi_item_key`` — when the explicit-key matcher above
                // can't disambiguate. We exclude common brake-repair
                // titles (pads/discs/calipers) BUT keep "brake fluid",
                // "brake hose", "brake bleed" etc. because those are
                // distinct service-interval items even when the VI
                // panel shows pad/disc inspection rows.
                const titleLower = (jg.title || jg.job_name || '').toLowerCase();
                const isBrakeFluidOrHose =
                  titleLower.includes('brake fluid')
                  || titleLower.includes('brake hose')
                  || titleLower.includes('brake bleed')
                  || titleLower.includes('brake flush');
                const isInspectionBrakeItem = 
                  !isBrakeFluidOrHose &&
                  (titleLower.includes('brake') || titleLower.includes('pad') || titleLower.includes('disc')) &&
                  [...inspectionViKeys].some(key => 
                    key.includes('brake') || key.includes('pad') || key.includes('disc')
                  );
                if (isInspectionBrakeItem) {
                  return false;
                }
                
                // Explicit interval item flags
                if (jg.is_interval_item || jg.source === "interval") return true;
                
                // Check title for known service items (NOT inspection items)
                const isServiceItem = 
                  titleLower.includes('timing belt') ||
                  titleLower.includes('cambelt') ||
                  titleLower.includes('cam belt') ||
                  titleLower.includes('oil change') ||
                  titleLower.includes('coolant flush') ||
                  titleLower.includes('gearbox oil') ||
                  titleLower.includes('transmission fluid') ||
                  titleLower.includes('spark plug') ||
                  (titleLower.includes('air filter') && !titleLower.includes('cabin')) ||
                  titleLower.includes('fuel filter');
                  
                // Exclude "service" alone to avoid false positives with brake service, etc.
                // Only include actual service interval items
                return isServiceItem;
              });
              
              if (serviceJobGroups.length === 0) return null;
              
              return (
                <div className="mt-4 pt-4 border-t border-slate-200" data-testid="left-panel-service-builder-section">
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    SERVICE BUILDER ({serviceJobGroups.length})
                  </p>
                  {serviceJobGroups.map(serviceJob => {
                    // Calculate if pricing has been STARTED (any labour/parts entered)
                    const hasLabour = serviceJob.labour_lines?.length > 0 && 
                      serviceJob.labour_lines.some(l => l.hours > 0 && l.rate > 0);
                    const hasParts = serviceJob.parts_lines?.length > 0 && 
                      serviceJob.parts_lines.some(p => p.quantity > 0 && p.unit_price > 0);
                    // GREEN only when FOH has explicitly confirmed pricing — matches the
                    // VI item rule (`pricedItemIds` requires `pricing_confirmed === true`).
                    // Just having labour/parts lines is "In Progress", not done.
                    const isPricingComplete = serviceJob.pricing_confirmed === true;
                    const isPricingInProgress = !isPricingComplete && (hasLabour || hasParts);
                    
                    // Calculate total for display
                    const labourTotal = serviceJob.labour_lines?.reduce((sum, l) => sum + (l.hours * l.rate), 0) || 0;
                    const partsTotal = serviceJob.parts_lines?.reduce((sum, p) => sum + (p.quantity * p.unit_price), 0) || 0;
                    const chargesTotal = serviceJob.charges?.reduce((sum, c) => sum + c.amount, 0) || 0;
                    const totalExVat = labourTotal + partsTotal + chargesTotal;
                    
                    return (
                      <div key={serviceJob.id} className="mb-2">
                        <div 
                          className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded-lg cursor-pointer transition-all ${
                            isPricingComplete 
                              ? "bg-green-50 border border-green-300 hover:bg-green-100" 
                              : isPricingInProgress
                                ? "bg-blue-50 border border-blue-300 hover:bg-blue-100"
                                : "bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"
                          }`}
                          onClick={() => {
                            // Focus on this service job in the center panel
                            focusOnJobGroup(serviceJob.id);
                          }}
                          data-testid={`service-item-${serviceJob.id}`}
                          data-pricing-state={isPricingComplete ? "confirmed" : isPricingInProgress ? "in_progress" : "pending"}
                        >
                          {/* Status indicator */}
                          <div className={`w-4 h-4 rounded shrink-0 flex items-center justify-center ${
                            isPricingComplete 
                              ? "bg-green-500" 
                              : isPricingInProgress
                                ? "bg-blue-400 border border-blue-500"
                                : "bg-emerald-200 border border-emerald-400"
                          }`}>
                            {isPricingComplete && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          
                          {/* Service item name */}
                          <span className={`flex-1 text-xs font-medium ${
                            isPricingComplete
                              ? "text-green-800"
                              : isPricingInProgress
                                ? "text-blue-900"
                                : "text-slate-700"
                          }`}>
                            {serviceJob.title || serviceJob.job_name || 'Service Item'}
                          </span>
                          
                          {/* Price or status badge */}
                          {isPricingComplete ? (
                            <span className="text-[10px] font-bold text-green-700" data-testid={`service-item-total-${serviceJob.id}`}>
                              £{totalExVat.toFixed(0)}
                            </span>
                          ) : isPricingInProgress ? (
                            <Badge className="bg-blue-500 text-white text-[10px]" data-testid={`service-item-in-progress-${serviceJob.id}`}>
                              In Progress
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-500 text-white text-[10px]">Price</Badge>
                          )}
                        </div>
                        
                        {/* Show completion status breakdown when confirmed only —
                            in-progress state intentionally avoids the green ticks
                            so FOH knows pricing still needs sign-off. */}
                        {isPricingComplete && (
                          <div className="mt-0.5 ml-6 text-[10px] text-green-600 flex items-center gap-2">
                            {hasLabour && <span>✓ Labour</span>}
                            {hasParts && <span>✓ Parts</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ADDED RECOMMENDATIONS — iter189 (moved here Feb 2026
                to honour the FOH ordering rule MOT → DIAG → VI → SERVICE →
                Extras). Non-MOT JGs created by FOH (wheel alignment,
                axle pair confirmations, diagnostics-added items, etc.). */}
            {(() => {
              const addedJgs = (jobGroups || []).filter(jg => {
                const src = (jg.source || '').toLowerCase();
                if (jg.subtype === 'tyre_replacement_group') return false;
                if (src.includes('dvsa_mot_result')) return false;
                if (src === 'inspection' || src === 'vi') return false;
                if (jg.type === 'service_recommendation') return true;
                if (jg.type === 'grouped_repair') return true;
                if (jg.subtype === 'wheel_alignment') return true;
                if ((jg.vi_item_key || '').toLowerCase() === 'wheel_alignment') return true;
                if (src === 'foh_manual' || src === 'mcc_auto_alignment') return true;
                return false;
              });
              if (addedJgs.length === 0) return null;
              const isPriced = (jg) => jg.pricing_confirmed === true
                || jg.total_inc_vat > 0;
              const renderRecRow = (jg) => {
                const priced = isPriced(jg);
                const toneClasses = priced
                  ? "bg-green-100 border-2 border-green-400"
                  : "bg-blue-100 hover:bg-blue-200 border-2 border-blue-300";
                const dotClasses = priced ? "bg-green-500" : "bg-blue-500";
                const cleanTitle = jg.title || jg.alignment_type_label
                  || (jg.subtype === 'wheel_alignment' ? 'Wheel Alignment' : 'Recommendation');
                return (
                  <div key={jg.id} className="mb-2">
                    <div
                      className={`flex items-start gap-2 text-sm py-1.5 px-2 rounded-lg cursor-pointer transition-all group ${toneClasses}`}
                      onClick={() => focusOnJobGroup(jg.id)}
                      data-testid={`left-panel-recommendation-${jg.id}`}
                    >
                      <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${dotClasses}`} />
                      <LeftPanelSourceBadge source="SVC" dataTestid={`left-panel-source-svc-${jg.id}`} />
                      <span className={`flex-1 min-w-0 text-xs break-words ${
                        priced ? "text-green-800" : "text-blue-900"
                      }`}>
                        {cleanTitle}
                        {jg.alignment_type_label && jg.subtype === 'wheel_alignment' && (
                          <span className="block text-[10px] mt-0.5 text-blue-700">
                            {jg.alignment_type_label}
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] font-bold text-slate-700 shrink-0">
                        £{Number(
                          // iter211as — LK61JFF bug: stored `total_inc_vat`
                          // was £48 (stale) while the recomputed `total`
                          // was £90 (correct). Left panel showed £48
                          // even though the middle card showed £90.
                          // Read whichever field is populated and non-
                          // zero; prefer the freshly-computed rollup
                          // when both exist.
                          jg.total || jg.total_inc_vat || 0
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              };
              return (
                <div className="mb-4 pb-4 border-t pt-3 border-slate-200" data-testid="left-panel-added-recommendations">
                  <p className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wider">
                    Added Recommendations ({addedJgs.length})
                  </p>
                  {addedJgs.map(renderRecRow)}
                </div>
              );
            })()}
          </div>
        </div>
        
        {/* CENTER COLUMN - Main Pricing Workspace */}
        <div className="flex-1 overflow-y-auto p-6">
        {/* Job Lock Warning Banner */}
        {isJobLocked && !hasLock && !lockWarningDismissed && (
          <LockWarningBanner 
            lockedByName={lockedByName}
            lockedAt={lockedAt}
            onDismiss={() => setLockWarningDismissed(true)}
          />
        )}
        
        {/* Active Lock Indicator - shows when current user has the lock */}
        {hasLock && !lockLoading && (
          <ActiveLockBanner screen="Pricing Builder" />
        )}

        {/* Customer Name (COMPACT) */}
        <Card className="mb-3">
          <CardContent className="py-2 px-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600 whitespace-nowrap">Customer:</Label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Customer name..."
                  className="w-48 h-8 text-sm"
                  data-testid="customer-name-input"
                />
              </div>
              {/* Key tag — physical key reference, editable inline. */}
              {data?.job?.id && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600 whitespace-nowrap">Key tag:</Label>
                  <KeyTagInline
                    jobId={data.job.id}
                    initial={data?.job?.key_tag_number}
                    onSaved={(v) =>
                      setData((d) => (d ? { ...d, job: { ...d.job, key_tag_number: v } } : d))
                    }
                  />
                </div>
              )}
              {/* Authorised Value Display - Compact inline */}
              {authorisedValueData.confirmed_at && (
                <div className="flex items-center gap-2 ml-auto">
                  {/* Show new Intent Display if configured, else show legacy display */}
                  {intentConfig ? (
                    <CustomerIntentDisplay
                      config={intentConfig}
                      onChangeClick={handleChangeIntent}
                      compact={true}
                      locked={isIntentLocked}
                    />
                  ) : (
                    <AuthorisedValueDisplay
                      value={authorisedValueData.value}
                      tags={authorisedValueData.tags}
                      confirmedAt={authorisedValueData.confirmed_at}
                      confirmedBy={authorisedValueData.confirmed_by}
                      onChangeClick={handleChangeAuthorisedValue}
                      compact={true}
                    />
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ═══ JOB READINESS & MOT STATUS PANEL ═══ */}
        {jobReadiness && (jobReadiness.intents || []).length > 0 && (
          <Card className="mb-3 border-2 border-slate-300">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-sm text-slate-800 flex items-center gap-2">
                  Job Readiness
                </h4>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  jobReadiness.job_status?.status === 'mot_pending' ? 'bg-amber-100 text-amber-700' :
                  jobReadiness.job_status?.status === 'pricing_ready' ? 'bg-green-100 text-green-700' :
                  'bg-slate-100 text-slate-600'
                }`}>{jobReadiness.job_status?.status_label || 'Unknown'}</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {(jobReadiness.intents || []).map(intent => (
                  <span key={intent} className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    ['mot', 'mot_failure_repair', 'mot_retest'].includes(intent) ? 'bg-teal-100 text-teal-700' :
                    ['service', 'safety_inspection'].includes(intent) ? 'bg-emerald-100 text-emerald-700' :
                    intent === 'diagnostic' ? 'bg-purple-100 text-purple-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{jobReadiness.intent_labels?.[intent] || intent}</span>
                ))}
              </div>
              <div className="space-y-1.5 mb-3">
                {(jobReadiness.job_status?.checklist || []).map((item, idx) => (
                  <div key={idx} className={`flex items-center gap-2 p-2 rounded text-sm ${item.completed ? 'bg-green-50' : item.pending ? 'bg-amber-50' : 'bg-slate-50'}`}>
                    {item.completed ? <Check className="w-4 h-4 text-green-600" /> : item.pending ? <Clock className="w-4 h-4 text-amber-500" /> : <div className="w-4 h-4 border-2 border-slate-300 rounded" />}
                    <span className={item.completed ? 'text-green-700' : item.pending ? 'text-amber-700' : 'text-slate-600'}>{item.label}</span>
                    {item.type === 'mot' && item.mot_status && (
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${
                        item.mot_status === 'mot_imported' ? 'bg-green-100 text-green-700' :
                        item.mot_status === 'mot_completed_ready' ? 'bg-blue-100 text-blue-700' :
                        item.mot_status === 'mot_pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>{item.mot_status.replace(/_/g, ' ').replace('mot ', '')}</span>
                    )}
                  </div>
                ))}
                {/* Phase 2: Unreviewed MOT defects chip */}
                {(motDefects.counts?.unreviewed_failures || 0) > 0 && (
                  <div className="flex items-center gap-2 p-2 rounded text-sm bg-amber-50 border border-amber-200" data-testid="unreviewed-mot-chip">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-amber-800">{motDefects.counts.unreviewed_failures} MOT defect{motDefects.counts.unreviewed_failures === 1 ? '' : 's'} awaiting FOH review</span>
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1">
                {jobReadiness.job_status?.mot_blocks_full_estimate
                  ? <><AlertTriangle className="w-3 h-3 text-amber-500" /> Full MOT-inclusive estimate blocked until MOT imported</>
                  : <><Check className="w-3 h-3 text-green-500" /> All estimate scopes available</>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══ MOT STATUS PANEL (FOH-Controlled) ═══ */}
        {jobReadiness?.mot_status && (
          <Card className="mb-3 border-2 border-teal-300">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-sm text-teal-800">MOT Status</h4>
                <div className="flex items-center gap-2">
                  {dvsaStatus && (
                    <button type="button"
                      onClick={() => setShowDvsaStatusModal(true)}
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                        dvsaStatus.mode === 'live' && dvsaStatus.ready_for_live ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                        dvsaStatus.mode === 'live' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                        'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                      title="View DVSA integration status"
                      data-testid="dvsa-mode-chip"
                    >DVSA {dvsaStatus.mode}</button>
                  )}
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    jobReadiness.mot_status.status === 'mot_imported' ? 'bg-green-500 text-white' :
                    jobReadiness.mot_status.status === 'mot_completed_ready' ? 'bg-blue-500 text-white' :
                    jobReadiness.mot_status.status === 'mot_pending' ? 'bg-amber-500 text-white' :
                    jobReadiness.mot_status.status === 'mot_lookup_failed' ? 'bg-red-500 text-white' :
                    'bg-slate-500 text-white'
                  }`}>{(jobReadiness.mot_status.status || '').replace(/_/g, ' ').replace('mot ', '').toUpperCase()}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { status: 'mot_pending', label: 'Pending', c: 'amber' },
                  { status: 'mot_completed_ready', label: 'Completed', c: 'blue' },
                  { status: 'mot_imported', label: 'Imported', c: 'green' },
                  { status: 'mot_not_available', label: 'Not Available', c: 'slate' },
                ].map(opt => (
                  <button key={opt.status} type="button"
                    onClick={async () => {
                      try {
                        const jobId = data?.job?.id;
                        await axios.put(`${API}/jobs/${jobId}/mot-status`, { status: opt.status });
                        const rRes = await axios.get(`${API}/jobs/${jobId}/readiness`);
                        setJobReadiness(rRes.data);
                      } catch (err) { console.error(err); }
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full border-2 font-medium transition-all ${
                      jobReadiness.mot_status.status === opt.status
                        ? opt.c === 'amber' ? 'border-amber-500 bg-amber-100 text-amber-800'
                        : opt.c === 'blue' ? 'border-blue-500 bg-blue-100 text-blue-800'
                        : opt.c === 'green' ? 'border-green-500 bg-green-100 text-green-800'
                        : 'border-slate-400 bg-slate-100 text-slate-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`} data-testid={`mot-status-${opt.status}`}
                  >{opt.label}</button>
                ))}
              </div>
              <button type="button"
                onClick={async () => {
                  try {
                    setMotLookupLoading(true);
                    const jobId = data?.job?.id;
                    const res = await axios.post(`${API}/jobs/${jobId}/mot-lookup-live`, {});
                    setMotLookupPreview(res.data);
                    // If lookup is irrelevant or no_record, surface modal
                    if (res.data.status === 'no_record') {
                      alert('No MOT record found for this VRM. You can mark MOT as not yet available.');
                    } else if (!res.data.relevance?.is_relevant) {
                      setShowRelevanceModal(true);
                    } else {
                      // Auto-import when relevance is fine
                      await axios.post(`${API}/jobs/${jobId}/mot-lookup/${res.data.lookup_id}/import`, {});
                      toast.success('MOT result imported');
                      const [rRes, dRes, cRes] = await Promise.all([
                        axios.get(`${API}/jobs/${jobId}/readiness`),
                        axios.get(`${API}/jobs/${jobId}/mot-defects`),
                        axios.get(`${API}/jobs/${jobId}/mot-defects/duplicate-candidates`),
                      ]);
                      setJobReadiness(rRes.data);
                      setMotDefects(dRes.data);
                      setMotDuplicates(cRes.data?.matches || []);
                    }
                  } catch (err) { alert(err.response?.data?.detail || 'MOT lookup failed'); }
                  finally { setMotLookupLoading(false); }
                }}
                disabled={motLookupLoading || jobReadiness.mot_status.status === 'mot_imported'}
                className={`w-full p-3 rounded-lg border-2 font-semibold text-sm transition-all ${
                  jobReadiness.mot_status.status === 'mot_imported' ? 'border-green-300 bg-green-50 text-green-700 cursor-default'
                  : 'border-teal-400 bg-teal-50 text-teal-800 hover:bg-teal-100'
                }`} data-testid="run-mot-lookup-btn"
              >{motLookupLoading ? 'Looking up...' : jobReadiness.mot_status.status === 'mot_imported' ? 'MOT Imported' : 'Run MOT Lookup'}</button>
              {jobReadiness.mot_status.updated_at && (
                <p className="text-xs text-slate-400 mt-2">Last updated: {new Date(jobReadiness.mot_status.updated_at).toLocaleString()} by {jobReadiness.mot_status.updated_by_name || 'System'}</p>
              )}
              {/* ─── MOT IMPORTED — UNIFIED BODY (defects, recurring, history) ─── */}
              {jobReadiness.mot_status.status === 'mot_imported' && (
                <div className="mt-3 space-y-3" data-testid="mot-imported-body">
                  {/* Result strip */}
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          (jobReadiness.mot_status.result || '').toUpperCase() === 'FAILED' ? 'bg-red-600 text-white' :
                          (jobReadiness.mot_status.result || '').toUpperCase() === 'PASSED' ? 'bg-green-600 text-white' :
                          'bg-slate-500 text-white'
                        }`} data-testid="mot-result-badge">MOT {jobReadiness.mot_status.result || 'UNKNOWN'}</span>
                        {jobReadiness.mot_status.expiry_date && (
                          <span className="text-xs text-slate-600">Expires <strong>{jobReadiness.mot_status.expiry_date}</strong></span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{jobReadiness.mot_status.test_date}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs">
                      {jobReadiness.mot_status.odometer && (
                        <span className="text-slate-600"><strong>{jobReadiness.mot_status.odometer}</strong> mi</span>
                      )}
                      {jobReadiness.mot_status.mot_test_number && (
                        <span className="text-slate-500 font-mono">#{jobReadiness.mot_status.mot_test_number}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(motDefects.counts?.dangerous || 0) > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 bg-red-100 text-red-800 rounded" data-testid="count-dangerous">{motDefects.counts.dangerous} Dangerous</span>
                      )}
                      {(motDefects.counts?.major || 0) > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 bg-orange-100 text-orange-800 rounded" data-testid="count-major">{motDefects.counts.major} Major</span>
                      )}
                      {(motDefects.counts?.minor || 0) > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 bg-amber-100 text-amber-800 rounded" data-testid="count-minor">{motDefects.counts.minor} Minor</span>
                      )}
                      {(motDefects.counts?.advisory || 0) > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 bg-blue-100 text-blue-800 rounded" data-testid="count-advisory">{motDefects.counts.advisory} Advisory</span>
                      )}
                      {motDefects.total === 0 && (
                        <span className="text-xs text-green-700">No defects or advisories</span>
                      )}
                    </div>
                  </div>

                  {/* Defects list — flat, severity-sorted, with inline "+ Add" and per-row "More actions" */}
                  {motDefects.total > 0 && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="mot-defect-list">
                      <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-700 flex items-center justify-between">
                        <span>Defects & Advisories ({motDefects.total})</span>
                        <span className="text-slate-400">Click + Add to price · ⋯ for more actions</span>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                        {['required_for_pass', 'needs_review', 'minor', 'advisory'].flatMap(k =>
                          (motDefects.groups?.[k] || []).map(defect => {
                            const dupMatches = motDuplicates.filter(m => m.mot_defect_id === defect.id);
                            const isOpen = motActionsOpen === defect.id;
                            return (
                              <div key={defect.id} className="p-2.5 bg-white" data-testid={`mot-defect-${defect.id}`}>
                                <div className="flex items-start gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-slate-800">{defect.original_dvsa_text}</p>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                        defect.defect_category === 'Dangerous' ? 'bg-red-600 text-white' :
                                        defect.defect_category === 'Major' ? 'bg-orange-500 text-white' :
                                        defect.defect_category === 'Minor' ? 'bg-amber-400 text-amber-900' :
                                        defect.defect_category === 'Advisory' || defect.defect_category === 'Pass Advisory' ? 'bg-blue-100 text-blue-700' :
                                        'bg-slate-300 text-slate-700'
                                      }`}>{defect.defect_category}</span>
                                      {defect.system_group && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded capitalize">{defect.system_group.replace(/_/g, ' ')}</span>}
                                      {defect.location && defect.location !== 'unknown' && (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{defect.location}</span>
                                      )}
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                        defect.status === 'Priced' ? 'bg-green-100 text-green-700' :
                                        defect.status === 'Deferred' ? 'bg-slate-200 text-slate-700' :
                                        defect.status === 'Marked Already Repaired' ? 'bg-green-50 text-green-600' :
                                        defect.status === 'Not Applicable' ? 'bg-slate-100 text-slate-500 line-through' :
                                        defect.status === 'Needs FOH Review' ? 'bg-amber-100 text-amber-700' :
                                        'bg-blue-50 text-blue-700'
                                      }`}>{defect.status}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button type="button"
                                      disabled={defect.priced || defect.status === 'Priced'}
                                      onClick={async () => {
                                        try {
                                          await axios.post(`${API}/mot-defects/${defect.id}/price-now`, {});
                                          toast.success('Added to yestimate');
                                          const [dRes, yRes] = await Promise.all([
                                            axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`),
                                            axios.get(`${API}/foh/yestimate/${yestimateId}`),
                                          ]);
                                          setMotDefects(dRes.data);
                                          setJobGroups(sanitizeJobGroups(yRes.data.yestimate?.job_groups || []));
                                        } catch (err) { alert(err.response?.data?.detail || 'Failed to add to yestimate'); }
                                      }}
                                      className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                      data-testid={`mot-add-${defect.id}`}
                                    >+ Add</button>
                                    <button type="button"
                                      onClick={() => setMotActionsOpen(isOpen ? null : defect.id)}
                                      className="text-xs px-1.5 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200"
                                      data-testid={`mot-more-${defect.id}`}
                                    >⋯</button>
                                  </div>
                                </div>
                                {dupMatches.length > 0 && (
                                  <div className="mt-1.5 p-1.5 bg-yellow-50 border border-yellow-200 rounded text-[11px] text-yellow-800 flex items-start gap-2">
                                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                                    <div><strong>Possible duplicate:</strong> {dupMatches.map(m => m.job_group_title).join(', ')}.
                                      <button type="button" className="ml-1 underline text-yellow-900" onClick={() => setLinkingDefect({ ...defect, candidates: dupMatches })}>Link instead?</button>
                                    </div>
                                  </div>
                                )}
                                {isOpen && (
                                  <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-slate-100">
                                    <button type="button" onClick={() => setLinkingDefect(defect)} className="text-[11px] px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">Link</button>
                                    <button type="button" onClick={async () => { await axios.patch(`${API}/mot-defects/${defect.id}`, { status: 'Deferred', foh_reviewed: true }); const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`); setMotDefects(dRes.data); }} className="text-[11px] px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300">Defer</button>
                                    <button type="button" onClick={async () => { await axios.patch(`${API}/mot-defects/${defect.id}`, { status: 'Marked Already Repaired', foh_reviewed: true }); const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`); setMotDefects(dRes.data); }} className="text-[11px] px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200">Already Repaired</button>
                                    <button type="button" onClick={async () => { await axios.patch(`${API}/mot-defects/${defect.id}`, { status: 'Not Applicable', foh_reviewed: true }); const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`); setMotDefects(dRes.data); }} className="text-[11px] px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200">N/A</button>
                                    <button type="button" onClick={async () => { await axios.patch(`${API}/mot-defects/${defect.id}`, { tech_verification_requested: true, foh_reviewed: true }); const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`); setMotDefects(dRes.data); toast.success('Tech verification requested'); }} className="text-[11px] px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200">Request Tech Verify</button>
                                    <button type="button" onClick={() => setEditingDefect(defect)} className="text-[11px] px-2 py-1 bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50">Edit Classification</button>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recurring advisories — purple highlight */}
                  {(motRecurring?.length || 0) > 0 && (
                    <div className="border border-purple-200 rounded-lg bg-purple-50/40 p-3" data-testid="mot-recurring-section">
                      <div className="flex items-center gap-1 text-xs font-semibold text-purple-700 mb-2">
                        <History className="w-3 h-3" /> Recurring across MOT history ({motRecurring.length})
                      </div>
                      <ul className="space-y-1">
                        {motRecurring.map((g, idx) => {
                          const loc = (g.location || '').replace(/_/g, ' ');
                          const sys = (g.system_group || '').replace(/_/g, ' ');
                          const sample = g.examples?.[0]?.text || '';
                          return (
                            <li key={idx} className="text-xs text-purple-900 bg-white rounded p-2" data-testid={`mot-recurring-${idx}`}>
                              <span className="font-semibold capitalize">{loc} {sys}</span>
                              <span className="ml-2 text-purple-700">· {g.occurrences}× MOTs</span>
                              {g.test_dates?.length > 1 && (
                                <span className="ml-2 text-purple-500 font-normal">({g.test_dates[g.test_dates.length-1]} → {g.test_dates[0]})</span>
                              )}
                              {sample && <div className="text-purple-700 mt-0.5">{sample}</div>}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Full history toggle */}
                  {(motAllTests?.length || 0) > 1 && (
                    <div>
                      <button type="button"
                        onClick={() => setMotHistoryExpanded(!motHistoryExpanded)}
                        className="text-xs text-teal-800 underline hover:text-teal-900"
                        data-testid="mot-history-toggle"
                      >{motHistoryExpanded ? 'Hide' : 'Show'} full MOT history ({motAllTests.length} tests)</button>
                      {motHistoryExpanded && (
                        <div className="mt-2 space-y-2">
                          {motAllTests.slice(1).map((t, i) => (
                            <div key={`mht-${i}`} className="text-xs bg-white rounded p-2 border border-slate-200">
                              <div className="flex justify-between items-center">
                                <span className="font-semibold">
                                  {(t.completedDate || '').slice(0, 10)} — <span className={t.testResult === 'PASSED' ? 'text-emerald-700' : 'text-red-700'}>{t.testResult}</span>
                                </span>
                                <span className="text-slate-500">{t.odometerValue} {t.odometerUnit}</span>
                              </div>
                              {(t.defects || t.rfrAndComments || []).map((r, j) => (
                                <div key={`mhr-${j}`} className="text-slate-600 mt-1">· <span className="font-medium">{r.type}:</span> {r.text || r.defect_text}</div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <p className="text-[11px] text-slate-400 italic">All defects default to internal-only. They will not appear in the customer email or portal until FOH approves them and the explanation engine generates suitable wording.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ═══ MOT DEFECT REVIEW removed — merged into the unified MOT card above ═══ */}
        {false && jobReadiness?.mot_status?.status === 'mot_imported' && motDefects.total > 0 && (
          <Card className="mb-3 border-2 border-slate-300" data-testid="mot-defect-review-section">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm text-slate-800">MOT Defect Review</h4>
                <span className="text-xs text-slate-500">{motDefects.total} item{motDefects.total === 1 ? '' : 's'} imported</span>
              </div>
              {[
                { key: 'required_for_pass', title: 'Required for MOT Pass', color: 'red', icon: AlertTriangle },
                { key: 'needs_review', title: 'Needs Review (Low Confidence)', color: 'amber', icon: Search },
                { key: 'minor', title: 'Minor Defects', color: 'amber', icon: AlertCircle },
                { key: 'advisory', title: 'Advisories', color: 'blue', icon: Info },
              ].map(group => {
                const items = motDefects.groups?.[group.key] || [];
                if (items.length === 0) return null;
                const isExpanded = expandedMotGroups[group.key];
                const Icon = group.icon;
                return (
                  <div key={group.key} className="mb-2 border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedMotGroups(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm font-semibold ${
                        group.color === 'red' ? 'bg-red-50 text-red-800 hover:bg-red-100' :
                        group.color === 'amber' ? 'bg-amber-50 text-amber-800 hover:bg-amber-100' :
                        'bg-blue-50 text-blue-800 hover:bg-blue-100'
                      }`}
                      data-testid={`mot-group-toggle-${group.key}`}
                    >
                      <span className="flex items-center gap-2"><Icon className="w-4 h-4" />{group.title} ({items.length})</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {isExpanded && (
                      <div className="divide-y divide-slate-200">
                        {items.map(defect => {
                          const dupMatches = motDuplicates.filter(m => m.mot_defect_id === defect.id);
                          return (
                            <div key={defect.id} className="p-3 bg-white" data-testid={`mot-defect-${defect.id}`}>
                              <div className="flex items-start justify-between mb-2 gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-800">{defect.original_dvsa_text}</p>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                                      defect.defect_category === 'Dangerous' ? 'bg-red-600 text-white' :
                                      defect.defect_category === 'Major' ? 'bg-orange-500 text-white' :
                                      defect.defect_category === 'Minor' ? 'bg-amber-400 text-amber-900' :
                                      defect.defect_category === 'Advisory' || defect.defect_category === 'Pass Advisory' ? 'bg-blue-100 text-blue-700' :
                                      'bg-slate-300 text-slate-700'
                                    }`}>{defect.defect_category}</span>
                                    <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{defect.system_group?.replace(/_/g, ' ')}</span>
                                    {defect.location && defect.location !== 'unknown' && (
                                      <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{defect.location}</span>
                                    )}
                                    {defect.component && (
                                      <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded italic">{defect.component}</span>
                                    )}
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                                      defect.confidence_level === 'HIGH' ? 'bg-green-100 text-green-700' :
                                      defect.confidence_level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                                      defect.confidence_level === 'LOW' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700'
                                    }`} title={defect.confidence_reason} data-testid={`confidence-${defect.id}`}>{defect.confidence_level}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      defect.status === 'Priced' ? 'bg-green-100 text-green-700' :
                                      defect.status === 'Deferred' ? 'bg-slate-200 text-slate-700' :
                                      defect.status === 'Marked Already Repaired' ? 'bg-green-50 text-green-600' :
                                      defect.status === 'Not Applicable' ? 'bg-slate-100 text-slate-500 line-through' :
                                      defect.status === 'Needs FOH Review' ? 'bg-amber-100 text-amber-700' :
                                      'bg-blue-50 text-blue-700'
                                    }`}>{defect.status}</span>
                                  </div>
                                </div>
                              </div>
                              {dupMatches.length > 0 && (
                                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-start gap-2" data-testid={`dup-warning-${defect.id}`}>
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <strong>Possible duplicate:</strong> {dupMatches.map(m => m.job_group_title).join(', ')}.
                                    <button type="button" className="ml-2 underline text-yellow-900" onClick={() => setLinkingDefect({ ...defect, candidates: dupMatches })}>Link instead?</button>
                                  </div>
                                </div>
                              )}
                              <div className="flex flex-wrap gap-1.5">
                                <button type="button"
                                  disabled={defect.priced || defect.status === 'Priced'}
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/mot-defects/${defect.id}/price-now`, {});
                                      toast.success('Draft pricing item created');
                                      const [dRes, yRes] = await Promise.all([
                                        axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`),
                                        axios.get(`${API}/foh/yestimate/${yestimateId}`),
                                      ]);
                                      setMotDefects(dRes.data);
                                      setJobGroups(sanitizeJobGroups(yRes.data.yestimate?.job_groups || []));
                                    } catch (err) { alert(err.response?.data?.detail || 'Failed to create pricing item'); }
                                  }}
                                  className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                  data-testid={`price-now-${defect.id}`}
                                >Price Now</button>
                                <button type="button"
                                  onClick={() => setLinkingDefect(defect)}
                                  className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                  data-testid={`link-pricing-${defect.id}`}
                                >Link</button>
                                <button type="button"
                                  onClick={async () => {
                                    await axios.patch(`${API}/mot-defects/${defect.id}`, { status: 'Deferred', foh_reviewed: true });
                                    const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`);
                                    setMotDefects(dRes.data);
                                  }}
                                  className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                                  data-testid={`defer-${defect.id}`}
                                >Defer</button>
                                <button type="button"
                                  onClick={async () => {
                                    await axios.patch(`${API}/mot-defects/${defect.id}`, { status: 'Marked Already Repaired', foh_reviewed: true });
                                    const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`);
                                    setMotDefects(dRes.data);
                                  }}
                                  className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  data-testid={`already-repaired-${defect.id}`}
                                >Already Repaired</button>
                                <button type="button"
                                  onClick={async () => {
                                    await axios.patch(`${API}/mot-defects/${defect.id}`, { status: 'Not Applicable', foh_reviewed: true });
                                    const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`);
                                    setMotDefects(dRes.data);
                                  }}
                                  className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200"
                                  data-testid={`na-${defect.id}`}
                                >N/A</button>
                                <button type="button"
                                  onClick={async () => {
                                    await axios.patch(`${API}/mot-defects/${defect.id}`, { tech_verification_requested: true, foh_reviewed: true });
                                    const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`);
                                    setMotDefects(dRes.data);
                                    toast.success('Tech verification requested');
                                  }}
                                  className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                  data-testid={`request-tech-${defect.id}`}
                                >Request Tech Verify</button>
                                <button type="button"
                                  onClick={() => setEditingDefect(defect)}
                                  className="text-xs px-2 py-1 bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
                                  data-testid={`edit-classification-${defect.id}`}
                                >Edit Classification</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-slate-400 mt-2 italic">All items default to internal-only. They will not appear in the customer email or portal until FOH approves them and the customer-facing explanation engine generates suitable wording.</p>
            </CardContent>
          </Card>
        )}

        {/* ═══ RELEVANCE WARNING MODAL (Phase 2) ═══ */}
        {showRelevanceModal && motLookupPreview && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="relevance-warning-modal">
            <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-amber-100 rounded-full p-2"><AlertTriangle className="w-6 h-6 text-amber-600" /></div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-slate-800">Latest MOT may not relate to this job</h3>
                  <p className="text-sm text-slate-600 mt-1">Please review before importing.</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded p-3 mb-3 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">MOT date:</span> <strong>{motLookupPreview.latest_test?.completedDate}</strong></div>
                  <div><span className="text-slate-500">MOT mileage:</span> <strong>{motLookupPreview.relevance?.mot_mileage ?? '—'} mi</strong></div>
                  <div><span className="text-slate-500">Job mileage:</span> <strong>{motLookupPreview.relevance?.job_mileage ?? '—'} mi</strong></div>
                  <div><span className="text-slate-500">Result:</span> <strong>{motLookupPreview.latest_test?.testResult}</strong></div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-xs text-amber-800">
                {(motLookupPreview.relevance?.reasons || []).map((r, i) => (
                  <p key={i} className="flex items-start gap-1"><span>•</span>{r}</p>
                ))}
              </div>
              <div>
                <Label className="text-xs">Override reason (required to import anyway)</Label>
                <Textarea
                  value={relevanceOverrideReason}
                  onChange={e => setRelevanceOverrideReason(e.target.value)}
                  placeholder="e.g. Confirmed with customer that this MOT relates to today's visit..."
                  className="mt-1 text-sm" rows={2}
                  data-testid="relevance-override-reason"
                />
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <Button
                  disabled={!relevanceOverrideReason.trim()}
                  onClick={async () => {
                    try {
                      const jobId = data?.job?.id;
                      await axios.post(`${API}/jobs/${jobId}/mot-lookup/${motLookupPreview.lookup_id}/import`, {
                        override_reason: relevanceOverrideReason.trim(),
                      });
                      toast.success('MOT imported with override reason');
                      const [rRes, dRes, cRes] = await Promise.all([
                        axios.get(`${API}/jobs/${jobId}/readiness`),
                        axios.get(`${API}/jobs/${jobId}/mot-defects`),
                        axios.get(`${API}/jobs/${jobId}/mot-defects/duplicate-candidates`),
                      ]);
                      setJobReadiness(rRes.data);
                      setMotDefects(dRes.data);
                      setMotDuplicates(cRes.data?.matches || []);
                      setShowRelevanceModal(false);
                      setRelevanceOverrideReason('');
                      setMotLookupPreview(null);
                    } catch (err) { alert(err.response?.data?.detail || 'Import failed'); }
                  }}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="import-anyway-btn"
                >Import Anyway</Button>
                <Button variant="outline"
                  onClick={async () => {
                    try {
                      setMotLookupLoading(true);
                      const jobId = data?.job?.id;
                      const res = await axios.post(`${API}/jobs/${jobId}/mot-lookup-live`, {});
                      setMotLookupPreview(res.data);
                      if (res.data.relevance?.is_relevant) setShowRelevanceModal(false);
                    } catch (err) { alert(err.response?.data?.detail || 'Retry failed'); }
                    finally { setMotLookupLoading(false); }
                  }}
                  data-testid="retry-lookup-btn"
                >Retry Lookup</Button>
                <Button variant="ghost"
                  onClick={async () => {
                    try {
                      const jobId = data?.job?.id;
                      await axios.post(`${API}/jobs/${jobId}/mot-lookup/${motLookupPreview.lookup_id}/cancel`, {
                        reason: relevanceOverrideReason || 'User cancelled relevance warning',
                      });
                    } catch (err) { /* non-blocking */ }
                    setShowRelevanceModal(false);
                    setRelevanceOverrideReason('');
                    setMotLookupPreview(null);
                  }}
                  data-testid="cancel-import-btn"
                >Cancel Import</Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ EDIT CLASSIFICATION MODAL ═══ */}
        {editingDefect && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="edit-classification-modal">
            <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl">
              <h3 className="font-bold text-base text-slate-800 mb-3">Edit MOT Defect Classification</h3>
              <div className="bg-slate-50 border-l-2 border-slate-300 p-2 text-xs mb-3 italic">"{editingDefect.original_dvsa_text}"</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Defect Category</Label>
                  <Select value={editingDefect.defect_category} onValueChange={v => setEditingDefect({ ...editingDefect, defect_category: v })}>
                    <SelectTrigger className="text-sm" data-testid="edit-defect-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Dangerous', 'Major', 'Minor', 'Advisory', 'Pass Advisory', 'Unknown'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">System Group</Label>
                  <Select value={editingDefect.system_group} onValueChange={v => setEditingDefect({ ...editingDefect, system_group: v })}>
                    <SelectTrigger className="text-sm" data-testid="edit-system-group"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['tyres', 'brakes', 'suspension', 'steering', 'lights', 'emissions', 'body', 'visibility', 'seatbelts', 'registration_plates', 'washers_wipers', 'exhaust', 'other', 'unknown'].map(g => <SelectItem key={g} value={g}>{g.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Select value={editingDefect.location} onValueChange={v => setEditingDefect({ ...editingDefect, location: v })}>
                    <SelectTrigger className="text-sm" data-testid="edit-location"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['NSF', 'OSF', 'NSR', 'OSR', 'front', 'rear', 'nearside', 'offside', 'centre', 'unknown'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Component (optional)</Label>
                  <Input value={editingDefect.component || ''} onChange={e => setEditingDefect({ ...editingDefect, component: e.target.value })} className="text-sm" data-testid="edit-component" />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={async () => {
                  try {
                    await axios.patch(`${API}/mot-defects/${editingDefect.id}`, {
                      defect_category: editingDefect.defect_category,
                      system_group: editingDefect.system_group,
                      location: editingDefect.location,
                      component: editingDefect.component,
                      foh_reviewed: true,
                    });
                    const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`);
                    setMotDefects(dRes.data);
                    setEditingDefect(null);
                    toast.success('Classification updated');
                  } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
                }} className="flex-1" data-testid="save-classification-btn">Save</Button>
                <Button variant="ghost" onClick={() => setEditingDefect(null)} data-testid="cancel-classification-btn">Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ LINK TO PRICING MODAL ═══ */}
        {linkingDefect && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="link-pricing-modal">
            <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-2xl">
              <h3 className="font-bold text-base text-slate-800 mb-2">Link MOT Defect to Existing Pricing Item</h3>
              <div className="bg-slate-50 border-l-2 border-slate-300 p-2 text-xs italic mb-3">"{linkingDefect.original_dvsa_text}"</div>
              {(linkingDefect.candidates || []).length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-yellow-700 mb-1">Suggested matches:</p>
                  {linkingDefect.candidates.map(c => (
                    <button key={c.job_group_id} type="button"
                      onClick={async () => {
                        await axios.post(`${API}/mot-defects/${linkingDefect.id}/link-pricing`, { linked_pricing_item_id: c.job_group_id });
                        const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`);
                        setMotDefects(dRes.data);
                        setLinkingDefect(null);
                        toast.success('Linked to existing pricing item');
                      }}
                      className="w-full text-left p-2 mb-1 border border-yellow-300 bg-yellow-50 rounded text-xs hover:bg-yellow-100"
                      data-testid={`link-suggested-${c.job_group_id}`}
                    >
                      <strong>{c.job_group_title}</strong> <span className="text-yellow-700">(score {c.score})</span>
                      <p className="text-slate-500 mt-0.5">{c.reason}</p>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs font-semibold text-slate-700 mb-1">All pricing items:</p>
              <div className="max-h-60 overflow-auto border border-slate-200 rounded">
                {(jobGroups || []).map(jg => (
                  <button key={jg.id} type="button"
                    onClick={async () => {
                      await axios.post(`${API}/mot-defects/${linkingDefect.id}/link-pricing`, { linked_pricing_item_id: jg.id });
                      const dRes = await axios.get(`${API}/jobs/${data?.job?.id}/mot-defects`);
                      setMotDefects(dRes.data);
                      setLinkingDefect(null);
                      toast.success('Linked to pricing item');
                    }}
                    className="w-full text-left p-2 text-xs border-b last:border-b-0 border-slate-100 hover:bg-slate-50"
                    data-testid={`link-jg-${jg.id}`}
                  >
                    <strong>{jg.title || jg.name || '(untitled)'}</strong>
                    {jg.category && <span className="ml-2 text-slate-500">{jg.category}</span>}
                  </button>
                ))}
                {(jobGroups || []).length === 0 && <p className="p-3 text-xs text-slate-400 italic">No pricing items yet</p>}
              </div>
              <div className="flex justify-end mt-3">
                <Button variant="ghost" onClick={() => setLinkingDefect(null)} data-testid="cancel-link-btn">Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ UNREVIEWED-MOT-ITEMS SEND OVERRIDE MODAL ═══ */}
        {showUnreviewedSendModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="unreviewed-send-modal">
            <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-1" />
                <div>
                  <h3 className="font-bold text-base text-slate-800">Unreviewed MOT items</h3>
                  <p className="text-sm text-slate-600 mt-1">There are MOT failure items that have not yet been reviewed or priced. Sending a full MOT-inclusive estimate may be incomplete.</p>
                </div>
              </div>
              <div>
                <Label className="text-xs">Override reason (required)</Label>
                <Textarea value={unreviewedOverrideReason} onChange={e => setUnreviewedOverrideReason(e.target.value)} className="mt-1 text-sm" rows={2} data-testid="unreviewed-override-reason" />
              </div>
              <div className="flex flex-col gap-2 mt-3">
                <Button onClick={() => { setShowUnreviewedSendModal(false); setUnreviewedOverrideReason(''); }} variant="outline" data-testid="review-mot-items-btn">Review MOT Items First</Button>
                <Button
                  disabled={!unreviewedOverrideReason.trim()}
                  onClick={async () => {
                    try {
                      const jobId = data?.job?.id;
                      const scope = jobReadiness?.estimate_scope || 'full_mot_inclusive';
                      await axios.post(`${API}/jobs/${jobId}/estimate-send`, { scope, override_reason: `[unreviewed MOT] ${unreviewedOverrideReason.trim()}` });
                      setShowUnreviewedSendModal(false);
                      setUnreviewedOverrideReason('');
                      navigate(`/app/foh/yestimate/${yestimateId}/preview`);
                    } catch (err) { alert(err.response?.data?.detail || 'Send failed'); }
                  }}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="send-with-unreviewed-override-btn"
                >Send With Override</Button>
                <Button variant="ghost" onClick={() => { setShowUnreviewedSendModal(false); setUnreviewedOverrideReason(''); }} data-testid="cancel-unreviewed-btn">Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ DVSA INTEGRATION STATUS MODAL (Admin Indicator) ═══ */}
        {showDvsaStatusModal && dvsaStatus && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="dvsa-status-modal">
            <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-2xl">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-base text-slate-800">DVSA Integration Status</h3>
                  <p className="text-xs text-slate-500">Configuration & last live lookup outcome</p>
                </div>
                <button type="button" onClick={async () => {
                  // Refresh status
                  try {
                    const sRes = await axios.get(`${API}/admin/dvsa/status`);
                    setDvsaStatus(sRes.data);
                  } catch (e) {}
                }} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded" data-testid="dvsa-status-refresh">Refresh</button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600">Mode</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded uppercase ${
                    dvsaStatus.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-700'
                  }`} data-testid="dvsa-status-mode">{dvsaStatus.mode}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600">API key configured</span>
                  <span className={`text-xs font-bold ${dvsaStatus.api_key_configured ? 'text-green-700' : 'text-red-700'}`} data-testid="dvsa-status-apikey">
                    {dvsaStatus.api_key_configured ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600">OAuth credentials configured</span>
                  <span className={`text-xs font-bold ${dvsaStatus.oauth_credentials_configured ? 'text-green-700' : 'text-red-700'}`} data-testid="dvsa-status-oauth">
                    {dvsaStatus.oauth_credentials_configured ? 'Yes' : 'No'}
                  </span>
                </div>
                {!dvsaStatus.oauth_credentials_configured && (
                  <div className="ml-2 text-xs text-slate-500 space-y-0.5">
                    <div>• client_id: {dvsaStatus.oauth_components?.client_id ? '✓' : '✗ missing'}</div>
                    <div>• client_secret: {dvsaStatus.oauth_components?.client_secret ? '✓' : '✗ missing'}</div>
                    <div>• tenant_id: {dvsaStatus.oauth_components?.tenant_id ? '✓' : '✗ missing'}</div>
                  </div>
                )}
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-slate-600">Ready for live</span>
                  <span className={`text-xs font-bold ${dvsaStatus.ready_for_live ? 'text-green-700' : 'text-amber-700'}`} data-testid="dvsa-status-ready">
                    {dvsaStatus.ready_for_live ? 'Yes' : 'Not yet — running in mock mode'}
                  </span>
                </div>
                <div className="p-2 bg-slate-50 rounded">
                  <p className="text-xs text-slate-500 mb-0.5">API base</p>
                  <p className="text-xs font-mono text-slate-700 break-all">{dvsaStatus.api_base}</p>
                </div>
                <div className="p-2 bg-emerald-50 rounded">
                  <p className="text-xs text-emerald-700 font-semibold mb-1">Last successful LIVE lookup</p>
                  {dvsaStatus.last_successful_lookup ? (
                    <div className="text-xs text-slate-700" data-testid="dvsa-status-last-success">
                      <div><strong>{dvsaStatus.last_successful_lookup.vrm}</strong> · {new Date(dvsaStatus.last_successful_lookup.triggered_at).toLocaleString()}</div>
                      <div className="text-slate-500">by {dvsaStatus.last_successful_lookup.triggered_by_name || 'system'} · HTTP {dvsaStatus.last_successful_lookup.http_status}</div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic" data-testid="dvsa-status-last-success-none">No live lookup has succeeded yet</p>
                  )}
                </div>
                <div className="p-2 bg-rose-50 rounded">
                  <p className="text-xs text-rose-700 font-semibold mb-1">Last LIVE lookup error</p>
                  {dvsaStatus.last_live_lookup_error ? (
                    <div className="text-xs text-slate-700" data-testid="dvsa-status-last-error">
                      <div><strong>{dvsaStatus.last_live_lookup_error.vrm}</strong> · {new Date(dvsaStatus.last_live_lookup_error.triggered_at).toLocaleString()}</div>
                      <div className="text-slate-500">HTTP {dvsaStatus.last_live_lookup_error.http_status} · by {dvsaStatus.last_live_lookup_error.triggered_by_name || 'system'}</div>
                      {dvsaStatus.last_live_lookup_error.error && (
                        <div className="mt-1 p-1.5 bg-white border border-rose-200 rounded text-rose-700 break-all font-mono text-[10px]">{dvsaStatus.last_live_lookup_error.error}</div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic" data-testid="dvsa-status-last-error-none">No live lookup errors recorded</p>
                  )}
                </div>
              </div>
              {!dvsaStatus.ready_for_live && (
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  <p className="font-semibold mb-1">To enable live mode, set in <span className="font-mono">backend/.env</span>:</p>
                  <ul className="ml-3 list-disc space-y-0.5">
                    <li><span className="font-mono">DVSA_MODE=live</span></li>
                    <li><span className="font-mono">DVSA_API_KEY</span> (legacy tier) <em>or</em></li>
                    <li><span className="font-mono">DVSA_CLIENT_ID</span> + <span className="font-mono">DVSA_CLIENT_SECRET</span> + <span className="font-mono">DVSA_TENANT_ID</span> (OAuth2)</li>
                    <li><span className="font-mono">DVSA_API_BASE</span> (optional, defaults to gov.uk history endpoint)</li>
                  </ul>
                </div>
              )}
              <div className="flex justify-end mt-4">
                <Button onClick={() => setShowDvsaStatusModal(false)} variant="ghost" data-testid="dvsa-status-close">Close</Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ ESTIMATE SCOPE SELECTOR ═══ */}
        {jobReadiness && (jobReadiness.job_status?.pricing_scope_available || []).length > 0 && (
          <Card className="mb-3 border-2 border-indigo-300" data-testid="estimate-scope-card">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-sm text-indigo-800">Estimate Scope</h4>
                {jobReadiness.estimate_scope && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                    {SCOPE_LABELS[jobReadiness.estimate_scope] || jobReadiness.estimate_scope}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-3">Choose what this estimate covers. Determines content sent to the customer.</p>
              <div className="flex flex-wrap gap-2">
                {/* Render available scopes, plus full_mot_inclusive as locked button when blocked */}
                {(() => {
                  const availableScopes = jobReadiness.job_status?.pricing_scope_available || [];
                  const blocksFull = jobReadiness.job_status?.mot_blocks_full_estimate;
                  // Always include full_mot_inclusive when MOT workflow exists, so user has visibility
                  const shouldShowFull = blocksFull && !availableScopes.includes('full_mot_inclusive');
                  const scopesToRender = shouldShowFull ? [...availableScopes, 'full_mot_inclusive'] : availableScopes;
                  return scopesToRender.map(scope => {
                    const isSelected = jobReadiness.estimate_scope === scope;
                    const isFullBlocked = scope === 'full_mot_inclusive' && blocksFull;
                    return (
                      <button
                        key={scope}
                        type="button"
                        onClick={async () => {
                          if (isFullBlocked) {
                            // Open MOT pending warning modal instead of calling API
                            setShowMotPendingModal(true);
                            return;
                          }
                          try {
                            const jobId = data?.job?.id;
                            await axios.put(`${API}/jobs/${jobId}/estimate-scope`, { scope });
                            const rRes = await axios.get(`${API}/jobs/${jobId}/readiness`);
                            setJobReadiness(rRes.data);
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Failed to set scope');
                          }
                        }}
                        className={`text-xs px-3 py-1.5 rounded-full border-2 font-medium transition-all ${
                          isFullBlocked ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 cursor-pointer'
                          : isSelected ? 'border-indigo-500 bg-indigo-100 text-indigo-800'
                          : 'border-slate-200 text-slate-600 hover:border-indigo-300'
                        }`}
                        data-testid={`scope-${scope}`}
                        title={isFullBlocked ? 'MOT result not yet imported — click to review options' : ''}
                      >{isFullBlocked && <Lock className="w-3 h-3 inline mr-1" />}{SCOPE_LABELS[scope] || scope}</button>
                    );
                  });
                })()}
              </div>
              {jobReadiness.job_status?.mot_blocks_full_estimate && (
                <div className="mt-3 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Full MOT-inclusive estimate is blocked. Send a partial estimate while the MOT is pending, or import the MOT result to unlock the full scope.</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ═══ MOT PENDING WARNING MODAL ═══ */}
        {showMotPendingModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="mot-pending-modal">
            <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-amber-100 rounded-full p-2"><AlertTriangle className="w-6 h-6 text-amber-600" /></div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-slate-800">MOT Result Not Yet Imported</h3>
                  <p className="text-sm text-slate-600 mt-1">This job has an MOT intent, but the MOT result has not been imported into Yestimate yet. A full MOT-inclusive estimate cannot be sent until the MOT result is available.</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded p-3 mb-4 text-xs text-slate-700">
                <p className="font-semibold mb-1">Recommended actions:</p>
                <ul className="space-y-1 ml-4 list-disc">
                  <li>Send a <strong>partial estimate</strong> covering pre-MOT findings only.</li>
                  <li>Or import the MOT result first, then send a full inclusive estimate.</li>
                </ul>
              </div>
              <div>
                <Label className="text-xs">Override reason (required to send anyway)</Label>
                <Textarea
                  value={estimateScopeOverrideReason}
                  onChange={e => setEstimateScopeOverrideReason(e.target.value)}
                  placeholder="e.g. Customer requested combined estimate before MOT booking..."
                  className="mt-1 text-sm"
                  rows={2}
                  data-testid="mot-override-reason"
                />
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <Button
                  onClick={async () => {
                    try {
                      const jobId = data?.job?.id;
                      await axios.put(`${API}/jobs/${jobId}/estimate-scope`, { scope: 'partial_mot_pending' });
                      const rRes = await axios.get(`${API}/jobs/${jobId}/readiness`);
                      setJobReadiness(rRes.data);
                      setShowMotPendingModal(false);
                      // Continue with normal send flow (partial scope is allowed)
                      const gateResult = await checkSendGate();
                      if (gateResult.can_send) {
                        try { await axios.post(`${API}/jobs/${jobId}/estimate-send`, { scope: 'partial_mot_pending' }); } catch (e) {}
                        navigate(`/app/foh/yestimate/${yestimateId}/preview`);
                      } else {
                        setSendGateData(gateResult);
                        setShowSendGateModal(true);
                      }
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed to set partial scope');
                    }
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="send-partial-btn"
                >Send Partial Estimate (MOT Pending)</Button>
                <Button
                  variant="outline"
                  disabled={!estimateScopeOverrideReason.trim()}
                  onClick={async () => {
                    try {
                      const jobId = data?.job?.id;
                      await axios.put(`${API}/jobs/${jobId}/estimate-scope`, {
                        scope: 'partial_mot_pending',  // backend blocks full while pending; record override
                        override_reason: estimateScopeOverrideReason.trim(),
                      });
                      try {
                        await axios.post(`${API}/jobs/${jobId}/estimate-send`, {
                          scope: 'partial_mot_pending',
                          override_reason: estimateScopeOverrideReason.trim(),
                        });
                      } catch (e) {}
                      const rRes = await axios.get(`${API}/jobs/${jobId}/readiness`);
                      setJobReadiness(rRes.data);
                      setShowMotPendingModal(false);
                      setEstimateScopeOverrideReason("");
                      navigate(`/app/foh/yestimate/${yestimateId}/preview`);
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed to send with override');
                    }
                  }}
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="send-with-override-btn"
                >Send Anyway (Override + Audit)</Button>
                <Button
                  variant="ghost"
                  onClick={() => { setShowMotPendingModal(false); setEstimateScopeOverrideReason(""); }}
                  data-testid="mot-modal-cancel-btn"
                >Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* DIAGNOSTIC PRICING REQUIRED ALERT */}
        {diagnosticInfo && !hasDiagnosticJobGroup && (
          <Card className="mb-6 border-2 border-red-400 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-8 h-8 text-red-600 mt-1" />
                <div className="flex-1">
                  <h3 className="font-bold text-red-800 text-lg">Diagnostic Rectification - PRICING REQUIRED</h3>
                  <p className="text-red-700 mt-1 mb-3">
                    A diagnostic inspection was completed. The rectification must be priced as a RED item before the SI Email can be generated.
                  </p>
                  <div className="bg-white rounded p-3 mb-3 border border-red-200">
                    <p className="text-xs font-semibold text-red-600 uppercase mb-1">Diagnostic Conclusion:</p>
                    <p className="text-slate-700">{diagnosticInfo.diagnostic_conclusion}</p>
                  </div>
                  <Button 
                    className="bg-red-600 hover:bg-red-700"
                    onClick={createDiagnosticJobGroup}
                    data-testid="add-diagnostic-job-btn"
                  >
                    Add Diagnostic Rectification to Pricing
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* SERVICE RECOMMENDATION ENGINE - V3 (NEW STRUCTURED RECOMMENDATION) */}
        {yestimateId && useNewRecommendationEngine && (
          <div className="mb-4">
            <ServiceRecommendationPanel
              yestimateId={yestimateId}
              existingJobGroups={jobGroups}
              addIntervalItemAsJob={(categoryId, item) => addIntervalItemAsJob(categoryId, item)}
              // Feb 2026 — Service Builder defaults to COLLAPSED on
              // every job. FOH clicks the header to expand when they
              // want to price a service interval. No caller-side
              // override anymore — the panel component owns the
              // default and exposes the toggle inline.
              onRefresh={() => {
                // Refresh interval data
                refreshIntervals();
              }}
            />
            {/* Toggle to legacy view */}
            <button
              onClick={() => setUseNewRecommendationEngine(false)}
              className="mt-2 text-xs text-slate-400 hover:text-slate-600 underline"
            >
              Switch to legacy interval view
            </button>
          </div>
        )}

        {/* SERVICE INTERVAL MAINTENANCE ITEMS - V2 (COMPACT) - Legacy view */}
        {(!useNewRecommendationEngine) && (dueIntervals.length > 0 || dueSoonIntervals.length > 0 || suppressedIntervals.length > 0) && (
          <Card className="mb-4 border border-blue-200 bg-blue-50/50">
            <CardHeader className="py-2 px-4">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-blue-800 text-base">
                  <Clock className="w-4 h-4" />
                  Service Intervals
                  {dueIntervals.length > 0 && (
                    <Badge className="bg-red-500 text-white text-xs">{dueIntervals.length} Due</Badge>
                  )}
                  {dueSoonIntervals.length > 0 && (
                    <Badge className="bg-amber-500 text-white text-xs">{dueSoonIntervals.length} Soon</Badge>
                  )}
                  {/* Compact history badges inline */}
                  <span className="text-xs text-slate-500 font-normal ml-2">
                    ({historySummary.confirmed_count} confirmed, {historySummary.partial_count} unverified)
                  </span>
                </CardTitle>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowHistoryPanel(!showHistoryPanel)}
                    className="text-purple-600 h-7 px-2 text-xs"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    History
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowIntervalPanel(!showIntervalPanel)}
                    className="h-8 w-8 p-0 hover:bg-blue-100 rounded-full"
                    data-testid="service-intervals-toggle"
                  >
                    {showIntervalPanel ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            {/* Service History Panel */}
            {showHistoryPanel && (
              <CardContent className="border-t border-blue-200 bg-purple-50 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-purple-800">Vehicle Service History</h4>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-blue-600 border-blue-300"
                      onClick={() => setShowImportPanel(!showImportPanel)}
                    >
                      <Upload className="w-4 h-4 mr-1" /> {showImportPanel ? 'Hide' : 'Paste'} Import
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setHistoryModal({ show: true, item: null, mode: 'add' })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Record
                    </Button>
                  </div>
                </div>
                
                {/* SERVICE HISTORY PASTE IMPORT PANEL */}
                {showImportPanel && (
                  <div className="bg-white border-2 border-blue-300 rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h5 className="font-semibold text-blue-800 flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Import from Garage Hive
                      </h5>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={importGroupByVisit}
                            onChange={(e) => setImportGroupByVisit(e.target.checked)}
                            className="w-3 h-3"
                          />
                          Group by Visit
                        </label>
                      </div>
                    </div>
                    
                    {/* Paste Area */}
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">
                        Paste service history from Garage Hive:
                      </label>
                      <textarea
                        value={importPasteText}
                        onChange={(e) => setImportPasteText(e.target.value)}
                        placeholder="Paste service history text here...

Example:
05/09/2025   Service Labour                    64,693 miles
05/09/2025   Oil Filter                        64,693 miles
05/09/2025   Engine Oil                        64,693 miles
12/03/2024   Brake Fluid Change                52,100 miles"
                        className="w-full h-32 p-3 border rounded font-mono text-xs resize-none"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-slate-400">
                          {importPasteText.split('\n').filter(l => l.trim()).length} lines
                        </span>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={clearImportPanel}
                            disabled={!importPasteText && parsedImportItems.length === 0}
                          >
                            Clear
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={parseServiceHistoryImport}
                            disabled={!importPasteText.trim() || parsingImport}
                          >
                            {parsingImport ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Parse'}
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Service Visits (Grouped View) */}
                    {importGroupByVisit && serviceVisits.length > 0 && (
                      <div className="space-y-3">
                        <h6 className="text-sm font-medium text-slate-700">Service Visits Detected:</h6>
                        {serviceVisits.map((visit, visitIdx) => (
                          <div 
                            key={visitIdx} 
                            className={`p-3 rounded border ${
                              visit.is_group ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {visit.visit_date ? (
                                    <span className="font-medium text-blue-800">
                                      Service Visit – {visit.visit_date}
                                    </span>
                                  ) : (
                                    <span className="font-medium text-amber-700">
                                      Items without date
                                    </span>
                                  )}
                                  {visit.visit_mileage && (
                                    <Badge className="bg-slate-200 text-slate-700 text-xs">
                                      {visit.visit_mileage.toLocaleString()} miles
                                    </Badge>
                                  )}
                                  <Badge className={`text-xs ${
                                    visit.is_group ? 'bg-blue-500 text-white' : 'bg-slate-400 text-white'
                                  }`}>
                                    {visit.item_count} items
                                  </Badge>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {visit.items.map((item, itemIdx) => (
                                    <span 
                                      key={itemIdx}
                                      className="text-xs px-2 py-0.5 bg-white rounded border text-slate-600"
                                    >
                                      {item.item_label || item.matched_phrase}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              {visit.is_group && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-300"
                                  onClick={() => confirmServiceVisit(visitIdx)}
                                >
                                  <Check className="w-4 h-4 mr-1" /> Confirm All
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Parsed Items Table */}
                    {parsedImportItems.length > 0 && (
                      <div className="space-y-2">
                        <h6 className="text-sm font-medium text-slate-700">
                          Parsed Items ({parsedImportItems.filter(i => i.include !== false).length} selected):
                        </h6>
                        <div className="max-h-64 overflow-y-auto border rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-100 sticky top-0">
                              <tr>
                                <th className="p-2 text-left w-8">
                                  <input
                                    type="checkbox"
                                    checked={parsedImportItems.every(i => i.include !== false)}
                                    onChange={(e) => {
                                      const updated = parsedImportItems.map(i => ({...i, include: e.target.checked}));
                                      setParsedImportItems(updated);
                                    }}
                                    className="w-3 h-3"
                                  />
                                </th>
                                <th className="p-2 text-left">Service Item</th>
                                <th className="p-2 text-left">Matched</th>
                                <th className="p-2 text-left">Date</th>
                                <th className="p-2 text-left">Mileage</th>
                                <th className="p-2 text-left">State</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parsedImportItems.map((item, idx) => (
                                <tr 
                                  key={idx} 
                                  className={`border-t ${
                                    item.include === false ? 'opacity-50 bg-slate-50' : 
                                    item.has_existing_history && !item.is_newer ? 'bg-amber-50' : ''
                                  }`}
                                >
                                  <td className="p-2">
                                    <input
                                      type="checkbox"
                                      checked={item.include !== false}
                                      onChange={() => toggleImportItemInclude(idx)}
                                      className="w-3 h-3"
                                    />
                                  </td>
                                  <td className="p-2 font-medium">{item.item_label}</td>
                                  <td className="p-2 text-slate-500 italic">"{item.matched_phrase}"</td>
                                  <td className="p-2">
                                    <input
                                      type="date"
                                      value={item.detected_date || ''}
                                      onChange={(e) => updateImportItemField(idx, 'detected_date', e.target.value)}
                                      className="w-28 px-1 py-0.5 border rounded text-xs"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input
                                      type="number"
                                      value={item.detected_mileage || ''}
                                      onChange={(e) => updateImportItemField(idx, 'detected_mileage', parseInt(e.target.value) || null)}
                                      className="w-20 px-1 py-0.5 border rounded text-xs"
                                      placeholder="miles"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                      item.confidence_state === 'confirmed' ? 'bg-green-100 text-green-800' :
                                      item.confidence_state === 'partial' ? 'bg-amber-100 text-amber-800' :
                                      'bg-slate-100 text-slate-600'
                                    }`}>
                                      {item.confidence_state}
                                    </span>
                                    {item.has_existing_history && (
                                      <span className={`ml-1 text-xs ${
                                        item.is_newer ? 'text-green-600' : 'text-amber-600'
                                      }`}>
                                        {item.is_newer ? '(newer)' : '(existing newer)'}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Import Actions */}
                        <div className="flex justify-end gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={saveServiceHistoryImport}
                            disabled={savingImport || parsedImportItems.filter(i => i.include !== false).length === 0}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {savingImport ? (
                              <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving...</>
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Save to Vehicle History ({parsedImportItems.filter(i => i.include !== false).length} items)
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Empty State */}
                    {!parsingImport && parsedImportItems.length === 0 && importPasteText.trim() && (
                      <p className="text-sm text-slate-500 italic text-center py-2">
                        Click "Parse" to analyze the pasted text
                      </p>
                    )}
                  </div>
                )}
                
                {serviceHistory.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">
                    No confirmed service history available for this vehicle.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {serviceHistory.map((record, idx) => {
                      const badge = getHistoryStateBadge(record.confidence_state);
                      return (
                        <div 
                          key={record.id || idx}
                          className={`p-3 rounded border ${badge.bg} border-opacity-50`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{record.item_label || record.item_id}</span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${badge.bg} ${badge.text}`}>
                                  {badge.icon} {badge.label}
                                </span>
                              </div>
                              {record.confidence_state === 'confirmed' && record.completed_date && (
                                <p className="text-xs text-slate-600 mt-1">
                                  Previously completed on {record.completed_date.slice(0, 10)} 
                                  {record.completed_mileage && ` at ${record.completed_mileage.toLocaleString()} miles`}
                                </p>
                              )}
                              {record.confidence_state === 'partial' && (
                                <p className="text-xs text-amber-700 mt-1 italic">
                                  Customer has indicated this may have been completed previously; no documentation has been provided.
                                </p>
                              )}
                              {record.confidence_state === 'previously_declined' && (
                                <p className="text-xs text-red-700 mt-1">
                                  Previously advised and declined
                                  {record.declined_at && ` on ${record.declined_at.slice(0, 10)}`}
                                  {record.decline_reason && ` - ${record.decline_reason}`}
                                </p>
                              )}
                              {record.source && (
                                <p className="text-xs text-slate-400">Source: {record.source.replace(/_/g, ' ')}</p>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setHistoryModal({ show: true, item: record, mode: 'edit' })}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            )}
            
            {showIntervalPanel && (
              <CardContent className="space-y-4">
                {/* DUE NOW SECTION */}
                {dueIntervals.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Due Now ({dueIntervals.length})
                    </h4>
                    {dueIntervals.map(item => {
                      const existingItem = intervalItems.find(i => i.item_id === item.item_id);
                      const isAdded = existingItem?.status !== "declined" && existingItem;
                      const isDeclined = existingItem?.status === "declined";
                      const historyRecord = serviceHistory.find(h => h.item_id === item.item_id);
                      const historyBadge = item.history_state ? getHistoryStateBadge(item.history_state) : null;
                      
                      return (
                        <div 
                          key={item.item_id}
                          className={`p-3 rounded border ${
                            isDeclined ? "bg-slate-100 border-slate-300" :
                            isAdded ? "bg-green-50 border-green-300" :
                            item.is_overdue ? "bg-red-50 border-red-300" :
                            "bg-amber-50 border-amber-300"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{item.label}</span>
                                {item.pricing_required && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">Mandatory</Badge>
                                )}
                                {item.is_overdue && (
                                  <Badge className="bg-red-500 text-white text-xs">OVERDUE</Badge>
                                )}
                                {item.due_state === "due_now" && !item.is_overdue && (
                                  <Badge className="bg-amber-500 text-white text-xs">DUE</Badge>
                                )}
                                {isAdded && (
                                  <Badge className="bg-green-500 text-white text-xs">Added</Badge>
                                )}
                                {isDeclined && (
                                  <Badge className="bg-slate-500 text-white text-xs">Declined</Badge>
                                )}
                                {/* V2: Interval source badge */}
                                {item.interval_source === "default_by_fuel" && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">Fuel-specific</Badge>
                                )}
                                {historyBadge && (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${historyBadge.bg} ${historyBadge.text}`}>
                                    {historyBadge.icon} {historyBadge.label}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                              {/* V2: Enhanced interval display */}
                              <p className="text-xs text-slate-400">
                                Interval: {item.interval_months_resolved || item.interval_months} months
                                {(item.interval_miles_resolved || item.interval_miles) && ` / ${(item.interval_miles_resolved || item.interval_miles).toLocaleString()} miles`}
                                {item.overdue_months_tolerance > 0 && ` (tolerance: +${item.overdue_months_tolerance}mo)`}
                              </p>
                              {/* V2: Show time/miles since */}
                              {(item.months_since || item.miles_since) && (
                                <p className="text-xs text-slate-500">
                                  Since last service: {item.months_since ? `${Math.round(item.months_since)} months` : ''}
                                  {item.months_since && item.miles_since ? ' / ' : ''}
                                  {item.miles_since ? `${item.miles_since.toLocaleString()} miles` : ''}
                                </p>
                              )}
                              {item.history_note && (
                                <p className={`text-xs mt-1 italic ${
                                  item.history_state === 'confirmed' ? 'text-green-700' :
                                  item.history_state === 'partial' ? 'text-amber-700' :
                                  item.history_state === 'previously_declined' ? 'text-red-700' :
                                  'text-slate-500'
                                }`}>
                                  {item.history_note}
                                </p>
                              )}
                              {isDeclined && existingItem?.decline_record && (
                                <p className="text-xs text-slate-500 mt-1 italic">
                                  Declined: {existingItem.decline_record.reason_label || existingItem.decline_record.reason}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex gap-2 items-center">
                              <Button 
                                size="sm"
                                variant="ghost"
                                className="text-purple-600"
                                title="Edit service history"
                                onClick={() => setHistoryModal({ 
                                  show: true, 
                                  item: historyRecord || { item_id: item.item_id, item_label: item.label },
                                  mode: historyRecord ? 'edit' : 'add'
                                })}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              
                              {!isAdded && !isDeclined && (
                                <>
                                  <Button 
                                    size="sm"
                                    onClick={() => addIntervalItemAsJob(item.category_id, item)}
                                  >
                                    Add to Pricing
                                  </Button>
                                  {item.decline_reason_required && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-slate-600"
                                      onClick={() => setDeclineModal({ show: true, item })}
                                    >
                                      Decline
                                    </Button>
                                  )}
                                </>
                              )}
                              
                              {isAdded && existingItem && (
                                <div className="text-right">
                                  <Badge className="bg-green-600 text-white">Added</Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* V2: DUE SOON SECTION */}
                {dueSoonIntervals.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Due Soon ({dueSoonIntervals.length})
                      {intervalV2Config && (
                        <span className="text-xs font-normal text-slate-500">
                          (within {intervalV2Config.due_soon_months} months / {intervalV2Config.due_soon_miles?.toLocaleString()} miles)
                        </span>
                      )}
                    </h4>
                    {dueSoonIntervals.map(item => {
                      const existingItem = intervalItems.find(i => i.item_id === item.item_id);
                      const isAdded = existingItem?.status !== "declined" && existingItem;
                      
                      return (
                        <div 
                          key={item.item_id}
                          className={`p-3 rounded border ${
                            isAdded ? "bg-green-50 border-green-300" : "bg-yellow-50 border-yellow-300"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{item.label}</span>
                                <Badge className="bg-yellow-500 text-white text-xs">DUE SOON</Badge>
                                {item.interval_source === "default_by_fuel" && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">Fuel-specific</Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                              <p className="text-xs text-slate-400">
                                Interval: {item.interval_months_resolved || item.interval_months} months
                                {(item.interval_miles_resolved || item.interval_miles) && ` / ${(item.interval_miles_resolved || item.interval_miles).toLocaleString()} miles`}
                              </p>
                              {item.due_reasons && item.due_reasons.length > 0 && (
                                <p className="text-xs text-amber-600 mt-1">
                                  {item.due_reasons[0]}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex gap-2 items-center">
                              {!isAdded && (
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => addIntervalItemAsJob(item.category_id, item)}
                                >
                                  Add to Pricing
                                </Button>
                              )}
                              {isAdded && existingItem && (
                                <div className="text-right">
                                  <Badge className="bg-green-600 text-white">Added</Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* V2: SUPPRESSED SECTION */}
                {suppressedIntervals.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-slate-500 flex items-center gap-2">
                        <EyeOff className="w-4 h-4" />
                        Suppressed ({suppressedIntervals.length})
                      </h4>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowSuppressedItems(!showSuppressedItems)}
                        className="text-slate-500"
                      >
                        {showSuppressedItems ? "Hide" : "Show"}
                      </Button>
                    </div>
                    {showSuppressedItems && (
                      <div className="space-y-2 opacity-60">
                        {suppressedIntervals.map(item => (
                          <div 
                            key={item.item_id}
                            className="p-3 rounded border bg-slate-50 border-slate-200"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-600">{item.label}</span>
                                  <Badge className="bg-slate-400 text-white text-xs">Suppressed</Badge>
                                </div>
                                <p className="text-xs text-slate-400 mt-1">
                                  Recently declined - suppressed for {intervalV2Config?.decline_suppression_months || 6} months
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show message if all items filtered out due to confirmed history */}
                {dueIntervals.length === 0 && dueSoonIntervals.length === 0 && historySummary.confirmed_count > 0 && (
                  <p className="text-sm text-green-700 italic text-center py-4">
                    All service items are up to date based on confirmed service history.
                  </p>
                )}
                
                {/* Interval Items Summary */}
                {intervalItems.filter(i => i.status !== "declined").length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-slate-700">Interval Maintenance Total:</span>
                      <span className="font-bold text-lg">£{intervalItemsTotal.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      * Priced based on age/mileage maintenance interval
                    </p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}
        
        {/* Toggle back to new recommendation view (when in legacy mode) */}
        {!useNewRecommendationEngine && (dueIntervals.length > 0 || dueSoonIntervals.length > 0 || suppressedIntervals.length > 0) && (
          <button
            onClick={() => setUseNewRecommendationEngine(true)}
            className="mt-2 mb-4 text-xs text-slate-400 hover:text-slate-600 underline"
          >
            Switch to new recommendation view
          </button>
        )}

        {/* Service History Modal */}
        {historyModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="text-purple-800">
                  {historyModal.mode === 'edit' ? 'Edit' : 'Add'} Service History
                </CardTitle>
                <p className="text-sm text-slate-600">
                  {historyModal.item?.item_label || historyModal.item?.item_id || 'Select a service item'}
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const itemId = historyModal.item?.item_id || formData.get("item_id");
                  
                  if (!itemId) {
                    alert("Please select a service item");
                    return;
                  }
                  
                  saveServiceHistory(itemId, {
                    confidence_state: formData.get("confidence_state"),
                    completed_date: formData.get("completed_date") || null,
                    completed_mileage: formData.get("completed_mileage") || null,
                    evidence_description: formData.get("evidence_description") || null,
                    source: formData.get("source") || null,
                    notes: formData.get("notes") || null
                  });
                }}>
                  <div className="space-y-4">
                    {!historyModal.item?.item_id && (
                      <div>
                        <Label>Service Item *</Label>
                        <select name="item_id" className="w-full mt-1 p-2 border rounded" required>
                          <option value="">Select item...</option>
                          <option value="timing_belt">Timing Belt / Wet Belt</option>
                          <option value="timing_chain">Timing Chain Service</option>
                          <option value="transmission_service_auto">Automatic Transmission Service</option>
                          <option value="transmission_service_manual">Manual Gearbox Oil</option>
                          <option value="engine_oil_filter">Engine Oil & Filter Service</option>
                          <option value="brake_fluid">Brake Fluid Change</option>
                          <option value="engine_coolant">Engine Coolant / Antifreeze</option>
                          <option value="auxiliary_belt">Auxiliary / Serpentine Belt</option>
                          <option value="air_filter">Air Filter</option>
                          <option value="cabin_filter">Cabin / Pollen Filter</option>
                          <option value="fuel_filter">Fuel Filter</option>
                          <option value="spark_plugs">Spark Plugs</option>
                          <option value="wiper_blades">Wiper Blades</option>
                        </select>
                      </div>
                    )}
                    
                    <div>
                      <Label>Confidence State *</Label>
                      <select 
                        name="confidence_state" 
                        className="w-full mt-1 p-2 border rounded"
                        defaultValue={historyModal.item?.confidence_state || "unknown"}
                        required
                      >
                        <option value="unknown">❓ Unknown - No information available</option>
                        <option value="partial">⚠️ Partial/Unverified - Customer indicates, no documentation</option>
                        <option value="confirmed">✅ Confirmed - Evidence exists (invoice, record, job)</option>
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        Only "Confirmed" history with evidence will reset service intervals
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Completed Date</Label>
                        <Input 
                          type="date" 
                          name="completed_date"
                          defaultValue={historyModal.item?.completed_date?.slice(0, 10) || ""}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Mileage at Service</Label>
                        <Input 
                          type="number" 
                          name="completed_mileage"
                          placeholder="e.g. 45000"
                          defaultValue={historyModal.item?.completed_mileage || ""}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Evidence Source</Label>
                      <select 
                        name="source" 
                        className="w-full mt-1 p-2 border rounded"
                        defaultValue={historyModal.item?.source || ""}
                      >
                        <option value="">Select source...</option>
                        <option value="internal_job">Internal Job Record</option>
                        <option value="external_invoice">External Invoice / Receipt</option>
                        <option value="digital_service_record">Digital Service Record</option>
                        <option value="service_book">Physical Service Book</option>
                        <option value="customer_statement">Customer Statement (verbal)</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label>Evidence Description</Label>
                      <Textarea 
                        name="evidence_description"
                        placeholder="Describe the evidence (e.g., 'Invoice from XYZ Garage dated 15/01/2024')"
                        defaultValue={historyModal.item?.evidence_description || ""}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>Additional Notes</Label>
                      <Textarea 
                        name="notes"
                        placeholder="Any additional context..."
                        defaultValue={historyModal.item?.notes || ""}
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="bg-purple-50 p-3 rounded text-sm text-purple-800">
                      <strong>Important:</strong> Only evidence-based confirmations (invoices, digital records, internal job records) will reset service intervals. Verbal confirmations without documentation will be recorded as "Partial/Unverified" and will not affect advising logic.
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <Button type="submit" className="flex-1">
                      Save History Record
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setHistoryModal({ show: false, item: null, mode: 'add' })}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Decline Reason Modal */}
        {declineModal.show && declineModal.item && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-amber-800">Decline Maintenance Item</CardTitle>
                <p className="text-sm text-slate-600">
                  Record why the customer is declining: <strong>{declineModal.item.label}</strong>
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const reason = formData.get("reason");
                  const notes = formData.get("notes");
                  
                  if (!reason) {
                    alert("Please select a decline reason");
                    return;
                  }
                  
                  // First add the item, then decline it
                  axios.post(`${API}/foh/yestimate/${yestimateId}/interval-item`, {
                    item_id: declineModal.item.item_id,
                    category_id: declineModal.item.category_id,
                    price: 0,
                    is_advice_only: false
                  }).then(res => {
                    return declineIntervalItem(res.data.item.id, reason, notes);
                  }).catch(err => {
                    alert(err.response?.data?.detail || "Failed to record decline");
                  });
                }}>
                  <div className="space-y-4">
                    <div>
                      <Label>Reason for Decline *</Label>
                      <select 
                        name="reason" 
                        className="w-full mt-1 p-2 border rounded"
                        required
                      >
                        <option value="">Select a reason...</option>
                        {declineReasons.map(r => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Additional Notes</Label>
                      <Textarea 
                        name="notes" 
                        placeholder="Any additional context..."
                        className="mt-1"
                      />
                    </div>
                    <div className="bg-amber-50 p-3 rounded text-sm text-amber-800">
                      <strong>Note:</strong> This decline will be recorded in the vehicle's history for future reference and liability protection.
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setDeclineModal({ show: false, item: null })}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                      Record Decline
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* MOT Panel removed — consolidated into the unified "MOT (DVSA)" card
            that appears with the readiness/intervals section above */}

        <div className="space-y-4">
            {/* Quick Actions Bar */}
            <Card>
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-slate-500">Quick Actions:</span>
                    <Button
                      onClick={startWizard}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                      size="sm"
                      data-testid="start-wizard-btn"
                    >
                      <Wrench className="w-4 h-4 mr-2" />
                      Smart Wizard
                    </Button>
                    <Button
                      onClick={quickAutoQuote}
                      disabled={quickPricingLoading}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700"
                      size="sm"
                      data-testid="quick-auto-quote-btn"
                    >
                      {quickPricingLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      {quickPricingLoading ? "Processing..." : "Auto-Quote"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openPackagesModal}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="job-packages-btn"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Packages
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPriceBookModal(true)}
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      data-testid="price-book-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Price Book
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ═══════════════════════════════════════════════════════════════════
                PROGRESS BAR & READY-TO-SEND CHECKLIST
                Shows FOH at-a-glance status of quote completion
            ═══════════════════════════════════════════════════════════════════ */}
            {jobGroups.length > 0 && (() => {
              // Calculate progress metrics
              const totalJobs = jobGroups.length;
              const redJobs = jobGroups.filter(jg => jg.status === 'red' || jg.vi_status === 'red');
              const amberJobs = jobGroups.filter(jg => (jg.status === 'amber' || jg.vi_status === 'amber') && jg.status !== 'red' && jg.vi_status !== 'red');
              const completedJobs = jobGroups.filter(jg => jg.pricing_confirmed);
              const unpricedJobs = jobGroups.filter(jg => !jg.pricing_confirmed && (jg.labour_lines?.length === 0 && jg.parts_lines?.length === 0));
              const notQuotedJobs = jobGroups.filter(jg => jg.not_quoted_reason);
              
              // Calculate totals
              const jobsWithPricing = jobGroups.filter(jg => jg.labour_lines?.length > 0 || jg.parts_lines?.length > 0 || jg.not_quoted_reason);
              const progressPercent = totalJobs > 0 ? Math.round((jobsWithPricing.length / totalJobs) * 100) : 0;
              
              // Checklist items
              const hasCustomerName = customerName && customerName.trim().length > 0;
              // Iter189 — Treat BLOCKED_TEST / UNKNOWN_CAUSE emissions
              // JGs as zero-charge informational items so they don't
              // block "Ready to send to customer". These JGs were
              // suppressed by the staged-routing engine and genuinely
              // have no parts/labour to price.
              const isStagedRoutingInfoOnly = (jg) =>
                jg.emissions_suppress_parts === true
                && ['BLOCKED_TEST', 'UNKNOWN_CAUSE'].includes(jg.emissions_state);
              const allRedItemsPriced = redJobs.every(jg =>
                jg.pricing_confirmed || jg.not_quoted_reason || isStagedRoutingInfoOnly(jg)
              );
              const hasDangerousUnpriced = jobGroups.some(jg =>
                jg.is_dangerous_defect
                && !jg.pricing_confirmed
                && !jg.not_quoted_reason
                && !isStagedRoutingInfoOnly(jg)
              );
              const hasZeroPriceJobs = jobGroups.some(jg => {
                if (jg.not_quoted_reason) return false;
                const labourTotal = (jg.labour_lines || []).reduce((sum, l) => sum + (l.hours * l.rate), 0);
                const partsTotal = (jg.parts_lines || []).reduce((sum, p) => sum + (p.quantity * p.unit_price), 0);
                return (jg.labour_lines?.length > 0 || jg.parts_lines?.length > 0) && (labourTotal + partsTotal) === 0;
              });
              
              const isReadyToSend = hasCustomerName && allRedItemsPriced && !hasDangerousUnpriced && completedJobs.length > 0;
              
              return (
                <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                  <CardContent className="py-4 px-5">
                    {/* Progress Section */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700">
                            Quote Progress
                          </span>
                          <span className="text-sm text-slate-500">
                            {jobsWithPricing.length} of {totalJobs} jobs addressed
                          </span>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden flex">
                          {/* Red segment */}
                          {redJobs.length > 0 && (
                            <div 
                              className="h-full bg-red-500 transition-all duration-300"
                              style={{ width: `${(redJobs.filter(jg => jg.pricing_confirmed || jg.not_quoted_reason).length / totalJobs) * 100}%` }}
                              title={`${redJobs.filter(jg => jg.pricing_confirmed || jg.not_quoted_reason).length} RED items complete`}
                            />
                          )}
                          {/* Amber segment */}
                          {amberJobs.length > 0 && (
                            <div 
                              className="h-full bg-amber-500 transition-all duration-300"
                              style={{ width: `${(amberJobs.filter(jg => jg.pricing_confirmed || jg.not_quoted_reason).length / totalJobs) * 100}%` }}
                              title={`${amberJobs.filter(jg => jg.pricing_confirmed || jg.not_quoted_reason).length} AMBER items complete`}
                            />
                          )}
                          {/* Other completed segment */}
                          {(() => {
                            const otherCompleted = completedJobs.filter(jg => 
                              jg.status !== 'red' && jg.vi_status !== 'red' && 
                              jg.status !== 'amber' && jg.vi_status !== 'amber'
                            ).length;
                            return otherCompleted > 0 ? (
                              <div 
                                className="h-full bg-green-500 transition-all duration-300"
                                style={{ width: `${(otherCompleted / totalJobs) * 100}%` }}
                                title={`${otherCompleted} other items complete`}
                              />
                            ) : null;
                          })()}
                        </div>
                        
                        {/* Legend */}
                        <div className="flex items-center gap-4 mt-2 text-xs">
                          {redJobs.length > 0 && (
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-red-500"></span>
                              RED: {redJobs.filter(jg => jg.pricing_confirmed || jg.not_quoted_reason).length}/{redJobs.length}
                            </span>
                          )}
                          {amberJobs.length > 0 && (
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                              AMBER: {amberJobs.filter(jg => jg.pricing_confirmed || jg.not_quoted_reason).length}/{amberJobs.length}
                            </span>
                          )}
                          {completedJobs.length > 0 && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Check className="w-3 h-3" />
                              {completedJobs.length} confirmed
                            </span>
                          )}
                          {notQuotedJobs.length > 0 && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <XCircle className="w-3 h-3" />
                              {notQuotedJobs.length} not quoted
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Ready Status Badge */}
                      <div className={`px-4 py-2 rounded-lg text-center min-w-[120px] ${
                        isReadyToSend 
                          ? 'bg-green-100 border border-green-300' 
                          : 'bg-amber-50 border border-amber-200'
                      }`}>
                        {isReadyToSend ? (
                          <>
                            <Check className="w-5 h-5 mx-auto text-green-600 mb-1" />
                            <span className="text-xs font-medium text-green-700">Ready to Send</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                            <span className="text-xs font-medium text-amber-700">In Progress</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Checklist Section */}
                    <div className="border-t border-slate-200 pt-3">
                      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        {/* Customer Name */}
                        <div className={`flex items-center gap-2 ${hasCustomerName ? 'text-green-600' : 'text-slate-500'}`}>
                          {hasCustomerName ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded border-2 border-slate-300" />}
                          <span>Customer name</span>
                        </div>
                        
                        {/* RED items priced */}
                        {redJobs.length > 0 && (
                          <div className={`flex items-center gap-2 ${allRedItemsPriced ? 'text-green-600' : 'text-red-600 font-medium'}`}>
                            {allRedItemsPriced ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            <span>All RED items addressed</span>
                          </div>
                        )}
                        
                        {/* Dangerous items */}
                        {hasDangerousUnpriced && (
                          <div className="flex items-center gap-2 text-red-600 font-medium animate-pulse">
                            <AlertCircle className="w-4 h-4" />
                            <span>⚠️ Dangerous defect needs pricing</span>
                          </div>
                        )}
                        
                        {/* Zero price warning */}
                        {hasZeroPriceJobs && (
                          <div className="flex items-center gap-2 text-amber-600">
                            <AlertCircle className="w-4 h-4" />
                            <span>Some jobs have £0 total</span>
                          </div>
                        )}
                        
                        {/* Unpriced items count */}
                        {unpricedJobs.length > 0 && (
                          <div className="flex items-center gap-2 text-slate-500">
                            <div className="w-4 h-4 rounded border-2 border-slate-300" />
                            <span>{unpricedJobs.length} job{unpricedJobs.length !== 1 ? 's' : ''} need pricing</span>
                          </div>
                        )}
                        
                        {/* All complete */}
                        {completedJobs.length === totalJobs && (
                          <div className="flex items-center gap-2 text-green-600 font-medium">
                            <Check className="w-4 h-4" />
                            <span>All jobs confirmed ✓</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* ═══════════════════════════════════════════════════════════════════
                QUOTE READINESS SUMMARY BAR (Replaces old PIE Warnings Panel)
                Lightweight summary showing estimate readiness status
            ═══════════════════════════════════════════════════════════════════ */}
            {estimateReadiness && (
              <QuoteReadinessSummary
                readinessData={estimateReadiness}
                onViewBlockers={() => setShowSendGateModal(true)}
                onJumpToJob={jumpToJobGroup}
                isLoading={integrityLoading}
              />
            )}

            {/* Phase-4: MCC-driven integrity warnings (alignment removed,
                pair partner missing, LED-unit-as-bulb, ADAS calibration
                missing, etc.). Reads from the yestimate GET payload. */}
            <MCCIntegrityBanner
              warnings={data?.mcc_integrity_warnings}
              jobGroups={jobGroups}
            />

            {/* Job Groups Header with Add Custom Job Button */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Job Groups ({jobGroups.length})
                {/* Collapse/Expand buttons */}
                <div className="flex items-center gap-1 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allCollapsed = {};
                      jobGroups.forEach(jg => { allCollapsed[jg.id] = false; });
                      setExpandedJobs(allCollapsed);
                    }}
                    className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
                    title="Collapse all job groups"
                  >
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Collapse All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allExpanded = {};
                      jobGroups.forEach(jg => { allExpanded[jg.id] = true; });
                      setExpandedJobs(allExpanded);
                    }}
                    className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
                    title="Expand all job groups"
                  >
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Expand All
                  </Button>
                </div>
              </h3>
              <div className="flex items-center gap-2">
                {jobGroups.some(jg => (jg.source || "").includes("dvsa_mot_result")) && (
                  <SendBackToTesterButton
                    yestimateId={yestimateId || (data?.yestimate || {}).id}
                  />
                )}
                {jobGroups.length > 0 && (
                  <QuickApplyAllCCNButton
                    yestimateId={yestimateId || (data?.yestimate || {}).id}
                    onApplied={async () => {
                      // Re-fetch yestimate so the new CCNs appear on every
                      // JG card immediately — auto-save would race the FE
                      // state otherwise.
                      const yid = yestimateId || (data?.yestimate || {}).id;
                      if (!yid) return;
                      try {
                        const r = await axios.get(`${API}/foh/yestimate/${yid}`);
                        if (r.data?.yestimate?.job_groups) {
                          setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                        }
                      } catch { /* non-blocking */ }
                    }}
                  />
                )}
                <AddRecommendationButton
                  yestimateId={yestimateId}
                  jobGroups={jobGroups}
                  onSaved={(rec) => {
                    // Optimistically merge or replace. Subtype dedup
                    // — only one alignment recommendation per
                    // yestimate.
                    setJobGroups((prev) => {
                      const filtered = prev.filter(
                        (jg) => jg.subtype !== "wheel_alignment",
                      );
                      return rec ? [...filtered, rec] : filtered;
                    });
                    setRecommendationRefreshKey((k) => k + 1);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddCustomJob}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  data-testid="add-custom-job-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Custom Job
                </Button>
              </div>
            </div>

            {/* Iter186 — Emissions Reasoning Engine panel. Renders the
                staged-routing card (BLOCKED_TEST / UNKNOWN_CAUSE /
                CONFIRMED_FAILURE) when the engine fires. Hidden when
                NOT_APPLICABLE. */}
            <EmissionsRoutingPanel routing={data?.emissions_routing} />

            {/* Iter184 — soft-suggest banner. Hidden when no triggers
                fire OR when alignment is already recommended. */}
            <AlignmentSuggestBanner
              yestimateId={yestimateId}
              refreshKey={recommendationRefreshKey}
              onAdded={async () => {
                // Bump the shared refresh key so the banner re-fetches
                // (will hide itself once already_present=true) and any
                // sibling chips refresh. Then re-load job_groups so
                // the newly-created alignment JG drops into the pricing
                // grid without a page reload.
                setRecommendationRefreshKey((k) => k + 1);
                const yid = yestimateId || (data?.yestimate || {}).id;
                if (!yid) return;
                try {
                  const r = await axios.get(`${API}/foh/yestimate/${yid}`);
                  if (r.data?.yestimate?.job_groups) {
                    setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                  }
                } catch { /* non-blocking */ }
              }}
            />

            {/* iter197aa — Repair Integrity Engine, Phase 1: charging
                system. Renders ONLY when the yestimate carries an
                alternator or starter motor JG. Component is a no-op
                otherwise. Lives ABOVE the JG list so FOH sees the
                Repair Integrity Status before pricing decisions. */}
            <RepairIntegrityCard
              yestimateId={yestimateId || (data?.yestimate || {}).id}
              refreshTrigger={recommendationRefreshKey}
            />

            {/* Phase-B Job Group Dedup — soft-match "Possible duplicate"
                banner. Hidden when no live suggestions; engine excludes
                dismissed pairs. Bumping recommendationRefreshKey forces
                a re-fetch (e.g. after a manual merge or unmerge). */}
            <DedupSuggestBanner
              yestimateId={yestimateId}
              refreshKey={recommendationRefreshKey}
              onChange={async () => {
                setRecommendationRefreshKey((k) => k + 1);
                try {
                  const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                  if (r.data?.yestimate?.job_groups) {
                    setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                  }
                } catch { /* non-blocking */ }
              }}
            />

            {/* Job Groups */}
            {jobGroups.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No jobs created yet</p>
                  <p className="text-sm mt-2">Click "Auto-Populate from VI" to generate jobs from Red/Amber findings, or use Job Packages</p>
                </CardContent>
              </Card>
            ) : (
              // Sort job groups by priority: DIAGNOSTIC > DANGEROUS > RED > AMBER > other
              // Secondary sort: maintain order consistent with left panel (Inspection Findings)
              (() => {
                // Build position map from left panel order (redItems sorted + amberItems)
                const sortedRedItems = [...redItems].sort((a, b) => {
                  const getPriority = (item) => {
                    const isDiag = item.item_key?.includes('warning') || item.item_key?.includes('dash_warning');
                    if (isDiag) return 0;
                    if (item.status === 'dangerous') return 1;
                    return 2;
                  };
                  return getPriority(a) - getPriority(b);
                });
                const leftPanelOrder = [...sortedRedItems, ...amberItems];
                const itemPositionMap = {};
                leftPanelOrder.forEach((item, idx) => {
                  itemPositionMap[item.id] = idx;
                });
                
                return [...jobGroups].sort((a, b) => {
                  // Strict category ordering to mirror the Left Panel:
                  //   MOT → DIAG → VI → SERVICE → ADDED RECS
                  // Buckets:
                  //   10–13: MOT (dangerous/major/amber/unclassified)
                  //   20   : DIAG (warning lights, fault codes, diagnostic source)
                  //   30–32: VI / Tyre groups (dangerous/red/amber)
                  //   40   : SERVICE intervals + everything else not above
                  //   90   : ADDED RECS (wheel alignment, ADAS, A/C, diag testing,
                  //          service_recommendation, grouped_repair, foh_manual)
                  const getPriority = (jg) => {
                    const src = (jg.source || '').toLowerCase();
                    const effectiveStatus = jg.vi_status || jg.status;

                    // (E) ADDED RECOMMENDATIONS — always bottom. Matches the
                    // left-panel "Added Recommendations" filter so both
                    // panels group these items identically.
                    const RECOMMENDATION_SUBTYPES = new Set([
                      "wheel_alignment", "adas_calibration",
                      "air_conditioning", "diagnostic_testing",
                    ]);
                    if (RECOMMENDATION_SUBTYPES.has(jg.subtype)) return 90;
                    if ((jg.vi_item_key || '').toLowerCase() === 'wheel_alignment') return 90;
                    if (src === 'foh_manual' || src === 'mcc_auto_alignment') return 90;
                    if (jg.type === 'service_recommendation' || jg.type === 'grouped_repair') {
                      // Only treat as Added Rec when NOT MOT/VI/inspection
                      // (those still resolve via their own buckets below).
                      if (!src.includes('dvsa_mot_result') && src !== 'inspection' && src !== 'vi') {
                        return 90;
                      }
                    }

                    // (A) MOT auto-imported defects — top of the list.
                    if (src.includes('dvsa_mot_result')) {
                      const sev = (jg.source_mot_severity || jg.dvsa_mot_severity || '').toLowerCase();
                      if (sev === 'dangerous') return 10;
                      if (sev === 'major') return 11;
                      if (['minor', 'advisory', 'pass_advisory', 'pass advisory'].includes(sev)) return 12;
                      return 13;
                    }

                    // (B) DIAGNOSTIC — second per FOH order. Mirror the
                    // JobGroupCard ``isDiagnosticJob`` detection (line
                    // ~22917) so any card showing the Diagnostic badge
                    // sorts into the DIAG bucket. Detection sources:
                    //   • explicit diagnostic source/key
                    //   • warning/dash_warning vi_item_key
                    //   • title regex (fault investigation, diagnose…)
                    //   • labour category = diagnostics / inspection_testing
                    const diagTitlePatterns = /diagnostic|fault\s*(?:investigation|finding|diagnosis)|diagnose\s*before|warning\s*light.*(?:diagnos|investigat)|(?:diagnos|investigat).*(?:fault|warning)|electrical\s*(?:diagnos|investigat)|abs.*(?:diagnos|investigat)|airbag.*(?:diagnos|investigat)/i;
                    const hasDiagLabour = (jg.labour_lines || []).some(l =>
                      l.category === 'diagnostics'
                      || l.category === 'diagnostic_labour'
                      || l.category === 'inspection_testing'
                    );
                    if (src.includes('diagnostic')
                        || jg.vi_item_key?.includes('warning')
                        || jg.vi_item_key?.includes('dash_warning')
                        || diagTitlePatterns.test(jg.title || '')
                        || hasDiagLabour) {
                      return 20;
                    }

                    // (C) Tyre replacement groups — VI bucket by child severity.
                    if (jg.subtype === "tyre_replacement_group") {
                      const children = jg.tyres || [];
                      if (children.some((c) => c.severity === "dangerous")) return 30;
                      if (children.some((c) => c.severity === "major" || c.severity === "damaged" || c.severity === "puncture")) return 31;
                      return 32;
                    }

                    // (C) VI items — third.
                    if (effectiveStatus === 'dangerous') return 30;
                    if (effectiveStatus === 'red' || jg.is_mandatory) return 31;
                    if (effectiveStatus === 'amber') return 32;

                    // (D) SERVICE intervals + everything else — fourth.
                    return 40;
                  };
                  
                  const priorityA = getPriority(a);
                  const priorityB = getPriority(b);
                  
                  // Primary sort by priority
                  if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                  }
                  
                  // Secondary sort: use position from left panel for consistent ordering
                  const posA = itemPositionMap[a.inspection_item_id] ?? 999;
                  const posB = itemPositionMap[b.inspection_item_id] ?? 999;
                  return posA - posB;
                });
              })().map((jobGroup, jobIdx) => {
                // Iter187 — render the structured Tyre Replacement
                // Group card instead of the standard JobGroupCard
                // when the JG carries the grouped-repair subtype.
                if (jobGroup.subtype === "tyre_replacement_group") {
                  // iter211ar — Register the tyre group's ref so
                  // `focusOnJobGroup(jg.id)` (fired when FOH clicks
                  // the Tyres card in the left Inspection Findings
                  // panel) can scrollIntoView. Without this wrapper
                  // the tyre group card was the only middle-panel
                  // card missing from `jobGroupRefs.current`, so
                  // clicking the left-panel tyre entry left the
                  // middle panel stationary.
                  return (
                    <div
                      key={jobGroup.id || `tyre_group_${jobIdx}`}
                      ref={el => jobGroupRefs.current[jobGroup.id] = el}
                      data-testid={`job-group-tyre-${jobIdx}`}
                    >
                      <TyreReplacementGroupCard
                        jobGroup={jobGroup}
                        yestimateId={yestimateId}
                        onConfirmPricing={confirmJobGroupPricing}
                        onUnconfirmPricing={unconfirmJobGroupPricing}
                        onLocalChange={(jgId, patch) => {
                          // iter196 — Live preview merge. Updates the
                          // parent jobGroups so grand total / bottom
                          // bar reflect what FOH just typed (before
                          // confirm/save). NOT persisted — confirm
                          // pricing handler does the PUT.
                          setJobGroups((prev) => prev.map((jg) =>
                            jg.id === jgId ? { ...jg, ...patch } : jg,
                          ));
                        }}
                        onRefresh={async () => {
                          try {
                            const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                            if (r.data?.yestimate?.job_groups) {
                              setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                            }
                            setRecommendationRefreshKey((k) => k + 1);
                          } catch { /* non-blocking */ }
                        }}
                        onAddAxlePair={(pair) => {
                          if (typeof window !== "undefined") {
                            window.alert?.(`Axle-pair suggestion noted: ${pair?.label || ""}\n\n(Confirm flow lands in a follow-up — current v1 records the recommendation only.)`);
                          }
                        }}
                      />
                    </div>
                  );
                }
                const jobTotals = calculateJobTotal(jobGroup);
                const isExpanded = expandedJobs[jobGroup.id] === true; // Default to collapsed
                // Get effective status for styling. MOT auto-imported defects
                // have no VI inspection so we derive the colour from the DVSA
                // severity: dangerous → black, major → red, minor/advisory →
                // amber. Keeps the main pricing card border and the left-rail
                // chips aligned for MOT-only jobs.
                const motSeverity = (jobGroup.source || '').includes('dvsa_mot_result')
                  ? (jobGroup.source_mot_severity || jobGroup.dvsa_mot_severity || '').toLowerCase()
                  : '';
                const motDerivedStatus =
                  motSeverity === 'dangerous' ? 'dangerous' :
                  motSeverity === 'major'     ? 'red' :
                  ['minor', 'advisory', 'pass_advisory', 'pass advisory'].includes(motSeverity) ? 'amber' :
                  '';
                const effectiveStatus = jobGroup.vi_status || jobGroup.status || motDerivedStatus;
                // Diagnostic jobs include warning lights, fault codes etc.
                // Diagnostic job detection - matches backend's is_diagnostic_item logic
                const diagTitlePatterns = /diagnostic|fault\s*(?:investigation|finding|diagnosis)|diagnose\s*before|warning\s*light.*(?:diagnos|investigat)|(?:diagnos|investigat).*(?:fault|warning)|electrical\s*(?:diagnos|investigat)|abs.*(?:diagnos|investigat)|airbag.*(?:diagnos|investigat)/i;
                const hasDiagLabour = jobGroup.labour_lines?.some(l => l.category === 'diagnostics' || l.category === 'diagnostic_labour' || l.category === 'inspection_testing');
                const isDiagnosticJob = jobGroup.source === "diagnostic" || 
                  jobGroup.vi_item_key?.includes('warning') || 
                  jobGroup.vi_item_key?.includes('dash_warning') ||
                  diagTitlePatterns.test(jobGroup.title || '') ||
                  hasDiagLabour;
                const isPackageJob = jobGroup.source === "job_package";
                const isAutoPopulated = jobGroup.source === "auto_populate";
                // Service interval items
                const isIntervalItem = jobGroup.is_interval_item || jobGroup.source === "interval";
                const isOilService = isIntervalItem && (jobGroup.interval_item_id === "oil_filter_service" || jobGroup.interval_item_id === "oil_air_filter_service");
                // Include "dangerous" status with red items
                const isRedItem = effectiveStatus === "red" || effectiveStatus === "dangerous" || jobGroup.is_mandatory;
                const isDangerousItem = effectiveStatus === "dangerous";
                const isAmberItem = effectiveStatus === "amber";
                const isNotQuoted = !!jobGroup.not_quoted_reason;
                const isSelectedForJCE = selectedJobForJCE?.id === jobGroup.id;
                
                // Check if this brake pads job is redundant (covered by discs & pads package)
                const isBrakePadsJob = jobGroup.vi_item_key?.includes("brake_pads") || 
                                       jobGroup.title?.toLowerCase().includes("brake pad");
                const hasDiscsAndPadsPackage = jobGroups.some(jg => 
                  jg.id !== jobGroup.id && 
                  (jg.title?.toLowerCase().includes("disc") && jg.title?.toLowerCase().includes("pad")) ||
                  jg.package_id?.includes("discs_pads")
                );
                const isPotentiallyRedundant = isBrakePadsJob && hasDiscsAndPadsPackage;
                
                return (
                  <Card 
                    key={jobGroup.id}
                    ref={el => jobGroupRefs.current[jobGroup.id] = el}
                    className={`transition-all overflow-hidden ${
                      isSelectedForJCE ? 'ring-2 ring-yellow-400 ring-offset-1' : ''
                    } ${isNotQuoted ? 'opacity-60' : ''}`}
                    style={{
                      // Visual hierarchy: prominent left border color
                      borderLeft: isDangerousItem ? '6px solid #000' :
                                  isRedItem ? '6px solid #ef4444' :
                                  isAmberItem ? '6px solid #f59e0b' :
                                  jobGroup.pricing_confirmed ? '6px solid #22c55e' :
                                  isIntervalItem ? '6px solid #10b981' :
                                  isDiagnosticJob ? '6px solid #8b5cf6' :
                                  isPackageJob ? '6px solid #3b82f6' :
                                  '6px solid #e2e8f0',
                      borderTop: '1px solid #e2e8f0',
                      borderRight: '1px solid #e2e8f0',
                      borderBottom: '1px solid #e2e8f0',
                      background: isDangerousItem ? 'linear-gradient(to right, rgba(0,0,0,0.05), white)' :
                                  isRedItem && !jobGroup.pricing_confirmed ? 'linear-gradient(to right, rgba(239,68,68,0.08), white)' :
                                  isAmberItem && !jobGroup.pricing_confirmed ? 'linear-gradient(to right, rgba(245,158,11,0.08), white)' :
                                  jobGroup.pricing_confirmed ? 'linear-gradient(to right, rgba(34,197,94,0.05), white)' :
                                  'white'
                    }}
                    data-testid={`job-group-${jobIdx}`}
                  >
                    <CardHeader 
                      className={`pb-2 cursor-pointer ${isDangerousItem ? 'bg-black rounded-t-lg' : 'hover:bg-slate-50'}`}
                      onClick={() => {
                        setExpandedJobs(prev => ({ ...prev, [jobGroup.id]: !isExpanded }));
                        setSelectedJobForJCE(jobGroup);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                          <div className={isDangerousItem ? "text-white/60" : "text-slate-400"}>
                            <ChevronRight className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                          </div>
                          {/* Diagnostic jobs show non-editable title */}
                          {isDiagnosticJob ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-lg text-red-700">{jobGroup.title}</span>
                              <Badge className="bg-purple-600 text-white">Diagnostic</Badge>
                              <Badge className="bg-red-500 text-white">RED</Badge>
                              {jobGroup.source === 'dvsa_mot_result' && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0 text-teal-700 border-teal-300 bg-teal-50"
                                  data-testid={`jg-mot-source-badge-${jobIdx}`}
                                >
                                  MOT — DVSA
                                </Badge>
                              )}
                              {/* Phase-A Job Group Dedup — merged provenance
                                  badge. Renders whenever the dedup engine
                                  has combined two or more captures (e.g.
                                  Diag + MOT, MOT + VI) into a single
                                  pricing row. Tooltip lists each origin so
                                  FOH can see exactly what was merged. */}
                              {Array.isArray(jobGroup?.merge_metadata?.merged_from) &&
                               jobGroup.merge_metadata.merged_from.length > 1 && (
                                <span className="inline-flex items-center gap-1">
                                  <Badge
                                    variant="outline"
                                    className="text-xs shrink-0 text-indigo-700 border-indigo-300 bg-indigo-50 font-semibold"
                                    title={
                                      'Merged from ' +
                                      (jobGroup.source_origins || [])
                                        .map(o => (o?.type || 'unknown') +
                                          (o?.text ? ' — ' + o.text : ''))
                                        .join(' · ')
                                    }
                                    data-testid={`jg-merged-badge-${jobIdx}`}
                                  >
                                    Merged ×{jobGroup.merge_metadata.merged_from.length}
                                  </Badge>
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm('Split this merged job group back into its originals?')) return;
                                      try {
                                        await axios.post(`${API}/foh/yestimate/${yestimateId}/job-groups/${jobGroup.id}/unmerge`);
                                        toast.success('Unmerged');
                                        const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                                        if (r.data?.yestimate?.job_groups) {
                                          setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                                        }
                                        setRecommendationRefreshKey((k) => k + 1);
                                      } catch (err) {
                                        toast.error(err?.response?.data?.detail || 'Failed to unmerge');
                                      }
                                    }}
                                    className="text-[10px] underline text-indigo-700 hover:text-indigo-900"
                                    data-testid={`jg-unmerge-btn-${jobIdx}`}
                                    title="Split merge back into originals"
                                  >
                                    unmerge
                                  </button>
                                </span>
                              )}
                              {jobGroup.pending_diagnostic_assessment && (
                                <Badge
                                  className="text-xs shrink-0 bg-purple-700 text-white font-semibold"
                                  title="Tester escalated this defect for Secondary Technical Assessment."
                                  data-testid={`jg-pending-diag-badge-${jobIdx}`}
                                >
                                  Pending Diagnostic
                                </Badge>
                              )}
                              {/* Phase-1 MCC Rapid Capture badges —
                                  pricing confidence + repair route. Only
                                  appear on DVSA-sourced job_groups where
                                  the tester has completed the rapid
                                  capture. Lets FOH triage at a glance
                                  before opening the item. */}
                              {jobGroup.mcc_pricing_confidence && (
                                <Badge
                                  className={`text-xs shrink-0 font-semibold ${
                                    jobGroup.mcc_pricing_confidence === 'high'
                                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                      : jobGroup.mcc_pricing_confidence === 'medium'
                                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                                      : 'bg-red-100 text-red-800 border-red-300'
                                  }`}
                                  variant="outline"
                                  title={`Tester pricing confidence: ${jobGroup.mcc_pricing_confidence}`}
                                  data-testid={`jg-mcc-confidence-badge-${jobIdx}`}
                                >
                                  {jobGroup.mcc_pricing_confidence === 'high' && 'High conf.'}
                                  {jobGroup.mcc_pricing_confidence === 'medium' && 'Med conf.'}
                                  {jobGroup.mcc_pricing_confidence === 'low' && 'Low conf.'}
                                </Badge>
                              )}
                              {jobGroup.mcc_repair_route && jobGroup.mcc_repair_route !== 'price_now' && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0 font-semibold bg-slate-100 text-slate-700 border-slate-300"
                                  title={`Repair route: ${jobGroup.mcc_repair_route.replace('_', ' ')}`}
                                  data-testid={`jg-mcc-route-badge-${jobIdx}`}
                                >
                                  {jobGroup.mcc_repair_route === 'parts_lookup' && 'Parts lookup'}
                                  {jobGroup.mcc_repair_route === 'investigation' && 'Investigate first'}
                                  {jobGroup.mcc_repair_route === 'strip' && 'Strip required'}
                                  {jobGroup.mcc_repair_route === 'specialist' && 'Specialist'}
                                </Badge>
                              )}
                              {/* iter197h — Tester's labour-time estimate, captured during MCC.
                                  Pure guidance for FOH; FOH still decides the actual labour line. */}
                              {typeof jobGroup.mcc_tester_estimated_hours === 'number' && jobGroup.mcc_tester_estimated_hours > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0 font-semibold bg-indigo-50 text-indigo-700 border-indigo-300"
                                  title="Tester's labour-time estimate (guidance only)"
                                  data-testid={`jg-mcc-tester-hours-badge-${jobIdx}`}
                                >
                                  Tester est: {jobGroup.mcc_tester_estimated_hours}h
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                              <Input
                                value={jobGroup.title}
                                onChange={(e) => updateJobGroupTitle(jobGroup.id, e.target.value)}
                                className={`font-bold text-lg flex-1 min-w-[200px] px-2 py-1 rounded-md transition-all ${
                                  isDangerousItem 
                                    ? 'bg-white/10 text-white placeholder:text-white/50 border border-white/30 hover:bg-white/20 focus:bg-white/20 focus:border-white/50' 
                                    : isRedItem
                                    ? 'bg-white border border-red-200 shadow-sm text-red-700 hover:border-red-300 hover:shadow focus:border-red-400 focus:ring-1 focus:ring-red-200'
                                    : isAmberItem
                                    ? 'bg-white border border-amber-200 shadow-sm text-amber-700 hover:border-amber-300 hover:shadow focus:border-amber-400 focus:ring-1 focus:ring-amber-200'
                                    : isIntervalItem
                                    ? 'bg-white border border-green-200 shadow-sm text-green-700 hover:border-green-300 hover:shadow focus:border-green-400 focus:ring-1 focus:ring-green-200'
                                    : 'bg-white border border-slate-200 shadow-sm text-slate-800 hover:border-slate-300 hover:shadow focus:border-blue-400 focus:ring-1 focus:ring-blue-200'
                                }`}
                                placeholder="Job Title"
                                data-testid={`job-title-${jobIdx}`}
                              />
                              {/* Status badges - DANGEROUS first, then RED, then AMBER */}
                              {isDangerousItem && (
                                <Badge className="bg-white text-black text-xs shrink-0 font-bold animate-pulse">
                                  ⚠️ DANGEROUS
                                </Badge>
                              )}
                              {isRedItem && !isDangerousItem && (
                                <Badge className="bg-red-500 text-white text-xs shrink-0">RED</Badge>
                              )}
                              {isAmberItem && !isRedItem && (
                                <Badge className="bg-amber-500 text-white text-xs shrink-0">AMBER</Badge>
                              )}
                              {/* Multi-corner auto-scale indicator — appears whenever the JG was auto-scaled by Tech VI multi-location selection */}
                              {jobGroup.qty_multiplier && jobGroup.qty_multiplier > 1 && (
                                <Badge className="bg-blue-600 text-white text-xs shrink-0" data-testid={`jg-multi-corner-badge-${jobIdx}`} title={jobGroup.qty_multiplier_reason || ""}>
                                  ×{jobGroup.qty_multiplier} corners
                                </Badge>
                              )}
                              {/* Brake pad sub-status badge for FOH visibility */}
                              {jobGroup.pad_sub_status === 'REPLACE_SOON' && (
                                <Badge className="bg-amber-600 text-white text-xs shrink-0 font-bold">
                                  REPLACE SOON
                                </Badge>
                              )}
                              {jobGroup.pad_sub_status === 'MONITOR' && (
                                <Badge variant="outline" className="text-xs text-amber-700 border-amber-400 shrink-0 bg-amber-50 font-semibold">
                                  MONITOR — Planning Opportunity
                                </Badge>
                              )}
                              {jobGroup.pad_sub_status === 'REPLACE_NOW' && (
                                <Badge className="bg-red-600 text-white text-xs shrink-0 font-bold animate-pulse">
                                  REPLACE NOW
                                </Badge>
                              )}
                              {/* Recommendation indicator for MONITOR items */}
                              {jobGroup.is_recommendation && (
                                <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 shrink-0 bg-blue-50">
                                  Recommendation
                                </Badge>
                              )}
                              {isIntervalItem && (
                                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300 shrink-0 bg-emerald-50">
                                  <Clock className="w-3 h-3 mr-1" /> Service Interval
                                </Badge>
                              )}
                              {jobGroup.price_book_source && (
                                <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-300 shrink-0 bg-indigo-50">
                                  <FileText className="w-3 h-3 mr-1" /> Price Book
                                </Badge>
                              )}
                              {isPackageJob && (
                                <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 shrink-0">
                                  <Package className="w-3 h-3 mr-1" /> From Package
                                </Badge>
                              )}
                              {isAutoPopulated && !isPackageJob && (
                                <Badge variant="outline" className={`text-xs shrink-0 ${
                                  isDangerousItem ? 'text-white/80 border-white/50' : 'text-green-600 border-green-300'
                                }`}>
                                  <Wrench className="w-3 h-3 mr-1" /> Auto-Populated
                                </Badge>
                              )}
                              {/* MOT auto-import + auto-quote provenance */}
                              {jobGroup.source === 'dvsa_mot_result' && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0 text-teal-700 border-teal-300 bg-teal-50"
                                  title={jobGroup.source_mot_text || 'Auto-imported from DVSA MOT result'}
                                  data-testid={`jg-mot-source-badge-${jobIdx}`}
                                >
                                  MOT — DVSA
                                </Badge>
                              )}
                              {/* Phase-A Job Group Dedup — merged provenance
                                  badge (editable header). */}
                              {Array.isArray(jobGroup?.merge_metadata?.merged_from) &&
                               jobGroup.merge_metadata.merged_from.length > 1 && (
                                <span className="inline-flex items-center gap-1">
                                  <Badge
                                    variant="outline"
                                    className="text-xs shrink-0 text-indigo-700 border-indigo-300 bg-indigo-50 font-semibold"
                                    title={
                                      'Merged from ' +
                                      (jobGroup.source_origins || [])
                                        .map(o => (o?.type || 'unknown') +
                                          (o?.text ? ' — ' + o.text : ''))
                                        .join(' · ')
                                    }
                                    data-testid={`jg-merged-badge-edit-${jobIdx}`}
                                  >
                                    Merged ×{jobGroup.merge_metadata.merged_from.length}
                                  </Badge>
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm('Split this merged job group back into its originals?')) return;
                                      try {
                                        await axios.post(`${API}/foh/yestimate/${yestimateId}/job-groups/${jobGroup.id}/unmerge`);
                                        toast.success('Unmerged');
                                        const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                                        if (r.data?.yestimate?.job_groups) {
                                          setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                                        }
                                        setRecommendationRefreshKey((k) => k + 1);
                                      } catch (err) {
                                        toast.error(err?.response?.data?.detail || 'Failed to unmerge');
                                      }
                                    }}
                                    className="text-[10px] underline text-indigo-700 hover:text-indigo-900"
                                    data-testid={`jg-unmerge-btn-edit-${jobIdx}`}
                                    title="Split merge back into originals"
                                  >
                                    unmerge
                                  </button>
                                </span>
                              )}
                              {/* MCC escalation flag — set when tester routes
                                  the defect to Secondary Technical Assessment.
                                  Visible pill so FOH knows diagnostic work is
                                  expected before they finalise pricing. */}
                              {jobGroup.pending_diagnostic_assessment && (
                                <Badge
                                  className="text-xs shrink-0 bg-purple-600 text-white font-semibold"
                                  title="Tester escalated this defect for Secondary Technical Assessment."
                                  data-testid={`jg-pending-diag-badge-${jobIdx}`}
                                >
                                  Pending Diagnostic
                                </Badge>
                              )}
                              {/* Phase-1 MCC Rapid Capture badges (editable view). */}
                              {jobGroup.mcc_pricing_confidence && (
                                <Badge
                                  className={`text-xs shrink-0 font-semibold ${
                                    jobGroup.mcc_pricing_confidence === 'high'
                                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                      : jobGroup.mcc_pricing_confidence === 'medium'
                                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                                      : 'bg-red-100 text-red-800 border-red-300'
                                  }`}
                                  variant="outline"
                                  title={`Tester pricing confidence: ${jobGroup.mcc_pricing_confidence}`}
                                  data-testid={`jg-mcc-confidence-badge-edit-${jobIdx}`}
                                >
                                  {jobGroup.mcc_pricing_confidence === 'high' && 'High conf.'}
                                  {jobGroup.mcc_pricing_confidence === 'medium' && 'Med conf.'}
                                  {jobGroup.mcc_pricing_confidence === 'low' && 'Low conf.'}
                                </Badge>
                              )}
                              {jobGroup.mcc_repair_route && jobGroup.mcc_repair_route !== 'price_now' && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0 font-semibold bg-slate-100 text-slate-700 border-slate-300"
                                  title={`Repair route: ${jobGroup.mcc_repair_route.replace('_', ' ')}`}
                                  data-testid={`jg-mcc-route-badge-edit-${jobIdx}`}
                                >
                                  {jobGroup.mcc_repair_route === 'parts_lookup' && 'Parts lookup'}
                                  {jobGroup.mcc_repair_route === 'investigation' && 'Investigate first'}
                                  {jobGroup.mcc_repair_route === 'strip' && 'Strip required'}
                                  {jobGroup.mcc_repair_route === 'specialist' && 'Specialist'}
                                </Badge>
                              )}
                              {typeof jobGroup.mcc_tester_estimated_hours === 'number' && jobGroup.mcc_tester_estimated_hours > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs shrink-0 font-semibold bg-indigo-50 text-indigo-700 border-indigo-300"
                                  title="Tester's labour-time estimate (guidance only)"
                                  data-testid={`jg-mcc-tester-hours-badge-edit-${jobIdx}`}
                                >
                                  Tester est: {jobGroup.mcc_tester_estimated_hours}h
                                </Badge>
                              )}
                              {jobGroup.auto_quoted && (
                                <Badge
                                  className="text-xs shrink-0 bg-teal-600 text-white font-semibold animate-pulse"
                                  title={`Auto-priced from canonical rule${jobGroup.rule_id ? ` (${jobGroup.rule_id})` : ''}. Please verify before sending.`}
                                  data-testid={`jg-auto-quoted-badge-${jobIdx}`}
                                >
                                  ✓ Auto-priced — review
                                </Badge>
                              )}
                              {isNotQuoted && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // iter197n — Resume pricing affordance.
                                    // Marking a JG as "Not Quoted" previously
                                    // had no undo path — the pricing form
                                    // collapses (line 22631 gate) and the
                                    // badge was a static label. FOH on
                                    // BD10GUH found themselves locked out
                                    // of the brake-binding advisory. One
                                    // tap on the badge now clears the
                                    // reason and re-expands the JG for
                                    // pricing.
                                    setJobGroups((prev) =>
                                      prev.map((g) =>
                                        g.id === jobGroup.id
                                          ? {
                                              ...g,
                                              not_quoted_reason: null,
                                              not_quoted_reason_label: null,
                                              not_quoted_notes: null,
                                            }
                                          : g
                                      )
                                    );
                                    setExpandedJobs((prev) => ({ ...prev, [jobGroup.id]: true }));
                                  }}
                                  className="text-xs px-2 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-amber-50 hover:border-amber-400 hover:text-amber-900 transition-colors shrink-0"
                                  title="Resume pricing — clears the Not Quoted state so you can edit pricing again"
                                  data-testid={`resume-pricing-btn-${jobGroup.id}`}
                                >
                                  Not Quoted: {jobGroup.not_quoted_reason_label || jobGroup.not_quoted_reason} · Resume
                                </button>
                              )}
                              {isSelectedForJCE && showJCEPanel && (
                                <Badge className="text-xs bg-yellow-500 text-white shrink-0 animate-pulse">
                                  JCE Focus
                                </Badge>
                              )}
                              {/* DRE - Diagnostic Requirement Badge */}
                              {dreResults[jobGroup.id] && (
                                <Badge 
                                  className={`text-xs shrink-0 ${
                                    dreResults[jobGroup.id].mode === 'none' ? 'bg-slate-100 text-slate-600' :
                                    dreResults[jobGroup.id].mode === 'optional' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                                    dreResults[jobGroup.id].mode === 'default_included' ? 'bg-amber-100 text-amber-700 border border-amber-300' :
                                    'bg-red-100 text-red-700 border border-red-300 animate-pulse'
                                  }`}
                                  title={dreResults[jobGroup.id].reason}
                                >
                                  {dreResults[jobGroup.id].mode === 'none' ? 'NO DIAG' :
                                   dreResults[jobGroup.id].mode === 'optional' ? 'DIAG OPTIONAL' :
                                   dreResults[jobGroup.id].mode === 'default_included' ? 'DIAG INCLUDED' :
                                   'DIAG REQUIRED'}
                                </Badge>
                              )}
                              {/* Show override badge if diagnostic was removed */}
                              {jobGroup.diagnostic_removed && (
                                <Badge className="text-xs bg-orange-100 text-orange-700 border border-orange-300 shrink-0">
                                  Diag Removed
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Not Quoted button — iter189: show on ALL
                              items including red MOT defects so FOH has
                              an escape hatch when a job can't be priced
                              (parts discontinued, customer declined
                              diagnosis, MOT staged-routing case where
                              no charge is appropriate). The modal
                              collects a reason for the audit trail. */}
                          {!isDiagnosticJob && !isNotQuoted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openNotQuotedModal(jobGroup)}
                              className={`text-xs ${
                                isRedItem
                                  ? "text-red-600 hover:text-red-800 border border-red-200 hover:bg-red-50"
                                  : "text-slate-500 hover:text-slate-700"
                              }`}
                              title="Mark as not quoted"
                              data-testid={`not-quoting-btn-${jobGroup.id}`}
                            >
                              Not Quoting
                            </Button>
                          )}
                          {/* Job Integrity Status Badge - shows integrity state */}
                          {jobIntegrityData[jobGroup.id] && !isNotQuoted && (
                            <CollapsedIntegrityStatus
                              integrityData={jobIntegrityData[jobGroup.id]}
                            />
                          )}
                          <span className="font-bold text-lg">£{jobTotals.total.toFixed(2)}</span>
                          {/* Show redundancy warning for brake pads when discs & pads package exists */}
                          {isPotentiallyRedundant && (
                            <Badge className="bg-yellow-100 text-yellow-700 text-xs">May be redundant</Badge>
                          )}
                          {/* Delete button - easier for redundant items */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeJobGroup(jobGroup.id)}
                            data-testid={`delete-job-group-${jobIdx}`}
                            title={isPotentiallyRedundant 
                              ? "Remove (May already be covered by Discs & Pads package)" 
                              : isRedItem || isDiagnosticJob 
                                ? "Remove (Warning: Mandatory)" 
                                : "Remove this job"}
                          >
                            <Trash2 className={`w-4 h-4 ${isPotentiallyRedundant ? 'text-yellow-600' : isRedItem || isDiagnosticJob ? 'text-orange-500' : 'text-red-500'}`} />
                          </Button>
                        </div>
                      </div>
                      {/* Show diagnostic conclusion preview */}
                      {isDiagnosticJob && jobGroup.diagnostic_conclusion && (
                        <p className="text-sm text-purple-700 mt-2 pl-7">
                          {jobGroup.diagnostic_conclusion}
                        </p>
                      )}

                      {/* iter198qq — `TesterEvidenceCard` removed from
                          the main pricing item. The same MCC content +
                          photos are now surfaced in the left-panel MOT
                          row's expanded panel (click the teal "Tester
                          evidence" chip on the row). Avoids
                          duplication and keeps the pricing item lean.
                       */}

                      {/* JG-level Pair Recommendation advisory.
                          Fires for any job group whose vi_item_key /
                          job_group_key / title indicates a left-right
                          paired component (suspension arm, wishbone,
                          shock, spring, ball joint, track rod end, drop
                          link, CV joint) that's only been priced for ONE
                          side. Recovers gracefully when both sides are
                          present in their own JGs. Reuses the same
                          amber/AlertCircle treatment as the parts-editor
                          pairs warning so FOH learns one visual idiom. */}
                      {(() => {
                        const _arrow = String(jobGroup.title || jobGroup.job_group_key || jobGroup.vi_item_key || "").toLowerCase();
                        const _isArmFamily = (
                          /\bsuspension arm|\bwishbone|\bcontrol arm|\btrailing arm|\blower arm/.test(_arrow)
                          || /(suspension|wishbone|control|trailing|lower)_arm/.test(_arrow)
                        );
                        if (!_isArmFamily) return null;
                        // Detect which side this JG covers.
                        const _hasOSF = /\b(osf|offside front|front offside)\b/i.test(jobGroup.title || "");
                        const _hasNSF = /\b(nsf|nearside front|front nearside)\b/i.test(jobGroup.title || "");
                        const _hasOSR = /\b(osr|offside rear|rear offside)\b/i.test(jobGroup.title || "");
                        const _hasNSR = /\b(nsr|nearside rear|rear nearside)\b/i.test(jobGroup.title || "");
                        const _isFront = _hasOSF || _hasNSF;
                        const _isRear  = _hasOSR || _hasNSR;
                        if (!_isFront && !_isRear) return null;
                        const _twinKeywords = _isFront
                          ? [/\b(osf|nsf|offside front|nearside front)\b/i, /(front_suspension_arm|front_wishbone|front_control_arm|front_lower_arm)/i]
                          : [/\b(osr|nsr|offside rear|nearside rear)\b/i, /(rear_suspension_arm|rear_trailing_arm|rear_control_arm)/i];
                        const _bothSidesPriced = jobGroups.some((other) => {
                          if (other === jobGroup) return false;
                          const _o = String(other.title || other.job_group_key || other.vi_item_key || "");
                          return _twinKeywords.some((re) => re.test(_o)) && _o !== (jobGroup.title || "");
                        });
                        if (_bothSidesPriced) return null;
                        const _axisLabel = _isFront ? "front" : "rear";
                        const _sideLabel = _hasOSF ? "offside" : _hasNSF ? "nearside" : _hasOSR ? "offside" : "nearside";
                        return (
                          <div
                            className="mx-7 mt-2 flex items-start gap-2 px-3 py-2 rounded bg-amber-50 border border-amber-300"
                            data-testid={`jg-pair-recommendation-${jobIdx}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="text-[12.5px] leading-snug">
                              <span className="font-semibold text-amber-900">Pair recommendation — </span>
                              <span className="text-amber-800">
                                Only the {_sideLabel} {_axisLabel} arm is priced. Wear is usually symmetrical, and four-wheel alignment is already mandatory after either-side replacement. Consider proposing both sides for the customer's safety, longevity and value.
                              </span>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Battery Type Note - Show for battery-related jobs (only when expanded) */}
                      {isExpanded && (jobGroup.vi_item_key === 'battery_test' || 
                        jobGroup.title?.toLowerCase().includes('battery')) && (() => {
                        // Get battery data from job group OR from VI items
                        const viItem = data?.items?.find(i => i.id === jobGroup.inspection_item_id || i.item_key === 'battery_test');
                        const batteryType = jobGroup.battery_type_required || viItem?.battery_type_required;
                        const batteryRegistration = jobGroup.battery_registration_required || viItem?.battery_registration_required;
                        const batteryNote = jobGroup.battery_technician_note || viItem?.battery_technician_note || viItem?.notes;
                        const batteryVoltage = jobGroup.battery_voltage_v || viItem?.battery_voltage_v;
                        const measuredCca = jobGroup.measured_cca_a || viItem?.measured_cca_a;
                        const ratedCca = jobGroup.rated_cca_a || viItem?.rated_cca_a;
                        const healthPercent = jobGroup.health_percent || viItem?.health_percent;
                        
                        return (
                        <div className="mt-2 pl-7">
                          <div className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg shadow-sm">
                            <p className="text-sm font-bold text-amber-800 flex items-center gap-2 border-b border-amber-200 pb-2 mb-2">
                              <Zap className="w-4 h-4" />
                              TECH VI REFERENCE – BATTERY
                            </p>
                            
                            {/* Battery Type */}
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-600">Battery Type:</span>
                                <span className={`font-semibold px-2 py-0.5 rounded ${
                                  batteryType === 'agm' ? 'bg-blue-100 text-blue-800' :
                                  batteryType === 'efb' ? 'bg-purple-100 text-purple-800' :
                                  batteryType === 'standard' ? 'bg-green-100 text-green-800' :
                                  'bg-amber-100 text-amber-800'
                                }`}>
                                  {batteryType === 'standard' ? 'Standard Lead Acid' :
                                   batteryType === 'efb' ? 'EFB (Enhanced Flooded)' :
                                   batteryType === 'agm' ? 'AGM (Absorbent Glass Mat)' :
                                   batteryType || 'Not specified'}
                                </span>
                              </div>
                              
                              {/* Coding Required */}
                              <div className="flex items-center gap-2">
                                <span className="text-slate-600">Coding:</span>
                                {(batteryRegistration === 'yes' || batteryRegistration === true) ? (
                                  <span className="font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Required
                                  </span>
                                ) : (batteryRegistration === 'no' || batteryRegistration === false) ? (
                                  <span className="font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    Not required
                                  </span>
                                ) : (
                                  <span className="font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                                    Check with tech
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Tech Notes */}
                            {batteryNote && (
                              <div className="mt-2 pt-2 border-t border-amber-200">
                                <p className="text-xs text-slate-500 font-medium">Tech Notes:</p>
                                <p className="text-sm text-slate-700 mt-0.5 italic">
                                  "{batteryNote}"
                                </p>
                              </div>
                            )}
                            
                            {/* Test Results if available */}
                            {(batteryVoltage || measuredCca || healthPercent !== undefined) && (
                              <div className="mt-2 pt-2 border-t border-amber-200 flex flex-wrap gap-3 text-xs">
                                {batteryVoltage && (
                                  <span className="text-slate-600">Voltage: <strong>{batteryVoltage}V</strong></span>
                                )}
                                {measuredCca && ratedCca && (
                                  <span className="text-slate-600">CCA: <strong>{measuredCca}A</strong> / {ratedCca}A rated</span>
                                )}
                                {healthPercent !== undefined && healthPercent !== null && (
                                  <span className={`font-medium ${
                                    healthPercent >= 75 ? 'text-green-600' :
                                    healthPercent >= 50 ? 'text-amber-600' : 'text-red-600'
                                  }`}>
                                    Health: {Math.round(healthPercent)}%
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Pricing Tips */}
                            <div className="mt-2 pt-2 border-t border-amber-200 text-xs text-slate-600">
                              <p className="font-medium text-amber-700 mb-1">Pricing Checklist:</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                <li>Price correct battery type ({batteryType || 'confirm with tech'})</li>
                                {(batteryRegistration === 'yes' || batteryRegistration === true) && (
                                  <li className="text-red-700">Add coding/registration labour (~0.3h)</li>
                                )}
                                <li>Consider terminal cleaning & protection</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        );
                      })()}
                      
                      {/* Locking Wheel Key Alert - Show for tyre/wheel jobs when key is missing (only when expanded) */}
                      {isExpanded && (jobGroup.vi_item_key?.includes('tyre') || 
                        jobGroup.title?.toLowerCase().includes('tyre') ||
                        jobGroup.title?.toLowerCase().includes('wheel')) && (() => {
                        // Check for locking key VI item
                        const lockingKeyItem = data?.items?.find(i => i.item_key === 'locking_wheel_key');
                        const keyMissing = lockingKeyItem && (
                          lockingKeyItem.locking_key_present === 'no' || 
                          lockingKeyItem.status === 'red' ||
                          lockingKeyItem.status === 'amber'
                        );
                        
                        if (!keyMissing) return null;
                        
                        // Auto-populate locking wheel nut lines if not already present
                        const hasLwnLabour = jobGroup.labour_lines?.some(l => l.description?.toLowerCase().includes('locking wheel nut'));
                        const hasLwnPart = jobGroup.parts_lines?.some(p => p.description?.toLowerCase().includes('locking wheel nut'));
                        const hasLwnCharge = jobGroup.charges?.some(c => c.description?.toLowerCase().includes('removal kit'));
                        
                        if (!hasLwnLabour || !hasLwnCharge || !hasLwnPart) {
                          // Auto-add missing items
                          const updatedGroup = { ...jobGroup };
                          if (!hasLwnLabour) {
                            updatedGroup.labour_lines = [...(updatedGroup.labour_lines || []), {
                              id: `${jobGroup.id}-lwn-labour`,
                              description: 'Locking Wheel Nut Removal',
                              category: 'strip_renew',
                              hours: 1.0,
                              rate: 0,
                              total: 0,
                              source: 'locking_wheel_nut',
                              vi_item_key: 'locking_wheel_key'
                            }];
                          }
                          if (!hasLwnCharge) {
                            updatedGroup.charges = [...(updatedGroup.charges || []), {
                              id: `${jobGroup.id}-lwn-charge`,
                              description: 'Locking Wheel Nut Removal Kit Usage',
                              amount: 8,
                              vat_rate: 20,
                              source: 'locking_wheel_nut',
                              vi_item_key: 'locking_wheel_key',
                              charge_type: 'environmental'
                            }];
                          }
                          if (!hasLwnPart) {
                            updatedGroup.parts_lines = [...(updatedGroup.parts_lines || []), {
                              id: `${jobGroup.id}-lwn-part`,
                              description: 'Locking Wheel Nut Set (New)',
                              part_number: null,
                              quantity: 1,
                              unit_price: 0,
                              vat_rate: 20,
                              total: 0,
                              is_placeholder: true,
                              source: 'locking_wheel_nut',
                              vi_item_key: 'locking_wheel_key'
                            }];
                          }
                          // Schedule state update (avoid render-cycle setState)
                          setTimeout(() => {
                            setJobGroups(prev => prev.map(jg => 
                              jg.id === jobGroup.id ? updatedGroup : jg
                            ));
                          }, 0);
                        }
                        
                        return (
                        <div className="mt-2 pl-7">
                          <div className="p-3 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg shadow-sm" data-testid="locking-nut-auto-populated">
                            <p className="text-sm font-bold text-red-800 flex items-center gap-2 border-b border-red-200 pb-2 mb-2">
                              <AlertCircle className="w-4 h-4" />
                              LOCKING WHEEL NUT KEY – NOT PRESENT
                            </p>
                            
                            {/* Tech Notes */}
                            {lockingKeyItem.notes && (
                              <p className="text-sm text-slate-700 mb-2 italic">
                                Tech Note: "{lockingKeyItem.notes}"
                              </p>
                            )}
                            
                            <div className="space-y-1 text-sm">
                              <p className="font-medium text-green-700 flex items-center gap-1">
                                <Check className="w-3.5 h-3.5" /> Auto-added to this job:
                              </p>
                              <div className="ml-5 space-y-0.5 text-slate-600">
                                <p>Labour: Locking wheel nut removal (1.0 hour)</p>
                                <p>Charge: Removal kit usage</p>
                                <p>Parts: New locking wheel nut set</p>
                              </div>
                            </div>
                            
                            <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-red-100">
                              Locking nut removal is specialist work requiring additional time and equipment. 
                              Quote appropriately to avoid under-recovery.
                            </p>
                          </div>
                        </div>
                        );
                      })()}
                    </CardHeader>
                    
                    {/* Collapsed state summary - shows at a glance what's in the job */}
                    {!isExpanded && (
                      <div className="px-6 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-600">
                        <div className="flex items-center gap-4">
                          {jobGroup.labour_lines?.length > 0 && (
                            <span>{jobGroup.labour_lines.length} labour line{jobGroup.labour_lines.length !== 1 ? 's' : ''}</span>
                          )}
                          {jobGroup.parts_lines?.length > 0 && (
                            <span>{jobGroup.parts_lines.length} part{jobGroup.parts_lines.length !== 1 ? 's' : ''}</span>
                          )}
                          {jobGroup.charges?.length > 0 && (
                            <span>{jobGroup.charges.length} charge{jobGroup.charges.length !== 1 ? 's' : ''}</span>
                          )}
                          {jobGroup.labour_lines?.length === 0 && jobGroup.parts_lines?.length === 0 && (
                            jobGroup.emissions_suppress_parts === true
                            && ['BLOCKED_TEST', 'UNKNOWN_CAUSE'].includes(jobGroup.emissions_state) ? (
                              <button
                                type="button"
                                className="text-amber-700 font-semibold hover:underline focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const el = document.getElementById('emissions-routing-panel-anchor');
                                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                                data-testid={`emissions-inline-marker-collapsed-${jobGroup.id}`}
                              >
                                {jobGroup.emissions_state === 'BLOCKED_TEST'
                                  ? 'Emissions test blocked — see staged plan above ↑'
                                  : 'Emissions cause unknown — see staged plan above ↑'}
                              </button>
                            ) : (
                              <span className="text-amber-600 font-medium">No pricing added yet</span>
                            )
                          )}
                          {jobGroup.pricing_confirmed && (
                            <span className="text-green-600 font-medium flex items-center gap-1">
                              <Check className="w-3 h-3" /> Complete
                            </span>
                          )}
                          {jobGroup.is_labour_only && (
                            <span className="text-blue-600 font-medium flex items-center gap-1 text-xs bg-blue-50 px-1.5 py-0.5 rounded">
                              Labour Only
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* JCE V2 Inline Hints - Show compact repair guidance.
                        iter197g — Loosened the gate to also render for
                        MCC-derived / DVSA-imported JGs (vi_item_key
                        null but mcc_category/canonical_rule_id/
                        jcu_package_id available). WX12FYD surfaced
                        this — the shock-absorber JG had every
                        identifier except vi_item_key, so the JCE
                        hints never rendered. */}
                    {isExpanded && (jobGroup.vi_item_key || jobGroup.mcc_category || jobGroup.canonical_rule_id || jobGroup.jcu_package_id || jobGroup.repair_intent_code) && (
                      <div className="px-6 py-2 border-t border-slate-100 bg-gradient-to-r from-amber-50/50 to-transparent">
                        <JCEV2InlineHints 
                          viItemKey={jobGroup.vi_item_key} 
                          repairIntentCode={jobGroup.repair_intent_code}
                          mccCategory={jobGroup.mcc_category}
                          canonicalRuleId={jobGroup.canonical_rule_id}
                          jcuPackageId={jobGroup.jcu_package_id}
                        />
                      </div>
                    )}
                    
                    {/* Job Integrity Section - Inline integrity display for this job */}
                    {isExpanded && !isNotQuoted && (
                      <div className="px-4">
                        <JobIntegritySection
                          jobGroup={jobGroup}
                          integrityData={jobIntegrityData[jobGroup.id]}
                          onAddItem={(item) => addIntegrityItem(jobGroup.id, item)}
                          onRemoveItem={(item, preset, custom) => removeIntegrityItem(jobGroup.id, item, preset, custom)}
                          onRestoreItem={(item) => restoreIntegrityItem(jobGroup.id, item)}
                          isExpanded={true}
                          isLoading={integrityLoading}
                        />
                      </div>
                    )}
                    
                    {/* Service Interval Item Guidance - Show for interval items when expanded */}
                    {isExpanded && isIntervalItem && (
                      <div className="px-4 py-3 border-t border-slate-100 bg-gradient-to-r from-emerald-50/50 to-transparent">
                        {isOilService ? (
                          <OilServicePanel
                            jobGroup={jobGroup}
                            yestimateId={yestimateId}
                            vehicleInfo={{
                              make: data?.job?.make,
                              model: data?.job?.model,
                              year: data?.job?.year,
                              fuel_type: data?.job?.fuel_type
                            }}
                            onConfirmationComplete={() => {
                              toast.success('Oil service specifications confirmed');
                            }}
                          />
                        ) : (
                          <ServiceMappingPanel
                            serviceItemId={jobGroup.interval_item_id}
                            categoryId={jobGroup.category_id}
                            vehicleInfo={{
                              make: data?.job?.make,
                              model: data?.job?.model,
                              year: data?.job?.year,
                              fuel_type: data?.job?.fuel_type
                            }}
                            onAddPart={(part) => {
                              // Add part to job group
                              const newPart = {
                                id: `part-${Date.now()}`,
                                description: part.name,
                                quantity: part.quantity || 1,
                                unit_price: 0,
                                buy_in_price: 0
                              };
                              addLabourLine(jobGroup.id); // iter213d — addPartLine not defined; fall through to labour line add
                            }}
                            onAddLabour={(labour) => {
                              // Add labour to job group
                              addLabourLine(jobGroup.id);
                            }}
                          />
                        )}
                      </div>
                    )}
                    
                    {isExpanded && (
                      <CardContent className="space-y-4">
                        {/* ============== CARD-LEVEL TOTAL SUMMARY ============== */}
                        {(() => {
                          const labourSum = jobGroup.labour_lines?.reduce((s, l) => s + ((l.hours || 0) * (l.rate || 0)), 0) || 0;
                          const partsSum = jobGroup.parts_lines?.reduce((s, p) => s + ((p.quantity || 0) * (p.unit_price || 0)), 0) || 0;
                          const chargesSum = jobGroup.charges?.reduce((s, c) => s + (c.amount || 0), 0) || 0;
                          return (
                            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid={`card-total-${jobIdx}`}>
                              <div className="flex items-center gap-4 text-xs text-slate-600">
                                {labourSum > 0 && <span>Labour: £{labourSum.toFixed(2)}</span>}
                                {partsSum > 0 && <span>Parts: £{partsSum.toFixed(2)}</span>}
                                {chargesSum > 0 && <span>Charges: £{chargesSum.toFixed(2)}</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Job Total:</span>
                                <span className="text-lg font-bold text-slate-900">£{jobTotals.total.toFixed(2)}</span>
                                <span className="text-[10px] text-slate-400">inc VAT</span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* iter190 — Inline EMISSIONS marker. When the
                            emissions reasoning engine has classified
                            this JG as BLOCKED_TEST or UNKNOWN_CAUSE,
                            parts/labour are intentionally suppressed
                            and the staged plan is shown above the JG
                            list in the top-level EmissionsRoutingPanel.
                            This badge tells FOH why the JG shows £0
                            and scrolls them up to the staged plan with
                            one tap, instead of leaving the card looking
                            empty / broken. */}
                        {jobGroup.emissions_suppress_parts === true
                         && ['BLOCKED_TEST', 'UNKNOWN_CAUSE'].includes(jobGroup.emissions_state) && (
                          <button
                            type="button"
                            onClick={() => {
                              const el = document.getElementById('emissions-routing-panel-anchor');
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            className="w-full flex items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors text-left px-4 py-3"
                            data-testid={`emissions-inline-marker-${jobGroup.id}`}
                          >
                            <div className="shrink-0 mt-0.5">
                              <AlertTriangle className="w-5 h-5 text-amber-700" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-bold text-amber-900 leading-tight">
                                {jobGroup.emissions_state === 'BLOCKED_TEST'
                                  ? 'Emissions test BLOCKED — staged plan applies'
                                  : 'Emissions cause UNKNOWN — diagnostic required'}
                              </p>
                              <p className="text-xs text-amber-800 mt-1 leading-snug">
                                Parts and labour are intentionally suppressed on this job until the staged workflow above resolves. Tap to jump to the staged plan.
                              </p>
                            </div>
                            <div className="shrink-0 self-center">
                              <ArrowUp className="w-4 h-4 text-amber-700" />
                            </div>
                          </button>
                        )}
                        
                        {/* FOH Diagnostic Explanation Script - shown for diagnostic jobs */}
                        {isDiagnosticJob && (
                          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4" data-testid={`diag-foh-script-${jobIdx}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-indigo-700" />
                                <span className="font-semibold text-indigo-900 text-sm">Customer Explanation Script</span>
                              </div>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    "We're recommending an initial diagnostic assessment so the technician can properly investigate and confirm the cause of the fault. From that first authorised time, one of three things will usually happen: either the issue is identified and resolved within that time, the fault is confirmed and we then quote the parts needed, or further testing is required. If anything further is needed, we will always contact you first before going ahead."
                                  );
                                }}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium px-2 py-1 rounded hover:bg-indigo-100 transition-colors"
                                data-testid={`diag-foh-copy-${jobIdx}`}
                              >
                                Copy
                              </button>
                            </div>
                            <p className="text-sm text-indigo-900 leading-relaxed italic">
                              "We're recommending an initial diagnostic assessment so the technician can properly investigate and confirm the cause of the fault. From that first authorised time, one of three things will usually happen: either the issue is identified and resolved within that time, the fault is confirmed and we then quote the parts needed, or further testing is required. If anything further is needed, we will always contact you first before going ahead."
                            </p>
                            <p className="text-xs text-indigo-500 mt-2">
                              Use this when explaining diagnostic charges to the customer
                            </p>
                          </div>
                        )}
                        
                        {/* ============== REPAIR INTELLIGENCE PANEL ============== */}
                        {/* Shows structured technician assessment data for suspension/steering items */}
                        {(jobGroup.repair_intelligence || jobGroup.structured_repair_data) && (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-2" data-testid={`repair-intelligence-panel-${jobGroup.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Wrench className="w-4 h-4 text-slate-600" />
                              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Repair Intelligence</span>
                              {jobGroup.repair_intelligence?.has_pricing_uncertainty && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs border border-amber-300">
                                  Provisional
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                              {jobGroup.repair_intelligence?.fault_label && (
                                <>
                                  <span className="text-slate-500">Fault</span>
                                  <span className="font-medium text-slate-800">{jobGroup.repair_intelligence.fault_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.joint_level_label && jobGroup.repair_intelligence.joint_level !== 'na' && (
                                <>
                                  <span className="text-slate-500">Position</span>
                                  <span className="font-medium text-slate-800">{jobGroup.repair_intelligence.joint_level_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.construction_label && (
                                <>
                                  <span className="text-slate-500">Construction</span>
                                  <span className="font-medium text-slate-800">{jobGroup.repair_intelligence.construction_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.repair_method_label && (
                                <>
                                  <span className="text-slate-500">Repair Method</span>
                                  <span className="font-medium text-slate-800">{jobGroup.repair_intelligence.repair_method_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.arm_condition_label && (
                                <>
                                  <span className="text-slate-500">Arm Condition</span>
                                  <span className="font-medium text-slate-800">{jobGroup.repair_intelligence.arm_condition_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.removal_risk_label && jobGroup.repair_intelligence.removal_risk !== 'standard' && (
                                <>
                                  <span className="text-slate-500">Removal Risk</span>
                                  <span className={`font-medium ${
                                    jobGroup.repair_intelligence.removal_risk === 'high_risk' ? 'text-red-700' : 'text-amber-700'
                                  }`}>{jobGroup.repair_intelligence.removal_risk_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.opposite_side_label && (
                                <>
                                  <span className="text-slate-500">Opposite Side</span>
                                  <span className="font-medium text-slate-800">{jobGroup.repair_intelligence.opposite_side_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.alignment_label && (
                                <>
                                  <span className="text-slate-500">Alignment</span>
                                  <span className={`font-medium ${
                                    jobGroup.repair_intelligence.alignment_action === 'auto_add' ? 'text-red-700' 
                                    : jobGroup.repair_intelligence.alignment_action === 'strong_suggest' ? 'text-amber-700'
                                    : 'text-slate-800'
                                  }`}>{jobGroup.repair_intelligence.alignment_label}</span>
                                </>
                              )}
                              {jobGroup.repair_intelligence?.pairing_recommendation && jobGroup.repair_intelligence.pairing_recommendation !== 'NO_SIGNAL' && (
                                <>
                                  <span className="text-slate-500">Pairing</span>
                                  <span className="font-medium text-blue-700">
                                    {jobGroup.repair_intelligence.pairing_recommendation === 'QUOTE_BOTH_DEFAULT' ? 'Quote Both Sides' : 'Recommend Pair'}
                                  </span>
                                </>
                              )}
                              {/* Repair path summary */}
                              {jobGroup.repair_intelligence?.repair_path?.repair_path && (
                                <>
                                  <span className="text-slate-500">Repair Path</span>
                                  <span className="font-medium text-slate-800">
                                    {jobGroup.repair_intelligence.repair_path.repair_path === 'joint_replacement' ? 'Joint Only' 
                                     : jobGroup.repair_intelligence.repair_path.repair_path === 'arm_replacement' ? 'Complete Arm'
                                     : jobGroup.repair_intelligence.repair_path.repair_path === 'press_in' ? 'Press-In Joint'
                                     : jobGroup.repair_intelligence.repair_path.repair_path === 'provisional' ? 'Provisional — Confirm at Strip'
                                     : 'Standard'}
                                  </span>
                                </>
                              )}
                            </div>
                            {/* Labour caution banner */}
                            {jobGroup.repair_intelligence?.has_labour_caution && (
                              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                                <strong>Labour Caution:</strong> {jobGroup.repair_intelligence.repair_path?.labour_caution || jobGroup.repair_intelligence.removal_note?.text}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* ============== ENHANCED ITEM INTELLIGENCE PANEL ============== */}
                        {/* Shows structured technician assessment for non-suspension items */}
                        {jobGroup.enhanced_item_intelligence && (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-2" data-testid={`enhanced-intelligence-panel-${jobGroup.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Wrench className="w-4 h-4 text-slate-600" />
                              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                {jobGroup.enhanced_item_intelligence.panel_title || 'Tech Assessment'}
                              </span>
                              {jobGroup.enhanced_item_intelligence.diagnostic_required && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs border border-amber-300">
                                  Diagnostic Required
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                              {jobGroup.enhanced_item_intelligence.labels?.map((entry, idx) => (
                                <span key={`enh-${idx}`} className="contents">
                                  <span className="text-slate-500">{entry.field_label}</span>
                                  <span className={`font-medium ${
                                    entry.highlight === 'danger' ? 'text-red-700' 
                                    : entry.highlight === 'warning' ? 'text-amber-700'
                                    : 'text-slate-800'
                                  }`}>{entry.value_label}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Labour Only indicator with undo */}
                        {jobGroup.is_labour_only && (
                          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid={`labour-only-badge-${jobGroup.id}`}>
                            <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                              <Wrench className="w-4 h-4" />
                              Confirmed as Labour Only — no parts required
                            </div>
                            <button
                              onClick={() => setJobGroups(prev => prev.map(jg => 
                                jg.id === jobGroup.id ? { ...jg, is_labour_only: false } : jg
                              ))}
                              className="text-xs text-blue-500 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                              data-testid={`undo-labour-only-${jobGroup.id}`}
                            >
                              Undo
                            </button>
                          </div>
                        )}
                        
                        {/* Labour Lines */}
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <Label className="text-xs font-medium text-blue-600">LABOUR</Label>
                            <Button variant="ghost" size="sm" onClick={() => addLabourLine(jobGroup.id)} data-testid={`add-labour-${jobIdx}`}>
                              <Plus className="w-3 h-3 mr-1" /> Add Labour
                            </Button>
                          </div>
                          {jobGroup.labour_lines?.map((line, lineIdx) => (
                            <div key={line.id} className="border rounded p-3 mb-2 bg-blue-50/30">
                              {/* Show "Added via job package" label if applicable */}
                              {line.source === "job_package" && (
                                <div className="mb-2">
                                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 bg-blue-50">
                                    <Package className="w-3 h-3 mr-1" /> Added via job package
                                  </Badge>
                                </div>
                              )}
                              <div className="grid grid-cols-12 gap-2 items-end">
                                <div className="col-span-4">
                                  <Label className="text-xs">Category</Label>
                                  <Select 
                                    value={line.category} 
                                    onValueChange={(v) => updateLabourLine(jobGroup.id, line.id, "category", v)}
                                  >
                                    <SelectTrigger className="text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Object.entries(labourCategories).map(([key, cat]) => (
                                        <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Hours</Label>
                                  <Input
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    value={line.hours || ""}
                                    onChange={(e) => updateLabourLine(jobGroup.id, line.id, "hours", parseFloat(e.target.value) || 0)}
                                    placeholder="0.0"
                                    data-testid={`labour-hours-input-${jobIdx}-${lineIdx}`}
                                  />
                                  {/* iter197w — Provenance chip: tester-seeded hours from MCC.
                                      Disappears the moment FOH edits any field on this line. */}
                                  {line.source === "mcc_tester_estimate" && (
                                    <p
                                      className="text-[10px] text-indigo-600 mt-1 font-medium flex items-center gap-1 leading-tight"
                                      data-testid={`labour-hours-from-tester-${jobIdx}-${lineIdx}`}
                                    >
                                      <Sparkles className="w-3 h-3 shrink-0" />
                                      from tester · edit to override
                                    </p>
                                  )}
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Rate (£/hr)</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={line.rate || ""}
                                    onChange={(e) => updateLabourLine(jobGroup.id, line.id, "rate", parseFloat(e.target.value) || 0)}
                                    placeholder="0"
                                  />
                                </div>
                                <div className="col-span-3 text-right">
                                  <span className="text-sm text-slate-600">
                                    £{((line.hours * line.rate) * 1.2).toFixed(2)} inc VAT
                                  </span>
                                </div>
                                <div className="col-span-1">
                                  <Button variant="ghost" size="sm" onClick={() => removeLabourLine(jobGroup.id, line.id)} data-testid={`delete-labour-${jobIdx}-${lineIdx}`}>
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Parts Lines */}
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <Label className="text-xs font-medium text-green-600">PARTS</Label>
                            <Button variant="ghost" size="sm" onClick={() => addPartsLine(jobGroup.id)} data-testid={`add-parts-${jobIdx}`}>
                              <Plus className="w-3 h-3 mr-1" /> Add Parts
                            </Button>
                          </div>
                          {jobGroup.parts_lines?.map((line, lineIdx) => {
                            // Calculate margin percentage
                            const buyIn = line.buy_in_price || 0;
                            const retail = line.unit_price || 0;
                            const marginPct = buyIn > 0 ? ((retail - buyIn) / buyIn * 100) : 0;
                            const profitPerUnit = retail - buyIn;
                            
                            return (
                            <div key={line.id} className="border rounded p-3 mb-2 bg-green-50/30">
                              {/* Show "Added via job package" label if applicable */}
                              {line.source === "job_package" && (
                                <div className="mb-2">
                                  <Badge variant="outline" className="text-xs text-green-600 border-green-300 bg-green-50">
                                    <Package className="w-3 h-3 mr-1" /> Added via job package
                                  </Badge>
                                </div>
                              )}
                              {/* Show auto-scaled qty badge when tech flagged multiple corners */}
                              {line.qty_multiplier_reason && (
                                <div className="mb-2">
                                  <Badge variant="outline" className="text-xs text-blue-700 border-blue-300 bg-blue-50" data-testid={`qty-multiplier-badge-${jobIdx}-${lineIdx}`}>
                                    ×{line.quantity} auto-scaled · {line.qty_multiplier_reason}
                                  </Badge>
                                </div>
                              )}
                              {/* Pairs / supplier-qty sanity check — flags parts
                                  conventionally sold singly that are commonly
                                  replaced in pairs (brake discs, shocks, springs,
                                  drums, calipers). The auto-quote template often
                                  labels these "(pair)" but supplier qty is 2.
                                  We don't auto-correct (the FOH might be replacing
                                  just one corner) — just surface the warning. */}
                              {(() => {
                                const desc = (line.description || '').toLowerCase();
                                const qty = parseFloat(line.quantity) || 1;
                                // Brake pads ALWAYS ship as a set (one box per
                                // axle covers a pair). Pad-fitting kits too.
                                // Suppress the warning entirely for these —
                                // qty=1 is the correct quantity.
                                const isAxleSetByDefault =
                                  /\bbrake\s*pad/.test(desc) ||
                                  /\bpad\s*(set|kit)\b/.test(desc) ||
                                  /\bpads?\b.*\bset\b/.test(desc) ||
                                  /\bfitting\s*kit\b/.test(desc) ||
                                  /\baxle\s*set\b/.test(desc);
                                if (isAxleSetByDefault) return null;
                                const labelSuggestsPair =
                                  desc.includes('(pair)') ||
                                  desc.includes(' pair') ||
                                  /\bpair\b/.test(desc);
                                const isSoldSingly =
                                  // Always sold singly per supplier line
                                  /brake disc(?!\s*pad)/.test(desc) ||
                                  /\brotor\b/.test(desc) ||
                                  /\bshock(\s|$)|\bshock absorber|\bdamper/.test(desc) ||
                                  /\bcoil spring|\bsuspension spring|^spring\b|\bsprings?\b/.test(desc) ||
                                  /\bbrake drum/.test(desc) ||
                                  /\bbrake caliper|\bbrake calliper/.test(desc) ||
                                  /\bcv joint|\bdrive shaft/.test(desc) ||
                                  // Suspension arms, wishbones, control arms — wear in pairs,
                                  // alignment is mandatory after replacement on either side.
                                  /\bsuspension arm|\bcontrol arm|\bwishbone|\btrailing arm/.test(desc) ||
                                  /\bball joint|\btrack rod end|\btie rod end/.test(desc) ||
                                  /\bdrop link|\banti[-\s]?roll bar link|\barb link/.test(desc);
                                // Warn if: a) label says pair but qty=1 (most common case)
                                //       b) sold-singly part with no qty=2 and no explicit single corner mention
                                const explicitSingleCorner = /\b(nsf|osf|nsr|osr|n\/s|o\/s|near\s*side|off\s*side)\b/i.test(line.description || '');
                                const needsPairWarning =
                                  (labelSuggestsPair && qty < 2) ||
                                  (isSoldSingly && qty < 2 && !explicitSingleCorner);
                                if (!needsPairWarning) return null;
                                return (
                                  <div
                                    className="mb-2 flex items-start gap-2 px-2.5 py-2 rounded bg-amber-50 border border-amber-300"
                                    data-testid={`pairs-warning-${jobIdx}-${lineIdx}`}
                                  >
                                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 text-[12px] leading-snug">
                                      <span className="font-semibold text-amber-900">Pairs check — </span>
                                      <span className="text-amber-800">
                                        {labelSuggestsPair
                                          ? `Description says "pair" but qty is ${qty}. Supplier ships singly — set qty = 2 for a pair replacement.`
                                          : `${desc.match(/brake disc/) ? 'Brake discs' : desc.match(/rotor/) ? 'Rotors' : desc.match(/shock|damper/) ? 'Shocks/dampers' : desc.match(/spring/) ? 'Springs' : desc.match(/drum/) ? 'Brake drums' : desc.match(/caliper|calliper/) ? 'Brake calipers' : 'CV joints/drive shafts'} are sold singly. If replacing a pair, change qty to 2 — otherwise tag the corner (e.g. "OSF") in the description.`}
                                      </span>
                                      <div className="mt-1.5 flex gap-2">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updatePartsLine(jobGroup.id, line.id, "quantity", 2);
                                          }}
                                          className="text-[11px] font-semibold text-amber-900 underline hover:text-amber-700"
                                          data-testid={`pairs-warning-fix-${jobIdx}-${lineIdx}`}
                                        >
                                          Set qty to 2
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                              {/* Row 1: Description and Quantity */}
                              <div className="grid grid-cols-12 gap-2 items-end mb-2">
                                <div className="col-span-7">
                                  <Label className="text-xs">Description</Label>
                                  <Input
                                    value={line.description}
                                    onChange={(e) => updatePartsLine(jobGroup.id, line.id, "description", e.target.value)}
                                    placeholder="Part description"
                                    data-testid={`parts-desc-${jobIdx}-${lineIdx}`}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Qty</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={line.quantity}
                                    onChange={(e) => updatePartsLine(jobGroup.id, line.id, "quantity", parseFloat(e.target.value) || 1)}
                                    data-testid={`parts-qty-${jobIdx}-${lineIdx}`}
                                  />
                                </div>
                                <div className="col-span-2 text-right">
                                  <span className="text-sm font-medium text-slate-700">
                                    £{((line.quantity * line.unit_price) * (1 + (line.vat_rate || 20) / 100)).toFixed(2)}
                                  </span>
                                  <span className="text-xs text-slate-500 block">inc VAT</span>
                                </div>
                                <div className="col-span-1">
                                  <Button variant="ghost" size="sm" onClick={() => removePartsLine(jobGroup.id, line.id)} data-testid={`delete-parts-${jobIdx}-${lineIdx}`}>
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </Button>
                                </div>
                              </div>
                              {/* Row 2: Buy-In Price, Retail Price, and Margin */}
                              <MarginPriceRow
                                line={line}
                                jobGroupId={jobGroup.id}
                                jobIdx={jobIdx}
                                lineIdx={lineIdx}
                                buyIn={buyIn}
                                retail={retail}
                                marginPct={marginPct}
                                profitPerUnit={profitPerUnit}
                                updatePartsLine={updatePartsLine}
                              />
                            </div>
                          )})}
                        </div>

                        {/* Charges */}
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <Label className="text-xs font-medium text-purple-600">CHARGES</Label>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => addCharge(jobGroup.id, "consumables")}>
                                <Plus className="w-3 h-3 mr-1" /> Consumables
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => addCharge(jobGroup.id, "environmental")}>
                                <Plus className="w-3 h-3 mr-1" /> Environmental
                              </Button>
                            </div>
                          </div>
                          {jobGroup.charges?.map((charge, chargeIdx) => (
                            <div key={charge.id} className="border rounded p-3 mb-2 bg-purple-50/30">
                              <div className="grid grid-cols-12 gap-2 items-end">
                                <div className="col-span-2">
                                  <Badge variant="outline" className="text-xs">
                                    {charge.charge_type === "consumables" ? "Consumables" : "Environmental"}
                                  </Badge>
                                </div>
                                <div className="col-span-5">
                                  <Input
                                    value={charge.description}
                                    onChange={(e) => updateCharge(jobGroup.id, charge.id, "description", e.target.value)}
                                    placeholder="Description"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs">Amount £</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={charge.amount}
                                    onChange={(e) => updateCharge(jobGroup.id, charge.id, "amount", parseFloat(e.target.value) || 0)}
                                  />
                                </div>
                                <div className="col-span-2 text-right">
                                  <span className="text-sm text-slate-600">
                                    £{(charge.amount * (1 + charge.vat_rate / 100)).toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-1">
                                  <Button variant="ghost" size="sm" onClick={() => removeCharge(jobGroup.id, charge.id)} data-testid={`delete-charge-${jobIdx}-${chargeIdx}`}>
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Package Notes - Non-priced notes from applied job packages */}
                        {jobGroup.package_notes?.length > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-amber-600" />
                              <Label className="text-xs font-medium text-amber-700">PACKAGE NOTES</Label>
                            </div>
                            {jobGroup.package_notes.map((note, noteIdx) => (
                              <div key={noteIdx} className="flex items-start gap-2 py-1.5 text-sm text-amber-900 border-b border-amber-200 last:border-b-0">
                                {note.note_type === "warning" && <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />}
                                {note.note_type === "info" && <FileText className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />}
                                <span>{note.text}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* iter197w — Unified Customer Message card.
                            Amalgamates (a) the Customer Advice Engine
                            headline + full story, (b) the Standard
                            Advisories toggle list, (c) the Contextual
                            Customer Note (workshop nuance), and (d) the
                            Standard Advisory Template Engine (SATE) /
                            recommended approach into one calm panel
                            with a single "Regenerate" coordinator. The
                            inner components (CAESection, SATESection,
                            ContextualCustomerNoteSection) keep their
                            own data shapes + handlers untouched. */}
                        <CustomerMessageCard
                          jobGroup={jobGroup}
                          jobIdx={jobIdx}
                          yestimateId={yestimateId}
                          onToggleCustomerNote={(jgId, noteKey) =>
                            toggleCustomerNote(jgId, noteKey)
                          }
                          onContextualNoteChange={(next) =>
                            setJobGroups((prev) =>
                              prev.map((jg) => {
                                if (jg.id !== jobGroup.id) return jg;
                                const { _customer_notes_override, ...rest } = next || {};
                                const patch = { contextual_customer_note: rest };
                                if (_customer_notes_override) {
                                  patch.customer_notes = _customer_notes_override;
                                }
                                return { ...jg, ...patch };
                              })
                            )
                          }
                          onSATEChange={(next) =>
                            setJobGroups((prev) =>
                              prev.map((jg) =>
                                jg.id === jobGroup.id ? { ...jg, sate: next } : jg
                              )
                            )
                          }
                          onCAEApplied={async () => {
                            try {
                              const refresh = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                              setJobGroups(sanitizeJobGroups(refresh.data.yestimate.job_groups || []));
                              setData(refresh.data);
                            } catch (e) {
                              // swallow — toast already raised by child
                            }
                          }}
                        />

                        {/* Read-only tester evidence (MCC) has been
                            relocated to appear directly under the item
                            header (above this expanded body), so FOH no
                            longer has to scroll past parts/labour to see
                            what the tester captured. */}

                        {/* Job Guide & Suggested Items - Powered by Canonical Rule Engine */}
                        <JobGuidePanel
                          jobGroup={jobGroup}
                          yestimateId={yestimateId}
                          onPricingUpdate={async (jobGroupId, addedLines) => {
                            // Refresh job groups after pricing update
                            if (addedLines && addedLines.length > 0) {
                              // iter190 — OPTIMISTIC LOCAL APPEND FIRST.
                              // The canonical rule engine already persisted
                              // the lines server-side. We must mirror them
                              // into local state SYNCHRONOUSLY so that any
                              // pending or imminent auto-save PUT carries
                              // the new lines. Without this, the in-flight
                              // auto-save closure (captured before the GET
                              // resync below completes) would overwrite the
                              // DB with `charges: []` / `parts_lines: [...]`
                              // sans the just-applied suggestion — which
                              // produced the "Added badge but no line on
                              // the card" bug (e.g. Exhaust Consumables).
                              setJobGroups(prev => prev.map(jg => {
                                if (jg.id !== jobGroupId) return jg;
                                const next = { ...jg };
                                const appended = { labour: [], part: [], consumable: [] };
                                for (const al of addedLines) {
                                  if (al?.type && al?.line) appended[al.type]?.push(al.line);
                                }
                                if (appended.labour.length) {
                                  next.labour_lines = [...(next.labour_lines || []), ...appended.labour];
                                }
                                if (appended.part.length) {
                                  next.parts_lines = [...(next.parts_lines || []), ...appended.part];
                                }
                                if (appended.consumable.length) {
                                  next.charges = [...(next.charges || []), ...appended.consumable];
                                }
                                return next;
                              }));
                              // Re-fetch the yestimate to get the canonical
                              // server view (applied_rules list, audit
                              // stamps, dedup outcome).
                              try {
                                const yestimateRes = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                                if (yestimateRes.data.yestimate?.job_groups) {
                                  setJobGroups(sanitizeJobGroups(yestimateRes.data.yestimate.job_groups));
                                }
                              } catch (err) {
                                console.error('Failed to refresh job groups:', err);
                              }
                              refreshJCUEvaluation();
                            }
                          }}
                          appliedRules={jobGroup.applied_rules || []}
                          onAddPart={(part, jobGroupId) => {
                            // Add a part line to the job group
                            const newPartLine = {
                              id: `part-${Date.now()}`,
                              description: part.name,
                              part_number: '',
                              quantity: part.quantity || 1,
                              unit_price: 0,
                              buy_in_price: 0
                            };
                            setJobGroups(jobGroups.map(jg => 
                              jg.id === jobGroupId 
                                ? { ...jg, parts_lines: [...(jg.parts_lines || []), newPartLine] }
                                : jg
                            ));
                          }}
                          onAddLabour={(labour, jobGroupId) => {
                            // Add a labour line to the job group - rate from Price Book
                            const labourCat = labour.category || 'strip_renew';
                            const newLabourLine = {
                              id: `labour-${Date.now()}`,
                              category: labourCat,
                              description: labour.description,
                              hours: labour.hours || 0,
                              rate: getLabourRate(labourCat, 65)
                            };
                            setJobGroups(jobGroups.map(jg => 
                              jg.id === jobGroupId 
                                ? { ...jg, labour_lines: [...(jg.labour_lines || []), newLabourLine] }
                                : jg
                            ));
                          }}
                        />

                        {/* Job Subtotal */}
                        <div className="border-t pt-3 text-right space-y-1">
                          <p className="text-sm text-slate-500">Subtotal: £{jobTotals.subtotal.toFixed(2)}</p>
                          <p className="text-sm text-slate-500">VAT: £{jobTotals.vat.toFixed(2)}</p>
                          <p className="font-bold">Job Total: £{jobTotals.total.toFixed(2)} inc VAT</p>
                        </div>
                        
                        {/* ═══════════════════════════════════════════════════════════════
                            INLINE VALIDATION ERRORS
                            Shows when FOH attempts to mark job as complete but validation fails
                            This is the PRIMARY enforcement point - NOT the send screen
                        ═══════════════════════════════════════════════════════════════ */}
                        {jobValidationErrors[jobGroup.id] && jobValidationErrors[jobGroup.id].length > 0 && (
                          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3" data-testid={`validation-errors-${jobGroup.id}`}>
                            <div className="flex items-start gap-3">
                              <div className="bg-red-100 rounded-full p-2 shrink-0">
                                <XCircle className="w-5 h-5 text-red-600" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-red-800 text-sm mb-2">
                                  Cannot mark this job as complete
                                </h4>
                                <p className="text-xs text-red-600 mb-3">
                                  This job is missing required pricing content. Please fix the issues below:
                                </p>
                                <ul className="space-y-2">
                                  {jobValidationErrors[jobGroup.id].map((error, errIdx) => (
                                    <li key={errIdx} className="flex items-start gap-2 text-sm text-red-700">
                                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                                      <span>{error}</span>
                                    </li>
                                  ))}
                                </ul>
                                <div className="mt-3 pt-3 border-t border-red-200">
                                  <p className="text-xs text-red-600 font-medium">
                                    Fix the issues above, then try "Confirm Pricing Complete" again.
                                  </p>
                                </div>
                                {/* Labour Only confirmation - show when parts are flagged as missing */}
                                {jobValidationErrors[jobGroup.id]?.some(e => 
                                  e.includes('require parts') || e.includes('requires') || e.includes('labour-only')
                                ) && !jobGroup.is_labour_only && (
                                  <div className="mt-3 pt-3 border-t border-red-200">
                                    <button
                                      onClick={() => {
                                        setJobGroups(prev => prev.map(jg => 
                                          jg.id === jobGroup.id ? { ...jg, is_labour_only: true } : jg
                                        ));
                                        clearJobValidationErrors(jobGroup.id);
                                      }}
                                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                                      data-testid={`confirm-labour-only-${jobGroup.id}`}
                                    >
                                      <Wrench className="w-4 h-4" />
                                      Confirm Labour Only — No Parts Required
                                    </button>
                                    <p className="text-xs text-slate-500 mt-1 text-center">
                                      Use this when the job intentionally has no parts (e.g. diagnostic, adjustment, inspection)
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 text-red-600 hover:text-red-800 hover:bg-red-100 w-full"
                              onClick={() => clearJobValidationErrors(jobGroup.id)}
                            >
                              Dismiss
                            </Button>
                          </div>
                        )}
                        
                        {/* Confirm Pricing Button */}
                        <div className="border-t pt-3 mt-3">
                          {jobGroup.pricing_confirmed ? (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-green-700">
                                <Check className="w-4 h-4" />
                                <span className="text-sm font-medium">Pricing Confirmed</span>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => unconfirmJobGroupPricing(jobGroup.id)}
                                className="text-slate-500 hover:text-slate-700"
                              >
                                Edit
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              className={`w-full ${jobValidationErrors[jobGroup.id]?.length > 0 
                                ? 'bg-red-500 hover:bg-red-600' 
                                : 'bg-amber-500 hover:bg-amber-600'} text-white`}
                              onClick={() => confirmJobGroupPricing(jobGroup.id)}
                              data-testid={`confirm-pricing-${jobGroup.id}`}
                            >
                              <Check className="w-4 h-4 mr-2" />
                              {jobValidationErrors[jobGroup.id]?.length > 0 
                                ? 'Fix Issues & Try Again' 
                                : 'Confirm Pricing Complete'}
                            </Button>
                          )}
                          {!jobGroup.pricing_confirmed && !jobValidationErrors[jobGroup.id] && jobTotals.total === 0 && (
                            <p className="text-xs text-amber-600 mt-1 text-center">
                              Add labour or parts before confirming
                            </p>
                          )}
                        </div>
                      </CardContent>
                    )}
                    {/* iter211f — AddRectificationPrompt: appears on diagnostic
                        JGs that have no linked rectification yet AND no
                        substantial hand-priced parts+labour already. Suppresses
                        the panel on diag JGs FOH has already priced ad-hoc
                        (avoids visual clutter — the panel is for UNRESOLVED
                        rectifications, not a duplicate rectification path). */}
                    {isDiagnosticJob
                     && !jobGroups.some(jg => jg.linked_diag_jg_id === jobGroup.id)
                     && (() => {
                        // "Substantial pricing" = parts_lines carry non-labour
                        // parts totalling >£20 OR non-diagnostic labour hours
                        // > 0. Diagnostic labour alone (the 1h assessment) is
                        // NOT substantial — it doesn't rectify anything, so
                        // the prompt should still fire.
                        const partsSubtotal = (jobGroup.parts_lines || [])
                          .filter(p => (p.type || 'part') === 'part')
                          .reduce((s, p) => s + (Number(p.subtotal) || Number(p.unit_price) * (Number(p.quantity) || 1) || 0), 0);
                        const rectLabourHours = (jobGroup.labour_lines || [])
                          .filter(l => !(['diagnostics','diagnostic_labour','inspection_testing'].includes(l.category)) && !l.is_diagnostic)
                          .reduce((s, l) => s + (Number(l.hours) || 0), 0);
                        return partsSubtotal <= 20 && rectLabourHours <= 0;
                     })() && (
                      <div className="px-4 pb-3">
                        <AddRectificationPrompt
                          yestimateId={yestimateId}
                          diagJobGroupId={jobGroup.id}
                          diagJobGroup={jobGroup}
                          diagConclusionText={
                            jobGroup.diagnostic_conclusion
                            || jobGroup.conclusion
                            || (jobGroup.customer_notes || [])[0]?.text
                            || jobGroup.notes
                          }
                          onRectificationAdded={async () => {
                            try {
                              const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                              if (r.data?.yestimate?.job_groups) {
                                setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                              }
                              setRecommendationRefreshKey((k) => k + 1);
                            } catch { /* silent refetch fail — the toast already fired */ }
                          }}
                          onRectificationDeferred={async () => {
                            try {
                              const r = await axios.get(`${API}/foh/yestimate/${yestimateId}`);
                              if (r.data?.yestimate?.job_groups) {
                                setJobGroups(sanitizeJobGroups(r.data.yestimate.job_groups));
                              }
                            } catch { /* silent refetch fail */ }
                          }}
                        />
                      </div>
                    )}
                  </Card>
                );
              })
            )}

            {/* Global Charges Panel - Consumables & Environmental */}
            {jobGroups.length > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Mandatory Charges
                  </h3>
                  
                  {/* Consumables */}
                  <div className="flex items-center justify-between py-2 border-b border-blue-200">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={globalCharges.consumables.applied}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              // Show disapply reason dropdown
                              setGlobalCharges(prev => ({
                                ...prev,
                                consumables: { ...prev.consumables, applied: false, showReasonDropdown: true }
                              }));
                            } else {
                              setGlobalCharges(prev => ({
                                ...prev,
                                consumables: { ...prev.consumables, applied: true, disapplyReason: null, showReasonDropdown: false }
                              }));
                            }
                          }}
                          className="w-4 h-4 rounded border-blue-300"
                        />
                        <span className="font-medium text-slate-700">Workshop Consumables</span>
                      </div>
                      <p className="text-xs text-slate-500 ml-6 mt-1">
                        {globalChargesConfig?.consumables?.customer_explanation || "Gloves, cleaning materials, lubricants, protective covers"}
                      </p>
                      {!globalCharges.consumables.applied && (
                        <div className="ml-6 mt-2">
                          <select
                            value={globalCharges.consumables.disapplyReason || ""}
                            onChange={(e) => setGlobalCharges(prev => ({
                              ...prev,
                              consumables: { ...prev.consumables, disapplyReason: e.target.value }
                            }))}
                            className="text-sm border border-amber-300 bg-amber-50 rounded px-2 py-1 text-amber-800"
                          >
                            <option value="">Select reason for removal...</option>
                            {(globalChargesConfig?.consumables?.disapply_reasons || [
                              "Customer declined - aware consumables used",
                              "Included in service package",
                              "Diagnostic only - no consumables used",
                              "Warranty/goodwill work"
                            ]).map((reason, idx) => (
                              <option key={idx} value={reason}>{reason}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <span className={`font-mono font-semibold ${globalCharges.consumables.applied ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
                      £{(globalCharges.consumables.amount || 12.50).toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Environmental Disposal */}
                  <div className="flex items-center justify-between py-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={globalCharges.environmental.applied}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              setGlobalCharges(prev => ({
                                ...prev,
                                environmental: { ...prev.environmental, applied: false, showReasonDropdown: true }
                              }));
                            } else {
                              setGlobalCharges(prev => ({
                                ...prev,
                                environmental: { ...prev.environmental, applied: true, disapplyReason: null, showReasonDropdown: false }
                              }));
                            }
                          }}
                          className="w-4 h-4 rounded border-blue-300"
                        />
                        <span className="font-medium text-slate-700">Environmental & Disposal</span>
                      </div>
                      <p className="text-xs text-slate-500 ml-6 mt-1">
                        {globalChargesConfig?.environmental?.customer_explanation || "Safe disposal of used oil, fluids, filters per environmental regulations"}
                      </p>
                      {!globalCharges.environmental.applied && (
                        <div className="ml-6 mt-2">
                          <select
                            value={globalCharges.environmental.disapplyReason || ""}
                            onChange={(e) => setGlobalCharges(prev => ({
                              ...prev,
                              environmental: { ...prev.environmental, disapplyReason: e.target.value }
                            }))}
                            className="text-sm border border-amber-300 bg-amber-50 rounded px-2 py-1 text-amber-800"
                          >
                            <option value="">Select reason for removal...</option>
                            {(globalChargesConfig?.environmental?.disapply_reasons || [
                              "Customer taking old parts/fluids",
                              "No waste generated",
                              "Diagnostic only - no disposal required",
                              "Included in other charge"
                            ]).map((reason, idx) => (
                              <option key={idx} value={reason}>{reason}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <span className={`font-mono font-semibold ${globalCharges.environmental.applied ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
                      £{(globalCharges.environmental.amount || 6.00).toFixed(2)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Grand Total */}
            {jobGroups.length > 0 && (
              <Card className="bg-slate-100" data-testid="pricing-grand-total-card">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-slate-600">Estimate Total ({jobGroups.length} job{jobGroups.length !== 1 ? "s" : ""})</span>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Subtotal: £{grandTotal.subtotal.toFixed(2)}</p>
                      <p className="text-sm text-slate-500">VAT: £{grandTotal.vat.toFixed(2)}</p>
                      <p className="text-sm text-slate-700 font-medium">New Work: £{grandTotal.newWorkTotal.toFixed(2)}</p>
                      {intentConfig && (
                        <p className="text-sm text-emerald-700 font-medium flex items-center justify-end gap-1.5" data-testid="pricing-already-authorised-line">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Previously Authorised
                          {(intentConfig.already_authorised_tags || []).length > 0 && (
                            <span className="text-[10px] uppercase tracking-wide opacity-70">
                              ({(intentConfig.already_authorised_tags || []).join(", ").toUpperCase()})
                            </span>
                          )}
                          : +£{grandTotal.alreadyAuthorised.toFixed(2)}
                        </p>
                      )}
                      <p className="text-xl font-bold mt-1 pt-1 border-t border-slate-300" data-testid="pricing-customer-total">£{grandTotal.total.toFixed(2)} inc VAT</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  // MOT-only yestimates have no inspection — bounce back to
                  // the FOH dashboard rather than /app/foh/inspection/null
                  // which 404s with "Inspection not found".
                  if (data.yestimate.inspection_id) {
                    navigate(`/app/foh/inspection/${data.yestimate.inspection_id}`);
                  } else {
                    navigate(`/app/foh`);
                  }
                }}
                data-testid="pricing-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setShowMarginSheet(true)}
                data-testid="margin-sheet-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Margin Sheet
              </Button>
              <Button className="flex-1" onClick={handleSave} disabled={saving} data-testid="save-pricing-btn">
                {saving ? "Saving..." : "Save & Preview"}
              </Button>
            </div>
          </div>
        </div>
        
        {/* RIGHT COLUMN - Job Intelligence Panel (embedded, not fixed) */}
        <div className="w-[320px] min-w-[300px] flex-shrink-0 border-l border-slate-200 bg-white overflow-y-auto">
          <JCEV2Panel
            isOpen={true}
            onClose={() => {}}
            jobGroups={jobGroups}
            selectedJob={selectedJobForJCE}
            onSelectJob={handleJCESelectJob}
            vehicleContext={memoizedVehicleContext}
            viItems={data?.items || []}  // Pass VI items for smart filtering
            onAddSuggestion={(suggestion) => {
              // Handle adding JCE V2 suggested item to estimate
              console.log('JCE V2 Add Suggestion:', suggestion);
              
              // Check if this is a brake fluid related suggestion
              const isBrakeFluidSuggestion = /brake.*fluid|brake_fluid/i.test(suggestion.code || '') || 
                                             /brake.*fluid/i.test(suggestion.label || '');
              
              // Check if brake fluid was already tested in VI
              const brakeFluidVIItem = data?.vi_items?.find(i => 
                i.item_key === 'brake_fluid_test' || i.item_key === 'brake_fluid_condition'
              );
              const brakeFluidAlreadyTested = brakeFluidVIItem && 
                brakeFluidVIItem.status && 
                brakeFluidVIItem.status !== 'blue' && // Not N/A
                brakeFluidVIItem.brake_fluid_moisture !== null; // Has actual test data
              
              // If there's a selected job, add to it
              if (selectedJobForJCE) {
                const targetJobGroup = selectedJobForJCE;
                
                // For brake fluid suggestions, add testing labour if not already done in VI
                if (isBrakeFluidSuggestion && !brakeFluidAlreadyTested) {
                  // Add inspection & testing labour line for brake fluid
                  const testingLabourLine = {
                    id: `${targetJobGroup.id}-bf-test-${Date.now()}`,
                    category: "inspection_testing",
                    description: "Brake Fluid Inspection & Testing",
                    hours: 0.25,
                    rate: 65,
                    is_jce_suggestion: true,
                    jce_code: 'brake_fluid_test',
                    auto_added_reason: 'Brake fluid not tested during vehicle inspection'
                  };
                  
                  setJobGroups(prev => prev.map(jg => {
                    if (jg.id === targetJobGroup.id) {
                      // Check if testing line doesn't already exist
                      const hasTestingLine = jg.labour_lines?.some(l => 
                        l.category === 'inspection_testing' && /brake.*fluid/i.test(l.description)
                      );
                      if (!hasTestingLine) {
                        return {
                          ...jg,
                          labour_lines: [...(jg.labour_lines || []), testingLabourLine]
                        };
                      }
                    }
                    return jg;
                  }));
                  
                  toast.info('Added brake fluid testing - not performed during inspection');
                }
                
                // Determine what to add based on suggestion type
                if (suggestion.type === 'labour' || suggestion.type === 'service') {
                  // Add as labour line
                  const newLabourLine = {
                    id: `${targetJobGroup.id}-jcev2-${Date.now()}`,
                    category: "strip_renew",
                    description: suggestion.label,
                    hours: suggestion.estimated_labour_hours || 0.5,
                    rate: 65,
                    is_jce_suggestion: true,
                    jce_code: suggestion.code
                  };
                  
                  setJobGroups(prev => prev.map(jg => {
                    if (jg.id === targetJobGroup.id) {
                      return {
                        ...jg,
                        labour_lines: [...(jg.labour_lines || []), newLabourLine]
                      };
                    }
                    return jg;
                  }));
                } else if (suggestion.type === 'part') {
                  // Add as parts line with proper pricing structure
                  const estimatedCost = suggestion.estimated_part_cost || 5;
                  const retailMarkup = 1.4; // 40% markup on parts
                  const quantity = suggestion.quantity || 1; // Use passed quantity (for paired items)
                  
                  const newPartLine = {
                    id: `${targetJobGroup.id}-part-${Date.now()}`,
                    description: suggestion.label,
                    quantity: quantity,
                    buy_in_price: estimatedCost,
                    unit_price: Math.round(estimatedCost * retailMarkup * 100) / 100, // Retail with markup
                    vat_rate: 20,
                    is_jce_suggestion: true,
                    jce_code: suggestion.code
                  };
                  
                  setJobGroups(prev => prev.map(jg => {
                    if (jg.id === targetJobGroup.id) {
                      return {
                        ...jg,
                        parts_lines: [...(jg.parts_lines || []), newPartLine]
                      };
                    }
                    return jg;
                  }));
                } else if (suggestion.type === 'fee' || suggestion.type === 'package') {
                  // Add as charge
                  const newCharge = {
                    id: `${targetJobGroup.id}-charge-${Date.now()}`,
                    description: suggestion.label,
                    amount: suggestion.estimated_part_cost || suggestion.estimated_labour_hours * 65 || 35,
                    vat_rate: 20,
                    is_jce_suggestion: true,
                    jce_code: suggestion.code
                  };
                  
                  setJobGroups(prev => prev.map(jg => {
                    if (jg.id === targetJobGroup.id) {
                      return {
                        ...jg,
                        charges: [...(jg.charges || []), newCharge]
                      };
                    }
                    return jg;
                  }));
                }
                
                // Ensure the job is expanded
                setExpandedJobs(prev => ({ ...prev, [targetJobGroup.id]: true }));
              } else {
                // No job selected - create a new job group
                const jobGroupId = Date.now().toString();
                const quantity = suggestion.quantity || 1; // Use passed quantity (for paired items)
                
                // Build labour lines array
                const labourLines = [];
                
                // Add main labour if suggestion has labour hours
                if (suggestion.estimated_labour_hours) {
                  labourLines.push({
                    id: `${jobGroupId}-labour`,
                    category: "strip_renew",
                    description: suggestion.label,
                    hours: suggestion.estimated_labour_hours,
                    rate: 65
                  });
                }
                
                // For brake fluid suggestions, add testing labour if not already done in VI
                if (isBrakeFluidSuggestion && !brakeFluidAlreadyTested) {
                  labourLines.push({
                    id: `${jobGroupId}-bf-test`,
                    category: "inspection_testing",
                    description: "Brake Fluid Inspection & Testing",
                    hours: 0.25,
                    rate: 65,
                    auto_added_reason: 'Brake fluid not tested during vehicle inspection'
                  });
                  toast.info('Added brake fluid testing - not performed during inspection');
                }
                
                const newJobGroup = {
                  id: jobGroupId,
                  title: suggestion.label,
                  source: "jce_v2_suggestion",
                  status: "amber",
                  vi_status: "amber",
                  repair_intent_code: suggestion.code,
                  labour_lines: labourLines,
                  parts_lines: suggestion.estimated_part_cost ? [{
                    id: `${jobGroupId}-part`,
                    description: `${suggestion.label} - Parts`,
                    quantity: quantity,
                    buy_in_price: suggestion.estimated_part_cost,
                    unit_price: Math.round(suggestion.estimated_part_cost * 1.4 * 100) / 100, // 40% markup
                    vat_rate: 20
                  }] : [],
                  charges: [],
                  created_at: new Date().toISOString()
                };
                
                setJobGroups(prev => [...prev, newJobGroup]);
                setExpandedJobs(prev => ({ ...prev, [jobGroupId]: true }));
                setSelectedJobForJCE(newJobGroup);
              }
            }}
          />
        </div>
      </div>

      {/* JCE Send Confirmation Modal - Phase 4 */}
      <JCESendConfirmationModal
        isOpen={showJCESendModal}
        onClose={handleJCESendClose}
        onConfirm={handleJCESendConfirm}
        estimateId={data?.yestimate?.id}
        jobGroups={jobGroups}
        vehicleContext={memoizedVehicleContext}
      />
      
      {/* JCU Integrity Score Details Modal */}
      {showJCUDetails && jcuEvaluation && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className={`px-6 py-4 rounded-t-xl ${
              jcuEvaluation.level === 'green' ? 'bg-emerald-500' :
              jcuEvaluation.level === 'amber' ? 'bg-amber-500' : 'bg-red-500'
            } text-white`}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6" />
                  <div>
                    <h2 className="font-bold text-lg">JCU Integrity Score</h2>
                    <p className="text-white/80 text-sm">{jcuEvaluation.message}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{Math.round(jcuEvaluation.score)}%</div>
                  <div className="text-xs uppercase tracking-wider">
                    {jcuEvaluation.can_send ? 'CAN SEND' : 'BLOCKED'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Evaluation Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Packages Evaluated</div>
                  <div className="text-2xl font-bold text-slate-800">{jcuEvaluation.package_count || 0}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Unmatched Jobs</div>
                  <div className="text-2xl font-bold text-slate-800">{jcuEvaluation.unmatched_job_count || 0}</div>
                </div>
              </div>
              
              {/* Score Thresholds Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-2">Score Thresholds</h4>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span>GREEN: ≥80%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span>AMBER: 70-79%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span>RED: &lt;70% (Blocked)</span>
                  </div>
                </div>
              </div>
              
              {/* Labour Overlap Warning */}
              {jcuEvaluation.labour_overlap && jcuEvaluation.labour_overlap.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Labour Overlap Detected
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {jcuEvaluation.labour_overlap.map((overlap, i) => (
                      <li key={i}>
                        {overlap.job_pair}: {overlap.overlap_hours}h overlap ({overlap.recommendation})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Last Evaluation Info */}
              <div className="text-xs text-slate-500 border-t pt-4 mt-4">
                <div className="flex justify-between">
                  <span>Last Trigger: {jcuEvaluation.last_trigger || 'N/A'}</span>
                  <span>
                    Last Evaluated: {jcuEvaluation.last_evaluated_at 
                      ? new Date(jcuEvaluation.last_evaluated_at).toLocaleString('en-GB') 
                      : 'N/A'}
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => refreshJCUEvaluation()}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${jcuLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button onClick={() => setShowJCUDetails(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* JCU Blocked Modal - Shown when send is blocked */}
      {jcuBlockedModal.show && jcuBlockedModal.details && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
            <div className="bg-red-500 px-6 py-4 rounded-t-xl text-white">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6" />
                <div>
                  <h2 className="font-bold text-lg">Estimate Blocked</h2>
                  <p className="text-red-100 text-sm">This estimate cannot be sent due to integrity issues</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-red-600">{Math.round(jcuBlockedModal.details.score)}%</div>
                <div className="text-sm text-red-500">Minimum required: 70%</div>
              </div>
              
              {/* Package Issues */}
              {jcuBlockedModal.details.package_issues?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-700">Issues Found:</h4>
                  {jcuBlockedModal.details.package_issues.map((pkg, i) => (
                    <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-red-800">{pkg.package}</span>
                        <Badge variant="destructive">{pkg.score}%</Badge>
                      </div>
                      <ul className="text-sm text-red-700 space-y-1">
                        {pkg.issues.map((issue, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                <strong>Resolution:</strong> {jcuBlockedModal.details.resolution || 'Add missing mandatory components to proceed'}
              </div>
              
              <div className="flex justify-end">
                <Button onClick={() => setJcuBlockedModal({ show: false, details: null })}>
                  Close & Fix Issues
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* JCU Override Modal - Required for RED estimates */}
      {jcuOverrideModal.show && jcuOverrideModal.details && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="jcu-override-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-orange-500 px-6 py-4 rounded-t-xl text-white">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6" />
                <div>
                  <h2 className="font-bold text-lg">Override Required</h2>
                  <p className="text-orange-100 text-sm">You are overriding a RED integrity warning</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Score Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-red-600">{Math.round(jcuOverrideModal.details.score)}%</div>
                <div className="text-sm text-red-500">Integrity Score (Minimum: 70%)</div>
              </div>
              
              {/* Warning Message */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>Warning:</strong> This estimate has integrity issues that may result in:
                </p>
                <ul className="mt-2 text-sm text-orange-700 list-disc list-inside space-y-1">
                  <li>Customer comebacks due to incomplete repairs</li>
                  <li>Potential warranty claims or rework</li>
                  <li>Reduced customer satisfaction</li>
                </ul>
              </div>
              
              {/* Package Issues Summary */}
              {jcuOverrideModal.details.package_issues?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-700 text-sm">Issues to acknowledge:</h4>
                  {jcuOverrideModal.details.package_issues.slice(0, 2).map((pkg, i) => (
                    <div key={i} className="bg-slate-50 rounded p-2 text-sm">
                      <span className="font-medium">{pkg.package}:</span>
                      <span className="text-slate-600 ml-1">{pkg.issues?.[0] || 'Missing components'}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Override Reason - MANDATORY */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Override Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={jcuOverrideReason}
                  onChange={(e) => setJcuOverrideReason(e.target.value)}
                  placeholder="Explain why this estimate should be sent despite integrity issues..."
                  className="w-full border rounded-lg p-3 text-sm h-24 resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="jcu-override-reason"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This reason will be logged for audit purposes
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setJcuOverrideModal({ show: false, details: null });
                    setJcuOverrideReason('');
                  }}
                >
                  Cancel & Fix Issues
                </Button>
                <Button
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={handleJCUOverrideConfirm}
                  disabled={!jcuOverrideReason.trim()}
                  data-testid="jcu-override-confirm"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Override & Send
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* DRE Diagnostic Override Modal */}
      {dreOverrideModal.show && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="dre-override-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-amber-500 px-6 py-4 rounded-t-xl text-white">
              <div className="flex items-center gap-3">
                <Stethoscope className="w-6 h-6" />
                <div>
                  <h2 className="font-bold text-lg">Remove Diagnostic?</h2>
                  <p className="text-amber-100 text-sm">This job requires diagnostic investigation</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800">
                  Job: {dreOverrideModal.jobTitle}
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  The diagnostic investigation for this job was included because the root cause is not confirmed.
                  Removing it increases the risk of incorrect repairs and customer comebacks.
                </p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Potential Consequences
                </h4>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Parts may be replaced without confirming the fault</li>
                  <li>Customer comeback if root cause was different</li>
                  <li>JCU integrity score penalty applied</li>
                </ul>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Reason for Removing Diagnostic <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={dreOverrideReason}
                  onChange={(e) => setDreOverrideReason(e.target.value)}
                  placeholder="e.g., Customer declined diagnostic, fault already diagnosed elsewhere, visual confirmation sufficient..."
                  className="w-full border rounded-lg p-3 text-sm h-24 resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="dre-override-reason"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This reason will be logged for audit purposes
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setDreOverrideModal({ show: false, jobGroupId: null, jobTitle: null });
                    setDreOverrideReason('');
                  }}
                >
                  Keep Diagnostic
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleDREOverrideConfirm}
                  disabled={!dreOverrideReason.trim()}
                  data-testid="dre-override-confirm"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Remove Anyway
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Margin Sheet Modal - Print-friendly view for FOH staff */}
      {showMarginSheet && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-testid="margin-sheet-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-auto print:max-h-none print:shadow-none print:rounded-none">
            {/* Print Header - Only visible when printing */}
            <div className="hidden print:block p-6 border-b-2 border-slate-300">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">JOB MARGIN BREAKDOWN</h1>
                  <p className="text-slate-600">Internal Use Only - Not for Customer</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-lg font-bold">{data?.job?.reg}</p>
                  <p className="text-sm text-slate-600">
                    {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Modal Header - Hidden when printing */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 print:hidden">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Job Margin Breakdown
                  </h2>
                  <p className="text-amber-100 text-sm">{data?.job?.reg} • {formatVehicleNameFromObject(data?.job)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.print()}
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    data-testid="print-margin-sheet"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print / PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMarginSheet(false)}
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    data-testid="close-margin-sheet"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Close
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Margin Sheet Content */}
            <div className="p-6">
              {/* Vehicle & Customer Info */}
              <div className="grid grid-cols-2 gap-6 mb-6 pb-6 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 mb-2">VEHICLE</h3>
                  <p className="text-2xl font-bold text-slate-800">{data?.job?.reg}</p>
                  <p className="text-slate-600">{formatVehicleNameFromObject(data?.job)} ({data?.job?.year})</p>
                  <p className="text-slate-500 text-sm">{(data?.yestimate?.mileage || data?.job?.mileage)?.toLocaleString()} miles</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 mb-2">CUSTOMER</h3>
                  <p className="text-lg font-semibold text-slate-800">{customerName || 'N/A'}</p>
                  <p className="text-slate-500 text-sm mt-1">Date: {new Date().toLocaleDateString('en-GB')}</p>
                </div>
              </div>

              {/* Job Groups with Breakdown */}
              {jobGroups.map((jobGroup, jgIdx) => {
                // Calculate totals for this job group
                const labourTotal = jobGroup.labour_lines?.reduce((sum, l) => sum + (l.hours * l.rate), 0) || 0;
                const partsRetailTotal = jobGroup.parts_lines?.reduce((sum, p) => sum + (p.quantity * p.unit_price), 0) || 0;
                const partsBuyInTotal = jobGroup.parts_lines?.reduce((sum, p) => sum + (p.quantity * (p.buy_in_price || 0)), 0) || 0;
                const partsMargin = partsRetailTotal - partsBuyInTotal;
                const partsMarginPct = partsBuyInTotal > 0 ? ((partsRetailTotal - partsBuyInTotal) / partsBuyInTotal * 100) : 0;
                const chargesTotal = jobGroup.charges?.reduce((sum, c) => sum + c.amount, 0) || 0;
                const jobTotal = labourTotal + partsRetailTotal + chargesTotal;
                const jobTotalIncVat = jobTotal * 1.2;
                
                return (
                  <div key={jobGroup.id} className="mb-6 border rounded-lg overflow-hidden">
                    {/* Job Header */}
                    <div className="bg-slate-100 px-4 py-3 border-b">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{jgIdx + 1}.</span>
                          <span className="font-semibold text-slate-800">{jobGroup.job_name || jobGroup.title || 'Unnamed Job'}</span>
                          {jobGroup.is_diagnostic && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">Diagnostic</Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-slate-800">£{jobTotalIncVat.toFixed(2)}</span>
                          <span className="text-xs text-slate-500 ml-1">inc VAT</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Job Content */}
                    <div className="p-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-slate-500 text-left">
                            <th className="pb-2 font-medium">Item</th>
                            <th className="pb-2 font-medium text-right">Labour</th>
                            <th className="pb-2 font-medium text-right">Parts (Retail)</th>
                            <th className="pb-2 font-medium text-right">Parts (Buy-In)</th>
                            <th className="pb-2 font-medium text-right">Margin</th>
                            <th className="pb-2 font-medium text-right">Margin %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Labour Lines */}
                          {jobGroup.labour_lines?.map((labour, idx) => (
                            <tr key={`labour-${idx}`} className="border-b border-slate-100">
                              <td className="py-2 text-slate-700">
                                <span className="text-amber-600 text-xs mr-1">L</span>
                                {labour.description || labour.category}
                              </td>
                              <td className="py-2 text-right font-mono text-amber-700">
                                £{(labour.hours * labour.rate).toFixed(2)}
                              </td>
                              <td className="py-2 text-right text-slate-400">-</td>
                              <td className="py-2 text-right text-slate-400">-</td>
                              <td className="py-2 text-right text-slate-400">-</td>
                              <td className="py-2 text-right text-slate-400">-</td>
                            </tr>
                          ))}
                          {/* Parts Lines */}
                          {jobGroup.parts_lines?.map((part, idx) => {
                            const lineRetail = part.quantity * part.unit_price;
                            const lineBuyIn = part.quantity * (part.buy_in_price || 0);
                            const lineMargin = lineRetail - lineBuyIn;
                            const lineMarginPct = lineBuyIn > 0 ? ((lineRetail - lineBuyIn) / lineBuyIn * 100) : 0;
                            return (
                              <tr key={`part-${idx}`} className="border-b border-slate-100">
                                <td className="py-2 text-slate-700">
                                  <span className="text-green-600 text-xs mr-1">P</span>
                                  {part.description}
                                  {part.quantity > 1 && <span className="text-slate-400 text-xs ml-1">x{part.quantity}</span>}
                                </td>
                                <td className="py-2 text-right text-slate-400">-</td>
                                <td className="py-2 text-right font-mono text-green-700">£{lineRetail.toFixed(2)}</td>
                                <td className="py-2 text-right font-mono text-slate-600">
                                  {lineBuyIn > 0 ? `£${lineBuyIn.toFixed(2)}` : '-'}
                                </td>
                                <td className={`py-2 text-right font-mono ${lineMargin > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                                  {lineBuyIn > 0 ? `£${lineMargin.toFixed(2)}` : '-'}
                                </td>
                                <td className={`py-2 text-right font-mono ${
                                  lineMarginPct >= 30 ? 'text-green-600' :
                                  lineMarginPct >= 15 ? 'text-amber-600' :
                                  lineBuyIn > 0 ? 'text-red-600' : 'text-slate-400'
                                }`}>
                                  {lineBuyIn > 0 ? `${lineMarginPct.toFixed(0)}%` : '-'}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Charges */}
                          {jobGroup.charges?.map((charge, idx) => (
                            <tr key={`charge-${idx}`} className="border-b border-slate-100">
                              <td className="py-2 text-slate-700">
                                <span className="text-purple-600 text-xs mr-1">C</span>
                                {charge.description}
                              </td>
                              <td className="py-2 text-right font-mono text-purple-700">£{charge.amount.toFixed(2)}</td>
                              <td className="py-2 text-right text-slate-400">-</td>
                              <td className="py-2 text-right text-slate-400">-</td>
                              <td className="py-2 text-right text-slate-400">-</td>
                              <td className="py-2 text-right text-slate-400">-</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-50 font-medium">
                            <td className="py-2 text-slate-700">Job Subtotal</td>
                            <td className="py-2 text-right font-mono text-amber-700">£{labourTotal.toFixed(2)}</td>
                            <td className="py-2 text-right font-mono text-green-700">£{partsRetailTotal.toFixed(2)}</td>
                            <td className="py-2 text-right font-mono text-slate-600">
                              {partsBuyInTotal > 0 ? `£${partsBuyInTotal.toFixed(2)}` : '-'}
                            </td>
                            <td className={`py-2 text-right font-mono ${partsMargin > 0 ? 'text-green-600 font-bold' : 'text-slate-400'}`}>
                              {partsBuyInTotal > 0 ? `£${partsMargin.toFixed(2)}` : '-'}
                            </td>
                            <td className={`py-2 text-right font-mono ${
                              partsMarginPct >= 30 ? 'text-green-600 font-bold' :
                              partsMarginPct >= 15 ? 'text-amber-600' :
                              partsBuyInTotal > 0 ? 'text-red-600' : 'text-slate-400'
                            }`}>
                              {partsBuyInTotal > 0 ? `${partsMarginPct.toFixed(0)}%` : '-'}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })}

              {/* Grand Totals */}
              {(() => {
                // iter211e — Exclude Not Quoting JGs from the grand
                // totals (matches the backend recomputation in the
                // not-quoted POST + PUT endpoints). FOH footer must
                // show the actual customer-facing total, not the
                // sum of every JG on the screen.
                const quotingJgs = jobGroups.filter(jg => !jg.not_quoted_reason);
                const grandLabour = quotingJgs.reduce((sum, jg) =>
                  sum + (jg.labour_lines?.reduce((s, l) => s + (l.hours * l.rate), 0) || 0), 0);
                const grandPartsRetail = quotingJgs.reduce((sum, jg) =>
                  sum + (jg.parts_lines?.reduce((s, p) => s + (p.quantity * p.unit_price), 0) || 0), 0);
                const grandPartsBuyIn = quotingJgs.reduce((sum, jg) =>
                  sum + (jg.parts_lines?.reduce((s, p) => s + (p.quantity * (p.buy_in_price || 0)), 0) || 0), 0);
                const grandCharges = quotingJgs.reduce((sum, jg) =>
                  sum + (jg.charges?.reduce((s, c) => s + c.amount, 0) || 0), 0);
                const grandPartsMargin = grandPartsRetail - grandPartsBuyIn;
                const grandPartsMarginPct = grandPartsBuyIn > 0 ? ((grandPartsRetail - grandPartsBuyIn) / grandPartsBuyIn * 100) : 0;
                const grandSubtotal = grandLabour + grandPartsRetail + grandCharges;
                const grandVat = grandSubtotal * 0.2;
                const grandNewWorkTotal = grandSubtotal + grandVat;
                // iter197d — Surface previously-authorised work (MOT fee,
                // prior service authorisation) in the FOH pricing review.
                // Without this the displayed TOTAL silently dropped the
                // authorised amount (£54.85 MOT fee on SL59LYZ).
                const alreadyAuthorised = Number(intentConfig?.already_authorised_value || 0);
                const alreadyAuthorisedTags = intentConfig?.already_authorised_tags || [];
                const grandTotal = grandNewWorkTotal + alreadyAuthorised;
                const totalProfit = grandLabour + grandPartsMargin + grandCharges;
                
                return (
                  <div className="border-2 border-amber-500 rounded-lg p-4 bg-amber-50" data-testid="foh-estimate-summary">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-amber-600" />
                      ESTIMATE SUMMARY
                    </h3>
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">Total Labour (ex VAT)</td>
                              <td className="py-2 text-right font-mono font-medium text-amber-700">£{grandLabour.toFixed(2)}</td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">Total Parts Retail (ex VAT)</td>
                              <td className="py-2 text-right font-mono font-medium text-green-700">£{grandPartsRetail.toFixed(2)}</td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">Total Parts Buy-In</td>
                              <td className="py-2 text-right font-mono text-slate-600">
                                {grandPartsBuyIn > 0 ? `£${grandPartsBuyIn.toFixed(2)}` : '-'}
                              </td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">Total Charges (ex VAT)</td>
                              <td className="py-2 text-right font-mono font-medium text-purple-700">£{grandCharges.toFixed(2)}</td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 font-medium">Subtotal (ex VAT)</td>
                              <td className="py-2 text-right font-mono font-bold">£{grandSubtotal.toFixed(2)}</td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">VAT (20%)</td>
                              <td className="py-2 text-right font-mono">£{grandVat.toFixed(2)}</td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 font-medium">New Work Total (inc VAT)</td>
                              <td className="py-2 text-right font-mono font-bold" data-testid="foh-estimate-new-work-total">£{grandNewWorkTotal.toFixed(2)}</td>
                            </tr>
                            {/* iter197d — Previously authorised work
                                row. ALWAYS shown when intentConfig is
                                loaded (even when £0) so FOH can see
                                exactly what's been included. Tags show
                                the source (mot, service, etc.). */}
                            {intentConfig && (
                              <tr className="border-b border-amber-200 bg-emerald-50/60">
                                <td className="py-2 text-emerald-900 font-medium">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                                    <span>Previously Authorised</span>
                                    {alreadyAuthorisedTags.length > 0 && (
                                      <span className="text-[10px] uppercase tracking-wide text-emerald-800/80 font-semibold">
                                        ({alreadyAuthorisedTags.join(", ").toUpperCase()})
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 text-right font-mono font-medium text-emerald-800" data-testid="foh-estimate-already-authorised">
                                  +£{alreadyAuthorised.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td className="py-2 font-bold text-lg">CUSTOMER TOTAL (inc VAT)</td>
                              <td className="py-2 text-right font-mono font-bold text-xl text-green-700" data-testid="foh-estimate-customer-total">£{grandTotal.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="border-l border-amber-300 pl-8">
                        <h4 className="font-semibold text-slate-700 mb-3">MARGIN ANALYSIS</h4>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">Parts Margin (£)</td>
                              <td className={`py-2 text-right font-mono font-bold ${grandPartsMargin > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                                {grandPartsBuyIn > 0 ? `£${grandPartsMargin.toFixed(2)}` : '-'}
                              </td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">Parts Margin (%)</td>
                              <td className={`py-2 text-right font-mono font-bold ${
                                grandPartsMarginPct >= 30 ? 'text-green-600' :
                                grandPartsMarginPct >= 15 ? 'text-amber-600' :
                                grandPartsBuyIn > 0 ? 'text-red-600' : 'text-slate-400'
                              }`}>
                                {grandPartsBuyIn > 0 ? `${grandPartsMarginPct.toFixed(1)}%` : '-'}
                              </td>
                            </tr>
                            <tr className="border-b border-amber-200">
                              <td className="py-2 text-slate-600">Labour Revenue</td>
                              <td className="py-2 text-right font-mono font-bold text-amber-600">£{grandLabour.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td className="py-2 font-medium text-slate-700">Est. Gross Profit</td>
                              <td className="py-2 text-right font-mono font-bold text-green-700 text-lg">
                                £{totalProfit.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <p className="text-xs text-slate-500 mt-3 italic">
                          * Gross profit = Labour + Parts Margin + Charges (excludes overhead)
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Print Footer */}
              <div className="mt-8 pt-4 border-t text-center text-xs text-slate-400 print:block hidden">
                <p>INTERNAL DOCUMENT - NOT FOR CUSTOMER DISTRIBUTION</p>
                <p>Generated: {new Date().toLocaleString('en-GB')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Packages Modal */}
      {showPackagesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="job-packages-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center shrink-0">
              <div>
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Job Packages
                </h2>
                <p className="text-blue-100 text-sm mt-1">Pre-populated labour, parts, and consumables</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPackagesModal(false)}
                className="text-white hover:bg-blue-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Category Tabs */}
            <div className="border-b px-4 py-2 flex gap-2 flex-wrap bg-slate-50 shrink-0">
              <Button
                variant={selectedPackageCategory === null ? "default" : "ghost"}
                size="sm"
                onClick={() => setSelectedPackageCategory(null)}
              >
                All
              </Button>
              {Object.keys(jobPackages.categories || {}).map(cat => (
                <Button
                  key={cat}
                  variant={selectedPackageCategory === cat ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setSelectedPackageCategory(cat)}
                >
                  {cat} ({(jobPackages.categories[cat] || []).length})
                </Button>
              ))}
            </div>

            {/* Package List */}
            <div className="overflow-y-auto flex-1 p-4">
              {loadingPackages ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">Loading packages...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {(selectedPackageCategory 
                    ? jobPackages.categories[selectedPackageCategory] || []
                    : jobPackages.packages || []
                  ).map(pkg => (
                    <div 
                      key={pkg.id}
                      className="border rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-800">{pkg.name}</h3>
                            {pkg.variant && (
                              <Badge className="bg-slate-100 text-slate-600 text-xs">{pkg.variant}</Badge>
                            )}
                            <Badge className="bg-blue-100 text-blue-700 text-xs">{pkg.category}</Badge>
                          </div>
                          {pkg.description && (
                            <p className="text-sm text-slate-600 mt-1">{pkg.description}</p>
                          )}
                          
                          {/* Package Contents Summary */}
                          <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {pkg.estimated_total_hours || pkg.labour_lines?.reduce((sum, l) => sum + l.hours, 0).toFixed(1)} hrs
                            </span>
                            <span className="flex items-center gap-1">
                              <Wrench className="w-3 h-3" />
                              {pkg.labour_lines?.length || 0} labour lines
                            </span>
                            <span className="flex items-center gap-1">
                              <Package className="w-3 h-3" />
                              {pkg.part_lines?.length || 0} parts
                            </span>
                            {pkg.notes?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                {pkg.notes.length} notes
                              </span>
                            )}
                          </div>

                          {/* Expandable Details */}
                          <details className="mt-3">
                            <summary className="text-xs text-blue-600 cursor-pointer hover:underline">
                              View package contents
                            </summary>
                            <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-2 text-xs">
                              {pkg.labour_lines?.length > 0 && (
                                <div>
                                  <p className="font-medium text-slate-600 mb-1">Labour:</p>
                                  <ul className="space-y-0.5 text-slate-500">
                                    {pkg.labour_lines.map((l, i) => (
                                      <li key={i}>• {l.description} ({l.hours}h)</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {pkg.part_lines?.length > 0 && (
                                <div>
                                  <p className="font-medium text-slate-600 mb-1">Parts:</p>
                                  <ul className="space-y-0.5 text-slate-500">
                                    {pkg.part_lines.map((p, i) => (
                                      <li key={i} className="flex items-center gap-1">
                                        • {p.description} x{p.quantity} — £{p.unit_price?.toFixed(2)}
                                        {p.is_mandatory_fixing && <Badge className="bg-orange-100 text-orange-700 text-[10px] py-0">fixing</Badge>}
                                        {p.is_consumable && <Badge className="bg-slate-100 text-slate-600 text-[10px] py-0">consumable</Badge>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {pkg.notes?.length > 0 && (
                                <div>
                                  <p className="font-medium text-slate-600 mb-1">Notes:</p>
                                  <ul className="space-y-0.5 text-slate-500">
                                    {pkg.notes.map((n, i) => (
                                      <li key={i} className={n.note_type === 'warning' ? 'text-amber-600' : n.note_type === 'requirement' ? 'text-red-600' : ''}>
                                        {n.note_type === 'warning' ? '⚠️' : n.note_type === 'requirement' ? '❗' : 'ℹ️'} {n.text}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </details>
                        </div>

                        {/* Edit and Apply Buttons */}
                        <div className="flex items-center gap-2 shrink-0 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPackageEditor(pkg)}
                            className="text-slate-600"
                            data-testid={`edit-package-${pkg.id}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => applyJobPackage(pkg)}
                            disabled={applyingPackage === pkg.id}
                            data-testid={`apply-package-${pkg.id}`}
                          >
                            {applyingPackage === pkg.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-1" />
                                Apply
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(selectedPackageCategory 
                    ? (jobPackages.categories[selectedPackageCategory] || []).length === 0
                    : (jobPackages.packages || []).length === 0
                  ) && (
                    <div className="text-center py-8 text-slate-500">
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No packages found in this category</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t px-6 py-4 bg-slate-50 shrink-0 flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openPackageEditor()}
                  className="text-green-600 border-green-200"
                >
                  <Plus className="w-4 h-4 mr-1" /> Create New Package
                </Button>
              </div>
              <div className="flex gap-2">
                <p className="text-xs text-slate-500 mr-4">
                  Lines added via packages can be edited or removed individually
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowPackagesModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Price Book Modal */}
      <AddFromPriceBookModal
        isOpen={showPriceBookModal}
        onClose={() => setShowPriceBookModal(false)}
        jobGroups={jobGroups}
        onAddItems={(itemData) => {
          // Handle adding items from price book
          if (itemData.targetJobGroupId) {
            // Add to existing job group
            setJobGroups(prev => prev.map(jg => {
              if (jg.id === itemData.targetJobGroupId) {
                const updatedJg = { ...jg };
                itemData.lines.forEach(line => {
                  if (line.category === 'labour') {
                    updatedJg.labour_lines = [...(updatedJg.labour_lines || []), {
                      id: line.id,
                      category: line.category || 'strip_renew',
                      description: line.description,
                      hours: line.hours || line.qty,
                      rate: line.rate || line.unit_price_ex_vat,
                      source: 'price_book',
                      source_type: line.price_book_source?.type,
                      dedupe_key: `price_book_${line.price_book_source?.code}`,
                    }];
                  } else if (line.category === 'part' || line.category === 'fee') {
                    updatedJg.parts_lines = [...(updatedJg.parts_lines || []), {
                      id: line.id,
                      description: line.description,
                      quantity: line.qty || 1,
                      unit_price: line.unit_price_ex_vat,
                      vat_rate: (line.vat_rate || 0.2) * 100,
                      source: 'price_book',
                      source_type: line.price_book_source?.type,
                      dedupe_key: `price_book_${line.price_book_source?.code}`,
                    }];
                  } else if (line.category === 'package') {
                    // Fixed price package - add as labour
                    updatedJg.labour_lines = [...(updatedJg.labour_lines || []), {
                      id: line.id,
                      category: 'fixed_price',
                      description: line.description,
                      hours: 1,
                      rate: line.unit_price_ex_vat,
                      source: 'price_book',
                      source_type: 'package',
                      dedupe_key: `price_book_${line.source_code}`,
                    }];
                  }
                });
                return updatedJg;
              }
              return jg;
            }));
          } else {
            // Create new job group
            const newJobGroup = {
              id: `jg_${Date.now()}`,
              title: itemData.title,
              source: 'price_book',
              status: 'amber',
              labour_lines: [],
              parts_lines: [],
              charges: [],
              pricing_confirmed: false,
            };
            
            itemData.lines.forEach(line => {
              if (line.category === 'labour') {
                newJobGroup.labour_lines.push({
                  id: line.id,
                  category: line.category || 'strip_renew',
                  description: line.description,
                  hours: line.hours || line.qty,
                  rate: line.rate || line.unit_price_ex_vat,
                  source: 'price_book',
                  source_type: line.price_book_source?.type,
                  dedupe_key: `price_book_${line.price_book_source?.code}`,
                });
              } else if (line.category === 'part' || line.category === 'fee') {
                newJobGroup.parts_lines.push({
                  id: line.id,
                  description: line.description,
                  quantity: line.qty || 1,
                  unit_price: line.unit_price_ex_vat,
                  vat_rate: (line.vat_rate || 0.2) * 100,
                  source: 'price_book',
                  source_type: line.price_book_source?.type,
                  dedupe_key: `price_book_${line.price_book_source?.code}`,
                });
              } else if (line.category === 'package') {
                // Fixed price package - add as labour
                newJobGroup.labour_lines.push({
                  id: line.id,
                  category: 'fixed_price',
                  description: line.description,
                  hours: 1,
                  rate: line.unit_price_ex_vat,
                  source: 'price_book',
                  source_type: 'package',
                  dedupe_key: `price_book_${line.source_code}`,
                });
              }
            });
            
            setJobGroups(prev => [...prev, newJobGroup]);
            // iter213d — setSelectedJobIndex not defined in this scope; no-op
          }
          setShowPriceBookModal(false);
        }}
      />

      {/* Auto-Populate Options Modal */}
      {showAutoPopulateOptions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="auto-populate-modal">
          <div className="bg-white rounded-xl max-w-md w-full mx-4 shadow-2xl">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-slate-800">Auto-Populate from VI</h2>
              <p className="text-sm text-slate-500 mt-1">
                Generate pricing jobs from Red/Amber inspection findings
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <p className="font-medium text-red-800">RED Items</p>
                  <p className="text-xs text-red-600">Safety-critical - always included</p>
                </div>
                <Badge className="bg-red-500 text-white">Mandatory</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <p className="font-medium text-amber-800">AMBER Items</p>
                  <p className="text-xs text-amber-600">Advisory items</p>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={autoPopulateIncludeAmber}
                    onChange={(e) => setAutoPopulateIncludeAmber(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Include</span>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="font-medium text-blue-800">Use Job Packages</p>
                  <p className="text-xs text-blue-600">Apply full packages where available</p>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={autoPopulateUsePackages}
                    onChange={(e) => setAutoPopulateUsePackages(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Use</span>
                </label>
              </div>
              
              <div className="p-3 bg-slate-100 rounded-lg text-xs text-slate-600">
                <p><strong>How it works:</strong></p>
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>Creates a job group for each flagged VI item</li>
                  <li>Uses job templates to pre-fill labour and parts</li>
                  <li>Parts are placeholders - you set the prices</li>
                  <li>RED items must be priced before sending</li>
                </ul>
              </div>
            </div>
            
            <div className="p-4 border-t bg-slate-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAutoPopulateOptions(false)}>
                Cancel
              </Button>
              <Button 
                onClick={autoPopulatePricing}
                disabled={autoPopulating}
                className="bg-green-600 hover:bg-green-700"
              >
                {autoPopulating ? "Populating..." : "Generate Pricing Jobs"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Not Quoted Modal */}
      {showNotQuotedModal && notQuotedJobGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="not-quoted-modal">
          <div className="bg-white rounded-xl max-w-md w-full mx-4 shadow-2xl">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-slate-800">Mark as Not Quoted</h2>
              <p className="text-sm text-slate-500 mt-1">
                {notQuotedJobGroup.title}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium">Reason (Required)</Label>
                <Select value={notQuotedReason} onValueChange={setNotQuotedReason}>
                  <SelectTrigger className="mt-1" data-testid="not-quoted-reason-select">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/* iter211 — Expanded coverage so FOH can capture
                        the most common "why this is on the worksheet
                        but NOT being charged" scenarios without
                        falling back to free-text. */}
                    <SelectItem value="already_in_another_job">Already covered by another job (combo/bundle)</SelectItem>
                    <SelectItem value="pads_included_in_disc_combo">Pads already included in disc combo</SelectItem>
                    <SelectItem value="separately_priced">Already priced separately elsewhere</SelectItem>
                    <SelectItem value="duplicate_of_another_job">Duplicate / superseded by another job</SelectItem>
                    <SelectItem value="customer_supplied_parts">Customer supplying own parts</SelectItem>
                    <SelectItem value="awaiting_diagnosis">Awaiting further diagnosis</SelectItem>
                    <SelectItem value="price_pending">Price pending supplier confirmation</SelectItem>
                    <SelectItem value="goodwill_no_charge">No-charge / goodwill / under warranty</SelectItem>
                    <SelectItem value="not_applicable">Not applicable to customer requirement</SelectItem>
                    <SelectItem value="deferred">Customer wishes to postpone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Notes (Optional)</Label>
                <textarea
                  value={notQuotedNotes}
                  onChange={(e) => setNotQuotedNotes(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-lg text-sm"
                  rows={3}
                  placeholder="Add any additional context..."
                />
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                This item will be excluded from the customer quote. The reason will be recorded for audit purposes.
              </div>
            </div>
            
            <div className="p-4 border-t bg-slate-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNotQuotedModal(false)} disabled={isSubmittingNotQuoted}>
                Cancel
              </Button>
              <Button
                onClick={submitNotQuoted}
                disabled={!notQuotedReason || isSubmittingNotQuoted}
                data-testid="not-quoted-confirm-btn"
              >
                {isSubmittingNotQuoted ? "Confirming…" : "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Package Editor Modal */}
      {showPackageEditor && editingPackage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="package-editor-modal">
          <div className="bg-white rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-6 border-b shrink-0">
              <h2 className="text-xl font-bold text-slate-800">
                {editingPackage.id ? "Edit Package" : "Create New Package"}
              </h2>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Package Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Package Name *</Label>
                  <Input
                    value={editingPackage.name}
                    onChange={(e) => setEditingPackage({ ...editingPackage, name: e.target.value })}
                    placeholder="e.g. Front Brake Service"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={editingPackage.category} 
                    onValueChange={(v) => setEditingPackage({ ...editingPackage, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Brakes">Brakes</SelectItem>
                      <SelectItem value="Service">Service</SelectItem>
                      <SelectItem value="Engine">Engine</SelectItem>
                      <SelectItem value="Suspension">Suspension</SelectItem>
                      <SelectItem value="Transmission">Transmission</SelectItem>
                      <SelectItem value="Air Conditioning">Air Conditioning</SelectItem>
                      <SelectItem value="Electrical">Electrical</SelectItem>
                      <SelectItem value="Exhaust">Exhaust</SelectItem>
                      <SelectItem value="General">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Variant (Optional)</Label>
                  <Input
                    value={editingPackage.variant || ""}
                    onChange={(e) => setEditingPackage({ ...editingPackage, variant: e.target.value })}
                    placeholder="e.g. Transit 2.0 TDCi"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={editingPackage.description || ""}
                    onChange={(e) => setEditingPackage({ ...editingPackage, description: e.target.value })}
                    placeholder="Brief description..."
                  />
                </div>
              </div>
              
              {/* Labour Lines */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-blue-600 font-medium">Labour Lines</Label>
                  <Button variant="ghost" size="sm" onClick={addEditingPackageLabourLine}>
                    <Plus className="w-4 h-4 mr-1" /> Add Labour
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingPackage.labour_lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-blue-50/50 p-2 rounded">
                      <div className="col-span-5">
                        <Input
                          value={line.description}
                          onChange={(e) => updateEditingPackageLabour(idx, "description", e.target.value)}
                          placeholder="Labour description..."
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-3">
                        <Select 
                          value={line.category} 
                          onValueChange={(v) => updateEditingPackageLabour(idx, "category", v)}
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="strip_renew">Strip & Renew</SelectItem>
                            <SelectItem value="service_labour">Service Labour</SelectItem>
                            <SelectItem value="diagnostics">Diagnostics</SelectItem>
                            <SelectItem value="inspection_testing">Inspection</SelectItem>
                            <SelectItem value="wheel_alignment">Alignment</SelectItem>
                            <SelectItem value="programming_coding">Programming</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={line.hours}
                          onChange={(e) => updateEditingPackageLabour(idx, "hours", parseFloat(e.target.value) || 0)}
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => removeEditingPackageLabourLine(idx)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Parts Lines */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-green-600 font-medium">Parts & Consumables</Label>
                  <Button variant="ghost" size="sm" onClick={addEditingPackagePartLine}>
                    <Plus className="w-4 h-4 mr-1" /> Add Part
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingPackage.part_lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-green-50/50 p-2 rounded">
                      <div className="col-span-4">
                        <Input
                          value={line.description}
                          onChange={(e) => updateEditingPackagePart(idx, "description", e.target.value)}
                          placeholder="Part description..."
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="1"
                          value={line.quantity}
                          onChange={(e) => updateEditingPackagePart(idx, "quantity", parseInt(e.target.value) || 1)}
                          className="text-sm"
                          placeholder="Qty"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={line.unit_price}
                          onChange={(e) => updateEditingPackagePart(idx, "unit_price", parseFloat(e.target.value) || 0)}
                          className="text-sm"
                          placeholder="Price £"
                        />
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={line.is_consumable}
                            onChange={(e) => updateEditingPackagePart(idx, "is_consumable", e.target.checked)}
                          />
                          Consumable
                        </label>
                      </div>
                      <div className="col-span-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => removeEditingPackagePartLine(idx)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-amber-600 font-medium">Package Notes</Label>
                  <Button variant="ghost" size="sm" onClick={addEditingPackageNote}>
                    <Plus className="w-4 h-4 mr-1" /> Add Note
                  </Button>
                </div>
                <div className="space-y-2">
                  {(editingPackage.notes || []).map((note, idx) => (
                    <div key={idx} className="flex gap-2 items-center bg-amber-50/50 p-2 rounded">
                      <Input
                        value={note.text}
                        onChange={(e) => {
                          const updated = [...editingPackage.notes];
                          updated[idx] = { ...note, text: e.target.value };
                          setEditingPackage({ ...editingPackage, notes: updated });
                        }}
                        placeholder="Note text..."
                        className="flex-1 text-sm"
                      />
                      <Select 
                        value={note.note_type} 
                        onValueChange={(v) => {
                          const updated = [...editingPackage.notes];
                          updated[idx] = { ...note, note_type: v };
                          setEditingPackage({ ...editingPackage, notes: updated });
                        }}
                      >
                        <SelectTrigger className="w-32 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="info">Info</SelectItem>
                          <SelectItem value="warning">Warning</SelectItem>
                          <SelectItem value="requirement">Required</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          const updated = editingPackage.notes.filter((_, i) => i !== idx);
                          setEditingPackage({ ...editingPackage, notes: updated });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t bg-slate-50 flex justify-between shrink-0">
              <div>
                {editingPackage.id && (
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-200"
                    onClick={() => {
                      deletePackage(editingPackage.id);
                      setShowPackageEditor(false);
                    }}
                  >
                    Delete Package
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPackageEditor(false)}>
                  Cancel
                </Button>
                <Button onClick={savePackage} disabled={savingPackage}>
                  {savingPackage ? "Saving..." : editingPackage.id ? "Update Package" : "Create Package"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Smart Estimate Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="smart-wizard-modal">
          <div className="bg-white rounded-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Wizard Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Smart Estimate Wizard</h2>
                  <p className="text-blue-100 text-sm">
                    {data?.job?.reg} • {formatVehicleNameFromObject(data?.job)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeWizard}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center mt-4 gap-1">
                {WIZARD_STEPS.map((step, idx) => (
                  <div 
                    key={step.id} 
                    className={`flex items-center ${idx < WIZARD_STEPS.length - 1 ? 'flex-1' : ''}`}
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                      ${wizardStep === step.id ? 'bg-white text-blue-600' : 
                        wizardStep > step.id ? 'bg-green-400 text-white' : 'bg-white/30 text-white/80'}`}
                    >
                      {wizardStep > step.id ? '✓' : step.icon}
                    </div>
                    <span className={`ml-2 text-xs hidden md:inline
                      ${wizardStep >= step.id ? 'text-white' : 'text-white/60'}`}
                    >
                      {step.title}
                    </span>
                    {idx < WIZARD_STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 
                        ${wizardStep > step.id ? 'bg-green-400' : 'bg-white/30'}`} 
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Wizard Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {wizardLoading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                  <span className="ml-3 text-slate-600">Loading...</span>
                </div>
              ) : (
                <>
                  {/* Step 1: Review Findings */}
                  {wizardStep === 1 && (
                    <div className="space-y-6">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-slate-800">Review Vehicle Inspection Findings</h3>
                        <p className="text-slate-500">Here's what the technician found during the inspection</p>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* RED Items */}
                        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-red-800">Requires Immediate Attention</h4>
                              <p className="text-xs text-red-600">{wizardFindings.red.length} safety-critical items</p>
                            </div>
                          </div>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {wizardFindings.red.length === 0 ? (
                              <p className="text-sm text-red-600 italic">No critical issues found</p>
                            ) : (
                              wizardFindings.red.map(item => (
                                <div key={item.item_key} className="bg-white/70 rounded p-2 text-sm">
                                  <p className="font-medium text-red-800">{item.item_label}</p>
                                  {item.notes && <p className="text-red-600 text-xs mt-1">{item.notes}</p>}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        
                        {/* AMBER Items */}
                        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-amber-800">Advisory Items</h4>
                              <p className="text-xs text-amber-600">{wizardFindings.amber.length} items for consideration</p>
                            </div>
                          </div>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {wizardFindings.amber.length === 0 ? (
                              <p className="text-sm text-amber-600 italic">No advisory items found</p>
                            ) : (
                              wizardFindings.amber.map(item => (
                                <div key={item.item_key} className="bg-white/70 rounded p-2 text-sm">
                                  <p className="font-medium text-amber-800">{item.item_label}</p>
                                  {item.notes && <p className="text-amber-600 text-xs mt-1">{item.notes}</p>}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Summary */}
                      <div className="bg-slate-100 rounded-lg p-4 text-center">
                        <p className="text-slate-700">
                          <strong>{wizardFindings.red.length + wizardFindings.amber.length}</strong> items require attention
                          • <span className="text-green-600"><strong>{wizardFindings.green.length}</strong> items passed</span>
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Step 2: Select Items */}
                  {wizardStep === 2 && (
                    <div className="space-y-6">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-slate-800">Select Items to Quote</h3>
                        <p className="text-slate-500">Red items are mandatory. You can optionally include amber items.</p>
                      </div>
                      
                      <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                        {wizardSelectedItems.map(item => (
                          <div 
                            key={item.item_key}
                            className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all
                              ${item.mandatory ? 'border-red-300 bg-red-50' : 
                                item.selected ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={() => toggleWizardItem(item.item_key)}
                                disabled={item.mandatory}
                                className="w-5 h-5"
                              />
                              <div>
                                <p className="font-medium text-slate-800">{item.item_label}</p>
                                {item.notes && <p className="text-xs text-slate-500">{item.notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {item.mandatory ? (
                                <Badge className="bg-red-500 text-white">RED - Mandatory</Badge>
                              ) : (
                                <Badge className={item.selected ? "bg-amber-500 text-white" : "bg-slate-300"}>
                                  AMBER
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                        <strong>Selected:</strong> {wizardSelectedItems.filter(i => i.selected).length} items
                        ({wizardSelectedItems.filter(i => i.mandatory).length} mandatory, 
                        {wizardSelectedItems.filter(i => i.selected && !i.mandatory).length} advisory)
                      </div>
                    </div>
                  )}
                  
                  {/* Step 3: Apply Packages */}
                  {wizardStep === 3 && (
                    <div className="space-y-6">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-slate-800">Apply Job Packages</h3>
                        <p className="text-slate-500">
                          We've created {jobGroups.length} job groups. Here are some relevant packages you might want to add.
                        </p>
                      </div>
                      
                      {/* Current Job Groups Summary */}
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <h4 className="font-medium text-green-800 mb-2">✓ Auto-Generated Jobs</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {jobGroups.slice(0, 6).map(jg => (
                            <div key={jg.id} className="bg-white rounded p-2 text-sm">
                              <p className="font-medium text-slate-700 truncate">{jg.title}</p>
                              <p className="text-green-600">£{calculateJobTotal(jg).total.toFixed(2)}</p>
                            </div>
                          ))}
                          {jobGroups.length > 6 && (
                            <div className="bg-white rounded p-2 text-sm text-center">
                              <p className="text-slate-500">+{jobGroups.length - 6} more</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Suggested Packages */}
                      {wizardSuggestedPackages.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-3">Suggested Packages</h4>
                          <div className="grid md:grid-cols-2 gap-3">
                            {wizardSuggestedPackages.map(pkg => (
                              <div key={pkg.id} className="border rounded-lg p-4 bg-blue-50/50">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h5 className="font-semibold text-slate-800">{pkg.name}</h5>
                                    <p className="text-xs text-slate-500">{pkg.category}</p>
                                    <p className="text-sm text-slate-600 mt-1">{pkg.description}</p>
                                    <p className="text-xs text-blue-600 mt-1">
                                      {pkg.labour_lines?.length || 0} labour • {pkg.part_lines?.length || 0} parts
                                    </p>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    onClick={() => wizardApplyPackage(pkg)}
                                    className="shrink-0"
                                  >
                                    <Plus className="w-4 h-4 mr-1" /> Add
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Browse All Packages */}
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowWizard(false);
                          setShowPackagesModal(true);
                        }}
                        className="w-full"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Browse All Packages
                      </Button>
                    </div>
                  )}
                  
                  {/* Step 4: Complete Pricing */}
                  {wizardStep === 4 && (
                    <div className="space-y-6">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-slate-800">Complete Pricing</h3>
                        <p className="text-slate-500">Review and fill in any placeholder prices</p>
                      </div>
                      
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 mb-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">Check for Placeholder Prices</p>
                            <p className="text-sm text-amber-600">
                              Some parts may have £0.00 prices that need to be filled in. 
                              Review each job group and update prices as needed.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Job Groups Summary with Edit Option */}
                      <div className="space-y-3 max-h-[45vh] overflow-y-auto">
                        {jobGroups.map((jg, idx) => {
                          const totals = calculateJobTotal(jg);
                          const hasPlaceholders = jg.parts_lines?.some(p => p.unit_price === 0 && p.is_placeholder);
                          
                          return (
                            <div 
                              key={jg.id}
                              className={`border rounded-lg p-4 ${hasPlaceholders ? 'border-amber-300 bg-amber-50/30' : 'bg-slate-50'}`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h5 className="font-semibold text-slate-800">{jg.title}</h5>
                                    {jg.vi_status === "red" && <Badge className="bg-red-500 text-white text-xs">RED</Badge>}
                                    {jg.vi_status === "amber" && <Badge className="bg-amber-500 text-white text-xs">AMBER</Badge>}
                                    {jg.ac_gas_type && <Badge className={`text-xs ${jg.ac_gas_type === 'R1234yf' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{jg.ac_gas_type}</Badge>}
                                    {hasPlaceholders && <Badge className="bg-amber-200 text-amber-800 text-xs">Needs Pricing</Badge>}
                                  </div>
                                  <p className="text-sm text-slate-500">
                                    {jg.labour_lines?.length || 0} labour • {jg.parts_lines?.length || 0} parts
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-lg text-slate-800">£{totals.total.toFixed(2)}</p>
                                  <p className="text-xs text-slate-500">inc VAT</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      <div className="bg-slate-100 rounded-lg p-4" data-testid="foh-wizard-estimated-total">
                        {(() => {
                          const gt = calculateGrandTotal();
                          return (
                            <div className="space-y-1">
                              <div className="flex justify-between items-center text-sm text-slate-600">
                                <span>New Work (inc VAT)</span>
                                <span className="font-mono">£{gt.newWorkTotal.toFixed(2)}</span>
                              </div>
                              {intentConfig && (
                                <div className="flex justify-between items-center text-sm text-emerald-700 font-medium">
                                  <span className="flex items-center gap-1.5">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Previously Authorised
                                    {(intentConfig.already_authorised_tags || []).length > 0 && (
                                      <span className="text-[10px] uppercase tracking-wide opacity-70">
                                        ({(intentConfig.already_authorised_tags || []).join(", ").toUpperCase()})
                                      </span>
                                    )}
                                  </span>
                                  <span className="font-mono">+£{gt.alreadyAuthorised.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between items-center pt-2 border-t border-slate-300">
                                <span className="font-medium text-slate-700">Customer Total</span>
                                <span className="text-2xl font-bold text-slate-800" data-testid="foh-wizard-customer-total">
                                  £{gt.total.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      
                      <Button 
                        variant="outline" 
                        onClick={() => setShowWizard(false)}
                        className="w-full"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Pricing in Detail View
                      </Button>
                    </div>
                  )}
                  
                  {/* Step 5: Review & Send */}
                  {wizardStep === 5 && wizardQuotePreview && (
                    <div className="space-y-6">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-slate-800">Review Your Estimate</h3>
                        <p className="text-slate-500">Check everything is correct before saving</p>
                      </div>
                      
                      {/* Quote Preview Card */}
                      <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-xl p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-bold">{wizardQuotePreview.vehicle}</h4>
                            <p className="text-slate-300">{wizardQuotePreview.reg}</p>
                            <p className="text-slate-400 text-sm">For: {wizardQuotePreview.customer}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-400">Total (inc VAT)</p>
                            <p className="text-3xl font-bold">£{wizardQuotePreview.total.toFixed(2)}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 py-4 border-t border-slate-700">
                          <div className="text-center">
                            <p className="text-2xl font-bold">{wizardQuotePreview.jobGroups}</p>
                            <p className="text-xs text-slate-400">Job Groups</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-red-400">{wizardQuotePreview.redItems}</p>
                            <p className="text-xs text-slate-400">Red Items</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-amber-400">{wizardQuotePreview.amberItems}</p>
                            <p className="text-xs text-slate-400">Amber Items</p>
                          </div>
                        </div>
                        
                        <div className="pt-4 border-t border-slate-700 text-sm space-y-1" data-testid="wizard-review-breakdown">
                          {/* iter197h — Itemised breakdown so FOH can
                              see every component of the customer total.
                              Previously only Subtotal + VAT + (sometimes)
                              Previously Authorised were shown, hiding
                              consumables/environmental and silently
                              dropping the authorised value if 0. */}
                          <div className="flex justify-between">
                            <span className="text-slate-400">Jobs ({wizardQuotePreview.jobGroups}) (ex VAT)</span>
                            <span data-testid="wizard-review-jobs-subtotal">£{(wizardQuotePreview.breakdown?.jobsSubtotal ?? wizardQuotePreview.subtotal).toFixed(2)}</span>
                          </div>
                          {wizardQuotePreview.breakdown?.consumablesApplied && (
                            <div className="flex justify-between text-slate-300" data-testid="wizard-review-consumables">
                              <span className="text-slate-400">Workshop Consumables (ex VAT)</span>
                              <span>£{wizardQuotePreview.breakdown.consumablesAmt.toFixed(2)}</span>
                            </div>
                          )}
                          {wizardQuotePreview.breakdown?.envApplied && (
                            <div className="flex justify-between text-slate-300" data-testid="wizard-review-environmental">
                              <span className="text-slate-400">Environmental &amp; Disposal (ex VAT)</span>
                              <span>£{wizardQuotePreview.breakdown.envAmt.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-1.5 mt-1.5 border-t border-slate-700/50">
                            <span className="text-slate-300 font-medium">Subtotal (ex VAT)</span>
                            <span className="font-medium" data-testid="wizard-review-subtotal">£{wizardQuotePreview.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">VAT (20%)</span>
                            <span data-testid="wizard-review-vat">£{wizardQuotePreview.vat.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between pt-1.5 mt-1.5 border-t border-slate-700/50">
                            <span className="text-slate-300 font-medium">New Work Total (inc VAT)</span>
                            <span className="font-medium" data-testid="wizard-review-new-work-total">£{(wizardQuotePreview.newWorkTotal ?? (wizardQuotePreview.subtotal + wizardQuotePreview.vat)).toFixed(2)}</span>
                          </div>
                          {(wizardQuotePreview.alreadyAuthorised || 0) > 0 && (
                            <div className="flex justify-between text-emerald-300" data-testid="wizard-review-already-authorised">
                              <span className="flex items-center gap-1.5">
                                <CheckCircle2 className="w-3 h-3" />
                                Previously Authorised
                                {(wizardQuotePreview.alreadyAuthorisedTags || []).length > 0 && (
                                  <span className="text-[10px] uppercase tracking-wide opacity-80">
                                    ({(wizardQuotePreview.alreadyAuthorisedTags || []).join(", ").toUpperCase()})
                                  </span>
                                )}
                              </span>
                              <span>+£{wizardQuotePreview.alreadyAuthorised.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 mt-2 border-t border-slate-600">
                            <span className="text-base font-semibold text-white">Customer Total (inc VAT)</span>
                            <span className="text-base font-bold text-white" data-testid="wizard-review-customer-total">£{wizardQuotePreview.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Validation Issues */}
                      {wizardQuotePreview.issues?.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" /> Issues to Resolve
                          </h4>
                          {wizardQuotePreview.issues.map((issue, idx) => (
                            <p key={idx} className="text-sm text-red-700 py-1">• {issue.message}</p>
                          ))}
                        </div>
                      )}
                      
                      {/* Validation Warnings */}
                      {wizardQuotePreview.warnings?.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" /> Warnings
                          </h4>
                          {wizardQuotePreview.warnings.map((warning, idx) => (
                            <p key={idx} className="text-sm text-amber-700 py-1">• {warning.message}</p>
                          ))}
                        </div>
                      )}
                      
                      {/* Ready to Send */}
                      {wizardQuotePreview.canSend && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                          <p className="font-medium text-green-800">Ready to Send!</p>
                          <p className="text-sm text-green-600">All required items have been priced</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Wizard Footer */}
            <div className="border-t bg-slate-50 px-6 py-4 shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  {wizardStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={() => setWizardStep(wizardStep - 1)}
                      disabled={wizardLoading}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                  )}
                </div>
                
                <div className="flex gap-2">
                  {wizardStep === 1 && (
                    <Button onClick={() => setWizardStep(2)} className="bg-blue-600 hover:bg-blue-700">
                      Continue to Selection
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  
                  {wizardStep === 2 && (
                    <Button 
                      onClick={wizardAutoPopulate} 
                      disabled={wizardLoading || wizardSelectedItems.filter(i => i.selected).length === 0}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {wizardLoading ? "Creating Jobs..." : "Create Price Jobs"}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  
                  {wizardStep === 3 && (
                    <Button onClick={() => setWizardStep(4)} className="bg-blue-600 hover:bg-blue-700">
                      Continue to Pricing
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  
                  {wizardStep === 4 && (
                    <Button 
                      onClick={wizardValidatePricing} 
                      disabled={wizardLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {wizardLoading ? "Validating..." : "Review Estimate"}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  
                  {wizardStep === 5 && (
                    <>
                      {!wizardQuotePreview?.canSend ? (
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setShowWizard(false);
                            // Keep job groups
                          }}
                        >
                          Fix Issues & Continue Later
                        </Button>
                      ) : (
                        <Button 
                          onClick={wizardFinish}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Save Estimate
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Pricing Summary Modal */}
      {showQuickPricingSummary && quickPricingResult && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="quick-pricing-modal">
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 shadow-2xl overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold" data-testid="auto-quote-complete-title">Auto-Quote Complete!</h2>
              <p className="text-green-100 mt-1">
                Created {quickPricingResult.jobsCreated} job groups in seconds
              </p>
            </div>
            
            {/* Stats Grid */}
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-red-50 rounded-lg" data-testid="red-items-count">
                  <p className="text-2xl font-bold text-red-600">{quickPricingResult.redItems}</p>
                  <p className="text-xs text-red-500">RED Items</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg" data-testid="amber-items-count">
                  <p className="text-2xl font-bold text-amber-600">{quickPricingResult.amberItems}</p>
                  <p className="text-xs text-amber-500">AMBER Items</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg" data-testid="total-jobs-count">
                  <p className="text-2xl font-bold text-blue-600">{quickPricingResult.totalJobGroups}</p>
                  <p className="text-xs text-blue-500">Total Jobs</p>
                </div>
              </div>
              
              {/* Estimate Total */}
              <div className="bg-slate-800 text-white rounded-xl p-4 mb-6" data-testid="estimate-total">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-slate-400 text-sm">Estimated Total</p>
                    <p className="text-3xl font-bold">£{quickPricingResult.total.toFixed(2)}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-slate-400">Subtotal: £{quickPricingResult.subtotal.toFixed(2)}</p>
                    <p className="text-slate-400">VAT: £{quickPricingResult.vat.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              
              {/* Status */}
              {quickPricingResult.canSend ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3" data-testid="status-ready">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Ready to Review</p>
                    <p className="text-sm text-green-600">All required items have been priced</p>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center gap-3" data-testid="status-needs-attention">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Needs Attention</p>
                    <p className="text-sm text-amber-600">
                      {quickPricingResult.issuesCount} issues, {quickPricingResult.warningsCount} warnings to review
                    </p>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  data-testid="open-wizard-btn"
                  onClick={() => {
                    setShowQuickPricingSummary(false);
                    startWizard();
                  }}
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Open Wizard
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="review-edit-btn"
                  onClick={() => setShowQuickPricingSummary(false)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Review & Edit
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Pair Policy Modal - Grouped Replacement Logic */}
      <PairPolicyModal
        isOpen={pairPolicyModal.show}
        onClose={() => setPairPolicyModal({ show: false, promptType: null, promptData: null, pendingItem: null, isMandatoryBundle: false, bundleData: null })}
        promptType={pairPolicyModal.promptType}
        promptData={pairPolicyModal.promptData}
        onAddBothSides={async () => {
          // User chose to add both sides
          // The opposite side needs to be added to job groups
          const pendingItem = pairPolicyModal.pendingItem;
          const resolution = pairPolicyModal.pairResolution;
          if (pendingItem && resolution?.opposite_position) {
            toast.success('Both sides will be added to the quote', { duration: 3000 });
            // Note: Actually adding the opposite item would require more logic
            // For now, just inform the user to manually add it
            toast.info(`Remember to add the ${resolution.opposite_position} side item`, { duration: 4000 });
          }
          setPairPolicyModal({ show: false, promptType: null, promptData: null, pendingItem: null, isMandatoryBundle: false, bundleData: null });
        }}
        onAddSingleSide={() => {
          // User chose single side - for RECOMMEND items, this is fine
          toast.info('Single side added. Consider adding opposite side for balanced performance.', { duration: 4000 });
          setPairPolicyModal({ show: false, promptType: null, promptData: null, pendingItem: null, isMandatoryBundle: false, bundleData: null });
        }}
        onSubmitOverride={async (reasonCode, reasonText) => {
          // User provided override reason for single-side quote
          try {
            // Submit override to backend
            const response = await axios.post(`${API}/pair-policy/submit-override`, {
              yestimate_id: yestimateId,
              component_family: pairPolicyModal.promptData?.component_family,
              job_group_id: pairPolicyModal.pendingItem?.job_group_id || jobGroups[jobGroups.length - 1]?.id,
              override_reason_code: reasonCode,
              override_reason_text: reasonText
            });
            
            if (response.data?.success) {
              toast.success('Single-side quote confirmed with override reason', { duration: 3000 });
            }
          } catch (err) {
            console.error('Failed to submit override:', err);
            toast.error('Failed to save override reason. Please try again.');
          }
        }}
      />
      
      {/* Send Gate Modal - Hard stop when trying to send with blockers */}
      <SendGateModal
        isOpen={showSendGateModal}
        onClose={() => setShowSendGateModal(false)}
        sendGateData={sendGateData}
        onJumpToJob={(jobId) => {
          setShowSendGateModal(false);
          jumpToJobGroup(jobId);
        }}
        onRetryAfterFix={() => {
          setShowSendGateModal(false);
          // Recalculate integrity
          calculateAllJobIntegrity();
        }}
      />
      
      {/* Battery Radio Code Alert - Reminder for FOH */}
      <AlertDialog open={showBatteryRadioCodeAlert} onOpenChange={setShowBatteryRadioCodeAlert}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Radio Security Code Check
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-700 text-left space-y-3">
              <p>
                <strong>Important:</strong> Battery disconnection will reset the vehicle's radio and may require a security code to reactivate.
              </p>
              <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                Please check with the customer that they have the radio security code available before proceeding with battery replacement.
              </p>
              <p className="text-sm text-slate-500">
                If the customer doesn't have the code, they may need to contact their vehicle dealer or check the owner's manual.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="battery-radio-code-confirm"
            >
              Understood - I'll check with customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter180d — Add Custom Job modal. The button (`add-custom-job-btn`)
          has been live for ages, but the modal JSX was never wired in,
          so clicks toggled state with no visible effect. Restored here
          using AlertDialog (same primitive as the radio-code alert above
          so we don't drag a second Dialog dependency into this giant
          component). */}
      <AlertDialog open={showCustomJobModal} onOpenChange={setShowCustomJobModal}>
        <AlertDialogContent className="max-w-lg" data-testid="add-custom-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-slate-800">
              <Plus className="w-5 h-5 text-blue-600" />
              Add a custom job
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 text-left">
              Use this for work that isn't in the inspection or MOT result —
              alignment, valeting, sublet jobs, additional advisories. The
              job will be added as a new pricing row you can edit just like
              the auto-generated ones.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Job title <span className="text-red-500">*</span>
              </label>
              <Input
                value={customJobTitle}
                onChange={(e) => setCustomJobTitle(e.target.value)}
                placeholder="e.g. Four-wheel alignment, MAF cleaning, valeting"
                maxLength={120}
                data-testid="add-custom-job-title-input"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Customer description (optional)
              </label>
              <Textarea
                value={customJobDescription}
                onChange={(e) => setCustomJobDescription(e.target.value)}
                placeholder="What the customer will see on the quote"
                rows={3}
                maxLength={500}
                data-testid="add-custom-job-description-input"
              />
              <p className="text-[11px] text-slate-400">
                {customJobDescription.length}/500
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="add-custom-job-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAddCustomJob}
              disabled={!customJobTitle.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-custom-job-confirm"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add job
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
      )}
      
      {/* ============== STICKY RUNNING TOTAL BAR ============== */}
      {data && jobGroups.length > 0 && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t-2 border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
          data-testid="sticky-total-bar"
        >
          <div className="max-w-[1600px] mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
            {/* Left: Progress indicator */}
            <div className="flex items-center gap-3">
              {(() => {
                const totalItems = redItems.filter(i => !ignoredItems.some(ig => ig.id === i.id)).length + amberItems.length;
                const pricedCount = [...pricedItemIds].filter(id => 
                  redItems.some(i => i.id === id) || amberItems.some(i => i.id === id)
                ).length;
                const progressPct = totalItems > 0 ? Math.round((pricedCount / totalItems) * 100) : 0;
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${progressPct === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-600 whitespace-nowrap" data-testid="sticky-progress-text">
                        {pricedCount}/{totalItems} priced
                      </span>
                    </div>
                    <div className="h-4 w-px bg-slate-300" />
                    <span className="text-xs text-slate-500 whitespace-nowrap">
                      {jobGroups.length} job{jobGroups.length !== 1 ? 's' : ''} · {jobGroups.filter(jg => jg.pricing_confirmed).length} confirmed
                    </span>
                  </>
                );
              })()}
            </div>
            
            {/* Center: Grand Total */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-slate-500">
                  Subtotal: £{grandTotal.subtotal.toFixed(2)} + VAT: £{grandTotal.vat.toFixed(2)}
                  {grandTotal.alreadyAuthorised > 0 && (
                    <span className="text-emerald-700 font-medium">
                      {" "}+ Auth: £{grandTotal.alreadyAuthorised.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900" data-testid="sticky-grand-total">
                £{grandTotal.total.toFixed(2)}
              </div>
              <span className="text-xs text-slate-500 font-medium">inc VAT</span>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <Button 
                size="sm"
                variant="outline"
                onClick={handleSave}
                disabled={saving}
                className="text-xs"
                data-testid="sticky-save-btn"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button 
                size="sm"
                onClick={async () => {
                  // MOT Phase 1: block full MOT-inclusive estimate while MOT pending
                  const motBlocked = jobReadiness?.job_status?.mot_blocks_full_estimate;
                  const scope = jobReadiness?.estimate_scope;
                  if (motBlocked && (!scope || scope === 'full_mot_inclusive')) {
                    setShowMotPendingModal(true);
                    return;
                  }
                  // MOT Phase 2: warn if unreviewed Dangerous/Major defects remain
                  const unreviewed = motDefects.counts?.unreviewed_failures || 0;
                  if (jobReadiness?.mot_status?.status === 'mot_imported' && unreviewed > 0) {
                    setShowUnreviewedSendModal(true);
                    return;
                  }
                  // Record send (non-blocking) then navigate
                  if (data?.job?.id && scope) {
                    try { await axios.post(`${API}/jobs/${data.job.id}/estimate-send`, { scope }); } catch (e) {}
                  }
                  // iter211f — "Send to Customer" now ALWAYS goes through
                  // the Customer Story Preview first. FOH can reorder /
                  // amalgamate / override severity bands there, then hit
                  // the in-page Send button to land on the actual send
                  // portal. Skipping this step meant the user reported
                  // "I have no portal review to change order of items
                  // for customer story" — the preview screen existed but
                  // was only reachable via a secondary CTA.
                  navigate(`/app/foh/yestimate/${yestimateId}/portal-preview`);
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                data-testid="sticky-send-btn"
              >
                Send to Customer
              </Button>
              {/* iter211 — Customer Story Preview CTA (sits next to
                  "Send to Customer"). Drops FOH into the preview-and-
                  arrange screen so they can reorder, regroup and re-
                  band BEFORE the quote leaves the garage. */}
              <Button
                onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/portal-preview`)}
                variant="outline"
                className="text-xs border-blue-500 text-blue-700 hover:bg-blue-50"
                data-testid="customer-story-preview-cta"
                title="Reorder, regroup, and override severity before sending"
              >
                Preview & Arrange Customer View
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* iter197l — Timing Service Intelligence Library modal */}
      <TimingServiceModal
        open={showTimingServiceModal}
        onClose={() => setShowTimingServiceModal(false)}
        job={data?.job}
        onApplied={refreshAfterTimingApplied}
      />
    </div>
  );
};
const FOHSIEmailPreview = () => {
  const { yestimateId } = useParams();
  const [emailData, setEmailData] = useState(null);
  const [techReport, setTechReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const navigate = useNavigate();
  
  // SI Email required fields state
  const [siEmailSettings, setSiEmailSettings] = useState({
    customer_name: "",
    agreed_work_value: "",
    advisor_name: "",
    garage_name: "",
    contact_details: ""
  });

  const loadData = async () => {
    try {
      const [emailRes, reportRes] = await Promise.all([
        axios.get(`${API}/foh/yestimate/${yestimateId}/si-email/preview`),
        axios.get(`${API}/foh/yestimate/${yestimateId}/technician-report`)
      ]);
      setEmailData(emailRes.data);
      setTechReport(reportRes.data);
      
      // Populate settings form from response
      const sections = emailRes.data.sections;
      const greeting = sections.find(s => s.type === "greeting");
      const agreedWork = sections.find(s => s.type === "agreed_work_value");
      const signOff = sections.find(s => s.type === "sign_off");
      
      setSiEmailSettings({
        customer_name: greeting?.is_filled ? greeting.content.replace("Dear ", "").replace(",", "") : "",
        agreed_work_value: agreedWork?.is_filled ? agreedWork.value.replace("£", "") : "",
        advisor_name: signOff?.fields_filled?.advisor_name ? signOff.advisor_name : "",
        garage_name: signOff?.fields_filled?.garage_name ? signOff.garage_name : "",
        contact_details: signOff?.fields_filled?.contact_details ? signOff.contact_details : ""
      });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to load SI Email");
      navigate("/app/foh");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [yestimateId]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/foh/yestimate/${yestimateId}/si-email/settings`, {
        customer_name: siEmailSettings.customer_name || null,
        agreed_work_value: siEmailSettings.agreed_work_value ? parseFloat(siEmailSettings.agreed_work_value) : null,
        advisor_name: siEmailSettings.advisor_name || null,
        garage_name: siEmailSettings.garage_name || null,
        contact_details: siEmailSettings.contact_details || null
      });
      // Reload to get updated preview
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    // Check required fields
    if (!emailData?.is_complete) {
      toast.warning("Incomplete form", {
        description: "Please fill in all required fields before sending"
      });
      return;
    }
    if (!customerEmail) {
      toast.warning("Email required", {
        description: "Please enter customer email address"
      });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/si-email/send`, {
        customer_email: customerEmail
      });
      toast.success("SI Email sent!", {
        description: `Sent to ${customerEmail}`
      });
      navigate(`/app/foh/yestimate/${yestimateId}/audit`);
    } catch (err) {
      toast.error("Failed to send", {
        description: err.response?.data?.detail
      });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/foh/yestimate/${yestimateId}/technician-report/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = emailData?.attachment?.name || 'Technician_Inspection_Report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading SI Email...</div>;
  if (!emailData) return <div className="p-8 text-center">Failed to generate SI Email</div>;

  const sectionNames = {
    in_car_checks: "In-Car Checks",
    under_bonnet: "Under-Bonnet",
    brakes_wheels: "Brakes & Wheels",
    tyres: "Tyres",
    suspension_steering: "Suspension & Steering",
    exhaust_underside: "Exhaust & Underside",
    lights_electrical: "Lights & Electrical",
    final_checks: "Final Checks"
  };

  // Check if diagnostic is blocking
  const diagnosticNotPriced = emailData.missing_fields?.includes("diagnostic_not_priced");

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/preview`)} className="mb-2">
            ← Back to Preview
          </Button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-bold text-xl text-slate-800">SI Email - Version B</h1>
              <p className="text-sm text-slate-500">Mandatory Structure - All Fields Required</p>
            </div>
            <Badge className={emailData.is_complete ? "bg-green-600 text-white" : "bg-amber-500 text-white"}>
              {emailData.is_complete ? "Ready to Send" : `${emailData.missing_fields?.length || 0} Fields Missing`}
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        {/* DIAGNOSTIC NOT PRICED - BLOCKING ERROR */}
        {diagnosticNotPriced && (
          <Card className="mb-6 border-2 border-red-400 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-8 h-8 text-red-600 mt-1" />
                <div>
                  <h3 className="font-bold text-red-800 text-lg">Diagnostic Rectification Not Priced</h3>
                  <p className="text-red-700 mt-1 mb-3">
                    A diagnostic inspection was completed but the rectification has not been added to pricing. 
                    <strong> SI Email cannot be generated until the diagnostic repair is priced.</strong>
                  </p>
                  <Button 
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/pricing`)}
                  >
                    Go to Pricing to Add Diagnostic
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Diagnostic Report Attachment Notice */}
        {emailData.diagnostic_attachment && (
          <Card className="mb-6 border-purple-300 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-purple-600" />
                <div>
                  <h3 className="font-semibold text-purple-800">Diagnostic Report Attachment</h3>
                  <p className="text-sm text-purple-600">
                    The technician's diagnostic report will be attached to this email as: {emailData.diagnostic_attachment.name}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Required Fields Form */}
        {!emailData.is_complete && !diagnosticNotPriced && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Required Fields - Must Be Completed Before Sending
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Customer Name *</Label>
                  <Input 
                    value={siEmailSettings.customer_name}
                    onChange={(e) => setSiEmailSettings(prev => ({...prev, customer_name: e.target.value}))}
                    placeholder="Enter customer name"
                    className="mt-1"
                    data-testid="si-customer-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Agreed Work Value (£) *</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={siEmailSettings.agreed_work_value}
                    onChange={(e) => setSiEmailSettings(prev => ({...prev, agreed_work_value: e.target.value}))}
                    placeholder="0.00"
                    className="mt-1"
                    data-testid="si-agreed-value"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Advisor Name *</Label>
                  <Input 
                    value={siEmailSettings.advisor_name}
                    onChange={(e) => setSiEmailSettings(prev => ({...prev, advisor_name: e.target.value}))}
                    placeholder="Enter advisor name"
                    className="mt-1"
                    data-testid="si-advisor-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Garage Name *</Label>
                  <Input 
                    value={siEmailSettings.garage_name}
                    onChange={(e) => setSiEmailSettings(prev => ({...prev, garage_name: e.target.value}))}
                    placeholder="Enter garage name"
                    className="mt-1"
                    data-testid="si-garage-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium">Contact Details *</Label>
                  <Input 
                    value={siEmailSettings.contact_details}
                    onChange={(e) => setSiEmailSettings(prev => ({...prev, contact_details: e.target.value}))}
                    placeholder="Phone / Email"
                    className="mt-1"
                    data-testid="si-contact-details"
                  />
                </div>
              </div>
              <Button onClick={handleSaveSettings} disabled={saving} className="w-full">
                {saving ? "Saving..." : "Save & Update Preview"}
              </Button>
            </CardContent>
          </Card>
        )}
        
        <Tabs defaultValue="email" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email" data-testid="tab-email">SI Email Preview</TabsTrigger>
            <TabsTrigger value="attachment" data-testid="tab-attachment">Technician Report (Attachment)</TabsTrigger>
          </TabsList>

          {/* SI Email Tab */}
          <TabsContent value="email">
            <Card>
              <CardHeader className="bg-slate-50 border-b">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Mail className="w-4 h-4" />
                  <span>Subject: {emailData.subject}</span>
                  <Badge variant="outline" className="ml-2 text-xs">FIXED - NOT EDITABLE</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* VERSION B MANDATORY STRUCTURE */}
                {emailData.sections.map((section, idx) => (
                  <div key={idx}>
                    {/* 2️⃣ GREETING */}
                    {section.type === "greeting" && (
                      <p className={`text-lg ${section.is_filled ? 'text-slate-800' : 'text-amber-600 font-medium'}`}>
                        {section.content}
                      </p>
                    )}
                    
                    {/* 3️⃣ OPENING PARAGRAPH - FIXED */}
                    {section.type === "opening_paragraph" && (
                      <div className="bg-slate-50 border-l-4 border-slate-300 pl-4 py-2">
                        <p className="text-slate-700 leading-relaxed">{section.content}</p>
                        <p className="text-xs text-slate-400 mt-2 italic">Fixed wording - cannot be edited</p>
                      </div>
                    )}
                    
                    {/* 4️⃣ AGREED WORK VALUE - MANDATORY */}
                    {section.type === "agreed_work_value" && (
                      <div className={`rounded p-4 ${section.is_filled ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-300'}`}>
                        <p className={`font-semibold ${section.is_filled ? 'text-green-800' : 'text-amber-700'}`}>
                          {section.label} <span className="text-lg">{section.value}</span>
                        </p>
                      </div>
                    )}
                    
                    {/* 5️⃣ FINDINGS INTRO - FIXED */}
                    {section.type === "findings_intro" && (
                      <div className="bg-slate-50 border-l-4 border-slate-300 pl-4 py-2">
                        <p className="text-slate-700 leading-relaxed">{section.content}</p>
                        <p className="text-xs text-slate-400 mt-2 italic">Fixed wording - cannot be edited</p>
                      </div>
                    )}
                    
                    {/* 6️⃣ RED ITEMS SECTION */}
                    {section.type === "red_items" && (
                      <div className="border-l-4 border-red-500 pl-4">
                        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                          <p className="text-red-700 text-sm leading-relaxed">{section.intro}</p>
                          <p className="text-xs text-red-400 mt-2 italic">Fixed intro - cannot be edited</p>
                        </div>
                        <div className="space-y-4">
                          {section.items.map((item, itemIdx) => (
                            <div key={itemIdx} className="bg-red-50 border border-red-200 rounded p-4">
                              <div className="flex justify-between items-start mb-3">
                                <span className="font-semibold text-red-800 text-base">{item.title}</span>
                                <span className="font-bold text-red-700 text-lg whitespace-nowrap ml-4">
                                  £{(item.price_inc_vat || 0).toFixed(2)} inc VAT
                                </span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="font-medium text-red-700">What was found: </span>
                                  <span className="text-red-600">{item.what_was_found}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-red-700">Why it matters: </span>
                                  <span className="text-red-600">{item.why_it_matters}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-red-700">Work required: </span>
                                  <span className="text-red-600">{item.work_required}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* 7️⃣ AMBER ITEMS SECTION */}
                    {section.type === "amber_items" && (
                      <div className="border-l-4 border-amber-500 pl-4">
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
                          <p className="text-amber-700 text-sm leading-relaxed">{section.intro}</p>
                          <p className="text-xs text-amber-400 mt-2 italic">Fixed intro - cannot be edited</p>
                        </div>
                        <div className="space-y-4">
                          {section.items.map((item, itemIdx) => (
                            <div key={itemIdx} className="bg-amber-50 border border-amber-200 rounded p-4">
                              <div className="flex justify-between items-start mb-3">
                                <span className="font-semibold text-amber-800 text-base">{item.title}</span>
                                <span className="font-bold text-amber-700 text-lg whitespace-nowrap ml-4">
                                  £{(item.price_inc_vat || 0).toFixed(2)} inc VAT
                                </span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="font-medium text-amber-700">Why recommended now: </span>
                                  <span className="text-amber-600">{item.why_recommended_now}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-amber-700">If delayed: </span>
                                  <span className="text-amber-600">{item.if_delayed}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* 8️⃣ NEXT STEPS - FIXED */}
                    {section.type === "next_steps" && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-4">
                        <h4 className="font-bold text-blue-800 mb-2">{section.heading}</h4>
                        <p className="text-blue-700 leading-relaxed">{section.content}</p>
                        <p className="text-xs text-blue-400 mt-2 italic">Fixed wording - cannot be edited</p>
                      </div>
                    )}
                    
                    {/* 9️⃣ PAYMENT ASSIST - FIXED */}
                    {section.type === "payment_assist" && (
                      <div className="bg-slate-50 border border-slate-200 rounded p-4">
                        <p className="text-slate-700 leading-relaxed">{section.content}</p>
                        <p className="text-xs text-slate-400 mt-2 italic">Fixed wording - cannot be edited</p>
                      </div>
                    )}
                    
                    {/* 🔟 CLOSING SUPPORT - FIXED */}
                    {section.type === "closing_support" && (
                      <p className="text-slate-700">{section.content}</p>
                    )}
                    
                    {/* 1️⃣1️⃣ SIGN-OFF */}
                    {section.type === "sign_off" && (
                      <div className="mt-6 pt-4 border-t border-slate-200">
                        <p className="text-slate-700">{section.sign_off_line}</p>
                        <p className={`font-medium ${section.fields_filled?.advisor_name ? 'text-slate-800' : 'text-amber-600'}`}>
                          {section.advisor_name}
                        </p>
                        <p className={`${section.fields_filled?.garage_name ? 'text-slate-600' : 'text-amber-600'}`}>
                          {section.garage_name}
                        </p>
                        <p className={`text-sm ${section.fields_filled?.contact_details ? 'text-slate-500' : 'text-amber-600'}`}>
                          {section.contact_details}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

                {/* Attachment Reference */}
                <div className="border-t pt-4 mt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <FileText className="w-4 h-4" />
                        <span>Attachment: {emailData.attachment?.name}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{emailData.attachment?.label}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleDownloadPdf}
                      disabled={downloading}
                      data-testid="download-pdf-inline-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {downloading ? "..." : "Download"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Technician Report Tab */}
          <TabsContent value="attachment">
            <Card>
              <CardHeader className="bg-amber-50 border-b border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-amber-600" />
                    <CardTitle className="text-base text-amber-800">{techReport?.report_title}</CardTitle>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloading ? "Downloading..." : "Download PDF"}
                  </Button>
                </div>
                <CardDescription className="text-amber-600">This report is read-only and attached for customer reference</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {/* Vehicle Info */}
                <div className="bg-slate-50 rounded p-4 mb-6">
                  <h3 className="font-medium mb-2">Vehicle Details</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Registration:</span> {techReport?.vehicle.reg}</div>
                    <div><span className="text-slate-500">Make/Model:</span> {techReport?.vehicle.make} {techReport?.vehicle.model}</div>
                    <div><span className="text-slate-500">Mileage:</span> {techReport?.vehicle.mileage}</div>
                    <div><span className="text-slate-500">Technician:</span> {techReport?.technician.name}</div>
                  </div>
                </div>

                {/* Status Summary */}
                <div className="grid grid-cols-5 gap-2 mb-6">
                  <div className="bg-black rounded p-3 text-center">
                    <div className="text-xl font-bold text-white">{techReport?.status_counts?.dangerous || 0}</div>
                    <div className="text-xs text-gray-300">Dangerous</div>
                  </div>
                  <div className="bg-red-50 rounded p-3 text-center">
                    <div className="text-xl font-bold text-red-700">{techReport?.status_counts.red}</div>
                    <div className="text-xs text-red-600">Red</div>
                  </div>
                  <div className="bg-amber-50 rounded p-3 text-center">
                    <div className="text-xl font-bold text-amber-700">{techReport?.status_counts.amber}</div>
                    <div className="text-xs text-amber-600">Amber</div>
                  </div>
                  <div className="bg-green-50 rounded p-3 text-center">
                    <div className="text-xl font-bold text-green-700">{techReport?.status_counts.green}</div>
                    <div className="text-xs text-green-600">Green</div>
                  </div>
                  <div className="bg-blue-50 rounded p-3 text-center">
                    <div className="text-xl font-bold text-blue-700">{techReport?.status_counts.blue}</div>
                    <div className="text-xs text-blue-600">N/A</div>
                  </div>
                </div>

                {/* Sections */}
                {techReport?.sections && Object.entries(techReport.sections).map(([sectionId, items]) => (
                  <div key={sectionId} className="mb-4">
                    <h3 className="font-medium text-sm bg-slate-100 px-3 py-2 rounded">{sectionNames[sectionId] || sectionId}</h3>
                    <div className="space-y-2 mt-2">
                      {items.map((item, idx) => (
                        <div key={idx} className="border rounded p-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              item.status === "red" ? "bg-red-500" :
                              item.status === "amber" ? "bg-amber-500" :
                              item.status === "green" ? "bg-green-500" : "bg-blue-500"
                            }`} />
                            <span className="font-medium">{item.item_label}</span>
                          </div>
                          {item.notes && <p className="text-slate-600 mt-1 ml-5">{item.notes}</p>}
                          {item.measurement_value && (
                            <p className="text-slate-500 mt-1 ml-5">
                              Measurement: {item.measurement_value} {item.measurement_unit}
                            </p>
                          )}
                          {item.photo_urls?.length > 0 && (
                            <p className="text-slate-400 text-xs mt-1 ml-5">📷 {item.photo_urls.length} photo(s) attached</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Send SI Email */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Send SI Email</CardTitle>
            <CardDescription>Send the Safety & Reliability Inspection email with technician report attached</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!emailData.is_complete && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-700 text-sm">
                Please complete all required fields in the form above before sending.
              </div>
            )}
            <div>
              <Label>Customer Email *</Label>
              <Input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="customer@example.com"
                required
                data-testid="si-customer-email"
              />
            </div>
            <Button 
              className="w-full" 
              onClick={handleSend}
              disabled={sending || !customerEmail || !emailData.is_complete}
              data-testid="send-si-email-btn"
            >
              {sending ? "Sending..." : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send SI Email with Technician Report
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== CUSTOMER APPROVAL PORTAL (PUBLIC - NO AUTH) ==============
const CustomerApprovalPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Garage logo and name state
  const [garageLogo, setGarageLogo] = useState(null);
  const [garageName, setGarageName] = useState(null);
  
  // Form state
  const [decisionType, setDecisionType] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionNotes, setRejectionNotes] = useState("");
  const [declineFollowUp, setDeclineFollowUp] = useState(null); // For follow-up prompts
  const [signatureType, setSignatureType] = useState("typed");
  const [typedName, setTypedName] = useState("");
  const [drawnSignature, setDrawnSignature] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [expandedAmberItems, setExpandedAmberItems] = useState({});
  const [showAllAmberItems, setShowAllAmberItems] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [expandedEducationInserts, setExpandedEducationInserts] = useState({});
  
  // Budget-based prioritisation state (AOV Optimisation)
  const [budgetAmount, setBudgetAmount] = useState("");
  const [prioritisedItems, setPrioritisedItems] = useState(null);
  const [showBudgetInput, setShowBudgetInput] = useState(false);
  const [budgetError, setBudgetError] = useState("");
  const [prioritisationConfirmed, setPrioritisationConfirmed] = useState(false);
  
  // Decline recovery state
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [recoveryResponse, setRecoveryResponse] = useState(null);
  
  // Engagement tracking - which sections were viewed
  const [engagementData, setEngagementData] = useState({
    redSectionViewed: false,
    amberSectionViewed: false,
    expandedItems: []
  });
  
  // Executive Summary state
  const [showExecutiveSummary, setShowExecutiveSummary] = useState(true);
  const [detailedSectionsCollapsed, setDetailedSectionsCollapsed] = useState(false);
  
  // AI Explanations state
  const [aiExplanations, setAiExplanations] = useState({});
  const [loadingAiExplanations, setLoadingAiExplanations] = useState(false);
  
  // Canvas state for signature
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Response deadline (configurable - default 2 hours from now)
  const getResponseDeadline = () => {
    const deadline = new Date();
    deadline.setHours(deadline.getHours() + 2);
    return deadline.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };
  
  useEffect(() => {
    fetchApprovalData();
    fetchGarageLogo();
    // AI explanations are loaded after the main data to avoid blocking
  }, [token]);
  
  // Load AI explanations after main data is available
  useEffect(() => {
    if (data && !loadingAiExplanations && Object.keys(aiExplanations).length === 0) {
      fetchAiExplanations();
    }
  }, [data]);
  
  const fetchAiExplanations = async () => {
    try {
      setLoadingAiExplanations(true);
      const res = await axios.post(`${API}/customer/approval/${token}/generate-explanations`);
      if (res.data.explanations) {
        setAiExplanations(res.data.explanations);
        console.log(`Loaded ${res.data.generated} AI explanations`);
      }
    } catch (err) {
      console.log("AI explanations not available, using defaults");
    } finally {
      setLoadingAiExplanations(false);
    }
  };
  
  const fetchGarageLogo = async () => {
    try {
      const res = await axios.get(`${API}/public/garage/logo`);
      if (res.data.has_logo && res.data.logo_data) {
        setGarageLogo(res.data.logo_data);
      }
      if (res.data.garage_name) {
        setGarageName(res.data.garage_name);
      }
    } catch (err) {
      // Silently fail - logo is optional
      console.log("No garage logo configured");
    }
  };
  
  const fetchApprovalData = async () => {
    try {
      const res = await axios.get(`${API}/customer/approval/${token}`);
      setData(res.data);
      // Pre-fill customer name if available from job data
      if (res.data.customer_name) {
        setCustomerName(res.data.customer_name);
        setTypedName(res.data.customer_name); // Also pre-fill signature name
      }
      if (res.data.already_responded) {
        setSubmitted(true);
      }
    } catch (err) {
      setError(getErrorMessage(err, "Invalid or expired approval link"));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/customer/approval/${token}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `technician_report_${data.vehicle?.reg || 'report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Failed to download report");
    }
  };
  
  const handleSubmit = async () => {
    if (!customerName.trim()) {
      alert("Please enter your name");
      return;
    }
    if (!decisionType) {
      alert("Please select a decision");
      return;
    }
    if (decisionType === "decline" && !rejectionReason) {
      alert("Please select a reason for declining");
      return;
    }
    if (decisionType === "prioritise_within_budget" && (!prioritisedItems?.success || !prioritisationConfirmed)) {
      alert("Please confirm your prioritised work selection");
      return;
    }
    if (signatureType === "typed" && !typedName.trim()) {
      alert("Please type your name as signature");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        decision_type: decisionType,
        rejection_reason: decisionType === "decline" ? rejectionReason : null,
        rejection_notes: decisionType === "decline" ? rejectionNotes : null,
        decline_follow_up: declineFollowUp,
        signature_type: signatureType,
        signature_data: signatureType === "typed" ? typedName : drawnSignature,
        customer_name: customerName,
        customer_email: customerEmail || null,
        customer_phone: customerPhone || null,
        // Budget prioritisation data (AOV Optimisation)
        budget_prioritisation: decisionType === "prioritise_within_budget" && prioritisedItems?.success ? {
          budget_entered: prioritisedItems.budget,
          included_items: prioritisedItems.included.map(i => ({ id: i.id, label: i.label, price: i.price_inc_vat, category: i.category })),
          excluded_items: prioritisedItems.excluded.map(i => ({ id: i.id, label: i.label, price: i.price_inc_vat, category: i.category })),
          total_authorised: prioritisedItems.totalIncluded,
          red_items_count: prioritisedItems.redCount,
          amber_included_count: prioritisedItems.amberIncludedCount,
          amber_excluded_count: prioritisedItems.amberExcludedCount
        } : null,
        // Recovery flow tracking
        recovery_flow: {
          decline_reason_selected: rejectionReason || null,
          recovery_prompt_shown: showRecoveryPrompt,
          recovery_response: recoveryResponse,
          converted_to_budget_flow: decisionType === "prioritise_within_budget" && rejectionReason === "cost_too_high"
        },
        // Engagement context for FOH (especially for "call me")
        engagement_context: {
          red_section_viewed: engagementData.redSectionViewed,
          amber_section_viewed: engagementData.amberSectionViewed,
          items_expanded: engagementData.expandedItems,
          red_total: data?.red_total || 0,
          amber_total: data?.amber_total || 0,
          total_estimate: data?.total_estimate || 0
        }
      };
      
      const res = await axios.post(`${API}/customer/approval/${token}/submit`, payload);
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };
  
  const toggleAmberItem = (itemId) => {
    setExpandedAmberItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
    // Track engagement
    if (!expandedAmberItems[itemId]) {
      setEngagementData(prev => ({
        ...prev,
        amberSectionViewed: true,
        expandedItems: [...new Set([...prev.expandedItems, itemId])]
      }));
    }
  };
  
  // Calculate prioritised items based on budget
  const calculatePrioritisedItems = (budget) => {
    if (!data || !budget || budget <= 0) return null;
    
    const redItems = data.red_items || [];
    const amberItems = data.amber_items || [];
    const redTotal = data.red_total || 0;
    
    // Check if budget can cover all red items
    if (budget < redTotal) {
      return {
        success: false,
        error: "budget_below_safety",
        redTotal: redTotal,
        budget: budget,
        message: `Safety-critical items total £${redTotal.toFixed(2)}, which exceeds your budget of £${parseFloat(budget).toFixed(2)}. These items are essential for safe driving.`
      };
    }
    
    // Include all red items
    const included = [...redItems.map(item => ({ ...item, category: "red" }))];
    let remainingBudget = budget - redTotal;
    const excluded = [];
    
    // Sort amber items by priority (escalation risk, safety impact, assembly efficiency)
    // This is a simplified priority - in production this could be more sophisticated
    const sortedAmber = [...amberItems].sort((a, b) => {
      // Priority based on common high-priority keywords
      const highPriorityKeywords = ['brake', 'steering', 'suspension', 'tyre', 'tire', 'wheel', 'bearing', 'cv', 'ball joint', 'tie rod'];
      const aHighPriority = highPriorityKeywords.some(kw => a.label?.toLowerCase().includes(kw));
      const bHighPriority = highPriorityKeywords.some(kw => b.label?.toLowerCase().includes(kw));
      if (aHighPriority && !bHighPriority) return -1;
      if (!aHighPriority && bHighPriority) return 1;
      // Then by price (lower first - easier wins)
      return (a.price_inc_vat || 0) - (b.price_inc_vat || 0);
    });
    
    // Add amber items that fit within remaining budget
    for (const item of sortedAmber) {
      const itemPrice = item.price_inc_vat || 0;
      if (itemPrice <= remainingBudget) {
        included.push({ ...item, category: "amber" });
        remainingBudget -= itemPrice;
      } else {
        excluded.push({ ...item, category: "amber" });
      }
    }
    
    const totalIncluded = included.reduce((sum, item) => sum + (item.price_inc_vat || 0), 0);
    
    return {
      success: true,
      included: included,
      excluded: excluded,
      totalIncluded: totalIncluded,
      budget: parseFloat(budget),
      remainingBudget: remainingBudget,
      redCount: redItems.length,
      amberIncludedCount: included.filter(i => i.category === "amber").length,
      amberExcludedCount: excluded.length
    };
  };
  
  // Handle budget submission
  const handleBudgetSubmit = () => {
    const budget = parseFloat(budgetAmount);
    if (!budget || budget <= 0) {
      setBudgetError("Please enter a valid budget amount");
      return;
    }
    setBudgetError("");
    const result = calculatePrioritisedItems(budget);
    setPrioritisedItems(result);
  };
  
  // Handle decline reason selection with recovery prompt
  const handleDeclineReasonSelect = (reason) => {
    setRejectionReason(reason);
    setDeclineFollowUp(null);
    setRecoveryResponse(null);
    // Show recovery prompt for specific reasons
    if (reason === "cost_too_high" || reason === "dont_understand" || reason === "need_time_to_think") {
      setShowRecoveryPrompt(true);
    } else {
      setShowRecoveryPrompt(false);
    }
  };
  
  // Handle recovery prompt response
  const handleRecoveryResponse = (response, reason) => {
    setRecoveryResponse(response);
    setDeclineFollowUp(response);
    
    if (response === "yes" && reason === "cost_too_high") {
      // Switch to prioritise_within_budget flow
      setDecisionType("prioritise_within_budget");
      setShowBudgetInput(true);
      setShowRecoveryPrompt(false);
    } else if (response === "yes" && reason === "dont_understand") {
      // Switch to call_to_discuss
      setDecisionType("call_to_discuss");
      setShowRecoveryPrompt(false);
    } else if (response === "yes" && reason === "need_time_to_think") {
      // Log that they want to be contacted later
      setDeclineFollowUp("contact_later_requested");
    }
  };
  
  // Get decline recovery prompt based on reason
  const getDeclineRecoveryPrompt = (reason) => {
    const prompts = {
      "cost_too_high": {
        question: "Would you like us to prioritise the most important work within a budget?",
        yesLabel: "Yes, let me set a budget",
        noLabel: "No, decline all work"
      },
      "dont_understand": {
        question: "Would you like a quick call to explain the findings?",
        yesLabel: "Yes, please call me",
        noLabel: "No thanks"
      },
      "need_time_to_think": {
        question: "Would you like us to contact you to book this in at a later date?",
        yesLabel: "Yes, contact me later",
        noLabel: "No thanks"
      }
    };
    return prompts[reason] || null;
  };
  
  // Get decline follow-up question based on reason (legacy - keeping for non-recovery reasons)
  const getDeclineFollowUp = (reason) => {
    const followUps = {
      "getting_second_opinion": { question: "Would you like us to send you the technical report to share?", options: ["Yes, send the report", "No thanks"] },
      "vehicle_being_sold": { question: "Would you like a summary for the buyer showing what's been identified?", options: ["Yes, prepare summary", "No thanks"] },
      "booking_elsewhere": { question: "Would you like us to match any quote you receive?", options: ["Yes, I'd consider that", "No thanks"] }
    };
    return followUps[reason] || null;
  };
  
  // Signature handlers
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = e.target;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Scale coordinates to account for CSS scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = e.target;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Scale coordinates to account for CSS scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  
  const stopDrawing = (e) => {
    setIsDrawing(false);
    if (e.target) {
      setDrawnSignature(e.target.toDataURL());
    }
  };
  
  const clearSignature = () => {
    const canvas = document.getElementById('signature-canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setDrawnSignature(null);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading inspection results...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700">{error}</h2>
            <p className="text-gray-600 mt-2">Please contact the service center for assistance.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (submitted) {
    const messages = {
      approve_all: "Thank you for choosing Safe + More Reliable. Our team will be in touch to arrange completion of the work.",
      approve_safety_only: "Thank you. We will proceed with the safety-critical items to get you back on the road safely. Our team will be in touch.",
      prioritise_within_budget: `Thank you for your prioritised approval of £${prioritisedItems?.totalIncluded?.toFixed(2) || '0.00'}. Our team will be in touch to arrange the work.`,
      call_to_discuss: "Thank you. A member of our team will call you shortly to discuss your options.",
      decline: "Thank you for your response. If you change your mind or have questions, please get in touch."
    };
    
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-green-700">Response Received</h2>
            <p className="text-gray-600 mt-2">
              {messages[decisionType] || "Thank you for your response."}
            </p>
            <Button onClick={handleDownloadPDF} variant="outline" className="mt-4">
              <Download className="w-4 h-4 mr-2" />
              Download Technical Report
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Toggle education insert visibility
  const toggleEducationInsert = (itemId) => {
    setExpandedEducationInserts(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  // Calculate overall vehicle status from item counts
  const getOverallVehicleStatus = () => {
    const hasDangerous = data?.dangerous_items?.length > 0;
    const hasRed = data?.red_items?.length > 0;
    const hasAmber = data?.amber_items?.length > 0;
    
    if (hasDangerous) return { status: 'critical', label: 'Critical Safety Issues', color: 'bg-red-600' };
    if (hasRed) return { status: 'urgent', label: 'Urgent Repairs Identified', color: 'bg-amber-500' };
    if (hasAmber) return { status: 'attention', label: 'Attention Needed', color: 'bg-amber-400' };
    return { status: 'good', label: 'Good Condition', color: 'bg-emerald-500' };
  };
  
  // Premium Inspection Item Card Component
  const PremiumInspectionItem = ({ item, isExpanded = true }) => (
    <div 
      key={item.id} 
      className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden mb-4"
      data-testid={`inspection-item-${item.id}`}
    >
      {/* Item Header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex justify-between items-start gap-4">
          <h4 className="font-semibold text-slate-800 text-base leading-snug">{item.label}</h4>
          <span className="font-bold text-slate-900 text-lg whitespace-nowrap">£{item.price_inc_vat?.toFixed(2) || "0.00"}</span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-5 space-y-4">
          {/* Wear Percentage Visual - for brakes */}
          {(item.wear_percentage !== null && item.wear_percentage !== undefined) && (
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Brake Pad Condition</span>
                <span className={`font-semibold ${
                  item.wear_percentage >= 80 ? 'text-red-600' :
                  item.wear_percentage >= 60 ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {item.wear_percentage}% worn
                </span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    item.wear_percentage >= 80 ? 'bg-red-500' :
                    item.wear_percentage >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${item.wear_percentage}%` }}
                />
              </div>
              {item.measurement_value && (
                <p className="text-xs text-slate-500 mt-2">
                  Pad thickness: {item.measurement_value}mm {item.pad_sub_status ? `(${item.pad_sub_status === 'REPLACE_NOW' ? 'Replace Now' : item.pad_sub_status === 'REPLACE_SOON' ? 'Replace Soon' : item.pad_sub_status === 'MONITOR' ? 'Monitor' : 'Good'})` : ''}
                </p>
              )}
            </div>
          )}
          
          {/* Brake Disc Wear Visual */}
          {(item.disc_wear_percentage !== null && item.disc_wear_percentage !== undefined) && (
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Brake Disc Condition</span>
                <span className={`font-semibold ${
                  item.disc_wear_percentage >= 80 ? 'text-red-600' :
                  item.disc_wear_percentage >= 60 ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {item.disc_wear_percentage}% worn
                </span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    item.disc_wear_percentage >= 80 ? 'bg-red-500' :
                    item.disc_wear_percentage >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${item.disc_wear_percentage}%` }}
                />
              </div>
              {item.measurement_value && item.disc_min_thickness && (
                <p className="text-xs text-slate-500 mt-2">
                  Current: {item.measurement_value}mm | Minimum: {item.disc_min_thickness}mm
                </p>
              )}
            </div>
          )}
          
          {/* Tyre Tread Visual */}
          {(item.percent_worn !== null && item.percent_worn !== undefined) && (
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Tyre Tread Condition</span>
                <span className={`font-semibold ${
                  item.percent_worn >= 80 ? 'text-red-600' :
                  item.percent_worn >= 60 ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {item.percent_worn}% worn
                </span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    item.percent_worn >= 80 ? 'bg-red-500' :
                    item.percent_worn >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${item.percent_worn}%` }}
                />
              </div>
              {item.measurement_value && (
                <p className="text-xs text-slate-500 mt-2">
                  Tread depth: {item.measurement_value}mm (legal minimum: 1.6mm)
                </p>
              )}
            </div>
          )}
          
          {/* Battery Health Visual */}
          {(item.health_percent !== null && item.health_percent !== undefined) && (
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Battery Health</span>
                <span className={`font-semibold ${
                  item.health_percent >= 80 ? 'text-emerald-600' :
                  item.health_percent >= 60 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {item.health_percent}% health
                </span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    item.health_percent >= 80 ? 'bg-emerald-500' :
                    item.health_percent >= 60 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${item.health_percent}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                {item.battery_voltage_v && (
                  <span>Voltage: {item.battery_voltage_v}V</span>
                )}
                {item.measured_cca_a && item.rated_cca_a && (
                  <span>CCA: {item.measured_cca_a}A / {item.rated_cca_a}A rated</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {item.health_percent >= 80 ? 'Good condition' :
                 item.health_percent >= 60 ? 'Fair - monitor recommended' :
                 'Poor - replacement recommended'}
              </p>
            </div>
          )}

          {/* Battery Health Visual */}
          {(item.health_percent !== null && item.health_percent !== undefined) && (
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Battery Health</span>
                <span className={`font-semibold ${
                  item.health_percent >= 80 ? 'text-emerald-600' :
                  item.health_percent >= 60 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {item.health_percent}% health
                </span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    item.health_percent >= 80 ? 'bg-emerald-500' :
                    item.health_percent >= 60 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${item.health_percent}%` }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3 text-xs text-slate-500">
                {item.battery_voltage_v && (
                  <div>
                    <span className="font-medium">Voltage:</span> {item.battery_voltage_v}V
                  </div>
                )}
                {item.measured_cca_a && item.rated_cca_a && (
                  <div>
                    <span className="font-medium">CCA:</span> {item.measured_cca_a}/{item.rated_cca_a}A
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Premium Explanation Layout */}
          <div className="space-y-3">
            {item.explanation?.what_required && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we found</p>
                <p className="text-sm text-slate-700 leading-relaxed">{item.explanation.what_required}</p>
              </div>
            )}
            {item.explanation?.why_required && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we recommend</p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {aiExplanations[item.id]?.recommendation || item.explanation.why_required}
                </p>
              </div>
            )}
            {(aiExplanations[item.id]?.why_it_matters || item.explanation?.consequences) && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Why it matters</p>
                <p className="text-sm text-slate-600 leading-relaxed italic">
                  {aiExplanations[item.id]?.why_it_matters || item.explanation.consequences}
                </p>
                {/* MOT Note - only shown if AI generated and applicable */}
                {aiExplanations[item.id]?.mot_note && (
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    <span className="font-semibold">MOT:</span> {aiExplanations[item.id].mot_note}
                  </p>
                )}
              </div>
            )}
            {/* Deferred-Cost Nudge — amber boot/gaiter-split paths only */}
            {item.explanation?.deferred_cost_indicator?.applicable && (
              <div
                className="mt-2 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-2"
                data-testid={`deferred-cost-chip-${item.id}`}
              >
                <span className="text-amber-600 mt-0.5">⚠</span>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Cost if deferred</p>
                  <p className="text-sm text-amber-900 mt-1 leading-relaxed">
                    <span className="font-semibold">{item.explanation.deferred_cost_indicator.fix_now_range}</span>
                    <span className="text-amber-700"> now ({item.explanation.deferred_cost_indicator.fix_now_description})</span>
                    <span className="text-amber-800"> vs </span>
                    <span className="font-semibold">{item.explanation.deferred_cost_indicator.if_deferred_range}</span>
                    <span className="text-amber-700"> if left ({item.explanation.deferred_cost_indicator.if_deferred_description})</span>
                  </p>
                  <p className="text-xs text-amber-700 mt-1 italic">Typically deteriorates {item.explanation.deferred_cost_indicator.typical_window}</p>
                </div>
              </div>
            )}
            {/* Exhaust bundle chip — surfaces when multiple exhaust locations were flagged */}
            {item.explanation?.exhaust_bundle?.applicable && (
              <div
                className="mt-2 p-3 rounded-lg border border-blue-200 bg-blue-50 flex items-start gap-2"
                data-testid={`exhaust-bundle-chip-${item.id}`}
              >
                <span className="text-blue-600 mt-0.5">⛯</span>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Recommended supply</p>
                  <p className="text-sm text-blue-900 mt-1 leading-relaxed">
                    {item.explanation.exhaust_bundle.description.charAt(0).toUpperCase() + item.explanation.exhaust_bundle.description.slice(1)}.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Customer Education Insert - expandable */}
          {item.education_insert && (
            <div className="pt-3 border-t border-slate-100">
              <button
                onClick={() => toggleEducationInsert(item.id)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid={`education-toggle-${item.id}`}
              >
                {expandedEducationInserts[item.id] ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                <span>View evidence & details</span>
              </button>
              {expandedEducationInserts[item.id] && (
                <div className="mt-3 p-4 bg-slate-50 rounded-xl text-sm text-slate-600 leading-relaxed" data-testid={`education-content-${item.id}`}>
                  {item.education_insert}
                </div>
              )}
            </div>
          )}
          
          {/* Photos */}
          {item.photos?.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Evidence Photographs</p>
              <div className="flex gap-2 flex-wrap">
                {item.photos.map((photo, idx) => (
                  <img
                    key={idx}
                    src={photo}
                    alt={`Evidence ${idx + 1}`}
                    className="w-20 h-20 object-cover rounded-lg cursor-pointer border-2 border-slate-200 hover:border-slate-400 transition-colors"
                    onClick={() => setEnlargedPhoto(photo)}
                  />
                ))}
              </div>
              {item.credibility_line && (
                <p className="text-xs text-slate-400 mt-2">{item.credibility_line}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  // Render item with Version B explanation and education insert (legacy wrapper)
  const renderInspectionItem = (item, isExpanded = true) => (
    <PremiumInspectionItem item={item} isExpanded={isExpanded} />
  );
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f7f9' }}>
      {/* iter211ap — PREVIEW banner. Rendered ONLY when the token
          was minted via FOH's "Preview Portal" button. Prevents a
          preview tab from being mistaken for the real customer
          approval URL and silently authorising the estimate. */}
      {data?._preview_only && (
        <div
          className="bg-amber-500 text-white text-center py-2 px-4 font-semibold text-sm sticky top-0 z-40"
          data-testid="preview-banner"
        >
          PREVIEW MODE — this is what the customer will see. Nothing you submit here will authorise the estimate.
        </div>
      )}
      {/* Photo Enlargement Modal */}
      {enlargedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setEnlargedPhoto(null)}
        >
          <img src={enlargedPhoto} alt="Enlarged" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
      
      {/* Premium Header - Deep Navy Gradient */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Garage Logo - graceful handling */}
          {garageLogo ? (
            <div className="flex justify-center mb-5">
              <img 
                src={garageLogo} 
                alt={garageName || "Garage"} 
                className="object-contain"
                style={{ 
                  maxHeight: '60px', 
                  maxWidth: '200px',
                  filter: 'brightness(0) invert(1)',
                  opacity: 0.9
                }}
                data-testid="header-garage-logo"
              />
            </div>
          ) : garageName ? (
            <p className="text-center text-slate-400 text-sm mb-4 tracking-wide" data-testid="header-garage-name">
              {garageName}
            </p>
          ) : null}
          
          {/* Main Title */}
          <h1 className="text-2xl sm:text-3xl font-bold text-center tracking-tight">
            Vehicle Health Report
          </h1>
          
          {/* Vehicle Subline */}
          <p className="text-center text-slate-300 mt-3 text-sm sm:text-base font-medium">
            {data.vehicle?.reg} • {data.vehicle?.make} {data.vehicle?.model} • {data.mileage?.toLocaleString()} miles
          </p>
          
          {/* Micro Line */}
          <p className="text-center text-slate-400 mt-2 text-xs tracking-wide">
            Inspected today • Evidence photographed
          </p>
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Hero Introduction Card */}
        <div 
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" 
          data-testid="hero-intro-card"
        >
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-4">
            We've carefully checked your vehicle
          </h2>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-4">
            We've completed a comprehensive Safety & Reliability Inspection while your vehicle has been with us.
            The findings below are prioritised to clearly show what needs urgent attention and what should be planned next — so you can make informed decisions with confidence.
          </p>
          
          {/* Overall Vehicle Status Strip */}
          {(() => {
            const status = getOverallVehicleStatus();
            return (
              <div className={`${status.color} rounded-xl px-4 py-3 text-white text-center font-medium text-sm mb-4`}>
                {status.label}
              </div>
            );
          })()}
          
          {/* MOT Micro Line */}
          <div className="pt-4 border-t border-slate-100">
            <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
              This inspection goes beyond the MOT by comparing key systems to manufacturer standards, not just minimum legal requirements.
            </p>
          </div>
        </div>
        
        {/* HEALTH INDICATORS PANEL - Battery, Brake Fluid, Coolant, Tyres, Brake Pads */}
        {data.health_indicators && data.health_indicators.length > 0 && (
          <div 
            className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6"
            data-testid="health-indicators-panel"
          >
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Vehicle Health Check
              </h3>
              <p className="text-sm text-slate-500 mt-1">Key safety and wear components</p>
            </div>
            
            {/* Fluid Indicators - Circular (Battery, Brake Fluid, Coolant) */}
            {data.health_indicators.filter(i => !i.indicator_type).length > 0 && (
              <div className="p-4 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Fluids & Battery</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {data.health_indicators.filter(i => !i.indicator_type).map((indicator, idx) => {
                    // Determine the proper label based on type
                    const displayLabel = indicator.is_brake_fluid ? 'Brake Fluid Health' : 
                                        indicator.is_battery ? 'Battery Health' :
                                        indicator.is_coolant ? 'Coolant Health' :
                                        indicator.label;
                    const showDegradation = indicator.is_brake_fluid || indicator.is_battery || indicator.is_coolant;
                    const degradationPct = indicator.degradation_percentage || (100 - indicator.health_percentage);
                    
                    return (
                      <div 
                        key={idx}
                        className={`rounded-lg p-3 border ${
                          indicator.health_color === 'green' ? 'bg-emerald-50/50 border-emerald-200' :
                          indicator.health_color === 'amber' ? 'bg-amber-50/50 border-amber-200' :
                          indicator.health_color === 'red' ? 'bg-red-50/50 border-red-200' :
                          'bg-slate-50/50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Small circular indicator */}
                          <div className="relative w-12 h-12 flex-shrink-0" title={`Health: ${indicator.health_percentage}% | Degradation: ${degradationPct}%`}>
                            <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                              <circle className="text-slate-200" strokeWidth="3" stroke="currentColor" fill="transparent" r="14" cx="18" cy="18" />
                              <circle
                                className={indicator.health_color === 'green' ? 'text-emerald-500' : indicator.health_color === 'amber' ? 'text-amber-500' : indicator.health_color === 'red' ? 'text-red-500' : 'text-slate-400'}
                                strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="transparent" r="14" cx="18" cy="18"
                                strokeDasharray={`${(indicator.health_percentage || 0) * 0.88} 100`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className={`text-xs font-bold ${indicator.health_color === 'green' ? 'text-emerald-700' : indicator.health_color === 'amber' ? 'text-amber-700' : indicator.health_color === 'red' ? 'text-red-700' : 'text-slate-600'}`}>
                                {indicator.health_percentage}%
                              </span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Label with Health indicator */}
                            <div className="text-sm font-medium text-slate-700">
                              {displayLabel}
                            </div>
                            <div className={`text-xs font-semibold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : indicator.health_color === 'red' ? 'text-red-600' : 'text-slate-500'}`}>
                              {indicator.health_label}
                            </div>
                            {/* Measurement with degradation context */}
                            <div className="text-xs text-slate-400">
                              {indicator.measurement_value}{indicator.measurement_unit}
                              {showDegradation && (
                                <span className="ml-1 text-slate-500">
                                  ({degradationPct}% degraded)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Help text for fluids/battery */}
                        {showDegradation && (
                          <div className="mt-2 pt-2 border-t border-slate-200/50">
                            <p className="text-xs text-slate-400 italic">
                              Health % = serviceability. Degradation % = 100 − Health %.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Tyre Indicators - Horizontal Bars */}
            {data.health_indicators.filter(i => i.indicator_type === 'tyre').length > 0 && (
              <div className="p-4 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tyre Tread Depth</p>
                <div className="grid grid-cols-2 gap-3">
                  {data.health_indicators.filter(i => i.indicator_type === 'tyre').map((indicator, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">{indicator.label}</span>
                        <span className={`text-sm font-bold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                          {indicator.measurement_value}mm
                        </span>
                      </div>
                      {/* Health Bar */}
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${indicator.health_color === 'green' ? 'bg-emerald-500' : indicator.health_color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${indicator.health_percentage}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-xs ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                          {indicator.health_label}
                        </span>
                        <span className="text-xs text-slate-400">{indicator.health_percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Brake Pad Indicators - Horizontal Bars */}
            {data.health_indicators.filter(i => i.indicator_type === 'brake_pads').length > 0 && (
              <div className="p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Brake Pad Thickness</p>
                <div className="grid grid-cols-2 gap-3">
                  {data.health_indicators.filter(i => i.indicator_type === 'brake_pads').map((indicator, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">{indicator.label}</span>
                        <span className={`text-sm font-bold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                          {indicator.measurement_value}mm
                        </span>
                      </div>
                      {/* Health Bar */}
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${indicator.health_color === 'green' ? 'bg-emerald-500' : indicator.health_color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${indicator.health_percentage}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-xs ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                          {indicator.health_label}
                        </span>
                        <span className="text-xs text-slate-400">{indicator.health_percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* EXECUTIVE SUMMARY PANEL */}
        {(() => {
          const redCount = data.red_items?.length || 0;
          const amberCount = data.amber_items?.length || 0;
          const totalItems = redCount + amberCount;
          const redTotal = data.red_total || 0;
          const amberTotal = data.amber_total || 0;
          const combinedTotal = data.total_estimate || (redTotal + amberTotal);
          
          // Only show if total items >= 3
          if (totalItems < 3) return null;
          
          // Auto-collapse detailed sections if >= 8 items (set once on mount)
          const shouldCollapseDetails = totalItems >= 8;
          
          return (
            <div 
              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
              data-testid="executive-summary-panel"
            >
              {/* Panel Header */}
              <button
                onClick={() => setShowExecutiveSummary(!showExecutiveSummary)}
                className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <h3 className="font-bold text-slate-800 text-lg">Executive Summary</h3>
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  {showExecutiveSummary ? (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Hide summary
                    </>
                  ) : (
                    <>
                      <ChevronRight className="w-4 h-4" />
                      Show summary
                    </>
                  )}
                </span>
              </button>
              
              {/* Panel Content - Collapsible */}
              <div 
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  showExecutiveSummary ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6 space-y-4">
                  {/* Urgent Safety Items Block */}
                  {redCount > 0 && (
                    <div className="bg-red-50/60 border border-red-100 rounded-xl p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-red-800 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            Urgent Safety Items ({redCount})
                          </h4>
                          <p className="text-red-700/80 text-sm mt-1 leading-relaxed">
                            To maintain safe and reliable performance, we recommend these items are addressed as a priority.
                          </p>
                        </div>
                        <div className="text-right sm:text-right">
                          <p className="text-2xl font-bold text-red-800">£{redTotal.toFixed(2)}</p>
                          <p className="text-xs text-red-600">inc. VAT</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Maintenance Soon Block */}
                  {amberCount > 0 && (
                    <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Maintenance Soon ({amberCount})
                          </h4>
                          <p className="text-amber-700/80 text-sm mt-1 leading-relaxed">
                            These items are not urgent today, but are best planned in good time to prevent wear progressing into more costly repairs.
                          </p>
                        </div>
                        <div className="text-right sm:text-right">
                          <p className="text-2xl font-bold text-amber-800">£{amberTotal.toFixed(2)}</p>
                          <p className="text-xs text-amber-600">inc. VAT</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Combined Total Block - only if both categories exist */}
                  {redCount > 0 && amberCount > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800">
                            If all recommended work is completed
                          </h4>
                          <p className="text-slate-500 text-sm mt-1">
                            Completing items during the same visit can often reduce duplicated labour and future inconvenience.
                          </p>
                        </div>
                        <div className="text-right sm:text-right">
                          <p className="text-2xl font-bold text-slate-900">£{combinedTotal.toFixed(2)}</p>
                          <p className="text-xs text-slate-500">total inc. VAT</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Agreed Work Anchor */}
        {data.agreed_work_value && data.agreed_work_value > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-emerald-800">Work already authorised:</span>
              <span className="text-xl font-bold text-emerald-700">£{data.agreed_work_value.toFixed(2)}</span>
            </div>
          </div>
        )}
        
        {/* Diagnostic Report (if present) */}
        {data.diagnostic_report && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-purple-50 border-b border-purple-100 px-6 py-4">
              <h3 className="text-purple-800 font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Diagnostic Assessment
              </h3>
            </div>
            <div className="p-6">
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed">
                {data.diagnostic_report}
              </div>
            </div>
          </div>
        )}
        
        {/* DANGEROUS ITEMS - Immediate Safety Risk (Highest Priority) */}
        {data.dangerous_items?.length > 0 && (
          <div className="bg-white rounded-2xl border-2 border-slate-800 shadow-lg overflow-hidden">
            {/* Section Header - Professional, Calm but Firm */}
            <div className="bg-slate-800 text-white px-6 py-5">
              <h3 className="font-bold flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Immediate Safety Risk Identified
              </h3>
              <p className="text-slate-300 text-sm mt-2">
                {data.dangerous_items.length} {data.dangerous_items.length === 1 ? 'item has' : 'items have'} been classified as dangerous. We strongly advise the vehicle is not driven until rectified.
              </p>
            </div>
            
            <div className="p-5 bg-slate-50">
              {/* Dangerous Items List with Structured Data */}
              {data.dangerous_items.map((item, idx) => (
                <div key={idx} className="bg-white border border-slate-300 rounded-xl p-5 mb-4 shadow-sm">
                  {/* Item Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="bg-slate-800 text-white text-xs font-bold px-2 py-1 rounded">DANGEROUS</span>
                      {item.category && (
                        <span className="text-xs text-slate-500">{item.category}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Item Label */}
                  <p className="font-bold text-slate-800 text-lg mb-3">{item.label}</p>
                  
                  {/* Risk Categories (if available) */}
                  {item.danger_categories?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Risk Category:</p>
                      <div className="flex flex-wrap gap-2">
                        {item.danger_categories.map((cat, catIdx) => (
                          <span key={catIdx} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Danger Reasons (if available) */}
                  {item.danger_reasons?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Reason Identified:</p>
                      <ul className="text-sm text-slate-700 space-y-1">
                        {item.danger_reasons.map((reason, rIdx) => (
                          <li key={rIdx} className="flex items-start gap-2">
                            <span className="text-red-500 mt-1">•</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Technician Notes */}
                  {item.notes && (
                    <div className="bg-slate-50 border border-slate-200 rounded p-3 mt-3">
                      <p className="text-xs font-semibold text-slate-600 mb-1">Technician Note:</p>
                      <p className="text-sm text-slate-700">{item.notes}</p>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Advisory Box - Professional Tone */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-2">Important Information</p>
                    <p className="leading-relaxed">
                      Driving a vehicle with dangerous defects may result in penalty points, fines, or insurance invalidation. 
                      We recommend the vehicle remains with us for immediate repair.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* URGENT SAFETY ITEMS (RED) */}
        {data.red_items?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Section Header - Softer Red */}
            <div className="bg-red-50 border-b border-red-100 px-6 py-5">
              <h3 className="text-red-800 font-bold text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Urgent Safety Items ({data.red_items.length})
              </h3>
              <p className="text-red-700/80 text-sm mt-1 leading-relaxed">
                These items require attention before the vehicle is driven further, to maintain safe and reliable operation.
              </p>
            </div>
            <div className="p-5">
              {data.red_items.map(item => renderInspectionItem(item, true))}
              <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                <span className="font-semibold text-slate-700">Urgent Safety Subtotal:</span>
                <span className="text-xl font-bold text-red-700">£{data.red_total?.toFixed(2) || "0.00"}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* MAINTENANCE SOON (AMBER) */}
        {data.amber_items?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Section Header - Softer Amber */}
            <div className="bg-amber-50 border-b border-amber-100 px-6 py-5">
              <h3 className="text-amber-800 font-bold text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Maintenance Soon ({data.amber_items.length})
              </h3>
              <p className="text-amber-700/80 text-sm mt-1 leading-relaxed">
                Not urgent today, but best planned in good time to prevent wear progressing into more costly repairs.
              </p>
            </div>
            <div className="p-5">
              {/* Bundle summary */}
              <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4 mb-5">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-amber-900">Maintenance Bundle</p>
                    <p className="text-sm text-amber-700">{data.amber_items.length} preventative items identified</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-amber-800">£{data.amber_total?.toFixed(2) || "0.00"}</p>
                    <p className="text-xs text-amber-600">inc. VAT</p>
                  </div>
                </div>
              </div>
              
              {/* Show first 3 amber items (or all if showAllAmberItems) */}
              {(showAllAmberItems ? data.amber_items : data.amber_items.slice(0, 3)).map(item => (
                <div key={item.id} className="bg-white rounded-xl border border-slate-200 mb-3 overflow-hidden shadow-sm">
                  <button
                    className="w-full p-4 flex justify-between items-center hover:bg-slate-50 transition-colors"
                    onClick={() => toggleAmberItem(item.id)}
                  >
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        {expandedAmberItems[item.id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <span className="font-medium text-slate-800">{item.label}</span>
                      </div>
                      {/* AOV Enhancement: Efficiency indicator badge */}
                      {item.efficiency_indicator && (
                        <div className="ml-6 mt-1">
                          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                            Cheaper to complete now
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="font-bold text-slate-800">£{item.price_inc_vat?.toFixed(2) || "0.00"}</span>
                  </button>
                  {expandedAmberItems[item.id] && (
                    <div className="p-5 bg-slate-50/50 border-t border-slate-100">
                      {/* AOV Enhancement: Escalation note (cost-prevention framing) */}
                      {item.escalation_note && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                          <p className="text-sm text-amber-800">
                            {item.escalation_note}
                          </p>
                        </div>
                      )}
                      
                      {/* AOV Enhancement: Shared labour detail */}
                      {item.efficiency_indicator && (
                        <div className="mb-4 p-2 bg-slate-100 border border-slate-200 rounded-lg">
                          <p className="text-xs text-slate-600">
                            {item.efficiency_indicator}
                          </p>
                        </div>
                      )}
                      
                      {/* Wear Percentage Visual - for brakes */}
                      {(item.wear_percentage !== null && item.wear_percentage !== undefined) && (
                        <div className="mb-4 p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-slate-600 font-medium">Brake Pad Condition</span>
                            <span className={`font-semibold ${
                              item.wear_percentage >= 80 ? 'text-red-600' :
                              item.wear_percentage >= 60 ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {item.wear_percentage}% worn
                            </span>
                          </div>
                          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                item.wear_percentage >= 80 ? 'bg-red-500' :
                                item.wear_percentage >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${item.wear_percentage}%` }}
                            />
                          </div>
                          {item.measurement_value && (
                            <p className="text-xs text-slate-500 mt-2">
                              Pad thickness: {item.measurement_value}mm {item.pad_sub_status ? `(${item.pad_sub_status === 'REPLACE_NOW' ? 'Replace Now' : item.pad_sub_status === 'REPLACE_SOON' ? 'Replace Soon' : item.pad_sub_status === 'MONITOR' ? 'Monitor' : 'Good'})` : ''}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Tyre Tread Visual */}
                      {(item.percent_worn !== null && item.percent_worn !== undefined) && (
                        <div className="mb-4 p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-slate-600 font-medium">Tyre Tread Condition</span>
                            <span className={`font-semibold ${
                              item.percent_worn >= 80 ? 'text-red-600' :
                              item.percent_worn >= 60 ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {item.percent_worn}% worn
                            </span>
                          </div>
                          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                item.percent_worn >= 80 ? 'bg-red-500' :
                                item.percent_worn >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${item.percent_worn}%` }}
                            />
                          </div>
                          {item.measurement_value && (
                            <p className="text-xs text-slate-500 mt-2">
                              Tread depth: {item.measurement_value}mm (legal minimum: 1.6mm)
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Premium Explanation Layout */}
                      <div className="space-y-3 mb-4">
                        {item.explanation?.what_required && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we found</p>
                            <p className="text-sm text-slate-700 leading-relaxed">
                              {aiExplanations[item.id]?.what_found || item.explanation.what_required}
                            </p>
                          </div>
                        )}
                        {item.explanation?.why_required && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">What we recommend</p>
                            <p className="text-sm text-slate-700 leading-relaxed">
                              {aiExplanations[item.id]?.recommendation || item.explanation.why_required}
                            </p>
                          </div>
                        )}
                        {(aiExplanations[item.id]?.why_it_matters || item.explanation?.consequences) && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Why it matters</p>
                            <p className="text-sm text-slate-600 leading-relaxed italic">
                              {aiExplanations[item.id]?.why_it_matters || item.explanation.consequences}
                            </p>
                            {/* MOT Note - only shown if AI generated and applicable */}
                            {aiExplanations[item.id]?.mot_note && (
                              <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                                <span className="font-semibold">MOT:</span> {aiExplanations[item.id].mot_note}
                              </p>
                            )}
                          </div>
                        )}
                        {/* Deferred-Cost Nudge — amber boot/gaiter-split paths */}
                        {item.explanation?.deferred_cost_indicator?.applicable && (
                          <div
                            className="mt-2 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-2"
                            data-testid={`deferred-cost-chip-customer-${item.id}`}
                          >
                            <span className="text-amber-600 mt-0.5">⚠</span>
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Cost if deferred</p>
                              <p className="text-sm text-amber-900 mt-1 leading-relaxed">
                                <span className="font-semibold">{item.explanation.deferred_cost_indicator.fix_now_range}</span>
                                <span className="text-amber-700"> now ({item.explanation.deferred_cost_indicator.fix_now_description})</span>
                                <span className="text-amber-800"> vs </span>
                                <span className="font-semibold">{item.explanation.deferred_cost_indicator.if_deferred_range}</span>
                                <span className="text-amber-700"> if left ({item.explanation.deferred_cost_indicator.if_deferred_description})</span>
                              </p>
                              <p className="text-xs text-amber-700 mt-1 italic">Typically deteriorates {item.explanation.deferred_cost_indicator.typical_window}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Customer Education Insert - expandable */}
                      {item.education_insert && (
                        <div className="pt-3 border-t border-slate-200">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleEducationInsert(item.id); }}
                            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                            data-testid={`education-toggle-amber-${item.id}`}
                          >
                            {expandedEducationInserts[item.id] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            <span>View evidence & details</span>
                          </button>
                          {expandedEducationInserts[item.id] && (
                            <div className="mt-3 p-4 bg-slate-50 rounded-xl text-sm text-slate-600 leading-relaxed" data-testid={`education-content-amber-${item.id}`}>
                              {item.education_insert}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {item.photos?.length > 0 && (
                        <div className="pt-3 border-t border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Evidence Photographs</p>
                          <div className="flex gap-2 flex-wrap">
                            {item.photos.map((photo, idx) => (
                              <img
                                key={idx}
                                src={photo}
                                alt={`Evidence ${idx + 1}`}
                                className="w-16 h-16 object-cover rounded-lg cursor-pointer border-2 border-slate-200 hover:border-slate-400 transition-colors"
                                onClick={() => setEnlargedPhoto(photo)}
                              />
                            ))}
                          </div>
                          {item.credibility_line && (
                            <p className="text-xs text-slate-400 mt-2">{item.credibility_line}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Show more button if > 3 items */}
              {data.amber_items.length > 3 && !showAllAmberItems && (
                <button
                  className="w-full py-4 text-center text-amber-700 hover:text-amber-900 text-sm font-medium border-t border-slate-200 mt-2"
                  onClick={() => {
                    setShowAllAmberItems(true);
                    setEngagementData(prev => ({ ...prev, amberSectionViewed: true }));
                  }}
                >
                  View {data.amber_items.length - 3} more items
                </button>
              )}
              
              {showAllAmberItems && data.amber_items.length > 3 && (
                <button
                  className="w-full py-4 text-center text-amber-700 hover:text-amber-900 text-sm font-medium border-t border-slate-200 mt-2"
                  onClick={() => setShowAllAmberItems(false)}
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Green Items Summary */}
        {data.green_count > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <span className="text-emerald-800">
              <strong>{data.green_count} items</strong> passed inspection with no issues found
            </span>
          </div>
        )}
        
        {/* Technical Report Download */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <button
            onClick={handleDownloadPDF}
            className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
          >
            <Download className="w-5 h-5" />
            <span>Download Technical Report (PDF)</span>
          </button>
          <p className="text-xs text-center text-slate-400 mt-2">Reference / supporting evidence</p>
        </div>
        
        {/* Total */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-2xl p-5 shadow-lg">
          <div className="flex justify-between items-center">
            <span className="text-lg">Total Estimate (inc. VAT):</span>
            <span className="text-2xl font-bold">£{data.total_estimate?.toFixed(2) || "0.00"}</span>
          </div>
        </div>
        
        {/* AOV Enhancement: Workshop Efficiency Line */}
        {data.workshop_efficiency_line && data.amber_items?.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-sm text-slate-600">
              {data.workshop_efficiency_line}
            </p>
          </div>
        )}
        
        {/* Decision Section - Outcome-Led Framing */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-5">
            <h3 className="text-xl font-bold text-slate-800">Choose how you'd like your vehicle returned</h3>
            <p className="text-slate-600 text-sm mt-1">
              Select the outcome that's right for you
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Customer Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-medium">Your Name *</Label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  className="mt-1"
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="07xxx xxxxxx"
                  className="mt-1"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>
            
            <Separator className="my-6" />
            
            {/* Outcome-Led Decision Options */}
            <div className="space-y-3">
              {/* PRIMARY OPTION - Safe + More Reliable (Recommended) */}
              <button
                className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                  decisionType === "approve_all" 
                    ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" 
                    : "border-emerald-300 bg-emerald-50/30 hover:bg-emerald-50 hover:border-emerald-400"
                }`}
                onClick={() => { 
                  setDecisionType("approve_all"); 
                  setDeclineFollowUp(null); 
                  setShowBudgetInput(false);
                  setPrioritisedItems(null);
                  setShowRecoveryPrompt(false);
                }}
                data-testid="approve-all-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-lg text-green-800">Safe + More Reliable</p>
                      <Badge className="bg-green-600 text-white text-xs">Recommended</Badge>
                    </div>
                    <p className="text-sm text-green-700 mb-2">
                      Complete all safety and preventative work — your vehicle returns in the best possible condition
                    </p>
                    <p className="text-lg font-bold text-green-900">£{data.total_estimate?.toFixed(2) || "0.00"}</p>
                  </div>
                </div>
              </button>
              
              {/* SECONDARY OPTION - Safe & Roadworthy (Minimum) */}
              {data.red_items?.length > 0 && (
                <button
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    decisionType === "approve_safety_only" 
                      ? "border-amber-500 bg-amber-50" 
                      : "border-gray-200 hover:border-amber-300"
                  }`}
                  onClick={() => { 
                    setDecisionType("approve_safety_only"); 
                    setDeclineFollowUp(null);
                    setShowBudgetInput(false);
                    setPrioritisedItems(null);
                    setShowRecoveryPrompt(false);
                  }}
                  data-testid="approve-safety-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-amber-800">Safe & Roadworthy</p>
                      <p className="text-sm text-gray-600">
                        Safety-critical items only — minimum to keep you safe on the road
                      </p>
                      <p className="text-base font-bold text-amber-900 mt-1">£{data.red_total?.toFixed(2)}</p>
                    </div>
                  </div>
                </button>
              )}
              
              {/* NEW OPTION - Prioritise Within Budget (AOV Optimisation) */}
              <button
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  decisionType === "prioritise_within_budget" 
                    ? "border-purple-500 bg-purple-50" 
                    : "border-gray-200 hover:border-purple-300"
                }`}
                onClick={() => { 
                  setDecisionType("prioritise_within_budget"); 
                  setShowBudgetInput(true);
                  setDeclineFollowUp(null);
                  setShowRecoveryPrompt(false);
                }}
                data-testid="prioritise-budget-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-purple-800">Prioritise within my budget</p>
                    <p className="text-sm text-gray-600">
                      We'll prioritise the most important work to keep you safe and reduce bigger repair risks
                    </p>
                  </div>
                </div>
              </button>
              
              {/* TERTIARY OPTION - Talk it through */}
              <button
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  decisionType === "call_to_discuss" 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-gray-200 hover:border-blue-300"
                }`}
                onClick={() => { 
                  setDecisionType("call_to_discuss"); 
                  setDeclineFollowUp(null);
                  setShowBudgetInput(false);
                  setPrioritisedItems(null);
                  setShowRecoveryPrompt(false);
                }}
                data-testid="call-discuss-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-blue-800">Talk it through</p>
                    <p className="text-sm text-gray-600">
                      Request a callback to discuss your options with our team
                    </p>
                  </div>
                </div>
              </button>
              
              {/* DECLINE OPTION - Subtle but available */}
              <button
                className={`w-full p-4 rounded-lg border text-left transition-all ${
                  decisionType === "decline" 
                    ? "border-red-400 bg-red-50" 
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => {
                  setDecisionType("decline");
                  setShowBudgetInput(false);
                  setPrioritisedItems(null);
                  setShowRecoveryPrompt(false);
                  setRejectionReason("");
                }}
                data-testid="decline-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-500">✗</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-700">Decline work</p>
                    <p className="text-sm text-gray-500">No work required at this time</p>
                  </div>
                </div>
              </button>
            </div>
            
            {/* Budget Prioritisation Flow (AOV Optimisation) */}
            {decisionType === "prioritise_within_budget" && showBudgetInput && (
              <div className="border-l-4 border-purple-400 bg-purple-50 p-4 rounded-r-lg space-y-4">
                <div>
                  <Label className="text-purple-800 font-medium">Enter your budget (£)</Label>
                  <p className="text-sm text-purple-600 mb-2">
                    We'll prioritise the most important work to keep your vehicle safe and reduce the risk of bigger repairs.
                  </p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                      <Input
                        type="number"
                        min="1"
                        step="0.01"
                        value={budgetAmount}
                        onChange={(e) => {
                          setBudgetAmount(e.target.value);
                          setPrioritisedItems(null);
                          setPrioritisationConfirmed(false);
                        }}
                        placeholder="Enter amount"
                        className="pl-7"
                        data-testid="budget-input"
                      />
                    </div>
                    <Button onClick={handleBudgetSubmit} data-testid="calculate-budget-btn">
                      Calculate
                    </Button>
                  </div>
                  {budgetError && <p className="text-red-600 text-sm mt-1">{budgetError}</p>}
                </div>
                
                {/* Budget Below Safety Warning */}
                {prioritisedItems && !prioritisedItems.success && prioritisedItems.error === "budget_below_safety" && (
                  <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                    <p className="text-red-800 font-medium mb-2">⚠️ Budget Below Safety Minimum</p>
                    <p className="text-sm text-red-700 mb-3">{prioritisedItems.message}</p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setBudgetAmount(prioritisedItems.redTotal.toString());
                          setPrioritisedItems(null);
                        }}
                        data-testid="increase-budget-btn"
                      >
                        Set to £{prioritisedItems.redTotal.toFixed(2)}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setDecisionType("call_to_discuss");
                          setShowBudgetInput(false);
                          setPrioritisedItems(null);
                        }}
                        data-testid="budget-call-btn"
                      >
                        Request a call instead
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Prioritised Items Result */}
                {prioritisedItems?.success && (
                  <div className="bg-white border border-purple-200 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <p className="font-semibold text-purple-800">Recommended within your budget</p>
                      <Badge className="bg-purple-600 text-white">£{prioritisedItems.totalIncluded.toFixed(2)}</Badge>
                    </div>
                    
                    {/* Included Items */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Included ({prioritisedItems.included.length} items):</p>
                      {prioritisedItems.included.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm py-1 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${item.category === 'red' ? 'bg-red-500' : 'bg-amber-500'}`}></span>
                            <span className={item.category === 'red' ? 'font-medium' : ''}>{item.label}</span>
                            {item.category === 'red' && <Badge className="bg-red-100 text-red-700 text-xs">Safety</Badge>}
                          </div>
                          <span className="font-medium">£{item.price_inc_vat?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Excluded Items (if any) */}
                    {prioritisedItems.excluded.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-500">Not included ({prioritisedItems.excluded.length} items):</p>
                        {prioritisedItems.excluded.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm text-gray-500 py-1">
                            <span>{item.label}</span>
                            <span>£{item.price_inc_vat?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="bg-purple-50 rounded p-3 mt-3">
                      <div className="flex justify-between text-sm">
                        <span>Your budget:</span>
                        <span>£{prioritisedItems.budget.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Work included:</span>
                        <span className="font-semibold">£{prioritisedItems.totalIncluded.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Remaining:</span>
                        <span>£{prioritisedItems.remainingBudget.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {/* Confirmation Checkbox */}
                    <div className="flex items-start gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="confirm-prioritisation"
                        checked={prioritisationConfirmed}
                        onChange={(e) => setPrioritisationConfirmed(e.target.checked)}
                        className="mt-1"
                        data-testid="confirm-prioritisation-checkbox"
                      />
                      <label htmlFor="confirm-prioritisation" className="text-sm text-gray-700">
                        I confirm I want to proceed with this prioritised work (£{prioritisedItems.totalIncluded.toFixed(2)})
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Decline Reason + Recovery Flow (AOV Optimisation) */}
            {decisionType === "decline" && (
              <div className="border-l-4 border-red-400 bg-red-50 p-4 rounded-r-lg space-y-4">
                <div>
                  <Label className="text-red-800">Reason for Declining *</Label>
                  <Select 
                    value={rejectionReason} 
                    onValueChange={handleDeclineReasonSelect}
                  >
                    <SelectTrigger data-testid="decline-reason-select">
                      <SelectValue placeholder="Please select a reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {data.rejection_reasons?.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Recovery Prompt (One single contextual prompt based on reason) */}
                {rejectionReason && getDeclineRecoveryPrompt(rejectionReason) && !recoveryResponse && (
                  <div className="bg-white border border-amber-300 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-800 mb-3">
                      {getDeclineRecoveryPrompt(rejectionReason).question}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleRecoveryResponse("yes", rejectionReason)}
                        data-testid="recovery-yes-btn"
                      >
                        {getDeclineRecoveryPrompt(rejectionReason).yesLabel}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRecoveryResponse("no", rejectionReason)}
                        data-testid="recovery-no-btn"
                      >
                        {getDeclineRecoveryPrompt(rejectionReason).noLabel}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Show response confirmation */}
                {recoveryResponse === "no" && (
                  <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Decline will be recorded. Thank you for your feedback.</p>
                  </div>
                )}
                
                {recoveryResponse === "yes" && rejectionReason === "need_time_to_think" && (
                  <div className="bg-green-100 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700">✓ We'll contact you to book this in at a later date.</p>
                  </div>
                )}
                
                {/* Legacy follow-up for other reasons (non-recovery) */}
                {rejectionReason && getDeclineFollowUp(rejectionReason) && (
                  <div className="bg-white border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-800 mb-3">
                      {getDeclineFollowUp(rejectionReason).question}
                    </p>
                    <div className="flex gap-2">
                      {getDeclineFollowUp(rejectionReason).options.map((option, idx) => (
                        <Button
                          key={idx}
                          variant={declineFollowUp === option ? "default" : "outline"}
                          size="sm"
                          onClick={() => setDeclineFollowUp(option)}
                          data-testid={`decline-followup-${idx}`}
                        >
                          {option}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <Label className="text-red-800">Additional Comments (optional)</Label>
                  <Textarea
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    placeholder="Any additional feedback..."
                    rows={2}
                    data-testid="decline-notes-input"
                  />
                </div>
              </div>
            )}
            
            <Separator />
            
            {/* Response Timeline Nudge */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <p className="text-sm text-blue-800">
                To avoid delaying completion, please let us know how you'd like to proceed by <strong>{getResponseDeadline()}</strong> today
              </p>
            </div>
            
            {/* Signature */}
            <div>
              <Label className="mb-3 block">Signature</Label>
              <div className="flex gap-4 mb-4">
                <Button
                  variant={signatureType === "typed" ? "default" : "outline"}
                  onClick={() => setSignatureType("typed")}
                  size="sm"
                  data-testid="signature-typed-btn"
                >
                  Type Name
                </Button>
                <Button
                  variant={signatureType === "drawn" ? "default" : "outline"}
                  onClick={() => setSignatureType("drawn")}
                  size="sm"
                  data-testid="signature-drawn-btn"
                >
                  Draw Signature
                </Button>
              </div>
              
              {signatureType === "typed" && (
                <div>
                  <Input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your full name as signature"
                    className="text-lg italic"
                    data-testid="signature-typed-input"
                  />
                  {typedName && (
                    <div className="mt-2 p-3 border rounded bg-gray-50">
                      <p className="text-xl font-serif italic text-gray-700">{typedName}</p>
                    </div>
                  )}
                </div>
              )}
              
              {signatureType === "drawn" && (
                <div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                    <canvas
                      id="signature-canvas"
                      width={350}
                      height={120}
                      className="w-full border rounded cursor-crosshair touch-none"
                      style={{ touchAction: 'none' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <Button variant="outline" size="sm" className="mt-2" onClick={clearSignature}>
                    Clear
                  </Button>
                </div>
              )}
            </div>
            
            <Separator />
            
            {/* Payment Assist - Subtle placement */}
            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
              <p>Payment Assist is available to spread the cost over monthly instalments. Please ask if you'd like to use this option.</p>
            </div>
            
            {/* Submit */}
            <Button
              size="lg"
              className={`w-full ${decisionType === 'approve_all' ? 'bg-green-600 hover:bg-green-700' : decisionType === 'prioritise_within_budget' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
              onClick={handleSubmit}
              disabled={submitting || !decisionType || (decisionType === "prioritise_within_budget" && (!prioritisedItems?.success || !prioritisationConfirmed))}
              data-testid="submit-decision-btn"
            >
              {submitting ? "Submitting..." : 
                decisionType === "approve_all" ? "Approve Recommended Work" :
                decisionType === "approve_safety_only" ? "Approve Safety Items Only" :
                decisionType === "prioritise_within_budget" ? `Approve Prioritised Work (£${prioritisedItems?.totalIncluded?.toFixed(2) || '0.00'})` :
                decisionType === "call_to_discuss" ? "Request Callback" :
                decisionType === "decline" ? "Submit Response" :
                "Submit Decision"
              }
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              By submitting, you confirm that you have reviewed the inspection results.
            </p>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8 mt-8">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-slate-400">
          <p>Southern Co. - MOT • Servicing • Repairs</p>
        </div>
      </div>
    </div>
  );
};


// ============== FOH FOLLOW-UPS DASHBOARD ==============
const FOHFollowUpsDashboard = () => {
  const navigate = useNavigate();
  const [followUps, setFollowUps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [showCallCompanion, setShowCallCompanion] = useState(false);
  const [callCompanionData, setCallCompanionData] = useState(null);
  const [callCompanionLoading, setCallCompanionLoading] = useState(false);
  const [callChecklist, setCallChecklist] = useState({});
  const [showDeclinedOnly, setShowDeclinedOnly] = useState(true);
  const [editingEstimate, setEditingEstimate] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [outcomeForm, setOutcomeForm] = useState({
    outcome: '',
    notes: '',
    recovered_value: '',
    next_follow_up_days: '',
    items_agreed: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Open Call Companion
  const openCallCompanion = async (fu) => {
    setSelectedFollowUp(fu);
    setCallCompanionLoading(true);
    setShowCallCompanion(true);
    try {
      const res = await axios.get(`${API}/follow-ups/${fu.id}/call-companion`);
      setCallCompanionData(res.data);
      setCallChecklist(
        res.data.call_script?.checklist?.reduce((acc, item) => ({ ...acc, [item.id]: false }), {}) || {}
      );
      setEditedItems(res.data.follow_up?.declined_items || []);
    } catch (err) {
      toast.error("Failed to load call companion data");
      setShowCallCompanion(false);
    } finally {
      setCallCompanionLoading(false);
    }
  };
  
  // Toggle checklist item
  const toggleChecklistItem = (id) => {
    setCallChecklist(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  // Update item price
  const updateItemPrice = (index, newPrice) => {
    setEditedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, price: parseFloat(newPrice) || 0 } : item
    ));
  };
  
  // Add new item to estimate
  const addNewItem = () => {
    setEditedItems(prev => [...prev, {
      description: '',
      price: 0,
      priority: 'amber',
      recommendation: '',
      is_new: true
    }]);
  };
  
  // Remove item from estimate
  const removeItem = (index) => {
    setEditedItems(prev => prev.filter((_, i) => i !== index));
  };
  
  // Save updated estimate
  const saveEstimateChanges = async () => {
    try {
      await axios.put(`${API}/follow-ups/${selectedFollowUp.id}/estimate`, {
        items: editedItems
      });
      toast.success("Estimate updated");
      setEditingEstimate(false);
      // Refresh data
      const res = await axios.get(`${API}/follow-ups/${selectedFollowUp.id}/call-companion`);
      setCallCompanionData(res.data);
      fetchFollowUps();
    } catch (err) {
      toast.error("Failed to update estimate");
    }
  };
  
  const fetchFollowUps = async () => {
    try {
      const [followUpsRes, statsRes] = await Promise.all([
        axios.get(`${API}/follow-ups`),
        axios.get(`${API}/follow-ups/stats/recovery?days=30`)
      ]);
      setFollowUps(followUpsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchFollowUps();
    // Poll every 30 seconds for updates
    const interval = setInterval(fetchFollowUps, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const handleLogOutcome = async () => {
    if (!outcomeForm.outcome) {
      toast.error("Please select an outcome");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/follow-ups/${selectedFollowUp.id}/outcome`, {
        outcome: outcomeForm.outcome,
        notes: outcomeForm.notes || null,
        recovered_value: outcomeForm.recovered_value ? parseFloat(outcomeForm.recovered_value) : null,
        next_follow_up_days: outcomeForm.next_follow_up_days ? parseInt(outcomeForm.next_follow_up_days) : null
      });
      toast.success("Outcome logged successfully");
      setShowOutcomeModal(false);
      setSelectedFollowUp(null);
      setOutcomeForm({ outcome: '', notes: '', recovered_value: '', next_follow_up_days: '' });
      fetchFollowUps();
    } catch (err) {
      toast.error("Failed to log outcome");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleReschedule = async (followUpId, days) => {
    try {
      await axios.post(`${API}/follow-ups/${followUpId}/reschedule?days=${days}`);
      toast.success(`Rescheduled for ${days} days`);
      fetchFollowUps();
    } catch (err) {
      toast.error("Failed to reschedule");
    }
  };
  
  const handleClose = async (followUpId) => {
    try {
      await axios.delete(`${API}/follow-ups/${followUpId}?reason=manual_close`);
      toast.success("Follow-up closed");
      fetchFollowUps();
    } catch (err) {
      toast.error("Failed to close");
    }
  };
  
  const FollowUpCard = ({ fu, priority }) => {
    const priorityColors = {
      book_requested: 'border-green-500 bg-green-50',
      overdue: 'border-red-500 bg-red-50',
      due_today: 'border-amber-500 bg-amber-50',
      pending: 'border-slate-300 bg-white'
    };
    
    const redItems = fu.declined_items?.filter(i => i.priority === 'red') || [];
    const amberItems = fu.declined_items?.filter(i => i.priority === 'amber') || [];
    
    return (
      <Card className={`border-2 ${priorityColors[priority] || 'border-slate-200'}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-lg">{fu.vehicle_reg}</p>
                {fu.book_requested && (
                  <Badge className="bg-green-500 text-white animate-pulse">WANTS TO BOOK</Badge>
                )}
                {priority === 'overdue' && !fu.book_requested && (
                  <Badge variant="destructive">OVERDUE</Badge>
                )}
              </div>
              <p className="text-slate-600">{fu.customer_name}</p>
              <p className="text-sm text-slate-500">{fu.customer_phone}</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-slate-900">£{fu.total_outstanding_value?.toFixed(0)}</p>
              <p className="text-xs text-slate-500">outstanding</p>
            </div>
          </div>
          
          {/* Declined items summary */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {redItems.length > 0 && (
              <Badge variant="destructive" className="text-xs">
                {redItems.length} Red item{redItems.length > 1 ? 's' : ''}
              </Badge>
            )}
            {amberItems.length > 0 && (
              <Badge className="bg-amber-500 text-white text-xs">
                {amberItems.length} Amber item{amberItems.length > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          
          {/* Status info */}
          <div className="text-xs text-slate-500 mb-3">
            {fu.sms_sent ? (
              <span>SMS sent {new Date(fu.sms_sent_at).toLocaleDateString()}</span>
            ) : (
              <span>SMS scheduled for {new Date(fu.follow_up_date).toLocaleDateString()}</span>
            )}
            {fu.contact_attempts?.length > 0 && (
              <span className="ml-2">• {fu.contact_attempts.length} contact attempt(s)</span>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button 
              size="sm" 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => openCallCompanion(fu)}
              data-testid={`start-call-${fu.id}`}
            >
              <Phone className="w-3 h-3 mr-1" />
              Start Call
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => {
                setSelectedFollowUp(fu);
                setShowOutcomeModal(true);
              }}
            >
              Quick Log
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleReschedule(fu.id, 7)}
            >
              Reschedule +7d
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-slate-500"
              onClick={() => handleClose(fu.id)}
            >
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-48"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Follow-Ups</h1>
            <p className="text-slate-600">Chase declined work and recover revenue</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/app/foh")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">£{stats.total_recovered_value?.toFixed(0) || 0}</p>
                <p className="text-sm text-slate-600">Recovered (30 days)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-slate-900">£{stats.total_outstanding_value?.toFixed(0) || 0}</p>
                <p className="text-sm text-slate-600">Outstanding</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.recovery_rate_percent || 0}%</p>
                <p className="text-sm text-slate-600">Recovery Rate</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{followUps?.summary?.chase_due_today_count || 0}</p>
                <p className="text-sm text-slate-600">To Chase Today</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Book Requested - Highest Priority */}
        {followUps?.book_requested?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Customer Wants to Book ({followUps.book_requested.length})
            </h2>
            <div className="space-y-3">
              {followUps.book_requested.map(fu => (
                <FollowUpCard key={fu.id} fu={fu} priority="book_requested" />
              ))}
            </div>
          </div>
        )}
        
        {/* Overdue Chases */}
        {followUps?.chase_overdue?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Overdue ({followUps.chase_overdue.length})
            </h2>
            <div className="space-y-3">
              {followUps.chase_overdue.map(fu => (
                <FollowUpCard key={fu.id} fu={fu} priority="overdue" />
              ))}
            </div>
          </div>
        )}
        
        {/* Due Today */}
        {followUps?.chase_due_today?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-amber-700 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Chase Due Today ({followUps.chase_due_today.length})
            </h2>
            <div className="space-y-3">
              {followUps.chase_due_today.map(fu => (
                <FollowUpCard key={fu.id} fu={fu} priority="due_today" />
              ))}
            </div>
          </div>
        )}
        
        {/* SMS Pending / Upcoming */}
        {(followUps?.sms_pending?.length > 0 || followUps?.upcoming?.length > 0) && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-700 mb-3">
              Scheduled ({(followUps?.sms_pending?.length || 0) + (followUps?.upcoming?.length || 0)})
            </h2>
            <div className="space-y-3">
              {[...(followUps?.sms_pending || []), ...(followUps?.upcoming || [])].map(fu => (
                <FollowUpCard key={fu.id} fu={fu} priority="pending" />
              ))}
            </div>
          </div>
        )}
        
        {/* Empty state */}
        {!followUps?.book_requested?.length && 
         !followUps?.chase_overdue?.length && 
         !followUps?.chase_due_today?.length &&
         !followUps?.sms_pending?.length &&
         !followUps?.upcoming?.length && (
          <Card className="text-center p-12">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">All caught up!</h2>
            <p className="text-slate-600">No follow-ups pending. Great work!</p>
          </Card>
        )}
        
        {/* Outcome Modal */}
        {showOutcomeModal && selectedFollowUp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Log Call Outcome</CardTitle>
                <p className="text-sm text-slate-600">
                  {selectedFollowUp.vehicle_reg} - {selectedFollowUp.customer_name}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="mb-2 block">What happened?</Label>
                  <div className="space-y-2">
                    {[
                      { value: 'no_answer', label: 'No answer - retry later' },
                      { value: 'callback_requested', label: 'Callback requested' },
                      { value: 'partial_recovery', label: 'Partial recovery - some work booked' },
                      { value: 'full_recovery', label: 'Full recovery - all work booked' },
                      { value: 'declined_again', label: 'Declined again - close' },
                      { value: 'customer_lost', label: 'Customer lost (sold car, etc)' }
                    ].map(opt => (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="outcome" 
                          value={opt.value}
                          checked={outcomeForm.outcome === opt.value}
                          onChange={(e) => setOutcomeForm(prev => ({ ...prev, outcome: e.target.value }))}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {['partial_recovery', 'full_recovery'].includes(outcomeForm.outcome) && (
                  <div>
                    <Label>Recovered Value (£)</Label>
                    <Input 
                      type="number"
                      value={outcomeForm.recovered_value}
                      onChange={(e) => setOutcomeForm(prev => ({ ...prev, recovered_value: e.target.value }))}
                      placeholder="e.g. 250"
                    />
                  </div>
                )}
                
                {['no_answer', 'callback_requested', 'partial_recovery'].includes(outcomeForm.outcome) && (
                  <div>
                    <Label>Follow up again in (days)</Label>
                    <Input 
                      type="number"
                      value={outcomeForm.next_follow_up_days}
                      onChange={(e) => setOutcomeForm(prev => ({ ...prev, next_follow_up_days: e.target.value }))}
                      placeholder="e.g. 3"
                    />
                  </div>
                )}
                
                <div>
                  <Label>Notes (optional)</Label>
                  <Input 
                    value={outcomeForm.notes}
                    onChange={(e) => setOutcomeForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes..."
                  />
                </div>
              </CardContent>
              <div className="flex gap-2 p-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowOutcomeModal(false);
                    setSelectedFollowUp(null);
                    setOutcomeForm({ outcome: '', notes: '', recovered_value: '', next_follow_up_days: '', items_agreed: [] });
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleLogOutcome}
                  disabled={submitting || !outcomeForm.outcome}
                >
                  {submitting ? 'Saving...' : 'Save Outcome'}
                </Button>
              </div>
            </Card>
          </div>
        )}
        
        {/* Call Companion Modal */}
        {showCallCompanion && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
              {callCompanionLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-600">Loading call companion...</p>
                </div>
              ) : callCompanionData ? (
                <>
                  {/* Header */}
                  <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white text-xl flex items-center gap-2">
                          <Phone className="w-5 h-5" />
                          Follow-Up Call Companion
                        </CardTitle>
                        <p className="text-blue-100 mt-1">
                          {callCompanionData.follow_up?.vehicle_reg} • {callCompanionData.follow_up?.vehicle_make} {callCompanionData.follow_up?.vehicle_model}
                        </p>
                        <p className="text-blue-200 text-sm">
                          Customer: {callCompanionData.follow_up?.customer_name} • {callCompanionData.follow_up?.customer_phone}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold">£{callCompanionData.summary?.total_outstanding?.toFixed(0)}</p>
                        <p className="text-blue-200 text-sm">Outstanding Value</p>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <div className="flex-1 overflow-y-auto">
                    <div className="grid grid-cols-3 gap-4 p-4">
                      {/* Left Column - Call Script */}
                      <div className="col-span-1 space-y-4">
                        <Card className="border-blue-200">
                          <CardHeader className="py-3 bg-blue-50">
                            <CardTitle className="text-sm text-blue-800">Call Script & Checklist</CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 space-y-4">
                            {/* Opening */}
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Opening</p>
                              <p className="text-sm text-slate-700 italic">"{callCompanionData.call_script?.opening?.greeting}"</p>
                              <p className="text-sm text-slate-600 mt-1">{callCompanionData.call_script?.opening?.purpose}</p>
                            </div>
                            
                            {/* Checklist */}
                            <div className="border-t pt-3">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Call Checklist</p>
                              <div className="space-y-2">
                                {callCompanionData.call_script?.checklist?.map(item => (
                                  <label 
                                    key={item.id}
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                      callChecklist[item.id] ? 'bg-green-50 border border-green-200' : 'bg-slate-50 hover:bg-slate-100'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={callChecklist[item.id] || false}
                                      onChange={() => toggleChecklistItem(item.id)}
                                      className="w-4 h-4 text-green-600"
                                    />
                                    <span className={`text-sm ${callChecklist[item.id] ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                                      {item.label}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Objection Handling */}
                            <div className="border-t pt-3">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Objection Handling</p>
                              <div className="space-y-2">
                                <details className="text-sm">
                                  <summary className="cursor-pointer font-medium text-slate-700 hover:text-blue-600">"Too expensive"</summary>
                                  <p className="mt-1 pl-2 text-slate-600 italic border-l-2 border-blue-200">
                                    {callCompanionData.call_script?.objection_handling?.too_expensive}
                                  </p>
                                </details>
                                <details className="text-sm">
                                  <summary className="cursor-pointer font-medium text-slate-700 hover:text-blue-600">"Need to think about it"</summary>
                                  <p className="mt-1 pl-2 text-slate-600 italic border-l-2 border-blue-200">
                                    {callCompanionData.call_script?.objection_handling?.need_to_think}
                                  </p>
                                </details>
                                <details className="text-sm">
                                  <summary className="cursor-pointer font-medium text-slate-700 hover:text-blue-600">"Going elsewhere"</summary>
                                  <p className="mt-1 pl-2 text-slate-600 italic border-l-2 border-blue-200">
                                    {callCompanionData.call_script?.objection_handling?.going_elsewhere}
                                  </p>
                                </details>
                                <details className="text-sm">
                                  <summary className="cursor-pointer font-medium text-slate-700 hover:text-blue-600">"Not right now"</summary>
                                  <p className="mt-1 pl-2 text-slate-600 italic border-l-2 border-blue-200">
                                    {callCompanionData.call_script?.objection_handling?.not_now}
                                  </p>
                                </details>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* Middle & Right Columns - Estimate/Items */}
                      <div className="col-span-2 space-y-4">
                        {/* View Toggle & Actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant={showDeclinedOnly ? "default" : "outline"}
                              onClick={() => setShowDeclinedOnly(true)}
                            >
                              Declined Only
                            </Button>
                            <Button
                              size="sm"
                              variant={!showDeclinedOnly ? "default" : "outline"}
                              onClick={() => setShowDeclinedOnly(false)}
                            >
                              Full Estimate
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            {callCompanionData.customer_portal_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(callCompanionData.customer_portal_url, '_blank')}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Customer View
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant={editingEstimate ? "default" : "outline"}
                              onClick={() => {
                                if (editingEstimate) {
                                  setEditingEstimate(false);
                                  setEditedItems(callCompanionData.follow_up?.declined_items || []);
                                } else {
                                  setEditingEstimate(true);
                                }
                              }}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              {editingEstimate ? 'Cancel Edit' : 'Edit Prices'}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Items List */}
                        <Card>
                          <CardHeader className="py-3 bg-slate-50">
                            <div className="flex justify-between items-center">
                              <CardTitle className="text-sm">
                                {showDeclinedOnly ? 'Declined Items' : 'Full Estimate'} 
                                {editingEstimate && <span className="text-amber-600 ml-2">(Editing)</span>}
                              </CardTitle>
                              {editingEstimate && (
                                <Button size="sm" variant="outline" onClick={addNewItem}>
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Item
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="p-0">
                            <div className="divide-y">
                              {/* Red Items */}
                              {editedItems.filter(i => i.priority === 'red').length > 0 && (
                                <div className="bg-red-50">
                                  <div className="px-4 py-2 border-b border-red-200">
                                    <p className="text-xs font-bold text-red-700 uppercase">Safety Critical (Red)</p>
                                  </div>
                                  {editedItems.filter(i => i.priority === 'red').map((item, idx) => {
                                    const realIdx = editedItems.findIndex(i => i === item);
                                    return (
                                      <div key={realIdx} className="px-4 py-3 flex items-center justify-between border-b border-red-100 last:border-0">
                                        <div className="flex-1">
                                          {editingEstimate ? (
                                            <Input
                                              value={item.description}
                                              onChange={(e) => {
                                                const newItems = [...editedItems];
                                                newItems[realIdx].description = e.target.value;
                                                setEditedItems(newItems);
                                              }}
                                              className="mb-1"
                                            />
                                          ) : (
                                            <p className="font-medium text-slate-800">{item.description}</p>
                                          )}
                                          <p className="text-sm text-slate-600">{item.recommendation}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {editingEstimate ? (
                                            <>
                                              <Input
                                                type="number"
                                                value={item.price}
                                                onChange={(e) => updateItemPrice(realIdx, e.target.value)}
                                                className="w-24 text-right"
                                              />
                                              <Button size="sm" variant="ghost" onClick={() => removeItem(realIdx)}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                              </Button>
                                            </>
                                          ) : (
                                            <span className="font-bold text-lg">£{item.price?.toFixed(2)}</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Amber Items */}
                              {editedItems.filter(i => i.priority === 'amber').length > 0 && (
                                <div className="bg-amber-50">
                                  <div className="px-4 py-2 border-b border-amber-200">
                                    <p className="text-xs font-bold text-amber-700 uppercase">Advisory (Amber)</p>
                                  </div>
                                  {editedItems.filter(i => i.priority === 'amber').map((item, idx) => {
                                    const realIdx = editedItems.findIndex(i => i === item);
                                    return (
                                      <div key={realIdx} className="px-4 py-3 flex items-center justify-between border-b border-amber-100 last:border-0">
                                        <div className="flex-1">
                                          {editingEstimate ? (
                                            <Input
                                              value={item.description}
                                              onChange={(e) => {
                                                const newItems = [...editedItems];
                                                newItems[realIdx].description = e.target.value;
                                                setEditedItems(newItems);
                                              }}
                                              className="mb-1"
                                            />
                                          ) : (
                                            <p className="font-medium text-slate-800">{item.description}</p>
                                          )}
                                          <p className="text-sm text-slate-600">{item.recommendation}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {editingEstimate ? (
                                            <>
                                              <Input
                                                type="number"
                                                value={item.price}
                                                onChange={(e) => updateItemPrice(realIdx, e.target.value)}
                                                className="w-24 text-right"
                                              />
                                              <Button size="sm" variant="ghost" onClick={() => removeItem(realIdx)}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                              </Button>
                                            </>
                                          ) : (
                                            <span className="font-bold text-lg">£{item.price?.toFixed(2)}</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Total */}
                              <div className="px-4 py-3 bg-slate-100 flex justify-between items-center">
                                <span className="font-semibold text-slate-700">Total Outstanding</span>
                                <span className="text-2xl font-bold text-slate-900">
                                  £{editedItems.reduce((sum, i) => sum + (i.price || 0), 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        {/* Save Estimate Changes */}
                        {editingEstimate && (
                          <div className="flex justify-end">
                            <Button onClick={saveEstimateChanges} className="bg-green-600 hover:bg-green-700">
                              <Check className="w-4 h-4 mr-1" />
                              Save Estimate Changes
                            </Button>
                          </div>
                        )}
                        
                        {/* Previous Contact Attempts */}
                        {callCompanionData.follow_up?.contact_attempts?.length > 0 && (
                          <Card>
                            <CardHeader className="py-2 bg-slate-50">
                              <CardTitle className="text-sm">Previous Contact Attempts</CardTitle>
                            </CardHeader>
                            <CardContent className="p-3">
                              <div className="space-y-2">
                                {callCompanionData.follow_up.contact_attempts.map((attempt, idx) => (
                                  <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                                    <div className="flex justify-between">
                                      <span className="font-medium">{attempt.outcome}</span>
                                      <span className="text-slate-500">{new Date(attempt.attempt_date).toLocaleDateString()}</span>
                                    </div>
                                    {attempt.notes && <p className="text-slate-600 mt-1">{attempt.notes}</p>}
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer Actions */}
                  <div className="flex-shrink-0 border-t p-4 bg-slate-50 flex justify-between items-center">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowCallCompanion(false);
                        setCallCompanionData(null);
                        setSelectedFollowUp(null);
                        setEditingEstimate(false);
                      }}
                    >
                      Close
                    </Button>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => handleReschedule(selectedFollowUp.id, 3)}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        Reschedule +3 days
                      </Button>
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          setShowCallCompanion(false);
                          setShowOutcomeModal(true);
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Log Call Outcome
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </Card>
          </div>
        )}

      </div>
    </div>
  );
};


// ============== MAIN APP ==============
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Marketing Homepage */}
          <Route path="/" element={<MarketingHomepage />} />
          
          {/* Public Marketing Demo Page */}
          <Route path="/demo" element={<DemoPageStandalone />} />
          <Route path="/demo/interactive" element={<MarketingDemo />} />
          
          {/* Public Customer Approval Portal - Mobile-first decision interface */}
          <Route path="/approve/:token" element={<MobileCustomerPortal />} />
          
          {/* Public Customer Book Now page - from SMS follow-up link */}
          <Route path="/book/:bookToken" element={<CustomerBookNowPage />} />
          
          {/* ============== APP ROUTES (Under /app) ============== */}
          
          {/* Auth */}
          <Route path="/app/login" element={<LoginPage />} />

          {/* iter213b — Garage Hive Deep-Link Gateway (called by GH AL
              extensions via /gh/open?mode=...&jobsheet_no=...). The
              component itself handles auth: if not logged in it
              redirects to /app/login with the intended destination in
              location.state.from. */}
          <Route path="/gh/open" element={<GHOpenGateway />} />
          
          {/* Redirect /app/new to /app/foh/intake (preserves query params for keyboard shortcut) */}
          <Route path="/app/new" element={<ProtectedRoute><NewToIntakeRedirect /></ProtectedRoute>} />
          <Route path="/app/i/:inspectionId" element={<ProtectedRoute><InspectionPermanentView /></ProtectedRoute>} />
          
          {/* Technician Routes */}
          <Route path="/app/tech" element={<ProtectedRoute><JobsListPage /></ProtectedRoute>} />
          <Route path="/app/tech/jobs" element={<ProtectedRoute><JobsListPage /></ProtectedRoute>} />
          <Route path="/app/tech/job/:id/start" element={<ProtectedRoute><StartInspectionPage /></ProtectedRoute>} />
          {/* iter213c — Technician Required Work chooser (used when the
              /gh/open?mode=technician deep-link resolves to ≥2 outstanding
              execution workflows on one job). */}
          <Route path="/app/tech/job/:id/required-work" element={<ProtectedRoute><RequiredWorkPage /></ProtectedRoute>} />
          <Route path="/app/tech/job/:id/diagnostic" element={<ProtectedRoute><DiagnosticCapturePage /></ProtectedRoute>} />
          <Route path="/app/tech/job/:id/arrival" element={<ProtectedRoute><ArrivalPhotosPage /></ProtectedRoute>} />
          <Route path="/app/tech/job/:id/sections" element={<ProtectedRoute><SectionsListPage /></ProtectedRoute>} />
          <Route path="/app/tech/job/:id/section/:sectionId" element={<ProtectedRoute><SectionItemsPage /></ProtectedRoute>} />
          <Route path="/app/tech/job/:id/item/:itemId" element={<ProtectedRoute><ItemDetailPage /></ProtectedRoute>} />
          <Route path="/app/tech/job/:id/submit" element={<ProtectedRoute><SubmitInspectionPage /></ProtectedRoute>} />
          <Route path="/app/tech/mot" element={<ProtectedRoute><MOTTodaysQueuePage /></ProtectedRoute>} />
          <Route path="/app/tech/mot/:jobId" element={<ProtectedRoute><MOTWorkspacePage /></ProtectedRoute>} />
          
          {/* FOH Routes */}
          <Route path="/app/foh" element={<ProtectedRoute><FOHDashboard /></ProtectedRoute>} />
          <Route path="/app/foh/follow-ups" element={<ProtectedRoute><FOHFollowUpsDashboard /></ProtectedRoute>} />
          <Route path="/app/foh/analytics/declines" element={<ProtectedRoute><DeclineAnalyticsPage /></ProtectedRoute>} />
          <Route path="/app/foh/archive" element={<ProtectedRoute><FOHInspectionArchive /></ProtectedRoute>} />
          <Route path="/app/foh/intake" element={<ProtectedRoute><FOHInspectionIntake /></ProtectedRoute>} />
          <Route path="/app/foh/inspection/:inspectionId" element={<ProtectedRoute><FOHInspectionReview /></ProtectedRoute>} />
          <Route path="/app/foh/job/:id" element={<ProtectedRoute><FOHJobView /></ProtectedRoute>} />
          <Route path="/app/foh/yestimate/:yestimateId/pricing" element={<ProtectedRoute><FOHPricingBuilder /></ProtectedRoute>} />
          <Route path="/app/foh/yestimate/:yestimateId/portal-preview" element={<ProtectedRoute><PortalPreviewPage /></ProtectedRoute>} />
          <Route path="/app/foh/yestimate/:yestimateId/preview" element={<ProtectedRoute><FOHCustomerPreview /></ProtectedRoute>} />
          <Route path="/app/foh/yestimate/:yestimateId/send-portal" element={<ProtectedRoute><FOHSendPortal /></ProtectedRoute>} />
          <Route path="/app/foh/yestimate/:yestimateId/audit" element={<ProtectedRoute><FOHAuditPanel /></ProtectedRoute>} />
          {/* Yestimate Intake Companion — single entry point for FOH intake (Phase 1) */}
          <Route path="/app/foh/start-intake" element={<ProtectedRoute><StartIntakePage /></ProtectedRoute>} />
          <Route path="/app/foh/intake/gh-case/:sessionId" element={<ProtectedRoute><GHCasePage /></ProtectedRoute>} />
          <Route path="/app/foh/yestimate-intake/:sessionId" element={<ProtectedRoute><IntakeSessionPage /></ProtectedRoute>} />
          
          {/* Parts Pack Routes */}
          <Route path="/app/foh/parts-packs" element={<ProtectedRoute><PartsPackList /></ProtectedRoute>} />
          <Route path="/app/foh/parts-pack/:partsPackId" element={<ProtectedRoute><PartsPackView /></ProtectedRoute>} />

          {/* iter213 — Vehicle history timeline (Yestimate + GH + DVSA) */}
          <Route path="/app/foh/vehicles" element={<ProtectedRoute><VehicleHistoryPage /></ProtectedRoute>} />
          <Route path="/app/foh/vehicles/:reg" element={<ProtectedRoute><VehicleHistoryPage /></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/app/admin/commonly-required" element={<ProtectedRoute><AdminCommonlyRequiredItems /></ProtectedRoute>} />
          <Route path="/app/admin/common-issues" element={<ProtectedRoute><CommonIssuesAdminPage /></ProtectedRoute>} />
          <Route path="/app/admin/pricing-packages" element={<ProtectedRoute><PricingPackageAdmin /></ProtectedRoute>} />
          {/* iter199al (Feb 2026) — Missing route wired up. The Settings
              menu has navigated to ``/app/admin/price-book`` since the
              page was built, but no Route was registered so the click
              fell through to the ``/admin/*`` catch-all → 404 loop.
              FOH had no UI path to actually edit labour rates / fees /
              packages despite the editor existing and the backend
              endpoints being fully wired.
              iter199am (Feb 2026) — Originally gated to ADMIN/MANAGER
              roles, but that bounced FOH users back to the tech
              dashboard via ProtectedRoute fallback. Sibling admin
              routes (commonly-required / common-issues / pricing-
              packages) have no role gate, so we match that pattern
              here — any authenticated user can edit the price book.
              Backend write endpoints already enforce their own role
              policy if needed. */}
          <Route path="/app/admin/price-book" element={<ProtectedRoute><PriceBookAdmin /></ProtectedRoute>} />
          <Route path="/app/admin/jce" element={<ProtectedRoute><JCEAdmin /></ProtectedRoute>} />
          <Route path="/app/admin/staff" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminStaffPage /></ProtectedRoute>} />
          <Route path="/app/admin/integration-health" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminIntegrationHealthPage /></ProtectedRoute>} />
          <Route path="/app/admin/gh-preview-fault-lab" element={<ProtectedRoute allowedRoles={["ADMIN"]}><GHPreviewFaultLabPage /></ProtectedRoute>} />
          
          {/* Profile (self-service password change) */}
          <Route path="/app/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          
          {/* Legacy redirects - redirect old routes to new /app routes */}
          <Route path="/login" element={<Navigate to="/app/login" replace />} />
          <Route path="/tech/job/:id/start" element={<TechStartRedirect />} />
          <Route path="/tech/job/:id/arrival" element={<TechArrivalRedirect />} />
          <Route path="/tech/job/:id/sections" element={<TechSectionsRedirect />} />
          <Route path="/tech/job/:id/section/:sectionId" element={<TechSectionRedirect />} />
          <Route path="/tech/job/:id/item/:itemId" element={<TechItemRedirect />} />
          <Route path="/tech/job/:id/submit" element={<TechSubmitRedirect />} />
          <Route path="/tech/job/:id/diagnostic" element={<TechDiagnosticRedirect />} />
          <Route path="/tech/jobs" element={<Navigate to="/app/tech/jobs" replace />} />
          <Route path="/tech" element={<Navigate to="/app/tech/jobs" replace />} />
          <Route path="/foh/*" element={<Navigate to="/app/foh" replace />} />
          <Route path="/admin/*" element={<Navigate to="/app/admin/commonly-required" replace />} />
          
          {/* Catch-all for /app without specific route */}
          <Route path="/app" element={<Navigate to="/app/login" replace />} />
          
          {/* Any other unmatched route goes to marketing homepage */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;